/*!
  * Bootstrap v5.3.3 (https://getbootstrap.com/)
  * Copyright 2011-2024 The Bootstrap Authors (https://github.com/twbs/bootstrap/graphs/contributors)
  * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
  */
(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
  typeof define === 'function' && define.amd ? define(factory) :
  (global = typeof globalThis !== 'undefined' ? globalThis : global || self, global.bootstrap = factory());
})(this, (function () { 'use strict';

  /**
   * --------------------------------------------------------------------------
   * Bootstrap dom/data.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  /**
   * Constants
   */

  const elementMap = new Map();
  const Data = {
    set(element, key, instance) {
      if (!elementMap.has(element)) {
        elementMap.set(element, new Map());
      }
      const instanceMap = elementMap.get(element);

      // make it clear we only want one instance per element
      // can be removed later when multiple key/instances are fine to be used
      if (!instanceMap.has(key) && instanceMap.size !== 0) {
        // eslint-disable-next-line no-console
        console.error(`Bootstrap doesn't allow more than one instance per element. Bound instance: ${Array.from(instanceMap.keys())[0]}.`);
        return;
      }
      instanceMap.set(key, instance);
    },
    get(element, key) {
      if (elementMap.has(element)) {
        return elementMap.get(element).get(key) || null;
      }
      return null;
    },
    remove(element, key) {
      if (!elementMap.has(element)) {
        return;
      }
      const instanceMap = elementMap.get(element);
      instanceMap.delete(key);

      // free up element references if there are no instances left for an element
      if (instanceMap.size === 0) {
        elementMap.delete(element);
      }
    }
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap util/index.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  const MAX_UID = 1000000;
  const MILLISECONDS_MULTIPLIER = 1000;
  const TRANSITION_END = 'transitionend';

  /**
   * Properly escape IDs selectors to handle weird IDs
   * @param {string} selector
   * @returns {string}
   */
  const parseSelector = selector => {
    if (selector && window.CSS && window.CSS.escape) {
      // document.querySelector needs escaping to handle IDs (html5+) containing for instance /
      selector = selector.replace(/#([^\s"#']+)/g, (match, id) => `#${CSS.escape(id)}`);
    }
    return selector;
  };

  // Shout-out Angus Croll (https://goo.gl/pxwQGp)
  const toType = object => {
    if (object === null || object === undefined) {
      return `${object}`;
    }
    return Object.prototype.toString.call(object).match(/\s([a-z]+)/i)[1].toLowerCase();
  };

  /**
   * Public Util API
   */

  const getUID = prefix => {
    do {
      prefix += Math.floor(Math.random() * MAX_UID);
    } while (document.getElementById(prefix));
    return prefix;
  };
  const getTransitionDurationFromElement = element => {
    if (!element) {
      return 0;
    }

    // Get transition-duration of the element
    let {
      transitionDuration,
      transitionDelay
    } = window.getComputedStyle(element);
    const floatTransitionDuration = Number.parseFloat(transitionDuration);
    const floatTransitionDelay = Number.parseFloat(transitionDelay);

    // Return 0 if element or transition duration is not found
    if (!floatTransitionDuration && !floatTransitionDelay) {
      return 0;
    }

    // If multiple durations are defined, take the first
    transitionDuration = transitionDuration.split(',')[0];
    transitionDelay = transitionDelay.split(',')[0];
    return (Number.parseFloat(transitionDuration) + Number.parseFloat(transitionDelay)) * MILLISECONDS_MULTIPLIER;
  };
  const triggerTransitionEnd = element => {
    element.dispatchEvent(new Event(TRANSITION_END));
  };
  const isElement$1 = object => {
    if (!object || typeof object !== 'object') {
      return false;
    }
    if (typeof object.jquery !== 'undefined') {
      object = object[0];
    }
    return typeof object.nodeType !== 'undefined';
  };
  const getElement = object => {
    // it's a jQuery object or a node element
    if (isElement$1(object)) {
      return object.jquery ? object[0] : object;
    }
    if (typeof object === 'string' && object.length > 0) {
      return document.querySelector(parseSelector(object));
    }
    return null;
  };
  const isVisible = element => {
    if (!isElement$1(element) || element.getClientRects().length === 0) {
      return false;
    }
    const elementIsVisible = getComputedStyle(element).getPropertyValue('visibility') === 'visible';
    // Handle `details` element as its content may falsie appear visible when it is closed
    const closedDetails = element.closest('details:not([open])');
    if (!closedDetails) {
      return elementIsVisible;
    }
    if (closedDetails !== element) {
      const summary = element.closest('summary');
      if (summary && summary.parentNode !== closedDetails) {
        return false;
      }
      if (summary === null) {
        return false;
      }
    }
    return elementIsVisible;
  };
  const isDisabled = element => {
    if (!element || element.nodeType !== Node.ELEMENT_NODE) {
      return true;
    }
    if (element.classList.contains('disabled')) {
      return true;
    }
    if (typeof element.disabled !== 'undefined') {
      return element.disabled;
    }
    return element.hasAttribute('disabled') && element.getAttribute('disabled') !== 'false';
  };
  const findShadowRoot = element => {
    if (!document.documentElement.attachShadow) {
      return null;
    }

    // Can find the shadow root otherwise it'll return the document
    if (typeof element.getRootNode === 'function') {
      const root = element.getRootNode();
      return root instanceof ShadowRoot ? root : null;
    }
    if (element instanceof ShadowRoot) {
      return element;
    }

    // when we don't find a shadow root
    if (!element.parentNode) {
      return null;
    }
    return findShadowRoot(element.parentNode);
  };
  const noop = () => {};

  /**
   * Trick to restart an element's animation
   *
   * @param {HTMLElement} element
   * @return void
   *
   * @see https://www.charistheo.io/blog/2021/02/restart-a-css-animation-with-javascript/#restarting-a-css-animation
   */
  const reflow = element => {
    element.offsetHeight; // eslint-disable-line no-unused-expressions
  };
  const getjQuery = () => {
    if (window.jQuery && !document.body.hasAttribute('data-bs-no-jquery')) {
      return window.jQuery;
    }
    return null;
  };
  const DOMContentLoadedCallbacks = [];
  const onDOMContentLoaded = callback => {
    if (document.readyState === 'loading') {
      // add listener on the first call when the document is in loading state
      if (!DOMContentLoadedCallbacks.length) {
        document.addEventListener('DOMContentLoaded', () => {
          for (const callback of DOMContentLoadedCallbacks) {
            callback();
          }
        });
      }
      DOMContentLoadedCallbacks.push(callback);
    } else {
      callback();
    }
  };
  const isRTL = () => document.documentElement.dir === 'rtl';
  const defineJQueryPlugin = plugin => {
    onDOMContentLoaded(() => {
      const $ = getjQuery();
      /* istanbul ignore if */
      if ($) {
        const name = plugin.NAME;
        const JQUERY_NO_CONFLICT = $.fn[name];
        $.fn[name] = plugin.jQueryInterface;
        $.fn[name].Constructor = plugin;
        $.fn[name].noConflict = () => {
          $.fn[name] = JQUERY_NO_CONFLICT;
          return plugin.jQueryInterface;
        };
      }
    });
  };
  const execute = (possibleCallback, args = [], defaultValue = possibleCallback) => {
    return typeof possibleCallback === 'function' ? possibleCallback(...args) : defaultValue;
  };
  const executeAfterTransition = (callback, transitionElement, waitForTransition = true) => {
    if (!waitForTransition) {
      execute(callback);
      return;
    }
    const durationPadding = 5;
    const emulatedDuration = getTransitionDurationFromElement(transitionElement) + durationPadding;
    let called = false;
    const handler = ({
      target
    }) => {
      if (target !== transitionElement) {
        return;
      }
      called = true;
      transitionElement.removeEventListener(TRANSITION_END, handler);
      execute(callback);
    };
    transitionElement.addEventListener(TRANSITION_END, handler);
    setTimeout(() => {
      if (!called) {
        triggerTransitionEnd(transitionElement);
      }
    }, emulatedDuration);
  };

  /**
   * Return the previous/next element of a list.
   *
   * @param {array} list    The list of elements
   * @param activeElement   The active element
   * @param shouldGetNext   Choose to get next or previous element
   * @param isCycleAllowed
   * @return {Element|elem} The proper element
   */
  const getNextActiveElement = (list, activeElement, shouldGetNext, isCycleAllowed) => {
    const listLength = list.length;
    let index = list.indexOf(activeElement);

    // if the element does not exist in the list return an element
    // depending on the direction and if cycle is allowed
    if (index === -1) {
      return !shouldGetNext && isCycleAllowed ? list[listLength - 1] : list[0];
    }
    index += shouldGetNext ? 1 : -1;
    if (isCycleAllowed) {
      index = (index + listLength) % listLength;
    }
    return list[Math.max(0, Math.min(index, listLength - 1))];
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap dom/event-handler.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const namespaceRegex = /[^.]*(?=\..*)\.|.*/;
  const stripNameRegex = /\..*/;
  const stripUidRegex = /::\d+$/;
  const eventRegistry = {}; // Events storage
  let uidEvent = 1;
  const customEvents = {
    mouseenter: 'mouseover',
    mouseleave: 'mouseout'
  };
  const nativeEvents = new Set(['click', 'dblclick', 'mouseup', 'mousedown', 'contextmenu', 'mousewheel', 'DOMMouseScroll', 'mouseover', 'mouseout', 'mousemove', 'selectstart', 'selectend', 'keydown', 'keypress', 'keyup', 'orientationchange', 'touchstart', 'touchmove', 'touchend', 'touchcancel', 'pointerdown', 'pointermove', 'pointerup', 'pointerleave', 'pointercancel', 'gesturestart', 'gesturechange', 'gestureend', 'focus', 'blur', 'change', 'reset', 'select', 'submit', 'focusin', 'focusout', 'load', 'unload', 'beforeunload', 'resize', 'move', 'DOMContentLoaded', 'readystatechange', 'error', 'abort', 'scroll']);

  /**
   * Private methods
   */

  function makeEventUid(element, uid) {
    return uid && `${uid}::${uidEvent++}` || element.uidEvent || uidEvent++;
  }
  function getElementEvents(element) {
    const uid = makeEventUid(element);
    element.uidEvent = uid;
    eventRegistry[uid] = eventRegistry[uid] || {};
    return eventRegistry[uid];
  }
  function bootstrapHandler(element, fn) {
    return function handler(event) {
      hydrateObj(event, {
        delegateTarget: element
      });
      if (handler.oneOff) {
        EventHandler.off(element, event.type, fn);
      }
      return fn.apply(element, [event]);
    };
  }
  function bootstrapDelegationHandler(element, selector, fn) {
    return function handler(event) {
      const domElements = element.querySelectorAll(selector);
      for (let {
        target
      } = event; target && target !== this; target = target.parentNode) {
        for (const domElement of domElements) {
          if (domElement !== target) {
            continue;
          }
          hydrateObj(event, {
            delegateTarget: target
          });
          if (handler.oneOff) {
            EventHandler.off(element, event.type, selector, fn);
          }
          return fn.apply(target, [event]);
        }
      }
    };
  }
  function findHandler(events, callable, delegationSelector = null) {
    return Object.values(events).find(event => event.callable === callable && event.delegationSelector === delegationSelector);
  }
  function normalizeParameters(originalTypeEvent, handler, delegationFunction) {
    const isDelegated = typeof handler === 'string';
    // TODO: tooltip passes `false` instead of selector, so we need to check
    const callable = isDelegated ? delegationFunction : handler || delegationFunction;
    let typeEvent = getTypeEvent(originalTypeEvent);
    if (!nativeEvents.has(typeEvent)) {
      typeEvent = originalTypeEvent;
    }
    return [isDelegated, callable, typeEvent];
  }
  function addHandler(element, originalTypeEvent, handler, delegationFunction, oneOff) {
    if (typeof originalTypeEvent !== 'string' || !element) {
      return;
    }
    let [isDelegated, callable, typeEvent] = normalizeParameters(originalTypeEvent, handler, delegationFunction);

    // in case of mouseenter or mouseleave wrap the handler within a function that checks for its DOM position
    // this prevents the handler from being dispatched the same way as mouseover or mouseout does
    if (originalTypeEvent in customEvents) {
      const wrapFunction = fn => {
        return function (event) {
          if (!event.relatedTarget || event.relatedTarget !== event.delegateTarget && !event.delegateTarget.contains(event.relatedTarget)) {
            return fn.call(this, event);
          }
        };
      };
      callable = wrapFunction(callable);
    }
    const events = getElementEvents(element);
    const handlers = events[typeEvent] || (events[typeEvent] = {});
    const previousFunction = findHandler(handlers, callable, isDelegated ? handler : null);
    if (previousFunction) {
      previousFunction.oneOff = previousFunction.oneOff && oneOff;
      return;
    }
    const uid = makeEventUid(callable, originalTypeEvent.replace(namespaceRegex, ''));
    const fn = isDelegated ? bootstrapDelegationHandler(element, handler, callable) : bootstrapHandler(element, callable);
    fn.delegationSelector = isDelegated ? handler : null;
    fn.callable = callable;
    fn.oneOff = oneOff;
    fn.uidEvent = uid;
    handlers[uid] = fn;
    element.addEventListener(typeEvent, fn, isDelegated);
  }
  function removeHandler(element, events, typeEvent, handler, delegationSelector) {
    const fn = findHandler(events[typeEvent], handler, delegationSelector);
    if (!fn) {
      return;
    }
    element.removeEventListener(typeEvent, fn, Boolean(delegationSelector));
    delete events[typeEvent][fn.uidEvent];
  }
  function removeNamespacedHandlers(element, events, typeEvent, namespace) {
    const storeElementEvent = events[typeEvent] || {};
    for (const [handlerKey, event] of Object.entries(storeElementEvent)) {
      if (handlerKey.includes(namespace)) {
        removeHandler(element, events, typeEvent, event.callable, event.delegationSelector);
      }
    }
  }
  function getTypeEvent(event) {
    // allow to get the native events from namespaced events ('click.bs.button' --> 'click')
    event = event.replace(stripNameRegex, '');
    return customEvents[event] || event;
  }
  const EventHandler = {
    on(element, event, handler, delegationFunction) {
      addHandler(element, event, handler, delegationFunction, false);
    },
    one(element, event, handler, delegationFunction) {
      addHandler(element, event, handler, delegationFunction, true);
    },
    off(element, originalTypeEvent, handler, delegationFunction) {
      if (typeof originalTypeEvent !== 'string' || !element) {
        return;
      }
      const [isDelegated, callable, typeEvent] = normalizeParameters(originalTypeEvent, handler, delegationFunction);
      const inNamespace = typeEvent !== originalTypeEvent;
      const events = getElementEvents(element);
      const storeElementEvent = events[typeEvent] || {};
      const isNamespace = originalTypeEvent.startsWith('.');
      if (typeof callable !== 'undefined') {
        // Simplest case: handler is passed, remove that listener ONLY.
        if (!Object.keys(storeElementEvent).length) {
          return;
        }
        removeHandler(element, events, typeEvent, callable, isDelegated ? handler : null);
        return;
      }
      if (isNamespace) {
        for (const elementEvent of Object.keys(events)) {
          removeNamespacedHandlers(element, events, elementEvent, originalTypeEvent.slice(1));
        }
      }
      for (const [keyHandlers, event] of Object.entries(storeElementEvent)) {
        const handlerKey = keyHandlers.replace(stripUidRegex, '');
        if (!inNamespace || originalTypeEvent.includes(handlerKey)) {
          removeHandler(element, events, typeEvent, event.callable, event.delegationSelector);
        }
      }
    },
    trigger(element, event, args) {
      if (typeof event !== 'string' || !element) {
        return null;
      }
      const $ = getjQuery();
      const typeEvent = getTypeEvent(event);
      const inNamespace = event !== typeEvent;
      let jQueryEvent = null;
      let bubbles = true;
      let nativeDispatch = true;
      let defaultPrevented = false;
      if (inNamespace && $) {
        jQueryEvent = $.Event(event, args);
        $(element).trigger(jQueryEvent);
        bubbles = !jQueryEvent.isPropagationStopped();
        nativeDispatch = !jQueryEvent.isImmediatePropagationStopped();
        defaultPrevented = jQueryEvent.isDefaultPrevented();
      }
      const evt = hydrateObj(new Event(event, {
        bubbles,
        cancelable: true
      }), args);
      if (defaultPrevented) {
        evt.preventDefault();
      }
      if (nativeDispatch) {
        element.dispatchEvent(evt);
      }
      if (evt.defaultPrevented && jQueryEvent) {
        jQueryEvent.preventDefault();
      }
      return evt;
    }
  };
  function hydrateObj(obj, meta = {}) {
    for (const [key, value] of Object.entries(meta)) {
      try {
        obj[key] = value;
      } catch (_unused) {
        Object.defineProperty(obj, key, {
          configurable: true,
          get() {
            return value;
          }
        });
      }
    }
    return obj;
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap dom/manipulator.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  function normalizeData(value) {
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
    if (value === Number(value).toString()) {
      return Number(value);
    }
    if (value === '' || value === 'null') {
      return null;
    }
    if (typeof value !== 'string') {
      return value;
    }
    try {
      return JSON.parse(decodeURIComponent(value));
    } catch (_unused) {
      return value;
    }
  }
  function normalizeDataKey(key) {
    return key.replace(/[A-Z]/g, chr => `-${chr.toLowerCase()}`);
  }
  const Manipulator = {
    setDataAttribute(element, key, value) {
      element.setAttribute(`data-bs-${normalizeDataKey(key)}`, value);
    },
    removeDataAttribute(element, key) {
      element.removeAttribute(`data-bs-${normalizeDataKey(key)}`);
    },
    getDataAttributes(element) {
      if (!element) {
        return {};
      }
      const attributes = {};
      const bsKeys = Object.keys(element.dataset).filter(key => key.startsWith('bs') && !key.startsWith('bsConfig'));
      for (const key of bsKeys) {
        let pureKey = key.replace(/^bs/, '');
        pureKey = pureKey.charAt(0).toLowerCase() + pureKey.slice(1, pureKey.length);
        attributes[pureKey] = normalizeData(element.dataset[key]);
      }
      return attributes;
    },
    getDataAttribute(element, key) {
      return normalizeData(element.getAttribute(`data-bs-${normalizeDataKey(key)}`));
    }
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap util/config.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Class definition
   */

  class Config {
    // Getters
    static get Default() {
      return {};
    }
    static get DefaultType() {
      return {};
    }
    static get NAME() {
      throw new Error('You have to implement the static method "NAME", for each component!');
    }
    _getConfig(config) {
      config = this._mergeConfigObj(config);
      config = this._configAfterMerge(config);
      this._typeCheckConfig(config);
      return config;
    }
    _configAfterMerge(config) {
      return config;
    }
    _mergeConfigObj(config, element) {
      const jsonConfig = isElement$1(element) ? Manipulator.getDataAttribute(element, 'config') : {}; // try to parse

      return {
        ...this.constructor.Default,
        ...(typeof jsonConfig === 'object' ? jsonConfig : {}),
        ...(isElement$1(element) ? Manipulator.getDataAttributes(element) : {}),
        ...(typeof config === 'object' ? config : {})
      };
    }
    _typeCheckConfig(config, configTypes = this.constructor.DefaultType) {
      for (const [property, expectedTypes] of Object.entries(configTypes)) {
        const value = config[property];
        const valueType = isElement$1(value) ? 'element' : toType(value);
        if (!new RegExp(expectedTypes).test(valueType)) {
          throw new TypeError(`${this.constructor.NAME.toUpperCase()}: Option "${property}" provided type "${valueType}" but expected type "${expectedTypes}".`);
        }
      }
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap base-component.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const VERSION = '5.3.3';

  /**
   * Class definition
   */

  class BaseComponent extends Config {
    constructor(element, config) {
      super();
      element = getElement(element);
      if (!element) {
        return;
      }
      this._element = element;
      this._config = this._getConfig(config);
      Data.set(this._element, this.constructor.DATA_KEY, this);
    }

    // Public
    dispose() {
      Data.remove(this._element, this.constructor.DATA_KEY);
      EventHandler.off(this._element, this.constructor.EVENT_KEY);
      for (const propertyName of Object.getOwnPropertyNames(this)) {
        this[propertyName] = null;
      }
    }
    _queueCallback(callback, element, isAnimated = true) {
      executeAfterTransition(callback, element, isAnimated);
    }
    _getConfig(config) {
      config = this._mergeConfigObj(config, this._element);
      config = this._configAfterMerge(config);
      this._typeCheckConfig(config);
      return config;
    }

    // Static
    static getInstance(element) {
      return Data.get(getElement(element), this.DATA_KEY);
    }
    static getOrCreateInstance(element, config = {}) {
      return this.getInstance(element) || new this(element, typeof config === 'object' ? config : null);
    }
    static get VERSION() {
      return VERSION;
    }
    static get DATA_KEY() {
      return `bs.${this.NAME}`;
    }
    static get EVENT_KEY() {
      return `.${this.DATA_KEY}`;
    }
    static eventName(name) {
      return `${name}${this.EVENT_KEY}`;
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap dom/selector-engine.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  const getSelector = element => {
    let selector = element.getAttribute('data-bs-target');
    if (!selector || selector === '#') {
      let hrefAttribute = element.getAttribute('href');

      // The only valid content that could double as a selector are IDs or classes,
      // so everything starting with `#` or `.`. If a "real" URL is used as the selector,
      // `document.querySelector` will rightfully complain it is invalid.
      // See https://github.com/twbs/bootstrap/issues/32273
      if (!hrefAttribute || !hrefAttribute.includes('#') && !hrefAttribute.startsWith('.')) {
        return null;
      }

      // Just in case some CMS puts out a full URL with the anchor appended
      if (hrefAttribute.includes('#') && !hrefAttribute.startsWith('#')) {
        hrefAttribute = `#${hrefAttribute.split('#')[1]}`;
      }
      selector = hrefAttribute && hrefAttribute !== '#' ? hrefAttribute.trim() : null;
    }
    return selector ? selector.split(',').map(sel => parseSelector(sel)).join(',') : null;
  };
  const SelectorEngine = {
    find(selector, element = document.documentElement) {
      return [].concat(...Element.prototype.querySelectorAll.call(element, selector));
    },
    findOne(selector, element = document.documentElement) {
      return Element.prototype.querySelector.call(element, selector);
    },
    children(element, selector) {
      return [].concat(...element.children).filter(child => child.matches(selector));
    },
    parents(element, selector) {
      const parents = [];
      let ancestor = element.parentNode.closest(selector);
      while (ancestor) {
        parents.push(ancestor);
        ancestor = ancestor.parentNode.closest(selector);
      }
      return parents;
    },
    prev(element, selector) {
      let previous = element.previousElementSibling;
      while (previous) {
        if (previous.matches(selector)) {
          return [previous];
        }
        previous = previous.previousElementSibling;
      }
      return [];
    },
    // TODO: this is now unused; remove later along with prev()
    next(element, selector) {
      let next = element.nextElementSibling;
      while (next) {
        if (next.matches(selector)) {
          return [next];
        }
        next = next.nextElementSibling;
      }
      return [];
    },
    focusableChildren(element) {
      const focusables = ['a', 'button', 'input', 'textarea', 'select', 'details', '[tabindex]', '[contenteditable="true"]'].map(selector => `${selector}:not([tabindex^="-"])`).join(',');
      return this.find(focusables, element).filter(el => !isDisabled(el) && isVisible(el));
    },
    getSelectorFromElement(element) {
      const selector = getSelector(element);
      if (selector) {
        return SelectorEngine.findOne(selector) ? selector : null;
      }
      return null;
    },
    getElementFromSelector(element) {
      const selector = getSelector(element);
      return selector ? SelectorEngine.findOne(selector) : null;
    },
    getMultipleElementsFromSelector(element) {
      const selector = getSelector(element);
      return selector ? SelectorEngine.find(selector) : [];
    }
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap util/component-functions.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  const enableDismissTrigger = (component, method = 'hide') => {
    const clickEvent = `click.dismiss${component.EVENT_KEY}`;
    const name = component.NAME;
    EventHandler.on(document, clickEvent, `[data-bs-dismiss="${name}"]`, function (event) {
      if (['A', 'AREA'].includes(this.tagName)) {
        event.preventDefault();
      }
      if (isDisabled(this)) {
        return;
      }
      const target = SelectorEngine.getElementFromSelector(this) || this.closest(`.${name}`);
      const instance = component.getOrCreateInstance(target);

      // Method argument is left, for Alert and only, as it doesn't implement the 'hide' method
      instance[method]();
    });
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap alert.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$f = 'alert';
  const DATA_KEY$a = 'bs.alert';
  const EVENT_KEY$b = `.${DATA_KEY$a}`;
  const EVENT_CLOSE = `close${EVENT_KEY$b}`;
  const EVENT_CLOSED = `closed${EVENT_KEY$b}`;
  const CLASS_NAME_FADE$5 = 'fade';
  const CLASS_NAME_SHOW$8 = 'show';

  /**
   * Class definition
   */

  class Alert extends BaseComponent {
    // Getters
    static get NAME() {
      return NAME$f;
    }

    // Public
    close() {
      const closeEvent = EventHandler.trigger(this._element, EVENT_CLOSE);
      if (closeEvent.defaultPrevented) {
        return;
      }
      this._element.classList.remove(CLASS_NAME_SHOW$8);
      const isAnimated = this._element.classList.contains(CLASS_NAME_FADE$5);
      this._queueCallback(() => this._destroyElement(), this._element, isAnimated);
    }

    // Private
    _destroyElement() {
      this._element.remove();
      EventHandler.trigger(this._element, EVENT_CLOSED);
      this.dispose();
    }

    // Static
    static jQueryInterface(config) {
      return this.each(function () {
        const data = Alert.getOrCreateInstance(this);
        if (typeof config !== 'string') {
          return;
        }
        if (data[config] === undefined || config.startsWith('_') || config === 'constructor') {
          throw new TypeError(`No method named "${config}"`);
        }
        data[config](this);
      });
    }
  }

  /**
   * Data API implementation
   */

  enableDismissTrigger(Alert, 'close');

  /**
   * jQuery
   */

  defineJQueryPlugin(Alert);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap button.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$e = 'button';
  const DATA_KEY$9 = 'bs.button';
  const EVENT_KEY$a = `.${DATA_KEY$9}`;
  const DATA_API_KEY$6 = '.data-api';
  const CLASS_NAME_ACTIVE$3 = 'active';
  const SELECTOR_DATA_TOGGLE$5 = '[data-bs-toggle="button"]';
  const EVENT_CLICK_DATA_API$6 = `click${EVENT_KEY$a}${DATA_API_KEY$6}`;

  /**
   * Class definition
   */

  class Button extends BaseComponent {
    // Getters
    static get NAME() {
      return NAME$e;
    }

    // Public
    toggle() {
      // Toggle class and sync the `aria-pressed` attribute with the return value of the `.toggle()` method
      this._element.setAttribute('aria-pressed', this._element.classList.toggle(CLASS_NAME_ACTIVE$3));
    }

    // Static
    static jQueryInterface(config) {
      return this.each(function () {
        const data = Button.getOrCreateInstance(this);
        if (config === 'toggle') {
          data[config]();
        }
      });
    }
  }

  /**
   * Data API implementation
   */

  EventHandler.on(document, EVENT_CLICK_DATA_API$6, SELECTOR_DATA_TOGGLE$5, event => {
    event.preventDefault();
    const button = event.target.closest(SELECTOR_DATA_TOGGLE$5);
    const data = Button.getOrCreateInstance(button);
    data.toggle();
  });

  /**
   * jQuery
   */

  defineJQueryPlugin(Button);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap util/swipe.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$d = 'swipe';
  const EVENT_KEY$9 = '.bs.swipe';
  const EVENT_TOUCHSTART = `touchstart${EVENT_KEY$9}`;
  const EVENT_TOUCHMOVE = `touchmove${EVENT_KEY$9}`;
  const EVENT_TOUCHEND = `touchend${EVENT_KEY$9}`;
  const EVENT_POINTERDOWN = `pointerdown${EVENT_KEY$9}`;
  const EVENT_POINTERUP = `pointerup${EVENT_KEY$9}`;
  const POINTER_TYPE_TOUCH = 'touch';
  const POINTER_TYPE_PEN = 'pen';
  const CLASS_NAME_POINTER_EVENT = 'pointer-event';
  const SWIPE_THRESHOLD = 40;
  const Default$c = {
    endCallback: null,
    leftCallback: null,
    rightCallback: null
  };
  const DefaultType$c = {
    endCallback: '(function|null)',
    leftCallback: '(function|null)',
    rightCallback: '(function|null)'
  };

  /**
   * Class definition
   */

  class Swipe extends Config {
    constructor(element, config) {
      super();
      this._element = element;
      if (!element || !Swipe.isSupported()) {
        return;
      }
      this._config = this._getConfig(config);
      this._deltaX = 0;
      this._supportPointerEvents = Boolean(window.PointerEvent);
      this._initEvents();
    }

    // Getters
    static get Default() {
      return Default$c;
    }
    static get DefaultType() {
      return DefaultType$c;
    }
    static get NAME() {
      return NAME$d;
    }

    // Public
    dispose() {
      EventHandler.off(this._element, EVENT_KEY$9);
    }

    // Private
    _start(event) {
      if (!this._supportPointerEvents) {
        this._deltaX = event.touches[0].clientX;
        return;
      }
      if (this._eventIsPointerPenTouch(event)) {
        this._deltaX = event.clientX;
      }
    }
    _end(event) {
      if (this._eventIsPointerPenTouch(event)) {
        this._deltaX = event.clientX - this._deltaX;
      }
      this._handleSwipe();
      execute(this._config.endCallback);
    }
    _move(event) {
      this._deltaX = event.touches && event.touches.length > 1 ? 0 : event.touches[0].clientX - this._deltaX;
    }
    _handleSwipe() {
      const absDeltaX = Math.abs(this._deltaX);
      if (absDeltaX <= SWIPE_THRESHOLD) {
        return;
      }
      const direction = absDeltaX / this._deltaX;
      this._deltaX = 0;
      if (!direction) {
        return;
      }
      execute(direction > 0 ? this._config.rightCallback : this._config.leftCallback);
    }
    _initEvents() {
      if (this._supportPointerEvents) {
        EventHandler.on(this._element, EVENT_POINTERDOWN, event => this._start(event));
        EventHandler.on(this._element, EVENT_POINTERUP, event => this._end(event));
        this._element.classList.add(CLASS_NAME_POINTER_EVENT);
      } else {
        EventHandler.on(this._element, EVENT_TOUCHSTART, event => this._start(event));
        EventHandler.on(this._element, EVENT_TOUCHMOVE, event => this._move(event));
        EventHandler.on(this._element, EVENT_TOUCHEND, event => this._end(event));
      }
    }
    _eventIsPointerPenTouch(event) {
      return this._supportPointerEvents && (event.pointerType === POINTER_TYPE_PEN || event.pointerType === POINTER_TYPE_TOUCH);
    }

    // Static
    static isSupported() {
      return 'ontouchstart' in document.documentElement || navigator.maxTouchPoints > 0;
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap carousel.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$c = 'carousel';
  const DATA_KEY$8 = 'bs.carousel';
  const EVENT_KEY$8 = `.${DATA_KEY$8}`;
  const DATA_API_KEY$5 = '.data-api';
  const ARROW_LEFT_KEY$1 = 'ArrowLeft';
  const ARROW_RIGHT_KEY$1 = 'ArrowRight';
  const TOUCHEVENT_COMPAT_WAIT = 500; // Time for mouse compat events to fire after touch

  const ORDER_NEXT = 'next';
  const ORDER_PREV = 'prev';
  const DIRECTION_LEFT = 'left';
  const DIRECTION_RIGHT = 'right';
  const EVENT_SLIDE = `slide${EVENT_KEY$8}`;
  const EVENT_SLID = `slid${EVENT_KEY$8}`;
  const EVENT_KEYDOWN$1 = `keydown${EVENT_KEY$8}`;
  const EVENT_MOUSEENTER$1 = `mouseenter${EVENT_KEY$8}`;
  const EVENT_MOUSELEAVE$1 = `mouseleave${EVENT_KEY$8}`;
  const EVENT_DRAG_START = `dragstart${EVENT_KEY$8}`;
  const EVENT_LOAD_DATA_API$3 = `load${EVENT_KEY$8}${DATA_API_KEY$5}`;
  const EVENT_CLICK_DATA_API$5 = `click${EVENT_KEY$8}${DATA_API_KEY$5}`;
  const CLASS_NAME_CAROUSEL = 'carousel';
  const CLASS_NAME_ACTIVE$2 = 'active';
  const CLASS_NAME_SLIDE = 'slide';
  const CLASS_NAME_END = 'carousel-item-end';
  const CLASS_NAME_START = 'carousel-item-start';
  const CLASS_NAME_NEXT = 'carousel-item-next';
  const CLASS_NAME_PREV = 'carousel-item-prev';
  const SELECTOR_ACTIVE = '.active';
  const SELECTOR_ITEM = '.carousel-item';
  const SELECTOR_ACTIVE_ITEM = SELECTOR_ACTIVE + SELECTOR_ITEM;
  const SELECTOR_ITEM_IMG = '.carousel-item img';
  const SELECTOR_INDICATORS = '.carousel-indicators';
  const SELECTOR_DATA_SLIDE = '[data-bs-slide], [data-bs-slide-to]';
  const SELECTOR_DATA_RIDE = '[data-bs-ride="carousel"]';
  const KEY_TO_DIRECTION = {
    [ARROW_LEFT_KEY$1]: DIRECTION_RIGHT,
    [ARROW_RIGHT_KEY$1]: DIRECTION_LEFT
  };
  const Default$b = {
    interval: 5000,
    keyboard: true,
    pause: 'hover',
    ride: false,
    touch: true,
    wrap: true
  };
  const DefaultType$b = {
    interval: '(number|boolean)',
    // TODO:v6 remove boolean support
    keyboard: 'boolean',
    pause: '(string|boolean)',
    ride: '(boolean|string)',
    touch: 'boolean',
    wrap: 'boolean'
  };

  /**
   * Class definition
   */

  class Carousel extends BaseComponent {
    constructor(element, config) {
      super(element, config);
      this._interval = null;
      this._activeElement = null;
      this._isSliding = false;
      this.touchTimeout = null;
      this._swipeHelper = null;
      this._indicatorsElement = SelectorEngine.findOne(SELECTOR_INDICATORS, this._element);
      this._addEventListeners();
      if (this._config.ride === CLASS_NAME_CAROUSEL) {
        this.cycle();
      }
    }

    // Getters
    static get Default() {
      return Default$b;
    }
    static get DefaultType() {
      return DefaultType$b;
    }
    static get NAME() {
      return NAME$c;
    }

    // Public
    next() {
      this._slide(ORDER_NEXT);
    }
    nextWhenVisible() {
      // FIXME TODO use `document.visibilityState`
      // Don't call next when the page isn't visible
      // or the carousel or its parent isn't visible
      if (!document.hidden && isVisible(this._element)) {
        this.next();
      }
    }
    prev() {
      this._slide(ORDER_PREV);
    }
    pause() {
      if (this._isSliding) {
        triggerTransitionEnd(this._element);
      }
      this._clearInterval();
    }
    cycle() {
      this._clearInterval();
      this._updateInterval();
      this._interval = setInterval(() => this.nextWhenVisible(), this._config.interval);
    }
    _maybeEnableCycle() {
      if (!this._config.ride) {
        return;
      }
      if (this._isSliding) {
        EventHandler.one(this._element, EVENT_SLID, () => this.cycle());
        return;
      }
      this.cycle();
    }
    to(index) {
      const items = this._getItems();
      if (index > items.length - 1 || index < 0) {
        return;
      }
      if (this._isSliding) {
        EventHandler.one(this._element, EVENT_SLID, () => this.to(index));
        return;
      }
      const activeIndex = this._getItemIndex(this._getActive());
      if (activeIndex === index) {
        return;
      }
      const order = index > activeIndex ? ORDER_NEXT : ORDER_PREV;
      this._slide(order, items[index]);
    }
    dispose() {
      if (this._swipeHelper) {
        this._swipeHelper.dispose();
      }
      super.dispose();
    }

    // Private
    _configAfterMerge(config) {
      config.defaultInterval = config.interval;
      return config;
    }
    _addEventListeners() {
      if (this._config.keyboard) {
        EventHandler.on(this._element, EVENT_KEYDOWN$1, event => this._keydown(event));
      }
      if (this._config.pause === 'hover') {
        EventHandler.on(this._element, EVENT_MOUSEENTER$1, () => this.pause());
        EventHandler.on(this._element, EVENT_MOUSELEAVE$1, () => this._maybeEnableCycle());
      }
      if (this._config.touch && Swipe.isSupported()) {
        this._addTouchEventListeners();
      }
    }
    _addTouchEventListeners() {
      for (const img of SelectorEngine.find(SELECTOR_ITEM_IMG, this._element)) {
        EventHandler.on(img, EVENT_DRAG_START, event => event.preventDefault());
      }
      const endCallBack = () => {
        if (this._config.pause !== 'hover') {
          return;
        }

        // If it's a touch-enabled device, mouseenter/leave are fired as
        // part of the mouse compatibility events on first tap - the carousel
        // would stop cycling until user tapped out of it;
        // here, we listen for touchend, explicitly pause the carousel
        // (as if it's the second time we tap on it, mouseenter compat event
        // is NOT fired) and after a timeout (to allow for mouse compatibility
        // events to fire) we explicitly restart cycling

        this.pause();
        if (this.touchTimeout) {
          clearTimeout(this.touchTimeout);
        }
        this.touchTimeout = setTimeout(() => this._maybeEnableCycle(), TOUCHEVENT_COMPAT_WAIT + this._config.interval);
      };
      const swipeConfig = {
        leftCallback: () => this._slide(this._directionToOrder(DIRECTION_LEFT)),
        rightCallback: () => this._slide(this._directionToOrder(DIRECTION_RIGHT)),
        endCallback: endCallBack
      };
      this._swipeHelper = new Swipe(this._element, swipeConfig);
    }
    _keydown(event) {
      if (/input|textarea/i.test(event.target.tagName)) {
        return;
      }
      const direction = KEY_TO_DIRECTION[event.key];
      if (direction) {
        event.preventDefault();
        this._slide(this._directionToOrder(direction));
      }
    }
    _getItemIndex(element) {
      return this._getItems().indexOf(element);
    }
    _setActiveIndicatorElement(index) {
      if (!this._indicatorsElement) {
        return;
      }
      const activeIndicator = SelectorEngine.findOne(SELECTOR_ACTIVE, this._indicatorsElement);
      activeIndicator.classList.remove(CLASS_NAME_ACTIVE$2);
      activeIndicator.removeAttribute('aria-current');
      const newActiveIndicator = SelectorEngine.findOne(`[data-bs-slide-to="${index}"]`, this._indicatorsElement);
      if (newActiveIndicator) {
        newActiveIndicator.classList.add(CLASS_NAME_ACTIVE$2);
        newActiveIndicator.setAttribute('aria-current', 'true');
      }
    }
    _updateInterval() {
      const element = this._activeElement || this._getActive();
      if (!element) {
        return;
      }
      const elementInterval = Number.parseInt(element.getAttribute('data-bs-interval'), 10);
      this._config.interval = elementInterval || this._config.defaultInterval;
    }
    _slide(order, element = null) {
      if (this._isSliding) {
        return;
      }
      const activeElement = this._getActive();
      const isNext = order === ORDER_NEXT;
      const nextElement = element || getNextActiveElement(this._getItems(), activeElement, isNext, this._config.wrap);
      if (nextElement === activeElement) {
        return;
      }
      const nextElementIndex = this._getItemIndex(nextElement);
      const triggerEvent = eventName => {
        return EventHandler.trigger(this._element, eventName, {
          relatedTarget: nextElement,
          direction: this._orderToDirection(order),
          from: this._getItemIndex(activeElement),
          to: nextElementIndex
        });
      };
      const slideEvent = triggerEvent(EVENT_SLIDE);
      if (slideEvent.defaultPrevented) {
        return;
      }
      if (!activeElement || !nextElement) {
        // Some weirdness is happening, so we bail
        // TODO: change tests that use empty divs to avoid this check
        return;
      }
      const isCycling = Boolean(this._interval);
      this.pause();
      this._isSliding = true;
      this._setActiveIndicatorElement(nextElementIndex);
      this._activeElement = nextElement;
      const directionalClassName = isNext ? CLASS_NAME_START : CLASS_NAME_END;
      const orderClassName = isNext ? CLASS_NAME_NEXT : CLASS_NAME_PREV;
      nextElement.classList.add(orderClassName);
      reflow(nextElement);
      activeElement.classList.add(directionalClassName);
      nextElement.classList.add(directionalClassName);
      const completeCallBack = () => {
        nextElement.classList.remove(directionalClassName, orderClassName);
        nextElement.classList.add(CLASS_NAME_ACTIVE$2);
        activeElement.classList.remove(CLASS_NAME_ACTIVE$2, orderClassName, directionalClassName);
        this._isSliding = false;
        triggerEvent(EVENT_SLID);
      };
      this._queueCallback(completeCallBack, activeElement, this._isAnimated());
      if (isCycling) {
        this.cycle();
      }
    }
    _isAnimated() {
      return this._element.classList.contains(CLASS_NAME_SLIDE);
    }
    _getActive() {
      return SelectorEngine.findOne(SELECTOR_ACTIVE_ITEM, this._element);
    }
    _getItems() {
      return SelectorEngine.find(SELECTOR_ITEM, this._element);
    }
    _clearInterval() {
      if (this._interval) {
        clearInterval(this._interval);
        this._interval = null;
      }
    }
    _directionToOrder(direction) {
      if (isRTL()) {
        return direction === DIRECTION_LEFT ? ORDER_PREV : ORDER_NEXT;
      }
      return direction === DIRECTION_LEFT ? ORDER_NEXT : ORDER_PREV;
    }
    _orderToDirection(order) {
      if (isRTL()) {
        return order === ORDER_PREV ? DIRECTION_LEFT : DIRECTION_RIGHT;
      }
      return order === ORDER_PREV ? DIRECTION_RIGHT : DIRECTION_LEFT;
    }

    // Static
    static jQueryInterface(config) {
      return this.each(function () {
        const data = Carousel.getOrCreateInstance(this, config);
        if (typeof config === 'number') {
          data.to(config);
          return;
        }
        if (typeof config === 'string') {
          if (data[config] === undefined || config.startsWith('_') || config === 'constructor') {
            throw new TypeError(`No method named "${config}"`);
          }
          data[config]();
        }
      });
    }
  }

  /**
   * Data API implementation
   */

  EventHandler.on(document, EVENT_CLICK_DATA_API$5, SELECTOR_DATA_SLIDE, function (event) {
    const target = SelectorEngine.getElementFromSelector(this);
    if (!target || !target.classList.contains(CLASS_NAME_CAROUSEL)) {
      return;
    }
    event.preventDefault();
    const carousel = Carousel.getOrCreateInstance(target);
    const slideIndex = this.getAttribute('data-bs-slide-to');
    if (slideIndex) {
      carousel.to(slideIndex);
      carousel._maybeEnableCycle();
      return;
    }
    if (Manipulator.getDataAttribute(this, 'slide') === 'next') {
      carousel.next();
      carousel._maybeEnableCycle();
      return;
    }
    carousel.prev();
    carousel._maybeEnableCycle();
  });
  EventHandler.on(window, EVENT_LOAD_DATA_API$3, () => {
    const carousels = SelectorEngine.find(SELECTOR_DATA_RIDE);
    for (const carousel of carousels) {
      Carousel.getOrCreateInstance(carousel);
    }
  });

  /**
   * jQuery
   */

  defineJQueryPlugin(Carousel);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap collapse.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$b = 'collapse';
  const DATA_KEY$7 = 'bs.collapse';
  const EVENT_KEY$7 = `.${DATA_KEY$7}`;
  const DATA_API_KEY$4 = '.data-api';
  const EVENT_SHOW$6 = `show${EVENT_KEY$7}`;
  const EVENT_SHOWN$6 = `shown${EVENT_KEY$7}`;
  const EVENT_HIDE$6 = `hide${EVENT_KEY$7}`;
  const EVENT_HIDDEN$6 = `hidden${EVENT_KEY$7}`;
  const EVENT_CLICK_DATA_API$4 = `click${EVENT_KEY$7}${DATA_API_KEY$4}`;
  const CLASS_NAME_SHOW$7 = 'show';
  const CLASS_NAME_COLLAPSE = 'collapse';
  const CLASS_NAME_COLLAPSING = 'collapsing';
  const CLASS_NAME_COLLAPSED = 'collapsed';
  const CLASS_NAME_DEEPER_CHILDREN = `:scope .${CLASS_NAME_COLLAPSE} .${CLASS_NAME_COLLAPSE}`;
  const CLASS_NAME_HORIZONTAL = 'collapse-horizontal';
  const WIDTH = 'width';
  const HEIGHT = 'height';
  const SELECTOR_ACTIVES = '.collapse.show, .collapse.collapsing';
  const SELECTOR_DATA_TOGGLE$4 = '[data-bs-toggle="collapse"]';
  const Default$a = {
    parent: null,
    toggle: true
  };
  const DefaultType$a = {
    parent: '(null|element)',
    toggle: 'boolean'
  };

  /**
   * Class definition
   */

  class Collapse extends BaseComponent {
    constructor(element, config) {
      super(element, config);
      this._isTransitioning = false;
      this._triggerArray = [];
      const toggleList = SelectorEngine.find(SELECTOR_DATA_TOGGLE$4);
      for (const elem of toggleList) {
        const selector = SelectorEngine.getSelectorFromElement(elem);
        const filterElement = SelectorEngine.find(selector).filter(foundElement => foundElement === this._element);
        if (selector !== null && filterElement.length) {
          this._triggerArray.push(elem);
        }
      }
      this._initializeChildren();
      if (!this._config.parent) {
        this._addAriaAndCollapsedClass(this._triggerArray, this._isShown());
      }
      if (this._config.toggle) {
        this.toggle();
      }
    }

    // Getters
    static get Default() {
      return Default$a;
    }
    static get DefaultType() {
      return DefaultType$a;
    }
    static get NAME() {
      return NAME$b;
    }

    // Public
    toggle() {
      if (this._isShown()) {
        this.hide();
      } else {
        this.show();
      }
    }
    show() {
      if (this._isTransitioning || this._isShown()) {
        return;
      }
      let activeChildren = [];

      // find active children
      if (this._config.parent) {
        activeChildren = this._getFirstLevelChildren(SELECTOR_ACTIVES).filter(element => element !== this._element).map(element => Collapse.getOrCreateInstance(element, {
          toggle: false
        }));
      }
      if (activeChildren.length && activeChildren[0]._isTransitioning) {
        return;
      }
      const startEvent = EventHandler.trigger(this._element, EVENT_SHOW$6);
      if (startEvent.defaultPrevented) {
        return;
      }
      for (const activeInstance of activeChildren) {
        activeInstance.hide();
      }
      const dimension = this._getDimension();
      this._element.classList.remove(CLASS_NAME_COLLAPSE);
      this._element.classList.add(CLASS_NAME_COLLAPSING);
      this._element.style[dimension] = 0;
      this._addAriaAndCollapsedClass(this._triggerArray, true);
      this._isTransitioning = true;
      const complete = () => {
        this._isTransitioning = false;
        this._element.classList.remove(CLASS_NAME_COLLAPSING);
        this._element.classList.add(CLASS_NAME_COLLAPSE, CLASS_NAME_SHOW$7);
        this._element.style[dimension] = '';
        EventHandler.trigger(this._element, EVENT_SHOWN$6);
      };
      const capitalizedDimension = dimension[0].toUpperCase() + dimension.slice(1);
      const scrollSize = `scroll${capitalizedDimension}`;
      this._queueCallback(complete, this._element, true);
      this._element.style[dimension] = `${this._element[scrollSize]}px`;
    }
    hide() {
      if (this._isTransitioning || !this._isShown()) {
        return;
      }
      const startEvent = EventHandler.trigger(this._element, EVENT_HIDE$6);
      if (startEvent.defaultPrevented) {
        return;
      }
      const dimension = this._getDimension();
      this._element.style[dimension] = `${this._element.getBoundingClientRect()[dimension]}px`;
      reflow(this._element);
      this._element.classList.add(CLASS_NAME_COLLAPSING);
      this._element.classList.remove(CLASS_NAME_COLLAPSE, CLASS_NAME_SHOW$7);
      for (const trigger of this._triggerArray) {
        const element = SelectorEngine.getElementFromSelector(trigger);
        if (element && !this._isShown(element)) {
          this._addAriaAndCollapsedClass([trigger], false);
        }
      }
      this._isTransitioning = true;
      const complete = () => {
        this._isTransitioning = false;
        this._element.classList.remove(CLASS_NAME_COLLAPSING);
        this._element.classList.add(CLASS_NAME_COLLAPSE);
        EventHandler.trigger(this._element, EVENT_HIDDEN$6);
      };
      this._element.style[dimension] = '';
      this._queueCallback(complete, this._element, true);
    }
    _isShown(element = this._element) {
      return element.classList.contains(CLASS_NAME_SHOW$7);
    }

    // Private
    _configAfterMerge(config) {
      config.toggle = Boolean(config.toggle); // Coerce string values
      config.parent = getElement(config.parent);
      return config;
    }
    _getDimension() {
      return this._element.classList.contains(CLASS_NAME_HORIZONTAL) ? WIDTH : HEIGHT;
    }
    _initializeChildren() {
      if (!this._config.parent) {
        return;
      }
      const children = this._getFirstLevelChildren(SELECTOR_DATA_TOGGLE$4);
      for (const element of children) {
        const selected = SelectorEngine.getElementFromSelector(element);
        if (selected) {
          this._addAriaAndCollapsedClass([element], this._isShown(selected));
        }
      }
    }
    _getFirstLevelChildren(selector) {
      const children = SelectorEngine.find(CLASS_NAME_DEEPER_CHILDREN, this._config.parent);
      // remove children if greater depth
      return SelectorEngine.find(selector, this._config.parent).filter(element => !children.includes(element));
    }
    _addAriaAndCollapsedClass(triggerArray, isOpen) {
      if (!triggerArray.length) {
        return;
      }
      for (const element of triggerArray) {
        element.classList.toggle(CLASS_NAME_COLLAPSED, !isOpen);
        element.setAttribute('aria-expanded', isOpen);
      }
    }

    // Static
    static jQueryInterface(config) {
      const _config = {};
      if (typeof config === 'string' && /show|hide/.test(config)) {
        _config.toggle = false;
      }
      return this.each(function () {
        const data = Collapse.getOrCreateInstance(this, _config);
        if (typeof config === 'string') {
          if (typeof data[config] === 'undefined') {
            throw new TypeError(`No method named "${config}"`);
          }
          data[config]();
        }
      });
    }
  }

  /**
   * Data API implementation
   */

  EventHandler.on(document, EVENT_CLICK_DATA_API$4, SELECTOR_DATA_TOGGLE$4, function (event) {
    // preventDefault only for <a> elements (which change the URL) not inside the collapsible element
    if (event.target.tagName === 'A' || event.delegateTarget && event.delegateTarget.tagName === 'A') {
      event.preventDefault();
    }
    for (const element of SelectorEngine.getMultipleElementsFromSelector(this)) {
      Collapse.getOrCreateInstance(element, {
        toggle: false
      }).toggle();
    }
  });

  /**
   * jQuery
   */

  defineJQueryPlugin(Collapse);

  var top = 'top';
  var bottom = 'bottom';
  var right = 'right';
  var left = 'left';
  var auto = 'auto';
  var basePlacements = [top, bottom, right, left];
  var start = 'start';
  var end = 'end';
  var clippingParents = 'clippingParents';
  var viewport = 'viewport';
  var popper = 'popper';
  var reference = 'reference';
  var variationPlacements = /*#__PURE__*/basePlacements.reduce(function (acc, placement) {
    return acc.concat([placement + "-" + start, placement + "-" + end]);
  }, []);
  var placements = /*#__PURE__*/[].concat(basePlacements, [auto]).reduce(function (acc, placement) {
    return acc.concat([placement, placement + "-" + start, placement + "-" + end]);
  }, []); // modifiers that need to read the DOM

  var beforeRead = 'beforeRead';
  var read = 'read';
  var afterRead = 'afterRead'; // pure-logic modifiers

  var beforeMain = 'beforeMain';
  var main = 'main';
  var afterMain = 'afterMain'; // modifier with the purpose to write to the DOM (or write into a framework state)

  var beforeWrite = 'beforeWrite';
  var write = 'write';
  var afterWrite = 'afterWrite';
  var modifierPhases = [beforeRead, read, afterRead, beforeMain, main, afterMain, beforeWrite, write, afterWrite];

  function getNodeName(element) {
    return element ? (element.nodeName || '').toLowerCase() : null;
  }

  function getWindow(node) {
    if (node == null) {
      return window;
    }

    if (node.toString() !== '[object Window]') {
      var ownerDocument = node.ownerDocument;
      return ownerDocument ? ownerDocument.defaultView || window : window;
    }

    return node;
  }

  function isElement(node) {
    var OwnElement = getWindow(node).Element;
    return node instanceof OwnElement || node instanceof Element;
  }

  function isHTMLElement(node) {
    var OwnElement = getWindow(node).HTMLElement;
    return node instanceof OwnElement || node instanceof HTMLElement;
  }

  function isShadowRoot(node) {
    // IE 11 has no ShadowRoot
    if (typeof ShadowRoot === 'undefined') {
      return false;
    }

    var OwnElement = getWindow(node).ShadowRoot;
    return node instanceof OwnElement || node instanceof ShadowRoot;
  }

  // and applies them to the HTMLElements such as popper and arrow

  function applyStyles(_ref) {
    var state = _ref.state;
    Object.keys(state.elements).forEach(function (name) {
      var style = state.styles[name] || {};
      var attributes = state.attributes[name] || {};
      var element = state.elements[name]; // arrow is optional + virtual elements

      if (!isHTMLElement(element) || !getNodeName(element)) {
        return;
      } // Flow doesn't support to extend this property, but it's the most
      // effective way to apply styles to an HTMLElement
      // $FlowFixMe[cannot-write]


      Object.assign(element.style, style);
      Object.keys(attributes).forEach(function (name) {
        var value = attributes[name];

        if (value === false) {
          element.removeAttribute(name);
        } else {
          element.setAttribute(name, value === true ? '' : value);
        }
      });
    });
  }

  function effect$2(_ref2) {
    var state = _ref2.state;
    var initialStyles = {
      popper: {
        position: state.options.strategy,
        left: '0',
        top: '0',
        margin: '0'
      },
      arrow: {
        position: 'absolute'
      },
      reference: {}
    };
    Object.assign(state.elements.popper.style, initialStyles.popper);
    state.styles = initialStyles;

    if (state.elements.arrow) {
      Object.assign(state.elements.arrow.style, initialStyles.arrow);
    }

    return function () {
      Object.keys(state.elements).forEach(function (name) {
        var element = state.elements[name];
        var attributes = state.attributes[name] || {};
        var styleProperties = Object.keys(state.styles.hasOwnProperty(name) ? state.styles[name] : initialStyles[name]); // Set all values to an empty string to unset them

        var style = styleProperties.reduce(function (style, property) {
          style[property] = '';
          return style;
        }, {}); // arrow is optional + virtual elements

        if (!isHTMLElement(element) || !getNodeName(element)) {
          return;
        }

        Object.assign(element.style, style);
        Object.keys(attributes).forEach(function (attribute) {
          element.removeAttribute(attribute);
        });
      });
    };
  } // eslint-disable-next-line import/no-unused-modules


  const applyStyles$1 = {
    name: 'applyStyles',
    enabled: true,
    phase: 'write',
    fn: applyStyles,
    effect: effect$2,
    requires: ['computeStyles']
  };

  function getBasePlacement(placement) {
    return placement.split('-')[0];
  }

  var max = Math.max;
  var min = Math.min;
  var round = Math.round;

  function getUAString() {
    var uaData = navigator.userAgentData;

    if (uaData != null && uaData.brands && Array.isArray(uaData.brands)) {
      return uaData.brands.map(function (item) {
        return item.brand + "/" + item.version;
      }).join(' ');
    }

    return navigator.userAgent;
  }

  function isLayoutViewport() {
    return !/^((?!chrome|android).)*safari/i.test(getUAString());
  }

  function getBoundingClientRect(element, includeScale, isFixedStrategy) {
    if (includeScale === void 0) {
      includeScale = false;
    }

    if (isFixedStrategy === void 0) {
      isFixedStrategy = false;
    }

    var clientRect = element.getBoundingClientRect();
    var scaleX = 1;
    var scaleY = 1;

    if (includeScale && isHTMLElement(element)) {
      scaleX = element.offsetWidth > 0 ? round(clientRect.width) / element.offsetWidth || 1 : 1;
      scaleY = element.offsetHeight > 0 ? round(clientRect.height) / element.offsetHeight || 1 : 1;
    }

    var _ref = isElement(element) ? getWindow(element) : window,
        visualViewport = _ref.visualViewport;

    var addVisualOffsets = !isLayoutViewport() && isFixedStrategy;
    var x = (clientRect.left + (addVisualOffsets && visualViewport ? visualViewport.offsetLeft : 0)) / scaleX;
    var y = (clientRect.top + (addVisualOffsets && visualViewport ? visualViewport.offsetTop : 0)) / scaleY;
    var width = clientRect.width / scaleX;
    var height = clientRect.height / scaleY;
    return {
      width: width,
      height: height,
      top: y,
      right: x + width,
      bottom: y + height,
      left: x,
      x: x,
      y: y
    };
  }

  // means it doesn't take into account transforms.

  function getLayoutRect(element) {
    var clientRect = getBoundingClientRect(element); // Use the clientRect sizes if it's not been transformed.
    // Fixes https://github.com/popperjs/popper-core/issues/1223

    var width = element.offsetWidth;
    var height = element.offsetHeight;

    if (Math.abs(clientRect.width - width) <= 1) {
      width = clientRect.width;
    }

    if (Math.abs(clientRect.height - height) <= 1) {
      height = clientRect.height;
    }

    return {
      x: element.offsetLeft,
      y: element.offsetTop,
      width: width,
      height: height
    };
  }

  function contains(parent, child) {
    var rootNode = child.getRootNode && child.getRootNode(); // First, attempt with faster native method

    if (parent.contains(child)) {
      return true;
    } // then fallback to custom implementation with Shadow DOM support
    else if (rootNode && isShadowRoot(rootNode)) {
        var next = child;

        do {
          if (next && parent.isSameNode(next)) {
            return true;
          } // $FlowFixMe[prop-missing]: need a better way to handle this...


          next = next.parentNode || next.host;
        } while (next);
      } // Give up, the result is false


    return false;
  }

  function getComputedStyle$1(element) {
    return getWindow(element).getComputedStyle(element);
  }

  function isTableElement(element) {
    return ['table', 'td', 'th'].indexOf(getNodeName(element)) >= 0;
  }

  function getDocumentElement(element) {
    // $FlowFixMe[incompatible-return]: assume body is always available
    return ((isElement(element) ? element.ownerDocument : // $FlowFixMe[prop-missing]
    element.document) || window.document).documentElement;
  }

  function getParentNode(element) {
    if (getNodeName(element) === 'html') {
      return element;
    }

    return (// this is a quicker (but less type safe) way to save quite some bytes from the bundle
      // $FlowFixMe[incompatible-return]
      // $FlowFixMe[prop-missing]
      element.assignedSlot || // step into the shadow DOM of the parent of a slotted node
      element.parentNode || ( // DOM Element detected
      isShadowRoot(element) ? element.host : null) || // ShadowRoot detected
      // $FlowFixMe[incompatible-call]: HTMLElement is a Node
      getDocumentElement(element) // fallback

    );
  }

  function getTrueOffsetParent(element) {
    if (!isHTMLElement(element) || // https://github.com/popperjs/popper-core/issues/837
    getComputedStyle$1(element).position === 'fixed') {
      return null;
    }

    return element.offsetParent;
  } // `.offsetParent` reports `null` for fixed elements, while absolute elements
  // return the containing block


  function getContainingBlock(element) {
    var isFirefox = /firefox/i.test(getUAString());
    var isIE = /Trident/i.test(getUAString());

    if (isIE && isHTMLElement(element)) {
      // In IE 9, 10 and 11 fixed elements containing block is always established by the viewport
      var elementCss = getComputedStyle$1(element);

      if (elementCss.position === 'fixed') {
        return null;
      }
    }

    var currentNode = getParentNode(element);

    if (isShadowRoot(currentNode)) {
      currentNode = currentNode.host;
    }

    while (isHTMLElement(currentNode) && ['html', 'body'].indexOf(getNodeName(currentNode)) < 0) {
      var css = getComputedStyle$1(currentNode); // This is non-exhaustive but covers the most common CSS properties that
      // create a containing block.
      // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block

      if (css.transform !== 'none' || css.perspective !== 'none' || css.contain === 'paint' || ['transform', 'perspective'].indexOf(css.willChange) !== -1 || isFirefox && css.willChange === 'filter' || isFirefox && css.filter && css.filter !== 'none') {
        return currentNode;
      } else {
        currentNode = currentNode.parentNode;
      }
    }

    return null;
  } // Gets the closest ancestor positioned element. Handles some edge cases,
  // such as table ancestors and cross browser bugs.


  function getOffsetParent(element) {
    var window = getWindow(element);
    var offsetParent = getTrueOffsetParent(element);

    while (offsetParent && isTableElement(offsetParent) && getComputedStyle$1(offsetParent).position === 'static') {
      offsetParent = getTrueOffsetParent(offsetParent);
    }

    if (offsetParent && (getNodeName(offsetParent) === 'html' || getNodeName(offsetParent) === 'body' && getComputedStyle$1(offsetParent).position === 'static')) {
      return window;
    }

    return offsetParent || getContainingBlock(element) || window;
  }

  function getMainAxisFromPlacement(placement) {
    return ['top', 'bottom'].indexOf(placement) >= 0 ? 'x' : 'y';
  }

  function within(min$1, value, max$1) {
    return max(min$1, min(value, max$1));
  }
  function withinMaxClamp(min, value, max) {
    var v = within(min, value, max);
    return v > max ? max : v;
  }

  function getFreshSideObject() {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    };
  }

  function mergePaddingObject(paddingObject) {
    return Object.assign({}, getFreshSideObject(), paddingObject);
  }

  function expandToHashMap(value, keys) {
    return keys.reduce(function (hashMap, key) {
      hashMap[key] = value;
      return hashMap;
    }, {});
  }

  var toPaddingObject = function toPaddingObject(padding, state) {
    padding = typeof padding === 'function' ? padding(Object.assign({}, state.rects, {
      placement: state.placement
    })) : padding;
    return mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
  };

  function arrow(_ref) {
    var _state$modifiersData$;

    var state = _ref.state,
        name = _ref.name,
        options = _ref.options;
    var arrowElement = state.elements.arrow;
    var popperOffsets = state.modifiersData.popperOffsets;
    var basePlacement = getBasePlacement(state.placement);
    var axis = getMainAxisFromPlacement(basePlacement);
    var isVertical = [left, right].indexOf(basePlacement) >= 0;
    var len = isVertical ? 'height' : 'width';

    if (!arrowElement || !popperOffsets) {
      return;
    }

    var paddingObject = toPaddingObject(options.padding, state);
    var arrowRect = getLayoutRect(arrowElement);
    var minProp = axis === 'y' ? top : left;
    var maxProp = axis === 'y' ? bottom : right;
    var endDiff = state.rects.reference[len] + state.rects.reference[axis] - popperOffsets[axis] - state.rects.popper[len];
    var startDiff = popperOffsets[axis] - state.rects.reference[axis];
    var arrowOffsetParent = getOffsetParent(arrowElement);
    var clientSize = arrowOffsetParent ? axis === 'y' ? arrowOffsetParent.clientHeight || 0 : arrowOffsetParent.clientWidth || 0 : 0;
    var centerToReference = endDiff / 2 - startDiff / 2; // Make sure the arrow doesn't overflow the popper if the center point is
    // outside of the popper bounds

    var min = paddingObject[minProp];
    var max = clientSize - arrowRect[len] - paddingObject[maxProp];
    var center = clientSize / 2 - arrowRect[len] / 2 + centerToReference;
    var offset = within(min, center, max); // Prevents breaking syntax highlighting...

    var axisProp = axis;
    state.modifiersData[name] = (_state$modifiersData$ = {}, _state$modifiersData$[axisProp] = offset, _state$modifiersData$.centerOffset = offset - center, _state$modifiersData$);
  }

  function effect$1(_ref2) {
    var state = _ref2.state,
        options = _ref2.options;
    var _options$element = options.element,
        arrowElement = _options$element === void 0 ? '[data-popper-arrow]' : _options$element;

    if (arrowElement == null) {
      return;
    } // CSS selector


    if (typeof arrowElement === 'string') {
      arrowElement = state.elements.popper.querySelector(arrowElement);

      if (!arrowElement) {
        return;
      }
    }

    if (!contains(state.elements.popper, arrowElement)) {
      return;
    }

    state.elements.arrow = arrowElement;
  } // eslint-disable-next-line import/no-unused-modules


  const arrow$1 = {
    name: 'arrow',
    enabled: true,
    phase: 'main',
    fn: arrow,
    effect: effect$1,
    requires: ['popperOffsets'],
    requiresIfExists: ['preventOverflow']
  };

  function getVariation(placement) {
    return placement.split('-')[1];
  }

  var unsetSides = {
    top: 'auto',
    right: 'auto',
    bottom: 'auto',
    left: 'auto'
  }; // Round the offsets to the nearest suitable subpixel based on the DPR.
  // Zooming can change the DPR, but it seems to report a value that will
  // cleanly divide the values into the appropriate subpixels.

  function roundOffsetsByDPR(_ref, win) {
    var x = _ref.x,
        y = _ref.y;
    var dpr = win.devicePixelRatio || 1;
    return {
      x: round(x * dpr) / dpr || 0,
      y: round(y * dpr) / dpr || 0
    };
  }

  function mapToStyles(_ref2) {
    var _Object$assign2;

    var popper = _ref2.popper,
        popperRect = _ref2.popperRect,
        placement = _ref2.placement,
        variation = _ref2.variation,
        offsets = _ref2.offsets,
        position = _ref2.position,
        gpuAcceleration = _ref2.gpuAcceleration,
        adaptive = _ref2.adaptive,
        roundOffsets = _ref2.roundOffsets,
        isFixed = _ref2.isFixed;
    var _offsets$x = offsets.x,
        x = _offsets$x === void 0 ? 0 : _offsets$x,
        _offsets$y = offsets.y,
        y = _offsets$y === void 0 ? 0 : _offsets$y;

    var _ref3 = typeof roundOffsets === 'function' ? roundOffsets({
      x: x,
      y: y
    }) : {
      x: x,
      y: y
    };

    x = _ref3.x;
    y = _ref3.y;
    var hasX = offsets.hasOwnProperty('x');
    var hasY = offsets.hasOwnProperty('y');
    var sideX = left;
    var sideY = top;
    var win = window;

    if (adaptive) {
      var offsetParent = getOffsetParent(popper);
      var heightProp = 'clientHeight';
      var widthProp = 'clientWidth';

      if (offsetParent === getWindow(popper)) {
        offsetParent = getDocumentElement(popper);

        if (getComputedStyle$1(offsetParent).position !== 'static' && position === 'absolute') {
          heightProp = 'scrollHeight';
          widthProp = 'scrollWidth';
        }
      } // $FlowFixMe[incompatible-cast]: force type refinement, we compare offsetParent with window above, but Flow doesn't detect it


      offsetParent = offsetParent;

      if (placement === top || (placement === left || placement === right) && variation === end) {
        sideY = bottom;
        var offsetY = isFixed && offsetParent === win && win.visualViewport ? win.visualViewport.height : // $FlowFixMe[prop-missing]
        offsetParent[heightProp];
        y -= offsetY - popperRect.height;
        y *= gpuAcceleration ? 1 : -1;
      }

      if (placement === left || (placement === top || placement === bottom) && variation === end) {
        sideX = right;
        var offsetX = isFixed && offsetParent === win && win.visualViewport ? win.visualViewport.width : // $FlowFixMe[prop-missing]
        offsetParent[widthProp];
        x -= offsetX - popperRect.width;
        x *= gpuAcceleration ? 1 : -1;
      }
    }

    var commonStyles = Object.assign({
      position: position
    }, adaptive && unsetSides);

    var _ref4 = roundOffsets === true ? roundOffsetsByDPR({
      x: x,
      y: y
    }, getWindow(popper)) : {
      x: x,
      y: y
    };

    x = _ref4.x;
    y = _ref4.y;

    if (gpuAcceleration) {
      var _Object$assign;

      return Object.assign({}, commonStyles, (_Object$assign = {}, _Object$assign[sideY] = hasY ? '0' : '', _Object$assign[sideX] = hasX ? '0' : '', _Object$assign.transform = (win.devicePixelRatio || 1) <= 1 ? "translate(" + x + "px, " + y + "px)" : "translate3d(" + x + "px, " + y + "px, 0)", _Object$assign));
    }

    return Object.assign({}, commonStyles, (_Object$assign2 = {}, _Object$assign2[sideY] = hasY ? y + "px" : '', _Object$assign2[sideX] = hasX ? x + "px" : '', _Object$assign2.transform = '', _Object$assign2));
  }

  function computeStyles(_ref5) {
    var state = _ref5.state,
        options = _ref5.options;
    var _options$gpuAccelerat = options.gpuAcceleration,
        gpuAcceleration = _options$gpuAccelerat === void 0 ? true : _options$gpuAccelerat,
        _options$adaptive = options.adaptive,
        adaptive = _options$adaptive === void 0 ? true : _options$adaptive,
        _options$roundOffsets = options.roundOffsets,
        roundOffsets = _options$roundOffsets === void 0 ? true : _options$roundOffsets;
    var commonStyles = {
      placement: getBasePlacement(state.placement),
      variation: getVariation(state.placement),
      popper: state.elements.popper,
      popperRect: state.rects.popper,
      gpuAcceleration: gpuAcceleration,
      isFixed: state.options.strategy === 'fixed'
    };

    if (state.modifiersData.popperOffsets != null) {
      state.styles.popper = Object.assign({}, state.styles.popper, mapToStyles(Object.assign({}, commonStyles, {
        offsets: state.modifiersData.popperOffsets,
        position: state.options.strategy,
        adaptive: adaptive,
        roundOffsets: roundOffsets
      })));
    }

    if (state.modifiersData.arrow != null) {
      state.styles.arrow = Object.assign({}, state.styles.arrow, mapToStyles(Object.assign({}, commonStyles, {
        offsets: state.modifiersData.arrow,
        position: 'absolute',
        adaptive: false,
        roundOffsets: roundOffsets
      })));
    }

    state.attributes.popper = Object.assign({}, state.attributes.popper, {
      'data-popper-placement': state.placement
    });
  } // eslint-disable-next-line import/no-unused-modules


  const computeStyles$1 = {
    name: 'computeStyles',
    enabled: true,
    phase: 'beforeWrite',
    fn: computeStyles,
    data: {}
  };

  var passive = {
    passive: true
  };

  function effect(_ref) {
    var state = _ref.state,
        instance = _ref.instance,
        options = _ref.options;
    var _options$scroll = options.scroll,
        scroll = _options$scroll === void 0 ? true : _options$scroll,
        _options$resize = options.resize,
        resize = _options$resize === void 0 ? true : _options$resize;
    var window = getWindow(state.elements.popper);
    var scrollParents = [].concat(state.scrollParents.reference, state.scrollParents.popper);

    if (scroll) {
      scrollParents.forEach(function (scrollParent) {
        scrollParent.addEventListener('scroll', instance.update, passive);
      });
    }

    if (resize) {
      window.addEventListener('resize', instance.update, passive);
    }

    return function () {
      if (scroll) {
        scrollParents.forEach(function (scrollParent) {
          scrollParent.removeEventListener('scroll', instance.update, passive);
        });
      }

      if (resize) {
        window.removeEventListener('resize', instance.update, passive);
      }
    };
  } // eslint-disable-next-line import/no-unused-modules


  const eventListeners = {
    name: 'eventListeners',
    enabled: true,
    phase: 'write',
    fn: function fn() {},
    effect: effect,
    data: {}
  };

  var hash$1 = {
    left: 'right',
    right: 'left',
    bottom: 'top',
    top: 'bottom'
  };
  function getOppositePlacement(placement) {
    return placement.replace(/left|right|bottom|top/g, function (matched) {
      return hash$1[matched];
    });
  }

  var hash = {
    start: 'end',
    end: 'start'
  };
  function getOppositeVariationPlacement(placement) {
    return placement.replace(/start|end/g, function (matched) {
      return hash[matched];
    });
  }

  function getWindowScroll(node) {
    var win = getWindow(node);
    var scrollLeft = win.pageXOffset;
    var scrollTop = win.pageYOffset;
    return {
      scrollLeft: scrollLeft,
      scrollTop: scrollTop
    };
  }

  function getWindowScrollBarX(element) {
    // If <html> has a CSS width greater than the viewport, then this will be
    // incorrect for RTL.
    // Popper 1 is broken in this case and never had a bug report so let's assume
    // it's not an issue. I don't think anyone ever specifies width on <html>
    // anyway.
    // Browsers where the left scrollbar doesn't cause an issue report `0` for
    // this (e.g. Edge 2019, IE11, Safari)
    return getBoundingClientRect(getDocumentElement(element)).left + getWindowScroll(element).scrollLeft;
  }

  function getViewportRect(element, strategy) {
    var win = getWindow(element);
    var html = getDocumentElement(element);
    var visualViewport = win.visualViewport;
    var width = html.clientWidth;
    var height = html.clientHeight;
    var x = 0;
    var y = 0;

    if (visualViewport) {
      width = visualViewport.width;
      height = visualViewport.height;
      var layoutViewport = isLayoutViewport();

      if (layoutViewport || !layoutViewport && strategy === 'fixed') {
        x = visualViewport.offsetLeft;
        y = visualViewport.offsetTop;
      }
    }

    return {
      width: width,
      height: height,
      x: x + getWindowScrollBarX(element),
      y: y
    };
  }

  // of the `<html>` and `<body>` rect bounds if horizontally scrollable

  function getDocumentRect(element) {
    var _element$ownerDocumen;

    var html = getDocumentElement(element);
    var winScroll = getWindowScroll(element);
    var body = (_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body;
    var width = max(html.scrollWidth, html.clientWidth, body ? body.scrollWidth : 0, body ? body.clientWidth : 0);
    var height = max(html.scrollHeight, html.clientHeight, body ? body.scrollHeight : 0, body ? body.clientHeight : 0);
    var x = -winScroll.scrollLeft + getWindowScrollBarX(element);
    var y = -winScroll.scrollTop;

    if (getComputedStyle$1(body || html).direction === 'rtl') {
      x += max(html.clientWidth, body ? body.clientWidth : 0) - width;
    }

    return {
      width: width,
      height: height,
      x: x,
      y: y
    };
  }

  function isScrollParent(element) {
    // Firefox wants us to check `-x` and `-y` variations as well
    var _getComputedStyle = getComputedStyle$1(element),
        overflow = _getComputedStyle.overflow,
        overflowX = _getComputedStyle.overflowX,
        overflowY = _getComputedStyle.overflowY;

    return /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX);
  }

  function getScrollParent(node) {
    if (['html', 'body', '#document'].indexOf(getNodeName(node)) >= 0) {
      // $FlowFixMe[incompatible-return]: assume body is always available
      return node.ownerDocument.body;
    }

    if (isHTMLElement(node) && isScrollParent(node)) {
      return node;
    }

    return getScrollParent(getParentNode(node));
  }

  /*
  given a DOM element, return the list of all scroll parents, up the list of ancesors
  until we get to the top window object. This list is what we attach scroll listeners
  to, because if any of these parent elements scroll, we'll need to re-calculate the
  reference element's position.
  */

  function listScrollParents(element, list) {
    var _element$ownerDocumen;

    if (list === void 0) {
      list = [];
    }

    var scrollParent = getScrollParent(element);
    var isBody = scrollParent === ((_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body);
    var win = getWindow(scrollParent);
    var target = isBody ? [win].concat(win.visualViewport || [], isScrollParent(scrollParent) ? scrollParent : []) : scrollParent;
    var updatedList = list.concat(target);
    return isBody ? updatedList : // $FlowFixMe[incompatible-call]: isBody tells us target will be an HTMLElement here
    updatedList.concat(listScrollParents(getParentNode(target)));
  }

  function rectToClientRect(rect) {
    return Object.assign({}, rect, {
      left: rect.x,
      top: rect.y,
      right: rect.x + rect.width,
      bottom: rect.y + rect.height
    });
  }

  function getInnerBoundingClientRect(element, strategy) {
    var rect = getBoundingClientRect(element, false, strategy === 'fixed');
    rect.top = rect.top + element.clientTop;
    rect.left = rect.left + element.clientLeft;
    rect.bottom = rect.top + element.clientHeight;
    rect.right = rect.left + element.clientWidth;
    rect.width = element.clientWidth;
    rect.height = element.clientHeight;
    rect.x = rect.left;
    rect.y = rect.top;
    return rect;
  }

  function getClientRectFromMixedType(element, clippingParent, strategy) {
    return clippingParent === viewport ? rectToClientRect(getViewportRect(element, strategy)) : isElement(clippingParent) ? getInnerBoundingClientRect(clippingParent, strategy) : rectToClientRect(getDocumentRect(getDocumentElement(element)));
  } // A "clipping parent" is an overflowable container with the characteristic of
  // clipping (or hiding) overflowing elements with a position different from
  // `initial`


  function getClippingParents(element) {
    var clippingParents = listScrollParents(getParentNode(element));
    var canEscapeClipping = ['absolute', 'fixed'].indexOf(getComputedStyle$1(element).position) >= 0;
    var clipperElement = canEscapeClipping && isHTMLElement(element) ? getOffsetParent(element) : element;

    if (!isElement(clipperElement)) {
      return [];
    } // $FlowFixMe[incompatible-return]: https://github.com/facebook/flow/issues/1414


    return clippingParents.filter(function (clippingParent) {
      return isElement(clippingParent) && contains(clippingParent, clipperElement) && getNodeName(clippingParent) !== 'body';
    });
  } // Gets the maximum area that the element is visible in due to any number of
  // clipping parents


  function getClippingRect(element, boundary, rootBoundary, strategy) {
    var mainClippingParents = boundary === 'clippingParents' ? getClippingParents(element) : [].concat(boundary);
    var clippingParents = [].concat(mainClippingParents, [rootBoundary]);
    var firstClippingParent = clippingParents[0];
    var clippingRect = clippingParents.reduce(function (accRect, clippingParent) {
      var rect = getClientRectFromMixedType(element, clippingParent, strategy);
      accRect.top = max(rect.top, accRect.top);
      accRect.right = min(rect.right, accRect.right);
      accRect.bottom = min(rect.bottom, accRect.bottom);
      accRect.left = max(rect.left, accRect.left);
      return accRect;
    }, getClientRectFromMixedType(element, firstClippingParent, strategy));
    clippingRect.width = clippingRect.right - clippingRect.left;
    clippingRect.height = clippingRect.bottom - clippingRect.top;
    clippingRect.x = clippingRect.left;
    clippingRect.y = clippingRect.top;
    return clippingRect;
  }

  function computeOffsets(_ref) {
    var reference = _ref.reference,
        element = _ref.element,
        placement = _ref.placement;
    var basePlacement = placement ? getBasePlacement(placement) : null;
    var variation = placement ? getVariation(placement) : null;
    var commonX = reference.x + reference.width / 2 - element.width / 2;
    var commonY = reference.y + reference.height / 2 - element.height / 2;
    var offsets;

    switch (basePlacement) {
      case top:
        offsets = {
          x: commonX,
          y: reference.y - element.height
        };
        break;

      case bottom:
        offsets = {
          x: commonX,
          y: reference.y + reference.height
        };
        break;

      case right:
        offsets = {
          x: reference.x + reference.width,
          y: commonY
        };
        break;

      case left:
        offsets = {
          x: reference.x - element.width,
          y: commonY
        };
        break;

      default:
        offsets = {
          x: reference.x,
          y: reference.y
        };
    }

    var mainAxis = basePlacement ? getMainAxisFromPlacement(basePlacement) : null;

    if (mainAxis != null) {
      var len = mainAxis === 'y' ? 'height' : 'width';

      switch (variation) {
        case start:
          offsets[mainAxis] = offsets[mainAxis] - (reference[len] / 2 - element[len] / 2);
          break;

        case end:
          offsets[mainAxis] = offsets[mainAxis] + (reference[len] / 2 - element[len] / 2);
          break;
      }
    }

    return offsets;
  }

  function detectOverflow(state, options) {
    if (options === void 0) {
      options = {};
    }

    var _options = options,
        _options$placement = _options.placement,
        placement = _options$placement === void 0 ? state.placement : _options$placement,
        _options$strategy = _options.strategy,
        strategy = _options$strategy === void 0 ? state.strategy : _options$strategy,
        _options$boundary = _options.boundary,
        boundary = _options$boundary === void 0 ? clippingParents : _options$boundary,
        _options$rootBoundary = _options.rootBoundary,
        rootBoundary = _options$rootBoundary === void 0 ? viewport : _options$rootBoundary,
        _options$elementConte = _options.elementContext,
        elementContext = _options$elementConte === void 0 ? popper : _options$elementConte,
        _options$altBoundary = _options.altBoundary,
        altBoundary = _options$altBoundary === void 0 ? false : _options$altBoundary,
        _options$padding = _options.padding,
        padding = _options$padding === void 0 ? 0 : _options$padding;
    var paddingObject = mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
    var altContext = elementContext === popper ? reference : popper;
    var popperRect = state.rects.popper;
    var element = state.elements[altBoundary ? altContext : elementContext];
    var clippingClientRect = getClippingRect(isElement(element) ? element : element.contextElement || getDocumentElement(state.elements.popper), boundary, rootBoundary, strategy);
    var referenceClientRect = getBoundingClientRect(state.elements.reference);
    var popperOffsets = computeOffsets({
      reference: referenceClientRect,
      element: popperRect,
      strategy: 'absolute',
      placement: placement
    });
    var popperClientRect = rectToClientRect(Object.assign({}, popperRect, popperOffsets));
    var elementClientRect = elementContext === popper ? popperClientRect : referenceClientRect; // positive = overflowing the clipping rect
    // 0 or negative = within the clipping rect

    var overflowOffsets = {
      top: clippingClientRect.top - elementClientRect.top + paddingObject.top,
      bottom: elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom,
      left: clippingClientRect.left - elementClientRect.left + paddingObject.left,
      right: elementClientRect.right - clippingClientRect.right + paddingObject.right
    };
    var offsetData = state.modifiersData.offset; // Offsets can be applied only to the popper element

    if (elementContext === popper && offsetData) {
      var offset = offsetData[placement];
      Object.keys(overflowOffsets).forEach(function (key) {
        var multiply = [right, bottom].indexOf(key) >= 0 ? 1 : -1;
        var axis = [top, bottom].indexOf(key) >= 0 ? 'y' : 'x';
        overflowOffsets[key] += offset[axis] * multiply;
      });
    }

    return overflowOffsets;
  }

  function computeAutoPlacement(state, options) {
    if (options === void 0) {
      options = {};
    }

    var _options = options,
        placement = _options.placement,
        boundary = _options.boundary,
        rootBoundary = _options.rootBoundary,
        padding = _options.padding,
        flipVariations = _options.flipVariations,
        _options$allowedAutoP = _options.allowedAutoPlacements,
        allowedAutoPlacements = _options$allowedAutoP === void 0 ? placements : _options$allowedAutoP;
    var variation = getVariation(placement);
    var placements$1 = variation ? flipVariations ? variationPlacements : variationPlacements.filter(function (placement) {
      return getVariation(placement) === variation;
    }) : basePlacements;
    var allowedPlacements = placements$1.filter(function (placement) {
      return allowedAutoPlacements.indexOf(placement) >= 0;
    });

    if (allowedPlacements.length === 0) {
      allowedPlacements = placements$1;
    } // $FlowFixMe[incompatible-type]: Flow seems to have problems with two array unions...


    var overflows = allowedPlacements.reduce(function (acc, placement) {
      acc[placement] = detectOverflow(state, {
        placement: placement,
        boundary: boundary,
        rootBoundary: rootBoundary,
        padding: padding
      })[getBasePlacement(placement)];
      return acc;
    }, {});
    return Object.keys(overflows).sort(function (a, b) {
      return overflows[a] - overflows[b];
    });
  }

  function getExpandedFallbackPlacements(placement) {
    if (getBasePlacement(placement) === auto) {
      return [];
    }

    var oppositePlacement = getOppositePlacement(placement);
    return [getOppositeVariationPlacement(placement), oppositePlacement, getOppositeVariationPlacement(oppositePlacement)];
  }

  function flip(_ref) {
    var state = _ref.state,
        options = _ref.options,
        name = _ref.name;

    if (state.modifiersData[name]._skip) {
      return;
    }

    var _options$mainAxis = options.mainAxis,
        checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
        _options$altAxis = options.altAxis,
        checkAltAxis = _options$altAxis === void 0 ? true : _options$altAxis,
        specifiedFallbackPlacements = options.fallbackPlacements,
        padding = options.padding,
        boundary = options.boundary,
        rootBoundary = options.rootBoundary,
        altBoundary = options.altBoundary,
        _options$flipVariatio = options.flipVariations,
        flipVariations = _options$flipVariatio === void 0 ? true : _options$flipVariatio,
        allowedAutoPlacements = options.allowedAutoPlacements;
    var preferredPlacement = state.options.placement;
    var basePlacement = getBasePlacement(preferredPlacement);
    var isBasePlacement = basePlacement === preferredPlacement;
    var fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipVariations ? [getOppositePlacement(preferredPlacement)] : getExpandedFallbackPlacements(preferredPlacement));
    var placements = [preferredPlacement].concat(fallbackPlacements).reduce(function (acc, placement) {
      return acc.concat(getBasePlacement(placement) === auto ? computeAutoPlacement(state, {
        placement: placement,
        boundary: boundary,
        rootBoundary: rootBoundary,
        padding: padding,
        flipVariations: flipVariations,
        allowedAutoPlacements: allowedAutoPlacements
      }) : placement);
    }, []);
    var referenceRect = state.rects.reference;
    var popperRect = state.rects.popper;
    var checksMap = new Map();
    var makeFallbackChecks = true;
    var firstFittingPlacement = placements[0];

    for (var i = 0; i < placements.length; i++) {
      var placement = placements[i];

      var _basePlacement = getBasePlacement(placement);

      var isStartVariation = getVariation(placement) === start;
      var isVertical = [top, bottom].indexOf(_basePlacement) >= 0;
      var len = isVertical ? 'width' : 'height';
      var overflow = detectOverflow(state, {
        placement: placement,
        boundary: boundary,
        rootBoundary: rootBoundary,
        altBoundary: altBoundary,
        padding: padding
      });
      var mainVariationSide = isVertical ? isStartVariation ? right : left : isStartVariation ? bottom : top;

      if (referenceRect[len] > popperRect[len]) {
        mainVariationSide = getOppositePlacement(mainVariationSide);
      }

      var altVariationSide = getOppositePlacement(mainVariationSide);
      var checks = [];

      if (checkMainAxis) {
        checks.push(overflow[_basePlacement] <= 0);
      }

      if (checkAltAxis) {
        checks.push(overflow[mainVariationSide] <= 0, overflow[altVariationSide] <= 0);
      }

      if (checks.every(function (check) {
        return check;
      })) {
        firstFittingPlacement = placement;
        makeFallbackChecks = false;
        break;
      }

      checksMap.set(placement, checks);
    }

    if (makeFallbackChecks) {
      // `2` may be desired in some cases – research later
      var numberOfChecks = flipVariations ? 3 : 1;

      var _loop = function _loop(_i) {
        var fittingPlacement = placements.find(function (placement) {
          var checks = checksMap.get(placement);

          if (checks) {
            return checks.slice(0, _i).every(function (check) {
              return check;
            });
          }
        });

        if (fittingPlacement) {
          firstFittingPlacement = fittingPlacement;
          return "break";
        }
      };

      for (var _i = numberOfChecks; _i > 0; _i--) {
        var _ret = _loop(_i);

        if (_ret === "break") break;
      }
    }

    if (state.placement !== firstFittingPlacement) {
      state.modifiersData[name]._skip = true;
      state.placement = firstFittingPlacement;
      state.reset = true;
    }
  } // eslint-disable-next-line import/no-unused-modules


  const flip$1 = {
    name: 'flip',
    enabled: true,
    phase: 'main',
    fn: flip,
    requiresIfExists: ['offset'],
    data: {
      _skip: false
    }
  };

  function getSideOffsets(overflow, rect, preventedOffsets) {
    if (preventedOffsets === void 0) {
      preventedOffsets = {
        x: 0,
        y: 0
      };
    }

    return {
      top: overflow.top - rect.height - preventedOffsets.y,
      right: overflow.right - rect.width + preventedOffsets.x,
      bottom: overflow.bottom - rect.height + preventedOffsets.y,
      left: overflow.left - rect.width - preventedOffsets.x
    };
  }

  function isAnySideFullyClipped(overflow) {
    return [top, right, bottom, left].some(function (side) {
      return overflow[side] >= 0;
    });
  }

  function hide(_ref) {
    var state = _ref.state,
        name = _ref.name;
    var referenceRect = state.rects.reference;
    var popperRect = state.rects.popper;
    var preventedOffsets = state.modifiersData.preventOverflow;
    var referenceOverflow = detectOverflow(state, {
      elementContext: 'reference'
    });
    var popperAltOverflow = detectOverflow(state, {
      altBoundary: true
    });
    var referenceClippingOffsets = getSideOffsets(referenceOverflow, referenceRect);
    var popperEscapeOffsets = getSideOffsets(popperAltOverflow, popperRect, preventedOffsets);
    var isReferenceHidden = isAnySideFullyClipped(referenceClippingOffsets);
    var hasPopperEscaped = isAnySideFullyClipped(popperEscapeOffsets);
    state.modifiersData[name] = {
      referenceClippingOffsets: referenceClippingOffsets,
      popperEscapeOffsets: popperEscapeOffsets,
      isReferenceHidden: isReferenceHidden,
      hasPopperEscaped: hasPopperEscaped
    };
    state.attributes.popper = Object.assign({}, state.attributes.popper, {
      'data-popper-reference-hidden': isReferenceHidden,
      'data-popper-escaped': hasPopperEscaped
    });
  } // eslint-disable-next-line import/no-unused-modules


  const hide$1 = {
    name: 'hide',
    enabled: true,
    phase: 'main',
    requiresIfExists: ['preventOverflow'],
    fn: hide
  };

  function distanceAndSkiddingToXY(placement, rects, offset) {
    var basePlacement = getBasePlacement(placement);
    var invertDistance = [left, top].indexOf(basePlacement) >= 0 ? -1 : 1;

    var _ref = typeof offset === 'function' ? offset(Object.assign({}, rects, {
      placement: placement
    })) : offset,
        skidding = _ref[0],
        distance = _ref[1];

    skidding = skidding || 0;
    distance = (distance || 0) * invertDistance;
    return [left, right].indexOf(basePlacement) >= 0 ? {
      x: distance,
      y: skidding
    } : {
      x: skidding,
      y: distance
    };
  }

  function offset(_ref2) {
    var state = _ref2.state,
        options = _ref2.options,
        name = _ref2.name;
    var _options$offset = options.offset,
        offset = _options$offset === void 0 ? [0, 0] : _options$offset;
    var data = placements.reduce(function (acc, placement) {
      acc[placement] = distanceAndSkiddingToXY(placement, state.rects, offset);
      return acc;
    }, {});
    var _data$state$placement = data[state.placement],
        x = _data$state$placement.x,
        y = _data$state$placement.y;

    if (state.modifiersData.popperOffsets != null) {
      state.modifiersData.popperOffsets.x += x;
      state.modifiersData.popperOffsets.y += y;
    }

    state.modifiersData[name] = data;
  } // eslint-disable-next-line import/no-unused-modules


  const offset$1 = {
    name: 'offset',
    enabled: true,
    phase: 'main',
    requires: ['popperOffsets'],
    fn: offset
  };

  function popperOffsets(_ref) {
    var state = _ref.state,
        name = _ref.name;
    // Offsets are the actual position the popper needs to have to be
    // properly positioned near its reference element
    // This is the most basic placement, and will be adjusted by
    // the modifiers in the next step
    state.modifiersData[name] = computeOffsets({
      reference: state.rects.reference,
      element: state.rects.popper,
      strategy: 'absolute',
      placement: state.placement
    });
  } // eslint-disable-next-line import/no-unused-modules


  const popperOffsets$1 = {
    name: 'popperOffsets',
    enabled: true,
    phase: 'read',
    fn: popperOffsets,
    data: {}
  };

  function getAltAxis(axis) {
    return axis === 'x' ? 'y' : 'x';
  }

  function preventOverflow(_ref) {
    var state = _ref.state,
        options = _ref.options,
        name = _ref.name;
    var _options$mainAxis = options.mainAxis,
        checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
        _options$altAxis = options.altAxis,
        checkAltAxis = _options$altAxis === void 0 ? false : _options$altAxis,
        boundary = options.boundary,
        rootBoundary = options.rootBoundary,
        altBoundary = options.altBoundary,
        padding = options.padding,
        _options$tether = options.tether,
        tether = _options$tether === void 0 ? true : _options$tether,
        _options$tetherOffset = options.tetherOffset,
        tetherOffset = _options$tetherOffset === void 0 ? 0 : _options$tetherOffset;
    var overflow = detectOverflow(state, {
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding,
      altBoundary: altBoundary
    });
    var basePlacement = getBasePlacement(state.placement);
    var variation = getVariation(state.placement);
    var isBasePlacement = !variation;
    var mainAxis = getMainAxisFromPlacement(basePlacement);
    var altAxis = getAltAxis(mainAxis);
    var popperOffsets = state.modifiersData.popperOffsets;
    var referenceRect = state.rects.reference;
    var popperRect = state.rects.popper;
    var tetherOffsetValue = typeof tetherOffset === 'function' ? tetherOffset(Object.assign({}, state.rects, {
      placement: state.placement
    })) : tetherOffset;
    var normalizedTetherOffsetValue = typeof tetherOffsetValue === 'number' ? {
      mainAxis: tetherOffsetValue,
      altAxis: tetherOffsetValue
    } : Object.assign({
      mainAxis: 0,
      altAxis: 0
    }, tetherOffsetValue);
    var offsetModifierState = state.modifiersData.offset ? state.modifiersData.offset[state.placement] : null;
    var data = {
      x: 0,
      y: 0
    };

    if (!popperOffsets) {
      return;
    }

    if (checkMainAxis) {
      var _offsetModifierState$;

      var mainSide = mainAxis === 'y' ? top : left;
      var altSide = mainAxis === 'y' ? bottom : right;
      var len = mainAxis === 'y' ? 'height' : 'width';
      var offset = popperOffsets[mainAxis];
      var min$1 = offset + overflow[mainSide];
      var max$1 = offset - overflow[altSide];
      var additive = tether ? -popperRect[len] / 2 : 0;
      var minLen = variation === start ? referenceRect[len] : popperRect[len];
      var maxLen = variation === start ? -popperRect[len] : -referenceRect[len]; // We need to include the arrow in the calculation so the arrow doesn't go
      // outside the reference bounds

      var arrowElement = state.elements.arrow;
      var arrowRect = tether && arrowElement ? getLayoutRect(arrowElement) : {
        width: 0,
        height: 0
      };
      var arrowPaddingObject = state.modifiersData['arrow#persistent'] ? state.modifiersData['arrow#persistent'].padding : getFreshSideObject();
      var arrowPaddingMin = arrowPaddingObject[mainSide];
      var arrowPaddingMax = arrowPaddingObject[altSide]; // If the reference length is smaller than the arrow length, we don't want
      // to include its full size in the calculation. If the reference is small
      // and near the edge of a boundary, the popper can overflow even if the
      // reference is not overflowing as well (e.g. virtual elements with no
      // width or height)

      var arrowLen = within(0, referenceRect[len], arrowRect[len]);
      var minOffset = isBasePlacement ? referenceRect[len] / 2 - additive - arrowLen - arrowPaddingMin - normalizedTetherOffsetValue.mainAxis : minLen - arrowLen - arrowPaddingMin - normalizedTetherOffsetValue.mainAxis;
      var maxOffset = isBasePlacement ? -referenceRect[len] / 2 + additive + arrowLen + arrowPaddingMax + normalizedTetherOffsetValue.mainAxis : maxLen + arrowLen + arrowPaddingMax + normalizedTetherOffsetValue.mainAxis;
      var arrowOffsetParent = state.elements.arrow && getOffsetParent(state.elements.arrow);
      var clientOffset = arrowOffsetParent ? mainAxis === 'y' ? arrowOffsetParent.clientTop || 0 : arrowOffsetParent.clientLeft || 0 : 0;
      var offsetModifierValue = (_offsetModifierState$ = offsetModifierState == null ? void 0 : offsetModifierState[mainAxis]) != null ? _offsetModifierState$ : 0;
      var tetherMin = offset + minOffset - offsetModifierValue - clientOffset;
      var tetherMax = offset + maxOffset - offsetModifierValue;
      var preventedOffset = within(tether ? min(min$1, tetherMin) : min$1, offset, tether ? max(max$1, tetherMax) : max$1);
      popperOffsets[mainAxis] = preventedOffset;
      data[mainAxis] = preventedOffset - offset;
    }

    if (checkAltAxis) {
      var _offsetModifierState$2;

      var _mainSide = mainAxis === 'x' ? top : left;

      var _altSide = mainAxis === 'x' ? bottom : right;

      var _offset = popperOffsets[altAxis];

      var _len = altAxis === 'y' ? 'height' : 'width';

      var _min = _offset + overflow[_mainSide];

      var _max = _offset - overflow[_altSide];

      var isOriginSide = [top, left].indexOf(basePlacement) !== -1;

      var _offsetModifierValue = (_offsetModifierState$2 = offsetModifierState == null ? void 0 : offsetModifierState[altAxis]) != null ? _offsetModifierState$2 : 0;

      var _tetherMin = isOriginSide ? _min : _offset - referenceRect[_len] - popperRect[_len] - _offsetModifierValue + normalizedTetherOffsetValue.altAxis;

      var _tetherMax = isOriginSide ? _offset + referenceRect[_len] + popperRect[_len] - _offsetModifierValue - normalizedTetherOffsetValue.altAxis : _max;

      var _preventedOffset = tether && isOriginSide ? withinMaxClamp(_tetherMin, _offset, _tetherMax) : within(tether ? _tetherMin : _min, _offset, tether ? _tetherMax : _max);

      popperOffsets[altAxis] = _preventedOffset;
      data[altAxis] = _preventedOffset - _offset;
    }

    state.modifiersData[name] = data;
  } // eslint-disable-next-line import/no-unused-modules


  const preventOverflow$1 = {
    name: 'preventOverflow',
    enabled: true,
    phase: 'main',
    fn: preventOverflow,
    requiresIfExists: ['offset']
  };

  function getHTMLElementScroll(element) {
    return {
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop
    };
  }

  function getNodeScroll(node) {
    if (node === getWindow(node) || !isHTMLElement(node)) {
      return getWindowScroll(node);
    } else {
      return getHTMLElementScroll(node);
    }
  }

  function isElementScaled(element) {
    var rect = element.getBoundingClientRect();
    var scaleX = round(rect.width) / element.offsetWidth || 1;
    var scaleY = round(rect.height) / element.offsetHeight || 1;
    return scaleX !== 1 || scaleY !== 1;
  } // Returns the composite rect of an element relative to its offsetParent.
  // Composite means it takes into account transforms as well as layout.


  function getCompositeRect(elementOrVirtualElement, offsetParent, isFixed) {
    if (isFixed === void 0) {
      isFixed = false;
    }

    var isOffsetParentAnElement = isHTMLElement(offsetParent);
    var offsetParentIsScaled = isHTMLElement(offsetParent) && isElementScaled(offsetParent);
    var documentElement = getDocumentElement(offsetParent);
    var rect = getBoundingClientRect(elementOrVirtualElement, offsetParentIsScaled, isFixed);
    var scroll = {
      scrollLeft: 0,
      scrollTop: 0
    };
    var offsets = {
      x: 0,
      y: 0
    };

    if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
      if (getNodeName(offsetParent) !== 'body' || // https://github.com/popperjs/popper-core/issues/1078
      isScrollParent(documentElement)) {
        scroll = getNodeScroll(offsetParent);
      }

      if (isHTMLElement(offsetParent)) {
        offsets = getBoundingClientRect(offsetParent, true);
        offsets.x += offsetParent.clientLeft;
        offsets.y += offsetParent.clientTop;
      } else if (documentElement) {
        offsets.x = getWindowScrollBarX(documentElement);
      }
    }

    return {
      x: rect.left + scroll.scrollLeft - offsets.x,
      y: rect.top + scroll.scrollTop - offsets.y,
      width: rect.width,
      height: rect.height
    };
  }

  function order(modifiers) {
    var map = new Map();
    var visited = new Set();
    var result = [];
    modifiers.forEach(function (modifier) {
      map.set(modifier.name, modifier);
    }); // On visiting object, check for its dependencies and visit them recursively

    function sort(modifier) {
      visited.add(modifier.name);
      var requires = [].concat(modifier.requires || [], modifier.requiresIfExists || []);
      requires.forEach(function (dep) {
        if (!visited.has(dep)) {
          var depModifier = map.get(dep);

          if (depModifier) {
            sort(depModifier);
          }
        }
      });
      result.push(modifier);
    }

    modifiers.forEach(function (modifier) {
      if (!visited.has(modifier.name)) {
        // check for visited object
        sort(modifier);
      }
    });
    return result;
  }

  function orderModifiers(modifiers) {
    // order based on dependencies
    var orderedModifiers = order(modifiers); // order based on phase

    return modifierPhases.reduce(function (acc, phase) {
      return acc.concat(orderedModifiers.filter(function (modifier) {
        return modifier.phase === phase;
      }));
    }, []);
  }

  function debounce(fn) {
    var pending;
    return function () {
      if (!pending) {
        pending = new Promise(function (resolve) {
          Promise.resolve().then(function () {
            pending = undefined;
            resolve(fn());
          });
        });
      }

      return pending;
    };
  }

  function mergeByName(modifiers) {
    var merged = modifiers.reduce(function (merged, current) {
      var existing = merged[current.name];
      merged[current.name] = existing ? Object.assign({}, existing, current, {
        options: Object.assign({}, existing.options, current.options),
        data: Object.assign({}, existing.data, current.data)
      }) : current;
      return merged;
    }, {}); // IE11 does not support Object.values

    return Object.keys(merged).map(function (key) {
      return merged[key];
    });
  }

  var DEFAULT_OPTIONS = {
    placement: 'bottom',
    modifiers: [],
    strategy: 'absolute'
  };

  function areValidElements() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return !args.some(function (element) {
      return !(element && typeof element.getBoundingClientRect === 'function');
    });
  }

  function popperGenerator(generatorOptions) {
    if (generatorOptions === void 0) {
      generatorOptions = {};
    }

    var _generatorOptions = generatorOptions,
        _generatorOptions$def = _generatorOptions.defaultModifiers,
        defaultModifiers = _generatorOptions$def === void 0 ? [] : _generatorOptions$def,
        _generatorOptions$def2 = _generatorOptions.defaultOptions,
        defaultOptions = _generatorOptions$def2 === void 0 ? DEFAULT_OPTIONS : _generatorOptions$def2;
    return function createPopper(reference, popper, options) {
      if (options === void 0) {
        options = defaultOptions;
      }

      var state = {
        placement: 'bottom',
        orderedModifiers: [],
        options: Object.assign({}, DEFAULT_OPTIONS, defaultOptions),
        modifiersData: {},
        elements: {
          reference: reference,
          popper: popper
        },
        attributes: {},
        styles: {}
      };
      var effectCleanupFns = [];
      var isDestroyed = false;
      var instance = {
        state: state,
        setOptions: function setOptions(setOptionsAction) {
          var options = typeof setOptionsAction === 'function' ? setOptionsAction(state.options) : setOptionsAction;
          cleanupModifierEffects();
          state.options = Object.assign({}, defaultOptions, state.options, options);
          state.scrollParents = {
            reference: isElement(reference) ? listScrollParents(reference) : reference.contextElement ? listScrollParents(reference.contextElement) : [],
            popper: listScrollParents(popper)
          }; // Orders the modifiers based on their dependencies and `phase`
          // properties

          var orderedModifiers = orderModifiers(mergeByName([].concat(defaultModifiers, state.options.modifiers))); // Strip out disabled modifiers

          state.orderedModifiers = orderedModifiers.filter(function (m) {
            return m.enabled;
          });
          runModifierEffects();
          return instance.update();
        },
        // Sync update – it will always be executed, even if not necessary. This
        // is useful for low frequency updates where sync behavior simplifies the
        // logic.
        // For high frequency updates (e.g. `resize` and `scroll` events), always
        // prefer the async Popper#update method
        forceUpdate: function forceUpdate() {
          if (isDestroyed) {
            return;
          }

          var _state$elements = state.elements,
              reference = _state$elements.reference,
              popper = _state$elements.popper; // Don't proceed if `reference` or `popper` are not valid elements
          // anymore

          if (!areValidElements(reference, popper)) {
            return;
          } // Store the reference and popper rects to be read by modifiers


          state.rects = {
            reference: getCompositeRect(reference, getOffsetParent(popper), state.options.strategy === 'fixed'),
            popper: getLayoutRect(popper)
          }; // Modifiers have the ability to reset the current update cycle. The
          // most common use case for this is the `flip` modifier changing the
          // placement, which then needs to re-run all the modifiers, because the
          // logic was previously ran for the previous placement and is therefore
          // stale/incorrect

          state.reset = false;
          state.placement = state.options.placement; // On each update cycle, the `modifiersData` property for each modifier
          // is filled with the initial data specified by the modifier. This means
          // it doesn't persist and is fresh on each update.
          // To ensure persistent data, use `${name}#persistent`

          state.orderedModifiers.forEach(function (modifier) {
            return state.modifiersData[modifier.name] = Object.assign({}, modifier.data);
          });

          for (var index = 0; index < state.orderedModifiers.length; index++) {
            if (state.reset === true) {
              state.reset = false;
              index = -1;
              continue;
            }

            var _state$orderedModifie = state.orderedModifiers[index],
                fn = _state$orderedModifie.fn,
                _state$orderedModifie2 = _state$orderedModifie.options,
                _options = _state$orderedModifie2 === void 0 ? {} : _state$orderedModifie2,
                name = _state$orderedModifie.name;

            if (typeof fn === 'function') {
              state = fn({
                state: state,
                options: _options,
                name: name,
                instance: instance
              }) || state;
            }
          }
        },
        // Async and optimistically optimized update – it will not be executed if
        // not necessary (debounced to run at most once-per-tick)
        update: debounce(function () {
          return new Promise(function (resolve) {
            instance.forceUpdate();
            resolve(state);
          });
        }),
        destroy: function destroy() {
          cleanupModifierEffects();
          isDestroyed = true;
        }
      };

      if (!areValidElements(reference, popper)) {
        return instance;
      }

      instance.setOptions(options).then(function (state) {
        if (!isDestroyed && options.onFirstUpdate) {
          options.onFirstUpdate(state);
        }
      }); // Modifiers have the ability to execute arbitrary code before the first
      // update cycle runs. They will be executed in the same order as the update
      // cycle. This is useful when a modifier adds some persistent data that
      // other modifiers need to use, but the modifier is run after the dependent
      // one.

      function runModifierEffects() {
        state.orderedModifiers.forEach(function (_ref) {
          var name = _ref.name,
              _ref$options = _ref.options,
              options = _ref$options === void 0 ? {} : _ref$options,
              effect = _ref.effect;

          if (typeof effect === 'function') {
            var cleanupFn = effect({
              state: state,
              name: name,
              instance: instance,
              options: options
            });

            var noopFn = function noopFn() {};

            effectCleanupFns.push(cleanupFn || noopFn);
          }
        });
      }

      function cleanupModifierEffects() {
        effectCleanupFns.forEach(function (fn) {
          return fn();
        });
        effectCleanupFns = [];
      }

      return instance;
    };
  }
  var createPopper$2 = /*#__PURE__*/popperGenerator(); // eslint-disable-next-line import/no-unused-modules

  var defaultModifiers$1 = [eventListeners, popperOffsets$1, computeStyles$1, applyStyles$1];
  var createPopper$1 = /*#__PURE__*/popperGenerator({
    defaultModifiers: defaultModifiers$1
  }); // eslint-disable-next-line import/no-unused-modules

  var defaultModifiers = [eventListeners, popperOffsets$1, computeStyles$1, applyStyles$1, offset$1, flip$1, preventOverflow$1, arrow$1, hide$1];
  var createPopper = /*#__PURE__*/popperGenerator({
    defaultModifiers: defaultModifiers
  }); // eslint-disable-next-line import/no-unused-modules

  const Popper = /*#__PURE__*/Object.freeze(/*#__PURE__*/Object.defineProperty({
    __proto__: null,
    afterMain,
    afterRead,
    afterWrite,
    applyStyles: applyStyles$1,
    arrow: arrow$1,
    auto,
    basePlacements,
    beforeMain,
    beforeRead,
    beforeWrite,
    bottom,
    clippingParents,
    computeStyles: computeStyles$1,
    createPopper,
    createPopperBase: createPopper$2,
    createPopperLite: createPopper$1,
    detectOverflow,
    end,
    eventListeners,
    flip: flip$1,
    hide: hide$1,
    left,
    main,
    modifierPhases,
    offset: offset$1,
    placements,
    popper,
    popperGenerator,
    popperOffsets: popperOffsets$1,
    preventOverflow: preventOverflow$1,
    read,
    reference,
    right,
    start,
    top,
    variationPlacements,
    viewport,
    write
  }, Symbol.toStringTag, { value: 'Module' }));

  /**
   * --------------------------------------------------------------------------
   * Bootstrap dropdown.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$a = 'dropdown';
  const DATA_KEY$6 = 'bs.dropdown';
  const EVENT_KEY$6 = `.${DATA_KEY$6}`;
  const DATA_API_KEY$3 = '.data-api';
  const ESCAPE_KEY$2 = 'Escape';
  const TAB_KEY$1 = 'Tab';
  const ARROW_UP_KEY$1 = 'ArrowUp';
  const ARROW_DOWN_KEY$1 = 'ArrowDown';
  const RIGHT_MOUSE_BUTTON = 2; // MouseEvent.button value for the secondary button, usually the right button

  const EVENT_HIDE$5 = `hide${EVENT_KEY$6}`;
  const EVENT_HIDDEN$5 = `hidden${EVENT_KEY$6}`;
  const EVENT_SHOW$5 = `show${EVENT_KEY$6}`;
  const EVENT_SHOWN$5 = `shown${EVENT_KEY$6}`;
  const EVENT_CLICK_DATA_API$3 = `click${EVENT_KEY$6}${DATA_API_KEY$3}`;
  const EVENT_KEYDOWN_DATA_API = `keydown${EVENT_KEY$6}${DATA_API_KEY$3}`;
  const EVENT_KEYUP_DATA_API = `keyup${EVENT_KEY$6}${DATA_API_KEY$3}`;
  const CLASS_NAME_SHOW$6 = 'show';
  const CLASS_NAME_DROPUP = 'dropup';
  const CLASS_NAME_DROPEND = 'dropend';
  const CLASS_NAME_DROPSTART = 'dropstart';
  const CLASS_NAME_DROPUP_CENTER = 'dropup-center';
  const CLASS_NAME_DROPDOWN_CENTER = 'dropdown-center';
  const SELECTOR_DATA_TOGGLE$3 = '[data-bs-toggle="dropdown"]:not(.disabled):not(:disabled)';
  const SELECTOR_DATA_TOGGLE_SHOWN = `${SELECTOR_DATA_TOGGLE$3}.${CLASS_NAME_SHOW$6}`;
  const SELECTOR_MENU = '.dropdown-menu';
  const SELECTOR_NAVBAR = '.navbar';
  const SELECTOR_NAVBAR_NAV = '.navbar-nav';
  const SELECTOR_VISIBLE_ITEMS = '.dropdown-menu .dropdown-item:not(.disabled):not(:disabled)';
  const PLACEMENT_TOP = isRTL() ? 'top-end' : 'top-start';
  const PLACEMENT_TOPEND = isRTL() ? 'top-start' : 'top-end';
  const PLACEMENT_BOTTOM = isRTL() ? 'bottom-end' : 'bottom-start';
  const PLACEMENT_BOTTOMEND = isRTL() ? 'bottom-start' : 'bottom-end';
  const PLACEMENT_RIGHT = isRTL() ? 'left-start' : 'right-start';
  const PLACEMENT_LEFT = isRTL() ? 'right-start' : 'left-start';
  const PLACEMENT_TOPCENTER = 'top';
  const PLACEMENT_BOTTOMCENTER = 'bottom';
  const Default$9 = {
    autoClose: true,
    boundary: 'clippingParents',
    display: 'dynamic',
    offset: [0, 2],
    popperConfig: null,
    reference: 'toggle'
  };
  const DefaultType$9 = {
    autoClose: '(boolean|string)',
    boundary: '(string|element)',
    display: 'string',
    offset: '(array|string|function)',
    popperConfig: '(null|object|function)',
    reference: '(string|element|object)'
  };

  /**
   * Class definition
   */

  class Dropdown extends BaseComponent {
    constructor(element, config) {
      super(element, config);
      this._popper = null;
      this._parent = this._element.parentNode; // dropdown wrapper
      // TODO: v6 revert #37011 & change markup https://getbootstrap.com/docs/5.3/forms/input-group/
      this._menu = SelectorEngine.next(this._element, SELECTOR_MENU)[0] || SelectorEngine.prev(this._element, SELECTOR_MENU)[0] || SelectorEngine.findOne(SELECTOR_MENU, this._parent);
      this._inNavbar = this._detectNavbar();
    }

    // Getters
    static get Default() {
      return Default$9;
    }
    static get DefaultType() {
      return DefaultType$9;
    }
    static get NAME() {
      return NAME$a;
    }

    // Public
    toggle() {
      return this._isShown() ? this.hide() : this.show();
    }
    show() {
      if (isDisabled(this._element) || this._isShown()) {
        return;
      }
      const relatedTarget = {
        relatedTarget: this._element
      };
      const showEvent = EventHandler.trigger(this._element, EVENT_SHOW$5, relatedTarget);
      if (showEvent.defaultPrevented) {
        return;
      }
      this._createPopper();

      // If this is a touch-enabled device we add extra
      // empty mouseover listeners to the body's immediate children;
      // only needed because of broken event delegation on iOS
      // https://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html
      if ('ontouchstart' in document.documentElement && !this._parent.closest(SELECTOR_NAVBAR_NAV)) {
        for (const element of [].concat(...document.body.children)) {
          EventHandler.on(element, 'mouseover', noop);
        }
      }
      this._element.focus();
      this._element.setAttribute('aria-expanded', true);
      this._menu.classList.add(CLASS_NAME_SHOW$6);
      this._element.classList.add(CLASS_NAME_SHOW$6);
      EventHandler.trigger(this._element, EVENT_SHOWN$5, relatedTarget);
    }
    hide() {
      if (isDisabled(this._element) || !this._isShown()) {
        return;
      }
      const relatedTarget = {
        relatedTarget: this._element
      };
      this._completeHide(relatedTarget);
    }
    dispose() {
      if (this._popper) {
        this._popper.destroy();
      }
      super.dispose();
    }
    update() {
      this._inNavbar = this._detectNavbar();
      if (this._popper) {
        this._popper.update();
      }
    }

    // Private
    _completeHide(relatedTarget) {
      const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE$5, relatedTarget);
      if (hideEvent.defaultPrevented) {
        return;
      }

      // If this is a touch-enabled device we remove the extra
      // empty mouseover listeners we added for iOS support
      if ('ontouchstart' in document.documentElement) {
        for (const element of [].concat(...document.body.children)) {
          EventHandler.off(element, 'mouseover', noop);
        }
      }
      if (this._popper) {
        this._popper.destroy();
      }
      this._menu.classList.remove(CLASS_NAME_SHOW$6);
      this._element.classList.remove(CLASS_NAME_SHOW$6);
      this._element.setAttribute('aria-expanded', 'false');
      Manipulator.removeDataAttribute(this._menu, 'popper');
      EventHandler.trigger(this._element, EVENT_HIDDEN$5, relatedTarget);
    }
    _getConfig(config) {
      config = super._getConfig(config);
      if (typeof config.reference === 'object' && !isElement$1(config.reference) && typeof config.reference.getBoundingClientRect !== 'function') {
        // Popper virtual elements require a getBoundingClientRect method
        throw new TypeError(`${NAME$a.toUpperCase()}: Option "reference" provided type "object" without a required "getBoundingClientRect" method.`);
      }
      return config;
    }
    _createPopper() {
      if (typeof Popper === 'undefined') {
        throw new TypeError('Bootstrap\'s dropdowns require Popper (https://popper.js.org)');
      }
      let referenceElement = this._element;
      if (this._config.reference === 'parent') {
        referenceElement = this._parent;
      } else if (isElement$1(this._config.reference)) {
        referenceElement = getElement(this._config.reference);
      } else if (typeof this._config.reference === 'object') {
        referenceElement = this._config.reference;
      }
      const popperConfig = this._getPopperConfig();
      this._popper = createPopper(referenceElement, this._menu, popperConfig);
    }
    _isShown() {
      return this._menu.classList.contains(CLASS_NAME_SHOW$6);
    }
    _getPlacement() {
      const parentDropdown = this._parent;
      if (parentDropdown.classList.contains(CLASS_NAME_DROPEND)) {
        return PLACEMENT_RIGHT;
      }
      if (parentDropdown.classList.contains(CLASS_NAME_DROPSTART)) {
        return PLACEMENT_LEFT;
      }
      if (parentDropdown.classList.contains(CLASS_NAME_DROPUP_CENTER)) {
        return PLACEMENT_TOPCENTER;
      }
      if (parentDropdown.classList.contains(CLASS_NAME_DROPDOWN_CENTER)) {
        return PLACEMENT_BOTTOMCENTER;
      }

      // We need to trim the value because custom properties can also include spaces
      const isEnd = getComputedStyle(this._menu).getPropertyValue('--bs-position').trim() === 'end';
      if (parentDropdown.classList.contains(CLASS_NAME_DROPUP)) {
        return isEnd ? PLACEMENT_TOPEND : PLACEMENT_TOP;
      }
      return isEnd ? PLACEMENT_BOTTOMEND : PLACEMENT_BOTTOM;
    }
    _detectNavbar() {
      return this._element.closest(SELECTOR_NAVBAR) !== null;
    }
    _getOffset() {
      const {
        offset
      } = this._config;
      if (typeof offset === 'string') {
        return offset.split(',').map(value => Number.parseInt(value, 10));
      }
      if (typeof offset === 'function') {
        return popperData => offset(popperData, this._element);
      }
      return offset;
    }
    _getPopperConfig() {
      const defaultBsPopperConfig = {
        placement: this._getPlacement(),
        modifiers: [{
          name: 'preventOverflow',
          options: {
            boundary: this._config.boundary
          }
        }, {
          name: 'offset',
          options: {
            offset: this._getOffset()
          }
        }]
      };

      // Disable Popper if we have a static display or Dropdown is in Navbar
      if (this._inNavbar || this._config.display === 'static') {
        Manipulator.setDataAttribute(this._menu, 'popper', 'static'); // TODO: v6 remove
        defaultBsPopperConfig.modifiers = [{
          name: 'applyStyles',
          enabled: false
        }];
      }
      return {
        ...defaultBsPopperConfig,
        ...execute(this._config.popperConfig, [defaultBsPopperConfig])
      };
    }
    _selectMenuItem({
      key,
      target
    }) {
      const items = SelectorEngine.find(SELECTOR_VISIBLE_ITEMS, this._menu).filter(element => isVisible(element));
      if (!items.length) {
        return;
      }

      // if target isn't included in items (e.g. when expanding the dropdown)
      // allow cycling to get the last item in case key equals ARROW_UP_KEY
      getNextActiveElement(items, target, key === ARROW_DOWN_KEY$1, !items.includes(target)).focus();
    }

    // Static
    static jQueryInterface(config) {
      return this.each(function () {
        const data = Dropdown.getOrCreateInstance(this, config);
        if (typeof config !== 'string') {
          return;
        }
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`);
        }
        data[config]();
      });
    }
    static clearMenus(event) {
      if (event.button === RIGHT_MOUSE_BUTTON || event.type === 'keyup' && event.key !== TAB_KEY$1) {
        return;
      }
      const openToggles = SelectorEngine.find(SELECTOR_DATA_TOGGLE_SHOWN);
      for (const toggle of openToggles) {
        const context = Dropdown.getInstance(toggle);
        if (!context || context._config.autoClose === false) {
          continue;
        }
        const composedPath = event.composedPath();
        const isMenuTarget = composedPath.includes(context._menu);
        if (composedPath.includes(context._element) || context._config.autoClose === 'inside' && !isMenuTarget || context._config.autoClose === 'outside' && isMenuTarget) {
          continue;
        }

        // Tab navigation through the dropdown menu or events from contained inputs shouldn't close the menu
        if (context._menu.contains(event.target) && (event.type === 'keyup' && event.key === TAB_KEY$1 || /input|select|option|textarea|form/i.test(event.target.tagName))) {
          continue;
        }
        const relatedTarget = {
          relatedTarget: context._element
        };
        if (event.type === 'click') {
          relatedTarget.clickEvent = event;
        }
        context._completeHide(relatedTarget);
      }
    }
    static dataApiKeydownHandler(event) {
      // If not an UP | DOWN | ESCAPE key => not a dropdown command
      // If input/textarea && if key is other than ESCAPE => not a dropdown command

      const isInput = /input|textarea/i.test(event.target.tagName);
      const isEscapeEvent = event.key === ESCAPE_KEY$2;
      const isUpOrDownEvent = [ARROW_UP_KEY$1, ARROW_DOWN_KEY$1].includes(event.key);
      if (!isUpOrDownEvent && !isEscapeEvent) {
        return;
      }
      if (isInput && !isEscapeEvent) {
        return;
      }
      event.preventDefault();

      // TODO: v6 revert #37011 & change markup https://getbootstrap.com/docs/5.3/forms/input-group/
      const getToggleButton = this.matches(SELECTOR_DATA_TOGGLE$3) ? this : SelectorEngine.prev(this, SELECTOR_DATA_TOGGLE$3)[0] || SelectorEngine.next(this, SELECTOR_DATA_TOGGLE$3)[0] || SelectorEngine.findOne(SELECTOR_DATA_TOGGLE$3, event.delegateTarget.parentNode);
      const instance = Dropdown.getOrCreateInstance(getToggleButton);
      if (isUpOrDownEvent) {
        event.stopPropagation();
        instance.show();
        instance._selectMenuItem(event);
        return;
      }
      if (instance._isShown()) {
        // else is escape and we check if it is shown
        event.stopPropagation();
        instance.hide();
        getToggleButton.focus();
      }
    }
  }

  /**
   * Data API implementation
   */

  EventHandler.on(document, EVENT_KEYDOWN_DATA_API, SELECTOR_DATA_TOGGLE$3, Dropdown.dataApiKeydownHandler);
  EventHandler.on(document, EVENT_KEYDOWN_DATA_API, SELECTOR_MENU, Dropdown.dataApiKeydownHandler);
  EventHandler.on(document, EVENT_CLICK_DATA_API$3, Dropdown.clearMenus);
  EventHandler.on(document, EVENT_KEYUP_DATA_API, Dropdown.clearMenus);
  EventHandler.on(document, EVENT_CLICK_DATA_API$3, SELECTOR_DATA_TOGGLE$3, function (event) {
    event.preventDefault();
    Dropdown.getOrCreateInstance(this).toggle();
  });

  /**
   * jQuery
   */

  defineJQueryPlugin(Dropdown);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap util/backdrop.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$9 = 'backdrop';
  const CLASS_NAME_FADE$4 = 'fade';
  const CLASS_NAME_SHOW$5 = 'show';
  const EVENT_MOUSEDOWN = `mousedown.bs.${NAME$9}`;
  const Default$8 = {
    className: 'modal-backdrop',
    clickCallback: null,
    isAnimated: false,
    isVisible: true,
    // if false, we use the backdrop helper without adding any element to the dom
    rootElement: 'body' // give the choice to place backdrop under different elements
  };
  const DefaultType$8 = {
    className: 'string',
    clickCallback: '(function|null)',
    isAnimated: 'boolean',
    isVisible: 'boolean',
    rootElement: '(element|string)'
  };

  /**
   * Class definition
   */

  class Backdrop extends Config {
    constructor(config) {
      super();
      this._config = this._getConfig(config);
      this._isAppended = false;
      this._element = null;
    }

    // Getters
    static get Default() {
      return Default$8;
    }
    static get DefaultType() {
      return DefaultType$8;
    }
    static get NAME() {
      return NAME$9;
    }

    // Public
    show(callback) {
      if (!this._config.isVisible) {
        execute(callback);
        return;
      }
      this._append();
      const element = this._getElement();
      if (this._config.isAnimated) {
        reflow(element);
      }
      element.classList.add(CLASS_NAME_SHOW$5);
      this._emulateAnimation(() => {
        execute(callback);
      });
    }
    hide(callback) {
      if (!this._config.isVisible) {
        execute(callback);
        return;
      }
      this._getElement().classList.remove(CLASS_NAME_SHOW$5);
      this._emulateAnimation(() => {
        this.dispose();
        execute(callback);
      });
    }
    dispose() {
      if (!this._isAppended) {
        return;
      }
      EventHandler.off(this._element, EVENT_MOUSEDOWN);
      this._element.remove();
      this._isAppended = false;
    }

    // Private
    _getElement() {
      if (!this._element) {
        const backdrop = document.createElement('div');
        backdrop.className = this._config.className;
        if (this._config.isAnimated) {
          backdrop.classList.add(CLASS_NAME_FADE$4);
        }
        this._element = backdrop;
      }
      return this._element;
    }
    _configAfterMerge(config) {
      // use getElement() with the default "body" to get a fresh Element on each instantiation
      config.rootElement = getElement(config.rootElement);
      return config;
    }
    _append() {
      if (this._isAppended) {
        return;
      }
      const element = this._getElement();
      this._config.rootElement.append(element);
      EventHandler.on(element, EVENT_MOUSEDOWN, () => {
        execute(this._config.clickCallback);
      });
      this._isAppended = true;
    }
    _emulateAnimation(callback) {
      executeAfterTransition(callback, this._getElement(), this._config.isAnimated);
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap util/focustrap.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$8 = 'focustrap';
  const DATA_KEY$5 = 'bs.focustrap';
  const EVENT_KEY$5 = `.${DATA_KEY$5}`;
  const EVENT_FOCUSIN$2 = `focusin${EVENT_KEY$5}`;
  const EVENT_KEYDOWN_TAB = `keydown.tab${EVENT_KEY$5}`;
  const TAB_KEY = 'Tab';
  const TAB_NAV_FORWARD = 'forward';
  const TAB_NAV_BACKWARD = 'backward';
  const Default$7 = {
    autofocus: true,
    trapElement: null // The element to trap focus inside of
  };
  const DefaultType$7 = {
    autofocus: 'boolean',
    trapElement: 'element'
  };

  /**
   * Class definition
   */

  class FocusTrap extends Config {
    constructor(config) {
      super();
      this._config = this._getConfig(config);
      this._isActive = false;
      this._lastTabNavDirection = null;
    }

    // Getters
    static get Default() {
      return Default$7;
    }
    static get DefaultType() {
      return DefaultType$7;
    }
    static get NAME() {
      return NAME$8;
    }

    // Public
    activate() {
      if (this._isActive) {
        return;
      }
      if (this._config.autofocus) {
        this._config.trapElement.focus();
      }
      EventHandler.off(document, EVENT_KEY$5); // guard against infinite focus loop
      EventHandler.on(document, EVENT_FOCUSIN$2, event => this._handleFocusin(event));
      EventHandler.on(document, EVENT_KEYDOWN_TAB, event => this._handleKeydown(event));
      this._isActive = true;
    }
    deactivate() {
      if (!this._isActive) {
        return;
      }
      this._isActive = false;
      EventHandler.off(document, EVENT_KEY$5);
    }

    // Private
    _handleFocusin(event) {
      const {
        trapElement
      } = this._config;
      if (event.target === document || event.target === trapElement || trapElement.contains(event.target)) {
        return;
      }
      const elements = SelectorEngine.focusableChildren(trapElement);
      if (elements.length === 0) {
        trapElement.focus();
      } else if (this._lastTabNavDirection === TAB_NAV_BACKWARD) {
        elements[elements.length - 1].focus();
      } else {
        elements[0].focus();
      }
    }
    _handleKeydown(event) {
      if (event.key !== TAB_KEY) {
        return;
      }
      this._lastTabNavDirection = event.shiftKey ? TAB_NAV_BACKWARD : TAB_NAV_FORWARD;
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap util/scrollBar.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const SELECTOR_FIXED_CONTENT = '.fixed-top, .fixed-bottom, .is-fixed, .sticky-top';
  const SELECTOR_STICKY_CONTENT = '.sticky-top';
  const PROPERTY_PADDING = 'padding-right';
  const PROPERTY_MARGIN = 'margin-right';

  /**
   * Class definition
   */

  class ScrollBarHelper {
    constructor() {
      this._element = document.body;
    }

    // Public
    getWidth() {
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/innerWidth#usage_notes
      const documentWidth = document.documentElement.clientWidth;
      return Math.abs(window.innerWidth - documentWidth);
    }
    hide() {
      const width = this.getWidth();
      this._disableOverFlow();
      // give padding to element to balance the hidden scrollbar width
      this._setElementAttributes(this._element, PROPERTY_PADDING, calculatedValue => calculatedValue + width);
      // trick: We adjust positive paddingRight and negative marginRight to sticky-top elements to keep showing fullwidth
      this._setElementAttributes(SELECTOR_FIXED_CONTENT, PROPERTY_PADDING, calculatedValue => calculatedValue + width);
      this._setElementAttributes(SELECTOR_STICKY_CONTENT, PROPERTY_MARGIN, calculatedValue => calculatedValue - width);
    }
    reset() {
      this._resetElementAttributes(this._element, 'overflow');
      this._resetElementAttributes(this._element, PROPERTY_PADDING);
      this._resetElementAttributes(SELECTOR_FIXED_CONTENT, PROPERTY_PADDING);
      this._resetElementAttributes(SELECTOR_STICKY_CONTENT, PROPERTY_MARGIN);
    }
    isOverflowing() {
      return this.getWidth() > 0;
    }

    // Private
    _disableOverFlow() {
      this._saveInitialAttribute(this._element, 'overflow');
      this._element.style.overflow = 'hidden';
    }
    _setElementAttributes(selector, styleProperty, callback) {
      const scrollbarWidth = this.getWidth();
      const manipulationCallBack = element => {
        if (element !== this._element && window.innerWidth > element.clientWidth + scrollbarWidth) {
          return;
        }
        this._saveInitialAttribute(element, styleProperty);
        const calculatedValue = window.getComputedStyle(element).getPropertyValue(styleProperty);
        element.style.setProperty(styleProperty, `${callback(Number.parseFloat(calculatedValue))}px`);
      };
      this._applyManipulationCallback(selector, manipulationCallBack);
    }
    _saveInitialAttribute(element, styleProperty) {
      const actualValue = element.style.getPropertyValue(styleProperty);
      if (actualValue) {
        Manipulator.setDataAttribute(element, styleProperty, actualValue);
      }
    }
    _resetElementAttributes(selector, styleProperty) {
      const manipulationCallBack = element => {
        const value = Manipulator.getDataAttribute(element, styleProperty);
        // We only want to remove the property if the value is `null`; the value can also be zero
        if (value === null) {
          element.style.removeProperty(styleProperty);
          return;
        }
        Manipulator.removeDataAttribute(element, styleProperty);
        element.style.setProperty(styleProperty, value);
      };
      this._applyManipulationCallback(selector, manipulationCallBack);
    }
    _applyManipulationCallback(selector, callBack) {
      if (isElement$1(selector)) {
        callBack(selector);
        return;
      }
      for (const sel of SelectorEngine.find(selector, this._element)) {
        callBack(sel);
      }
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap modal.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$7 = 'modal';
  const DATA_KEY$4 = 'bs.modal';
  const EVENT_KEY$4 = `.${DATA_KEY$4}`;
  const DATA_API_KEY$2 = '.data-api';
  const ESCAPE_KEY$1 = 'Escape';
  const EVENT_HIDE$4 = `hide${EVENT_KEY$4}`;
  const EVENT_HIDE_PREVENTED$1 = `hidePrevented${EVENT_KEY$4}`;
  const EVENT_HIDDEN$4 = `hidden${EVENT_KEY$4}`;
  const EVENT_SHOW$4 = `show${EVENT_KEY$4}`;
  const EVENT_SHOWN$4 = `shown${EVENT_KEY$4}`;
  const EVENT_RESIZE$1 = `resize${EVENT_KEY$4}`;
  const EVENT_CLICK_DISMISS = `click.dismiss${EVENT_KEY$4}`;
  const EVENT_MOUSEDOWN_DISMISS = `mousedown.dismiss${EVENT_KEY$4}`;
  const EVENT_KEYDOWN_DISMISS$1 = `keydown.dismiss${EVENT_KEY$4}`;
  const EVENT_CLICK_DATA_API$2 = `click${EVENT_KEY$4}${DATA_API_KEY$2}`;
  const CLASS_NAME_OPEN = 'modal-open';
  const CLASS_NAME_FADE$3 = 'fade';
  const CLASS_NAME_SHOW$4 = 'show';
  const CLASS_NAME_STATIC = 'modal-static';
  const OPEN_SELECTOR$1 = '.modal.show';
  const SELECTOR_DIALOG = '.modal-dialog';
  const SELECTOR_MODAL_BODY = '.modal-body';
  const SELECTOR_DATA_TOGGLE$2 = '[data-bs-toggle="modal"]';
  const Default$6 = {
    backdrop: true,
    focus: true,
    keyboard: true
  };
  const DefaultType$6 = {
    backdrop: '(boolean|string)',
    focus: 'boolean',
    keyboard: 'boolean'
  };

  /**
   * Class definition
   */

  class Modal extends BaseComponent {
    constructor(element, config) {
      super(element, config);
      this._dialog = SelectorEngine.findOne(SELECTOR_DIALOG, this._element);
      this._backdrop = this._initializeBackDrop();
      this._focustrap = this._initializeFocusTrap();
      this._isShown = false;
      this._isTransitioning = false;
      this._scrollBar = new ScrollBarHelper();
      this._addEventListeners();
    }

    // Getters
    static get Default() {
      return Default$6;
    }
    static get DefaultType() {
      return DefaultType$6;
    }
    static get NAME() {
      return NAME$7;
    }

    // Public
    toggle(relatedTarget) {
      return this._isShown ? this.hide() : this.show(relatedTarget);
    }
    show(relatedTarget) {
      if (this._isShown || this._isTransitioning) {
        return;
      }
      const showEvent = EventHandler.trigger(this._element, EVENT_SHOW$4, {
        relatedTarget
      });
      if (showEvent.defaultPrevented) {
        return;
      }
      this._isShown = true;
      this._isTransitioning = true;
      this._scrollBar.hide();
      document.body.classList.add(CLASS_NAME_OPEN);
      this._adjustDialog();
      this._backdrop.show(() => this._showElement(relatedTarget));
    }
    hide() {
      if (!this._isShown || this._isTransitioning) {
        return;
      }
      const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE$4);
      if (hideEvent.defaultPrevented) {
        return;
      }
      this._isShown = false;
      this._isTransitioning = true;
      this._focustrap.deactivate();
      this._element.classList.remove(CLASS_NAME_SHOW$4);
      this._queueCallback(() => this._hideModal(), this._element, this._isAnimated());
    }
    dispose() {
      EventHandler.off(window, EVENT_KEY$4);
      EventHandler.off(this._dialog, EVENT_KEY$4);
      this._backdrop.dispose();
      this._focustrap.deactivate();
      super.dispose();
    }
    handleUpdate() {
      this._adjustDialog();
    }

    // Private
    _initializeBackDrop() {
      return new Backdrop({
        isVisible: Boolean(this._config.backdrop),
        // 'static' option will be translated to true, and booleans will keep their value,
        isAnimated: this._isAnimated()
      });
    }
    _initializeFocusTrap() {
      return new FocusTrap({
        trapElement: this._element
      });
    }
    _showElement(relatedTarget) {
      // try to append dynamic modal
      if (!document.body.contains(this._element)) {
        document.body.append(this._element);
      }
      this._element.style.display = 'block';
      this._element.removeAttribute('aria-hidden');
      this._element.setAttribute('aria-modal', true);
      this._element.setAttribute('role', 'dialog');
      this._element.scrollTop = 0;
      const modalBody = SelectorEngine.findOne(SELECTOR_MODAL_BODY, this._dialog);
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
      reflow(this._element);
      this._element.classList.add(CLASS_NAME_SHOW$4);
      const transitionComplete = () => {
        if (this._config.focus) {
          this._focustrap.activate();
        }
        this._isTransitioning = false;
        EventHandler.trigger(this._element, EVENT_SHOWN$4, {
          relatedTarget
        });
      };
      this._queueCallback(transitionComplete, this._dialog, this._isAnimated());
    }
    _addEventListeners() {
      EventHandler.on(this._element, EVENT_KEYDOWN_DISMISS$1, event => {
        if (event.key !== ESCAPE_KEY$1) {
          return;
        }
        if (this._config.keyboard) {
          this.hide();
          return;
        }
        this._triggerBackdropTransition();
      });
      EventHandler.on(window, EVENT_RESIZE$1, () => {
        if (this._isShown && !this._isTransitioning) {
          this._adjustDialog();
        }
      });
      EventHandler.on(this._element, EVENT_MOUSEDOWN_DISMISS, event => {
        // a bad trick to segregate clicks that may start inside dialog but end outside, and avoid listen to scrollbar clicks
        EventHandler.one(this._element, EVENT_CLICK_DISMISS, event2 => {
          if (this._element !== event.target || this._element !== event2.target) {
            return;
          }
          if (this._config.backdrop === 'static') {
            this._triggerBackdropTransition();
            return;
          }
          if (this._config.backdrop) {
            this.hide();
          }
        });
      });
    }
    _hideModal() {
      this._element.style.display = 'none';
      this._element.setAttribute('aria-hidden', true);
      this._element.removeAttribute('aria-modal');
      this._element.removeAttribute('role');
      this._isTransitioning = false;
      this._backdrop.hide(() => {
        document.body.classList.remove(CLASS_NAME_OPEN);
        this._resetAdjustments();
        this._scrollBar.reset();
        EventHandler.trigger(this._element, EVENT_HIDDEN$4);
      });
    }
    _isAnimated() {
      return this._element.classList.contains(CLASS_NAME_FADE$3);
    }
    _triggerBackdropTransition() {
      const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE_PREVENTED$1);
      if (hideEvent.defaultPrevented) {
        return;
      }
      const isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight;
      const initialOverflowY = this._element.style.overflowY;
      // return if the following background transition hasn't yet completed
      if (initialOverflowY === 'hidden' || this._element.classList.contains(CLASS_NAME_STATIC)) {
        return;
      }
      if (!isModalOverflowing) {
        this._element.style.overflowY = 'hidden';
      }
      this._element.classList.add(CLASS_NAME_STATIC);
      this._queueCallback(() => {
        this._element.classList.remove(CLASS_NAME_STATIC);
        this._queueCallback(() => {
          this._element.style.overflowY = initialOverflowY;
        }, this._dialog);
      }, this._dialog);
      this._element.focus();
    }

    /**
     * The following methods are used to handle overflowing modals
     */

    _adjustDialog() {
      const isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight;
      const scrollbarWidth = this._scrollBar.getWidth();
      const isBodyOverflowing = scrollbarWidth > 0;
      if (isBodyOverflowing && !isModalOverflowing) {
        const property = isRTL() ? 'paddingLeft' : 'paddingRight';
        this._element.style[property] = `${scrollbarWidth}px`;
      }
      if (!isBodyOverflowing && isModalOverflowing) {
        const property = isRTL() ? 'paddingRight' : 'paddingLeft';
        this._element.style[property] = `${scrollbarWidth}px`;
      }
    }
    _resetAdjustments() {
      this._element.style.paddingLeft = '';
      this._element.style.paddingRight = '';
    }

    // Static
    static jQueryInterface(config, relatedTarget) {
      return this.each(function () {
        const data = Modal.getOrCreateInstance(this, config);
        if (typeof config !== 'string') {
          return;
        }
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`);
        }
        data[config](relatedTarget);
      });
    }
  }

  /**
   * Data API implementation
   */

  EventHandler.on(document, EVENT_CLICK_DATA_API$2, SELECTOR_DATA_TOGGLE$2, function (event) {
    const target = SelectorEngine.getElementFromSelector(this);
    if (['A', 'AREA'].includes(this.tagName)) {
      event.preventDefault();
    }
    EventHandler.one(target, EVENT_SHOW$4, showEvent => {
      if (showEvent.defaultPrevented) {
        // only register focus restorer if modal will actually get shown
        return;
      }
      EventHandler.one(target, EVENT_HIDDEN$4, () => {
        if (isVisible(this)) {
          this.focus();
        }
      });
    });

    // avoid conflict when clicking modal toggler while another one is open
    const alreadyOpen = SelectorEngine.findOne(OPEN_SELECTOR$1);
    if (alreadyOpen) {
      Modal.getInstance(alreadyOpen).hide();
    }
    const data = Modal.getOrCreateInstance(target);
    data.toggle(this);
  });
  enableDismissTrigger(Modal);

  /**
   * jQuery
   */

  defineJQueryPlugin(Modal);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap offcanvas.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$6 = 'offcanvas';
  const DATA_KEY$3 = 'bs.offcanvas';
  const EVENT_KEY$3 = `.${DATA_KEY$3}`;
  const DATA_API_KEY$1 = '.data-api';
  const EVENT_LOAD_DATA_API$2 = `load${EVENT_KEY$3}${DATA_API_KEY$1}`;
  const ESCAPE_KEY = 'Escape';
  const CLASS_NAME_SHOW$3 = 'show';
  const CLASS_NAME_SHOWING$1 = 'showing';
  const CLASS_NAME_HIDING = 'hiding';
  const CLASS_NAME_BACKDROP = 'offcanvas-backdrop';
  const OPEN_SELECTOR = '.offcanvas.show';
  const EVENT_SHOW$3 = `show${EVENT_KEY$3}`;
  const EVENT_SHOWN$3 = `shown${EVENT_KEY$3}`;
  const EVENT_HIDE$3 = `hide${EVENT_KEY$3}`;
  const EVENT_HIDE_PREVENTED = `hidePrevented${EVENT_KEY$3}`;
  const EVENT_HIDDEN$3 = `hidden${EVENT_KEY$3}`;
  const EVENT_RESIZE = `resize${EVENT_KEY$3}`;
  const EVENT_CLICK_DATA_API$1 = `click${EVENT_KEY$3}${DATA_API_KEY$1}`;
  const EVENT_KEYDOWN_DISMISS = `keydown.dismiss${EVENT_KEY$3}`;
  const SELECTOR_DATA_TOGGLE$1 = '[data-bs-toggle="offcanvas"]';
  const Default$5 = {
    backdrop: true,
    keyboard: true,
    scroll: false
  };
  const DefaultType$5 = {
    backdrop: '(boolean|string)',
    keyboard: 'boolean',
    scroll: 'boolean'
  };

  /**
   * Class definition
   */

  class Offcanvas extends BaseComponent {
    constructor(element, config) {
      super(element, config);
      this._isShown = false;
      this._backdrop = this._initializeBackDrop();
      this._focustrap = this._initializeFocusTrap();
      this._addEventListeners();
    }

    // Getters
    static get Default() {
      return Default$5;
    }
    static get DefaultType() {
      return DefaultType$5;
    }
    static get NAME() {
      return NAME$6;
    }

    // Public
    toggle(relatedTarget) {
      return this._isShown ? this.hide() : this.show(relatedTarget);
    }
    show(relatedTarget) {
      if (this._isShown) {
        return;
      }
      const showEvent = EventHandler.trigger(this._element, EVENT_SHOW$3, {
        relatedTarget
      });
      if (showEvent.defaultPrevented) {
        return;
      }
      this._isShown = true;
      this._backdrop.show();
      if (!this._config.scroll) {
        new ScrollBarHelper().hide();
      }
      this._element.setAttribute('aria-modal', true);
      this._element.setAttribute('role', 'dialog');
      this._element.classList.add(CLASS_NAME_SHOWING$1);
      const completeCallBack = () => {
        if (!this._config.scroll || this._config.backdrop) {
          this._focustrap.activate();
        }
        this._element.classList.add(CLASS_NAME_SHOW$3);
        this._element.classList.remove(CLASS_NAME_SHOWING$1);
        EventHandler.trigger(this._element, EVENT_SHOWN$3, {
          relatedTarget
        });
      };
      this._queueCallback(completeCallBack, this._element, true);
    }
    hide() {
      if (!this._isShown) {
        return;
      }
      const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE$3);
      if (hideEvent.defaultPrevented) {
        return;
      }
      this._focustrap.deactivate();
      this._element.blur();
      this._isShown = false;
      this._element.classList.add(CLASS_NAME_HIDING);
      this._backdrop.hide();
      const completeCallback = () => {
        this._element.classList.remove(CLASS_NAME_SHOW$3, CLASS_NAME_HIDING);
        this._element.removeAttribute('aria-modal');
        this._element.removeAttribute('role');
        if (!this._config.scroll) {
          new ScrollBarHelper().reset();
        }
        EventHandler.trigger(this._element, EVENT_HIDDEN$3);
      };
      this._queueCallback(completeCallback, this._element, true);
    }
    dispose() {
      this._backdrop.dispose();
      this._focustrap.deactivate();
      super.dispose();
    }

    // Private
    _initializeBackDrop() {
      const clickCallback = () => {
        if (this._config.backdrop === 'static') {
          EventHandler.trigger(this._element, EVENT_HIDE_PREVENTED);
          return;
        }
        this.hide();
      };

      // 'static' option will be translated to true, and booleans will keep their value
      const isVisible = Boolean(this._config.backdrop);
      return new Backdrop({
        className: CLASS_NAME_BACKDROP,
        isVisible,
        isAnimated: true,
        rootElement: this._element.parentNode,
        clickCallback: isVisible ? clickCallback : null
      });
    }
    _initializeFocusTrap() {
      return new FocusTrap({
        trapElement: this._element
      });
    }
    _addEventListeners() {
      EventHandler.on(this._element, EVENT_KEYDOWN_DISMISS, event => {
        if (event.key !== ESCAPE_KEY) {
          return;
        }
        if (this._config.keyboard) {
          this.hide();
          return;
        }
        EventHandler.trigger(this._element, EVENT_HIDE_PREVENTED);
      });
    }

    // Static
    static jQueryInterface(config) {
      return this.each(function () {
        const data = Offcanvas.getOrCreateInstance(this, config);
        if (typeof config !== 'string') {
          return;
        }
        if (data[config] === undefined || config.startsWith('_') || config === 'constructor') {
          throw new TypeError(`No method named "${config}"`);
        }
        data[config](this);
      });
    }
  }

  /**
   * Data API implementation
   */

  EventHandler.on(document, EVENT_CLICK_DATA_API$1, SELECTOR_DATA_TOGGLE$1, function (event) {
    const target = SelectorEngine.getElementFromSelector(this);
    if (['A', 'AREA'].includes(this.tagName)) {
      event.preventDefault();
    }
    if (isDisabled(this)) {
      return;
    }
    EventHandler.one(target, EVENT_HIDDEN$3, () => {
      // focus on trigger when it is closed
      if (isVisible(this)) {
        this.focus();
      }
    });

    // avoid conflict when clicking a toggler of an offcanvas, while another is open
    const alreadyOpen = SelectorEngine.findOne(OPEN_SELECTOR);
    if (alreadyOpen && alreadyOpen !== target) {
      Offcanvas.getInstance(alreadyOpen).hide();
    }
    const data = Offcanvas.getOrCreateInstance(target);
    data.toggle(this);
  });
  EventHandler.on(window, EVENT_LOAD_DATA_API$2, () => {
    for (const selector of SelectorEngine.find(OPEN_SELECTOR)) {
      Offcanvas.getOrCreateInstance(selector).show();
    }
  });
  EventHandler.on(window, EVENT_RESIZE, () => {
    for (const element of SelectorEngine.find('[aria-modal][class*=show][class*=offcanvas-]')) {
      if (getComputedStyle(element).position !== 'fixed') {
        Offcanvas.getOrCreateInstance(element).hide();
      }
    }
  });
  enableDismissTrigger(Offcanvas);

  /**
   * jQuery
   */

  defineJQueryPlugin(Offcanvas);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap util/sanitizer.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  // js-docs-start allow-list
  const ARIA_ATTRIBUTE_PATTERN = /^aria-[\w-]*$/i;
  const DefaultAllowlist = {
    // Global attributes allowed on any supplied element below.
    '*': ['class', 'dir', 'id', 'lang', 'role', ARIA_ATTRIBUTE_PATTERN],
    a: ['target', 'href', 'title', 'rel'],
    area: [],
    b: [],
    br: [],
    col: [],
    code: [],
    dd: [],
    div: [],
    dl: [],
    dt: [],
    em: [],
    hr: [],
    h1: [],
    h2: [],
    h3: [],
    h4: [],
    h5: [],
    h6: [],
    i: [],
    img: ['src', 'srcset', 'alt', 'title', 'width', 'height'],
    li: [],
    ol: [],
    p: [],
    pre: [],
    s: [],
    small: [],
    span: [],
    sub: [],
    sup: [],
    strong: [],
    u: [],
    ul: []
  };
  // js-docs-end allow-list

  const uriAttributes = new Set(['background', 'cite', 'href', 'itemtype', 'longdesc', 'poster', 'src', 'xlink:href']);

  /**
   * A pattern that recognizes URLs that are safe wrt. XSS in URL navigation
   * contexts.
   *
   * Shout-out to Angular https://github.com/angular/angular/blob/15.2.8/packages/core/src/sanitization/url_sanitizer.ts#L38
   */
  // eslint-disable-next-line unicorn/better-regex
  const SAFE_URL_PATTERN = /^(?!javascript:)(?:[a-z0-9+.-]+:|[^&:/?#]*(?:[/?#]|$))/i;
  const allowedAttribute = (attribute, allowedAttributeList) => {
    const attributeName = attribute.nodeName.toLowerCase();
    if (allowedAttributeList.includes(attributeName)) {
      if (uriAttributes.has(attributeName)) {
        return Boolean(SAFE_URL_PATTERN.test(attribute.nodeValue));
      }
      return true;
    }

    // Check if a regular expression validates the attribute.
    return allowedAttributeList.filter(attributeRegex => attributeRegex instanceof RegExp).some(regex => regex.test(attributeName));
  };
  function sanitizeHtml(unsafeHtml, allowList, sanitizeFunction) {
    if (!unsafeHtml.length) {
      return unsafeHtml;
    }
    if (sanitizeFunction && typeof sanitizeFunction === 'function') {
      return sanitizeFunction(unsafeHtml);
    }
    const domParser = new window.DOMParser();
    const createdDocument = domParser.parseFromString(unsafeHtml, 'text/html');
    const elements = [].concat(...createdDocument.body.querySelectorAll('*'));
    for (const element of elements) {
      const elementName = element.nodeName.toLowerCase();
      if (!Object.keys(allowList).includes(elementName)) {
        element.remove();
        continue;
      }
      const attributeList = [].concat(...element.attributes);
      const allowedAttributes = [].concat(allowList['*'] || [], allowList[elementName] || []);
      for (const attribute of attributeList) {
        if (!allowedAttribute(attribute, allowedAttributes)) {
          element.removeAttribute(attribute.nodeName);
        }
      }
    }
    return createdDocument.body.innerHTML;
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap util/template-factory.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$5 = 'TemplateFactory';
  const Default$4 = {
    allowList: DefaultAllowlist,
    content: {},
    // { selector : text ,  selector2 : text2 , }
    extraClass: '',
    html: false,
    sanitize: true,
    sanitizeFn: null,
    template: '<div></div>'
  };
  const DefaultType$4 = {
    allowList: 'object',
    content: 'object',
    extraClass: '(string|function)',
    html: 'boolean',
    sanitize: 'boolean',
    sanitizeFn: '(null|function)',
    template: 'string'
  };
  const DefaultContentType = {
    entry: '(string|element|function|null)',
    selector: '(string|element)'
  };

  /**
   * Class definition
   */

  class TemplateFactory extends Config {
    constructor(config) {
      super();
      this._config = this._getConfig(config);
    }

    // Getters
    static get Default() {
      return Default$4;
    }
    static get DefaultType() {
      return DefaultType$4;
    }
    static get NAME() {
      return NAME$5;
    }

    // Public
    getContent() {
      return Object.values(this._config.content).map(config => this._resolvePossibleFunction(config)).filter(Boolean);
    }
    hasContent() {
      return this.getContent().length > 0;
    }
    changeContent(content) {
      this._checkContent(content);
      this._config.content = {
        ...this._config.content,
        ...content
      };
      return this;
    }
    toHtml() {
      const templateWrapper = document.createElement('div');
      templateWrapper.innerHTML = this._maybeSanitize(this._config.template);
      for (const [selector, text] of Object.entries(this._config.content)) {
        this._setContent(templateWrapper, text, selector);
      }
      const template = templateWrapper.children[0];
      const extraClass = this._resolvePossibleFunction(this._config.extraClass);
      if (extraClass) {
        template.classList.add(...extraClass.split(' '));
      }
      return template;
    }

    // Private
    _typeCheckConfig(config) {
      super._typeCheckConfig(config);
      this._checkContent(config.content);
    }
    _checkContent(arg) {
      for (const [selector, content] of Object.entries(arg)) {
        super._typeCheckConfig({
          selector,
          entry: content
        }, DefaultContentType);
      }
    }
    _setContent(template, content, selector) {
      const templateElement = SelectorEngine.findOne(selector, template);
      if (!templateElement) {
        return;
      }
      content = this._resolvePossibleFunction(content);
      if (!content) {
        templateElement.remove();
        return;
      }
      if (isElement$1(content)) {
        this._putElementInTemplate(getElement(content), templateElement);
        return;
      }
      if (this._config.html) {
        templateElement.innerHTML = this._maybeSanitize(content);
        return;
      }
      templateElement.textContent = content;
    }
    _maybeSanitize(arg) {
      return this._config.sanitize ? sanitizeHtml(arg, this._config.allowList, this._config.sanitizeFn) : arg;
    }
    _resolvePossibleFunction(arg) {
      return execute(arg, [this]);
    }
    _putElementInTemplate(element, templateElement) {
      if (this._config.html) {
        templateElement.innerHTML = '';
        templateElement.append(element);
        return;
      }
      templateElement.textContent = element.textContent;
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap tooltip.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$4 = 'tooltip';
  const DISALLOWED_ATTRIBUTES = new Set(['sanitize', 'allowList', 'sanitizeFn']);
  const CLASS_NAME_FADE$2 = 'fade';
  const CLASS_NAME_MODAL = 'modal';
  const CLASS_NAME_SHOW$2 = 'show';
  const SELECTOR_TOOLTIP_INNER = '.tooltip-inner';
  const SELECTOR_MODAL = `.${CLASS_NAME_MODAL}`;
  const EVENT_MODAL_HIDE = 'hide.bs.modal';
  const TRIGGER_HOVER = 'hover';
  const TRIGGER_FOCUS = 'focus';
  const TRIGGER_CLICK = 'click';
  const TRIGGER_MANUAL = 'manual';
  const EVENT_HIDE$2 = 'hide';
  const EVENT_HIDDEN$2 = 'hidden';
  const EVENT_SHOW$2 = 'show';
  const EVENT_SHOWN$2 = 'shown';
  const EVENT_INSERTED = 'inserted';
  const EVENT_CLICK$1 = 'click';
  const EVENT_FOCUSIN$1 = 'focusin';
  const EVENT_FOCUSOUT$1 = 'focusout';
  const EVENT_MOUSEENTER = 'mouseenter';
  const EVENT_MOUSELEAVE = 'mouseleave';
  const AttachmentMap = {
    AUTO: 'auto',
    TOP: 'top',
    RIGHT: isRTL() ? 'left' : 'right',
    BOTTOM: 'bottom',
    LEFT: isRTL() ? 'right' : 'left'
  };
  const Default$3 = {
    allowList: DefaultAllowlist,
    animation: true,
    boundary: 'clippingParents',
    container: false,
    customClass: '',
    delay: 0,
    fallbackPlacements: ['top', 'right', 'bottom', 'left'],
    html: false,
    offset: [0, 6],
    placement: 'top',
    popperConfig: null,
    sanitize: true,
    sanitizeFn: null,
    selector: false,
    template: '<div class="tooltip" role="tooltip">' + '<div class="tooltip-arrow"></div>' + '<div class="tooltip-inner"></div>' + '</div>',
    title: '',
    trigger: 'hover focus'
  };
  const DefaultType$3 = {
    allowList: 'object',
    animation: 'boolean',
    boundary: '(string|element)',
    container: '(string|element|boolean)',
    customClass: '(string|function)',
    delay: '(number|object)',
    fallbackPlacements: 'array',
    html: 'boolean',
    offset: '(array|string|function)',
    placement: '(string|function)',
    popperConfig: '(null|object|function)',
    sanitize: 'boolean',
    sanitizeFn: '(null|function)',
    selector: '(string|boolean)',
    template: 'string',
    title: '(string|element|function)',
    trigger: 'string'
  };

  /**
   * Class definition
   */

  class Tooltip extends BaseComponent {
    constructor(element, config) {
      if (typeof Popper === 'undefined') {
        throw new TypeError('Bootstrap\'s tooltips require Popper (https://popper.js.org)');
      }
      super(element, config);

      // Private
      this._isEnabled = true;
      this._timeout = 0;
      this._isHovered = null;
      this._activeTrigger = {};
      this._popper = null;
      this._templateFactory = null;
      this._newContent = null;

      // Protected
      this.tip = null;
      this._setListeners();
      if (!this._config.selector) {
        this._fixTitle();
      }
    }

    // Getters
    static get Default() {
      return Default$3;
    }
    static get DefaultType() {
      return DefaultType$3;
    }
    static get NAME() {
      return NAME$4;
    }

    // Public
    enable() {
      this._isEnabled = true;
    }
    disable() {
      this._isEnabled = false;
    }
    toggleEnabled() {
      this._isEnabled = !this._isEnabled;
    }
    toggle() {
      if (!this._isEnabled) {
        return;
      }
      this._activeTrigger.click = !this._activeTrigger.click;
      if (this._isShown()) {
        this._leave();
        return;
      }
      this._enter();
    }
    dispose() {
      clearTimeout(this._timeout);
      EventHandler.off(this._element.closest(SELECTOR_MODAL), EVENT_MODAL_HIDE, this._hideModalHandler);
      if (this._element.getAttribute('data-bs-original-title')) {
        this._element.setAttribute('title', this._element.getAttribute('data-bs-original-title'));
      }
      this._disposePopper();
      super.dispose();
    }
    show() {
      if (this._element.style.display === 'none') {
        throw new Error('Please use show on visible elements');
      }
      if (!(this._isWithContent() && this._isEnabled)) {
        return;
      }
      const showEvent = EventHandler.trigger(this._element, this.constructor.eventName(EVENT_SHOW$2));
      const shadowRoot = findShadowRoot(this._element);
      const isInTheDom = (shadowRoot || this._element.ownerDocument.documentElement).contains(this._element);
      if (showEvent.defaultPrevented || !isInTheDom) {
        return;
      }

      // TODO: v6 remove this or make it optional
      this._disposePopper();
      const tip = this._getTipElement();
      this._element.setAttribute('aria-describedby', tip.getAttribute('id'));
      const {
        container
      } = this._config;
      if (!this._element.ownerDocument.documentElement.contains(this.tip)) {
        container.append(tip);
        EventHandler.trigger(this._element, this.constructor.eventName(EVENT_INSERTED));
      }
      this._popper = this._createPopper(tip);
      tip.classList.add(CLASS_NAME_SHOW$2);

      // If this is a touch-enabled device we add extra
      // empty mouseover listeners to the body's immediate children;
      // only needed because of broken event delegation on iOS
      // https://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html
      if ('ontouchstart' in document.documentElement) {
        for (const element of [].concat(...document.body.children)) {
          EventHandler.on(element, 'mouseover', noop);
        }
      }
      const complete = () => {
        EventHandler.trigger(this._element, this.constructor.eventName(EVENT_SHOWN$2));
        if (this._isHovered === false) {
          this._leave();
        }
        this._isHovered = false;
      };
      this._queueCallback(complete, this.tip, this._isAnimated());
    }
    hide() {
      if (!this._isShown()) {
        return;
      }
      const hideEvent = EventHandler.trigger(this._element, this.constructor.eventName(EVENT_HIDE$2));
      if (hideEvent.defaultPrevented) {
        return;
      }
      const tip = this._getTipElement();
      tip.classList.remove(CLASS_NAME_SHOW$2);

      // If this is a touch-enabled device we remove the extra
      // empty mouseover listeners we added for iOS support
      if ('ontouchstart' in document.documentElement) {
        for (const element of [].concat(...document.body.children)) {
          EventHandler.off(element, 'mouseover', noop);
        }
      }
      this._activeTrigger[TRIGGER_CLICK] = false;
      this._activeTrigger[TRIGGER_FOCUS] = false;
      this._activeTrigger[TRIGGER_HOVER] = false;
      this._isHovered = null; // it is a trick to support manual triggering

      const complete = () => {
        if (this._isWithActiveTrigger()) {
          return;
        }
        if (!this._isHovered) {
          this._disposePopper();
        }
        this._element.removeAttribute('aria-describedby');
        EventHandler.trigger(this._element, this.constructor.eventName(EVENT_HIDDEN$2));
      };
      this._queueCallback(complete, this.tip, this._isAnimated());
    }
    update() {
      if (this._popper) {
        this._popper.update();
      }
    }

    // Protected
    _isWithContent() {
      return Boolean(this._getTitle());
    }
    _getTipElement() {
      if (!this.tip) {
        this.tip = this._createTipElement(this._newContent || this._getContentForTemplate());
      }
      return this.tip;
    }
    _createTipElement(content) {
      const tip = this._getTemplateFactory(content).toHtml();

      // TODO: remove this check in v6
      if (!tip) {
        return null;
      }
      tip.classList.remove(CLASS_NAME_FADE$2, CLASS_NAME_SHOW$2);
      // TODO: v6 the following can be achieved with CSS only
      tip.classList.add(`bs-${this.constructor.NAME}-auto`);
      const tipId = getUID(this.constructor.NAME).toString();
      tip.setAttribute('id', tipId);
      if (this._isAnimated()) {
        tip.classList.add(CLASS_NAME_FADE$2);
      }
      return tip;
    }
    setContent(content) {
      this._newContent = content;
      if (this._isShown()) {
        this._disposePopper();
        this.show();
      }
    }
    _getTemplateFactory(content) {
      if (this._templateFactory) {
        this._templateFactory.changeContent(content);
      } else {
        this._templateFactory = new TemplateFactory({
          ...this._config,
          // the `content` var has to be after `this._config`
          // to override config.content in case of popover
          content,
          extraClass: this._resolvePossibleFunction(this._config.customClass)
        });
      }
      return this._templateFactory;
    }
    _getContentForTemplate() {
      return {
        [SELECTOR_TOOLTIP_INNER]: this._getTitle()
      };
    }
    _getTitle() {
      return this._resolvePossibleFunction(this._config.title) || this._element.getAttribute('data-bs-original-title');
    }

    // Private
    _initializeOnDelegatedTarget(event) {
      return this.constructor.getOrCreateInstance(event.delegateTarget, this._getDelegateConfig());
    }
    _isAnimated() {
      return this._config.animation || this.tip && this.tip.classList.contains(CLASS_NAME_FADE$2);
    }
    _isShown() {
      return this.tip && this.tip.classList.contains(CLASS_NAME_SHOW$2);
    }
    _createPopper(tip) {
      const placement = execute(this._config.placement, [this, tip, this._element]);
      const attachment = AttachmentMap[placement.toUpperCase()];
      return createPopper(this._element, tip, this._getPopperConfig(attachment));
    }
    _getOffset() {
      const {
        offset
      } = this._config;
      if (typeof offset === 'string') {
        return offset.split(',').map(value => Number.parseInt(value, 10));
      }
      if (typeof offset === 'function') {
        return popperData => offset(popperData, this._element);
      }
      return offset;
    }
    _resolvePossibleFunction(arg) {
      return execute(arg, [this._element]);
    }
    _getPopperConfig(attachment) {
      const defaultBsPopperConfig = {
        placement: attachment,
        modifiers: [{
          name: 'flip',
          options: {
            fallbackPlacements: this._config.fallbackPlacements
          }
        }, {
          name: 'offset',
          options: {
            offset: this._getOffset()
          }
        }, {
          name: 'preventOverflow',
          options: {
            boundary: this._config.boundary
          }
        }, {
          name: 'arrow',
          options: {
            element: `.${this.constructor.NAME}-arrow`
          }
        }, {
          name: 'preSetPlacement',
          enabled: true,
          phase: 'beforeMain',
          fn: data => {
            // Pre-set Popper's placement attribute in order to read the arrow sizes properly.
            // Otherwise, Popper mixes up the width and height dimensions since the initial arrow style is for top placement
            this._getTipElement().setAttribute('data-popper-placement', data.state.placement);
          }
        }]
      };
      return {
        ...defaultBsPopperConfig,
        ...execute(this._config.popperConfig, [defaultBsPopperConfig])
      };
    }
    _setListeners() {
      const triggers = this._config.trigger.split(' ');
      for (const trigger of triggers) {
        if (trigger === 'click') {
          EventHandler.on(this._element, this.constructor.eventName(EVENT_CLICK$1), this._config.selector, event => {
            const context = this._initializeOnDelegatedTarget(event);
            context.toggle();
          });
        } else if (trigger !== TRIGGER_MANUAL) {
          const eventIn = trigger === TRIGGER_HOVER ? this.constructor.eventName(EVENT_MOUSEENTER) : this.constructor.eventName(EVENT_FOCUSIN$1);
          const eventOut = trigger === TRIGGER_HOVER ? this.constructor.eventName(EVENT_MOUSELEAVE) : this.constructor.eventName(EVENT_FOCUSOUT$1);
          EventHandler.on(this._element, eventIn, this._config.selector, event => {
            const context = this._initializeOnDelegatedTarget(event);
            context._activeTrigger[event.type === 'focusin' ? TRIGGER_FOCUS : TRIGGER_HOVER] = true;
            context._enter();
          });
          EventHandler.on(this._element, eventOut, this._config.selector, event => {
            const context = this._initializeOnDelegatedTarget(event);
            context._activeTrigger[event.type === 'focusout' ? TRIGGER_FOCUS : TRIGGER_HOVER] = context._element.contains(event.relatedTarget);
            context._leave();
          });
        }
      }
      this._hideModalHandler = () => {
        if (this._element) {
          this.hide();
        }
      };
      EventHandler.on(this._element.closest(SELECTOR_MODAL), EVENT_MODAL_HIDE, this._hideModalHandler);
    }
    _fixTitle() {
      const title = this._element.getAttribute('title');
      if (!title) {
        return;
      }
      if (!this._element.getAttribute('aria-label') && !this._element.textContent.trim()) {
        this._element.setAttribute('aria-label', title);
      }
      this._element.setAttribute('data-bs-original-title', title); // DO NOT USE IT. Is only for backwards compatibility
      this._element.removeAttribute('title');
    }
    _enter() {
      if (this._isShown() || this._isHovered) {
        this._isHovered = true;
        return;
      }
      this._isHovered = true;
      this._setTimeout(() => {
        if (this._isHovered) {
          this.show();
        }
      }, this._config.delay.show);
    }
    _leave() {
      if (this._isWithActiveTrigger()) {
        return;
      }
      this._isHovered = false;
      this._setTimeout(() => {
        if (!this._isHovered) {
          this.hide();
        }
      }, this._config.delay.hide);
    }
    _setTimeout(handler, timeout) {
      clearTimeout(this._timeout);
      this._timeout = setTimeout(handler, timeout);
    }
    _isWithActiveTrigger() {
      return Object.values(this._activeTrigger).includes(true);
    }
    _getConfig(config) {
      const dataAttributes = Manipulator.getDataAttributes(this._element);
      for (const dataAttribute of Object.keys(dataAttributes)) {
        if (DISALLOWED_ATTRIBUTES.has(dataAttribute)) {
          delete dataAttributes[dataAttribute];
        }
      }
      config = {
        ...dataAttributes,
        ...(typeof config === 'object' && config ? config : {})
      };
      config = this._mergeConfigObj(config);
      config = this._configAfterMerge(config);
      this._typeCheckConfig(config);
      return config;
    }
    _configAfterMerge(config) {
      config.container = config.container === false ? document.body : getElement(config.container);
      if (typeof config.delay === 'number') {
        config.delay = {
          show: config.delay,
          hide: config.delay
        };
      }
      if (typeof config.title === 'number') {
        config.title = config.title.toString();
      }
      if (typeof config.content === 'number') {
        config.content = config.content.toString();
      }
      return config;
    }
    _getDelegateConfig() {
      const config = {};
      for (const [key, value] of Object.entries(this._config)) {
        if (this.constructor.Default[key] !== value) {
          config[key] = value;
        }
      }
      config.selector = false;
      config.trigger = 'manual';

      // In the future can be replaced with:
      // const keysWithDifferentValues = Object.entries(this._config).filter(entry => this.constructor.Default[entry[0]] !== this._config[entry[0]])
      // `Object.fromEntries(keysWithDifferentValues)`
      return config;
    }
    _disposePopper() {
      if (this._popper) {
        this._popper.destroy();
        this._popper = null;
      }
      if (this.tip) {
        this.tip.remove();
        this.tip = null;
      }
    }

    // Static
    static jQueryInterface(config) {
      return this.each(function () {
        const data = Tooltip.getOrCreateInstance(this, config);
        if (typeof config !== 'string') {
          return;
        }
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`);
        }
        data[config]();
      });
    }
  }

  /**
   * jQuery
   */

  defineJQueryPlugin(Tooltip);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap popover.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$3 = 'popover';
  const SELECTOR_TITLE = '.popover-header';
  const SELECTOR_CONTENT = '.popover-body';
  const Default$2 = {
    ...Tooltip.Default,
    content: '',
    offset: [0, 8],
    placement: 'right',
    template: '<div class="popover" role="tooltip">' + '<div class="popover-arrow"></div>' + '<h3 class="popover-header"></h3>' + '<div class="popover-body"></div>' + '</div>',
    trigger: 'click'
  };
  const DefaultType$2 = {
    ...Tooltip.DefaultType,
    content: '(null|string|element|function)'
  };

  /**
   * Class definition
   */

  class Popover extends Tooltip {
    // Getters
    static get Default() {
      return Default$2;
    }
    static get DefaultType() {
      return DefaultType$2;
    }
    static get NAME() {
      return NAME$3;
    }

    // Overrides
    _isWithContent() {
      return this._getTitle() || this._getContent();
    }

    // Private
    _getContentForTemplate() {
      return {
        [SELECTOR_TITLE]: this._getTitle(),
        [SELECTOR_CONTENT]: this._getContent()
      };
    }
    _getContent() {
      return this._resolvePossibleFunction(this._config.content);
    }

    // Static
    static jQueryInterface(config) {
      return this.each(function () {
        const data = Popover.getOrCreateInstance(this, config);
        if (typeof config !== 'string') {
          return;
        }
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`);
        }
        data[config]();
      });
    }
  }

  /**
   * jQuery
   */

  defineJQueryPlugin(Popover);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap scrollspy.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$2 = 'scrollspy';
  const DATA_KEY$2 = 'bs.scrollspy';
  const EVENT_KEY$2 = `.${DATA_KEY$2}`;
  const DATA_API_KEY = '.data-api';
  const EVENT_ACTIVATE = `activate${EVENT_KEY$2}`;
  const EVENT_CLICK = `click${EVENT_KEY$2}`;
  const EVENT_LOAD_DATA_API$1 = `load${EVENT_KEY$2}${DATA_API_KEY}`;
  const CLASS_NAME_DROPDOWN_ITEM = 'dropdown-item';
  const CLASS_NAME_ACTIVE$1 = 'active';
  const SELECTOR_DATA_SPY = '[data-bs-spy="scroll"]';
  const SELECTOR_TARGET_LINKS = '[href]';
  const SELECTOR_NAV_LIST_GROUP = '.nav, .list-group';
  const SELECTOR_NAV_LINKS = '.nav-link';
  const SELECTOR_NAV_ITEMS = '.nav-item';
  const SELECTOR_LIST_ITEMS = '.list-group-item';
  const SELECTOR_LINK_ITEMS = `${SELECTOR_NAV_LINKS}, ${SELECTOR_NAV_ITEMS} > ${SELECTOR_NAV_LINKS}, ${SELECTOR_LIST_ITEMS}`;
  const SELECTOR_DROPDOWN = '.dropdown';
  const SELECTOR_DROPDOWN_TOGGLE$1 = '.dropdown-toggle';
  const Default$1 = {
    offset: null,
    // TODO: v6 @deprecated, keep it for backwards compatibility reasons
    rootMargin: '0px 0px -25%',
    smoothScroll: false,
    target: null,
    threshold: [0.1, 0.5, 1]
  };
  const DefaultType$1 = {
    offset: '(number|null)',
    // TODO v6 @deprecated, keep it for backwards compatibility reasons
    rootMargin: 'string',
    smoothScroll: 'boolean',
    target: 'element',
    threshold: 'array'
  };

  /**
   * Class definition
   */

  class ScrollSpy extends BaseComponent {
    constructor(element, config) {
      super(element, config);

      // this._element is the observablesContainer and config.target the menu links wrapper
      this._targetLinks = new Map();
      this._observableSections = new Map();
      this._rootElement = getComputedStyle(this._element).overflowY === 'visible' ? null : this._element;
      this._activeTarget = null;
      this._observer = null;
      this._previousScrollData = {
        visibleEntryTop: 0,
        parentScrollTop: 0
      };
      this.refresh(); // initialize
    }

    // Getters
    static get Default() {
      return Default$1;
    }
    static get DefaultType() {
      return DefaultType$1;
    }
    static get NAME() {
      return NAME$2;
    }

    // Public
    refresh() {
      this._initializeTargetsAndObservables();
      this._maybeEnableSmoothScroll();
      if (this._observer) {
        this._observer.disconnect();
      } else {
        this._observer = this._getNewObserver();
      }
      for (const section of this._observableSections.values()) {
        this._observer.observe(section);
      }
    }
    dispose() {
      this._observer.disconnect();
      super.dispose();
    }

    // Private
    _configAfterMerge(config) {
      // TODO: on v6 target should be given explicitly & remove the {target: 'ss-target'} case
      config.target = getElement(config.target) || document.body;

      // TODO: v6 Only for backwards compatibility reasons. Use rootMargin only
      config.rootMargin = config.offset ? `${config.offset}px 0px -30%` : config.rootMargin;
      if (typeof config.threshold === 'string') {
        config.threshold = config.threshold.split(',').map(value => Number.parseFloat(value));
      }
      return config;
    }
    _maybeEnableSmoothScroll() {
      if (!this._config.smoothScroll) {
        return;
      }

      // unregister any previous listeners
      EventHandler.off(this._config.target, EVENT_CLICK);
      EventHandler.on(this._config.target, EVENT_CLICK, SELECTOR_TARGET_LINKS, event => {
        const observableSection = this._observableSections.get(event.target.hash);
        if (observableSection) {
          event.preventDefault();
          const root = this._rootElement || window;
          const height = observableSection.offsetTop - this._element.offsetTop;
          if (root.scrollTo) {
            root.scrollTo({
              top: height,
              behavior: 'smooth'
            });
            return;
          }

          // Chrome 60 doesn't support `scrollTo`
          root.scrollTop = height;
        }
      });
    }
    _getNewObserver() {
      const options = {
        root: this._rootElement,
        threshold: this._config.threshold,
        rootMargin: this._config.rootMargin
      };
      return new IntersectionObserver(entries => this._observerCallback(entries), options);
    }

    // The logic of selection
    _observerCallback(entries) {
      const targetElement = entry => this._targetLinks.get(`#${entry.target.id}`);
      const activate = entry => {
        this._previousScrollData.visibleEntryTop = entry.target.offsetTop;
        this._process(targetElement(entry));
      };
      const parentScrollTop = (this._rootElement || document.documentElement).scrollTop;
      const userScrollsDown = parentScrollTop >= this._previousScrollData.parentScrollTop;
      this._previousScrollData.parentScrollTop = parentScrollTop;
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          this._activeTarget = null;
          this._clearActiveClass(targetElement(entry));
          continue;
        }
        const entryIsLowerThanPrevious = entry.target.offsetTop >= this._previousScrollData.visibleEntryTop;
        // if we are scrolling down, pick the bigger offsetTop
        if (userScrollsDown && entryIsLowerThanPrevious) {
          activate(entry);
          // if parent isn't scrolled, let's keep the first visible item, breaking the iteration
          if (!parentScrollTop) {
            return;
          }
          continue;
        }

        // if we are scrolling up, pick the smallest offsetTop
        if (!userScrollsDown && !entryIsLowerThanPrevious) {
          activate(entry);
        }
      }
    }
    _initializeTargetsAndObservables() {
      this._targetLinks = new Map();
      this._observableSections = new Map();
      const targetLinks = SelectorEngine.find(SELECTOR_TARGET_LINKS, this._config.target);
      for (const anchor of targetLinks) {
        // ensure that the anchor has an id and is not disabled
        if (!anchor.hash || isDisabled(anchor)) {
          continue;
        }
        const observableSection = SelectorEngine.findOne(decodeURI(anchor.hash), this._element);

        // ensure that the observableSection exists & is visible
        if (isVisible(observableSection)) {
          this._targetLinks.set(decodeURI(anchor.hash), anchor);
          this._observableSections.set(anchor.hash, observableSection);
        }
      }
    }
    _process(target) {
      if (this._activeTarget === target) {
        return;
      }
      this._clearActiveClass(this._config.target);
      this._activeTarget = target;
      target.classList.add(CLASS_NAME_ACTIVE$1);
      this._activateParents(target);
      EventHandler.trigger(this._element, EVENT_ACTIVATE, {
        relatedTarget: target
      });
    }
    _activateParents(target) {
      // Activate dropdown parents
      if (target.classList.contains(CLASS_NAME_DROPDOWN_ITEM)) {
        SelectorEngine.findOne(SELECTOR_DROPDOWN_TOGGLE$1, target.closest(SELECTOR_DROPDOWN)).classList.add(CLASS_NAME_ACTIVE$1);
        return;
      }
      for (const listGroup of SelectorEngine.parents(target, SELECTOR_NAV_LIST_GROUP)) {
        // Set triggered links parents as active
        // With both <ul> and <nav> markup a parent is the previous sibling of any nav ancestor
        for (const item of SelectorEngine.prev(listGroup, SELECTOR_LINK_ITEMS)) {
          item.classList.add(CLASS_NAME_ACTIVE$1);
        }
      }
    }
    _clearActiveClass(parent) {
      parent.classList.remove(CLASS_NAME_ACTIVE$1);
      const activeNodes = SelectorEngine.find(`${SELECTOR_TARGET_LINKS}.${CLASS_NAME_ACTIVE$1}`, parent);
      for (const node of activeNodes) {
        node.classList.remove(CLASS_NAME_ACTIVE$1);
      }
    }

    // Static
    static jQueryInterface(config) {
      return this.each(function () {
        const data = ScrollSpy.getOrCreateInstance(this, config);
        if (typeof config !== 'string') {
          return;
        }
        if (data[config] === undefined || config.startsWith('_') || config === 'constructor') {
          throw new TypeError(`No method named "${config}"`);
        }
        data[config]();
      });
    }
  }

  /**
   * Data API implementation
   */

  EventHandler.on(window, EVENT_LOAD_DATA_API$1, () => {
    for (const spy of SelectorEngine.find(SELECTOR_DATA_SPY)) {
      ScrollSpy.getOrCreateInstance(spy);
    }
  });

  /**
   * jQuery
   */

  defineJQueryPlugin(ScrollSpy);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap tab.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME$1 = 'tab';
  const DATA_KEY$1 = 'bs.tab';
  const EVENT_KEY$1 = `.${DATA_KEY$1}`;
  const EVENT_HIDE$1 = `hide${EVENT_KEY$1}`;
  const EVENT_HIDDEN$1 = `hidden${EVENT_KEY$1}`;
  const EVENT_SHOW$1 = `show${EVENT_KEY$1}`;
  const EVENT_SHOWN$1 = `shown${EVENT_KEY$1}`;
  const EVENT_CLICK_DATA_API = `click${EVENT_KEY$1}`;
  const EVENT_KEYDOWN = `keydown${EVENT_KEY$1}`;
  const EVENT_LOAD_DATA_API = `load${EVENT_KEY$1}`;
  const ARROW_LEFT_KEY = 'ArrowLeft';
  const ARROW_RIGHT_KEY = 'ArrowRight';
  const ARROW_UP_KEY = 'ArrowUp';
  const ARROW_DOWN_KEY = 'ArrowDown';
  const HOME_KEY = 'Home';
  const END_KEY = 'End';
  const CLASS_NAME_ACTIVE = 'active';
  const CLASS_NAME_FADE$1 = 'fade';
  const CLASS_NAME_SHOW$1 = 'show';
  const CLASS_DROPDOWN = 'dropdown';
  const SELECTOR_DROPDOWN_TOGGLE = '.dropdown-toggle';
  const SELECTOR_DROPDOWN_MENU = '.dropdown-menu';
  const NOT_SELECTOR_DROPDOWN_TOGGLE = `:not(${SELECTOR_DROPDOWN_TOGGLE})`;
  const SELECTOR_TAB_PANEL = '.list-group, .nav, [role="tablist"]';
  const SELECTOR_OUTER = '.nav-item, .list-group-item';
  const SELECTOR_INNER = `.nav-link${NOT_SELECTOR_DROPDOWN_TOGGLE}, .list-group-item${NOT_SELECTOR_DROPDOWN_TOGGLE}, [role="tab"]${NOT_SELECTOR_DROPDOWN_TOGGLE}`;
  const SELECTOR_DATA_TOGGLE = '[data-bs-toggle="tab"], [data-bs-toggle="pill"], [data-bs-toggle="list"]'; // TODO: could only be `tab` in v6
  const SELECTOR_INNER_ELEM = `${SELECTOR_INNER}, ${SELECTOR_DATA_TOGGLE}`;
  const SELECTOR_DATA_TOGGLE_ACTIVE = `.${CLASS_NAME_ACTIVE}[data-bs-toggle="tab"], .${CLASS_NAME_ACTIVE}[data-bs-toggle="pill"], .${CLASS_NAME_ACTIVE}[data-bs-toggle="list"]`;

  /**
   * Class definition
   */

  class Tab extends BaseComponent {
    constructor(element) {
      super(element);
      this._parent = this._element.closest(SELECTOR_TAB_PANEL);
      if (!this._parent) {
        return;
        // TODO: should throw exception in v6
        // throw new TypeError(`${element.outerHTML} has not a valid parent ${SELECTOR_INNER_ELEM}`)
      }

      // Set up initial aria attributes
      this._setInitialAttributes(this._parent, this._getChildren());
      EventHandler.on(this._element, EVENT_KEYDOWN, event => this._keydown(event));
    }

    // Getters
    static get NAME() {
      return NAME$1;
    }

    // Public
    show() {
      // Shows this elem and deactivate the active sibling if exists
      const innerElem = this._element;
      if (this._elemIsActive(innerElem)) {
        return;
      }

      // Search for active tab on same parent to deactivate it
      const active = this._getActiveElem();
      const hideEvent = active ? EventHandler.trigger(active, EVENT_HIDE$1, {
        relatedTarget: innerElem
      }) : null;
      const showEvent = EventHandler.trigger(innerElem, EVENT_SHOW$1, {
        relatedTarget: active
      });
      if (showEvent.defaultPrevented || hideEvent && hideEvent.defaultPrevented) {
        return;
      }
      this._deactivate(active, innerElem);
      this._activate(innerElem, active);
    }

    // Private
    _activate(element, relatedElem) {
      if (!element) {
        return;
      }
      element.classList.add(CLASS_NAME_ACTIVE);
      this._activate(SelectorEngine.getElementFromSelector(element)); // Search and activate/show the proper section

      const complete = () => {
        if (element.getAttribute('role') !== 'tab') {
          element.classList.add(CLASS_NAME_SHOW$1);
          return;
        }
        element.removeAttribute('tabindex');
        element.setAttribute('aria-selected', true);
        this._toggleDropDown(element, true);
        EventHandler.trigger(element, EVENT_SHOWN$1, {
          relatedTarget: relatedElem
        });
      };
      this._queueCallback(complete, element, element.classList.contains(CLASS_NAME_FADE$1));
    }
    _deactivate(element, relatedElem) {
      if (!element) {
        return;
      }
      element.classList.remove(CLASS_NAME_ACTIVE);
      element.blur();
      this._deactivate(SelectorEngine.getElementFromSelector(element)); // Search and deactivate the shown section too

      const complete = () => {
        if (element.getAttribute('role') !== 'tab') {
          element.classList.remove(CLASS_NAME_SHOW$1);
          return;
        }
        element.setAttribute('aria-selected', false);
        element.setAttribute('tabindex', '-1');
        this._toggleDropDown(element, false);
        EventHandler.trigger(element, EVENT_HIDDEN$1, {
          relatedTarget: relatedElem
        });
      };
      this._queueCallback(complete, element, element.classList.contains(CLASS_NAME_FADE$1));
    }
    _keydown(event) {
      if (![ARROW_LEFT_KEY, ARROW_RIGHT_KEY, ARROW_UP_KEY, ARROW_DOWN_KEY, HOME_KEY, END_KEY].includes(event.key)) {
        return;
      }
      event.stopPropagation(); // stopPropagation/preventDefault both added to support up/down keys without scrolling the page
      event.preventDefault();
      const children = this._getChildren().filter(element => !isDisabled(element));
      let nextActiveElement;
      if ([HOME_KEY, END_KEY].includes(event.key)) {
        nextActiveElement = children[event.key === HOME_KEY ? 0 : children.length - 1];
      } else {
        const isNext = [ARROW_RIGHT_KEY, ARROW_DOWN_KEY].includes(event.key);
        nextActiveElement = getNextActiveElement(children, event.target, isNext, true);
      }
      if (nextActiveElement) {
        nextActiveElement.focus({
          preventScroll: true
        });
        Tab.getOrCreateInstance(nextActiveElement).show();
      }
    }
    _getChildren() {
      // collection of inner elements
      return SelectorEngine.find(SELECTOR_INNER_ELEM, this._parent);
    }
    _getActiveElem() {
      return this._getChildren().find(child => this._elemIsActive(child)) || null;
    }
    _setInitialAttributes(parent, children) {
      this._setAttributeIfNotExists(parent, 'role', 'tablist');
      for (const child of children) {
        this._setInitialAttributesOnChild(child);
      }
    }
    _setInitialAttributesOnChild(child) {
      child = this._getInnerElement(child);
      const isActive = this._elemIsActive(child);
      const outerElem = this._getOuterElement(child);
      child.setAttribute('aria-selected', isActive);
      if (outerElem !== child) {
        this._setAttributeIfNotExists(outerElem, 'role', 'presentation');
      }
      if (!isActive) {
        child.setAttribute('tabindex', '-1');
      }
      this._setAttributeIfNotExists(child, 'role', 'tab');

      // set attributes to the related panel too
      this._setInitialAttributesOnTargetPanel(child);
    }
    _setInitialAttributesOnTargetPanel(child) {
      const target = SelectorEngine.getElementFromSelector(child);
      if (!target) {
        return;
      }
      this._setAttributeIfNotExists(target, 'role', 'tabpanel');
      if (child.id) {
        this._setAttributeIfNotExists(target, 'aria-labelledby', `${child.id}`);
      }
    }
    _toggleDropDown(element, open) {
      const outerElem = this._getOuterElement(element);
      if (!outerElem.classList.contains(CLASS_DROPDOWN)) {
        return;
      }
      const toggle = (selector, className) => {
        const element = SelectorEngine.findOne(selector, outerElem);
        if (element) {
          element.classList.toggle(className, open);
        }
      };
      toggle(SELECTOR_DROPDOWN_TOGGLE, CLASS_NAME_ACTIVE);
      toggle(SELECTOR_DROPDOWN_MENU, CLASS_NAME_SHOW$1);
      outerElem.setAttribute('aria-expanded', open);
    }
    _setAttributeIfNotExists(element, attribute, value) {
      if (!element.hasAttribute(attribute)) {
        element.setAttribute(attribute, value);
      }
    }
    _elemIsActive(elem) {
      return elem.classList.contains(CLASS_NAME_ACTIVE);
    }

    // Try to get the inner element (usually the .nav-link)
    _getInnerElement(elem) {
      return elem.matches(SELECTOR_INNER_ELEM) ? elem : SelectorEngine.findOne(SELECTOR_INNER_ELEM, elem);
    }

    // Try to get the outer element (usually the .nav-item)
    _getOuterElement(elem) {
      return elem.closest(SELECTOR_OUTER) || elem;
    }

    // Static
    static jQueryInterface(config) {
      return this.each(function () {
        const data = Tab.getOrCreateInstance(this);
        if (typeof config !== 'string') {
          return;
        }
        if (data[config] === undefined || config.startsWith('_') || config === 'constructor') {
          throw new TypeError(`No method named "${config}"`);
        }
        data[config]();
      });
    }
  }

  /**
   * Data API implementation
   */

  EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
    if (['A', 'AREA'].includes(this.tagName)) {
      event.preventDefault();
    }
    if (isDisabled(this)) {
      return;
    }
    Tab.getOrCreateInstance(this).show();
  });

  /**
   * Initialize on focus
   */
  EventHandler.on(window, EVENT_LOAD_DATA_API, () => {
    for (const element of SelectorEngine.find(SELECTOR_DATA_TOGGLE_ACTIVE)) {
      Tab.getOrCreateInstance(element);
    }
  });
  /**
   * jQuery
   */

  defineJQueryPlugin(Tab);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap toast.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */


  /**
   * Constants
   */

  const NAME = 'toast';
  const DATA_KEY = 'bs.toast';
  const EVENT_KEY = `.${DATA_KEY}`;
  const EVENT_MOUSEOVER = `mouseover${EVENT_KEY}`;
  const EVENT_MOUSEOUT = `mouseout${EVENT_KEY}`;
  const EVENT_FOCUSIN = `focusin${EVENT_KEY}`;
  const EVENT_FOCUSOUT = `focusout${EVENT_KEY}`;
  const EVENT_HIDE = `hide${EVENT_KEY}`;
  const EVENT_HIDDEN = `hidden${EVENT_KEY}`;
  const EVENT_SHOW = `show${EVENT_KEY}`;
  const EVENT_SHOWN = `shown${EVENT_KEY}`;
  const CLASS_NAME_FADE = 'fade';
  const CLASS_NAME_HIDE = 'hide'; // @deprecated - kept here only for backwards compatibility
  const CLASS_NAME_SHOW = 'show';
  const CLASS_NAME_SHOWING = 'showing';
  const DefaultType = {
    animation: 'boolean',
    autohide: 'boolean',
    delay: 'number'
  };
  const Default = {
    animation: true,
    autohide: true,
    delay: 5000
  };

  /**
   * Class definition
   */

  class Toast extends BaseComponent {
    constructor(element, config) {
      super(element, config);
      this._timeout = null;
      this._hasMouseInteraction = false;
      this._hasKeyboardInteraction = false;
      this._setListeners();
    }

    // Getters
    static get Default() {
      return Default;
    }
    static get DefaultType() {
      return DefaultType;
    }
    static get NAME() {
      return NAME;
    }

    // Public
    show() {
      const showEvent = EventHandler.trigger(this._element, EVENT_SHOW);
      if (showEvent.defaultPrevented) {
        return;
      }
      this._clearTimeout();
      if (this._config.animation) {
        this._element.classList.add(CLASS_NAME_FADE);
      }
      const complete = () => {
        this._element.classList.remove(CLASS_NAME_SHOWING);
        EventHandler.trigger(this._element, EVENT_SHOWN);
        this._maybeScheduleHide();
      };
      this._element.classList.remove(CLASS_NAME_HIDE); // @deprecated
      reflow(this._element);
      this._element.classList.add(CLASS_NAME_SHOW, CLASS_NAME_SHOWING);
      this._queueCallback(complete, this._element, this._config.animation);
    }
    hide() {
      if (!this.isShown()) {
        return;
      }
      const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE);
      if (hideEvent.defaultPrevented) {
        return;
      }
      const complete = () => {
        this._element.classList.add(CLASS_NAME_HIDE); // @deprecated
        this._element.classList.remove(CLASS_NAME_SHOWING, CLASS_NAME_SHOW);
        EventHandler.trigger(this._element, EVENT_HIDDEN);
      };
      this._element.classList.add(CLASS_NAME_SHOWING);
      this._queueCallback(complete, this._element, this._config.animation);
    }
    dispose() {
      this._clearTimeout();
      if (this.isShown()) {
        this._element.classList.remove(CLASS_NAME_SHOW);
      }
      super.dispose();
    }
    isShown() {
      return this._element.classList.contains(CLASS_NAME_SHOW);
    }

    // Private

    _maybeScheduleHide() {
      if (!this._config.autohide) {
        return;
      }
      if (this._hasMouseInteraction || this._hasKeyboardInteraction) {
        return;
      }
      this._timeout = setTimeout(() => {
        this.hide();
      }, this._config.delay);
    }
    _onInteraction(event, isInteracting) {
      switch (event.type) {
        case 'mouseover':
        case 'mouseout':
          {
            this._hasMouseInteraction = isInteracting;
            break;
          }
        case 'focusin':
        case 'focusout':
          {
            this._hasKeyboardInteraction = isInteracting;
            break;
          }
      }
      if (isInteracting) {
        this._clearTimeout();
        return;
      }
      const nextElement = event.relatedTarget;
      if (this._element === nextElement || this._element.contains(nextElement)) {
        return;
      }
      this._maybeScheduleHide();
    }
    _setListeners() {
      EventHandler.on(this._element, EVENT_MOUSEOVER, event => this._onInteraction(event, true));
      EventHandler.on(this._element, EVENT_MOUSEOUT, event => this._onInteraction(event, false));
      EventHandler.on(this._element, EVENT_FOCUSIN, event => this._onInteraction(event, true));
      EventHandler.on(this._element, EVENT_FOCUSOUT, event => this._onInteraction(event, false));
    }
    _clearTimeout() {
      clearTimeout(this._timeout);
      this._timeout = null;
    }

    // Static
    static jQueryInterface(config) {
      return this.each(function () {
        const data = Toast.getOrCreateInstance(this, config);
        if (typeof config === 'string') {
          if (typeof data[config] === 'undefined') {
            throw new TypeError(`No method named "${config}"`);
          }
          data[config](this);
        }
      });
    }
  }

  /**
   * Data API implementation
   */

  enableDismissTrigger(Toast);

  /**
   * jQuery
   */

  defineJQueryPlugin(Toast);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap index.umd.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  const index_umd = {
    Alert,
    Button,
    Carousel,
    Collapse,
    Dropdown,
    Modal,
    Offcanvas,
    Popover,
    ScrollSpy,
    Tab,
    Toast,
    Tooltip
  };

  return index_umd;

}));
//# sourceMappingURL=bootstrap.bundle.js.map

(function () {
  'use strict';

  (function () {
    // Guarda contra dupla inscrição: o arquivo entra em mais de um bundle e dois
    // handlers ativos disparariam a navegação duas vezes por tecla.
    if (window.__zeevShortcutsLoaded) return;
    window.__zeevShortcutsLoaded = true;

    /*
     * A lista de elementos é resolvida A CADA TECLA, e não uma única vez na carga.
     * O snapshot antigo (querySelectorAll no topo do IIFE) só via o que já estava
     * no DOM naquele instante — qualquer coisa renderizada depois ficava de fora
     * para sempre. Era o caso dos itens do menu (<wc-menu> só existe depois do
     * fetch do menu.json) e de tudo que vem de <wc-modal>/<wc-offcanvas>.
     * querySelectorAll em keyup é irrelevante em custo: roda uma vez por tecla.
     */
    const getShortcutElements = () => document.querySelectorAll("[data-accesskey], [data-accesskey-code]");

    /*
     * `data-accesskey` guarda a tecla ("n"); `data-accesskey-code` é o formato
     * legado com o keyCode numérico ("78") e NUNCA casa, porque a comparação é
     * contra event.key. Mantido na consulta apenas para não alterar o
     * comportamento das telas legadas que o usam.
     */
    const getShortcutKey = el => el.getAttribute("data-accesskey") ?? el.getAttribute("data-accesskey-code");

    /*
     * "A pessoa está digitando?" tem duas respostas e as duas importam: o elemento
     * com foco e o alvo do evento. Elas divergem quando o campo é re-renderizado
     * entre o keydown e o keyup, rotina em campo de busca remota.
     *
     * `isContentEditable` é PROPRIEDADE, não atributo, e é o ponto central: ela é
     * herdada, então vale para qualquer descendente de uma região editável. Ler
     * `getAttribute("contenteditable") === "true"` só no alvo deixava passar o caret
     * dentro de um filho — é o caso do <wc-input-token>, que envolve o conteúdo em
     * <span class="token"> dentro de um div contenteditable — e ignorava
     * `contenteditable=""` e `plaintext-only`, igualmente editáveis. Era por isso que
     * digitar em certos campos disparava atalho de módulo.
     */
    const isEditable = el => el != null && el !== document.body && (/^(?:input|select|textarea)$/i.test(el.tagName) || el.isContentEditable === true);
    const isTyping = e => isEditable(document.activeElement) || isEditable(e.target) || e.target?.closest?.("input, select, textarea, [contenteditable]") != null;
    const activate = el => {
      // Um gatilho de modal/offcanvas tem href="#id": navegar para lá só sujaria a
      // URL. Ele precisa é do clique.
      const isBootstrapToggle = el.getAttribute("data-bs-toggle") != null;
      if (!isBootstrapToggle && el.href != null && el.href !== "") {
        if (el.target === "_blank") {
          window.open(el.href);
          return;
        }
        location.href = el.href;
        return;
      }

      // `new Event("click")` NÃO borbulha, então nunca chegava aos listeners
      // delegados que o Bootstrap registra no document para data-bs-toggle — só
      // funcionava em elemento com onclick inline. MouseEvent com bubbles:true
      // atende os dois casos.
      el.dispatchEvent(new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window
      }));
    };
    document.addEventListener("keyup", function (e) {
      if (e == null || e.target == null) return;

      // Combinações com modificador pertencem ao browser (accesskey nativo do
      // Alt+letra) ou ao app (Ctrl+F etc. do desenhador). Sem este guard, Ctrl+N
      // navegaria junto com o atalho nativo.
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (isTyping(e)) return;
      const pressed = String(e.key).toLowerCase();
      getShortcutElements().forEach(function (el) {
        const key = getShortcutKey(el);
        if (key && String(key).toLowerCase() === pressed) activate(el);
      });
    });
  })();

  const SPRITE_PREFIX = "sprites-";
  const SPRITE_ID = `${SPRITE_PREFIX}sgkbl78mkrymvu04qme`;
  let loadingSpritePromises = null;
  const currentScriptSrc = document.currentScript?.src;
  async function loadSvgToSymbolLibrary(svgId) {
    if (!svgId) return;
    const container = createOrGetHtmlSymbolContainer();
    const escapedId = CSS.escape(svgId);
    if (container.querySelector(`#${escapedId}`)) return;
    const spriteContent = await getSpriteContent();
    if (!spriteContent) {
      console.log("No sprite content");
      return;
    }
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(spriteContent, "text/xml");
    if (!xmlDoc || xmlDoc.querySelector("parsererror")) {
      console.log("Error parsing sprite");
      return;
    }
    const symbol = xmlDoc.querySelector(`#${escapedId}`);
    if (!symbol) {
      console.log(`Symbol ${svgId} not found in sprite`);
      return;
    }
    container.appendChild(symbol);
  }
  function createOrGetHtmlSymbolContainer() {
    let container = document.getElementById(SPRITE_ID);
    if (container) return container;
    container = document.createElement("div");
    container.className = "svg-icons";
    container.setAttribute("hidden", "");
    container.setAttribute("id", SPRITE_ID);
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svg.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    container.appendChild(svg);
    document.body.insertBefore(container, document.body.firstChild);
    return svg;
  }
  async function getSpriteContent() {
    if (!hasSpriteInLocalStorage()) {
      clearOldSpritesFromLocalStorage();
      await getNewSpriteFromHttp();
    }
    return getSpriteFromLocalStorage();
  }
  function hasSpriteInLocalStorage() {
    const cached = localStorage.getItem(SPRITE_ID);
    if (!cached) return false;
    const data = JSON.parse(cached);
    return data?.content;
  }
  async function getNewSpriteFromHttp() {
    if (!loadingSpritePromises) {
      loadingSpritePromises = _getFromHttp();
    }
    const data = await loadingSpritePromises;
    localStorage.setItem(SPRITE_ID, JSON.stringify({
      content: data
    }));
    return true;
    async function _getFromHttp() {
      const url = `${_getScriptUrl()}icon/sprite/sprite.svg`;
      const response = await fetch(url);
      return await response.text();
    }
    function _getScriptUrl() {
      const thisFileUrl = _getAssetsPath(currentScriptSrc) ?? "/assets/";
      return thisFileUrl;
    }
    function _getAssetsPath(fullUrl) {
      if (!fullUrl) return null;
      const match = fullUrl.match(/^(.*?\/\/.*?\/assets\/)/);
      return match ? match[1] : null;
    }
  }
  function getSpriteFromLocalStorage() {
    if (!hasSpriteInLocalStorage()) return null;
    const cached = localStorage.getItem(SPRITE_ID);
    const data = JSON.parse(cached);
    return data.content;
  }
  function clearOldSpritesFromLocalStorage() {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(SPRITE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  const fullScriptUrl = document.currentScript?.src;

  /*
   * Converter a configuração de mascara de string para JSON
   */
  function convertToJson(stringJsonAttribute) {
    if (!stringJsonAttribute) return "";
    if (!stringJsonAttribute.startsWith("{") && !stringJsonAttribute.endsWith("}")) {
      stringJsonAttribute = `{${stringJsonAttribute}}`;
    }
    try {
      return JSON.parse(`${stringJsonAttribute.replace(/'/g, '"')}`);
    } catch (e) {
      console.error(`Error converting ${stringJsonAttribute} to JSON.`);
      return "";
    }
  }
  function htmlEncode(rawStr) {
    if (!rawStr) return;
    return rawStr.replace(/[\u00A0-\u9999<>\\&]/gim, function (i) {
      return "&#" + i.charCodeAt(0) + ";";
    });
  }
  function hasStorage(name) {
    return getStorage(name) !== null;
  }
  function setStorage(name, value) {
    localStorage.setItem(name, value);
  }
  function getStorage(name) {
    return localStorage.getItem(name);
  }
  function generateRandomId() {
    return `r${Math.random().toString(36).substr(2, 9)}`;
  }
  async function loadScriptAsync(url) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.onload = resolve;
      script.onerror = reject;
      //não remover a linha abaixo, apesar de a propriedade ser deprecated, é necessário para carregar o codemirror corretamente
      script.charset = "UTF-8";
      document.head.appendChild(script);
    });
  }
  async function loadCssAsync(url) {
    return new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.href = url;
      link.rel = "stylesheet";
      link.type = "text/css";
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }
  function getZeevBootstrapPath(path) {
    const url = getZeevBootstrapUrl();
    if (!url) return;
    return `${url}${path}`;
  }
  function getZeevBootstrapUrl() {
    if (!fullScriptUrl) return null;
    const match = fullScriptUrl.match(/^(.*?\/\/.*?\/)(assets\/)/);
    return match ? match[1] : null;
  }
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|IEMobile|Opera Mini/i.test(navigator.userAgent);
  function delegateEvent(container, selector, event, callback) {
    if (!container) return;
    if (!selector) return;
    if (!event) return;
    if (!callback) return;
    container.addEventListener(event, function (e) {
      const target = e.target.closest(selector);
      if (!target) return;
      callback(e, target);
    });
  }
  function insertTextAtCursor(el, text) {
    const startPos = el.selectionStart;
    const endPos = el.selectionEnd;
    el.value = el.value.substring(0, startPos) + text + el.value.substring(endPos, el.value.length);
    el.selectionStart = startPos + text.length;
    el.selectionEnd = startPos + text.length;
    el.focus();
  }
  function insertTextBetweenPosition(el, text, startPosition1, startPosition2) {
    el.value = el.value.slice(0, startPosition1) + text + el.value.slice(startPosition2, el.value.length);
  }
  function stringContains(string1, string2) {
    const _removeAccents = str => {
      return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    };
    return _removeAccents(string1).toLowerCase().includes(_removeAccents(string2).toLowerCase());
  }

  //sanitizeForInterpolation
  function sanitize(string) {
    return string.replace(/"/g, "'");
  }
  function generateGUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === "x" ? r : r & 0x3 | 0x8;
      return v.toString(16);
    });
  }
  function isNullOrEmpty(string = "") {
    return string === null || string.toString().trim().length == 0;
  }
  function capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
  function replaceNewLine(str) {
    return str?.replace(/\n/g, "<br>");
  }
  function isJson(str) {
    if (typeof str !== "string") return false;
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  }

  HTMLElement.prototype.getBooleanAttribute = function (attr, defaultValue) {
    if (!this.hasAttribute(attr)) return defaultValue;
    const value = this.getAttribute(attr);
    if (value !== null && value === "") return defaultValue;
    if (["null", "undefined"].includes(value)) return defaultValue;
    if (["0", "false"].includes(value)) return false;
    if (!value) return false;
    return true;
  };
  HTMLElement.prototype.getStringAttribute = function (attr, defaultValue = "", allowedValues = []) {
    if (!this.hasAttribute(attr)) return defaultValue;
    let value = this.getAttribute(attr);
    if (typeof value === "undefined") return defaultValue;
    if (typeof value !== String) value = value.toString();
    if (value !== null && value === "") return defaultValue;
    if (value === null || value === "null" || value === undefined || value === "undefined") return defaultValue;
    if (allowedValues.length > 0 && !allowedValues.includes(value)) {
      console.log(`Valores inválidos para atributo ${attr}. Os valores possíveis são ${allowedValues.join(", ")}`);
      return defaultValue;
    }
    return value;
  };
  HTMLElement.prototype.getIntegerAttribute = function (attr, defaultValue = 0) {
    if (!this.hasAttribute(attr)) return defaultValue;
    let value = this.getAttribute(attr);
    if (typeof value === "undefined") return defaultValue;
    if (typeof value !== String) value = value.toString();
    if (value !== null && value === "") return defaultValue;
    if (value === null || value === "null" || value === undefined || value === "undefined") return defaultValue;
    return parseInt(value);
  };
  HTMLElement.prototype.getDecimalAttribute = function (attr, defaultValue = 0) {
    if (!this.hasAttribute(attr)) return defaultValue;
    let value = this.getAttribute(attr);
    if (typeof value === "undefined") return defaultValue;
    if (typeof value !== String) value = value.toString();
    if (value !== null && value === "") return defaultValue;
    if (value === null || value === "null" || value === undefined || value === "undefined") return defaultValue;
    return parseFloat(value);
  };
  HTMLElement.prototype.getJsonAttribute = function (attr, defaultValue = {}) {
    if (!this.hasAttribute(attr)) return defaultValue;
    let value = this.getAttribute(attr);
    if (typeof value !== String) value = value.toString();
    if (value !== null && value === "") return defaultValue;
    if (value === null || value === "null" || value === undefined || value === "undefined") return defaultValue;
    return convertToJson(value);
  };
  HTMLElement.prototype.getArrayAttribute = function (attr, defaultValue = []) {
    if (!this.hasAttribute(attr)) return defaultValue;
    let value = this.getAttribute(attr);
    if (typeof value !== String) value = value.toString();
    if (value !== null && value === "") return defaultValue;
    if (value === null || value === "null" || value === undefined || value === "undefined") return defaultValue;
    return value.split(/[,;\s]/).map(s => s.trim()).filter(s => s !== "");
  };
  HTMLElement.prototype.getTemplate = function (name) {
    if (!name) return null;
    const template = this.querySelector(`template[name='${name}']`);
    if (!template) return null;
    const templateDocumentFragment = template.content;

    //https://github.com/ionic-team/stencil/issues/1169
    //Bug do VUE.
    //Em situações não claras, o VUE tira o conteudo do document-fragment e coloca como irmão dele, diretamente abaixo do <template></template>
    const childrens = [...template.children];
    if (childrens.length > 0) {
      childrens.forEach(children => {
        templateDocumentFragment.appendChild(children); //.cloneNode(true));
      });
    }
    return templateDocumentFragment;
  };
  HTMLElement.prototype.getTemplateHtml = function (name) {
    if (!name) return "";
    const template = this.getTemplate(name);
    if (!template) return "";
    const container = document.createElement("div");
    container.appendChild(template.cloneNode(true));
    return container.innerHTML;
  };

  /**
   * Transfere os atributos para o elemento target.
   * Os atributos transferidos sao removidos do elemento source.
   * @param {HTMLElement} target
   * @param {Array<String>} includeList
   * @param {Array<String} excludeList
   */
  HTMLElement.prototype.transferAttrsTo = function (target, includeList = [], excludeList = []) {
    if (!target) return;
    const filteredAttrs = getFilteredAttributes(this, includeList, excludeList);
    filteredAttrs.forEach(attr => {
      target.setAttribute(attr.name, attr.value);
      this.removeAttribute(attr.name);
    });
  };

  /**
   * Copia os atributos para o elemento target.
   * Os atributos copiados permanecem no elemento source.
   * @param {HTMLElement} target
   * @param {Array<String>} includeList
   * @param {Array<String} excludeList
   */
  HTMLElement.prototype.copyAttrsTo = function (target, includeList = [], excludeList = []) {
    if (!target) return;
    const filteredAttrs = getFilteredAttributes(this, includeList, excludeList);
    filteredAttrs.forEach(attr => {
      target.setAttribute(attr.name, attr.value);
    });
  };
  function getFilteredAttributes(source, includeList = [], excludeList = []) {
    if (!includeList || includeList.length === 0) {
      includeList = Array.from(source.attributes);
    } else {
      includeList = Array.from(source.attributes).filter(attr => includeList.includes(attr.name));
    }
    if (!excludeList || excludeList.length === 0) excludeList = ["id"];
    return includeList.filter(attr => attr !== null && !excludeList.includes(attr.name));
  }

  // eslint-disable-next-line no-unused-vars
  const i18nlocales = {
    lng: () => {
      if (!document.documentElement.lang) return "pt";
      return document.documentElement.lang.split("-")[0];
    },
    fallbackLng: "pt",
    t: key => {
      const langResources = i18nlocales.resources[i18nlocales.lng()] || i18nlocales.resources[i18nlocales.fallbackLng];
      return i18nlocales._search(key, langResources.translation) || langResources.fallback && i18nlocales.resources.translation[langResources.fallback][key] || key;
    },
    _search: (key, translations) => {
      if (translations[key]) return translations[key];
      const keyParts = key.split(".");
      let translation = translations;
      for (let i = 0; i < keyParts.length; i++) {
        translation = translation[keyParts[i]];
        if (!translation) return key;
      }
      return translation;
    },
    resources: {
      pt: {
        translation: {
          or: "ou",
          title: "Olá",
          close: "Fechar",
          add: "Adicionar",
          cancel: "Cancelar",
          delete: "Excluir",
          confirm: "Confirmar",
          select: "Selecione",
          search: "Pesquisar",
          send: "Enviar",
          return: "Voltar",
          searchTokens: "Pesquisar tokens",
          searchOrCreateNew: "Pesquisar ou digitar para criar novo",
          removeAllSelected: "Remover todos os itens selecionados",
          remove: "Remover",
          browseFiles: "Carregar arquivos",
          dismissTip: "Dispensar essa dica",
          clientError: "Ocorreu um erro na interação com o seu navegador. ",
          offline: "Você está offline.",
          privacyPolicy: "Utilizamos cookies e tecnologias semelhantes para melhorar sua experiência em nossos serviços. Ao continuar navegando, você concorda com estas condições.",
          attention: "Atenção",
          confirmDelete: "Tem certeza que deseja excluir esses(s) registros(s)? Essa operação é irreversível.",
          confirmDefault: "Tem certeza que deseja prosseguir? Essa operação é irreversível.",
          noItemFound: "Nenhum item encontrado",
          light: "Claro",
          dark: "Escuro",
          auto: "Automático",
          dragAndDropHere: "Arraste e solte seu arquivo aqui",
          removeFile: "Remover arquivo",
          uploadMaxFileSize: "Máximo de {{size}} MB por arquivo.",
          allowedExtensions: "Extensões permitidas: {{extensions}}",
          invalidFileType: "Tipo de arquivo inválido",
          invalidFileSize: "Tamanho de arquivo inválido ({{filesize}} MB). Tamanho máximo permitido {{maxFilesize}} MB.",
          premiumFeature: "Desbloqueie recursos Premium",
          challengeInstructions: "Por segurança, para prosseguir, informe o valor da seguinte operação",
          challengeWrong: "Resposta incorreta, tente de novo.",
          tip: "Dica",
          warning: "Aviso",
          optional: "Opcional",
          required: "Obrigatório",
          portal: "Gerenciar",
          prompt: "Instruções",
          history: "Histórico",
          promptPlaceholder: "Digite suas instruções aqui",
          emptyPromptHistory: "Nenhuma interação registrada ainda.",
          editor: {
            header: "Estilo",
            bold: "Negrito",
            italic: "Itálico",
            underline: "Sublinhado",
            strike: "Tachado",
            blockquote: "Citação",
            code: "Código",
            list: "Lista",
            indent: "Indentar",
            color: "Cor",
            background: "Fundo",
            align: "Alinhamento",
            link: "Link",
            image: "Imagem",
            video: "Vídeo",
            clean: "Limpar"
          },
          codeMirror: {
            "Control character": "Caractere de controle",
            "Selection deleted": "Seleção excluída",
            "Folded lines": "Linhas recolhidas",
            "Unfolded lines": "Linhas expandidas",
            to: "até",
            "folded code": "código recolhido",
            unfold: "expandir",
            "Fold line": "Recolher linha",
            "Unfold line": "Expandir linha",
            "Go to line": "Ir para linha",
            go: "OK",
            Find: "Buscar",
            Replace: "Substituir",
            next: "próximo",
            previous: "anterior",
            all: "todos",
            "match case": "diferenciar maiúsculas",
            "by word": "palavra inteira",
            replace: "substituir",
            "replace all": "substituir todos",
            close: "fechar",
            "current match": "correspondência atual",
            "replaced $ matches": "$ correspondências substituídas",
            "replaced match on line $": "correspondência substituída na linha $",
            "on line": "na linha",
            Completions: "Autocompletar",
            Diagnostics: "Diagnósticos",
            "No diagnostics": "Sem diagnósticos"
          },
          modeler: {
            notationBpmn: "BPMN Clássico",
            notationFriendly: "BPMN Moderno",
            search: "Pesquisar",
            fullScreen: "Tela cheia",
            minimap: "Minimapa",
            center: "Centralizar",
            zoomIn: "Zoom In",
            zoomOut: "Zoom Out",
            downloadSuccess: "Download realizado com sucesso para o seu computador",
            deleteConfirm: "Tem certeza que deseja excluir este item?",
            cannotDeleteHasExecutions: "A exclusão deste elemento foi desfeita automaticamente, por existir uma ou mais solicitações deste aplicativo em andamento (paradas em qualquer tarefa ou finalizadas). <br /><br /> Dica: Para remover elementos, você pode criar uma nova versão ou uma cópia deste aplicativo."
          },
          chat: {
            feedbackPlaceholder: "Digite um feedback",
            feedbackCancel: "Cancelar",
            feedbackSend: "Enviar feedback",
            referencesLabel: "Referências",
            copyCode: "Copiar",
            copiedCode: "Copiado!",
            controlLikeTitle: "Gostei",
            controlDislikeTitle: "Não gostei",
            controlCopyTitle: "Copiar resposta",
            controlCopyEditTitle: "Copiar e editar resposta",
            optionsAriaLabel: "Opções do chat",
            tooltipHistory: "Histórico de conversas",
            tooltipDeleteChat: "Apagar chat",
            toggleExamplesTitle: "Mostrar ou esconder os exemplos",
            resetConversationTitle: "Reiniciar a conversa",
            tooltipNewChat: "Novo chat",
            inputPlaceholder: "Digite uma pergunta",
            stopTitle: "Abortar",
            aiWarning: "Respostas de IA podem ser incorretas ou imprecisas.",
            privacy: "Privacidade",
            removeContextAria: "Remover contexto da conversa.",
            removeContextTooltip: "Remover contexto da conversa.",
            consumeInfo1: "Recurso com limite mensal de {0} usos.<br>Este recurso já foi consumido {1} vezes neste mês.",
            consumeInfo2: "<strong>Importante!</strong> Este chat atingiu o limite de {n} conversas no mês.",
            consumeInfo3: "Você ainda pode enviar mensagens. No futuro, poderemos revisar nossa política de uso e as conversas poderão ter custos adicionais.",
            consumeInfo4: "<strong>Importante!</strong> Um ou mais recursos atingiram o limite de conversas no mês: <ul>{0}</ul>",
            consumeInfo5: "<li class='small'>{0} atingiu o limite de {n} conversas no mês.</li>",
            inactivityInfo: "A interação foi reiniciada após {0} minutos de inatividade.",
            title: {
              AIForKB: "Dúvidas gerais sobre o Zeev",
              AIForConfig: "Configurações do aplicativo",
              AIForAnalytics: "Estatísticas de uso"
            },
            formScripts: {
              tabs: {
                javaScript: {
                  label: "Aba JavaScript",
                  description: "Incluir código da aba JavaScript no contexto da conversa."
                },
                css: {
                  label: "Aba CSS",
                  description: "Incluir código da aba CSS no contexto da conversa."
                }
              }
            }
          }
        }
      },
      en: {
        fallback: "pt",
        translation: {
          or: "or",
          title: "Hello",
          add: "Add",
          close: "Close",
          cancel: "Cancel",
          delete: "Delete",
          confirm: "Confirm",
          select: "Select",
          search: "Search",
          send: "Send",
          return: "Return",
          searchTokens: "Search tokens",
          searchOrCreateNew: "Search or type to create new",
          removeAllSelected: "Remove all selected items",
          remove: "Remove",
          browseFiles: "Browse files",
          dismissTip: "Dismiss this tip",
          clientError: "An error occurred in the interaction with your browser",
          offline: "You are offline.",
          privacyPolicy: "We use cookies and similar technologies to improve your experience on our services. By continuing to browse, you agree to these conditions.",
          attention: "Attention",
          confirmDelete: "Are you sure you want to delete these records? This operation is irreversible.",
          confirmDefault: "Are you sure you want to proceed? This operation is irreversible.",
          noItemFound: "No item found",
          light: "Light",
          dark: "Dark",
          auto: "Auto",
          dragAndDropHere: "Drag and drop your file here",
          removeFile: "Remove file",
          uploadMaxFileSize: "Maximum of {{size}} MB per file.",
          allowedExtensions: "Allowed extensions: {{extensions}}",
          invalidFileType: "Invalid file type",
          invalidFileSize: "Invalid file size ({{filesize}}MiB). Maximum allowed size {{maxFilesize}}MiB.",
          premiumFeature: "Unlock Premium features",
          challengeInstructions: "For security, to proceed, enter the value of the following operation",
          challengeWrong: "Incorrect answer, try again.",
          tip: "Tip",
          warning: "Warning",
          optional: "Optional",
          required: "Required",
          portal: "Manage",
          prompt: "Prompt",
          history: "History",
          promptPlaceholder: "Type your prompt here",
          emptyPromptHistory: "No interaction recorded yet.",
          editor: {
            header: "Style",
            bold: "Bold",
            italic: "Italic",
            underline: "Underline",
            strike: "Strikethrough",
            blockquote: "Blockquote",
            code: "Code",
            list: "List",
            indent: "Indent",
            color: "Color",
            background: "Background",
            align: "Align",
            link: "Link",
            image: "Image",
            video: "Video",
            clean: "Clean"
          },
          codeMirror: {
            "Control character": "Control character",
            "Selection deleted": "Selection deleted",
            "Folded lines": "Folded lines",
            "Unfolded lines": "Unfolded lines",
            to: "to",
            "folded code": "folded code",
            unfold: "unfold",
            "Fold line": "Fold line",
            "Unfold line": "Unfold line",
            "Go to line": "Go to line",
            go: "go",
            Find: "Find",
            Replace: "Replace",
            next: "next",
            previous: "previous",
            all: "all",
            "match case": "match case",
            "by word": "by word",
            replace: "replace",
            "replace all": "replace all",
            close: "close",
            "current match": "current match",
            "replaced $ matches": "replaced $ matches",
            "replaced match on line $": "replaced match on line $",
            "on line": "on line",
            Completions: "Completions",
            Diagnostics: "Diagnostics",
            "No diagnostics": "No diagnostics"
          },
          modeler: {
            notationBpmn: "Classic BPMN",
            notationFriendly: "Modern BPMN",
            search: "Search",
            fullScreen: "Full Screen",
            minimap: "Minimap",
            center: "Center",
            zoomIn: "Zoom In",
            zoomOut: "Zoom Out",
            downloadSuccess: "Download successfully completed to your computer",
            deleteConfirm: "Are you sure you want to delete this item?",
            cannotDeleteHasExecutions: "The deletion of this element was automatically undone, as there is one or more requests for this application in progress (stopped in any task or completed). <br /><br /> Tip: To remove elements, you can create a new version or copy of this application."
          },
          chat: {
            feedbackPlaceholder: "Type feedback",
            feedbackCancel: "Cancel",
            feedbackSend: "Send feedback",
            referencesLabel: "References",
            copyCode: "Copy",
            copiedCode: "Copied!",
            controlLikeTitle: "Like",
            controlDislikeTitle: "Dislike",
            controlCopyTitle: "Copy response",
            controlCopyEditTitle: "Copy and edit response",
            optionsAriaLabel: "Chat options",
            tooltipHistory: "Conversation history",
            tooltipDeleteChat: "Delete chat",
            toggleExamplesTitle: "Show or hide examples",
            resetConversationTitle: "Restart conversation",
            tooltipNewChat: "New chat",
            inputPlaceholder: "Type a question",
            stopTitle: "Abort",
            aiWarning: "AI responses may be incorrect or imprecise.",
            privacy: "Privacy",
            removeContextAria: "Remove context from conversation.",
            removeContextTooltip: "Remove context from conversation.",
            consumeInfo1: "Resource with a monthly limit of {0} uses.<br>This resource has already been consumed {1} times this month.",
            consumeInfo2: "<strong>Important!</strong> This chat has reached the limit of {n} conversations in the month.",
            consumeInfo3: "You can still send messages. In the future, we may revise our usage policy and conversations may incur additional charges.",
            consumeInfo4: "<strong>Important!</strong> One or more resources have reached the conversation limit for the month: <ul>{0}</ul>",
            consumeInfo5: "<li class='small'>{0} has reached the limit of {n} conversations this month.</li>",
            inactivityInfo: "Interaction has been reset after {0} minutes of inactivity.",
            title: {
              AIForKB: "General questions about Zeev",
              AIForConfig: "Application settings",
              AIForAnalytics: "Usage statistics"
            },
            formScripts: {
              tabs: {
                javaScript: {
                  label: "JavaScript tab",
                  description: "Include JavaScript tab code in the conversation context."
                },
                css: {
                  label: "CSS tab",
                  description: "Include CSS tab code in the conversation context."
                }
              }
            }
          }
        }
      },
      es: {
        fallback: "pt",
        translation: {
          or: "o",
          title: "Hola",
          add: "Agregar",
          close: "Cerrar",
          cancel: "Cancelar",
          confirm: "Confirmar",
          delete: "Eliminar",
          select: "Seleccionar",
          search: "Buscar",
          send: "Enviar",
          return: "Volver",
          searchTokens: "Buscar tokens",
          searchOrCreateNew: "Buscar o escribir para crear nuevo",
          removeAllSelected: "Eliminar todos los elementos seleccionados",
          remove: "Eliminar",
          browseFiles: "Cargar archivos",
          dismissTip: "Descartar esta sugerencia",
          clientError: "Se produjo un error en la interacción con su navegador",
          offline: "Estás desconectado.",
          privacyPolicy: "Utilizamos cookies y tecnologías similares para mejorar su experiencia en nuestros servicios. Al continuar navegando, acepta estas condiciones.",
          attention: "Atención",
          confirmDelete: "¿Estás seguro de que deseas eliminar estos registros? Esta operación es irreversible.",
          confirmDefault: "¿Estás seguro de que deseas continuar? Esta operación es irreversible.",
          noItemFound: "No se encontró ningún elemento",
          light: "Claro",
          dark: "Oscuro",
          auto: "Automático",
          dragAndDropHere: "Arrastra y suelta tu archivo aquí",
          removeFile: "Eliminar archivo",
          uploadMaxFileSize: "Máximo de {{size}} MB por archivo.",
          allowedExtensions: "Extensiones permitidas: {{extensions}}",
          invalidFileType: "Tipo de archivo inválido",
          invalidFileSize: "Tamaño de archivo inválido ({{filesize}}MiB). Tamaño máximo permitido {{maxFilesize}}MiB.",
          premiumFeature: "Desbloquear funciones Premium",
          challengeInstructions: "Por seguridad, para continuar, ingrese el valor de la siguiente operación",
          challengeWrong: "Respuesta incorrecta, inténtalo de nuevo.",
          tip: "Consejo",
          warning: "Aviso",
          optional: "Opcional",
          required: "Obligatorio",
          portal: "Cambiar",
          prompt: "Instrucciones",
          history: "Historial",
          promptPlaceholder: "Escriba sus instrucciones aquí",
          emptyPromptHistory: "Ninguna interacción registrada todavía.",
          editor: {
            header: "Estilo",
            bold: "Negrita",
            italic: "Cursiva",
            underline: "Subrayado",
            strike: "Tachado",
            blockquote: "Cita",
            code: "Código",
            list: "Lista",
            indent: "Sangría",
            color: "Color",
            background: "Fondo",
            align: "Alinear",
            link: "Enlace",
            image: "Imagen",
            video: "Vídeo",
            clean: "Limpiar"
          },
          codeMirror: {
            "Control character": "Carácter de control",
            "Selection deleted": "Selección eliminada",
            "Folded lines": "Líneas plegadas",
            "Unfolded lines": "Líneas desplegadas",
            to: "hasta",
            "folded code": "código plegado",
            unfold: "desplegar",
            "Fold line": "Plegar línea",
            "Unfold line": "Desplegar línea",
            "Go to line": "Ir a línea",
            go: "OK",
            Find: "Buscar",
            Replace: "Reemplazar",
            next: "siguiente",
            previous: "anterior",
            all: "todos",
            "match case": "coincidir mayúsculas",
            "by word": "palabra completa",
            replace: "reemplazar",
            "replace all": "reemplazar todos",
            close: "cerrar",
            "current match": "coincidencia actual",
            "replaced $ matches": "$ coincidencias reemplazadas",
            "replaced match on line $": "coincidencia reemplazada en línea $",
            "on line": "en línea",
            Completions: "Autocompletado",
            Diagnostics: "Diagnósticos",
            "No diagnostics": "Sin diagnósticos"
          },
          modeler: {
            notationBpmn: "BPMN Clásico",
            notationFriendly: "BPMN Moderno",
            search: "Buscar",
            fullScreen: "Pantalla completa",
            minimap: "Minimapa",
            center: "Centrar",
            zoomIn: "Acercar",
            zoomOut: "Alejar",
            downloadSuccess: "Descarga realizada con éxito a su computadora",
            deleteConfirm: "¿Está seguro de que desea eliminar este elemento?",
            cannotDeleteHasExecutions: "La eliminación de este elemento se deshizo automáticamente, ya que hay una o más solicitudes para esta aplicación en curso (detenidas en cualquier tarea o completadas). <br /><br /> Sugerencia: Para eliminar elementos, puede crear una nueva versión o copia de esta aplicación."
          },
          chat: {
            feedbackPlaceholder: "Escribe un feedback",
            feedbackCancel: "Cancelar",
            feedbackSend: "Enviar feedback",
            referencesLabel: "Referencias",
            copyCode: "Copiar",
            copiedCode: "¡Copiado!",
            controlLikeTitle: "Me gusta",
            controlDislikeTitle: "No me gusta",
            controlCopyTitle: "Copiar respuesta",
            controlCopyEditTitle: "Copiar y editar respuesta",
            optionsAriaLabel: "Opciones del chat",
            tooltipHistory: "Historial de conversaciones",
            tooltipDeleteChat: "Eliminar chat",
            toggleExamplesTitle: "Mostrar u ocultar ejemplos",
            resetConversationTitle: "Reiniciar la conversación",
            tooltipNewChat: "Nuevo chat",
            inputPlaceholder: "Escribe una pregunta",
            stopTitle: "Abortar",
            aiWarning: "Las respuestas de IA pueden ser incorrectas o imprecisas.",
            privacy: "Privacidad",
            removeContextAria: "Quitar contexto de la conversación.",
            removeContextTooltip: "Quitar contexto de la conversación.",
            consumeInfo1: "Recurso con un límite mensual de {0} usos.<br>Este recurso ya se ha consumido {1} veces este mes.",
            consumeInfo2: "<strong>¡Importante!</strong> Este chat ha alcanzado el límite de {n} conversaciones en el mes.",
            consumeInfo3: "Aún puedes enviar mensajes. En el futuro, es posible que revisemos nuestra política de uso y las conversaciones pueden generar cargos adicionales.",
            consumeInfo4: "<strong>¡Importante!</strong> Uno o más recursos han alcanzado el límite de conversaciones en el mes: <ul>{0}</ul>",
            consumeInfo5: "<li class='small'>{0} ha alcanzado el límite de {n} conversaciones en el mes.</li>",
            inactivityInfo: "La interacción se reinició después de {0} minutos de inactividad.",
            title: {
              AIForKB: "Preguntas generales sobre Zeev",
              AIForConfig: "Configuraciones de la aplicación",
              AIForAnalytics: "Estadísticas de uso"
            },
            formScripts: {
              tabs: {
                javaScript: {
                  label: "Pestaña JavaScript",
                  description: "Incluir el código de la pestaña JavaScript en el contexto de la conversación."
                },
                css: {
                  label: "Pestaña CSS",
                  description: "Incluir el código de la pestaña CSS en el contexto de la conversación."
                }
              }
            }
          }
        }
      }
    }
  };

  class announceElement extends HTMLElement {
    constructor() {
      super();
      this._isPersistent = false;
    }
    async connectedCallback() {
      await this.init();
    }
    async init() {
      this.classList.add("d-none");
      if (!(await this._shouldDisplay())) return;
      this._render();
      this._addEvents();
      this.addTriggers();
    }
    get isPersistent() {
      return this._isPersistent;
    }
    set isPersistent(value) {
      this._isPersistent = value;
    }

    /*
     * Se irá aparecer backdrop ou não
     */
    get backdrop() {
      return this.getBooleanAttribute("backdrop", false);
    }
    set backdrop(value) {
      this.setAttribute("backdrop", value);
    }

    /*
     * Título do alerta
     */
    get title() {
      return this.getStringAttribute("title", "");
    }
    set title(newValue) {
      this.setAttribute("title", newValue);
    }

    /*
     * Detalhes do alerta
     */
    get details() {
      return this.getStringAttribute("details", "");
    }
    set details(newValue) {
      this.setAttribute("details", newValue);
    }

    /*
     * Cor do alerta
     */
    get color() {
      return this.getStringAttribute("color", "danger", ["primary", "secondary", "success", "tertiary", "danger", "warning", "info", "light", "dark"]);
    }
    set color(value) {
      this.setAttribute("color", value);
    }

    /*
     * Posição do alerta
     */
    get placement() {
      return this.getStringAttribute("placement", "top", ["top", "bottom"]);
    }
    set placement(value) {
      this.setAttribute("placement", value);
    }

    /*
     * Título do botão de fechar
     */
    get buttonTitle() {
      return this.getStringAttribute("buttontitle", i18nlocales.t("close"));
    }
    set buttonTitle(value) {
      this.setAttribute("buttontitle", value);
    }

    /*
     * Se irá aparecer o botão de fechar ou não
     */
    get showButton() {
      return this.getBooleanAttribute("showbutton", true);
    }
    set showButton(value) {
      this.setAttribute("showbutton", value);
    }
    get colorClass() {
      if (!this.color) return "";
      return `alert-${this.color}`;
    }
    get placementClass() {
      if (!this.placement) return "";
      return `announce-${this.placement}`;
    }
    get buttonClass() {
      if (!this.color) return "btn-secondary";
      return `btn-${this.color}`;
    }
    get storageKey() {
      return `${this.title}${this.details}`;
    }
    _render() {
      this.innerHTML = `
                        ${this.backdrop ? `<div class="announce-backdrop"></div>` : ""}
                        
                            <div class="alert ${this.colorClass} announce ${this.placementClass}  d-flex justify-content-between align-items-center">
                                <div class="announce-content">
                                    <div class="announce-title"></div>
                                    <div class="extra-small announce-details"></div>
                                </div>
                                ${this.showButton ? `<button type="button" class="ms-3 btn ${this.buttonClass} " data-close="true" title="${this.buttonTitle}" aria-label="Close">${this.buttonTitle}</button>` : ""}
                            </div>
                        `;
    }
    addTriggers() {}
    _addEvents() {
      const btn = this.querySelector("[data-close]");
      if (!btn) return;
      btn.addEventListener("click", async () => {
        this.dismiss();
      });
    }
    async dismiss() {
      this.classList.add("d-none");
      await this._saveDismissed();
    }
    show(title = "", details = "") {
      if (!title) title = this.title;
      if (!details) details = this.details;
      const containerTitle = this.querySelector(".announce-title");
      if (!containerTitle) return;
      containerTitle.innerHTML = title;
      this.classList.remove("d-none");
      const containerDetails = this.querySelector(".announce-details");
      if (!containerDetails) return;
      containerDetails.innerHTML = details;
    }
    hide() {
      this.classList.add("d-none");
    }
    async _shouldDisplay() {
      if (!this.isPersistent) return true;
      return !(await this._hasAlreadyDismissed());
    }
    async _saveDismissed() {
      if (!this.isPersistent) return;
      const hash = await this._generateHash(this.storageKey);
      this._setStorage(hash, "true");
    }
    async _hasAlreadyDismissed() {
      const hash = await this._generateHash(this.storageKey);
      return this._storageExists(hash);
    }
    _storageExists(name) {
      return this._getStorage(name) !== null;
    }
    _setStorage(name, value) {
      name = `announce-${name}`;
      localStorage.setItem(name, value);
    }
    _getStorage(name) {
      name = `announce-${name}`;
      return localStorage.getItem(name);
    }
    async _generateHash(text) {
      const encoder = new TextEncoder();
      const data = encoder.encode(text);
      const hashBuffer = await crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, "0")).join("");
      return hashHex;
    }
  }
  customElements.define("wc-announce", announceElement);

  class clientErrorElement extends announceElement {
    constructor() {
      super();
    }
    connectedCallback() {
      super.title = i18nlocales.t("clientError");
      super.init();
    }

    /*
     * Propriedade que indica o gatilho é automático, a partir de window.onerror, ou manual, a partir de um evento disparado pelo desenvolvedor
     */
    get trigger() {
      return this.getStringAttribute("trigger", "event", ["event", "manual"]);
    }

    /*
     * Se o gatilho for automático, adiciona o evento window.onerror
     */
    addTriggers() {
      if (this.trigger !== "event") return;
      window.onerror = (message, file, line) => {
        this.details = `${message}${file ? `. ${file}` : ""}${line ? `. ${line}` : ""}`;
        this.show();
      };
    }
  }
  customElements.define("wc-client-error", clientErrorElement);

  class offlineStatusElement extends announceElement {
    constructor() {
      super();
      this._offline = false;
    }
    connectedCallback() {
      super.title = i18nlocales.t("offline");
      super.color = "danger";
      super.backdrop = true;
      super.showButton = false;
      super.trigger = "event";
      super.init();
    }

    /*
     * Define se a conexão está offline
     */
    set offline(newValue) {
      this._offline = newValue;
      if (newValue) this._showOffline();else this._showOnline();
    }
    get offline() {
      return this._offline;
    }

    /*
     * Propriedade que indica o gatilho é automático, a partir de window.onerror, ou manual, a partir de um evento disparado pelo desenvolvedor
     */
    get trigger() {
      return this.getStringAttribute("trigger", "event", ["event", "manual"]);
    }

    /*
     * Os gatilhos usam o evento online/offline do window
     */
    addTriggers() {
      if (this.trigger !== "event") return;
      window.addEventListener("online", this._checkOnlineStatus.bind(this));
      window.addEventListener("offline", this._checkOnlineStatus.bind(this));
    }

    /*
     * Verifica se está online ou offline a partir de navigator.onLine
     */
    _checkOnlineStatus() {
      this.offline = !navigator.onLine;
    }

    /*
     * Esconde o componente se conexao está online
     */
    _showOnline() {
      super.hide();
    }

    /*
     * Mostra o componente se conexao está online
     */
    _showOffline() {
      super.show();
    }
  }
  customElements.define("wc-offline-status", offlineStatusElement);

  class privacyStatementElement extends announceElement {
    constructor() {
      super();
    }
    async connectedCallback() {
      super.title = this._privacyMessage;
      super.color = "info";
      super.placement = "bottom";
      super.isPersistent = true;
      await super.init();
    }

    /*
     * Propriedade que indica o gatilho é automático, a partir de window.onload, ou manual, a partir de um evento disparado pelo desenvolvedor
     */
    get trigger() {
      return this.getStringAttribute("trigger", "load", ["load", "manual"]);
    }
    get _privacyMessage() {
      if (this.innerHTML.trim()) return this.innerHTML;
      return i18nlocales.t("privacyPolicy");
    }
    addTriggers() {
      if (this.trigger !== "load") return;
      window.addEventListener("load", () => this.show());
    }
  }
  customElements.define("wc-privacy-statement", privacyStatementElement);

  function getPlacementClass(placement) {
    let className = "";
    switch (placement) {
      case "top-right":
        className += ` top-0 start-100 translate-middle`;
        break;
      case "top-left":
        className += ` top-0 start-0 translate-middle`;
        break;
      case "bottom-right":
        className += ` top-100 start-100 translate-middle`;
        break;
      case "bottom-left":
        className += ` top-100 start-0 translate-middle`;
        break;
      case "top":
        className += ` top-0 start-50 translate-middle `;
        break;
      case "bottom":
        className += ` top-100 start-50 translate-middle `;
        break;
      case "left":
        className += ` top-50 start-0 translate-middle `;
        break;
      case "right":
        className += ` top-50 start-100 translate-middle `;
        break;
    }
    return className;
  }
  const placementOptions = ["top", "bottom", "left", "right", "top-left", "top-right", "bottom-left", "bottom-right"];

  class coachingTipElement extends HTMLElement {
    constructor() {
      super();
      this._anchorId = null;
    }
    connectedCallback() {
      this._init();
    }
    _init() {
      if (!this._shouldDisplay()) {
        this.hide();
        return;
      }
      this._render();
    }

    /*
     * A cor do sinal da dica
     */
    get color() {
      return this.getStringAttribute("color", "primary", ["primary", "secondary", "success", "danger", "warning", "info"]);
    }
    get colorClass() {
      return `text-bg-${this.color}`;
    }

    /*
     * A posição da dica / popover
     */
    get placement() {
      return this.getStringAttribute("placement", "top-right", placementOptions);
    }

    /*
     *
     */
    get dismiss() {
      return this.getBooleanAttribute("dismiss", true);
    }

    /*
     * O título da dica / popover
     */
    get caption() {
      return this.getStringAttribute("caption", "");
    }
    set caption(value) {
      this.setAttribute("caption", value);
    }

    /*
     *  O conteudo da dica / popover
     */
    get content() {
      return this.getStringAttribute("content", "");
    }
    set content(value) {
      this.setAttribute("content", value);
    }

    /*
     * O conteudo da dica / popover com botão de dispensar
     */
    get contentWithDismiss() {
      return `${this.content}
                ${this.dismiss ? `<hr><a role='button' id='${this.dismissId}' class='dismiss extra-small'>${i18nlocales.t("dismissTip")}</a>` : ""}`;
    }

    /*
     * O seletor do disparador da dica / popover
     */
    get anchorId() {
      if (this._anchorId) return this._anchorId;
      this._anchorId = `tip-${this.getStringAttribute("id", generateRandomId())}`;
      return this._anchorId;
    }

    /*
     * O elemento disparador da dica / popover
     */
    get anchorElement() {
      return this.querySelector(`#${this.anchorId}`);
    }

    /*
     * O id do botão de dispensar
     */
    get dismissId() {
      return `dismiss-${this.anchorId}`;
    }

    /*
     * O elemento do botão de dispensar
     */
    get dismissElement() {
      return document.querySelector(`#${this.dismissId}`);
    }

    /*
     * O elemento associado de wc-popover
     */
    get popoverElement() {
      return this.querySelector("wc-popover");
    }

    /*
     * O sinal / icone da popover
     */
    get signal() {
      return this.getStringAttribute("signal", "?");
    }
    set signal(value) {
      this.setAttribute("signal", value);
    }
    _render() {
      this.className = `position-absolute ${getPlacementClass(this.placement)}`;
      this.innerHTML = `<a
                        tabindex="0"
                        id="${this.anchorId}"
                        role="button"
                        >
                          <wc-pin size="lg" color="${this.color}" animation="pulse" border="true">
                            ${this.signal}
                          </wc-pin>
                        </a>
                        <wc-popover 
                            trigger="focus"
                            html="true"
                            caption="${this.caption}" 
                            content="${this.contentWithDismiss}"
                            target="#${this.anchorId}">
                        </wc-popover>`;
      this._monitorEvents();
      this._changeParentButtonToRelative();
    }
    _monitorEvents() {
      this.anchorElement.addEventListener("shown.bs.popover", this._bindDismissEvent.bind(this));
    }
    _bindDismissEvent() {
      if (!this.dismissElement) return;
      this.dismissElement.addEventListener("click", this._dismiss.bind(this));
    }
    _dismiss() {
      this._saveDismissed();
      this.popoverElement.hide();
      this.hide();
    }
    _shouldDisplay() {
      return !this._hasAlreadyDismissed();
    }
    _saveDismissed() {
      setStorage(this.anchorId, "true");
    }
    _hasAlreadyDismissed() {
      return hasStorage(this.anchorId);
    }

    /*
     * Se o elemento pai for button, ele deve ter position relative
     */
    _changeParentButtonToRelative() {
      if (this.parentElement.tagName !== "BUTTON") return;
      this.parentElement.style.position = "relative";
    }
    hide() {
      this.classList.add("d-none");
    }
  }
  customElements.define("wc-coaching-tip", coachingTipElement);

  class helperElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    _init() {
      this._render();
    }
    /*
     *  A posição da tooltip
     */
    get placement() {
      return this.getStringAttribute("placement", "auto", ["auto", "top", "bottom", "left", "right"]);
    }
    set placement(value) {
      this.setAttribute("placement", value);
    }

    /*
     * O título da tooltip
     */
    get caption() {
      return this.getStringAttribute("caption", "");
    }
    set caption(value) {
      this.setAttribute("caption", value);
    }

    /*
     * O conteudo da tooltip
     */
    get content() {
      return this.getStringAttribute("content", "");
    }
    set content(value) {
      this.setAttribute("content", value);
    }
    get link() {
      return this.getStringAttribute("link", "");
    }
    set link(value) {
      this.setAttribute("link", value);
    }

    /*
     * O seletor do disparador da dica / popover
     */
    get anchorId() {
      if (this._anchorId) return this._anchorId;
      this._anchorId = `helper-${this.getStringAttribute("id", generateRandomId())}`;
      return this._anchorId;
    }
    _render() {
      this.innerHTML = `<a
        tabindex="0"
        id="${this.anchorId}"
        class="help fade-element"
        target="_blank"
        ${this.link ? `href="${this.link}" target="_blank"` : ""}
        ><wc-icon icon="help-circle" class="lh-0"></wc-icon></a>
        <wc-tooltip
            size="xl"
            ${this.caption ? `caption="${this.caption}"` : ""}
            ${this.content ? `content="${this.content}"` : ""}
            target="#${this.anchorId}">
        </wc-tooltip>`;
    }
  }
  customElements.define("wc-help", helperElement);

  // eslint-disable-next-line no-unused-vars
  class dialogBaseElement extends HTMLElement {
    constructor() {
      super();
      this._bsElement = null;
      this._loaded = false;
      this._shoudReRender = true;
      this._iframe = null;
      this._body = null;
      this._footerTemplate = null;
      this._footer = null;
      this._headerTemplate = null;
      this._header = null;
    }
    connectedCallback() {
      this._openDialogBasedOnUrl();
    }
    disconnectedCallback() {
      if (this.bsElement) this.bsElement.dispose();
    }

    /*
     * Id do componente
     */

    set id(value) {
      this.setAttribute("id", value);
    }
    get id() {
      return this.getAttribute("id");
    }

    /*
     * Título do modal
     */
    get caption() {
      return this.getStringAttribute("caption", "");
    }
    set caption(value) {
      this.setAttribute("caption", value);
    }

    /*
     * Cor do modal
     */
    get color() {
      return this.getStringAttribute("color", "", ["primary", "secondary", "success", "tertiary", "danger", "warning", "info", "light", "dark"]);
    }
    set color(value) {
      this.setAttribute("color", value);
    }

    /*
     * Tamanhos disponíveis: sm, md, lg, xl, xxl
     */
    get size() {
      return this.getStringAttribute("size", "md", ["sm", "md", "lg", "xl", "xxl"]);
    }
    set size(value) {
      this.setAttribute("size", value);
    }

    /*
     * URL da página a ser carregada no iframe
     */
    get url() {
      return this.getStringAttribute("url");
    }
    set url(value) {
      this.setAttribute("url", value);
    }

    /*
     * URL da página a ser carregada via ajax
     */
    get endpoint() {
      return this.getStringAttribute("endpoint");
    }
    set endpoint(value) {
      this.setAttribute("endpoint", value);
    }

    /*
     * Closes the offcanvas when escape key is pressed.
     */
    get keyboard() {
      return this.getBooleanAttribute("keyboard", true);
    }
    set keyboard(value) {
      this.setAttribute("keyboard", value);
    }

    /*
     * Apply a backdrop on body while offcanvas is open.
     * Alternatively, specify static for a backdrop which doesn’t close the offcanvas when clicked.
     */
    get backdrop() {
      return this.getStringAttribute("backdrop", "true", ["true", "false", "static"]);
    }
    set backdrop(value) {
      this.setAttribute("backdrop", value);
    }

    /*
     * Verifica se deve pre-carregar o conteúdo URL/iframe do offcanvas
     */
    get preload() {
      return this.getBooleanAttribute("preload", false);
    }
    set preload(value) {
      this.setAttribute("preload", value);
    }

    /*
     * Configura que o componente deve ser re-renderizado antes de ser mostrado
     */
    get shouldReRender() {
      return this._shoudReRender;
    }
    set shouldReRender(value) {
      this._shoudReRender = value;
    }

    /*
     * Define o corpo do modal
     */
    get body() {
      return this._body;
    }
    set body(value) {
      this._body = value === undefined ? null : value;
    }

    /*
     * Define o rodapé do modal
     */
    get footer() {
      return this._footer;
    }
    set footer(value) {
      this._footer = value === undefined ? null : value;
    }

    /*
     * Define o cabeçalho do modal
     */
    get header() {
      return this._header;
    }
    set header(value) {
      this._header = value === undefined ? null : value;
    }
    get hasHeader() {
      return this.header || this.caption;
    }

    /*
     * Indica e verifica se o conteúdo do iframe já foi carregado
     */
    get isIframeLoaded() {
      return this._loaded;
    }
    set isIframeLoaded(value) {
      this._loaded = value;
    }
    set iframe(value) {
      this._iframe = value;
    }
    get iframe() {
      return this._iframe;
    }

    /*
     * O elemento loader utilizado junto com o iframe
     */
    get loaderHtml() {
      return `<wc-rainbow></wc-rainbow>`;
    }
    get loader() {
      return this.querySelector("wc-rainbow");
    }

    /*
     * O elemento original do Bootstrap
     */
    get bsElement() {
      return this._bsElement;
    }
    set bsElement(value) {
      this._bsElement = value;
    }

    /*
     * Se o primeiro elemento de input deve ser focado
     */
    get autofocus() {
      return this.getBooleanAttribute("autofocus", true);
    }
    set autofocus(value) {
      this.setAttribute("autofocus", value);
    }

    /*
     * Renderizar o componente
     */
    $render() {
      throw new Error("Method render needs override");
    }

    /*
     * Configurar o componente
     */
    $configure() {
      throw new Error("Method configure needs override");
    }

    /*
     * Anexar eventos
     */
    $bindBsEvents() {
      throw new Error("Method bindBsEvents needs override");
    }

    /*
     * Pega o corpo do modal
     */
    getBody() {
      if (this.url) return this._getIframe();
      if (this.body) return this._htmlToElement(this.body);
      return this.getTemplate("body");
    }
    getFooter() {
      if (this.footer) return this._htmlToElement(this.footer);
      return this.getTemplate("footer");
    }
    getHeader() {
      if (this.header) return this._htmlToElement(this.header);
      return this.getTemplate("header");
    }
    _htmlToElement(html) {
      if (!html) return null;
      var template = document.createElement("template");
      template.innerHTML = html.trim();
      return template.content;
    }

    /*
     * Pega o botão de fechar o modal anexado no body
     */
    getBodyCloseButton(modalType) {
      return `<div class="bs-secondary-bg position-absolute top-0 end-0 me-4 mt-2 rounded-1 p-1 opacity-75">
                    <button 
                        type="button" 
                        class="btn-close btn-close-sm p-2 mb-1 mx-1" 
                        title="${i18nlocales.t("close")}"
                        data-bs-dismiss="${modalType}" 
                        aria-label="Close"></button>
                </div>`;
    }
    async show() {
      if (this.shouldReRender) {
        await this._checkForAjaxLoading();
        this._display();
        this._checkForIframeLoading();
      }
      this.classList.add("show");
      this.bsElement.show();
      this._searchForElementToFocus();
    }
    hide() {
      this.bsElement.hide();
    }
    onHide() {
      if (!this.iframe) return;
      this.iframe.src = "about:blank";
    }
    reRender() {
      this.shouldReRender = true;
      this.$render();
      this.$configure();
      this.$bindBsEvents();
    }
    _display() {
      if (!this.shouldReRender) return;
      this.$render();
      this.$configure();
      this.$bindBsEvents();
      this.shouldReRender = false;
      this._loadEndpointScripts(this);
    }
    _openDialogBasedOnUrl() {
      if (!this._currentUrlContainsId()) return;
      this.show();
    }
    _getIframe() {
      if (!this.url) return "";
      const url = this.preload ? this.url : "about:blank";
      this.iframe = document.createElement("iframe");
      this.iframe.setAttribute("width", "100%");
      this.iframe.setAttribute("height", "100%");
      this.iframe.setAttribute("frameborder", "0");
      this.iframe.setAttribute("src", url);
      return this.iframe;
    }
    _currentUrlContainsId() {
      return window.location.href.includes(`#${this.id}`);
    }
    _checkForIframeLoading() {
      if (this.preload) return;
      if (!this.iframe) return;
      this._loadIframeUrl();
    }
    _loadIframeUrl() {
      if (!this.iframe) return;
      this._showLoader();
      this.iframe.src = this.url;
      this.iframe.addEventListener("load", () => this._finishIframeLoading());
    }
    _finishIframeLoading() {
      this._hideLoader();
    }
    async _checkForAjaxLoading() {
      if (!this.endpoint) return;
      await this._getHtmlEndpoint();
    }
    async _getHtmlEndpoint() {
      if (!this.endpoint) return "";
      this._showLoader();
      const response = await fetch(this.endpoint);
      const htmlText = await response.text();
      const parser = new DOMParser();
      const htmlContent = parser.parseFromString(htmlText, "text/html");
      this._getHtmlEndpointTemplate(htmlContent, "body");
      this._getHtmlEndpointTemplate(htmlContent, "footer");
      this._getHtmlEndpointTemplate(htmlContent, "header");
      this._hideLoader();
    }
    _getHtmlEndpointTemplate(endpointContent, templateName) {
      if (!endpointContent) return "";
      if (!templateName) return "";
      const templateContent = endpointContent.querySelector(`template[name='${templateName}']`);
      if (!templateContent) return "";
      var template = document.createElement("template");
      template.setAttribute("name", templateName);
      template.innerHTML = templateContent.innerHTML;
      this.appendChild(template);
    }
    _loadEndpointScripts(container) {
      const scripts = container.querySelectorAll("script");
      scripts.forEach(script => {
        const scriptText = script.text;
        const scriptElement = document.createElement("script");
        scriptElement.type = "text/javascript";
        scriptElement.text = scriptText;
        document.body.appendChild(scriptElement);
      });
    }
    _showLoader() {
      if (this.loader) this.loader.classList.remove("d-none");
    }
    _hideLoader() {
      if (this.loader) this.loader.classList.add("d-none");
    }
    _searchForElementToFocus() {
      if (!this.autofocus) return;
      const inputs = this.querySelector("input:not([type='hidden']), textarea, select");
      if (inputs) {
        setTimeout(() => {
          inputs.focus();
        }, 1000);
        return;
      }
    }
  }

  class modalElement extends dialogBaseElement {
    constructor() {
      super();
      this._id = generateRandomId();
    }
    connectedCallback() {}
    static get observedAttributes() {
      return ["size", "backdrop", "keyboard", "caption", "url", "fullscreen", "centered", "autofocus", "endpoint"];
    }
    attributeChangedCallback() {
      super.shouldReRender = true;
    }
    get fullScreen() {
      return this.getBooleanAttribute("fullscreen", false);
    }
    set fullScreen(value) {
      this.setAttribute("fullscreen", value);
    }
    get centered() {
      return this.getBooleanAttribute("centered", true);
    }
    set centered(value) {
      this.setAttribute("centered", value);
    }
    get centeredClass() {
      if (this.centered) return "modal-dialog-centered";
      return "";
    }
    get fullScreenClass() {
      if (this.fullScreen) return "modal-fullscreen";
      if (super.size === "xxl") return "modal-fullscreen";
      return "";
    }
    get sizeClass() {
      return `modal-${super.size}`;
    }
    get colorClass() {
      if (super.color) return `text-bg-${super.color}`;
      return "";
    }
    get contentUrlClass() {
      if (!super.url) return "";
      return `modal-content-with-url`;
    }
    get scrollableClass() {
      if (!super.url) return "modal-dialog-scrollable";
      return "";
    }
    get element() {
      return this.querySelector(`#${this._id}`);
    }
    get headerElement() {
      return this.element.querySelector(`.modal-header-content`);
    }
    get bodyElement() {
      return this.element.querySelector(`.modal-body-content`);
    }
    get footerElement() {
      return this.element.querySelector(`.modal-footer-content`);
    }
    $render() {
      const header = super.getHeader();
      const body = super.getBody();
      const footer = super.getFooter();
      const hasHeader = header || super.caption;
      const loader = super.url ? super.loaderHtml : "";
      this._removeElementIfExists();
      this.insertAdjacentHTML("beforeend", `<div  
            id="${this._id}"
            class="modal fade" 
            tabindex="-1" 
            data-bs-backdrop="${super.backdrop}"  
            data-bs-keyboard="${super.keyboard}" 
            aria-hidden="true">
              <div class="modal-dialog 
              modal-dialog-scrollable
                          ${this.scrollableClass}
                          ${this.centeredClass} 
                          ${this.fullScreenClass} 
                          ${this.sizeClass}">
                  <div class="modal-content ${this.contentUrlClass}">
                      ${hasHeader ? `<div class="modal-header">
                              ${super.caption ? `<h4 class="modal-title">${super.caption}</h4>` : ""}
                              <div class="modal-header-content"></div>
                              <button type="button" class="btn-close" title="${i18nlocales.t("close")}" data-bs-dismiss="modal" aria-label="Close"></button>
                          </div>` : ""}
                      <div class="modal-body">
                          ${!hasHeader ? super.getBodyCloseButton("modal") : ""}
                          ${loader}
                          <div class="modal-body-content"></div>
                      </div>
                      ${footer ? `<div class="modal-footer"><div class="modal-footer-content"></div></div>` : ""}
                  </div>
              </div>
          </div>`);
      this._addHeader(header);
      this._addBody(body);
      this._addFooter(footer);
    }
    _removeElementIfExists() {
      if (!this.element) return;
      this.element.remove();
    }
    _addHeader(header) {
      if (!header) return;
      if (!this.headerElement) return;
      this.headerElement.appendChild(header);
    }
    _addBody(body) {
      if (!body) return;
      if (!this.bodyElement) return;
      this.bodyElement.appendChild(body);
    }
    _addFooter(footer) {
      if (!footer) return;
      if (!this.footerElement) return;
      this.footerElement.appendChild(footer);
    }
    $configure() {
      super.bsElement = window.bootstrap.Modal.getOrCreateInstance(this.element);
    }
    $bindBsEvents() {
      this.addEventListener("hidden.bs.modal", () => super.onHide());
    }
  }
  customElements.define("wc-modal", modalElement);

  class offcanvasElement extends dialogBaseElement {
    constructor() {
      super();
      this._id = generateRandomId();
      this._onHide = null;
    }
    connectedCallback() {}
    static get observedAttributes() {
      return ["size", "backdrop", "keyboard", "caption", "url", "placement", "autofocus"];
    }
    attributeChangedCallback() {
      super.shouldReRender = true;
    }

    /*
     * Definir a posição do offcanvas
     */
    get placement() {
      return this.getStringAttribute("placement", "start", ["left", "start", "right", "end", "top", "bottom"]);
    }
    set placement(value) {
      this.setAttribute("placement", value);
    }

    /*
     * Permitir que o scroll da página apareca quando o offcanvas estiver aberto
     */
    get scroll() {
      return this.getBooleanAttribute("scroll", false);
    }
    set scroll(value) {
      this.setAttribute("scroll", value);
    }
    get sizeClass() {
      return `offcanvas-size-${super.size}`;
    }
    get placementClass() {
      if (this.placement === "left") {
        this.placement = "start";
      }
      if (this.placement === "right") {
        this.placement = "end";
      }
      return `offcanvas-${this.placement}`;
    }
    get bodyUrlClass() {
      if (super.url) return `offcanvas-body-with-url`;
      if (super.endpoint) return `offcanvas-body-with-endpoint`;
      return "";
    }
    get element() {
      return this.querySelector(`#${this._id}`);
    }
    get headerElement() {
      return this.element.querySelector(`.offcanvas-header-content`);
    }
    get bodyElement() {
      return this.element.querySelector(`.offcanvas-body-content`);
    }
    get footerElement() {
      return this.element.querySelector(`.offcanvas-footer-content`);
    }
    set onhide(value) {
      this._onHide = value;
    }
    $render() {
      const zIndex = this._nextOffcanvasOpenZIndex();
      const header = super.getHeader();
      const body = super.getBody();
      const footer = super.getFooter();
      const hasHeader = header || super.caption;
      const loader = super.url ? super.loaderHtml : "";
      this._removeElementIfExists();
      this.insertAdjacentHTML("beforeend", `<div 
          id="${this._id}"
          class="offcanvas ${this.placementClass} ${this.sizeClass} " 
          ${zIndex ? `style="z-index: ${zIndex}"` : ""}
          tabindex="-1" 
          data-bs-scroll="${this.scroll}"
          data-bs-keyboard="${super.keyboard}" 
          data-bs-backdrop="${super.backdrop}" 
          aria-hidden="true">   
                  ${hasHeader ? `
                      <div class="offcanvas-header">
                          ${super.caption ? `<h4 class="offcanvas-title">${super.caption}</h4>` : ""}
                          <div class="offcanvas-header-content"></div>
                          <button type="button" title="${i18nlocales.t("close")}" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                      </div>` : ""}
                  <div class="offcanvas-body ${this.bodyUrlClass}">
                      ${!hasHeader ? super.getBodyCloseButton("offcanvas") : ""}
                      ${loader}
                      <div class="offcanvas-body-content"></div>
                  </div>
                  ${footer ? `<div class="offcanvas-footer"><div class="offcanvas-footer-content"></div></div>` : ""}
          </div>`);
      this._addHeader(header);
      this._addBody(body);
      this._addFooter(footer);
    }
    _removeElementIfExists() {
      if (!this.element) return;
      this.element.remove();
    }
    _addHeader(header) {
      if (!header) return;
      if (!this.headerElement) return;
      this.headerElement.appendChild(header);
    }
    _addBody(body) {
      if (!body) return;
      if (!this.bodyElement) return;
      this.bodyElement.appendChild(body);
    }
    _addFooter(footer) {
      if (!footer) return;
      if (!this.footerElement) return;
      this.footerElement.appendChild(footer);
    }
    $configure() {
      super.bsElement = window.bootstrap.Offcanvas.getOrCreateInstance(this.element);
    }
    $bindBsEvents() {
      this.addEventListener("hidden.bs.offcanvas", () => {
        super.onHide();
        if (typeof this._onHide == "function") this._onHide();
      });
    }

    /*
     * Pega o valor do maior zindex de outros offcanvas abertos
     */
    _nextOffcanvasOpenZIndex() {
      const openOffcanvas = document.querySelectorAll(".offcanvas.show, .offcanvas-sm.show, .offcanvas-md.show, .offcanvas-lg.show, .offcanvas-xl.show, .offcanvas-xxl.show");
      if (openOffcanvas.length === 0) return null;
      let maxZIndex = 0;
      [...openOffcanvas].forEach(element => {
        let zIndex = parseInt(window.getComputedStyle(element).zIndex, 10);
        if (!isNaN(zIndex)) {
          maxZIndex = Math.max(maxZIndex, zIndex);
        }
      });
      return maxZIndex + 1;
    }
  }
  customElements.define("wc-offcanvas", offcanvasElement);

  class selectableDropdownElement extends HTMLElement {
    constructor() {
      super();
      this._format = null;
    }
    static get observedAttributes() {
      return ["value"];
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "value" && oldValue !== newValue) {
        this._activeSelectedItem();
      }
    }
    connectedCallback() {
      this._init();
    }
    _init() {
      this._bindEvents();
      this._activeSelectedItem();
    }
    get _triggerElement() {
      return this.querySelector("[data-bs-toggle]");
    }
    get _items() {
      return this.querySelectorAll(".dropdown-item");
    }
    get value() {
      return this.getStringAttribute("value");
    }
    set value(newValue) {
      this.setAttribute("value", newValue);
    }
    get format() {
      return this._format;
    }
    set format(newValue) {
      this._format = newValue;
    }
    _bindEvents() {
      if (!this._items) return;
      this._bindSelectNewItem();
    }
    _bindSelectNewItem() {
      this._items.forEach(item => {
        item.addEventListener("click", () => {
          this._selectItem(item);
        });
      });
    }
    _selectItem(item) {
      this.value = this._getItemValue(item);
      this._activeSelectedItem();
      this._dispatchChangeEvent(item);
    }
    _activeSelectedItem(item) {
      if (!this.value) return;
      item = item || this._getSelectedItem();
      if (!item) return;
      this._inactiveAll();
      this._triggerElement.innerHTML = this._getItemText(item);
      item.classList.add("active");
      item.setAttribute("aria-pressed", "true");
    }
    _dispatchChangeEvent(item) {
      this.dispatchEvent(new CustomEvent("change", {
        detail: {
          value: this.value,
          text: this._getItemText(item)
        }
      }));
    }
    _inactiveAll() {
      if (!this._items) return;
      this._items.forEach(item => {
        item.classList.remove("active");
        item.setAttribute("aria-pressed", "false");
      });
    }
    _getItemValue(item) {
      return item.getStringAttribute("data-value", this._getItemText(item));
    }
    _getItemText(item) {
      return item.getStringAttribute("data-text", item.innerText);
    }
    _getSelectedItem() {
      return [...this._items].find(item => this._isSelectedItem(item));
    }
    _isSelectedItem(item) {
      return this._getItemValue(item) === this.value;
    }
  }
  customElements.define("wc-selectable-dropdown", selectableDropdownElement);

  const loadingSvgPromises = [];
  class iconElement extends HTMLElement {
    constructor() {
      super();
    }
    async connectedCallback() {
      await this._init();
    }
    async _init() {
      if (!this._alreadyExistsSymbol()) {
        await this._loadSvgToSymbolLibrary();
      }
      this._render();
    }
    set size(value) {
      this.setAttribute("size", value);
    }
    get size() {
      return this.getStringAttribute("size", "md", ["sm", "md", "lg", "xl"]);
    }
    set icon(value) {
      this.setAttribute("icon", value);
      this._init();
    }
    get icon() {
      return this.getStringAttribute("icon", "");
    }
    _render() {
      this.innerHTML = `<svg class="ico ico-${this.size}">
          <use xlink:href="#${this.icon}"></use>
        </svg>`;
    }
    _alreadyExistsSymbol() {
      return document.querySelectorAll(`symbol[id='${this.icon}']`).length > 0;
    }

    /*
     * Se existir o mesmo ícone diversas vezes na mesma página, assegura que ele seja adicionado somente uma vez ao DOM,
     * compartilhando o mesmo promises em todas as chamadas de uso dele
     */
    async _loadSvgToSymbolLibrary() {
      if (!loadingSvgPromises?.find(promise => promise?.icon === this.icon)) {
        loadingSvgPromises.push({
          icon: this.icon,
          promises: loadSvgToSymbolLibrary(this.icon)
        });
      }
      await loadingSvgPromises.find(promise => promise?.icon === this.icon).promises;
    }
    changeIcon(icon) {
      this.icon = icon;
      this._init();
    }
    refresh() {
      this._init();
    }
  }
  customElements.define("wc-icon", iconElement);

  function ownKeys(e, r) {
    var t = Object.keys(e);
    if (Object.getOwnPropertySymbols) {
      var o = Object.getOwnPropertySymbols(e);
      r && (o = o.filter(function (r) {
        return Object.getOwnPropertyDescriptor(e, r).enumerable;
      })), t.push.apply(t, o);
    }
    return t;
  }
  function _objectSpread2(e) {
    for (var r = 1; r < arguments.length; r++) {
      var t = null != arguments[r] ? arguments[r] : {};
      r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
        _defineProperty(e, r, t[r]);
      }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
        Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
      });
    }
    return e;
  }
  function _toPrimitive(t, r) {
    if ("object" != typeof t || !t) return t;
    var e = t[Symbol.toPrimitive];
    if (void 0 !== e) {
      var i = e.call(t, r || "default");
      if ("object" != typeof i) return i;
      throw new TypeError("@@toPrimitive must return a primitive value.");
    }
    return ("string" === r ? String : Number)(t);
  }
  function _toPropertyKey(t) {
    var i = _toPrimitive(t, "string");
    return "symbol" == typeof i ? i : String(i);
  }
  function _defineProperty(obj, key, value) {
    key = _toPropertyKey(key);
    if (key in obj) {
      Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
      });
    } else {
      obj[key] = value;
    }
    return obj;
  }

  let quillJsLoadingPromises = null;
  let quillCssLoadingPromises = null;
  let codeMirrorLoadingPromises = null;
  let tinyMceLoadingPromises = null;
  let bpmnJsLoadingPromises = null;
  let bpmnJsCssLoadingPromises = null;
  let inputMaskLibraryLoadingPromisses = null;
  let lottieLoadingPromise = null;
  async function loadInputMask() {
    if (!inputMaskLibraryLoadingPromisses) {
      inputMaskLibraryLoadingPromisses = loadScriptAsync(getZeevBootstrapPath("ondemand/inputmask/inputmask.min.js"));
    }
    await inputMaskLibraryLoadingPromisses;
  }
  async function loadQuill() {
    if (!quillJsLoadingPromises) {
      quillJsLoadingPromises = loadScriptAsync(getZeevBootstrapPath("ondemand/quill/quill.custom.min.js"));
    }
    if (!quillCssLoadingPromises) {
      quillCssLoadingPromises = loadCssAsync(getZeevBootstrapPath("ondemand/quill/quill.snow.min.css"));
    }
    await Promise.all([quillJsLoadingPromises, quillCssLoadingPromises]);
  }
  async function loadCodeEditor() {
    if (!codeMirrorLoadingPromises) {
      codeMirrorLoadingPromises = loadScriptAsync(getZeevBootstrapPath("ondemand/codemirror/codemirror.min.js"));
    }
    await codeMirrorLoadingPromises;
  }
  async function loadTinyMce() {
    if (!tinyMceLoadingPromises) {
      tinyMceLoadingPromises = loadScriptAsync(getZeevBootstrapPath("ondemand/tinymce/tinymce.min.js"));
    }
    await tinyMceLoadingPromises;
  }
  async function loadBpmnJs() {
    if (!bpmnJsLoadingPromises) {
      bpmnJsLoadingPromises = loadScriptAsync(getZeevBootstrapPath("ondemand/bpmn-js/js/zeev-bundle-bpmnjs.min.js"));
    }
    if (!bpmnJsCssLoadingPromises) {
      bpmnJsCssLoadingPromises = loadCssAsync(getZeevBootstrapPath("ondemand/bpmn-js/css/zeev-bundle-bpmnjs.min.css"));
    }
    await Promise.all([bpmnJsLoadingPromises, bpmnJsCssLoadingPromises]);
    await bpmnJsLoadingPromises;
  }
  async function loadLottie() {
    if (!lottieLoadingPromise) {
      lottieLoadingPromise = loadScriptAsync(getZeevBootstrapPath("ondemand/lottie/js/zeev-bundle-lottie.min.js"));
    }
    await lottieLoadingPromise;
  }

  class inputMaskElement extends HTMLElement {
    constructor() {
      super();
      this._value = null;
      this._control = null;
      this._controlId = null;
      this._numericFormats = {
        pt: {
          groupSeparator: ".",
          radixPoint: ","
        },
        en: {
          groupSeparator: ",",
          radixPoint: "."
        },
        es: {
          groupSeparator: ".",
          radixPoint: ","
        }
      };
    }
    async connectedCallback() {
      if (this?.control) return;
      await this.init();
    }
    async init() {
      await loadInputMask();
      this._render();
      this._transferAttrs();
      this._configure();
      this._setInitialValue();
      const observer = new MutationObserver(mutationsList => this._mutationCallback(mutationsList));
      observer.observe(this, {
        attributes: true
      });
    }

    /*
     * O controle input associado ao componente
     */
    get control() {
      return this._control;
    }
    set control(newValue) {
      this._control = newValue;
    }

    /*
     * Pegar o value do controle associado
     */
    get value() {
      return this._value = this.control?.value;
    }

    /*
     * Definir o value do controle associado
     */
    set value(newValue) {
      if (newValue == null) newValue = "";
      this._value = newValue;
      if (!this.control) return;
      this.control.value = newValue;
    }

    /*
     * Caso a mascara seja definida por um alias
     */
    get alias() {
      return this.getStringAttribute("alias");
    }
    set alias(value) {
      this.setAttribute("alias", value);
    }

    /*
     * Caso a mascara seja definida por configuração
     */
    get mask() {
      return this.getStringAttribute("mask");
    }
    set mask(value) {
      this.setAttribute("mask", value);
    }

    /*
     * Caso a mascara seja definida por regular expression
     */
    get regexp() {
      return this.getStringAttribute("regexp");
    }
    set regexp(value) {
      this.setAttribute("regexp", value);
    }
    get customConfig() {
      return this.getStringAttribute("custom-config");
    }
    set customConfig(value) {
      this.setAttribute("custom-config", value);
    }

    /*
     * Renderizar o componente associado
     */
    _render() {
      this.control = document.createElement("input");
      this.control.setAttribute("id", generateRandomId());
      this.appendChild(this.control);
    }

    /*
     * Transferir os atributos e eventos do webcomponent para o controle associado
     */
    _transferAttrs() {
      const excludeList = ["id", "alias", "mask", "regexp", "custom-config", "time", "mode", "value"];
      const attrs = Array.from(this.attributes).filter(attr => attr !== null && !excludeList.includes(attr.name));
      attrs.forEach(attr => {
        if (attr.name == "class") {
          this._transferClasses();
          return;
        }
        this.control.setAttribute(attr.name, attr.value);
        this.removeAttribute(attr.name);
      });
    }

    /*
     * Configurar a mascara do controle associado
     */
    _configure() {
      if (this.control.inputmask) return;
      const config = this._compileFinalConfigurations();
      window.Inputmask(config).mask(this.control);
    }
    _compileFinalConfigurations() {
      let config = {};
      if (this.alias) {
        config = _objectSpread2(_objectSpread2({}, config), {
          alias: this.alias
        });
      }
      if (this.mask) {
        config = _objectSpread2(_objectSpread2({}, config), {
          mask: this.mask
        });
      }
      if (this.regexp) {
        config = _objectSpread2(_objectSpread2({}, config), {
          regex: this.regexp
        });
      }
      if (this.customConfig) {
        config = _objectSpread2(_objectSpread2({}, config), _objectSpread2({}, convertToJson(this.customConfig)));
      }
      config = this._addMaskAttributes(config);
      return config;
    }

    /*
     * Configurar atributos de mascaras especiais
     */
    _addMaskAttributes(config) {
      if (!config) return config;
      if (!config.alias) return config;
      config = this._addDecimalMaskAttributes(config);
      config = this._addNumericMaskAttributes(config);
      config = this._addDatetimeMaskAttributes(config);
      config = this._addDateMaskAttributes(config);
      return config;
    }

    /*
     * Configurar atributos de mascaras de valor decimal
     */
    _addDecimalMaskAttributes(config) {
      if (!config) return config;
      if (!config.alias) return config;
      if (config.alias !== "decimal" && config.alias !== "money" && config.alias !== "currency") return config;
      const actualNumericFormat = this._numericFormats[i18nlocales.lng()];
      config = _objectSpread2(_objectSpread2({}, config), {
        groupSeparator: actualNumericFormat.groupSeparator,
        radixPoint: actualNumericFormat.radixPoint,
        autoGroup: true,
        digits: 2,
        digitsOptional: false,
        autoUnmask: true,
        rightAlign: false
      });
      return config;
    }

    /*
     * Configurar atributos de mascaras de valor numérico
     */
    _addNumericMaskAttributes(config) {
      if (!config) return config;
      if (!config.alias) return config;
      if (config.alias !== "numeric") return config;
      const actualNumericFormat = this._numericFormats[i18nlocales.lng()];
      config = _objectSpread2(_objectSpread2({}, config), {
        groupSeparator: actualNumericFormat.groupSeparator,
        radixPoint: actualNumericFormat.radixPoint,
        autoGroup: true,
        digits: 2,
        digitsOptional: true,
        autoUnmask: true,
        rightAlign: false
      });
      return config;
    }

    /*
     * Configurar atributos de mascaras de valor data
     */
    _addDateMaskAttributes(config) {
      if (!config) return config;
      if (!config.alias) return config;
      if (config.alias !== "date") return config;
      const dateFormat = {
        pt: "dd/mm/yyyy",
        en: "mm/dd/yyyy",
        es: "dd/mm/yyyy"
      };
      const actualDateFormat = dateFormat[i18nlocales.lng()];
      config = _objectSpread2(_objectSpread2({}, config), {
        alias: "datetime",
        inputFormat: actualDateFormat,
        prefillYear: false,
        placeholder: "__/__/____"
      });
      return config;
    }

    /*
     * Configurar atributos de mascaras de valor data e hora
     */
    _addDatetimeMaskAttributes(config) {
      if (!config) return config;
      if (!config.alias) return config;
      if (config.alias !== "datetime") return config;
      const dateFormat = {
        pt: "dd/mm/yyyy HH:MM",
        en: "mm/dd/yyyy HH:MM",
        es: "dd/mm/yyyy HH:MM"
      };
      const actualDateFormat = dateFormat[i18nlocales.lng()];
      config = _objectSpread2(_objectSpread2({}, config), {
        alias: "datetime",
        inputFormat: actualDateFormat,
        prefillYear: false,
        placeholder: "__/__/____"
      });
      return config;
    }
    addToken(token) {
      insertTextAtCursor(this.control, token);
    }

    /*
     * Esse método é preciso principalmente para integrações com Vue com v-model.
     * O Vue seta o valor de value do webcomponent antes do control existir
     * O vue seta esse valor em "codigo", como webcomponent.value = "xxxx"
     */
    _setInitialValue() {
      this.value = this._value || this.getAttribute("value");
    }
    _mutationCallback(mutationsList) {
      for (const mutation of mutationsList) {
        if (mutation.type !== "attributes" || mutation.attributeName !== "class") continue;
        this._transferClasses();
      }
    }
    _transferClasses() {
      const isInvalidClass = "is-invalid";
      const containsIsInvalid = this.classList.contains(isInvalidClass);
      this.classList.forEach(x => this.control.classList.add(x));

      //a classe "is-invalid" deve ser transferida para o controle, mas também mantida no componente pai
      if (containsIsInvalid) {
        if (this.getAttribute("class") != isInvalidClass) this.setAttribute("class", isInvalidClass);
      } else {
        this.control.classList.remove(isInvalidClass);
        this.removeAttribute("class");
      }
    }
  }
  customElements.define("wc-input-mask", inputMaskElement);

  let inputFlatPickrLibraryLoadingPromisses = null;
  class inputDateElement extends inputMaskElement {
    constructor() {
      super();
      this._customConfig = null;
      this._flatpickr = null;
    }
    async connectedCallback() {
      await this.init();
    }

    /*
     * Indica se deve usar tempo
     */
    get time() {
      return this.getBooleanAttribute("time", false);
    }
    set time(value) {
      this.setAttribute("time", value);
    }
    get mode() {
      return this.getStringAttribute("mode", "single", ["single", "multiple", "range"]);
    }
    set mode(value) {
      this.setAttribute("mode", value);
    }

    /*
     * Define a máscara do componente. Sobrescreve a propriedade do componente InputMask
     */
    get alias() {
      return this._shouldUseMask ? this.time ? "datetime" : "date" : "";
    }

    /*
     * Verifica se deve utilizar máscara.
     */
    get _shouldUseMask() {
      //Se o modo do componente for diferente de single, não utilizar máscara
      if (this.mode !== "single") return false;
      return true;
    }

    /*
     * Verifica se deve permitir ao usuário digitar a data
     */
    get _allowInput() {
      return this._shouldUseMask;
    }

    /*
     * Configuração definida pelo DEV + padrão
     */
    get _config() {
      return {
        disableMobile: "true",
        allowInput: this._allowInput,
        dateFormat: this._dateHourFormat,
        enableTime: this.time,
        mode: this.mode,
        locale: i18nlocales.lng()
      };
    }

    /*
     * Acessa o objeto flatpickr
     */
    get fpElement() {
      return this._flatpickr;
    }

    /*
     * Acessa o objeto flatpickr
     */
    set fpElement(newValue) {
      this._flatpickr = newValue;
    }

    /*
     * Retorna array de datas selecionadas no campo
     */
    get selectedDates() {
      return this._flatpickr.selectedDates;
    }

    /*
     * Define o maxlength de caracteres no campo
     */
    get _maxlength() {
      if (this.time) return 16;
      return 10;
    }

    /*
     * Define o formato da data
     */
    get _dateHourFormat() {
      return this._dateFormat + (this.time ? " H:i" : "");
    }
    get _dateFormat() {
      const lng = i18nlocales.lng();
      if (lng === "en") return "m/d/Y";
      return "d/m/Y";
    }
    async init() {
      if (this?.fpElement?.input) return;

      //realiza as inicializações do componente pai
      await super.init();
      await this._loadFlatpickr();
      this._configureDatePicker();
      this._monitorEvents();
    }

    /*
     * Carregar a biblioteca flatpkr, somente 1 vez na página
     */
    async _loadFlatpickr() {
      if (!inputFlatPickrLibraryLoadingPromisses) {
        const flatpickrMain = loadScriptAsync(getZeevBootstrapPath("ondemand/flatpickr/flatpickr.custom.min.js"));
        inputFlatPickrLibraryLoadingPromisses = Promise.all([flatpickrMain]);
      }
      await inputFlatPickrLibraryLoadingPromisses;
    }

    /*
     * Confirgura o componente para utilizar o plugin de datepicker
     */
    _configureDatePicker() {
      super.control.setAttribute("type", "text");
      super.control.setAttribute("autocomplete", "off");
      super.control.setAttribute("maxlength", this._maxlength);
      this.fpElement = window.flatpickr(super.control, this._config);
    }
    _monitorEvents() {
      this.fpElement.config.onChange.push(() => {
        this._emit("ready", this);
      });
    }
    _emit(event, data) {
      const customEvent = new CustomEvent(event, {
        detail: data
      });
      this.dispatchEvent(customEvent);
    }
  }
  customElements.define("wc-input-date", inputDateElement);

  class inputFake extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    get content() {
      return this.getStringAttribute("content", "");
    }
    set content(value) {
      this.setAttribute("content", value);
    }
    get cursor() {
      return this.getStringAttribute("cursor", "not-allowed");
    }
    set cursor(value) {
      this.setAttribute("cursor", value);
    }
    _init() {
      this._render();
    }
    _render() {
      this.innerHTML = `<span
                            class="form-control form-control-sm form-fake"
                            style="cursor: ${this.cursor}">
                             ${this.content}
                        </span>`;
    }
  }
  customElements.define("wc-input-fake", inputFake);

  class inputTokenElement extends HTMLElement {
    constructor() {
      super();
      this._control = null;
      this._controlId = null;
    }
    connectedCallback() {
      this.init();
    }
    init() {
      this._render();
      this._addEvents();
      this._transferAttrs();
    }

    /*
     * O controle input associado ao componente
     */
    get control() {
      return this._control;
    }
    set control(newValue) {
      this._control = newValue;
    }

    /*
     * Pegar o value do controle associado
     */
    get value() {
      return this.control.innerText;
    }
    get valueHtml() {
      return this.control.innerHTML;
    }

    /*
     * Definir o value do controle associado
     */
    set value(newValue) {
      this.control.innerText = newValue;
      this._formatTokens();
    }

    /*
     * Definir o ID do controle associado
     */
    get controlId() {
      if (this._controlId !== null) return this._controlId;
      if (this.getAttribute("id")) this._controlId = `${this.getAttribute("id")}-control`;
      if (!this.getAttribute("id")) this._controlId = "";
      return this._controlId;
    }

    /*
     * Renderizar o componente associado
     */
    _render() {
      this.control = document.createElement("div");
      this.control.setAttribute("contenteditable", "true");
      this.control.setAttribute("id", this.controlId);
      this.appendChild(this.control);
    }
    _addEvents() {
      this.control.addEventListener("input", this._formatTokens.bind(this));
    }
    _formatTokens() {
      let content = this.control.innerHTML;
      const tokens = this.control.querySelectorAll(".token");
      tokens.forEach(token => {
        const tokenValue = token.innerText;
        content = content.replace(token.outerHTML, tokenValue);
      });

      // Get the current cursor position
      const offset = this._getCurrentCursorPosition(this.control);

      // Define a regular expression to match expressions in the form of {prefix.word}
      const expressionRegex = /\{(\w+\.\w+)\}/g;

      // Generate a unique ID for each match
      const uniqueId = Math.random().toString(36).substr(2, 9);

      // Replace the expressions with HTML tags
      const replacedContent = content.replace(expressionRegex, `<span id="${uniqueId}" class="token badge badge-tag">$&</span>`);

      // Set the modified content back to the DIV
      this.control.innerHTML = replacedContent;

      // Restore the cursor position
      this._setCurrentCursorPosition(offset, this.control);
    }

    /*
     * Transferir os atributos e eventos do webcomponent para o controle associado
     */
    _transferAttrs() {
      const attrs = Array.from(this.attributes).filter(attr => attr !== null && attr.name !== "id");
      attrs.forEach(attr => {
        this.control.setAttribute(attr.name, attr.value);
        this.removeAttribute(attr.name);
      });
    }
    _getCurrentCursorPosition(parentElement) {
      var selection = window.getSelection(),
        charCount = -1,
        node;
      if (selection.focusNode) {
        if (this._isChildOf(selection.focusNode, parentElement)) {
          node = selection.focusNode;
          charCount = selection.focusOffset;
          while (node) {
            if (node === parentElement) {
              break;
            }
            if (node.previousSibling) {
              node = node.previousSibling;
              charCount += node.textContent.length;
            } else {
              node = node.parentNode;
              if (node === null) {
                break;
              }
            }
          }
        }
      }
      return charCount;
    }
    _setCurrentCursorPosition(chars, element) {
      if (chars >= 0) {
        var selection = window.getSelection();
        let range = this._createRange(element, {
          count: chars
        });
        if (range) {
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }
    _createRange(node, chars, range) {
      if (!range) {
        range = document.createRange();
        range.selectNode(node);
        range.setStart(node, 0);
      }
      if (chars.count === 0) {
        range.setEnd(node, chars.count);
      } else if (node && chars.count > 0) {
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.textContent.length < chars.count) {
            chars.count -= node.textContent.length;
          } else {
            range.setEnd(node, chars.count);
            chars.count = 0;
          }
        } else {
          for (var lp = 0; lp < node.childNodes.length; lp++) {
            range = this._createRange(node.childNodes[lp], chars, range);
            if (chars.count === 0) {
              break;
            }
          }
        }
      }
      return range;
    }
    _isChildOf(node, parentElement) {
      while (node !== null) {
        if (node === parentElement) {
          return true;
        }
        node = node.parentNode;
      }
      return false;
    }
  }
  customElements.define("wc-input-token", inputTokenElement);

  class inputRange extends HTMLElement {
    constructor() {
      super();
      this._rangeId = generateRandomId();
    }
    connectedCallback() {
      this._init();
    }
    get min() {
      return this.getDecimalAttribute("min", 0);
    }
    set min(value) {
      this.setAttribute("min", value);
    }
    get max() {
      return this.getDecimalAttribute("max", 1);
    }
    set max(value) {
      this.setAttribute("max", value);
    }
    get step() {
      return this.getDecimalAttribute("step", 1);
    }
    set step(value) {
      this.setAttribute("step", value);
    }
    get rangeElement() {
      return this.querySelector("input");
    }
    get counterElement() {
      return this.querySelector(".badge");
    }
    get initialValue() {
      return this.getDecimalAttribute("value", this.min);
    }
    get value() {
      return this.rangeElement.value;
    }
    set value(value) {
      this._setValue(value);
    }
    _init() {
      this._render();
      this._addEvents();
      this._setValue(this.initialValue);
    }
    _render() {
      this.innerHTML = `
            <div class="d-flex">
                <input type="range" class="form-range" min="${this.min}" max="${this.max}" step="${this.step}" id="${this._rangeId}">
                <div class="ms-2 badge badge-count"></div>
            </div>`;
    }
    _addEvents() {
      this.rangeElement.addEventListener("input", () => {
        this.counterElement.innerHTML = this.value;
      });
    }
    _setValue(value) {
      this.rangeElement.value = value;
      this.counterElement.textContent = value;
    }
  }
  customElements.define("wc-input-range", inputRange);

  /**
   * Range de estágios discretos identificados por rótulo textual.
   *
   * O input opera com índices numéricos, mas o valor exposto e emitido é sempre o
   * texto do estágio selecionado. Os rótulos ficam distribuídos de forma equidistante
   * e cada um é centralizado na posição exata que o thumb assume naquele estágio.
   *
   * Os estágios chegam em dois atributos paralelos, na mesma ordem:
   *   values="none,minimal,low,medium,high,xhigh"
   *   labels="Nenhum|Mínimo|Pouco|Médio|Alto|Máximo"
   *
   * Os rótulos usam "|" como separador porque podem conter espaço, o que a lista
   * separada por vírgula de getArrayAttribute não suporta.
   *
   * O valor selecionado é lido e escrito pelo texto:
   *   value="high"
   *
   * O valor do host é o texto do estágio, enquanto o input interno opera com índice
   * numérico. Por isso os eventos nativos do input não propagam: o componente emite o
   * próprio evento "change", com o texto em event.detail.
   */
  class inputRangeLabeledElement extends HTMLElement {
    constructor() {
      super();
      this._rangeId = generateRandomId();
    }
    connectedCallback() {
      this._init();
    }

    /**
     * Valores dos estágios, na ordem de exibição.
     *
     * Precisa de setter, mesmo sendo só leitura por atributo: frameworks como o Vue
     * atribuem "values"/"labels" como PROPRIEDADE do elemento (não como atributo HTML)
     * sempre que a propriedade existe no protótipo — e fazem isso ANTES do elemento
     * entrar no DOM, quando "connectedCallback"/"_render" ainda não rodaram. Sem
     * setter, essa atribuição falha em silêncio (Vue só loga um aviso) e o componente
     * nunca recebe o valor real: fica preso em "min=0 max=0", sem rótulo nenhum.
     */
    get values() {
      return this._values ?? this.getArrayAttribute("values", []);
    }
    set values(value) {
      if (Array.isArray(value)) {
        this._values = value;
      } else if (value === null || value === undefined || value === "") {
        this._values = [];
      } else {
        this._values = String(value).split(/[,;\s]/).map(s => s.trim()).filter(s => s !== "");
      }
      if (this.initialized) this._rebuild();
    }

    /**
     * Rótulos dos estágios. Estágio sem rótulo exibe o próprio valor.
     */
    get labels() {
      if (this._labels != null) return this._labels;
      const labels = this.getStringAttribute("labels", "");
      return labels === "" ? [] : labels.split("|").map(l => l.trim());
    }
    set labels(value) {
      const text = Array.isArray(value) ? value.join("|") : value ?? "";
      this._labels = text === "" ? [] : text.split("|").map(l => l.trim());
      if (this.initialized) this._rebuild();
    }
    get rangeElement() {
      return this.querySelector("input");
    }
    get initialValue() {
      return this._value ?? this.getStringAttribute("value", null);
    }

    /**
     * Texto do estágio selecionado.
     *
     * Antes do "_init" o <input> interno ainda não existe, e "selectedIndex" cairia
     * para 0 — o getter devolveria o primeiro estágio em vez do valor recebido. Nessa
     * janela, responde pelo valor guardado.
     */
    get value() {
      if (!this.initialized) return this.initialValue;
      return this.values[this.selectedIndex] ?? null;
    }
    set value(value) {
      this._value = value;
      // Mesmo motivo do setter de "values": antes do "_render", "rangeElement" é
      // null (o <input> interno ainda não existe), e "_setValue" lançaria ao tentar
      // escrever nele. Guarda o valor e aplica no "_init".
      if (this.initialized) this._setValue(value);
    }
    get selectedIndex() {
      return Number(this.rangeElement?.value ?? 0);
    }
    _init() {
      if (this.initialized) return;
      this._rebuild();
      this.initialized = true;
    }

    /**
     * Monta o DOM, registra os eventos e reaplica o estágio selecionado.
     *
     * Os três passos andam juntos: `_render` reescreve o innerHTML e descarta o <input>
     * anterior junto com os listeners registrados nele, e o input novo nasce sem valor —
     * o browser assumiria o meio da faixa. Chamar só `_render` deixaria o componente
     * mudo e num estágio que ninguém pediu.
     */
    _rebuild() {
      this._render();
      this._addEvents();
      this._setValue(this.initialValue);
    }
    _render() {
      const values = this.values;
      const labels = this.labels;
      const lastIndex = Math.max(values.length - 1, 0);
      const renderedLabels = values.map((value, index) => {
        const position = lastIndex === 0 ? 0 : index / lastIndex;
        const label = labels[index] ?? value;
        return `<span class="range-labeled-label extra-small text-muted" style="--range-labeled-pos: ${position}">${htmlEncode(label) ?? ""}</span>`;
      }).join("");
      this.innerHTML = `
      <div class="range-labeled">
        <input type="range" class="form-range" min="0" max="${lastIndex}" step="1" id="${this._rangeId}">
        <div class="range-labeled-labels">${renderedLabels}</div>
      </div>`;
    }
    _addEvents() {
      // O host é a única origem de evento do componente: o input interno opera com
      // índice e quem consome espera o texto. Os dois eventos nativos do range são
      // interrompidos aqui.
      this.rangeElement.addEventListener("input", event => {
        event.stopPropagation();
        // Guardar o estágio escolhido mantém o host como fonte da verdade: uma troca de
        // "values"/"labels" reconstrói o DOM e precisa reaplicar o que está selecionado,
        // não o valor com que o componente nasceu.
        this._value = this.value;
        this._syncAccessibleText();
        this._emit("change", this._value);
      });

      // O "change" nativo do range dispara ao soltar o mouse e não carrega detail.
      // Se propagasse, sobrescreveria o valor do consumidor com undefined logo
      // depois do arraste ter entregue o valor correto.
      this.rangeElement.addEventListener("change", event => {
        event.stopPropagation();
      });
    }
    _setValue(value) {
      const index = this.values.indexOf(value);
      this.rangeElement.value = index >= 0 ? index : 0;
      this._syncAccessibleText();
    }

    /**
     * Anuncia o rótulo do estágio para leitor de tela.
     *
     * O input carrega um índice, então sem isto o leitor anuncia "2 de 5" — número que
     * não significa nada num controle cujos estágios são identificados por texto.
     */
    _syncAccessibleText() {
      const index = this.selectedIndex;
      this.rangeElement.setAttribute("aria-valuetext", this.labels[index] ?? this.values[index] ?? "");
    }
    _emit(event, data) {
      const customEvent = new CustomEvent(event, {
        detail: data
      });
      this.dispatchEvent(customEvent);
    }
  }
  customElements.define("wc-input-range-labeled", inputRangeLabeledElement);

  class preloaderElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    _init() {
      this._render();
    }
    get color() {
      return this.getStringAttribute("color", "success");
    }
    set color(value) {
      this.setAttribute("color", value);
      if (!this.loader) return;
      this.loader.setAttribute("color", value);
    }
    get container() {
      return this.querySelector(".preloader");
    }
    get loader() {
      return this.querySelector("wc-spinner");
    }
    _render() {
      this.innerHTML = `<div class="preloader">
                            <wc-spinner show="true" color="${this.color}" size="lg"></wc-spinner>
                        </div>`;
      this._addEvents();
    }
    _addEvents() {
      if (!this.container) return;
      this.container.addEventListener("click", () => this.hide());
      document.addEventListener("DOMContentLoaded", () => this._pageLoad());
      window.addEventListener("pagehide", () => this._pageLeave());
    }
    _pageLeave() {
      this.color = "warning";
      this.show();
    }
    _pageLoad() {
      this.hide();
    }
    hide() {
      this.classList.add("d-none");
    }
    show() {
      this.classList.remove("d-none");
    }
  }
  customElements.define("wc-preloader", preloaderElement);

  class rainbowElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.init();
    }
    init() {
      this._render();
    }
    _render() {
      this.innerHTML = `<div class="rainbow-progress-bar">
                            <span class="visually-hidden">Loading...</span>
                        </div>`;
    }
  }
  customElements.define("wc-rainbow", rainbowElement);

  class spinnerElement extends HTMLElement {
    constructor() {
      super();
    }
    static get observedAttributes() {
      return ["color", "show"];
    }
    attributeChangedCallback(name) {
      if (name === "color") {
        return this.changeColor();
      }
    }
    connectedCallback() {
      this.init();
    }
    init() {
      this._render();
    }
    get color() {
      if (this.hasAttribute("color")) return this.getAttribute("color");
      return "";
    }
    get size() {
      if (this.hasAttribute("size")) return this.getAttribute("size");
      return "sm";
    }
    get showAttr() {
      return this.getBooleanAttribute("show", true);
    }
    set showAttr(value) {
      this._showHide(value);
    }
    get colorClass() {
      return this.color ? ` text-${this.color} ` : "";
    }
    get sizeClass() {
      return ` spinner-border-${this.size} `;
    }
    get cssClass() {
      return `spinner-border spinner-border-${this.size} ${this.colorClass}`;
    }
    get container() {
      return this.querySelector("div");
    }
    _render() {
      this.innerHTML = `<div class="${this.cssClass}" role="status">
                            <span class="visually-hidden">Loading...</span>
                          </div>`;
    }
    _showHide(value) {
      if (value) this.show();else this.hide();
    }
    show() {
      this.classList.remove("d-none");
      this.setAttribute("show", true);
    }
    hide() {
      this.classList.add("d-none");
      this.setAttribute("show", false);
    }
    toggle() {
      if (this.showAttr) this.hide();else this.show();
    }
    changeColor() {
      if (!this.container) return;
      this.container.className = this.cssClass;
    }
  }
  customElements.define("wc-spinner", spinnerElement);

  const changeThemeEvent = "changeTheme";
  // ------------------------------------------------------------------------

  const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");
  function isEmbedded() {
    return window.self !== window.top;
  }

  /**
   * Tema declarado pelo autor no <html data-bs-theme="...">, capturado ANTES de
   * qualquer escrita nossa — depois do primeiro setTheme() o atributo passa a
   * refletir o estado, nao a declaracao.
   *
   * "auto" NAO conta como declaracao: e justamente o opt-in para seguir a
   * preferencia do usuario / do sistema.
   */
  let authoredTheme = (() => {
    const value = document.documentElement.getAttribute("data-bs-theme");
    return value === "light" || value === "dark" ? value : null;
  })();
  function setTheme(theme) {
    // Escolha explicita do usuario supera o padrao declarado na pagina.
    authoredTheme = null;
    localStorage.setItem("theme", theme);
    document.documentElement.setAttribute("data-bs-theme", resolveTheme());
    syncThemeColorMeta();
    triggerChangeTheme();
  }
  function triggerChangeTheme() {
    document.dispatchEvent(new CustomEvent(changeThemeEvent, {
      detail: {
        theme: getResolvedTheme(),
        preference: getTheme()
      }
    }));
  }

  /**
   * Preferencia crua: pode ser "auto". Precedencia, da maior para a menor:
   *
   *   1. data-force-theme no <html>          trava, ignora tudo o mais
   *   2. data-bs-theme="light|dark" no HTML  padrao da pagina, ate o usuario trocar
   *   3. localStorage["theme"]               escolha do usuario
   *   4. "auto"                              segue o host (se embutida) ou o SO
   */
  function getTheme() {
    const forcedTheme = document.documentElement.getAttribute("data-force-theme");
    if (forcedTheme) return forcedTheme;
    if (authoredTheme) return authoredTheme;
    const storedTheme = localStorage.getItem("theme");
    if (storedTheme) return storedTheme;
    return "auto";
  }

  /**
   * Traduz "auto" para a aparencia real. Nunca devolve "auto".
   *
   * Dentro de um iframe, "auto" nao segue o SO — segue o host. Existe para
   * telas embutidas num shell que so suporta light: sem isto, "auto" caia
   * direto no SO e a tela ficava escura dentro de um host claro. Ordem:
   *
   *   1. tema resolvido do parent, se acessivel (same-origin) -> espelha o host
   *   2. embutida mas parent sem sinal (cross-origin ou sem data-bs-theme) -> "light"
   *   3. nao embutida -> segue o SO
   */
  function resolveTheme(theme) {
    return "light"; // TRAVA TEMPORARIA (8564) — apague esta linha para restaurar
  }

  /** Aparencia EFETIVA da pagina: sempre "light" ou "dark". */
  function getResolvedTheme() {
    return resolveTheme(getTheme());
  }
  function loadTheme() {
    setTheme(getTheme());
  }

  /**
   * Aparencia efetiva, nao a preferencia.
   *
   * Antes comparava getTheme() === "dark" — com a preferencia "auto" e o SO no
   * escuro isso devolvia false, entao CodeMirror, TinyMCE e AnnounceKit recebiam
   * skin CLARO dentro de uma pagina escura.
   */
  function isDarkTheme() {
    return getResolvedTheme() === "dark";
  }

  /**
   * Mantem <meta name="theme-color"> alinhado com --bs-body-bg do tema ativo.
   *
   * Ignora as tags com `media` (o par prefers-color-scheme light/dark de
   * dock-2026/partials/head.html): aquelas o proprio navegador ja escolhe, e
   * escrever nelas trocaria a cor do esquema errado.
   */
  function syncThemeColorMeta() {
    const meta = document.querySelector('meta[name="theme-color"]:not([media])');
    if (!meta) return;
    const bodyBg = getComputedStyle(document.documentElement).getPropertyValue("--bs-body-bg").trim();
    if (bodyBg) meta.setAttribute("content", bodyBg);
  }

  // Com a preferencia "auto", trocar o tema do SO precisa repintar a pagina.
  darkQuery.addEventListener("change", () => {
    if (getTheme() === "auto") setTheme("auto");
  });

  // Mesma ideia, mas para o host: se a pagina esta embutida e o parent e
  // same-origin, uma troca de tema no host tambem precisa repintar aqui. So
  // funciona se o host tambem dispara changeThemeEvent (mesmo modulo); um
  // host cross-origin lanca no acesso a parent.document e cai no catch, sem
  // efeito — o fallback estatico "light" de resolveTheme ja cobre esse caso.
  if (isEmbedded()) {
    try {
      window.parent.document.addEventListener(changeThemeEvent, () => {
        if (getTheme() === "auto") setTheme("auto");
      });
    } catch {
      // sem acesso ao parent — nada a fazer, fallback estatico ja resolve
    }
  }

  // O <html> pode chegar com data-bs-theme="auto" (dock-2026/partials/head.html),
  // que nao casa NENHUMA regra CSS — sem resolver isso a pagina fica clara mesmo
  // com o SO no escuro. Antes so o <wc-choose-theme> resolvia, entao toda pagina
  // sem esse componente ficava presa no claro.
  //
  // Escrever sempre e seguro: quando a pagina declara "light" ou "dark",
  // getTheme() ja devolve essa declaracao, entao isto reescreve o mesmo valor.
  // Quem sobrescreve uma declaracao explicita e so o data-force-theme.
  document.documentElement.setAttribute("data-bs-theme", getResolvedTheme());
  syncThemeColorMeta();

  class chooseThemeElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      loadTheme();
      this._render();
      this._transferClassAttr();
    }
    get iconSize() {
      return this.getStringAttribute("icon-size", "md");
    }
    get selectableDropdown() {
      return this.querySelector("wc-selectable-dropdown");
    }
    get button() {
      return this.selectableDropdown.querySelector("button");
    }
    _render() {
      const v = getTheme() ?? "auto";
      this.innerHTML = `					
            <wc-selectable-dropdown value="${v}">
                <div class="dropdown">
                    ${this.innerHTML}
                    <ul class="dropdown-menu dropdown-menu-end">
                        <li>
                            <button 
                                type="button" 
                                class="dropdown-item align-items-center"
                                data-value="light" 
                                data-text='<wc-icon icon="sun" size="${this.iconSize}"></wc-icon>'
                                >
                                <wc-icon icon="sun" class="me-1"></wc-icon>
                                ${i18nlocales.t("light")}	
                            </button>
                        </li>
                        <li>
                            <button 
                                type="button" 
                                class="dropdown-item d-flex align-items-center"
                                data-value="dark"
                                data-text='<wc-icon icon="moon" size="${this.iconSize}"></wc-icon>'
                                >
                                <wc-icon icon="moon" class="me-1"></wc-icon>
                                ${i18nlocales.t("dark")}
                            </button>
                        </li>
                        <li>
                            <button 
                                type="button" 
                                class="dropdown-item d-flex align-items-center"
                                data-value="auto"
                                data-text='<wc-icon icon="monitor" size="${this.iconSize}"></wc-icon>'
                                >
                                <wc-icon icon="monitor" class="me-1"></wc-icon>
                                ${i18nlocales.t("auto")}	
                            </button>
                        </li>
                    </ul>
                </div>
            </wc-selectable-dropdown>`;
      this._monitorEvents();
    }
    _transferClassAttr() {
      if (!this.button) return;
      if (!this.className) return;
      this.button.className = `${this.className} ${this.button.className}`;
      this.className = "";
    }
    _monitorEvents() {
      if (!this.selectableDropdown) return;
      this._bindSelectTheme();
    }
    _bindSelectTheme() {
      if (!this.selectableDropdown) return;
      this.selectableDropdown.addEventListener("change", () => {
        setTheme(this.selectableDropdown.value);
      });
    }
  }
  customElements.define("wc-choose-theme", chooseThemeElement);

  class toastContainerElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.init();
    }
    init() {
      this._render();
    }
    static get observedAttributes() {
      return ["placement"];
    }
    attributeChangedCallback(name) {
      if (name === "placement") this.changePlacement();
    }
    get container() {
      return this.querySelector(".toast-container");
    }
    get placement() {
      return this.getStringAttribute("placement", "top-right", placementOptions);
    }
    set placement(value) {
      this.setAttribute("placement", value);
    }
    get fullClass() {
      let className = "toast-container position-fixed p-3";
      switch (this.placement) {
        case "top-right":
          className += " top-0 end-0 ";
          break;
        case "top-left":
          className += " top-0 start-0 ";
          break;
        case "bottom-right":
          className += " bottom-0 end-0 ";
          break;
        case "bottom-left":
          className += " bottom-0 start-0 ";
          break;
        case "top":
          className += " top-0 start-50 translate-middle-x ";
          break;
        case "bottom":
          className += " bottom-0 start-50 translate-middle-x ";
          break;
        default:
          className += " top-0 end-0 ";
          break;
      }
      return className;
    }
    _render() {
      this.innerHTML = `
                <div 
                    class="${this.fullClass}"
                >${this.innerHTML}</div>            
        `;
    }
    changePlacement() {
      if (!this.container) return;
      this.container.className = this.fullClass;
    }
    appendChild(el) {
      this.container.appendChild(el);
    }
  }
  customElements.define("wc-toast-container", toastContainerElement);

  class toastElement extends HTMLElement {
    constructor() {
      super();
      this._bsElement = null;
      this._shouldReRender = false;
    }
    connectedCallback() {
      this.init();
    }
    disconnectedCallback() {
      this._dispose();
    }
    static get observedAttributes() {
      return ["color", "message", "caption", "subtitle", "icon", "timeout", "header"];
    }
    attributeChangedCallback() {
      this._shouldReRender = true;
    }
    init() {
      this._render();
    }

    /*
     * A cor do toast
     */
    get color() {
      return this.getStringAttribute("color", "secondary", ["primary", "secondary", "success", "danger", "warning", "info", "dark", "light"]);
    }
    set color(value) {
      this.setAttribute("color", value);
    }
    get colorClass() {
      if (!this.color) return "";
      return `text-bg-${this.color}`;
    }

    /*
     * A mensagem principal do toast
     */
    get message() {
      return this.getStringAttribute("message", "");
    }
    set message(value) {
      this.setAttribute("message", value);
    }

    /*
     * O título do toast
     */
    get caption() {
      return this.getStringAttribute("caption");
    }
    set caption(value) {
      this.setAttribute("caption", value);
    }

    /*
     * O subtítulo do toast
     */
    get subcaption() {
      return this.getStringAttribute("subcaption", "");
    }
    set subcaption(value) {
      this.setAttribute("subcaption", value);
    }

    /*
     * O ícone do toast
     */
    get icon() {
      return this.getStringAttribute("icon", "");
    }
    set icon(value) {
      this.setAttribute("icon", value);
    }

    /*
     * O tempo, em milisegundos, que o toas aparece em tela
     */
    get timeout() {
      return parseInt(this.getStringAttribute("timeout", "5000"));
    }
    set timeout(value) {
      this.setAttribute("timeout", value);
    }

    /*
     * Se o header deve ser mostrado ou não
     */
    get header() {
      return this.caption || this.subcaption;
    }
    set header(value) {
      this.setAttribute("header", value);
    }
    get closeButton() {
      return `<button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>`;
    }
    get bsElement() {
      return this._bsElement;
    }
    set bsElement(value) {
      this._bsElement = value;
    }
    _render() {
      this._dispose();
      this.innerHTML = `<div 
                                class="toast ${this.colorClass} my-1" 
                                role="alert" 
                                aria-live="assertive" 
                                aria-atomic="true" 
                                data-bs-delay="${this.timeout}">
                            ${this.header ? `<div class="toast-header">
                                    ${this.icon ? `<wc-icon icon="${this.icon}" class="me-2"></wc-icon>` : ""}
                                    <strong class="me-auto text-truncate">${this.caption}</strong>
                                    <small class="text-truncate">${this.subcaption}</small>
                                    <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
                                </div>` : ""}
                            <div class="d-flex">
                                <div class="toast-body">
                                    ${this.message}
                                </div>
                                ${this.header ? "" : `<div data-bs-theme="dark" class="ms-auto align-content-center">
                                    <button type="button" class="btn-close me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
                                    </div>`}
                            </div>
                        </>`;
      const toastElement = this.querySelector(".toast");
      this.bsElement = window.bootstrap.Toast.getOrCreateInstance(toastElement);
    }
    _dispose() {
      if (this.bsElement) this.bsElement.dispose();
    }
    show() {
      if (this._shouldReRender) this._render();
      this.bsElement.show();
    }
    hide() {
      this.bsElement.hide();
    }
  }
  customElements.define("wc-toast", toastElement);

  class popoverElement extends HTMLElement {
    constructor() {
      super();
      this._bsElement = null;
    }
    connectedCallback() {
      this._init();
    }

    /*
     * O título da tooltip
     */
    get caption() {
      return this.getStringAttribute("caption", "");
    }
    set caption(value) {
      this.setAttribute("caption", value);
    }

    /*
     *  O conteudo do popover
     */
    get content() {
      return this.getStringAttribute("content", "");
    }
    set content(value) {
      this.setAttribute("content", value);
    }

    /*
     * O seletor do container onde a popover será inserida no DOM
     */
    get container() {
      return this.getStringAttribute("container", "body");
    }
    set container(value) {
      this.setAttribute("container", value);
    }

    /*
     * Conteúdo HTML da tooltip
     */
    get html() {
      return this.getBooleanAttribute("html", false);
    }
    set html(value) {
      this.setAttribute("html", value);
    }

    /*
     *  A posição da tooltip
     */
    get placement() {
      return this.getStringAttribute("placement", "auto", ["auto", "top", "bottom", "left", "right"]);
    }
    set placement(value) {
      this.setAttribute("placement", value);
    }

    /*
     * Como a popover é acionada
     */
    get trigger() {
      return this.getStringAttribute("trigger", "hover focus");
    }
    set trigger(value) {
      this.setAttribute("trigger", value);
    }

    /*
     * O seletor do elemento que a tooltip vai ser ancorada
     */
    get target() {
      return this.getStringAttribute("target", "");
    }
    get targetElement() {
      return document.querySelector(this.target);
    }
    get bsElement() {
      return this._bsElement;
    }
    set bsElement(value) {
      this._bsElement = value;
    }
    _init() {
      if (!this.target) return;
      if (!this.caption && !this.content) return;
      let target = document.querySelector(this.target);
      if (!target) return;
      this.bsElement = new window.bootstrap.Popover(target, {
        container: this.container,
        title: this.caption,
        content: this.content,
        html: this.html,
        placement: this.placement,
        trigger: this.trigger
      });
    }
    show() {
      this.bsElement.show();
    }
    hide() {
      this.bsElement.hide();
    }
    _emit(event, data) {
      const customEvent = new CustomEvent(event, {
        detail: data
      });
      this.dispatchEvent(customEvent);
    }
  }
  customElements.define("wc-popover", popoverElement);

  class tooltipElement extends HTMLElement {
    constructor() {
      super();
      this._bsElement = null;
      this._regexTargetIdSelector = /^#\d+/;
    }
    connectedCallback() {
      this._init();
    }
    disconnectedCallback() {
      this._bsElement?.dispose();
      this._bsElement = null;
    }
    static get observedAttributes() {
      return ["content", "disabled"];
    }
    attributeChangedCallback(name) {
      if (name === "disabled") {
        this._toggleDisabled();
      } else if (name === "content") {
        this.bsElement?.setContent({
          ".tooltip-inner": this._htmlContent
        });
      }
    }
    getTargetElement(target) {
      if (!target) return null;
      if (this._regexTargetIdSelector.test(target)) return document.querySelector(`#${CSS.escape(target.slice(1))}`);
      return document.querySelector(target);
    }

    /*
     * O título da tooltip
     */
    get caption() {
      return this.getStringAttribute("caption", "");
    }
    set caption(value) {
      this.setAttribute("caption", value);
    }

    /*
     * O conteudo da tooltip
     */
    get content() {
      return this.getStringAttribute("content", "");
    }
    set content(value) {
      this.setAttribute("content", value);
    }
    get disabled() {
      return this.getBooleanAttribute("disabled", false);
    }
    set disabled(value) {
      this.setAttribute("disabled", value);
    }

    /*
     * Se a tooltip deve ser desabilitada no mobile. Default: true
     */
    get mobileDisabled() {
      return this.getBooleanAttribute("mobile-disabled", true);
    }
    set mobileDisabled(value) {
      this.setAttribute("mobile-disabled", value);
    }

    /*
     * Como a tooltip é acionada
     */
    get trigger() {
      return this.getStringAttribute("trigger", "hover focus");
    }
    set trigger(value) {
      this.setAttribute("trigger", value);
    }

    /*
     * O conteudo da tooltip
     */
    get _htmlContent() {
      return `${this.caption ? `<div class="tooltip-caption">${this.caption}</div>` : ""} ${this.content}`;
    }

    /*
     * Tooltip permite HTML
     */
    get _html() {
      return true;
    }

    /*
     *  A posição da tooltip
     */
    get placement() {
      return this.getStringAttribute("placement", "auto", ["auto", "top", "bottom", "left", "right"]);
    }
    set placement(value) {
      this.setAttribute("placement", value);
    }

    /*
     * O elemento que a tooltip vai ser ancorada
     */
    get target() {
      return this.getStringAttribute("target", "");
    }
    set target(value) {
      this.setAttribute("target", value);
    }
    get bsElement() {
      return this._bsElement;
    }
    set bsElement(value) {
      this._bsElement = value;
    }
    _init() {
      if (!this.target) return;
      if (!this.caption && !this.content) return;
      let target = this.getTargetElement(this.target);
      if (!target) return;

      //if (this.mobileDisabled && isMobile) return;

      this.bsElement = new window.bootstrap.Tooltip(target, {
        title: this._htmlContent,
        html: this._html,
        placement: this.placement,
        trigger: this.trigger
      });

      // Fechar por clique só faz sentido onde existe hover: com mouse a tooltip abre no
      // mouseover e o clique no alvo (um botão, um link) a dispensa. Em touch não há hover -
      // o iOS simula o mouseover no tap (é para isso que o Bootstrap registra listeners noop
      // nos filhos do body ao exibir) e o click do MESMO gesto chegava logo em seguida,
      // fechando a tooltip que tinha acabado de abrir: ela só piscava na tela. Sem este
      // listener, em touch quem fecha é o próprio Bootstrap, no toque fora do alvo.
      if (window.matchMedia("(hover: hover)").matches) {
        target.addEventListener("click", () => {
          if (!this.bsElement) return;
          this.bsElement.hide();
        });
      }
      this._toggleDisabled();
    }
    _toggleDisabled() {
      if (!this.bsElement) return;
      if (this.disabled) {
        this.bsElement.disable();
      } else {
        this.bsElement.enable();
      }
    }
  }
  customElements.define("wc-tooltip", tooltipElement);

  class visualEditorElement extends HTMLElement {
    constructor() {
      super();
      this._quill = null;
      this._id = generateRandomId();
      this._value = null;
    }
    async connectedCallback() {
      await this._init();
    }

    /*
     * Instância do Quill estática global
     */
    get globalQuill() {
      return QuillBuilder.Quill;
    }

    /*
     * Instância do editor vinculada a esse webcomponent
     */
    get quillEditor() {
      return this._quill;
    }
    set quillEditor(value) {
      this._quill = value;
    }

    /*
     * Todas opções da toolbar disponíveis
     */
    get avalaibleToolbarConfig() {
      return [{
        uid: "styles",
        config: {
          header: [1, 2, 3, 4, 5, 6, false]
        }
      }, {
        uid: "backcolor",
        config: {
          background: []
        }
      }, {
        uid: "forecolor",
        config: {
          color: []
        }
      }, {
        uid: "bold",
        config: "bold"
      }, {
        uid: "italic",
        config: "italic"
      }, {
        uid: "underline",
        config: "underline"
      }, {
        uid: "strike",
        config: "strike"
      }, {
        uid: "align",
        config: {
          align: []
        }
      }, {
        uid: "numlist",
        config: {
          list: "ordered"
        }
      }, {
        uid: "bullist",
        config: {
          list: "bullet"
        }
      }, {
        uid: "increaseindent",
        config: {
          indent: "-1"
        }
      }, {
        uid: "decreaseindent",
        config: {
          indent: "+1"
        }
      }, {
        uid: "blockquote",
        config: "blockquote"
      }, {
        uid: "link",
        config: "link"
      }, {
        uid: "image",
        config: "image"
      }, {
        uid: "media",
        config: "video"
      }, {
        uid: "removeformat",
        config: "clean"
      }];
    }

    /*
     * Valor do editor em HTML
     */
    get value() {
      return this._getEditorHTML();
    }
    set value(content) {
      this._value = content;
    }

    /*
     * O conteudo do editor em formato delta, proprietário do Quill
     */
    get delta() {
      return this.quillEditor.getContents();
    }
    set delta(content) {
      this.quillEditor.setContents(content);
    }

    /*
     * Altura do editor
     */
    get height() {
      return this.getStringAttribute("height", "");
    }
    set height(value) {
      this.setAttribute("height", value);
    }

    /*
     * Se o editor é singleline
     */
    get singleline() {
      return this.getBooleanAttribute("singleline", false);
    }
    set singleline(value) {
      this.setAttribute("singleline", value);
    }

    /*
     * Posição da toolbar: top, bottom
     */
    get position() {
      return this.getStringAttribute("position", "top", ["top", "bottom"]);
    }
    set position(value) {
      this.setAttribute("position", value);
    }

    /*
     * Texto de placeholder do editor
     */
    get placeholder() {
      return this.getStringAttribute("placeholder", "");
    }
    set placeholder(value) {
      this.setAttribute("placeholder", value);
    }

    /*
     * Toolbar options: full, basic, none
     */
    get toolbar() {
      return this.getStringAttribute("toolbar", "full");
    }
    set toolbar(value) {
      this.setAttribute("toolbar", value);
    }

    /*
     * Propriedade para passar classes CSS adicionais
     * Exemplo de uso no HTML:
     * <wc-visual-editor custom-classes="minha-classe outra-classe"></wc-visual-editor>
     */
    get customClasses() {
      return this.getStringAttribute("custom-classes", "");
    }
    set customClasses(value) {
      this.setAttribute("custom-classes", value);
    }
    async _init() {
      this._setStyle();
      this._addContainer();
      await loadQuill();
      this._addModules();
      this._reconfigureModules();
      this._configure();
      this._addTooltips();
      this._monitorEvents();
      this.setValue(this._value);
    }

    /*
     * Adiciona classes especiais ao componente conforme definições
     */
    _setStyle() {
      this.classList.add("quill-container");
      if (this.singleline) this.classList.add("quill-singleline");
      if (this.toolbar === "none") this.classList.add("quill-no-toolbar");
      if (this.position === "bottom") this.classList.add("quill-bottom");

      // Adiciona classes CSS customizadas passadas via propriedade "custom-classes"
      const customClasses = this.customClasses.trim();
      if (customClasses) {
        customClasses.split(/\s+/).forEach(cls => {
          if (cls) this.classList.add(cls);
        });
      }
    }

    /*
     * Adiciona o container do editor, onde ele vai existir
     */
    _addContainer() {
      this.insertAdjacentHTML("afterbegin", `<div style="${this.height ? `height:${this.height}` : ""}" id="${this._id}"></div>`);
    }
    _addModules() {}

    /*
     * Adiciona um módulo customizado ao editor
     */
    _addModule(name, module) {
      const exists = this.globalQuill.imports[name];
      if (exists) return;
      this.globalQuill.register(name, module);
    }

    /*
     * Reconfigura módulos existentes
     */
    _reconfigureModules() {
      this._reconfigureBoldModule();
    }

    /*
     * Reconfigura a tag de negrito de strong para b para diminuir espaço
     */
    _reconfigureBoldModule() {
      const boldModule = this.globalQuill.import("formats/bold");
      boldModule.tagName = "b";
      this.globalQuill.register(boldModule, true);
    }
    _configure() {
      this.quillEditor = new this.globalQuill(`#${this._id}`, this._getEditorConfiguration());
    }

    /*
     * Configurações do editor
     */
    _getEditorConfiguration() {
      const config = {};
      config.modules = {
        toolbar: this._buildToolbarConfiguration(),
        keyboard: {
          bindings: this._buildKeyboardBindinds()
        }
      };
      if (this.placeholder) {
        config.placeholder = this.placeholder;
      }
      config.theme = "snow";
      return config;
    }

    /*
     * Gerenciar ações do teclado especiais
     */
    _buildKeyboardBindinds() {
      const bindings = {};
      this._handleKeyboardEnter(bindings);
      return bindings;
    }

    /*
     * Se o componente é singleline, desabilita a ação do enter
     */
    _handleKeyboardEnter(bindinds) {
      if (!this.singleline) return;
      bindinds.enter = {
        key: "Enter",
        handler: function () {
          return false;
        }
      };
    }

    /*
     * Monta a configuração da toolbar
     */
    _buildToolbarConfiguration() {
      if (this.toolbar === "none") return false;
      const toolbarElements = this._getToolbarElements();
      const toolbarConfiguration = [];
      toolbarElements.forEach(toolbarElementUid => {
        this.avalaibleToolbarConfig.find(option => {
          if (option.uid === toolbarElementUid) {
            toolbarConfiguration.push(option.config);
          }
        });
      });
      return toolbarConfiguration;
    }

    /*
     * Retorna os elementos da toolbar em formato de array, conforme definido pelo dev
     */
    _getToolbarElements() {
      if (this.toolbar === "full") return [...this.avalaibleToolbarConfig.map(option => option.uid)];
      if (this.toolbar === "basic") return ["bold", "italic", "underline", "blockquote", "clean"];
      return [...this.toolbar.split(" ").map(item => item.trim())];
    }

    /*
     * Adiciona tooltips aos botões da toolbar
     */
    _addTooltips() {
      this._addTooltip(".ql-header", i18nlocales.t("editor.header"));
      this._addTooltip(".ql-bold", i18nlocales.t("editor.bold"));
      this._addTooltip(".ql-italic", i18nlocales.t("editor.italic"));
      this._addTooltip(".ql-underline", i18nlocales.t("editor.underline"));
      this._addTooltip(".ql-strike", i18nlocales.t("editor.strike"));
      this._addTooltip(".ql-blockquote", i18nlocales.t("editor.blockquote"));
      this._addTooltip(".ql-code-block", i18nlocales.t("editor.code"));
      this.querySelectorAll(".ql-list").forEach(list => this._addTooltip(list, i18nlocales.t("editor.list")));
      this.querySelectorAll(".ql-indent").forEach(indent => this._addTooltip(indent, i18nlocales.t("editor.indent")));
      this._addTooltip(".ql-color", i18nlocales.t("editor.color"));
      this._addTooltip(".ql-background", i18nlocales.t("editor.background"));
      this._addTooltip(".ql-align", i18nlocales.t("editor.align"));
      this._addTooltip(".ql-link", i18nlocales.t("editor.link"));
      this._addTooltip(".ql-image", i18nlocales.t("editor.image"));
      this._addTooltip(".ql-video", i18nlocales.t("editor.video"));
      this._addTooltip(".ql-clean", i18nlocales.t("editor.clean"));
    }

    /*
     * Adiciona tooltip em um elemento
     */
    _addTooltip(selectorOrElement, tip) {
      if (typeof selectorOrElement === "string") selectorOrElement = this.querySelector(selectorOrElement);
      selectorOrElement?.setAttribute("title", tip);
    }

    /*
     * Monitora eventos do editor
     */
    _monitorEvents() {
      this._monitorChangeEvent();
    }

    /*
     * Monitora evento de mudança de texto
     */
    _monitorChangeEvent() {
      this.quillEditor.on("text-change", (delta, oldDelta, source) => {
        this.dispatchEvent(new CustomEvent("change", {
          detail: {
            delta,
            oldDelta,
            source
          }
        }));
        this.dispatchEvent(new CustomEvent("input", {
          detail: {
            delta,
            oldDelta,
            source
          }
        }));
      });
    }

    /*
     * Transforma o conteudo do editor em HTML. Utiliza uma biblioteca que converte o delta do Quill para HTML
     * Essa biblioteca é mais otimizada que quill.root.innerHTML, pois tira os lixos HTML que o Quill gera.
     */
    _getEditorHTML() {
      const converter = new QuillBuilder.QuillDeltaToHtml.QuillDeltaToHtmlConverter(this.quillEditor.getContents().ops, {
        customTag: op => {
          return this._defineHTMLTag(op);
        }
      });
      return converter.convert();
    }

    /*
     * Define as tags que serão geradas para determinados formatos do Quill
     */
    _defineHTMLTag(deltaAttribute) {
      if (deltaAttribute === "bold") return "b";
    }
    focus() {
      this.quillEditor.focus();
    }
    addToken(token) {
      this.quillEditor.insertText(this.quillEditor.getSelection().index, token);
    }
    setValue(value) {
      this.quillEditor.root.innerHTML = value;
    }
  }
  customElements.define("wc-visual-editor", visualEditorElement);

  class advancedVisualEditorElement extends HTMLElement {
    constructor() {
      super();
      this._editor = null;
      this._value = null;
      this._isInitialized = false;
    }
    async connectedCallback() {
      await this._init();
    }
    async _init() {
      this._showSpinner();
      this._checkForId();
      this._addContainer();
      await loadTinyMce();
      await this._configure();
      this._setEditorContentTheme();
      this._monitorTheme();
      this.setValue(this._value);
      this.setInitialized(true);
    }
    get id() {
      return this.getStringAttribute("id");
    }
    set id(value) {
      this.setAttribute("id", value);
    }
    get height() {
      return this.getIntegerAttribute("height", 300);
    }
    set height(value) {
      this.setAttribute("height", value);
    }
    get code() {
      return this.getBooleanAttribute("code", false);
    }
    set code(value) {
      this.setAttribute("code", value);
    }
    get containerId() {
      return `${this.id}container`;
    }
    get outerContainer() {
      return this.querySelector(`#outer_${this.containerId}`);
    }
    get container() {
      return this.querySelector(`#${this.containerId}`);
    }
    get spinner() {
      return this.querySelector(".placeholder-glow");
    }
    get value() {
      this._value = this.getValue();
      return this._value;
    }

    /*
     * Na inicialização do componente o valor é somente armazenado.
     * Ele só é definido no método init que chama setValue;
     */
    set value(newValue) {
      this._value = newValue;
    }
    get defaultToolbar() {
      return `fullscreen h1 h2 h3 backcolor forecolor bold italic underline alignleft aligncenter alignright alignjustify numlist  bullist blockquote link unlink image media table removeformat emoticons${this.code ? " | code " : ""}`;
    }
    get toolbar() {
      return this.getStringAttribute("toolbar", this.defaultToolbar);
    }
    set toolbar(value) {
      this.setAttribute("toolbar", value);
    }
    get exclude() {
      return this.getStringAttribute("exclude", "");
    }
    set exclude(value) {
      this.setAttribute("exclude", value);
    }
    get defaultPlugins() {
      return "code table advlist link image lists fullscreen autolink preview media emoticons";
    }
    get plugins() {
      return this.getStringAttribute("plugins", this.defaultPlugins);
    }
    set plugins(value) {
      this.setAttribute("plugins", value);
    }
    get skin() {
      return isDarkTheme() ? "oxide-dark" : "oxide";
    }
    get theme() {
      return isDarkTheme() ? "dark" : "light";
    }
    get contentCss() {
      return getZeevBootstrapPath("assets/css/tinymce-theme.css");
    }
    get languageCode() {
      const lng = i18nlocales.lng();
      if (lng === "pt") return "pt_BR";
      if (lng === "es") return "es";
      return "";
    }
    get editor() {
      return this._editor[0];
    }
    set editor(value) {
      this._editor = value;
    }
    get editorContentHtmlTag() {
      if (!this.editor) return null;
      return this.editor.dom.doc.querySelector("html");
    }
    get configCustom() {
      return this.getJsonAttribute("config");
    }
    _showSpinner() {
      this.innerHTML = `<div class="placeholder-glow">
        <span class="placeholder placeholder-lg col-12" style="height:${this.height}px"></span>
      </div>`;
    }
    _hideSpinner() {
      if (!this.spinner) return;
      this.spinner.remove();
    }
    _checkForId() {
      if (this.id) return;
      this.id = `editor-${generateRandomId()}`;
    }
    _addContainer() {
      this.insertAdjacentHTML("afterbegin", `<div id="outer_${this.containerId}" class="d-none" >
        <div id="${this.containerId}"></div>
      </div>`);
    }
    _getDefaultConfiguration() {
      return {
        selector: `#${this.containerId}`,
        toolbar_sticky: false,
        height: this.height,
        max_height: this.height,
        resize: false,
        elementpath: false,
        statusbar: false,
        inline: false,
        promotion: false,
        branding: false,
        plugins: this.plugins,
        toolbar: this._getToolbarItems(),
        images_upload_base64: true,
        menubar: "",
        skin: this.skin,
        content_css: this.contentCss,
        language: this.languageCode,
        toolbar_mode: "scrolling",
        toolbar_location: "top",
        setup: editor => {
          editor.on("init", () => {
            this.outerContainer.classList.remove("d-none");
            this._hideSpinner();
          }), editor.on("Change", () => {
            this.dispatchEvent(new Event("change"));
            //Isso é necessário pois a formatação do texto via toolbar lança o evento change
            //porém, para uso do editor no Vue, o Vue mapeia o evento input
            this.dispatchEvent(new Event("input"));
          });
          editor.on("input", () => {
            this.dispatchEvent(new Event("input"));
          });
          editor.on("click", () => {
            //Isso é necessário para que dropdowns abertos sejam fechados quando é clicado neste componente.
            this.click();
          });
        }
      };
    }
    _getConfig() {
      return _objectSpread2(_objectSpread2({}, this._getDefaultConfiguration()), this.configCustom);
    }
    async _configure() {
      this.editor = await window.tinymce.init(this._getConfig());
    }
    _monitorTheme() {
      document.addEventListener(changeThemeEvent, () => {
        this._setEditorContentTheme();
      });
    }
    _setEditorContentTheme() {
      this.editorContentHtmlTag?.setAttribute("data-bs-theme", this.theme);
    }
    _getToolbarItems() {
      if (this.exclude.length == 0 || this.toolbar.length == 0) return this.toolbar;
      let toolbarItems = this.toolbar.split(" ");
      let excludeItems = this.exclude.split(" ");
      return toolbarItems.filter(item => {
        return !excludeItems.includes(item);
      }).join(" ");
    }
    addToken(token) {
      this.editor.insertContent(token);
    }
    getValue() {
      //Caso o tinymce não tenha carregado, obtém o valor this._value.
      if (!this._isInitialized) return this._value;
      return window.tinymce?.get(this.containerId)?.getContent();
    }
    setValue(value) {
      window.tinymce.get(this.containerId)?.setContent(value ?? "");
    }
    setInitialized(value) {
      this._isInitialized = value;
    }
  }
  customElements.define("wc-advanced-visual-editor", advancedVisualEditorElement);

  class avatarElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    get photo() {
      return this.getStringAttribute("photo");
    }
    get icon() {
      return this.getStringAttribute("icon", "");
    }
    set icon(value) {
      this.setAttribute("icon", value);
    }
    set photo(value) {
      this.setAttribute("photo", value);
    }
    get name() {
      return this.getStringAttribute("name");
    }
    set name(value) {
      this.setAttribute("name", value);
    }
    get userid() {
      return this.getStringAttribute("userid");
    }
    set userid(value) {
      this.setAttribute("userid", value);
    }
    get size() {
      return this.getStringAttribute("size", "md", ["xs", "sm", "md", "lg", "xl"]);
    }
    set size(value) {
      this.setAttribute("size", value);
    }
    get sizeClass() {
      return `avatar-${this.size}`;
    }
    get colors() {
      return ["primary", "secondary", "success", "danger", "warning", "info"];
    }
    get color() {
      return this.getStringAttribute("color", this._getColorForSeed(this.userid || this.name));
    }
    get colorClass() {
      if (this.photo) return "";
      return `text-bg-${this.color}-subtle`;
    }
    _init() {
      this._render();
    }
    _render() {
      this.insertAdjacentHTML("afterbegin", `
            <div 
                class="avatar ${this.sizeClass} ${this.colorClass}"
                title="${this.name}"	
                style="${this._getBackground()}"
            >
            <div class="bold">${this._getContent()}</div>
        </div>
        `);
    }
    _getContent() {
      if (this.photo) return "&nbsp;";
      if (this.icon) return `<wc-icon icon="${this.icon}"></wc-icon>`;
      if (this.name) return this._getInitials(this.name);
    }
    _getInitials() {
      if (this.name.length <= 3) return this.name.toUpperCase();
      const words = this.name.trim().split(/\s+/);
      const firstName = words[0];

      // So um nome (sem sobrenome): words[words.length - 1] cairia no proprio firstName e
      // duplicaria a letra (ex.: "Renata" -> "RR"). Usa so a inicial do nome nesse caso.
      if (words.length === 1) return firstName[0].toUpperCase();
      const lastName = words[words.length - 1];
      const initials = firstName[0].toUpperCase() + lastName[0].toUpperCase();
      return initials;
    }
    _getBackground() {
      if (this.photo) return `background-image: url(${this.photo})`;
      return "";
    }

    // userid tem prioridade sobre name como seed: nomes iguais (pessoas distintas) devem
    // cair em cores diferentes quando o identificador unico estiver disponivel. Sem seed
    // (nenhum dos dois atributos setado, ex.: avatar so com icone) mantem o sorteio antigo,
    // ja que nao ha identidade nenhuma pra ser consistente entre renders.
    _getColorForSeed(seed) {
      if (!seed) return this.colors[Math.floor(Math.random() * this.colors.length)];
      return this.colors[this._hashSeed(seed) % this.colors.length];
    }

    // djb2 - hash simples e deterministico, so precisa distribuir bem entre poucos buckets
    // (colors.length), nao precisa resistir a colisao criptografica.
    _hashSeed(seed) {
      let hash = 5381;
      for (let i = 0; i < seed.length; i++) {
        hash = (hash << 5) + hash + seed.charCodeAt(i) >>> 0;
      }
      return hash;
    }
  }
  customElements.define("wc-avatar", avatarElement);

  class avatarGroupElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.classList.add("avatar-group");
    }
  }
  customElements.define("wc-avatar-group", avatarGroupElement);

  class userElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    get photo() {
      return this.getStringAttribute("photo");
    }
    set photo(value) {
      this.setAttribute("photo", value);
    }
    get name() {
      return this.getStringAttribute("name");
    }
    set name(value) {
      this.setAttribute("name", value);
    }
    get complement() {
      return this.getStringAttribute("complement");
    }
    set complement(value) {
      this.setAttribute("complement", value);
    }
    get userid() {
      return this.getStringAttribute("userid");
    }
    set userid(value) {
      this.setAttribute("userid", value);
    }
    get size() {
      return this.getStringAttribute("size", "md", ["xs", "sm", "md", "lg", "xl"]);
    }
    set size(value) {
      this.setAttribute("size", value);
    }
    get sizeClass() {
      return `user-${this.size}`;
    }
    _init() {
      this._render();
    }
    _render() {
      this.innerHTML = `<div class="user ${this.sizeClass}">
                            <wc-avatar photo="${this.photo}" name="${this.name}" userid="${this.userid}" size="${this.size}"></wc-avatar>
                            <div class="user-detail">
                                <div class="user-name text-reset">${this.name}</div>
                                <span class="user-complement">${this.complement}</span>
                            </div>
                          </div>`;
    }
  }
  customElements.define("wc-user", userElement);

  class userBadgeElement extends HTMLElement {
    constructor() {
      super();
      this._showCard = this._showCard.bind(this);
      this._hideCard = this._hideCard.bind(this);
    }
    connectedCallback() {
      this._init();
    }
    disconnectedCallback() {
      const link = this.querySelector(".user-badge-link");
      if (link) {
        link.removeEventListener("mouseenter", this._showCard);
        link.removeEventListener("mouseleave", this._hideCard);
      }
    }
    get name() {
      return this.getStringAttribute("name", "");
    }
    set name(value) {
      this.setAttribute("name", value);
    }
    get photo() {
      return this.getStringAttribute("photo", "");
    }
    set photo(value) {
      this.setAttribute("photo", value);
    }
    get email() {
      return this.getStringAttribute("email", "");
    }
    set email(value) {
      this.setAttribute("email", value);
    }
    get role() {
      return this.getStringAttribute("role", "");
    }
    set role(value) {
      this.setAttribute("role", value);
    }
    get department() {
      return this.getStringAttribute("department", "");
    }
    set department(value) {
      this.setAttribute("department", value);
    }
    _init() {
      this._render();
      this._bindEvents();
    }
    _render() {
      const roleDept = [this.role, this.department].filter(Boolean);
      this.innerHTML = `
      <span class="user-badge">
        <a href="#" class="user-badge-link text-decoration-none">${this.name}</a>
        <div class="user-badge-card">
          <div class="user-badge-card-header">
            <wc-avatar
              photo="${this.photo}"
              name="${this.name}"
              size="md"
            ></wc-avatar>
            <div class="user-badge-card-name">${this.name}</div>
          </div>
          ${this.email || roleDept.length ? `
          <div class="user-badge-card-details">
            ${this.email ? `<div class="fs-sm text-muted">${this.email}</div>` : ""}
            ${roleDept.length ? `
            <div class="d-flex gap-1">
              ${roleDept.map(v => `<div class="fs-sm text-muted">${v}</div>`).join('<div class="vr"></div>')}
            </div>` : ""}
          </div>` : ""}
        </div>
      </span>`;
    }
    _bindEvents() {
      const link = this.querySelector(".user-badge-link");
      const card = this.querySelector(".user-badge-card");
      if (!link || !card) return;
      link.addEventListener("mouseenter", this._showCard);
      link.addEventListener("mouseleave", this._hideCard);
      card.addEventListener("mouseenter", this._showCard);
      card.addEventListener("mouseleave", this._hideCard);
    }
    _showCard() {
      const card = this.querySelector(".user-badge-card");
      if (!card) return;
      card.classList.remove("flip-x", "flip-y");
      card.classList.add("show");
      const rect = card.getBoundingClientRect();
      if (rect.right > window.innerWidth) card.classList.add("flip-x");
      if (rect.bottom > window.innerHeight) card.classList.add("flip-y");
    }
    _hideCard() {
      const card = this.querySelector(".user-badge-card");
      if (!card) return;
      card.classList.remove("show", "flip-x", "flip-y");
    }
  }
  customElements.define("wc-user-badge", userBadgeElement);

  class pinElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    static get observedAttributes() {
      return ["color", "placement", "size", "border", "animation"];
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== newValue && this.initialized) {
        this._render();
      }
    }

    /*
     * A posição da dica
     */
    get placement() {
      return this.getStringAttribute("placement", "", placementOptions);
    }
    set placement(value) {
      this.setAttribute("placement", value);
    }
    get size() {
      return this.getStringAttribute("size", "sm", ["sm", "md", "lg", "xl"]);
    }
    set size(value) {
      this.setAttribute("size", value);
    }
    get color() {
      return this.getStringAttribute("color", "", ["primary", "secondary", "success", "danger", "warning", "info"]);
    }
    set color(value) {
      this.setAttribute("color", value);
    }
    get border() {
      return this.getBooleanAttribute("border", false);
    }
    get animation() {
      return this.getStringAttribute("animation", "", ["pulse", "blink"]);
    }
    set animation(value) {
      this.setAttribute("animation", value);
    }
    set border(value) {
      this.setAttribute("border", value);
    }
    get colorClass() {
      if (!this.color) return "";
      return `text-bg-${this.color}`;
    }
    get sizeClass() {
      if (!this.size) return "";
      return `pin-${this.size}`;
    }
    get borderClass() {
      if (!this.border) return "";
      return `pin-border`;
    }
    get animationClass() {
      if (!this.animation) return "";
      return `pin-${this.animation}`;
    }
    get placementClass() {
      if (!this.placement) return "";
      return `position-absolute ${getPlacementClass(this.placement)}`;
    }
    _init() {
      if (this.initialized) return;
      this._originalContent = this.innerHTML;
      this._render();
      this._setParentPositionRelative();
      this.initialized = true;
    }
    _render() {
      this.innerHTML = `<span class="pin ${this.sizeClass} ${this.borderClass} ${this.animationClass} ${this.placementClass} ${this.colorClass}">
        ${this._originalContent}
        <span class="visually-hidden"></span>
      </span>`;
    }
    _setParentPositionRelative() {
      this.parentElement.style.position = "relative";
    }
  }
  customElements.define("wc-pin", pinElement);

  class announceKitElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    get userEmail() {
      return this.getStringAttribute("userEmail", "");
    }
    get userFirstName() {
      return this.getStringAttribute("userFirstName", "");
    }
    get userPersona() {
      return this.getStringAttribute("userPersona", "");
    }
    get baseUrl() {
      return this.getStringAttribute("baseUrl", "");
    }
    get productName() {
      return this.getStringAttribute("productName", "");
    }
    get data() {
      return {
        user: {
          id: this.userId,
          email: this.userEmail,
          name: this._removeAccentsAndSpecialCharacters(this.userEmail)
        },
        data: {
          user_persona: this.userPersona,
          base_url: this.baseUrl,
          product_name: this.productName
        }
      };
    }
    get widgets() {
      return [...this.querySelectorAll("wc-announcekit-widget")];
    }
    _init() {
      this._loadExternalPlugin();
      this._loadWidgets();
      this._monitorChangeTheme();
    }
    _monitorChangeTheme() {
      document.addEventListener(changeThemeEvent, () => {
        this._loadWidgets();
      });
    }
    _loadWidgets() {
      const filteredWidgets = this.widgets.filter(widget => widget.getAttribute("theme") === (isDarkTheme() ? "dark" : "light"));
      filteredWidgets.forEach(widget => {
        this._triggerAnnounceKit(widget.getAttribute("uid"), widget.getAttribute("target"));
      });
    }
    _triggerAnnounceKit(uid, target = "") {
      if (window.announcekit && window.announcekit[`widget$${uid}`]) return;
      const badge = target ? {
        selector: target,
        badge: {
          style: {
            position: "absolute",
            top: "0px"
          }
        }
      } : {};
      window.announcekit = window.announcekit || {
        queue: [],
        on: function (n, x) {
          window.announcekit.queue.push([n, x]);
        },
        push: function (x) {
          window.announcekit.queue.push(x);
        }
      };
      window.announcekit.push(_objectSpread2(_objectSpread2({
        widget: `https://announcekit.app/widgets/v2/${uid}`,
        name: uid,
        lang: "pt"
      }, badge), this.data));
    }
    _loadExternalPlugin() {
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://cdn.announcekit.app/widget-v2.js";
      document.head.appendChild(script);
    }
    _removeAccentsAndSpecialCharacters(text) {
      return text.normalize("NFD").replace(/[^\w\s]/gi, "");
    }
    show(uid) {
      window.announcekit[`widget$${uid}`].open();
    }
  }
  customElements.define("wc-announcekit", announceKitElement);

  class announceKitWidgetElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {}
    get uid() {
      return this.getStringAttribute("uid", "");
    }
    set uid(value) {
      this.setAttribute("uid", value);
    }
    get theme() {
      return this.getStringAttribute("theme", "light");
    }
    set theme(value) {
      this.setAttribute("theme", value);
    }
  }
  customElements.define("wc-announcekit-widget", announceKitWidgetElement);

  class collapseElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    get id() {
      return this.getStringAttribute("id", "");
    }
    get storageKey() {
      return `collapse-${this.id}`;
    }

    /*
     * A instância é SEMPRE criada com { toggle: false }.
     * Sem isso o Bootstrap usa Default.toggle === true e o construtor chama
     * this.toggle() (node_modules/bootstrap/js/src/collapse.js:84-86). Como o
     * elemento pode nascer sem `.show`, o toggle virava show(): _isTransitioning
     * passava a true, o hide() seguinte era engolido pelo guard da linha 167 e o
     * shown.bs.collapse regravava "show" no storage — a preferência do usuário
     * ficava invertida a cada load.
     * O data-api de click do Bootstrap (collapse.js:287) também usa
     * { toggle: false } e getOrCreateInstance reaproveita esta mesma instância.
     */
    get collapsible() {
      return window.bootstrap.Collapse.getOrCreateInstance(this, {
        toggle: false
      });
    }

    /*
     * `wc-collapse` também é usado como simples invólucro de um gatilho + painel
     * externo (src/apps/properties/partials/event-message/basico.html). Nesses
     * casos o próprio elemento não é um collapse e não tem estado a salvar.
     */
    get isCollapseTarget() {
      return this.classList.contains("collapse") || this.classList.contains("collapsing");
    }
    _init() {
      if (!this.id) {
        console.error("collapseElement: id is required");
        return;
      }
      this._restoreState();
      this._monitorEvents();
    }

    /*
     * Restaura a preferência nos DOIS sentidos e SEM animação: a classe `.show` é
     * manipulada direto e só então o Bootstrap é instanciado.
     * Sem valor salvo, o estado escrito no HTML é a verdade e NADA é gravado — a
     * chave só existe depois de uma ação explícita do usuário.
     */
    _restoreState() {
      if (!this.isCollapseTarget) return;
      if (hasStorage(this.storageKey)) {
        this.classList.toggle("show", getStorage(this.storageKey) === "show");
      }
      this._syncTriggers();
    }

    /*
     * Instanciar com { toggle: false } faz o Bootstrap alinhar aria-expanded e a
     * classe `.collapsed` de todos os gatilhos com o estado atual
     * (_addAriaAndCollapsedClass, collapse.js:80-82) sem disparar transição.
     */
    _syncTriggers() {
      void this.collapsible;
    }
    _monitorEvents() {
      // Os eventos do Bootstrap borbulham: submenus internos passam por aqui.
      this.addEventListener("hidden.bs.collapse", event => {
        if (event.target !== this) return;
        setStorage(this.storageKey, "hide");
      });
      this.addEventListener("shown.bs.collapse", event => {
        if (event.target !== this) return;
        setStorage(this.storageKey, "show");
      });
    }
  }
  customElements.define("wc-collapse", collapseElement);

  let dropZoneLoadingPromises = null;
  class dropZoneElement extends HTMLElement {
    constructor() {
      super();
      this._containerId = generateRandomId();
      this._dropzoneElement = null;
    }
    async connectedCallback() {
      await this._init();
    }
    /*
     * Lista de extensões permitidas
     */
    get accept() {
      return this.getStringAttribute("accept", ".txt");
    }
    set accept(value) {
      this.setAttribute("accept", value);
    }

    /*
     * Se o upload inicia automaticamente
     */
    get auto() {
      return this.getBooleanAttribute("auto", false);
    }
    set auto(value) {
      this.setAttribute("auto", value);
    }

    /*
     * Tamanho máximo por arquivo em MB
     */
    get maxFilesize() {
      return this.getIntegerAttribute("max-filesize", 5);
    }
    set maxFilesize(value) {
      this.setAttribute("max-filesize", value);
    }
    get maxFileSizeInMb() {
      return this.maxFilesize;
    }

    /*
     * Método de postagem, geralmente POST ou PUT
     */
    get method() {
      return this.getStringAttribute("method", "post");
    }
    set method(value) {
      this.setAttribute("method", value);
    }

    /*
     * Endpoint de upload
     */
    get endpoint() {
      return this.getStringAttribute("endpoint");
    }
    set endpoint(value) {
      this.setAttribute("endpoint", value);
    }

    /*
     * Se o campo é obrigatório
     */
    get required() {
      return this.getBooleanAttribute("required", false);
    }
    set required(value) {
      this.setAttribute("required", value);
    }
    get value() {
      return this.dropzoneElement.getAcceptedFiles();
    }
    get _defaultConfig() {
      return {
        url: this.endpoint,
        method: this.method,
        acceptedFiles: this.accept,
        autoProcessQueue: this.auto,
        maxFilesize: this.maxFilesize,
        previewTemplate: this.template,
        dictInvalidFileType: i18nlocales.t("invalidFileType"),
        dictFileTooBig: i18nlocales.t("invalidFileSize")
      };
    }
    get config() {
      return this._defaultConfig;
    }
    get container() {
      return this.querySelector(".dropzone");
    }
    get containerMessage() {
      return this.querySelector(".dropzone-message");
    }
    get dropzoneElement() {
      return this._dropzoneElement;
    }
    set dropzoneElement(value) {
      this._dropzoneElement = value;
    }
    get template() {
      return `
    <div class="dz-preview dz-file-preview box w-100 mt-2 position-relative">
      <div class="position-absolute top-0 end-0 p-3">
        <button type="button" title="${i18nlocales.t("removeFile")}" data-dz-remove class="btn-close btn-sm"></button>
      </div>
      <div class="dz-details d-flex align-items-center mb-2">
          <div style="width: 50px" class="me-3">
            <img data-dz-thumbnail class="img-fluid" src="https://htmlstream.com/preview/front-dashboard-v2.1.1/assets/svg/illustrations/oc-browse.svg"  />
          </div>
          <div class="d-flex flex-column flex-grow-1 text-start overflow-hidden">
            <div class="dz-filename small bold text-truncate" style="width:95%" data-dz-name></div>
            <div class="dz-size extra-small" data-dz-size></div>
            <div class="dz-progress progress mb-2">        
              <div class="progress-bar bg-success" role="progressbar" style="width: 0%;" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" data-dz-uploadprogress=""></div>
            </div>
            <div class="d-flex align-items-center text-start">        
                <div class="dz-success-mark"><wc-icon icon="check" class="text-success"></wc-icon></div>
                <div class="dz-error-mark"><wc-icon icon="alert-circle" class="text-danger me-2"></wc-icon></div>
                <div class="dz-error-message text-danger extra-small"><span data-dz-errormessage></span></div>
            </div>
          </div>
      </div>
    </div>`;
    }
    async _init() {
      await this._loadDropZone();
      this._render();
      this._configure();
      this._addEvents();
    }
    _render() {
      this.innerHTML = `<div id="${this._containerId}" class="dropzone">
                        
                        <div class="dz-message">
                          <div class="d-none d-lg-block">
                            <label class="form-label">${i18nlocales.t("dragAndDropHere")}</label>
                            <p class="mb-2 extra-small">${i18nlocales.t("or")}</p>
                          </div>
                          <span class="btn btn-secondary btn-sm">${i18nlocales.t("browseFiles")}</span>
                        </div>
                      </div>
                      <div class="text-tertiary-color extra-small my-2 text-center">
                        ${i18nlocales.t("uploadMaxFileSize").replace("{{size}}", this.maxFileSizeInMb)} 
                        ${i18nlocales.t("allowedExtensions").replace("{{extensions}}", this.accept)}
                      </div>
                      <div class="dropzone-message small"></div>`;
    }
    _configure() {
      this.dropzoneElement = new window.Dropzone(`#${this._containerId}`, this.config);
    }
    _addEvents() {
      this.dropzoneElement.on("addedfile", () => {
        this._updateStatus();
      });
      this.dropzoneElement.on("removedfile", () => {
        //this._updateStatus();
      });
      this.dropzoneElement.on("error", () => {
        //this._updateStatus();
      });
      this.dropzoneElement.on("processing", () => {
        //this._updateStatus();
      });
      this.dropzoneElement.on("success", () => {
        //this._updateStatus();
      });
    }

    /*
     * Carregar a biblioteca dropzone, somente 1 vez na página
     */
    async _loadDropZone() {
      if (!dropZoneLoadingPromises) {
        dropZoneLoadingPromises = loadScriptAsync(getZeevBootstrapPath("ondemand/dropzone/dropzone-min.js"));
      }
      await dropZoneLoadingPromises;
    }
    _triggerChange() {
      this.dispatchEvent(new Event("change"));
    }
    _updateStatus() {
      //this._triggerChange();
      this._validate();
      this.containerMessage.innerHTML = `<ul>
      <li>Total de arquivos: ${this.dropzoneElement.files.length}</li>
      <li>Arquivos aceitos: ${this.dropzoneElement.getAcceptedFiles().length}</li>
      <li>Arquivos rejeitados: ${this.dropzoneElement.getRejectedFiles().length}</li>
      <li>Arquivos em fila: ${this.dropzoneElement.getQueuedFiles().length}</li>
      <li>Arquivos em processamento: ${this.dropzoneElement.getUploadingFiles().length}</li>
    </ul>`;
    }
    upload() {
      if (!this.dropzoneElement) return false;
      if (this.dropzoneElement.files.length === 0) return false;
      if (this.dropzoneElement.getQueuedFiles().length === 0) return false;
      this.dropzoneElement.processQueue();
    }
    _validate() {
      if (this.dropzoneElement.getRejectedFiles().length > 0) this._setInvalid();else this._setValid();
    }
    _setInvalid() {
      this.container.classList.add("is-invalid");
    }
    _setValid() {
      this.container.classList.remove("is-invalid");
    }
  }
  customElements.define("wc-dropzone", dropZoneElement);

  class LimitLinesElement extends HTMLElement {
    constructor() {
      super();
      this.isExpanded = false;
    }
    connectedCallback() {
      this._init();
    }
    get lines() {
      return this.getIntegerAttribute("lines", 1);
    }
    set lines(value) {
      this.setAttribute("lines", value);
    }
    _init() {
      this.hide();
      this._addEvents();
    }
    _addEvents() {
      this.addEventListener("click", this.toggle.bind(this));
    }
    hide() {
      this.setAttribute("role", "button");
      this.classList.add("limit-lines");
      this.style.webkitLineClamp = this.lines;
      this.style.lineClamp = this.lines;
      this.isExpanded = false;
    }
    show() {
      this.removeAttribute("role");
      this.classList.remove("limit-lines");
      this.style.webkitLineClamp = "";
      this.style.lineClamp = "";
      this.isExpanded = true;
    }
    toggle() {
      if (this.isExpanded) {
        this.hide();
      } else {
        this.show();
      }
    }
  }
  customElements.define("wc-limit-lines", LimitLinesElement);

  class premiumElement extends HTMLElement {
    constructor() {
      super();
      this._containerId = generateRandomId();
    }
    connectedCallback() {
      this._init();
    }

    /*
     * O nome do Plano do ambiente
     */
    get plan() {
      return this.getStringAttribute("plan", "");
    }
    set plan(value) {
      this.setAttribute("plan", value);
    }
    _init() {
      this._render();
    }
    _render() {
      this.innerHTML = `
        <a id="${this._containerId}" role="button" class="text-warning" id="premium" href="https://zeev.it/planos-e-precos?plan=${this.plan}" target="_blank">
            <wc-icon icon="award"></wc-icon>
        </a>
        <wc-tooltip target="#${this._containerId}" content="${i18nlocales.t("premiumFeature")}"></wc-tooltip>`;
    }
  }
  customElements.define("wc-premium", premiumElement);

  const LANGUAGES = Object.freeze({
    javascript: "javascript",
    html: "html",
    css: "css",
    json: "json",
    xml: "xml"
  });
  class codeEditorElement extends HTMLElement {
    constructor() {
      super();
      this._editor = null;
      this._value = "";
      this._themeCompartment = null;
      this._readonlyCompartment = null;
      this._maxLengthCompartment = null;
      this._customSnippets = [];
      this._eslintLinter = null;
    }
    async connectedCallback() {
      await this._init();
      this.dispatchEvent(new Event("ready"));
    }
    static get observedAttributes() {
      return ["readonly", "max-length"];
    }
    attributeChangedCallback(name) {
      if (name === "readonly" && this.editor) {
        this.editor.dispatch({
          effects: this._readonlyCompartment.reconfigure(CodeMirror.EditorState.readOnly.of(this.readonly))
        });
      }
      if (name === "max-length" && this.editor) {
        this.editor.dispatch({
          effects: this._maxLengthCompartment.reconfigure(this._getMaxLengthFilter())
        });
      }
    }
    get lines() {
      return this.getIntegerAttribute("lines", 6);
    }
    set lines(value) {
      this.setAttribute("lines", value);
    }
    get readonly() {
      return this.hasAttribute("readonly");
    }
    set readonly(value) {
      if (value) this.setAttribute("readonly", value);else this.removeAttribute("readonly");
    }
    get maxlength() {
      return this.getIntegerAttribute("max-length", 0);
    }
    set maxlength(value) {
      if (value > 0) this.setAttribute("max-length", value);else this.removeAttribute("max-length");
    }
    set borderRadius(value) {
      this.setAttribute("border-radius", value);
    }
    get borderRadius() {
      return this.getBooleanAttribute("borderradius", true);
    }
    get height() {
      return this.getStringAttribute("height");
    }
    set height(value) {
      this.setAttribute("height", value);
    }
    get language() {
      return this.getStringAttribute("language", LANGUAGES.javascript, [LANGUAGES.javascript, LANGUAGES.html, LANGUAGES.css, LANGUAGES.json, LANGUAGES.xml]);
    }
    set language(value) {
      this.setAttribute("language", value);
    }
    get globals() {
      return this.getStringAttribute("globals", "").split(",").filter(g => g.trim()).reduce((acc, g) => {
        acc[g.trim()] = true;
        return acc;
      }, {});
    }
    set globals(value) {
      this.setAttribute("globals", value);
    }
    get editor() {
      return this._editor;
    }
    set editor(value) {
      this._editor = value;
    }
    set value(value) {
      this.setValue(value);
    }
    get value() {
      return this.getValue();
    }
    get themeCompartment() {
      return this._themeCompartment;
    }
    set themeCompartment(value) {
      this._themeCompartment = value;
    }
    async _init() {
      if (this.editor) return;
      await loadCodeEditor();
      this._configure();
      this._fixHeight();
      this._monitorTheme();
    }
    _configure() {
      const content = this._value || this.innerHTML || Array(this.lines).fill("").join("\n");
      this.innerHTML = "";
      this.editor = new CodeMirror.EditorView({
        extensions: this._getExtensions(),
        parent: this,
        doc: content
      });
    }
    _getExtensions() {
      this.themeCompartment = new CodeMirror.Compartment();
      this._readonlyCompartment = new CodeMirror.Compartment();
      this._maxLengthCompartment = new CodeMirror.Compartment();
      let customTheme = CodeMirror.EditorView.theme({
        "&": {
          height: this.height !== "" ? this.height : undefined
        },
        "&.cm-focused": {
          outline: "0px"
        },
        ".cm-lintRange-error": {
          backgroundColor: "rgba(255, 0, 0, 0.15)"
        },
        ".cm-lintRange-warning": {
          backgroundColor: "rgba(255, 255, 0, 0.15)"
        },
        ".cm-gutterElement.cm-lint-gutter-error": {
          color: "var(--bs-danger)"
        },
        ".cm-gutterElement.cm-lint-gutter-warning": {
          color: "var(--bs-warning)"
        }
      });
      const languagePack = this._getLanguagePack();
      const extensions = [CodeMirror.basicSetup, CodeMirror.keymap.of([CodeMirror.indentWithTab]), languagePack, CodeMirror.EditorView.editorAttributes.of({
        class: `form-control p-0 ${!this.borderRadius ? "rounded-0" : ""}`
      }), customTheme, this.themeCompartment.of([]), this._readonlyCompartment.of(CodeMirror.EditorState.readOnly.of(this.readonly)), this._maxLengthCompartment.of(this._getMaxLengthFilter()), CodeMirror.EditorState.phrases.of(i18nlocales.t("codeMirror")), CodeMirror.EditorView.updateListener.of(this._handleCodeChange.bind(this)), CodeMirror.EditorView.updateListener.of(this._handleValidationChange.bind(this)), CodeMirror.autocompletion(), languagePack.language.data.of({
        autocomplete: this._snippetCompletionSource.bind(this)
      })];
      if (this.language === LANGUAGES.javascript && !this.readonly) {
        extensions.push(CodeMirror.linter(this._eslintDiagnosticSource.bind(this), {
          delay: 500
        }), CodeMirror.lintGutter());
      }
      return extensions;
    }
    _handleCodeChange(v) {
      let oldValue = v.startState.doc.toString();
      let newValue = this.editor.state.doc.toString();
      if (oldValue != newValue) {
        this.dispatchEvent(new CustomEvent("codeChange", {
          detail: {
            newValue
          }
        }));
        this.dispatchEvent(new CustomEvent("code:changed", {
          detail: {
            newValue
          }
        }));
      }
    }
    _getMaxLengthFilter() {
      const limit = this.maxlength;
      if (limit <= 0) return [];
      return CodeMirror.EditorState.transactionFilter.of(tr => {
        if (!tr.docChanged || tr.newDoc.length <= limit) {
          return tr;
        }

        // Se a mudanca for para diminuir o tamanho (ex: deletar), permitimos
        // mesmo que o estado atual ja esteja acima do limite
        if (tr.newDoc.length < tr.startState.doc.length) {
          return tr;
        }

        // Caso contrario, bloqueamos a transacao que excederia o limite
        this.dispatchEvent(new CustomEvent("code:limit-reached", {
          detail: {
            limit
          }
        }));
        return [];
      });
    }
    _handleValidationChange(v) {
      if (this.language !== LANGUAGES.javascript) return;
      if (!v.docChanged && !v.transactions.some(tr => tr.effects.length > 0)) return;
      let errors = 0;
      let warnings = 0;
      CodeMirror.forEachDiagnostic(v.state, diagnostic => {
        if (diagnostic.severity === "error") errors++;else if (diagnostic.severity === "warning") warnings++;
      });
      this.dispatchEvent(new CustomEvent("code:linted", {
        detail: {
          errors,
          warnings
        }
      }));
    }
    _eslintDiagnosticSource(view) {
      const code = view.state.doc.toString();
      if (!this._eslintLinter) this._eslintLinter = new CodeMirror.Linter();
      const config = [{
        languageOptions: {
          ecmaVersion: "latest",
          sourceType: "module",
          globals: _objectSpread2(_objectSpread2({}, CodeMirror.globals.browser), this.globals)
        },
        rules: _objectSpread2(_objectSpread2({}, CodeMirror.js.configs.recommended.rules), {}, {
          "no-eval": "error",
          "no-magic-numbers": "warn",
          "no-var": "warn",
          "no-undef": "warn",
          "no-unused-vars": "warn"
        })
      }];
      try {
        const messages = this._eslintLinter.verify(code, config);
        const diagnostics = [];
        for (const message of messages) {
          const from = this._getOffset(view.state.doc, message.line - 1, message.column - 1);
          const to = message.endLine && message.endColumn ? this._getOffset(view.state.doc, message.endLine - 1, message.endColumn - 1) : from;
          diagnostics.push({
            from,
            to,
            severity: message.severity === 2 ? "error" : "warning",
            message: message.message,
            source: "ESLint"
          });
        }
        return diagnostics;
      } catch (error) {
        console.error("ESLint error:", error);
        return [];
      }
    }
    _getOffset(doc, line, col) {
      const lineObj = doc.line(line + 1);
      return lineObj.from + Math.min(col, lineObj.length);
    }
    _snippetCompletionSource(context) {
      const word = context.matchBefore(/\w*/);
      if (!word || word.from === word.to && !context.explicit) return null;
      return {
        from: word.from,
        options: this._customSnippets.map(s => {
          if (s.apply && typeof s.apply === "string") {
            return _objectSpread2(_objectSpread2({}, s), {}, {
              apply: CodeMirror.snippet(s.apply)
            });
          }
          return s;
        })
      };
    }
    _fixHeight() {
      //Esse codigo, quando o editor esta escondido dentro de uma tab, faz com que o editor fique com altura zero
      //const actualHeight = this.editor.dom.clientHeight;
      //this.editor.dom.style.height = `${actualHeight}px`;
    }
    _getDownloadExtension() {
      const extensions = {
        javascript: ".js",
        css: ".css",
        xml: ".xml",
        json: ".json"
      };
      return extensions[this.language] || ".html";
    }
    _getDownloadMimeType() {
      const mimeTypes = {
        javascript: "application/javascript",
        css: "text/css",
        xml: "application/xml",
        json: "application/json"
      };
      return mimeTypes[this.language] || "text/html";
    }
    _getLanguagePack() {
      const languagePacks = {
        javascript: CodeMirror.javascript,
        css: CodeMirror.css,
        json: CodeMirror.json,
        xml: CodeMirror.xml
      };
      const languague = languagePacks[this.language] || CodeMirror.html;
      return languague();
    }
    _monitorTheme() {
      document.addEventListener(changeThemeEvent, () => {
        this.editor.dispatch({
          effects: this.themeCompartment.reconfigure(isDarkTheme() ? CodeMirror.oneDark : [])
        });
      });
    }
    insertText(text) {
      if (!this.editor) return;
      const transaction = this.editor.state.replaceSelection(text);
      this.editor.dispatch(transaction);
    }
    addToken(token) {
      const cursorPos = this.editor.state.selection.main.head;
      this.editor.dispatch({
        changes: {
          from: cursorPos,
          to: cursorPos,
          insert: token
        }
      });
    }
    download(filename = "code") {
      const blob = new Blob([this.value], {
        type: this._getDownloadMimeType()
      });
      const blobUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = blobUrl;
      downloadLink.download = `${filename}${this._getDownloadExtension()}`;
      downloadLink.click();
      URL.revokeObjectURL(blobUrl);
    }
    setValue(value) {
      this._value = value;
      if (!this.editor) return;
      if (this._value == this.editor.state.doc) return;
      this.editor.dispatch({
        changes: {
          from: 0,
          to: this.editor.state.doc.length,
          insert: this._value
        }
      });
    }
    getValue() {
      if (!this.editor) return "";
      return this.editor.state.doc.toString();
    }
    registerSnippets(snippets) {
      if (!Array.isArray(snippets)) return;
      this._customSnippets = snippets;
    }
  }
  if (!customElements.get("wc-code-editor")) {
    customElements.define("wc-code-editor", codeEditorElement);
  }

  class inputCounter extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    get target() {
      return this.getStringAttribute("target", "");
    }
    set target(value) {
      this.setAttribute("target", value);
    }
    get targetElement() {
      if (!this.target) return null;
      return document.querySelector(this.target);
    }
    get counterElement() {
      return this.querySelector(".counter");
    }
    get maxlength() {
      if (!this.targetElement) return null;
      return this.targetElement.getIntegerAttribute("maxlength");
    }
    get length() {
      if (!this.targetElement) return null;
      return this.targetElement.value?.length ?? 0;
    }
    _init() {
      if (!this.maxlength) return;
      this.classList.add("d-flex");
      this.classList.add("justify-content-end");
      this._render();
      this._bindEvents();
      this._defineCounter();
    }
    _render() {
      this.innerHTML = `<span class="input-badge mt-1">
                                <span class="counter me-1">${this.length}</span> / ${this.maxlength} </span>`;
    }
    _updateCounter() {
      if (!this.counterElement) return;
      this.counterElement.textContent = this.length;
    }
    _bindEvents() {
      if (!this.targetElement) return;
      this.targetElement.addEventListener("input", this._updateCounter.bind(this));
      this.targetElement.addEventListener("change", this._updateCounter.bind(this));
      this.targetElement.addEventListener("paste", this._updateCounter.bind(this));
    }
    _defineCounter() {
      setTimeout(() => {
        this._updateCounter();
      }, 1000);
    }
  }
  customElements.define("wc-input-counter", inputCounter);

  class resizeTextarea extends HTMLElement {
    constructor() {
      super();
      this._elementId = generateRandomId();
      this._initialValue = this.innerHTML;
    }
    connectedCallback() {
      this._init();
    }
    get limit() {
      return this.getIntegerAttribute("limit", 0);
    }
    set limit(value) {
      this.setAttribute("limit", value);
    }
    get maxlength() {
      return this.getIntegerAttribute("maxlength", 0);
    }
    set maxlength(value) {
      this.setAttribute("maxlength", value);
    }
    get element() {
      return this.querySelector(`#${this._elementId}`);
    }
    get value() {
      return this.element.value;
    }
    set value(value) {
      this._setValue(value);
    }
    _init() {
      this._render();
      this._setInitialValue();
      this._resize();
      this._bindEvents();
    }
    _render() {
      this.innerHTML = `<textarea id="${this._elementId}"></textarea>`;
      this.transferAttrsTo(this.element, [], ["limit", "id"]);
    }
    _setInitialValue() {
      if (!this._initialValue) return;
      this.value = this._initialValue;
    }
    _resize() {
      if (!this.element) return;
      if (this.limit > 0 && this.element.scrollHeight > this.limit) return;
      if (!this.value) {
        this.element.style.height = "auto";
        return;
      }
      this.element.style.height = this.element.scrollHeight + "px";
    }
    _bindEvents() {
      this.element.addEventListener("input", this._resize.bind(this));
      this.element.addEventListener("change", this._dispachChange.bind(this));
    }
    _dispachChange() {
      this.dispatchEvent(new Event("change"));
    }
    _setValue(value) {
      if (this.maxlength > 0 && value.length > this.maxlength) {
        value = value.substring(0, this.maxlength);
      }
      this.element.value = value;
      this._dispachChange();
      this._resize();
    }
  }
  customElements.define("wc-resize-textarea", resizeTextarea);

  /**
  * Tom Select v2.2.8
  * Licensed under the Apache License, Version 2.0 (the "License");
  */

  /**
   * MicroEvent - to make any js object an event emitter
   *
   * - pure javascript - server compatible, browser compatible
   * - dont rely on the browser doms
   * - super simple - you get it immediatly, no mistery, no magic involved
   *
   * @author Jerome Etienne (https://github.com/jeromeetienne)
   */

  /**
   * Execute callback for each event in space separated list of event names
   *
   */
  function forEvents(events, callback) {
    events.split(/\s+/).forEach(event => {
      callback(event);
    });
  }
  class MicroEvent {
    constructor() {
      this._events = void 0;
      this._events = {};
    }
    on(events, fct) {
      forEvents(events, event => {
        const event_array = this._events[event] || [];
        event_array.push(fct);
        this._events[event] = event_array;
      });
    }
    off(events, fct) {
      var n = arguments.length;
      if (n === 0) {
        this._events = {};
        return;
      }
      forEvents(events, event => {
        if (n === 1) {
          delete this._events[event];
          return;
        }
        const event_array = this._events[event];
        if (event_array === undefined) return;
        event_array.splice(event_array.indexOf(fct), 1);
        this._events[event] = event_array;
      });
    }
    trigger(events, ...args) {
      var self = this;
      forEvents(events, event => {
        const event_array = self._events[event];
        if (event_array === undefined) return;
        event_array.forEach(fct => {
          fct.apply(self, args);
        });
      });
    }
  }

  /**
   * microplugin.js
   * Copyright (c) 2013 Brian Reavis & contributors
   *
   * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this
   * file except in compliance with the License. You may obtain a copy of the License at:
   * http://www.apache.org/licenses/LICENSE-2.0
   *
   * Unless required by applicable law or agreed to in writing, software distributed under
   * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
   * ANY KIND, either express or implied. See the License for the specific language
   * governing permissions and limitations under the License.
   *
   * @author Brian Reavis <brian@thirdroute.com>
   */

  function MicroPlugin(Interface) {
    Interface.plugins = {};
    return class extends Interface {
      constructor(...args) {
        super(...args);
        this.plugins = {
          names: [],
          settings: {},
          requested: {},
          loaded: {}
        };
      }
      /**
       * Registers a plugin.
       *
       * @param {function} fn
       */
      static define(name, fn) {
        Interface.plugins[name] = {
          'name': name,
          'fn': fn
        };
      }

      /**
       * Initializes the listed plugins (with options).
       * Acceptable formats:
       *
       * List (without options):
       *   ['a', 'b', 'c']
       *
       * List (with options):
       *   [{'name': 'a', options: {}}, {'name': 'b', options: {}}]
       *
       * Hash (with options):
       *   {'a': { ... }, 'b': { ... }, 'c': { ... }}
       *
       * @param {array|object} plugins
       */
      initializePlugins(plugins) {
        var key, name;
        const self = this;
        const queue = [];
        if (Array.isArray(plugins)) {
          plugins.forEach(plugin => {
            if (typeof plugin === 'string') {
              queue.push(plugin);
            } else {
              self.plugins.settings[plugin.name] = plugin.options;
              queue.push(plugin.name);
            }
          });
        } else if (plugins) {
          for (key in plugins) {
            if (plugins.hasOwnProperty(key)) {
              self.plugins.settings[key] = plugins[key];
              queue.push(key);
            }
          }
        }
        while (name = queue.shift()) {
          self.require(name);
        }
      }
      loadPlugin(name) {
        var self = this;
        var plugins = self.plugins;
        var plugin = Interface.plugins[name];
        if (!Interface.plugins.hasOwnProperty(name)) {
          throw new Error('Unable to find "' + name + '" plugin');
        }
        plugins.requested[name] = true;
        plugins.loaded[name] = plugin.fn.apply(self, [self.plugins.settings[name] || {}]);
        plugins.names.push(name);
      }

      /**
       * Initializes a plugin.
       *
       */
      require(name) {
        var self = this;
        var plugins = self.plugins;
        if (!self.plugins.loaded.hasOwnProperty(name)) {
          if (plugins.requested[name]) {
            throw new Error('Plugin has circular dependency ("' + name + '")');
          }
          self.loadPlugin(name);
        }
        return plugins.loaded[name];
      }
    };
  }

  /*! @orchidjs/unicode-variants | https://github.com/orchidjs/unicode-variants | Apache License (v2) */
  /**
   * Convert array of strings to a regular expression
   *	ex ['ab','a'] => (?:ab|a)
   * 	ex ['a','b'] => [ab]
   * @param {string[]} chars
   * @return {string}
   */
  const arrayToPattern = chars => {
    chars = chars.filter(Boolean);
    if (chars.length < 2) {
      return chars[0] || '';
    }
    return maxValueLength(chars) == 1 ? '[' + chars.join('') + ']' : '(?:' + chars.join('|') + ')';
  };
  /**
   * @param {string[]} array
   * @return {string}
   */

  const sequencePattern = array => {
    if (!hasDuplicates(array)) {
      return array.join('');
    }
    let pattern = '';
    let prev_char_count = 0;
    const prev_pattern = () => {
      if (prev_char_count > 1) {
        pattern += '{' + prev_char_count + '}';
      }
    };
    array.forEach((char, i) => {
      if (char === array[i - 1]) {
        prev_char_count++;
        return;
      }
      prev_pattern();
      pattern += char;
      prev_char_count = 1;
    });
    prev_pattern();
    return pattern;
  };
  /**
   * Convert array of strings to a regular expression
   *	ex ['ab','a'] => (?:ab|a)
   * 	ex ['a','b'] => [ab]
   * @param {Set<string>} chars
   * @return {string}
   */

  const setToPattern = chars => {
    let array = toArray(chars);
    return arrayToPattern(array);
  };
  /**
   *
   * https://stackoverflow.com/questions/7376598/in-javascript-how-do-i-check-if-an-array-has-duplicate-values
   * @param {any[]} array
   */

  const hasDuplicates = array => {
    return new Set(array).size !== array.length;
  };
  /**
   * https://stackoverflow.com/questions/63006601/why-does-u-throw-an-invalid-escape-error
   * @param {string} str
   * @return {string}
   */

  const escape_regex = str => {
    return (str + '').replace(/([\$\(\)\*\+\.\?\[\]\^\{\|\}\\])/gu, '\\$1');
  };
  /**
   * Return the max length of array values
   * @param {string[]} array
   *
   */

  const maxValueLength = array => {
    return array.reduce((longest, value) => Math.max(longest, unicodeLength(value)), 0);
  };
  /**
   * @param {string} str
   */

  const unicodeLength = str => {
    return toArray(str).length;
  };
  /**
   * @param {any} p
   * @return {any[]}
   */

  const toArray = p => Array.from(p);

  /*! @orchidjs/unicode-variants | https://github.com/orchidjs/unicode-variants | Apache License (v2) */
  /**
   * Get all possible combinations of substrings that add up to the given string
   * https://stackoverflow.com/questions/30169587/find-all-the-combination-of-substrings-that-add-up-to-the-given-string
   * @param {string} input
   * @return {string[][]}
   */
  const allSubstrings = input => {
    if (input.length === 1) return [[input]];
    /** @type {string[][]} */

    let result = [];
    const start = input.substring(1);
    const suba = allSubstrings(start);
    suba.forEach(function (subresult) {
      let tmp = subresult.slice(0);
      tmp[0] = input.charAt(0) + tmp[0];
      result.push(tmp);
      tmp = subresult.slice(0);
      tmp.unshift(input.charAt(0));
      result.push(tmp);
    });
    return result;
  };

  /*! @orchidjs/unicode-variants | https://github.com/orchidjs/unicode-variants | Apache License (v2) */

  /**
   * @typedef {{[key:string]:string}} TUnicodeMap
   * @typedef {{[key:string]:Set<string>}} TUnicodeSets
   * @typedef {[[number,number]]} TCodePoints
   * @typedef {{folded:string,composed:string,code_point:number}} TCodePointObj
   * @typedef {{start:number,end:number,length:number,substr:string}} TSequencePart
   */
  /** @type {TCodePoints} */

  const code_points = [[0, 65535]];
  const accent_pat$5 = '[\u0300-\u036F\u{b7}\u{2be}\u{2bc}]';
  /** @type {TUnicodeMap} */

  let unicode_map;
  /** @type {RegExp} */

  let multi_char_reg;
  const max_char_length = 3;
  /** @type {TUnicodeMap} */

  const latin_convert$5 = {};
  /** @type {TUnicodeMap} */

  const latin_condensed$5 = {
    '/': '⁄∕',
    '0': '߀',
    "a": "ⱥɐɑ",
    "aa": "ꜳ",
    "ae": "æǽǣ",
    "ao": "ꜵ",
    "au": "ꜷ",
    "av": "ꜹꜻ",
    "ay": "ꜽ",
    "b": "ƀɓƃ",
    "c": "ꜿƈȼↄ",
    "d": "đɗɖᴅƌꮷԁɦ",
    "e": "ɛǝᴇɇ",
    "f": "ꝼƒ",
    "g": "ǥɠꞡᵹꝿɢ",
    "h": "ħⱨⱶɥ",
    "i": "ɨı",
    "j": "ɉȷ",
    "k": "ƙⱪꝁꝃꝅꞣ",
    "l": "łƚɫⱡꝉꝇꞁɭ",
    "m": "ɱɯϻ",
    "n": "ꞥƞɲꞑᴎлԉ",
    "o": "øǿɔɵꝋꝍᴑ",
    "oe": "œ",
    "oi": "ƣ",
    "oo": "ꝏ",
    "ou": "ȣ",
    "p": "ƥᵽꝑꝓꝕρ",
    "q": "ꝗꝙɋ",
    "r": "ɍɽꝛꞧꞃ",
    "s": "ßȿꞩꞅʂ",
    "t": "ŧƭʈⱦꞇ",
    "th": "þ",
    "tz": "ꜩ",
    "u": "ʉ",
    "v": "ʋꝟʌ",
    "vy": "ꝡ",
    "w": "ⱳ",
    "y": "ƴɏỿ",
    "z": "ƶȥɀⱬꝣ",
    "hv": "ƕ"
  };
  for (let latin in latin_condensed$5) {
    let unicode = latin_condensed$5[latin] || '';
    for (let i = 0; i < unicode.length; i++) {
      let char = unicode.substring(i, i + 1);
      latin_convert$5[char] = latin;
    }
  }
  const convert_pat = new RegExp(Object.keys(latin_convert$5).join('|') + '|' + accent_pat$5, 'gu');
  /**
   * Initialize the unicode_map from the give code point ranges
   *
   * @param {TCodePoints=} _code_points
   */

  const initialize = _code_points => {
    if (unicode_map !== undefined) return;
    unicode_map = generateMap(_code_points || code_points);
  };
  /**
   * Helper method for normalize a string
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
   * @param {string} str
   * @param {string} form
   */

  const normalize = (str, form = 'NFKD') => str.normalize(form);
  /**
   * Remove accents without reordering string
   * calling str.normalize('NFKD') on \u{594}\u{595}\u{596} becomes \u{596}\u{594}\u{595}
   * via https://github.com/krisk/Fuse/issues/133#issuecomment-318692703
   * @param {string} str
   * @return {string}
   */

  const asciifold = str => {
    return toArray(str).reduce(
    /**
     * @param {string} result
     * @param {string} char
     */
    (result, char) => {
      return result + _asciifold(char);
    }, '');
  };
  /**
   * @param {string} str
   * @return {string}
   */

  const _asciifold = str => {
    str = normalize(str).toLowerCase().replace(convert_pat, ( /** @type {string} */
    char) => {
      return latin_convert$5[char] || '';
    }); //return str;

    return normalize(str, 'NFC');
  };
  /**
   * Generate a list of unicode variants from the list of code points
   * @param {TCodePoints} code_points
   * @yield {TCodePointObj}
   */

  function* generator(code_points) {
    for (const [code_point_min, code_point_max] of code_points) {
      for (let i = code_point_min; i <= code_point_max; i++) {
        let composed = String.fromCharCode(i);
        let folded = asciifold(composed);
        if (folded == composed.toLowerCase()) {
          continue;
        } // skip when folded is a string longer than 3 characters long
        // bc the resulting regex patterns will be long
        // eg:
        // folded صلى الله عليه وسلم length 18 code point 65018
        // folded جل جلاله length 8 code point 65019

        if (folded.length > max_char_length) {
          continue;
        }
        if (folded.length == 0) {
          continue;
        }
        yield {
          folded: folded,
          composed: composed,
          code_point: i
        };
      }
    }
  }
  /**
   * Generate a unicode map from the list of code points
   * @param {TCodePoints} code_points
   * @return {TUnicodeSets}
   */

  const generateSets = code_points => {
    /** @type {{[key:string]:Set<string>}} */
    const unicode_sets = {};
    /**
     * @param {string} folded
     * @param {string} to_add
     */

    const addMatching = (folded, to_add) => {
      /** @type {Set<string>} */
      const folded_set = unicode_sets[folded] || new Set();
      const patt = new RegExp('^' + setToPattern(folded_set) + '$', 'iu');
      if (to_add.match(patt)) {
        return;
      }
      folded_set.add(escape_regex(to_add));
      unicode_sets[folded] = folded_set;
    };
    for (let value of generator(code_points)) {
      addMatching(value.folded, value.folded);
      addMatching(value.folded, value.composed);
    }
    return unicode_sets;
  };
  /**
   * Generate a unicode map from the list of code points
   * ae => (?:(?:ae|Æ|Ǽ|Ǣ)|(?:A|Ⓐ|Ａ...)(?:E|ɛ|Ⓔ...))
   *
   * @param {TCodePoints} code_points
   * @return {TUnicodeMap}
   */

  const generateMap = code_points => {
    /** @type {TUnicodeSets} */
    const unicode_sets = generateSets(code_points);
    /** @type {TUnicodeMap} */

    const unicode_map = {};
    /** @type {string[]} */

    let multi_char = [];
    for (let folded in unicode_sets) {
      let set = unicode_sets[folded];
      if (set) {
        unicode_map[folded] = setToPattern(set);
      }
      if (folded.length > 1) {
        multi_char.push(escape_regex(folded));
      }
    }
    multi_char.sort((a, b) => b.length - a.length);
    const multi_char_patt = arrayToPattern(multi_char);
    multi_char_reg = new RegExp('^' + multi_char_patt, 'u');
    return unicode_map;
  };
  /**
   * Map each element of an array from it's folded value to all possible unicode matches
   * @param {string[]} strings
   * @param {number} min_replacement
   * @return {string}
   */

  const mapSequence = (strings, min_replacement = 1) => {
    let chars_replaced = 0;
    strings = strings.map(str => {
      if (unicode_map[str]) {
        chars_replaced += str.length;
      }
      return unicode_map[str] || str;
    });
    if (chars_replaced >= min_replacement) {
      return sequencePattern(strings);
    }
    return '';
  };
  /**
   * Convert a short string and split it into all possible patterns
   * Keep a pattern only if min_replacement is met
   *
   * 'abc'
   * 		=> [['abc'],['ab','c'],['a','bc'],['a','b','c']]
   *		=> ['abc-pattern','ab-c-pattern'...]
   *
   *
   * @param {string} str
   * @param {number} min_replacement
   * @return {string}
   */

  const substringsToPattern = (str, min_replacement = 1) => {
    min_replacement = Math.max(min_replacement, str.length - 1);
    return arrayToPattern(allSubstrings(str).map(sub_pat => {
      return mapSequence(sub_pat, min_replacement);
    }));
  };
  /**
   * Convert an array of sequences into a pattern
   * [{start:0,end:3,length:3,substr:'iii'}...] => (?:iii...)
   *
   * @param {Sequence[]} sequences
   * @param {boolean} all
   */

  const sequencesToPattern = (sequences, all = true) => {
    let min_replacement = sequences.length > 1 ? 1 : 0;
    return arrayToPattern(sequences.map(sequence => {
      let seq = [];
      const len = all ? sequence.length() : sequence.length() - 1;
      for (let j = 0; j < len; j++) {
        seq.push(substringsToPattern(sequence.substrs[j] || '', min_replacement));
      }
      return sequencePattern(seq);
    }));
  };
  /**
   * Return true if the sequence is already in the sequences
   * @param {Sequence} needle_seq
   * @param {Sequence[]} sequences
   */

  const inSequences = (needle_seq, sequences) => {
    for (const seq of sequences) {
      if (seq.start != needle_seq.start || seq.end != needle_seq.end) {
        continue;
      }
      if (seq.substrs.join('') !== needle_seq.substrs.join('')) {
        continue;
      }
      let needle_parts = needle_seq.parts;
      /**
       * @param {TSequencePart} part
       */

      const filter = part => {
        for (const needle_part of needle_parts) {
          if (needle_part.start === part.start && needle_part.substr === part.substr) {
            return false;
          }
          if (part.length == 1 || needle_part.length == 1) {
            continue;
          } // check for overlapping parts
          // a = ['::=','==']
          // b = ['::','===']
          // a = ['r','sm']
          // b = ['rs','m']

          if (part.start < needle_part.start && part.end > needle_part.start) {
            return true;
          }
          if (needle_part.start < part.start && needle_part.end > part.start) {
            return true;
          }
        }
        return false;
      };
      let filtered = seq.parts.filter(filter);
      if (filtered.length > 0) {
        continue;
      }
      return true;
    }
    return false;
  };
  class Sequence {
    constructor() {
      /** @type {TSequencePart[]} */
      this.parts = [];
      /** @type {string[]} */

      this.substrs = [];
      this.start = 0;
      this.end = 0;
    }
    /**
     * @param {TSequencePart|undefined} part
     */

    add(part) {
      if (part) {
        this.parts.push(part);
        this.substrs.push(part.substr);
        this.start = Math.min(part.start, this.start);
        this.end = Math.max(part.end, this.end);
      }
    }
    last() {
      return this.parts[this.parts.length - 1];
    }
    length() {
      return this.parts.length;
    }
    /**
     * @param {number} position
     * @param {TSequencePart} last_piece
     */

    clone(position, last_piece) {
      let clone = new Sequence();
      let parts = JSON.parse(JSON.stringify(this.parts));
      let last_part = parts.pop();
      for (const part of parts) {
        clone.add(part);
      }
      let last_substr = last_piece.substr.substring(0, position - last_part.start);
      let clone_last_len = last_substr.length;
      clone.add({
        start: last_part.start,
        end: last_part.start + clone_last_len,
        length: clone_last_len,
        substr: last_substr
      });
      return clone;
    }
  }
  /**
   * Expand a regular expression pattern to include unicode variants
   * 	eg /a/ becomes /aⓐａẚàáâầấẫẩãāăằắẵẳȧǡäǟảåǻǎȁȃạậặḁąⱥɐɑAⒶＡÀÁÂẦẤẪẨÃĀĂẰẮẴẲȦǠÄǞẢÅǺǍȀȂẠẬẶḀĄȺⱯ/
   *
   * Issue:
   *  ﺊﺋ [ 'ﺊ = \\u{fe8a}', 'ﺋ = \\u{fe8b}' ]
   *	becomes:	ئئ [ 'ي = \\u{64a}', 'ٔ = \\u{654}', 'ي = \\u{64a}', 'ٔ = \\u{654}' ]
   *
   *	İĲ = IIJ = ⅡJ
   *
   * 	1/2/4
   *
   * @param {string} str
   * @return {string|undefined}
   */

  const getPattern = str => {
    initialize();
    str = asciifold(str);
    let pattern = '';
    let sequences = [new Sequence()];
    for (let i = 0; i < str.length; i++) {
      let substr = str.substring(i);
      let match = substr.match(multi_char_reg);
      const char = str.substring(i, i + 1);
      const match_str = match ? match[0] : null; // loop through sequences
      // add either the char or multi_match

      let overlapping = [];
      let added_types = new Set();
      for (const sequence of sequences) {
        const last_piece = sequence.last();
        if (!last_piece || last_piece.length == 1 || last_piece.end <= i) {
          // if we have a multi match
          if (match_str) {
            const len = match_str.length;
            sequence.add({
              start: i,
              end: i + len,
              length: len,
              substr: match_str
            });
            added_types.add('1');
          } else {
            sequence.add({
              start: i,
              end: i + 1,
              length: 1,
              substr: char
            });
            added_types.add('2');
          }
        } else if (match_str) {
          let clone = sequence.clone(i, last_piece);
          const len = match_str.length;
          clone.add({
            start: i,
            end: i + len,
            length: len,
            substr: match_str
          });
          overlapping.push(clone);
        } else {
          // don't add char
          // adding would create invalid patterns: 234 => [2,34,4]
          added_types.add('3');
        }
      } // if we have overlapping

      if (overlapping.length > 0) {
        // ['ii','iii'] before ['i','i','iii']
        overlapping = overlapping.sort((a, b) => {
          return a.length() - b.length();
        });
        for (let clone of overlapping) {
          // don't add if we already have an equivalent sequence
          if (inSequences(clone, sequences)) {
            continue;
          }
          sequences.push(clone);
        }
        continue;
      } // if we haven't done anything unique
      // clean up the patterns
      // helps keep patterns smaller
      // if str = 'r₨㎧aarss', pattern will be 446 instead of 655

      if (i > 0 && added_types.size == 1 && !added_types.has('3')) {
        pattern += sequencesToPattern(sequences, false);
        let new_seq = new Sequence();
        const old_seq = sequences[0];
        if (old_seq) {
          new_seq.add(old_seq.last());
        }
        sequences = [new_seq];
      }
    }
    pattern += sequencesToPattern(sequences, true);
    return pattern;
  };

  /*! sifter.js | https://github.com/orchidjs/sifter.js | Apache License (v2) */

  /**
   * A property getter resolving dot-notation
   * @param  {Object}  obj     The root object to fetch property on
   * @param  {String}  name    The optionally dotted property name to fetch
   * @return {Object}          The resolved property value
   */
  const getAttr = (obj, name) => {
    if (!obj) return;
    return obj[name];
  };
  /**
   * A property getter resolving dot-notation
   * @param  {Object}  obj     The root object to fetch property on
   * @param  {String}  name    The optionally dotted property name to fetch
   * @return {Object}          The resolved property value
   */

  const getAttrNesting = (obj, name) => {
    if (!obj) return;
    var part,
      names = name.split(".");
    while ((part = names.shift()) && (obj = obj[part]));
    return obj;
  };
  /**
   * Calculates how close of a match the
   * given value is against a search token.
   *
   */

  const scoreValue = (value, token, weight) => {
    var score, pos;
    if (!value) return 0;
    value = value + '';
    if (token.regex == null) return 0;
    pos = value.search(token.regex);
    if (pos === -1) return 0;
    score = token.string.length / value.length;
    if (pos === 0) score += 0.5;
    return score * weight;
  };
  /**
   * Cast object property to an array if it exists and has a value
   *
   */

  const propToArray = (obj, key) => {
    var value = obj[key];
    if (typeof value == 'function') return value;
    if (value && !Array.isArray(value)) {
      obj[key] = [value];
    }
  };
  /**
   * Iterates over arrays and hashes.
   *
   * ```
   * iterate(this.items, function(item, id) {
   *    // invoked for each item
   * });
   * ```
   *
   */

  const iterate$1 = (object, callback) => {
    if (Array.isArray(object)) {
      object.forEach(callback);
    } else {
      for (var key in object) {
        if (object.hasOwnProperty(key)) {
          callback(object[key], key);
        }
      }
    }
  };
  const cmp = (a, b) => {
    if (typeof a === 'number' && typeof b === 'number') {
      return a > b ? 1 : a < b ? -1 : 0;
    }
    a = asciifold(a + '').toLowerCase();
    b = asciifold(b + '').toLowerCase();
    if (a > b) return 1;
    if (b > a) return -1;
    return 0;
  };

  /*! sifter.js | https://github.com/orchidjs/sifter.js | Apache License (v2) */

  /**
   * sifter.js
   * Copyright (c) 2013–2020 Brian Reavis & contributors
   *
   * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this
   * file except in compliance with the License. You may obtain a copy of the License at:
   * http://www.apache.org/licenses/LICENSE-2.0
   *
   * Unless required by applicable law or agreed to in writing, software distributed under
   * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
   * ANY KIND, either express or implied. See the License for the specific language
   * governing permissions and limitations under the License.
   *
   * @author Brian Reavis <brian@thirdroute.com>
   */

  class Sifter {
    // []|{};

    /**
     * Textually searches arrays and hashes of objects
     * by property (or multiple properties). Designed
     * specifically for autocomplete.
     *
     */
    constructor(items, settings) {
      this.items = void 0;
      this.settings = void 0;
      this.items = items;
      this.settings = settings || {
        diacritics: true
      };
    }

    /**
     * Splits a search string into an array of individual
     * regexps to be used to match results.
     *
     */
    tokenize(query, respect_word_boundaries, weights) {
      if (!query || !query.length) return [];
      const tokens = [];
      const words = query.split(/\s+/);
      var field_regex;
      if (weights) {
        field_regex = new RegExp('^(' + Object.keys(weights).map(escape_regex).join('|') + ')\:(.*)$');
      }
      words.forEach(word => {
        let field_match;
        let field = null;
        let regex = null; // look for "field:query" tokens

        if (field_regex && (field_match = word.match(field_regex))) {
          field = field_match[1];
          word = field_match[2];
        }
        if (word.length > 0) {
          if (this.settings.diacritics) {
            regex = getPattern(word) || null;
          } else {
            regex = escape_regex(word);
          }
          if (regex && respect_word_boundaries) regex = "\\b" + regex;
        }
        tokens.push({
          string: word,
          regex: regex ? new RegExp(regex, 'iu') : null,
          field: field
        });
      });
      return tokens;
    }

    /**
     * Returns a function to be used to score individual results.
     *
     * Good matches will have a higher score than poor matches.
     * If an item is not a match, 0 will be returned by the function.
     *
     * @returns {T.ScoreFn}
     */
    getScoreFunction(query, options) {
      var search = this.prepareSearch(query, options);
      return this._getScoreFunction(search);
    }
    /**
     * @returns {T.ScoreFn}
     *
     */

    _getScoreFunction(search) {
      const tokens = search.tokens,
        token_count = tokens.length;
      if (!token_count) {
        return function () {
          return 0;
        };
      }
      const fields = search.options.fields,
        weights = search.weights,
        field_count = fields.length,
        getAttrFn = search.getAttrFn;
      if (!field_count) {
        return function () {
          return 1;
        };
      }
      /**
       * Calculates the score of an object
       * against the search query.
       *
       */

      const scoreObject = function () {
        if (field_count === 1) {
          return function (token, data) {
            const field = fields[0].field;
            return scoreValue(getAttrFn(data, field), token, weights[field] || 1);
          };
        }
        return function (token, data) {
          var sum = 0; // is the token specific to a field?

          if (token.field) {
            const value = getAttrFn(data, token.field);
            if (!token.regex && value) {
              sum += 1 / field_count;
            } else {
              sum += scoreValue(value, token, 1);
            }
          } else {
            iterate$1(weights, (weight, field) => {
              sum += scoreValue(getAttrFn(data, field), token, weight);
            });
          }
          return sum / field_count;
        };
      }();
      if (token_count === 1) {
        return function (data) {
          return scoreObject(tokens[0], data);
        };
      }
      if (search.options.conjunction === 'and') {
        return function (data) {
          var score,
            sum = 0;
          for (let token of tokens) {
            score = scoreObject(token, data);
            if (score <= 0) return 0;
            sum += score;
          }
          return sum / token_count;
        };
      } else {
        return function (data) {
          var sum = 0;
          iterate$1(tokens, token => {
            sum += scoreObject(token, data);
          });
          return sum / token_count;
        };
      }
    }

    /**
     * Returns a function that can be used to compare two
     * results, for sorting purposes. If no sorting should
     * be performed, `null` will be returned.
     *
     * @return function(a,b)
     */
    getSortFunction(query, options) {
      var search = this.prepareSearch(query, options);
      return this._getSortFunction(search);
    }
    _getSortFunction(search) {
      var implicit_score,
        sort_flds = [];
      const self = this,
        options = search.options,
        sort = !search.query && options.sort_empty ? options.sort_empty : options.sort;
      if (typeof sort == 'function') {
        return sort.bind(this);
      }
      /**
       * Fetches the specified sort field value
       * from a search result item.
       *
       */

      const get_field = function get_field(name, result) {
        if (name === '$score') return result.score;
        return search.getAttrFn(self.items[result.id], name);
      }; // parse options

      if (sort) {
        for (let s of sort) {
          if (search.query || s.field !== '$score') {
            sort_flds.push(s);
          }
        }
      } // the "$score" field is implied to be the primary
      // sort field, unless it's manually specified

      if (search.query) {
        implicit_score = true;
        for (let fld of sort_flds) {
          if (fld.field === '$score') {
            implicit_score = false;
            break;
          }
        }
        if (implicit_score) {
          sort_flds.unshift({
            field: '$score',
            direction: 'desc'
          });
        } // without a search.query, all items will have the same score
      } else {
        sort_flds = sort_flds.filter(fld => fld.field !== '$score');
      } // build function

      const sort_flds_count = sort_flds.length;
      if (!sort_flds_count) {
        return null;
      }
      return function (a, b) {
        var result, field;
        for (let sort_fld of sort_flds) {
          field = sort_fld.field;
          let multiplier = sort_fld.direction === 'desc' ? -1 : 1;
          result = multiplier * cmp(get_field(field, a), get_field(field, b));
          if (result) return result;
        }
        return 0;
      };
    }

    /**
     * Parses a search query and returns an object
     * with tokens and fields ready to be populated
     * with results.
     *
     */
    prepareSearch(query, optsUser) {
      const weights = {};
      var options = Object.assign({}, optsUser);
      propToArray(options, 'sort');
      propToArray(options, 'sort_empty'); // convert fields to new format

      if (options.fields) {
        propToArray(options, 'fields');
        const fields = [];
        options.fields.forEach(field => {
          if (typeof field == 'string') {
            field = {
              field: field,
              weight: 1
            };
          }
          fields.push(field);
          weights[field.field] = 'weight' in field ? field.weight : 1;
        });
        options.fields = fields;
      }
      return {
        options: options,
        query: query.toLowerCase().trim(),
        tokens: this.tokenize(query, options.respect_word_boundaries, weights),
        total: 0,
        items: [],
        weights: weights,
        getAttrFn: options.nesting ? getAttrNesting : getAttr
      };
    }

    /**
     * Searches through all items and returns a sorted array of matches.
     *
     */
    search(query, options) {
      var self = this,
        score,
        search;
      search = this.prepareSearch(query, options);
      options = search.options;
      query = search.query; // generate result scoring function

      const fn_score = options.score || self._getScoreFunction(search); // perform search and sort

      if (query.length) {
        iterate$1(self.items, (item, id) => {
          score = fn_score(item);
          if (options.filter === false || score > 0) {
            search.items.push({
              'score': score,
              'id': id
            });
          }
        });
      } else {
        iterate$1(self.items, (_, id) => {
          search.items.push({
            'score': 1,
            'id': id
          });
        });
      }
      const fn_sort = self._getSortFunction(search);
      if (fn_sort) search.items.sort(fn_sort); // apply limits

      search.total = search.items.length;
      if (typeof options.limit === 'number') {
        search.items = search.items.slice(0, options.limit);
      }
      return search;
    }
  }

  /**
   * Iterates over arrays and hashes.
   *
   * ```
   * iterate(this.items, function(item, id) {
   *    // invoked for each item
   * });
   * ```
   *
   */
  const iterate$2 = (object, callback) => {
    if (Array.isArray(object)) {
      object.forEach(callback);
    } else {
      for (var key in object) {
        if (object.hasOwnProperty(key)) {
          callback(object[key], key);
        }
      }
    }
  };

  /**
   * Return a dom element from either a dom query string, jQuery object, a dom element or html string
   * https://stackoverflow.com/questions/494143/creating-a-new-dom-element-from-an-html-string-using-built-in-dom-methods-or-pro/35385518#35385518
   *
   * param query should be {}
   */
  const getDom$5 = query => {
    if (query.jquery) {
      return query[0];
    }
    if (query instanceof HTMLElement) {
      return query;
    }
    if (isHtmlString$5(query)) {
      var tpl = document.createElement('template');
      tpl.innerHTML = query.trim(); // Never return a text node of whitespace as the result
      return tpl.content.firstChild;
    }
    return document.querySelector(query);
  };
  const isHtmlString$5 = arg => {
    if (typeof arg === 'string' && arg.indexOf('<') > -1) {
      return true;
    }
    return false;
  };
  const escapeQuery = query => {
    return query.replace(/['"\\]/g, '\\$&');
  };

  /**
   * Dispatch an event
   *
   */
  const triggerEvent = (dom_el, event_name) => {
    var event = document.createEvent('HTMLEvents');
    event.initEvent(event_name, true, false);
    dom_el.dispatchEvent(event);
  };

  /**
   * Apply CSS rules to a dom element
   *
   */
  const applyCSS = (dom_el, css) => {
    Object.assign(dom_el.style, css);
  };

  /**
   * Add css classes
   *
   */
  const addClasses$1 = (elmts, ...classes) => {
    var norm_classes = classesArray$1(classes);
    elmts = castAsArray$1(elmts);
    elmts.map(el => {
      norm_classes.map(cls => {
        el.classList.add(cls);
      });
    });
  };

  /**
   * Remove css classes
   *
   */
  const removeClasses = (elmts, ...classes) => {
    var norm_classes = classesArray$1(classes);
    elmts = castAsArray$1(elmts);
    elmts.map(el => {
      norm_classes.map(cls => {
        el.classList.remove(cls);
      });
    });
  };

  /**
   * Return arguments
   *
   */
  const classesArray$1 = args => {
    var classes = [];
    iterate$2(args, _classes => {
      if (typeof _classes === 'string') {
        _classes = _classes.trim().split(/[\11\12\14\15\40]/);
      }
      if (Array.isArray(_classes)) {
        classes = classes.concat(_classes);
      }
    });
    return classes.filter(Boolean);
  };

  /**
   * Create an array from arg if it's not already an array
   *
   */
  const castAsArray$1 = arg => {
    if (!Array.isArray(arg)) {
      arg = [arg];
    }
    return arg;
  };

  /**
   * Get the closest node to the evt.target matching the selector
   * Stops at wrapper
   *
   */
  const parentMatch = (target, selector, wrapper) => {
    if (wrapper && !wrapper.contains(target)) {
      return;
    }
    while (target && target.matches) {
      if (target.matches(selector)) {
        return target;
      }
      target = target.parentNode;
    }
  };

  /**
   * Get the first or last item from an array
   *
   * > 0 - right (last)
   * <= 0 - left (first)
   *
   */
  const getTail = (list, direction = 0) => {
    if (direction > 0) {
      return list[list.length - 1];
    }
    return list[0];
  };

  /**
   * Return true if an object is empty
   *
   */
  const isEmptyObject = obj => {
    return Object.keys(obj).length === 0;
  };

  /**
   * Get the index of an element amongst sibling nodes of the same type
   *
   */
  const nodeIndex = (el, amongst) => {
    if (!el) return -1;
    amongst = amongst || el.nodeName;
    var i = 0;
    while (el = el.previousElementSibling) {
      if (el.matches(amongst)) {
        i++;
      }
    }
    return i;
  };

  /**
   * Set attributes of an element
   *
   */
  const setAttr = (el, attrs) => {
    iterate$2(attrs, (val, attr) => {
      if (val == null) {
        el.removeAttribute(attr);
      } else {
        el.setAttribute(attr, '' + val);
      }
    });
  };

  /**
   * Replace a node
   */
  const replaceNode = (existing, replacement) => {
    if (existing.parentNode) existing.parentNode.replaceChild(replacement, existing);
  };

  /**
   * highlight v3 | MIT license | Johann Burkard <jb@eaio.com>
   * Highlights arbitrary terms in a node.
   *
   * - Modified by Marshal <beatgates@gmail.com> 2011-6-24 (added regex)
   * - Modified by Brian Reavis <brian@thirdroute.com> 2012-8-27 (cleanup)
   */
  const highlight = (element, regex) => {
    if (regex === null) return;

    // convet string to regex
    if (typeof regex === 'string') {
      if (!regex.length) return;
      regex = new RegExp(regex, 'i');
    }

    // Wrap matching part of text node with highlighting <span>, e.g.
    // Soccer  ->  <span class="highlight">Soc</span>cer  for regex = /soc/i
    const highlightText = node => {
      var match = node.data.match(regex);
      if (match && node.data.length > 0) {
        var spannode = document.createElement('span');
        spannode.className = 'highlight';
        var middlebit = node.splitText(match.index);
        middlebit.splitText(match[0].length);
        var middleclone = middlebit.cloneNode(true);
        spannode.appendChild(middleclone);
        replaceNode(middlebit, spannode);
        return 1;
      }
      return 0;
    };

    // Recurse element node, looking for child text nodes to highlight, unless element
    // is childless, <script>, <style>, or already highlighted: <span class="hightlight">
    const highlightChildren = node => {
      if (node.nodeType === 1 && node.childNodes && !/(script|style)/i.test(node.tagName) && (node.className !== 'highlight' || node.tagName !== 'SPAN')) {
        Array.from(node.childNodes).forEach(element => {
          highlightRecursive(element);
        });
      }
    };
    const highlightRecursive = node => {
      if (node.nodeType === 3) {
        return highlightText(node);
      }
      highlightChildren(node);
      return 0;
    };
    highlightRecursive(element);
  };

  /**
   * removeHighlight fn copied from highlight v5 and
   * edited to remove with(), pass js strict mode, and use without jquery
   */
  const removeHighlight = el => {
    var elements = el.querySelectorAll("span.highlight");
    Array.prototype.forEach.call(elements, function (el) {
      var parent = el.parentNode;
      parent.replaceChild(el.firstChild, el);
      parent.normalize();
    });
  };
  const KEY_A = 65;
  const KEY_RETURN = 13;
  const KEY_ESC$1 = 27;
  const KEY_LEFT = 37;
  const KEY_UP = 38;
  const KEY_RIGHT = 39;
  const KEY_DOWN = 40;
  const KEY_BACKSPACE = 8;
  const KEY_DELETE = 46;
  const KEY_TAB$1 = 9;
  const IS_MAC = typeof navigator === 'undefined' ? false : /Mac/.test(navigator.userAgent);
  const KEY_SHORTCUT = IS_MAC ? 'metaKey' : 'ctrlKey'; // ctrl key or apple key for ma

  var defaults = {
    options: [],
    optgroups: [],
    plugins: [],
    delimiter: ',',
    splitOn: null,
    // regexp or string for splitting up values from a paste command
    persist: true,
    diacritics: true,
    create: null,
    createOnBlur: false,
    createFilter: null,
    highlight: true,
    openOnFocus: true,
    shouldOpen: null,
    maxOptions: 50,
    maxItems: null,
    hideSelected: null,
    duplicates: false,
    addPrecedence: false,
    selectOnTab: false,
    preload: null,
    allowEmptyOption: false,
    //closeAfterSelect: false,

    loadThrottle: 300,
    loadingClass: 'loading',
    dataAttr: null,
    //'data-data',
    optgroupField: 'optgroup',
    valueField: 'value',
    labelField: 'text',
    disabledField: 'disabled',
    optgroupLabelField: 'label',
    optgroupValueField: 'value',
    lockOptgroupOrder: false,
    sortField: '$order',
    searchField: ['text'],
    searchConjunction: 'and',
    mode: null,
    wrapperClass: 'ts-wrapper',
    controlClass: 'ts-control',
    dropdownClass: 'ts-dropdown',
    dropdownContentClass: 'ts-dropdown-content',
    itemClass: 'item',
    optionClass: 'option',
    dropdownParent: null,
    controlInput: '<input type="text" autocomplete="off" size="1" />',
    copyClassesToDropdown: false,
    placeholder: null,
    hidePlaceholder: null,
    shouldLoad: function (query) {
      return query.length > 0;
    },
    /*
    load                 : null, // function(query, callback) { ... }
    score                : null, // function(search) { ... }
    onInitialize         : null, // function() { ... }
    onChange             : null, // function(value) { ... }
    onItemAdd            : null, // function(value, $item) { ... }
    onItemRemove         : null, // function(value) { ... }
    onClear              : null, // function() { ... }
    onOptionAdd          : null, // function(value, data) { ... }
    onOptionRemove       : null, // function(value) { ... }
    onOptionClear        : null, // function() { ... }
    onOptionGroupAdd     : null, // function(id, data) { ... }
    onOptionGroupRemove  : null, // function(id) { ... }
    onOptionGroupClear   : null, // function() { ... }
    onDropdownOpen       : null, // function(dropdown) { ... }
    onDropdownClose      : null, // function(dropdown) { ... }
    onType               : null, // function(str) { ... }
    onDelete             : null, // function(values) { ... }
    */

    render: {
      /*
      item: null,
      optgroup: null,
      optgroup_header: null,
      option: null,
      option_create: null
      */
    }
  };

  /**
   * Converts a scalar to its best string representation
   * for hash keys and HTML attribute values.
   *
   * Transformations:
   *   'str'     -> 'str'
   *   null      -> ''
   *   undefined -> ''
   *   true      -> '1'
   *   false     -> '0'
   *   0         -> '0'
   *   1         -> '1'
   *
   */
  const hash_key$1 = value => {
    if (typeof value === 'undefined' || value === null) return null;
    return get_hash$1(value);
  };
  const get_hash$1 = value => {
    if (typeof value === 'boolean') return value ? '1' : '0';
    return value + '';
  };

  /**
   * Escapes a string for use within HTML.
   *
   */
  const escape_html$1 = str => {
    return (str + '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  /**
   * Debounce the user provided load function
   *
   */
  const loadDebounce = (fn, delay) => {
    var timeout;
    return function (value, callback) {
      var self = this;
      if (timeout) {
        self.loading = Math.max(self.loading - 1, 0);
        clearTimeout(timeout);
      }
      timeout = setTimeout(function () {
        timeout = null;
        self.loadedSearches[value] = true;
        fn.call(self, value, callback);
      }, delay);
    };
  };

  /**
   * Debounce all fired events types listed in `types`
   * while executing the provided `fn`.
   *
   */
  const debounce_events = (self, types, fn) => {
    var type;
    var trigger = self.trigger;
    var event_args = {};

    // override trigger method
    self.trigger = function () {
      var type = arguments[0];
      if (types.indexOf(type) !== -1) {
        event_args[type] = arguments;
      } else {
        return trigger.apply(self, arguments);
      }
    };

    // invoke provided function
    fn.apply(self, []);
    self.trigger = trigger;

    // trigger queued events
    for (type of types) {
      if (type in event_args) {
        trigger.apply(self, event_args[type]);
      }
    }
  };

  /**
   * Determines the current selection within a text input control.
   * Returns an object containing:
   *   - start
   *   - length
   *
   */
  const getSelection = input => {
    return {
      start: input.selectionStart || 0,
      length: (input.selectionEnd || 0) - (input.selectionStart || 0)
    };
  };

  /**
   * Prevent default
   *
   */
  const preventDefault$4 = (evt, stop = false) => {
    if (evt) {
      evt.preventDefault();
      if (stop) {
        evt.stopPropagation();
      }
    }
  };

  /**
   * Add event helper
   *
   */
  const addEvent$2 = (target, type, callback, options) => {
    target.addEventListener(type, callback, options);
  };

  /**
   * Return true if the requested key is down
   * Will return false if more than one control character is pressed ( when [ctrl+shift+a] != [ctrl+a] )
   * The current evt may not always set ( eg calling advanceSelection() )
   *
   */
  const isKeyDown = (key_name, evt) => {
    if (!evt) {
      return false;
    }
    if (!evt[key_name]) {
      return false;
    }
    var count = (evt.altKey ? 1 : 0) + (evt.ctrlKey ? 1 : 0) + (evt.shiftKey ? 1 : 0) + (evt.metaKey ? 1 : 0);
    if (count === 1) {
      return true;
    }
    return false;
  };

  /**
   * Get the id of an element
   * If the id attribute is not set, set the attribute with the given id
   *
   */
  const getId = (el, id) => {
    const existing_id = el.getAttribute('id');
    if (existing_id) {
      return existing_id;
    }
    el.setAttribute('id', id);
    return id;
  };

  /**
   * Returns a string with backslashes added before characters that need to be escaped.
   */
  const addSlashes = str => {
    return str.replace(/[\\"']/g, '\\$&');
  };

  /**
   *
   */
  const append = (parent, node) => {
    if (node) parent.append(node);
  };
  function getSettings(input, settings_user) {
    var settings = Object.assign({}, defaults, settings_user);
    var attr_data = settings.dataAttr;
    var field_label = settings.labelField;
    var field_value = settings.valueField;
    var field_disabled = settings.disabledField;
    var field_optgroup = settings.optgroupField;
    var field_optgroup_label = settings.optgroupLabelField;
    var field_optgroup_value = settings.optgroupValueField;
    var tag_name = input.tagName.toLowerCase();
    var placeholder = input.getAttribute('placeholder') || input.getAttribute('data-placeholder');
    if (!placeholder && !settings.allowEmptyOption) {
      let option = input.querySelector('option[value=""]');
      if (option) {
        placeholder = option.textContent;
      }
    }
    var settings_element = {
      placeholder: placeholder,
      options: [],
      optgroups: [],
      items: [],
      maxItems: null
    };

    /**
     * Initialize from a <select> element.
     *
     */
    var init_select = () => {
      var tagName;
      var options = settings_element.options;
      var optionsMap = {};
      var group_count = 1;
      var readData = el => {
        var data = Object.assign({}, el.dataset); // get plain object from DOMStringMap
        var json = attr_data && data[attr_data];
        if (typeof json === 'string' && json.length) {
          data = Object.assign(data, JSON.parse(json));
        }
        return data;
      };
      var addOption = (option, group) => {
        var value = hash_key$1(option.value);
        if (value == null) return;
        if (!value && !settings.allowEmptyOption) return;

        // if the option already exists, it's probably been
        // duplicated in another optgroup. in this case, push
        // the current group to the "optgroup" property on the
        // existing option so that it's rendered in both places.
        if (optionsMap.hasOwnProperty(value)) {
          if (group) {
            var arr = optionsMap[value][field_optgroup];
            if (!arr) {
              optionsMap[value][field_optgroup] = group;
            } else if (!Array.isArray(arr)) {
              optionsMap[value][field_optgroup] = [arr, group];
            } else {
              arr.push(group);
            }
          }
        } else {
          var option_data = readData(option);
          option_data[field_label] = option_data[field_label] || option.textContent;
          option_data[field_value] = option_data[field_value] || value;
          option_data[field_disabled] = option_data[field_disabled] || option.disabled;
          option_data[field_optgroup] = option_data[field_optgroup] || group;
          option_data.$option = option;
          optionsMap[value] = option_data;
          options.push(option_data);
        }
        if (option.selected) {
          settings_element.items.push(value);
        }
      };
      var addGroup = optgroup => {
        var id, optgroup_data;
        optgroup_data = readData(optgroup);
        optgroup_data[field_optgroup_label] = optgroup_data[field_optgroup_label] || optgroup.getAttribute('label') || '';
        optgroup_data[field_optgroup_value] = optgroup_data[field_optgroup_value] || group_count++;
        optgroup_data[field_disabled] = optgroup_data[field_disabled] || optgroup.disabled;
        settings_element.optgroups.push(optgroup_data);
        id = optgroup_data[field_optgroup_value];
        iterate$2(optgroup.children, option => {
          addOption(option, id);
        });
      };
      settings_element.maxItems = input.hasAttribute('multiple') ? null : 1;
      iterate$2(input.children, child => {
        tagName = child.tagName.toLowerCase();
        if (tagName === 'optgroup') {
          addGroup(child);
        } else if (tagName === 'option') {
          addOption(child);
        }
      });
    };

    /**
     * Initialize from a <input type="text"> element.
     *
     */
    var init_textbox = () => {
      const data_raw = input.getAttribute(attr_data);
      if (!data_raw) {
        var value = input.value.trim() || '';
        if (!settings.allowEmptyOption && !value.length) return;
        const values = value.split(settings.delimiter);
        iterate$2(values, value => {
          const option = {};
          option[field_label] = value;
          option[field_value] = value;
          settings_element.options.push(option);
        });
        settings_element.items = values;
      } else {
        settings_element.options = JSON.parse(data_raw);
        iterate$2(settings_element.options, opt => {
          settings_element.items.push(opt[field_value]);
        });
      }
    };
    if (tag_name === 'select') {
      init_select();
    } else {
      init_textbox();
    }
    return Object.assign({}, defaults, settings_element, settings_user);
  }
  var instance_i = 0;
  class TomSelect extends MicroPlugin(MicroEvent) {
    constructor(input_arg, user_settings) {
      super();
      this.control_input = void 0;
      this.wrapper = void 0;
      this.dropdown = void 0;
      this.control = void 0;
      this.dropdown_content = void 0;
      this.focus_node = void 0;
      this.order = 0;
      this.settings = void 0;
      this.input = void 0;
      this.tabIndex = void 0;
      this.is_select_tag = void 0;
      this.rtl = void 0;
      this.inputId = void 0;
      this._destroy = void 0;
      this.sifter = void 0;
      this.isOpen = false;
      this.isDisabled = false;
      this.isRequired = void 0;
      this.isInvalid = false;
      // @deprecated 1.8
      this.isValid = true;
      this.isLocked = false;
      this.isFocused = false;
      this.isInputHidden = false;
      this.isSetup = false;
      this.ignoreFocus = false;
      this.ignoreHover = false;
      this.hasOptions = false;
      this.currentResults = void 0;
      this.lastValue = '';
      this.caretPos = 0;
      this.loading = 0;
      this.loadedSearches = {};
      this.activeOption = null;
      this.activeItems = [];
      this.optgroups = {};
      this.options = {};
      this.userOptions = {};
      this.items = [];
      instance_i++;
      var dir;
      var input = getDom$5(input_arg);
      if (input.tomselect) {
        throw new Error('Tom Select already initialized on this element');
      }
      input.tomselect = this;

      // detect rtl environment
      var computedStyle = window.getComputedStyle && window.getComputedStyle(input, null);
      dir = computedStyle.getPropertyValue('direction');

      // setup default state
      const settings = getSettings(input, user_settings);
      this.settings = settings;
      this.input = input;
      this.tabIndex = input.tabIndex || 0;
      this.is_select_tag = input.tagName.toLowerCase() === 'select';
      this.rtl = /rtl/i.test(dir);
      this.inputId = getId(input, 'tomselect-' + instance_i);
      this.isRequired = input.required;

      // search system
      this.sifter = new Sifter(this.options, {
        diacritics: settings.diacritics
      });

      // option-dependent defaults
      settings.mode = settings.mode || (settings.maxItems === 1 ? 'single' : 'multi');
      if (typeof settings.hideSelected !== 'boolean') {
        settings.hideSelected = settings.mode === 'multi';
      }
      if (typeof settings.hidePlaceholder !== 'boolean') {
        settings.hidePlaceholder = settings.mode !== 'multi';
      }

      // set up createFilter callback
      var filter = settings.createFilter;
      if (typeof filter !== 'function') {
        if (typeof filter === 'string') {
          filter = new RegExp(filter);
        }
        if (filter instanceof RegExp) {
          settings.createFilter = input => filter.test(input);
        } else {
          settings.createFilter = value => {
            return this.settings.duplicates || !this.options[value];
          };
        }
      }
      this.initializePlugins(settings.plugins);
      this.setupCallbacks();
      this.setupTemplates();

      // Create all elements
      const wrapper = getDom$5('<div>');
      const control = getDom$5('<div>');
      const dropdown = this._render('dropdown');
      const dropdown_content = getDom$5(`<div role="listbox" tabindex="-1">`);
      const classes = this.input.getAttribute('class') || '';
      const inputMode = settings.mode;
      var control_input;
      addClasses$1(wrapper, settings.wrapperClass, classes, inputMode);
      addClasses$1(control, settings.controlClass);
      append(wrapper, control);
      addClasses$1(dropdown, settings.dropdownClass, inputMode);
      if (settings.copyClassesToDropdown) {
        addClasses$1(dropdown, classes);
      }
      addClasses$1(dropdown_content, settings.dropdownContentClass);
      append(dropdown, dropdown_content);
      getDom$5(settings.dropdownParent || wrapper).appendChild(dropdown);

      // default controlInput
      if (isHtmlString$5(settings.controlInput)) {
        control_input = getDom$5(settings.controlInput);

        // set attributes
        var attrs = ['autocorrect', 'autocapitalize', 'autocomplete'];
        iterate$1(attrs, attr => {
          if (input.getAttribute(attr)) {
            setAttr(control_input, {
              [attr]: input.getAttribute(attr)
            });
          }
        });
        control_input.tabIndex = -1;
        control.appendChild(control_input);
        this.focus_node = control_input;

        // dom element
      } else if (settings.controlInput) {
        control_input = getDom$5(settings.controlInput);
        this.focus_node = control_input;
      } else {
        control_input = getDom$5('<input/>');
        this.focus_node = control;
      }
      this.wrapper = wrapper;
      this.dropdown = dropdown;
      this.dropdown_content = dropdown_content;
      this.control = control;
      this.control_input = control_input;
      this.setup();
    }

    /**
     * set up event bindings.
     *
     */
    setup() {
      const self = this;
      const settings = self.settings;
      const control_input = self.control_input;
      const dropdown = self.dropdown;
      const dropdown_content = self.dropdown_content;
      const wrapper = self.wrapper;
      const control = self.control;
      const input = self.input;
      const focus_node = self.focus_node;
      const passive_event = {
        passive: true
      };
      const listboxId = self.inputId + '-ts-dropdown';
      setAttr(dropdown_content, {
        id: listboxId
      });
      setAttr(focus_node, {
        role: 'combobox',
        'aria-haspopup': 'listbox',
        'aria-expanded': 'false',
        'aria-controls': listboxId
      });
      const control_id = getId(focus_node, self.inputId + '-ts-control');
      const query = "label[for='" + escapeQuery(self.inputId) + "']";
      const label = document.querySelector(query);
      const label_click = self.focus.bind(self);
      if (label) {
        addEvent$2(label, 'click', label_click);
        setAttr(label, {
          for: control_id
        });
        const label_id = getId(label, self.inputId + '-ts-label');
        setAttr(focus_node, {
          'aria-labelledby': label_id
        });
        setAttr(dropdown_content, {
          'aria-labelledby': label_id
        });
      }
      wrapper.style.width = input.style.width;
      if (self.plugins.names.length) {
        const classes_plugins = 'plugin-' + self.plugins.names.join(' plugin-');
        addClasses$1([wrapper, dropdown], classes_plugins);
      }
      if ((settings.maxItems === null || settings.maxItems > 1) && self.is_select_tag) {
        setAttr(input, {
          multiple: 'multiple'
        });
      }
      if (settings.placeholder) {
        setAttr(control_input, {
          placeholder: settings.placeholder
        });
      }

      // if splitOn was not passed in, construct it from the delimiter to allow pasting universally
      if (!settings.splitOn && settings.delimiter) {
        settings.splitOn = new RegExp('\\s*' + escape_regex(settings.delimiter) + '+\\s*');
      }

      // debounce user defined load() if loadThrottle > 0
      // after initializePlugins() so plugins can create/modify user defined loaders
      if (settings.load && settings.loadThrottle) {
        settings.load = loadDebounce(settings.load, settings.loadThrottle);
      }
      self.control_input.type = input.type;
      addEvent$2(dropdown, 'mousemove', () => {
        self.ignoreHover = false;
      });
      addEvent$2(dropdown, 'mouseenter', e => {
        var target_match = parentMatch(e.target, '[data-selectable]', dropdown);
        if (target_match) self.onOptionHover(e, target_match);
      }, {
        capture: true
      });

      // clicking on an option should select it
      addEvent$2(dropdown, 'click', evt => {
        const option = parentMatch(evt.target, '[data-selectable]');
        if (option) {
          self.onOptionSelect(evt, option);
          preventDefault$4(evt, true);
        }
      });
      addEvent$2(control, 'click', evt => {
        var target_match = parentMatch(evt.target, '[data-ts-item]', control);
        if (target_match && self.onItemSelect(evt, target_match)) {
          preventDefault$4(evt, true);
          return;
        }

        // retain focus (see control_input mousedown)
        if (control_input.value != '') {
          return;
        }
        self.onClick();
        preventDefault$4(evt, true);
      });

      // keydown on focus_node for arrow_down/arrow_up
      addEvent$2(focus_node, 'keydown', e => self.onKeyDown(e));

      // keypress and input/keyup
      addEvent$2(control_input, 'keypress', e => self.onKeyPress(e));
      addEvent$2(control_input, 'input', e => self.onInput(e));
      addEvent$2(focus_node, 'blur', e => self.onBlur(e));
      addEvent$2(focus_node, 'focus', e => self.onFocus(e));
      addEvent$2(control_input, 'paste', e => self.onPaste(e));
      const doc_mousedown = evt => {
        // blur if target is outside of this instance
        // dropdown is not always inside wrapper
        const target = evt.composedPath()[0];
        if (!wrapper.contains(target) && !dropdown.contains(target)) {
          if (self.isFocused) {
            self.blur();
          }
          self.inputState();
          return;
        }

        // retain focus by preventing native handling. if the
        // event target is the input it should not be modified.
        // otherwise, text selection within the input won't work.
        // Fixes bug #212 which is no covered by tests
        if (target == control_input && self.isOpen) {
          evt.stopPropagation();

          // clicking anywhere in the control should not blur the control_input (which would close the dropdown)
        } else {
          preventDefault$4(evt, true);
        }
      };
      const win_scroll = () => {
        if (self.isOpen) {
          self.positionDropdown();
        }
      };
      addEvent$2(document, 'mousedown', doc_mousedown);
      addEvent$2(window, 'scroll', win_scroll, passive_event);
      addEvent$2(window, 'resize', win_scroll, passive_event);
      this._destroy = () => {
        document.removeEventListener('mousedown', doc_mousedown);
        window.removeEventListener('scroll', win_scroll);
        window.removeEventListener('resize', win_scroll);
        if (label) label.removeEventListener('click', label_click);
      };

      // store original html and tab index so that they can be
      // restored when the destroy() method is called.
      this.revertSettings = {
        innerHTML: input.innerHTML,
        tabIndex: input.tabIndex
      };
      input.tabIndex = -1;
      input.insertAdjacentElement('afterend', self.wrapper);
      self.sync(false);
      settings.items = [];
      delete settings.optgroups;
      delete settings.options;
      addEvent$2(input, 'invalid', () => {
        if (self.isValid) {
          self.isValid = false;
          self.isInvalid = true;
          self.refreshState();
        }
      });
      self.updateOriginalInput();
      self.refreshItems();
      self.close(false);
      self.inputState();
      self.isSetup = true;
      if (input.disabled) {
        self.disable();
      } else {
        self.enable(); //sets tabIndex
      }
      self.on('change', this.onChange);
      addClasses$1(input, 'tomselected', 'ts-hidden-accessible');
      self.trigger('initialize');

      // preload options
      if (settings.preload === true) {
        self.preload();
      }
    }

    /**
     * Register options and optgroups
     *
     */
    setupOptions(options = [], optgroups = []) {
      // build options table
      this.addOptions(options);

      // build optgroup table
      iterate$1(optgroups, optgroup => {
        this.registerOptionGroup(optgroup);
      });
    }

    /**
     * Sets up default rendering functions.
     */
    setupTemplates() {
      var self = this;
      var field_label = self.settings.labelField;
      var field_optgroup = self.settings.optgroupLabelField;
      var templates = {
        'optgroup': data => {
          let optgroup = document.createElement('div');
          optgroup.className = 'optgroup';
          optgroup.appendChild(data.options);
          return optgroup;
        },
        'optgroup_header': (data, escape) => {
          return '<div class="optgroup-header">' + escape(data[field_optgroup]) + '</div>';
        },
        'option': (data, escape) => {
          return '<div>' + escape(data[field_label]) + '</div>';
        },
        'item': (data, escape) => {
          return '<div>' + escape(data[field_label]) + '</div>';
        },
        'option_create': (data, escape) => {
          return '<div class="create">Add <strong>' + escape(data.input) + '</strong>&hellip;</div>';
        },
        'no_results': () => {
          return '<div class="no-results">No results found</div>';
        },
        'loading': () => {
          return '<div class="spinner"></div>';
        },
        'not_loading': () => {},
        'dropdown': () => {
          return '<div></div>';
        }
      };
      self.settings.render = Object.assign({}, templates, self.settings.render);
    }

    /**
     * Maps fired events to callbacks provided
     * in the settings used when creating the control.
     */
    setupCallbacks() {
      var key, fn;
      var callbacks = {
        'initialize': 'onInitialize',
        'change': 'onChange',
        'item_add': 'onItemAdd',
        'item_remove': 'onItemRemove',
        'item_select': 'onItemSelect',
        'clear': 'onClear',
        'option_add': 'onOptionAdd',
        'option_remove': 'onOptionRemove',
        'option_clear': 'onOptionClear',
        'optgroup_add': 'onOptionGroupAdd',
        'optgroup_remove': 'onOptionGroupRemove',
        'optgroup_clear': 'onOptionGroupClear',
        'dropdown_open': 'onDropdownOpen',
        'dropdown_close': 'onDropdownClose',
        'type': 'onType',
        'load': 'onLoad',
        'focus': 'onFocus',
        'blur': 'onBlur'
      };
      for (key in callbacks) {
        fn = this.settings[callbacks[key]];
        if (fn) this.on(key, fn);
      }
    }

    /**
     * Sync the Tom Select instance with the original input or select
     *
     */
    sync(get_settings = true) {
      const self = this;
      const settings = get_settings ? getSettings(self.input, {
        delimiter: self.settings.delimiter
      }) : self.settings;
      self.setupOptions(settings.options, settings.optgroups);
      self.setValue(settings.items || [], true); // silent prevents recursion

      self.lastQuery = null; // so updated options will be displayed in dropdown
    }

    /**
     * Triggered when the main control element
     * has a click event.
     *
     */
    onClick() {
      var self = this;
      if (self.activeItems.length > 0) {
        self.clearActiveItems();
        self.focus();
        return;
      }
      if (self.isFocused && self.isOpen) {
        self.blur();
      } else {
        self.focus();
      }
    }

    /**
     * @deprecated v1.7
     *
     */
    onMouseDown() {}

    /**
     * Triggered when the value of the control has been changed.
     * This should propagate the event to the original DOM
     * input / select element.
     */
    onChange() {
      triggerEvent(this.input, 'input');
      triggerEvent(this.input, 'change');
    }

    /**
     * Triggered on <input> paste.
     *
     */
    onPaste(e) {
      var self = this;
      if (self.isInputHidden || self.isLocked) {
        preventDefault$4(e);
        return;
      }

      // If a regex or string is included, this will split the pasted
      // input and create Items for each separate value
      if (!self.settings.splitOn) {
        return;
      }

      // Wait for pasted text to be recognized in value
      setTimeout(() => {
        var pastedText = self.inputValue();
        if (!pastedText.match(self.settings.splitOn)) {
          return;
        }
        var splitInput = pastedText.trim().split(self.settings.splitOn);
        iterate$1(splitInput, piece => {
          const hash = hash_key$1(piece);
          if (hash) {
            if (this.options[piece]) {
              self.addItem(piece);
            } else {
              self.createItem(piece);
            }
          }
        });
      }, 0);
    }

    /**
     * Triggered on <input> keypress.
     *
     */
    onKeyPress(e) {
      var self = this;
      if (self.isLocked) {
        preventDefault$4(e);
        return;
      }
      var character = String.fromCharCode(e.keyCode || e.which);
      if (self.settings.create && self.settings.mode === 'multi' && character === self.settings.delimiter) {
        self.createItem();
        preventDefault$4(e);
        return;
      }
    }

    /**
     * Triggered on <input> keydown.
     *
     */
    onKeyDown(e) {
      var self = this;
      self.ignoreHover = true;
      if (self.isLocked) {
        if (e.keyCode !== KEY_TAB$1) {
          preventDefault$4(e);
        }
        return;
      }
      switch (e.keyCode) {
        // ctrl+A: select all
        case KEY_A:
          if (isKeyDown(KEY_SHORTCUT, e)) {
            if (self.control_input.value == '') {
              preventDefault$4(e);
              self.selectAll();
              return;
            }
          }
          break;

        // esc: close dropdown
        case KEY_ESC$1:
          if (self.isOpen) {
            preventDefault$4(e, true);
            self.close();
          }
          self.clearActiveItems();
          return;

        // down: open dropdown or move selection down
        case KEY_DOWN:
          if (!self.isOpen && self.hasOptions) {
            self.open();
          } else if (self.activeOption) {
            let next = self.getAdjacent(self.activeOption, 1);
            if (next) self.setActiveOption(next);
          }
          preventDefault$4(e);
          return;

        // up: move selection up
        case KEY_UP:
          if (self.activeOption) {
            let prev = self.getAdjacent(self.activeOption, -1);
            if (prev) self.setActiveOption(prev);
          }
          preventDefault$4(e);
          return;

        // return: select active option
        case KEY_RETURN:
          if (self.canSelect(self.activeOption)) {
            self.onOptionSelect(e, self.activeOption);
            preventDefault$4(e);

            // if the option_create=null, the dropdown might be closed
          } else if (self.settings.create && self.createItem()) {
            preventDefault$4(e);

            // don't submit form when searching for a value
          } else if (document.activeElement == self.control_input && self.isOpen) {
            preventDefault$4(e);
          }
          return;

        // left: modifiy item selection to the left
        case KEY_LEFT:
          self.advanceSelection(-1, e);
          return;

        // right: modifiy item selection to the right
        case KEY_RIGHT:
          self.advanceSelection(1, e);
          return;

        // tab: select active option and/or create item
        case KEY_TAB$1:
          if (self.settings.selectOnTab) {
            if (self.canSelect(self.activeOption)) {
              self.onOptionSelect(e, self.activeOption);

              // prevent default [tab] behaviour of jump to the next field
              // if select isFull, then the dropdown won't be open and [tab] will work normally
              preventDefault$4(e);
            }
            if (self.settings.create && self.createItem()) {
              preventDefault$4(e);
            }
          }
          return;

        // delete|backspace: delete items
        case KEY_BACKSPACE:
        case KEY_DELETE:
          self.deleteSelection(e);
          return;
      }

      // don't enter text in the control_input when active items are selected
      if (self.isInputHidden && !isKeyDown(KEY_SHORTCUT, e)) {
        preventDefault$4(e);
      }
    }

    /**
     * Triggered on <input> keyup.
     *
     */
    onInput(e) {
      var self = this;
      if (self.isLocked) {
        return;
      }
      var value = self.inputValue();
      if (self.lastValue !== value) {
        self.lastValue = value;
        if (self.settings.shouldLoad.call(self, value)) {
          self.load(value);
        }
        self.refreshOptions();
        self.trigger('type', value);
      }
    }

    /**
     * Triggered when the user rolls over
     * an option in the autocomplete dropdown menu.
     *
     */
    onOptionHover(evt, option) {
      if (this.ignoreHover) return;
      this.setActiveOption(option, false);
    }

    /**
     * Triggered on <input> focus.
     *
     */
    onFocus(e) {
      var self = this;
      var wasFocused = self.isFocused;
      if (self.isDisabled) {
        self.blur();
        preventDefault$4(e);
        return;
      }
      if (self.ignoreFocus) return;
      self.isFocused = true;
      if (self.settings.preload === 'focus') self.preload();
      if (!wasFocused) self.trigger('focus');
      if (!self.activeItems.length) {
        self.showInput();
        self.refreshOptions(!!self.settings.openOnFocus);
      }
      self.refreshState();
    }

    /**
     * Triggered on <input> blur.
     *
     */
    onBlur(e) {
      if (document.hasFocus() === false) return;
      var self = this;
      if (!self.isFocused) return;
      self.isFocused = false;
      self.ignoreFocus = false;
      var deactivate = () => {
        self.close();
        self.setActiveItem();
        self.setCaret(self.items.length);
        self.trigger('blur');
      };
      if (self.settings.create && self.settings.createOnBlur) {
        self.createItem(null, deactivate);
      } else {
        deactivate();
      }
    }

    /**
     * Triggered when the user clicks on an option
     * in the autocomplete dropdown menu.
     *
     */
    onOptionSelect(evt, option) {
      var value,
        self = this;

      // should not be possible to trigger a option under a disabled optgroup
      if (option.parentElement && option.parentElement.matches('[data-disabled]')) {
        return;
      }
      if (option.classList.contains('create')) {
        self.createItem(null, () => {
          if (self.settings.closeAfterSelect) {
            self.close();
          }
        });
      } else {
        value = option.dataset.value;
        if (typeof value !== 'undefined') {
          self.lastQuery = null;
          self.addItem(value);
          if (self.settings.closeAfterSelect) {
            self.close();
          }
          if (!self.settings.hideSelected && evt.type && /click/.test(evt.type)) {
            self.setActiveOption(option);
          }
        }
      }
    }

    /**
     * Return true if the given option can be selected
     *
     */
    canSelect(option) {
      if (this.isOpen && option && this.dropdown_content.contains(option)) {
        return true;
      }
      return false;
    }

    /**
     * Triggered when the user clicks on an item
     * that has been selected.
     *
     */
    onItemSelect(evt, item) {
      var self = this;
      if (!self.isLocked && self.settings.mode === 'multi') {
        preventDefault$4(evt);
        self.setActiveItem(item, evt);
        return true;
      }
      return false;
    }

    /**
     * Determines whether or not to invoke
     * the user-provided option provider / loader
     *
     * Note, there is a subtle difference between
     * this.canLoad() and this.settings.shouldLoad();
     *
     *	- settings.shouldLoad() is a user-input validator.
     *	When false is returned, the not_loading template
     *	will be added to the dropdown
     *
     *	- canLoad() is lower level validator that checks
     * 	the Tom Select instance. There is no inherent user
     *	feedback when canLoad returns false
     *
     */
    canLoad(value) {
      if (!this.settings.load) return false;
      if (this.loadedSearches.hasOwnProperty(value)) return false;
      return true;
    }

    /**
     * Invokes the user-provided option provider / loader.
     *
     */
    load(value) {
      const self = this;
      if (!self.canLoad(value)) return;
      addClasses$1(self.wrapper, self.settings.loadingClass);
      self.loading++;
      const callback = self.loadCallback.bind(self);
      self.settings.load.call(self, value, callback);
    }

    /**
     * Invoked by the user-provided option provider
     *
     */
    loadCallback(options, optgroups) {
      const self = this;
      self.loading = Math.max(self.loading - 1, 0);
      self.lastQuery = null;
      self.clearActiveOption(); // when new results load, focus should be on first option
      self.setupOptions(options, optgroups);
      self.refreshOptions(self.isFocused && !self.isInputHidden);
      if (!self.loading) {
        removeClasses(self.wrapper, self.settings.loadingClass);
      }
      self.trigger('load', options, optgroups);
    }
    preload() {
      var classList = this.wrapper.classList;
      if (classList.contains('preloaded')) return;
      classList.add('preloaded');
      this.load('');
    }

    /**
     * Sets the input field of the control to the specified value.
     *
     */
    setTextboxValue(value = '') {
      var input = this.control_input;
      var changed = input.value !== value;
      if (changed) {
        input.value = value;
        triggerEvent(input, 'update');
        this.lastValue = value;
      }
    }

    /**
     * Returns the value of the control. If multiple items
     * can be selected (e.g. <select multiple>), this returns
     * an array. If only one item can be selected, this
     * returns a string.
     *
     */
    getValue() {
      if (this.is_select_tag && this.input.hasAttribute('multiple')) {
        return this.items;
      }
      return this.items.join(this.settings.delimiter);
    }

    /**
     * Resets the selected items to the given value.
     *
     */
    setValue(value, silent) {
      var events = silent ? [] : ['change'];
      debounce_events(this, events, () => {
        this.clear(silent);
        this.addItems(value, silent);
      });
    }

    /**
     * Resets the number of max items to the given value
     *
     */
    setMaxItems(value) {
      if (value === 0) value = null; //reset to unlimited items.
      this.settings.maxItems = value;
      this.refreshState();
    }

    /**
     * Sets the selected item.
     *
     */
    setActiveItem(item, e) {
      var self = this;
      var eventName;
      var i, begin, end, swap;
      var last;
      if (self.settings.mode === 'single') return;

      // clear the active selection
      if (!item) {
        self.clearActiveItems();
        if (self.isFocused) {
          self.showInput();
        }
        return;
      }

      // modify selection
      eventName = e && e.type.toLowerCase();
      if (eventName === 'click' && isKeyDown('shiftKey', e) && self.activeItems.length) {
        last = self.getLastActive();
        begin = Array.prototype.indexOf.call(self.control.children, last);
        end = Array.prototype.indexOf.call(self.control.children, item);
        if (begin > end) {
          swap = begin;
          begin = end;
          end = swap;
        }
        for (i = begin; i <= end; i++) {
          item = self.control.children[i];
          if (self.activeItems.indexOf(item) === -1) {
            self.setActiveItemClass(item);
          }
        }
        preventDefault$4(e);
      } else if (eventName === 'click' && isKeyDown(KEY_SHORTCUT, e) || eventName === 'keydown' && isKeyDown('shiftKey', e)) {
        if (item.classList.contains('active')) {
          self.removeActiveItem(item);
        } else {
          self.setActiveItemClass(item);
        }
      } else {
        self.clearActiveItems();
        self.setActiveItemClass(item);
      }

      // ensure control has focus
      self.hideInput();
      if (!self.isFocused) {
        self.focus();
      }
    }

    /**
     * Set the active and last-active classes
     *
     */
    setActiveItemClass(item) {
      const self = this;
      const last_active = self.control.querySelector('.last-active');
      if (last_active) removeClasses(last_active, 'last-active');
      addClasses$1(item, 'active last-active');
      self.trigger('item_select', item);
      if (self.activeItems.indexOf(item) == -1) {
        self.activeItems.push(item);
      }
    }

    /**
     * Remove active item
     *
     */
    removeActiveItem(item) {
      var idx = this.activeItems.indexOf(item);
      this.activeItems.splice(idx, 1);
      removeClasses(item, 'active');
    }

    /**
     * Clears all the active items
     *
     */
    clearActiveItems() {
      removeClasses(this.activeItems, 'active');
      this.activeItems = [];
    }

    /**
     * Sets the selected item in the dropdown menu
     * of available options.
     *
     */
    setActiveOption(option, scroll = true) {
      if (option === this.activeOption) {
        return;
      }
      this.clearActiveOption();
      if (!option) return;
      this.activeOption = option;
      setAttr(this.focus_node, {
        'aria-activedescendant': option.getAttribute('id')
      });
      setAttr(option, {
        'aria-selected': 'true'
      });
      addClasses$1(option, 'active');
      if (scroll) this.scrollToOption(option);
    }

    /**
     * Sets the dropdown_content scrollTop to display the option
     *
     */
    scrollToOption(option, behavior) {
      if (!option) return;
      const content = this.dropdown_content;
      const height_menu = content.clientHeight;
      const scrollTop = content.scrollTop || 0;
      const height_item = option.offsetHeight;
      const y = option.getBoundingClientRect().top - content.getBoundingClientRect().top + scrollTop;
      if (y + height_item > height_menu + scrollTop) {
        this.scroll(y - height_menu + height_item, behavior);
      } else if (y < scrollTop) {
        this.scroll(y, behavior);
      }
    }

    /**
     * Scroll the dropdown to the given position
     *
     */
    scroll(scrollTop, behavior) {
      const content = this.dropdown_content;
      if (behavior) {
        content.style.scrollBehavior = behavior;
      }
      content.scrollTop = scrollTop;
      content.style.scrollBehavior = '';
    }

    /**
     * Clears the active option
     *
     */
    clearActiveOption() {
      if (this.activeOption) {
        removeClasses(this.activeOption, 'active');
        setAttr(this.activeOption, {
          'aria-selected': null
        });
      }
      this.activeOption = null;
      setAttr(this.focus_node, {
        'aria-activedescendant': null
      });
    }

    /**
     * Selects all items (CTRL + A).
     */
    selectAll() {
      const self = this;
      if (self.settings.mode === 'single') return;
      const activeItems = self.controlChildren();
      if (!activeItems.length) return;
      self.hideInput();
      self.close();
      self.activeItems = activeItems;
      iterate$1(activeItems, item => {
        self.setActiveItemClass(item);
      });
    }

    /**
     * Determines if the control_input should be in a hidden or visible state
     *
     */
    inputState() {
      var self = this;
      if (!self.control.contains(self.control_input)) return;
      setAttr(self.control_input, {
        placeholder: self.settings.placeholder
      });
      if (self.activeItems.length > 0 || !self.isFocused && self.settings.hidePlaceholder && self.items.length > 0) {
        self.setTextboxValue();
        self.isInputHidden = true;
      } else {
        if (self.settings.hidePlaceholder && self.items.length > 0) {
          setAttr(self.control_input, {
            placeholder: ''
          });
        }
        self.isInputHidden = false;
      }
      self.wrapper.classList.toggle('input-hidden', self.isInputHidden);
    }

    /**
     * Hides the input element out of view, while
     * retaining its focus.
     * @deprecated 1.3
     */
    hideInput() {
      this.inputState();
    }

    /**
     * Restores input visibility.
     * @deprecated 1.3
     */
    showInput() {
      this.inputState();
    }

    /**
     * Get the input value
     */
    inputValue() {
      return this.control_input.value.trim();
    }

    /**
     * Gives the control focus.
     */
    focus() {
      var self = this;
      if (self.isDisabled) return;
      self.ignoreFocus = true;
      if (self.control_input.offsetWidth) {
        self.control_input.focus();
      } else {
        self.focus_node.focus();
      }
      setTimeout(() => {
        self.ignoreFocus = false;
        self.onFocus();
      }, 0);
    }

    /**
     * Forces the control out of focus.
     *
     */
    blur() {
      this.focus_node.blur();
      this.onBlur();
    }

    /**
     * Returns a function that scores an object
     * to show how good of a match it is to the
     * provided query.
     *
     * @return {function}
     */
    getScoreFunction(query) {
      return this.sifter.getScoreFunction(query, this.getSearchOptions());
    }

    /**
     * Returns search options for sifter (the system
     * for scoring and sorting results).
     *
     * @see https://github.com/orchidjs/sifter.js
     * @return {object}
     */
    getSearchOptions() {
      var settings = this.settings;
      var sort = settings.sortField;
      if (typeof settings.sortField === 'string') {
        sort = [{
          field: settings.sortField
        }];
      }
      return {
        fields: settings.searchField,
        conjunction: settings.searchConjunction,
        sort: sort,
        nesting: settings.nesting
      };
    }

    /**
     * Searches through available options and returns
     * a sorted array of matches.
     *
     */
    search(query) {
      var result, calculateScore;
      var self = this;
      var options = this.getSearchOptions();

      // validate user-provided result scoring function
      if (self.settings.score) {
        calculateScore = self.settings.score.call(self, query);
        if (typeof calculateScore !== 'function') {
          throw new Error('Tom Select "score" setting must be a function that returns a function');
        }
      }

      // perform search
      if (query !== self.lastQuery) {
        self.lastQuery = query;
        result = self.sifter.search(query, Object.assign(options, {
          score: calculateScore
        }));
        self.currentResults = result;
      } else {
        result = Object.assign({}, self.currentResults);
      }

      // filter out selected items
      if (self.settings.hideSelected) {
        result.items = result.items.filter(item => {
          let hashed = hash_key$1(item.id);
          return !(hashed && self.items.indexOf(hashed) !== -1);
        });
      }
      return result;
    }

    /**
     * Refreshes the list of available options shown
     * in the autocomplete dropdown menu.
     *
     */
    refreshOptions(triggerDropdown = true) {
      var i, j, k, n, optgroup, optgroups, html, has_create_option, active_group;
      var create;
      const groups = {};
      const groups_order = [];
      var self = this;
      var query = self.inputValue();
      const same_query = query === self.lastQuery || query == '' && self.lastQuery == null;
      var results = self.search(query);
      var active_option = null;
      var show_dropdown = self.settings.shouldOpen || false;
      var dropdown_content = self.dropdown_content;
      if (same_query) {
        active_option = self.activeOption;
        if (active_option) {
          active_group = active_option.closest('[data-group]');
        }
      }

      // build markup
      n = results.items.length;
      if (typeof self.settings.maxOptions === 'number') {
        n = Math.min(n, self.settings.maxOptions);
      }
      if (n > 0) {
        show_dropdown = true;
      }

      // render and group available options individually
      for (i = 0; i < n; i++) {
        // get option dom element
        let item = results.items[i];
        if (!item) continue;
        let opt_value = item.id;
        let option = self.options[opt_value];
        if (option === undefined) continue;
        let opt_hash = get_hash$1(opt_value);
        let option_el = self.getOption(opt_hash, true);

        // toggle 'selected' class
        if (!self.settings.hideSelected) {
          option_el.classList.toggle('selected', self.items.includes(opt_hash));
        }
        optgroup = option[self.settings.optgroupField] || '';
        optgroups = Array.isArray(optgroup) ? optgroup : [optgroup];
        for (j = 0, k = optgroups && optgroups.length; j < k; j++) {
          optgroup = optgroups[j];
          if (!self.optgroups.hasOwnProperty(optgroup)) {
            optgroup = '';
          }
          let group_fragment = groups[optgroup];
          if (group_fragment === undefined) {
            group_fragment = document.createDocumentFragment();
            groups_order.push(optgroup);
          }

          // nodes can only have one parent, so if the option is in mutple groups, we need a clone
          if (j > 0) {
            option_el = option_el.cloneNode(true);
            setAttr(option_el, {
              id: option.$id + '-clone-' + j,
              'aria-selected': null
            });
            option_el.classList.add('ts-cloned');
            removeClasses(option_el, 'active');

            // make sure we keep the activeOption in the same group
            if (self.activeOption && self.activeOption.dataset.value == opt_value) {
              if (active_group && active_group.dataset.group === optgroup.toString()) {
                active_option = option_el;
              }
            }
          }
          group_fragment.appendChild(option_el);
          groups[optgroup] = group_fragment;
        }
      }

      // sort optgroups
      if (self.settings.lockOptgroupOrder) {
        groups_order.sort((a, b) => {
          const grp_a = self.optgroups[a];
          const grp_b = self.optgroups[b];
          const a_order = grp_a && grp_a.$order || 0;
          const b_order = grp_b && grp_b.$order || 0;
          return a_order - b_order;
        });
      }

      // render optgroup headers & join groups
      html = document.createDocumentFragment();
      iterate$1(groups_order, optgroup => {
        let group_fragment = groups[optgroup];
        if (!group_fragment || !group_fragment.children.length) return;
        let group_heading = self.optgroups[optgroup];
        if (group_heading !== undefined) {
          let group_options = document.createDocumentFragment();
          let header = self.render('optgroup_header', group_heading);
          append(group_options, header);
          append(group_options, group_fragment);
          let group_html = self.render('optgroup', {
            group: group_heading,
            options: group_options
          });
          append(html, group_html);
        } else {
          append(html, group_fragment);
        }
      });
      dropdown_content.innerHTML = '';
      append(dropdown_content, html);

      // highlight matching terms inline
      if (self.settings.highlight) {
        removeHighlight(dropdown_content);
        if (results.query.length && results.tokens.length) {
          iterate$1(results.tokens, tok => {
            highlight(dropdown_content, tok.regex);
          });
        }
      }

      // helper method for adding templates to dropdown
      var add_template = template => {
        let content = self.render(template, {
          input: query
        });
        if (content) {
          show_dropdown = true;
          dropdown_content.insertBefore(content, dropdown_content.firstChild);
        }
        return content;
      };

      // add loading message
      if (self.loading) {
        add_template('loading');

        // invalid query
      } else if (!self.settings.shouldLoad.call(self, query)) {
        add_template('not_loading');

        // add no_results message
      } else if (results.items.length === 0) {
        add_template('no_results');
      }

      // add create option
      has_create_option = self.canCreate(query);
      if (has_create_option) {
        create = add_template('option_create');
      }

      // activate
      self.hasOptions = results.items.length > 0 || has_create_option;
      if (show_dropdown) {
        if (results.items.length > 0) {
          if (!active_option && self.settings.mode === 'single' && self.items[0] != undefined) {
            active_option = self.getOption(self.items[0]);
          }
          if (!dropdown_content.contains(active_option)) {
            let active_index = 0;
            if (create && !self.settings.addPrecedence) {
              active_index = 1;
            }
            active_option = self.selectable()[active_index];
          }
        } else if (create) {
          active_option = create;
        }
        if (triggerDropdown && !self.isOpen) {
          self.open();
          self.scrollToOption(active_option, 'auto');
        }
        self.setActiveOption(active_option);
      } else {
        self.clearActiveOption();
        if (triggerDropdown && self.isOpen) {
          self.close(false); // if create_option=null, we want the dropdown to close but not reset the textbox value
        }
      }
    }

    /**
     * Return list of selectable options
     *
     */
    selectable() {
      return this.dropdown_content.querySelectorAll('[data-selectable]');
    }

    /**
     * Adds an available option. If it already exists,
     * nothing will happen. Note: this does not refresh
     * the options list dropdown (use `refreshOptions`
     * for that).
     *
     * Usage:
     *
     *   this.addOption(data)
     *
     */
    addOption(data, user_created = false) {
      const self = this;

      // @deprecated 1.7.7
      // use addOptions( array, user_created ) for adding multiple options
      if (Array.isArray(data)) {
        self.addOptions(data, user_created);
        return false;
      }
      const key = hash_key$1(data[self.settings.valueField]);
      if (key === null || self.options.hasOwnProperty(key)) {
        return false;
      }
      data.$order = data.$order || ++self.order;
      data.$id = self.inputId + '-opt-' + data.$order;
      self.options[key] = data;
      self.lastQuery = null;
      if (user_created) {
        self.userOptions[key] = user_created;
        self.trigger('option_add', key, data);
      }
      return key;
    }

    /**
     * Add multiple options
     *
     */
    addOptions(data, user_created = false) {
      iterate$1(data, dat => {
        this.addOption(dat, user_created);
      });
    }

    /**
     * @deprecated 1.7.7
     */
    registerOption(data) {
      return this.addOption(data);
    }

    /**
     * Registers an option group to the pool of option groups.
     *
     * @return {boolean|string}
     */
    registerOptionGroup(data) {
      var key = hash_key$1(data[this.settings.optgroupValueField]);
      if (key === null) return false;
      data.$order = data.$order || ++this.order;
      this.optgroups[key] = data;
      return key;
    }

    /**
     * Registers a new optgroup for options
     * to be bucketed into.
     *
     */
    addOptionGroup(id, data) {
      var hashed_id;
      data[this.settings.optgroupValueField] = id;
      if (hashed_id = this.registerOptionGroup(data)) {
        this.trigger('optgroup_add', hashed_id, data);
      }
    }

    /**
     * Removes an existing option group.
     *
     */
    removeOptionGroup(id) {
      if (this.optgroups.hasOwnProperty(id)) {
        delete this.optgroups[id];
        this.clearCache();
        this.trigger('optgroup_remove', id);
      }
    }

    /**
     * Clears all existing option groups.
     */
    clearOptionGroups() {
      this.optgroups = {};
      this.clearCache();
      this.trigger('optgroup_clear');
    }

    /**
     * Updates an option available for selection. If
     * it is visible in the selected items or options
     * dropdown, it will be re-rendered automatically.
     *
     */
    updateOption(value, data) {
      const self = this;
      var item_new;
      var index_item;
      const value_old = hash_key$1(value);
      const value_new = hash_key$1(data[self.settings.valueField]);

      // sanity checks
      if (value_old === null) return;
      const data_old = self.options[value_old];
      if (data_old == undefined) return;
      if (typeof value_new !== 'string') throw new Error('Value must be set in option data');
      const option = self.getOption(value_old);
      const item = self.getItem(value_old);
      data.$order = data.$order || data_old.$order;
      delete self.options[value_old];

      // invalidate render cache
      // don't remove existing node yet, we'll remove it after replacing it
      self.uncacheValue(value_new);
      self.options[value_new] = data;

      // update the option if it's in the dropdown
      if (option) {
        if (self.dropdown_content.contains(option)) {
          const option_new = self._render('option', data);
          replaceNode(option, option_new);
          if (self.activeOption === option) {
            self.setActiveOption(option_new);
          }
        }
        option.remove();
      }

      // update the item if we have one
      if (item) {
        index_item = self.items.indexOf(value_old);
        if (index_item !== -1) {
          self.items.splice(index_item, 1, value_new);
        }
        item_new = self._render('item', data);
        if (item.classList.contains('active')) addClasses$1(item_new, 'active');
        replaceNode(item, item_new);
      }

      // invalidate last query because we might have updated the sortField
      self.lastQuery = null;
    }

    /**
     * Removes a single option.
     *
     */
    removeOption(value, silent) {
      const self = this;
      value = get_hash$1(value);
      self.uncacheValue(value);
      delete self.userOptions[value];
      delete self.options[value];
      self.lastQuery = null;
      self.trigger('option_remove', value);
      self.removeItem(value, silent);
    }

    /**
     * Clears all options.
     */
    clearOptions(filter) {
      const boundFilter = (filter || this.clearFilter).bind(this);
      this.loadedSearches = {};
      this.userOptions = {};
      this.clearCache();
      const selected = {};
      iterate$1(this.options, (option, key) => {
        if (boundFilter(option, key)) {
          selected[key] = option;
        }
      });
      this.options = this.sifter.items = selected;
      this.lastQuery = null;
      this.trigger('option_clear');
    }

    /**
     * Used by clearOptions() to decide whether or not an option should be removed
     * Return true to keep an option, false to remove
     *
     */
    clearFilter(option, value) {
      if (this.items.indexOf(value) >= 0) {
        return true;
      }
      return false;
    }

    /**
     * Returns the dom element of the option
     * matching the given value.
     *
     */
    getOption(value, create = false) {
      const hashed = hash_key$1(value);
      if (hashed === null) return null;
      const option = this.options[hashed];
      if (option != undefined) {
        if (option.$div) {
          return option.$div;
        }
        if (create) {
          return this._render('option', option);
        }
      }
      return null;
    }

    /**
     * Returns the dom element of the next or previous dom element of the same type
     * Note: adjacent options may not be adjacent DOM elements (optgroups)
     *
     */
    getAdjacent(option, direction, type = 'option') {
      var self = this,
        all;
      if (!option) {
        return null;
      }
      if (type == 'item') {
        all = self.controlChildren();
      } else {
        all = self.dropdown_content.querySelectorAll('[data-selectable]');
      }
      for (let i = 0; i < all.length; i++) {
        if (all[i] != option) {
          continue;
        }
        if (direction > 0) {
          return all[i + 1];
        }
        return all[i - 1];
      }
      return null;
    }

    /**
     * Returns the dom element of the item
     * matching the given value.
     *
     */
    getItem(item) {
      if (typeof item == 'object') {
        return item;
      }
      var value = hash_key$1(item);
      return value !== null ? this.control.querySelector(`[data-value="${addSlashes(value)}"]`) : null;
    }

    /**
     * "Selects" multiple items at once. Adds them to the list
     * at the current caret position.
     *
     */
    addItems(values, silent) {
      var self = this;
      var items = Array.isArray(values) ? values : [values];
      items = items.filter(x => self.items.indexOf(x) === -1);
      const last_item = items[items.length - 1];
      items.forEach(item => {
        self.isPending = item !== last_item;
        self.addItem(item, silent);
      });
    }

    /**
     * "Selects" an item. Adds it to the list
     * at the current caret position.
     *
     */
    addItem(value, silent) {
      var events = silent ? [] : ['change', 'dropdown_close'];
      debounce_events(this, events, () => {
        var item, wasFull;
        const self = this;
        const inputMode = self.settings.mode;
        const hashed = hash_key$1(value);
        if (hashed && self.items.indexOf(hashed) !== -1) {
          if (inputMode === 'single') {
            self.close();
          }
          if (inputMode === 'single' || !self.settings.duplicates) {
            return;
          }
        }
        if (hashed === null || !self.options.hasOwnProperty(hashed)) return;
        if (inputMode === 'single') self.clear(silent);
        if (inputMode === 'multi' && self.isFull()) return;
        item = self._render('item', self.options[hashed]);
        if (self.control.contains(item)) {
          // duplicates
          item = item.cloneNode(true);
        }
        wasFull = self.isFull();
        self.items.splice(self.caretPos, 0, hashed);
        self.insertAtCaret(item);
        if (self.isSetup) {
          // update menu / remove the option (if this is not one item being added as part of series)
          if (!self.isPending && self.settings.hideSelected) {
            let option = self.getOption(hashed);
            let next = self.getAdjacent(option, 1);
            if (next) {
              self.setActiveOption(next);
            }
          }

          // refreshOptions after setActiveOption(),
          // otherwise setActiveOption() will be called by refreshOptions() with the wrong value
          if (!self.isPending && !self.settings.closeAfterSelect) {
            self.refreshOptions(self.isFocused && inputMode !== 'single');
          }

          // hide the menu if the maximum number of items have been selected or no options are left
          if (self.settings.closeAfterSelect != false && self.isFull()) {
            self.close();
          } else if (!self.isPending) {
            self.positionDropdown();
          }
          self.trigger('item_add', hashed, item);
          if (!self.isPending) {
            self.updateOriginalInput({
              silent: silent
            });
          }
        }
        if (!self.isPending || !wasFull && self.isFull()) {
          self.inputState();
          self.refreshState();
        }
      });
    }

    /**
     * Removes the selected item matching
     * the provided value.
     *
     */
    removeItem(item = null, silent) {
      const self = this;
      item = self.getItem(item);
      if (!item) return;
      var i, idx;
      const value = item.dataset.value;
      i = nodeIndex(item);
      item.remove();
      if (item.classList.contains('active')) {
        idx = self.activeItems.indexOf(item);
        self.activeItems.splice(idx, 1);
        removeClasses(item, 'active');
      }
      self.items.splice(i, 1);
      self.lastQuery = null;
      if (!self.settings.persist && self.userOptions.hasOwnProperty(value)) {
        self.removeOption(value, silent);
      }
      if (i < self.caretPos) {
        self.setCaret(self.caretPos - 1);
      }
      self.updateOriginalInput({
        silent: silent
      });
      self.refreshState();
      self.positionDropdown();
      self.trigger('item_remove', value, item);
    }

    /**
     * Invokes the `create` method provided in the
     * TomSelect options that should provide the data
     * for the new item, given the user input.
     *
     * Once this completes, it will be added
     * to the item list.
     *
     */
    createItem(input = null, callback = () => {}) {
      // triggerDropdown parameter @deprecated 2.1.1
      if (arguments.length === 3) {
        callback = arguments[2];
      }
      if (typeof callback != 'function') {
        callback = () => {};
      }
      var self = this;
      var caret = self.caretPos;
      var output;
      input = input || self.inputValue();
      if (!self.canCreate(input)) {
        callback();
        return false;
      }
      self.lock();
      var created = false;
      var create = data => {
        self.unlock();
        if (!data || typeof data !== 'object') return callback();
        var value = hash_key$1(data[self.settings.valueField]);
        if (typeof value !== 'string') {
          return callback();
        }
        self.setTextboxValue();
        self.addOption(data, true);
        self.setCaret(caret);
        self.addItem(value);
        callback(data);
        created = true;
      };
      if (typeof self.settings.create === 'function') {
        output = self.settings.create.call(this, input, create);
      } else {
        output = {
          [self.settings.labelField]: input,
          [self.settings.valueField]: input
        };
      }
      if (!created) {
        create(output);
      }
      return true;
    }

    /**
     * Re-renders the selected item lists.
     */
    refreshItems() {
      var self = this;
      self.lastQuery = null;
      if (self.isSetup) {
        self.addItems(self.items);
      }
      self.updateOriginalInput();
      self.refreshState();
    }

    /**
     * Updates all state-dependent attributes
     * and CSS classes.
     */
    refreshState() {
      const self = this;
      self.refreshValidityState();
      const isFull = self.isFull();
      const isLocked = self.isLocked;
      self.wrapper.classList.toggle('rtl', self.rtl);
      const wrap_classList = self.wrapper.classList;
      wrap_classList.toggle('focus', self.isFocused);
      wrap_classList.toggle('disabled', self.isDisabled);
      wrap_classList.toggle('required', self.isRequired);
      wrap_classList.toggle('invalid', !self.isValid);
      wrap_classList.toggle('locked', isLocked);
      wrap_classList.toggle('full', isFull);
      wrap_classList.toggle('input-active', self.isFocused && !self.isInputHidden);
      wrap_classList.toggle('dropdown-active', self.isOpen);
      wrap_classList.toggle('has-options', isEmptyObject(self.options));
      wrap_classList.toggle('has-items', self.items.length > 0);
    }

    /**
     * Update the `required` attribute of both input and control input.
     *
     * The `required` property needs to be activated on the control input
     * for the error to be displayed at the right place. `required` also
     * needs to be temporarily deactivated on the input since the input is
     * hidden and can't show errors.
     */
    refreshValidityState() {
      var self = this;
      if (!self.input.validity) {
        return;
      }
      self.isValid = self.input.validity.valid;
      self.isInvalid = !self.isValid;
    }

    /**
     * Determines whether or not more items can be added
     * to the control without exceeding the user-defined maximum.
     *
     * @returns {boolean}
     */
    isFull() {
      return this.settings.maxItems !== null && this.items.length >= this.settings.maxItems;
    }

    /**
     * Refreshes the original <select> or <input>
     * element to reflect the current state.
     *
     */
    updateOriginalInput(opts = {}) {
      const self = this;
      var option, label;
      const empty_option = self.input.querySelector('option[value=""]');
      if (self.is_select_tag) {
        const selected = [];
        const has_selected = self.input.querySelectorAll('option:checked').length;
        function AddSelected(option_el, value, label) {
          if (!option_el) {
            option_el = getDom$5('<option value="' + escape_html$1(value) + '">' + escape_html$1(label) + '</option>');
          }

          // don't move empty option from top of list
          // fixes bug in firefox https://bugzilla.mozilla.org/show_bug.cgi?id=1725293
          if (option_el != empty_option) {
            self.input.append(option_el);
          }
          selected.push(option_el);

          // marking empty option as selected can break validation
          // fixes https://github.com/orchidjs/tom-select/issues/303
          if (option_el != empty_option || has_selected > 0) {
            option_el.selected = true;
          }
          return option_el;
        }

        // unselect all selected options
        self.input.querySelectorAll('option:checked').forEach(option_el => {
          option_el.selected = false;
        });

        // nothing selected?
        if (self.items.length == 0 && self.settings.mode == 'single') {
          AddSelected(empty_option, "", "");

          // order selected <option> tags for values in self.items
        } else {
          self.items.forEach(value => {
            option = self.options[value];
            label = option[self.settings.labelField] || '';
            if (selected.includes(option.$option)) {
              const reuse_opt = self.input.querySelector(`option[value="${addSlashes(value)}"]:not(:checked)`);
              AddSelected(reuse_opt, value, label);
            } else {
              option.$option = AddSelected(option.$option, value, label);
            }
          });
        }
      } else {
        self.input.value = self.getValue();
      }
      if (self.isSetup) {
        if (!opts.silent) {
          self.trigger('change', self.getValue());
        }
      }
    }

    /**
     * Shows the autocomplete dropdown containing
     * the available options.
     */
    open() {
      var self = this;
      if (self.isLocked || self.isOpen || self.settings.mode === 'multi' && self.isFull()) return;
      self.isOpen = true;
      setAttr(self.focus_node, {
        'aria-expanded': 'true'
      });
      self.refreshState();
      applyCSS(self.dropdown, {
        visibility: 'hidden',
        display: 'block'
      });
      self.positionDropdown();
      applyCSS(self.dropdown, {
        visibility: 'visible',
        display: 'block'
      });
      self.focus();
      self.trigger('dropdown_open', self.dropdown);
    }

    /**
     * Closes the autocomplete dropdown menu.
     */
    close(setTextboxValue = true) {
      var self = this;
      var trigger = self.isOpen;
      if (setTextboxValue) {
        // before blur() to prevent form onchange event
        self.setTextboxValue();
        if (self.settings.mode === 'single' && self.items.length) {
          self.hideInput();
        }
      }
      self.isOpen = false;
      setAttr(self.focus_node, {
        'aria-expanded': 'false'
      });
      applyCSS(self.dropdown, {
        display: 'none'
      });
      if (self.settings.hideSelected) {
        self.clearActiveOption();
      }
      self.refreshState();
      if (trigger) self.trigger('dropdown_close', self.dropdown);
    }

    /**
     * Calculates and applies the appropriate
     * position of the dropdown if dropdownParent = 'body'.
     * Otherwise, position is determined by css
     */
    positionDropdown() {
      if (this.settings.dropdownParent !== 'body') {
        return;
      }
      var context = this.control;
      var rect = context.getBoundingClientRect();
      var top = context.offsetHeight + rect.top + window.scrollY;
      var left = rect.left + window.scrollX;
      applyCSS(this.dropdown, {
        width: rect.width + 'px',
        top: top + 'px',
        left: left + 'px'
      });
    }

    /**
     * Resets / clears all selected items
     * from the control.
     *
     */
    clear(silent) {
      var self = this;
      if (!self.items.length) return;
      var items = self.controlChildren();
      iterate$1(items, item => {
        self.removeItem(item, true);
      });
      self.showInput();
      if (!silent) self.updateOriginalInput();
      self.trigger('clear');
    }

    /**
     * A helper method for inserting an element
     * at the current caret position.
     *
     */
    insertAtCaret(el) {
      const self = this;
      const caret = self.caretPos;
      const target = self.control;
      target.insertBefore(el, target.children[caret] || null);
      self.setCaret(caret + 1);
    }

    /**
     * Removes the current selected item(s).
     *
     */
    deleteSelection(e) {
      var direction, selection, caret, tail;
      var self = this;
      direction = e && e.keyCode === KEY_BACKSPACE ? -1 : 1;
      selection = getSelection(self.control_input);

      // determine items that will be removed
      const rm_items = [];
      if (self.activeItems.length) {
        tail = getTail(self.activeItems, direction);
        caret = nodeIndex(tail);
        if (direction > 0) {
          caret++;
        }
        iterate$1(self.activeItems, item => rm_items.push(item));
      } else if ((self.isFocused || self.settings.mode === 'single') && self.items.length) {
        const items = self.controlChildren();
        let rm_item;
        if (direction < 0 && selection.start === 0 && selection.length === 0) {
          rm_item = items[self.caretPos - 1];
        } else if (direction > 0 && selection.start === self.inputValue().length) {
          rm_item = items[self.caretPos];
        }
        if (rm_item !== undefined) {
          rm_items.push(rm_item);
        }
      }
      if (!self.shouldDelete(rm_items, e)) {
        return false;
      }
      preventDefault$4(e, true);

      // perform removal
      if (typeof caret !== 'undefined') {
        self.setCaret(caret);
      }
      while (rm_items.length) {
        self.removeItem(rm_items.pop());
      }
      self.showInput();
      self.positionDropdown();
      self.refreshOptions(false);
      return true;
    }

    /**
     * Return true if the items should be deleted
     */
    shouldDelete(items, evt) {
      const values = items.map(item => item.dataset.value);

      // allow the callback to abort
      if (!values.length || typeof this.settings.onDelete === 'function' && this.settings.onDelete(values, evt) === false) {
        return false;
      }
      return true;
    }

    /**
     * Selects the previous / next item (depending on the `direction` argument).
     *
     * > 0 - right
     * < 0 - left
     *
     */
    advanceSelection(direction, e) {
      var last_active,
        adjacent,
        self = this;
      if (self.rtl) direction *= -1;
      if (self.inputValue().length) return;

      // add or remove to active items
      if (isKeyDown(KEY_SHORTCUT, e) || isKeyDown('shiftKey', e)) {
        last_active = self.getLastActive(direction);
        if (last_active) {
          if (!last_active.classList.contains('active')) {
            adjacent = last_active;
          } else {
            adjacent = self.getAdjacent(last_active, direction, 'item');
          }

          // if no active item, get items adjacent to the control input
        } else if (direction > 0) {
          adjacent = self.control_input.nextElementSibling;
        } else {
          adjacent = self.control_input.previousElementSibling;
        }
        if (adjacent) {
          if (adjacent.classList.contains('active')) {
            self.removeActiveItem(last_active);
          }
          self.setActiveItemClass(adjacent); // mark as last_active !! after removeActiveItem() on last_active
        }

        // move caret to the left or right
      } else {
        self.moveCaret(direction);
      }
    }
    moveCaret(direction) {}

    /**
     * Get the last active item
     *
     */
    getLastActive(direction) {
      let last_active = this.control.querySelector('.last-active');
      if (last_active) {
        return last_active;
      }
      var result = this.control.querySelectorAll('.active');
      if (result) {
        return getTail(result, direction);
      }
    }

    /**
     * Moves the caret to the specified index.
     *
     * The input must be moved by leaving it in place and moving the
     * siblings, due to the fact that focus cannot be restored once lost
     * on mobile webkit devices
     *
     */
    setCaret(new_pos) {
      this.caretPos = this.items.length;
    }

    /**
     * Return list of item dom elements
     *
     */
    controlChildren() {
      return Array.from(this.control.querySelectorAll('[data-ts-item]'));
    }

    /**
     * Disables user input on the control. Used while
     * items are being asynchronously created.
     */
    lock() {
      this.isLocked = true;
      this.refreshState();
    }

    /**
     * Re-enables user input on the control.
     */
    unlock() {
      this.isLocked = false;
      this.refreshState();
    }

    /**
     * Disables user input on the control completely.
     * While disabled, it cannot receive focus.
     */
    disable() {
      var self = this;
      self.input.disabled = true;
      self.control_input.disabled = true;
      self.focus_node.tabIndex = -1;
      self.isDisabled = true;
      this.close();
      self.lock();
    }

    /**
     * Enables the control so that it can respond
     * to focus and user input.
     */
    enable() {
      var self = this;
      self.input.disabled = false;
      self.control_input.disabled = false;
      self.focus_node.tabIndex = self.tabIndex;
      self.isDisabled = false;
      self.unlock();
    }

    /**
     * Completely destroys the control and
     * unbinds all event listeners so that it can
     * be garbage collected.
     */
    destroy() {
      var self = this;
      var revertSettings = self.revertSettings;
      self.trigger('destroy');
      self.off();
      self.wrapper.remove();
      self.dropdown.remove();
      self.input.innerHTML = revertSettings.innerHTML;
      self.input.tabIndex = revertSettings.tabIndex;
      removeClasses(self.input, 'tomselected', 'ts-hidden-accessible');
      self._destroy();
      delete self.input.tomselect;
    }

    /**
     * A helper method for rendering "item" and
     * "option" templates, given the data.
     *
     */
    render(templateName, data) {
      var id, html;
      const self = this;
      if (typeof this.settings.render[templateName] !== 'function') {
        return null;
      }

      // render markup
      html = self.settings.render[templateName].call(this, data, escape_html$1);
      if (!html) {
        return null;
      }
      html = getDom$5(html);

      // add mandatory attributes
      if (templateName === 'option' || templateName === 'option_create') {
        if (data[self.settings.disabledField]) {
          setAttr(html, {
            'aria-disabled': 'true'
          });
        } else {
          setAttr(html, {
            'data-selectable': ''
          });
        }
      } else if (templateName === 'optgroup') {
        id = data.group[self.settings.optgroupValueField];
        setAttr(html, {
          'data-group': id
        });
        if (data.group[self.settings.disabledField]) {
          setAttr(html, {
            'data-disabled': ''
          });
        }
      }
      if (templateName === 'option' || templateName === 'item') {
        const value = get_hash$1(data[self.settings.valueField]);
        setAttr(html, {
          'data-value': value
        });

        // make sure we have some classes if a template is overwritten
        if (templateName === 'item') {
          addClasses$1(html, self.settings.itemClass);
          setAttr(html, {
            'data-ts-item': ''
          });
        } else {
          addClasses$1(html, self.settings.optionClass);
          setAttr(html, {
            role: 'option',
            id: data.$id
          });

          // update cache
          data.$div = html;
          self.options[value] = data;
        }
      }
      return html;
    }

    /**
     * Type guarded rendering
     *
     */
    _render(templateName, data) {
      const html = this.render(templateName, data);
      if (html == null) {
        throw 'HTMLElement expected';
      }
      return html;
    }

    /**
     * Clears the render cache for a template. If
     * no template is given, clears all render
     * caches.
     *
     */
    clearCache() {
      iterate$1(this.options, option => {
        if (option.$div) {
          option.$div.remove();
          delete option.$div;
        }
      });
    }

    /**
     * Removes a value from item and option caches
     *
     */
    uncacheValue(value) {
      const option_el = this.getOption(value);
      if (option_el) option_el.remove();
    }

    /**
     * Determines whether or not to display the
     * create item prompt, given a user input.
     *
     */
    canCreate(input) {
      return this.settings.create && input.length > 0 && this.settings.createFilter.call(this, input);
    }

    /**
     * Wraps this.`method` so that `new_fn` can be invoked 'before', 'after', or 'instead' of the original method
     *
     * this.hook('instead','onKeyDown',function( arg1, arg2 ...){
     *
     * });
     */
    hook(when, method, new_fn) {
      var self = this;
      var orig_method = self[method];
      self[method] = function () {
        var result, result_new;
        if (when === 'after') {
          result = orig_method.apply(self, arguments);
        }
        result_new = new_fn.apply(self, arguments);
        if (when === 'instead') {
          return result_new;
        }
        if (when === 'before') {
          result = orig_method.apply(self, arguments);
        }
        return result;
      };
    }
  }

  /**
  * Tom Select v2.2.8
  * Licensed under the Apache License, Version 2.0 (the "License");
  */

  const KEY_ESC = 27;
  const KEY_TAB = 9;
  typeof navigator === 'undefined' ? false : /Mac/.test(navigator.userAgent);
  // ctrl key or apple key for ma

  /*! @orchidjs/unicode-variants | https://github.com/orchidjs/unicode-variants | Apache License (v2) */
  const accent_pat$4 = '[\u0300-\u036F\u{b7}\u{2be}\u{2bc}]';
  /** @type {TUnicodeMap} */

  const latin_convert$4 = {};
  /** @type {TUnicodeMap} */

  const latin_condensed$4 = {
    '/': '⁄∕',
    '0': '߀',
    "a": "ⱥɐɑ",
    "aa": "ꜳ",
    "ae": "æǽǣ",
    "ao": "ꜵ",
    "au": "ꜷ",
    "av": "ꜹꜻ",
    "ay": "ꜽ",
    "b": "ƀɓƃ",
    "c": "ꜿƈȼↄ",
    "d": "đɗɖᴅƌꮷԁɦ",
    "e": "ɛǝᴇɇ",
    "f": "ꝼƒ",
    "g": "ǥɠꞡᵹꝿɢ",
    "h": "ħⱨⱶɥ",
    "i": "ɨı",
    "j": "ɉȷ",
    "k": "ƙⱪꝁꝃꝅꞣ",
    "l": "łƚɫⱡꝉꝇꞁɭ",
    "m": "ɱɯϻ",
    "n": "ꞥƞɲꞑᴎлԉ",
    "o": "øǿɔɵꝋꝍᴑ",
    "oe": "œ",
    "oi": "ƣ",
    "oo": "ꝏ",
    "ou": "ȣ",
    "p": "ƥᵽꝑꝓꝕρ",
    "q": "ꝗꝙɋ",
    "r": "ɍɽꝛꞧꞃ",
    "s": "ßȿꞩꞅʂ",
    "t": "ŧƭʈⱦꞇ",
    "th": "þ",
    "tz": "ꜩ",
    "u": "ʉ",
    "v": "ʋꝟʌ",
    "vy": "ꝡ",
    "w": "ⱳ",
    "y": "ƴɏỿ",
    "z": "ƶȥɀⱬꝣ",
    "hv": "ƕ"
  };
  for (let latin in latin_condensed$4) {
    let unicode = latin_condensed$4[latin] || '';
    for (let i = 0; i < unicode.length; i++) {
      let char = unicode.substring(i, i + 1);
      latin_convert$4[char] = latin;
    }
  }
  new RegExp(Object.keys(latin_convert$4).join('|') + '|' + accent_pat$4, 'gu');

  /**
   * Iterates over arrays and hashes.
   *
   * ```
   * iterate(this.items, function(item, id) {
   *    // invoked for each item
   * });
   * ```
   *
   */
  const iterate = (object, callback) => {
    if (Array.isArray(object)) {
      object.forEach(callback);
    } else {
      for (var key in object) {
        if (object.hasOwnProperty(key)) {
          callback(object[key], key);
        }
      }
    }
  };

  /**
   * Return a dom element from either a dom query string, jQuery object, a dom element or html string
   * https://stackoverflow.com/questions/494143/creating-a-new-dom-element-from-an-html-string-using-built-in-dom-methods-or-pro/35385518#35385518
   *
   * param query should be {}
   */
  const getDom$4 = query => {
    if (query.jquery) {
      return query[0];
    }
    if (query instanceof HTMLElement) {
      return query;
    }
    if (isHtmlString$4(query)) {
      var tpl = document.createElement('template');
      tpl.innerHTML = query.trim(); // Never return a text node of whitespace as the result
      return tpl.content.firstChild;
    }
    return document.querySelector(query);
  };
  const isHtmlString$4 = arg => {
    if (typeof arg === 'string' && arg.indexOf('<') > -1) {
      return true;
    }
    return false;
  };

  /**
   * Add css classes
   *
   */
  const addClasses = (elmts, ...classes) => {
    var norm_classes = classesArray(classes);
    elmts = castAsArray(elmts);
    elmts.map(el => {
      norm_classes.map(cls => {
        el.classList.add(cls);
      });
    });
  };

  /**
   * Return arguments
   *
   */
  const classesArray = args => {
    var classes = [];
    iterate(args, _classes => {
      if (typeof _classes === 'string') {
        _classes = _classes.trim().split(/[\11\12\14\15\40]/);
      }
      if (Array.isArray(_classes)) {
        classes = classes.concat(_classes);
      }
    });
    return classes.filter(Boolean);
  };

  /**
   * Create an array from arg if it's not already an array
   *
   */
  const castAsArray = arg => {
    if (!Array.isArray(arg)) {
      arg = [arg];
    }
    return arg;
  };

  /**
   * Converts a scalar to its best string representation
   * for hash keys and HTML attribute values.
   *
   * Transformations:
   *   'str'     -> 'str'
   *   null      -> ''
   *   undefined -> ''
   *   true      -> '1'
   *   false     -> '0'
   *   0         -> '0'
   *   1         -> '1'
   *
   */

  /**
   * Prevent default
   *
   */
  const preventDefault$3 = (evt, stop = false) => {
    if (evt) {
      evt.preventDefault();
      if (stop) {
        evt.stopPropagation();
      }
    }
  };

  /**
   * Add event helper
   *
   */
  const addEvent$1 = (target, type, callback, options) => {
    target.addEventListener(type, callback, options);
  };

  /**
   * Plugin: "dropdown_input" (Tom Select)
   * Copyright (c) contributors
   *
   * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this
   * file except in compliance with the License. You may obtain a copy of the License at:
   * http://www.apache.org/licenses/LICENSE-2.0
   *
   * Unless required by applicable law or agreed to in writing, software distributed under
   * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
   * ANY KIND, either express or implied. See the License for the specific language
   * governing permissions and limitations under the License.
   *
   */
  function plugin$4() {
    const self = this;
    self.settings.shouldOpen = true; // make sure the input is shown even if there are no options to display in the dropdown

    self.hook('before', 'setup', () => {
      self.focus_node = self.control;
      addClasses(self.control_input, 'dropdown-input');
      const div = getDom$4('<div class="dropdown-input-wrap">');
      div.append(self.control_input);
      self.dropdown.insertBefore(div, self.dropdown.firstChild);

      // set a placeholder in the select control
      const placeholder = getDom$4('<input class="items-placeholder" tabindex="-1" />');
      placeholder.placeholder = self.settings.placeholder || '';
      self.control.append(placeholder);
    });
    self.on('initialize', () => {
      // set tabIndex on control to -1, otherwise [shift+tab] will put focus right back on control_input
      self.control_input.addEventListener('keydown', evt => {
        //addEvent(self.control_input,'keydown' as const,(evt:KeyboardEvent) =>{
        switch (evt.keyCode) {
          case KEY_ESC:
            if (self.isOpen) {
              preventDefault$3(evt, true);
              self.close();
            }
            self.clearActiveItems();
            return;
          case KEY_TAB:
            self.focus_node.tabIndex = -1;
            break;
        }
        return self.onKeyDown.call(self, evt);
      });
      self.on('blur', () => {
        self.focus_node.tabIndex = self.isDisabled ? -1 : self.tabIndex;
      });

      // give the control_input focus when the dropdown is open
      self.on('dropdown_open', () => {
        self.control_input.focus();
      });

      // prevent onBlur from closing when focus is on the control_input
      const orig_onBlur = self.onBlur;
      self.hook('instead', 'onBlur', evt => {
        if (evt && evt.relatedTarget == self.control_input) return;
        return orig_onBlur.call(self);
      });
      addEvent$1(self.control_input, 'blur', () => self.onBlur());

      // return focus to control to allow further keyboard input
      self.hook('before', 'close', () => {
        if (!self.isOpen) return;
        self.focus_node.focus({
          preventScroll: true
        });
      });
    });
  }

  /**
  * Tom Select v2.2.8
  * Licensed under the Apache License, Version 2.0 (the "License");
  */

  /**
   * Converts a scalar to its best string representation
   * for hash keys and HTML attribute values.
   *
   * Transformations:
   *   'str'     -> 'str'
   *   null      -> ''
   *   undefined -> ''
   *   true      -> '1'
   *   false     -> '0'
   *   0         -> '0'
   *   1         -> '1'
   *
   */
  const hash_key = value => {
    if (typeof value === 'undefined' || value === null) return null;
    return get_hash(value);
  };
  const get_hash = value => {
    if (typeof value === 'boolean') return value ? '1' : '0';
    return value + '';
  };

  /**
   * Prevent default
   *
   */
  const preventDefault$2 = (evt, stop = false) => {
    if (evt) {
      evt.preventDefault();
      if (stop) {
        evt.stopPropagation();
      }
    }
  };

  /*! @orchidjs/unicode-variants | https://github.com/orchidjs/unicode-variants | Apache License (v2) */
  const accent_pat$3 = '[\u0300-\u036F\u{b7}\u{2be}\u{2bc}]';
  /** @type {TUnicodeMap} */

  const latin_convert$3 = {};
  /** @type {TUnicodeMap} */

  const latin_condensed$3 = {
    '/': '⁄∕',
    '0': '߀',
    "a": "ⱥɐɑ",
    "aa": "ꜳ",
    "ae": "æǽǣ",
    "ao": "ꜵ",
    "au": "ꜷ",
    "av": "ꜹꜻ",
    "ay": "ꜽ",
    "b": "ƀɓƃ",
    "c": "ꜿƈȼↄ",
    "d": "đɗɖᴅƌꮷԁɦ",
    "e": "ɛǝᴇɇ",
    "f": "ꝼƒ",
    "g": "ǥɠꞡᵹꝿɢ",
    "h": "ħⱨⱶɥ",
    "i": "ɨı",
    "j": "ɉȷ",
    "k": "ƙⱪꝁꝃꝅꞣ",
    "l": "łƚɫⱡꝉꝇꞁɭ",
    "m": "ɱɯϻ",
    "n": "ꞥƞɲꞑᴎлԉ",
    "o": "øǿɔɵꝋꝍᴑ",
    "oe": "œ",
    "oi": "ƣ",
    "oo": "ꝏ",
    "ou": "ȣ",
    "p": "ƥᵽꝑꝓꝕρ",
    "q": "ꝗꝙɋ",
    "r": "ɍɽꝛꞧꞃ",
    "s": "ßȿꞩꞅʂ",
    "t": "ŧƭʈⱦꞇ",
    "th": "þ",
    "tz": "ꜩ",
    "u": "ʉ",
    "v": "ʋꝟʌ",
    "vy": "ꝡ",
    "w": "ⱳ",
    "y": "ƴɏỿ",
    "z": "ƶȥɀⱬꝣ",
    "hv": "ƕ"
  };
  for (let latin in latin_condensed$3) {
    let unicode = latin_condensed$3[latin] || '';
    for (let i = 0; i < unicode.length; i++) {
      let char = unicode.substring(i, i + 1);
      latin_convert$3[char] = latin;
    }
  }
  new RegExp(Object.keys(latin_convert$3).join('|') + '|' + accent_pat$3, 'gu');

  /**
   * Return a dom element from either a dom query string, jQuery object, a dom element or html string
   * https://stackoverflow.com/questions/494143/creating-a-new-dom-element-from-an-html-string-using-built-in-dom-methods-or-pro/35385518#35385518
   *
   * param query should be {}
   */
  const getDom$3 = query => {
    if (query.jquery) {
      return query[0];
    }
    if (query instanceof HTMLElement) {
      return query;
    }
    if (isHtmlString$3(query)) {
      var tpl = document.createElement('template');
      tpl.innerHTML = query.trim(); // Never return a text node of whitespace as the result
      return tpl.content.firstChild;
    }
    return document.querySelector(query);
  };
  const isHtmlString$3 = arg => {
    if (typeof arg === 'string' && arg.indexOf('<') > -1) {
      return true;
    }
    return false;
  };

  /**
   * Plugin: "restore_on_backspace" (Tom Select)
   * Copyright (c) contributors
   *
   * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this
   * file except in compliance with the License. You may obtain a copy of the License at:
   * http://www.apache.org/licenses/LICENSE-2.0
   *
   * Unless required by applicable law or agreed to in writing, software distributed under
   * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
   * ANY KIND, either express or implied. See the License for the specific language
   * governing permissions and limitations under the License.
   *
   */
  function plugin$3() {
    var self = this;
    var orig_onOptionSelect = self.onOptionSelect;
    self.settings.hideSelected = false;

    // update the checkbox for an option
    var UpdateCheckbox = function UpdateCheckbox(option) {
      setTimeout(() => {
        var checkbox = option.querySelector('input');
        if (checkbox instanceof HTMLInputElement) {
          if (option.classList.contains('selected')) {
            checkbox.checked = true;
          } else {
            checkbox.checked = false;
          }
        }
      }, 1);
    };

    // add checkbox to option template
    self.hook('after', 'setupTemplates', () => {
      var orig_render_option = self.settings.render.option;
      self.settings.render.option = (data, escape_html) => {
        var rendered = getDom$3(orig_render_option.call(self, data, escape_html));
        var checkbox = document.createElement('input');
        checkbox.addEventListener('click', function (evt) {
          preventDefault$2(evt);
        });
        checkbox.type = 'checkbox';
        const hashed = hash_key(data[self.settings.valueField]);
        if (hashed && self.items.indexOf(hashed) > -1) {
          checkbox.checked = true;
        }
        rendered.prepend(checkbox);
        return rendered;
      };
    });

    // uncheck when item removed
    self.on('item_remove', value => {
      var option = self.getOption(value);
      if (option) {
        // if dropdown hasn't been opened yet, the option won't exist
        option.classList.remove('selected'); // selected class won't be removed yet
        UpdateCheckbox(option);
      }
    });

    // check when item added
    self.on('item_add', value => {
      var option = self.getOption(value);
      if (option) {
        // if dropdown hasn't been opened yet, the option won't exist
        UpdateCheckbox(option);
      }
    });

    // remove items when selected option is clicked
    self.hook('instead', 'onOptionSelect', (evt, option) => {
      if (option.classList.contains('selected')) {
        option.classList.remove('selected');
        self.removeItem(option.dataset.value);
        self.refreshOptions();
        preventDefault$2(evt, true);
        return;
      }
      orig_onOptionSelect.call(self, evt, option);
      UpdateCheckbox(option);
    });
  }

  /**
  * Tom Select v2.2.8
  * Licensed under the Apache License, Version 2.0 (the "License");
  */

  /*! @orchidjs/unicode-variants | https://github.com/orchidjs/unicode-variants | Apache License (v2) */
  const accent_pat$2 = '[\u0300-\u036F\u{b7}\u{2be}\u{2bc}]';
  /** @type {TUnicodeMap} */

  const latin_convert$2 = {};
  /** @type {TUnicodeMap} */

  const latin_condensed$2 = {
    '/': '⁄∕',
    '0': '߀',
    "a": "ⱥɐɑ",
    "aa": "ꜳ",
    "ae": "æǽǣ",
    "ao": "ꜵ",
    "au": "ꜷ",
    "av": "ꜹꜻ",
    "ay": "ꜽ",
    "b": "ƀɓƃ",
    "c": "ꜿƈȼↄ",
    "d": "đɗɖᴅƌꮷԁɦ",
    "e": "ɛǝᴇɇ",
    "f": "ꝼƒ",
    "g": "ǥɠꞡᵹꝿɢ",
    "h": "ħⱨⱶɥ",
    "i": "ɨı",
    "j": "ɉȷ",
    "k": "ƙⱪꝁꝃꝅꞣ",
    "l": "łƚɫⱡꝉꝇꞁɭ",
    "m": "ɱɯϻ",
    "n": "ꞥƞɲꞑᴎлԉ",
    "o": "øǿɔɵꝋꝍᴑ",
    "oe": "œ",
    "oi": "ƣ",
    "oo": "ꝏ",
    "ou": "ȣ",
    "p": "ƥᵽꝑꝓꝕρ",
    "q": "ꝗꝙɋ",
    "r": "ɍɽꝛꞧꞃ",
    "s": "ßȿꞩꞅʂ",
    "t": "ŧƭʈⱦꞇ",
    "th": "þ",
    "tz": "ꜩ",
    "u": "ʉ",
    "v": "ʋꝟʌ",
    "vy": "ꝡ",
    "w": "ⱳ",
    "y": "ƴɏỿ",
    "z": "ƶȥɀⱬꝣ",
    "hv": "ƕ"
  };
  for (let latin in latin_condensed$2) {
    let unicode = latin_condensed$2[latin] || '';
    for (let i = 0; i < unicode.length; i++) {
      let char = unicode.substring(i, i + 1);
      latin_convert$2[char] = latin;
    }
  }
  new RegExp(Object.keys(latin_convert$2).join('|') + '|' + accent_pat$2, 'gu');

  /**
   * Return a dom element from either a dom query string, jQuery object, a dom element or html string
   * https://stackoverflow.com/questions/494143/creating-a-new-dom-element-from-an-html-string-using-built-in-dom-methods-or-pro/35385518#35385518
   *
   * param query should be {}
   */
  const getDom$2 = query => {
    if (query.jquery) {
      return query[0];
    }
    if (query instanceof HTMLElement) {
      return query;
    }
    if (isHtmlString$2(query)) {
      var tpl = document.createElement('template');
      tpl.innerHTML = query.trim(); // Never return a text node of whitespace as the result
      return tpl.content.firstChild;
    }
    return document.querySelector(query);
  };
  const isHtmlString$2 = arg => {
    if (typeof arg === 'string' && arg.indexOf('<') > -1) {
      return true;
    }
    return false;
  };

  /**
   * Converts a scalar to its best string representation
   * for hash keys and HTML attribute values.
   *
   * Transformations:
   *   'str'     -> 'str'
   *   null      -> ''
   *   undefined -> ''
   *   true      -> '1'
   *   false     -> '0'
   *   0         -> '0'
   *   1         -> '1'
   *
   */

  /**
   * Escapes a string for use within HTML.
   *
   */
  const escape_html = str => {
    return (str + '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  /**
   * Prevent default
   *
   */
  const preventDefault$1 = (evt, stop = false) => {
    if (evt) {
      evt.preventDefault();
      if (stop) {
        evt.stopPropagation();
      }
    }
  };

  /**
   * Add event helper
   *
   */
  const addEvent = (target, type, callback, options) => {
    target.addEventListener(type, callback, options);
  };

  /**
   * Plugin: "remove_button" (Tom Select)
   * Copyright (c) contributors
   *
   * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this
   * file except in compliance with the License. You may obtain a copy of the License at:
   * http://www.apache.org/licenses/LICENSE-2.0
   *
   * Unless required by applicable law or agreed to in writing, software distributed under
   * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
   * ANY KIND, either express or implied. See the License for the specific language
   * governing permissions and limitations under the License.
   *
   */
  function plugin$2(userOptions) {
    const options = Object.assign({
      label: '&times;',
      title: 'Remove',
      className: 'remove',
      append: true
    }, userOptions);

    //options.className = 'remove-single';
    var self = this;

    // override the render method to add remove button to each item
    if (!options.append) {
      return;
    }
    var html = '<a href="javascript:void(0)" class="' + options.className + '" tabindex="-1" title="' + escape_html(options.title) + '">' + options.label + '</a>';
    self.hook('after', 'setupTemplates', () => {
      var orig_render_item = self.settings.render.item;
      self.settings.render.item = (data, escape) => {
        var item = getDom$2(orig_render_item.call(self, data, escape));
        var close_button = getDom$2(html);
        item.appendChild(close_button);
        addEvent(close_button, 'mousedown', evt => {
          preventDefault$1(evt, true);
        });
        addEvent(close_button, 'click', evt => {
          // propagating will trigger the dropdown to show for single mode
          preventDefault$1(evt, true);
          if (self.isLocked) return;
          if (!self.shouldDelete([item], evt)) return;
          self.removeItem(item);
          self.refreshOptions(false);
          self.inputState();
        });
        return item;
      };
    });
  }

  /**
  * Tom Select v2.2.8
  * Licensed under the Apache License, Version 2.0 (the "License");
  */

  /*! @orchidjs/unicode-variants | https://github.com/orchidjs/unicode-variants | Apache License (v2) */
  const accent_pat$1 = '[\u0300-\u036F\u{b7}\u{2be}\u{2bc}]';
  /** @type {TUnicodeMap} */

  const latin_convert$1 = {};
  /** @type {TUnicodeMap} */

  const latin_condensed$1 = {
    '/': '⁄∕',
    '0': '߀',
    "a": "ⱥɐɑ",
    "aa": "ꜳ",
    "ae": "æǽǣ",
    "ao": "ꜵ",
    "au": "ꜷ",
    "av": "ꜹꜻ",
    "ay": "ꜽ",
    "b": "ƀɓƃ",
    "c": "ꜿƈȼↄ",
    "d": "đɗɖᴅƌꮷԁɦ",
    "e": "ɛǝᴇɇ",
    "f": "ꝼƒ",
    "g": "ǥɠꞡᵹꝿɢ",
    "h": "ħⱨⱶɥ",
    "i": "ɨı",
    "j": "ɉȷ",
    "k": "ƙⱪꝁꝃꝅꞣ",
    "l": "łƚɫⱡꝉꝇꞁɭ",
    "m": "ɱɯϻ",
    "n": "ꞥƞɲꞑᴎлԉ",
    "o": "øǿɔɵꝋꝍᴑ",
    "oe": "œ",
    "oi": "ƣ",
    "oo": "ꝏ",
    "ou": "ȣ",
    "p": "ƥᵽꝑꝓꝕρ",
    "q": "ꝗꝙɋ",
    "r": "ɍɽꝛꞧꞃ",
    "s": "ßȿꞩꞅʂ",
    "t": "ŧƭʈⱦꞇ",
    "th": "þ",
    "tz": "ꜩ",
    "u": "ʉ",
    "v": "ʋꝟʌ",
    "vy": "ꝡ",
    "w": "ⱳ",
    "y": "ƴɏỿ",
    "z": "ƶȥɀⱬꝣ",
    "hv": "ƕ"
  };
  for (let latin in latin_condensed$1) {
    let unicode = latin_condensed$1[latin] || '';
    for (let i = 0; i < unicode.length; i++) {
      let char = unicode.substring(i, i + 1);
      latin_convert$1[char] = latin;
    }
  }
  new RegExp(Object.keys(latin_convert$1).join('|') + '|' + accent_pat$1, 'gu');

  /**
   * Return a dom element from either a dom query string, jQuery object, a dom element or html string
   * https://stackoverflow.com/questions/494143/creating-a-new-dom-element-from-an-html-string-using-built-in-dom-methods-or-pro/35385518#35385518
   *
   * param query should be {}
   */
  const getDom$1 = query => {
    if (query.jquery) {
      return query[0];
    }
    if (query instanceof HTMLElement) {
      return query;
    }
    if (isHtmlString$1(query)) {
      var tpl = document.createElement('template');
      tpl.innerHTML = query.trim(); // Never return a text node of whitespace as the result
      return tpl.content.firstChild;
    }
    return document.querySelector(query);
  };
  const isHtmlString$1 = arg => {
    if (typeof arg === 'string' && arg.indexOf('<') > -1) {
      return true;
    }
    return false;
  };

  /**
   * Plugin: "dropdown_header" (Tom Select)
   * Copyright (c) contributors
   *
   * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this
   * file except in compliance with the License. You may obtain a copy of the License at:
   * http://www.apache.org/licenses/LICENSE-2.0
   *
   * Unless required by applicable law or agreed to in writing, software distributed under
   * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
   * ANY KIND, either express or implied. See the License for the specific language
   * governing permissions and limitations under the License.
   *
   */
  function plugin$1(userOptions) {
    const self = this;
    const options = Object.assign({
      className: 'clear-button',
      title: 'Clear All',
      html: data => {
        return `<div class="${data.className}" title="${data.title}">&#10799;</div>`;
      }
    }, userOptions);
    self.on('initialize', () => {
      var button = getDom$1(options.html(options));
      button.addEventListener('click', evt => {
        if (self.isDisabled) {
          return;
        }
        self.clear();
        if (self.settings.mode === 'single' && self.settings.allowEmptyOption) {
          self.addItem('');
        }
        evt.preventDefault();
        evt.stopPropagation();
      });
      self.control.appendChild(button);
    });
  }

  /**
  * Tom Select v2.2.8
  * Licensed under the Apache License, Version 2.0 (the "License");
  */

  /*! @orchidjs/unicode-variants | https://github.com/orchidjs/unicode-variants | Apache License (v2) */
  const accent_pat = '[\u0300-\u036F\u{b7}\u{2be}\u{2bc}]';
  /** @type {TUnicodeMap} */

  const latin_convert = {};
  /** @type {TUnicodeMap} */

  const latin_condensed = {
    '/': '⁄∕',
    '0': '߀',
    "a": "ⱥɐɑ",
    "aa": "ꜳ",
    "ae": "æǽǣ",
    "ao": "ꜵ",
    "au": "ꜷ",
    "av": "ꜹꜻ",
    "ay": "ꜽ",
    "b": "ƀɓƃ",
    "c": "ꜿƈȼↄ",
    "d": "đɗɖᴅƌꮷԁɦ",
    "e": "ɛǝᴇɇ",
    "f": "ꝼƒ",
    "g": "ǥɠꞡᵹꝿɢ",
    "h": "ħⱨⱶɥ",
    "i": "ɨı",
    "j": "ɉȷ",
    "k": "ƙⱪꝁꝃꝅꞣ",
    "l": "łƚɫⱡꝉꝇꞁɭ",
    "m": "ɱɯϻ",
    "n": "ꞥƞɲꞑᴎлԉ",
    "o": "øǿɔɵꝋꝍᴑ",
    "oe": "œ",
    "oi": "ƣ",
    "oo": "ꝏ",
    "ou": "ȣ",
    "p": "ƥᵽꝑꝓꝕρ",
    "q": "ꝗꝙɋ",
    "r": "ɍɽꝛꞧꞃ",
    "s": "ßȿꞩꞅʂ",
    "t": "ŧƭʈⱦꞇ",
    "th": "þ",
    "tz": "ꜩ",
    "u": "ʉ",
    "v": "ʋꝟʌ",
    "vy": "ꝡ",
    "w": "ⱳ",
    "y": "ƴɏỿ",
    "z": "ƶȥɀⱬꝣ",
    "hv": "ƕ"
  };
  for (let latin in latin_condensed) {
    let unicode = latin_condensed[latin] || '';
    for (let i = 0; i < unicode.length; i++) {
      let char = unicode.substring(i, i + 1);
      latin_convert[char] = latin;
    }
  }
  new RegExp(Object.keys(latin_convert).join('|') + '|' + accent_pat, 'gu');

  /**
   * Return a dom element from either a dom query string, jQuery object, a dom element or html string
   * https://stackoverflow.com/questions/494143/creating-a-new-dom-element-from-an-html-string-using-built-in-dom-methods-or-pro/35385518#35385518
   *
   * param query should be {}
   */
  const getDom = query => {
    if (query.jquery) {
      return query[0];
    }
    if (query instanceof HTMLElement) {
      return query;
    }
    if (isHtmlString(query)) {
      var tpl = document.createElement('template');
      tpl.innerHTML = query.trim(); // Never return a text node of whitespace as the result
      return tpl.content.firstChild;
    }
    return document.querySelector(query);
  };
  const isHtmlString = arg => {
    if (typeof arg === 'string' && arg.indexOf('<') > -1) {
      return true;
    }
    return false;
  };

  /**
   * Converts a scalar to its best string representation
   * for hash keys and HTML attribute values.
   *
   * Transformations:
   *   'str'     -> 'str'
   *   null      -> ''
   *   undefined -> ''
   *   true      -> '1'
   *   false     -> '0'
   *   0         -> '0'
   *   1         -> '1'
   *
   */

  /**
   * Prevent default
   *
   */
  const preventDefault = (evt, stop = false) => {
    if (evt) {
      evt.preventDefault();
      if (stop) {
        evt.stopPropagation();
      }
    }
  };

  /**
   * Plugin: "dropdown_header" (Tom Select)
   * Copyright (c) contributors
   *
   * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this
   * file except in compliance with the License. You may obtain a copy of the License at:
   * http://www.apache.org/licenses/LICENSE-2.0
   *
   * Unless required by applicable law or agreed to in writing, software distributed under
   * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
   * ANY KIND, either express or implied. See the License for the specific language
   * governing permissions and limitations under the License.
   *
   */
  function plugin(userOptions) {
    const self = this;
    const options = Object.assign({
      title: 'Untitled',
      headerClass: 'dropdown-header',
      titleRowClass: 'dropdown-header-title',
      labelClass: 'dropdown-header-label',
      closeClass: 'dropdown-header-close',
      html: data => {
        return '<div class="' + data.headerClass + '">' + '<div class="' + data.titleRowClass + '">' + '<span class="' + data.labelClass + '">' + data.title + '</span>' + '<a class="' + data.closeClass + '">&times;</a>' + '</div>' + '</div>';
      }
    }, userOptions);
    self.on('initialize', () => {
      var header = getDom(options.html(options));
      var close_link = header.querySelector('.' + options.closeClass);
      if (close_link) {
        close_link.addEventListener('click', evt => {
          preventDefault(evt, true);
          self.close();
        });
      }
      self.dropdown.insertBefore(header, self.dropdown.firstChild);
    });
  }

  class advancedSelectElement extends HTMLElement {
    constructor() {
      super();
      this._elementId = generateRandomId();
      this._tomSelectElement = null;
    }
    connectedCallback() {
      this._value = this.getStringAttribute("value");
      this._init();
    }

    //monitor attribute changes
    static get observedAttributes() {
      return ["value", "maxitems", "maxoptions", "search", "create", "size", "createpatterns", "placeholder", "noresults", "disabled"];
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      if (name === "createpatterns") this._updatePatterns();
      if (name === "placeholder") this._updatePlaceholder();
      if (name === "disabled") {
        if (this.hasAttribute("disabled")) this.disable();else this.enable();
      }
    }

    /*
     * O elemento select associado
     */
    get element() {
      return this.querySelector("select");
    }

    /*
     * O valor selecionado do componente;
     */
    get value() {
      if (this._isInitialized) return this.getValues();else return this._value;
    }

    /*
     * Definir o valor selecionado do componente;
     */
    set value(newValue) {
      this._value = newValue;
      if (this._isInitialized) this.setValues(this._value);
    }

    /*
     * Se esta carregando e deve mostrar spinner
     */
    set loading(value) {
      this._setLoading(value);
    }

    /*
     * Se aceita multiplos itens
     */
    set multiple(newValue) {
      if (newValue) this.setAttribute("multiple", newValue);else this.removeAttribute("multiple");
    }
    get multiple() {
      return this.element ? this.element.hasAttribute("multiple") : this.hasAttribute("multiple");
    }

    /*
     * A mensagem de placeholder
     */
    set placeholder(newValue) {
      this.setAttribute("placeholder", newValue);
    }
    get placeholder() {
      return this.getStringAttribute("placeholder", "");
    }
    get placeholderOuterElement() {
      return this.tomSelectElement.wrapper.querySelector(".items-placeholder");
    }
    get placeholderInnerElement() {
      return this.tomSelectElement.control_input;
    }

    /*
     * A mensagem de não encontrou nenhum resultado
     */
    set noResultsMessage(newValue) {
      this.setAttribute("noresults", newValue);
    }
    get noResultsMessage() {
      return this.getStringAttribute("noresults", "");
    }

    /*
     * Define quantidade maxima de items que podem ser selecionados
     */
    get maxItems() {
      return this.getIntegerAttribute("maxitems", 0);
    }
    set maxItems(newValue) {
      this.setAttribute("maxitems", newValue);
    }

    /*
     * Define quantidade máxima de items exibidos
     */
    get maxOptions() {
      return this.getIntegerAttribute("maxoptions", 0);
    }
    set maxOptions(newValue) {
      this.setAttribute("maxoptions", newValue);
    }

    /*
     * Define se é possível criar/adicionar novos items
     */
    get create() {
      return this.getBooleanAttribute("create", false);
    }
    set create(newValue) {
      this.setAttribute("create", newValue);
    }

    /*
     * Regex que determina se o item criado é valido
     */
    get createPatterns() {
      return this.getArrayAttribute("createpatterns", []);
    }
    set createPatterns(newValue) {
      this.setAttribute("createpatterns", newValue);
    }

    /*
     * Define se é o campo de pesquisa deve ser mostrado
     */
    get search() {
      return this.getBooleanAttribute("search", true);
    }
    set search(newValue) {
      this.setAttribute("search", newValue);
    }

    /*
     * Define o tamanho do componente
     */
    get size() {
      return this.getStringAttribute("size", "md", ["sm", "md", "lg"]);
    }
    set size(newValue) {
      this.setAttribute("size", newValue);
    }
    get clearButton() {
      return this.getBooleanAttribute("clearbutton", true);
    }
    set clearButton(value) {
      this.setAttribute("clearbutton", value);
    }
    get sizeClass() {
      return `ts-wrapper-${this.size}`;
    }

    /*
     * Referencia ao objeto TomSelect
     */
    get tomSelectElement() {
      return this._tomSelectElement;
    }
    set tomSelectElement(newValue) {
      this._tomSelectElement = newValue;
    }
    _init() {
      if (this?.tomSelectElement?.input) return;
      this._render();
      this._configure();
      this._addEvents();
      this._addFooter();
      this._stylezide();
      this.setValue(this._value);
      this._addSubSpinner();
      this._isInitialized = true;
    }
    _render() {
      this.insertAdjacentHTML("beforeend", `<select 
            value="${this._value}"
            id="${this._elementId}"
            >${this.getTemplateHtml("options")}
        </select>`);
      this.transferAttrsTo(this.element, [], ["id", "maxitems", "maxoptions", "search", "create", "size", "createpatterns", "placeholder", "noresults", "disabled", "clearbutton"]);
      this.copyAttrsTo(this.element, ["disabled"]);
    }
    _configure() {
      if (this.element.tomselect) this.element.tomselect.destroy();
      this.tomSelectElement = new TomSelect(this.element, this._getSettings());
    }
    _getSettings() {
      const settings = {};
      settings.allowEmptyOption = true;
      settings.plugins = this._getPlugins();
      settings.persist = true;
      settings.placeholder = this._getPlaceHolder();
      settings.score = function (search) {
        var score = this.getScoreFunction(search);
        return function (item) {
          if (item.value == "" || item.value == null) return 0;
          return score(item);
        };
      };
      if (this.maxItems) settings.maxItems = this.maxItems;
      if (this.maxOptions) settings.maxOptions = this.maxOptions;
      if (this.create) settings.create = true;
      if (this.createPatterns.length > 0) settings.createFilter = this._getCreateFilterHandler();
      settings.shouldLoad = function (query) {
        return query.length >= 3;
      };

      // search=false: sem campo de busca, o input de controle do TomSelect ainda existia dentro do
      // .ts-control e recebia foco ao abrir o dropdown, exibindo um caret piscando como se desse pra
      // digitar (mas nao da). Com controlInput=null o TomSelect cria o input destacado do controle e
      // usa o proprio .ts-control como focus_node (ver dist/esm/tom-select.js) - nao ha caret visivel.
      // Nos campos com search o dropdown_input plugin mantem o input, entao o comportamento nao muda.
      if (!this.search) settings.controlInput = null;
      this._getSettingsRenders(settings);
      return settings;
    }
    _addEvents() {
      if (!this.tomSelectElement) return;
      this.on("focus", () => {
        //Isso é necessário para que dropdowns abertos sejam fechados quando é clicado neste componente.
        this.click();
      });
    }
    _getPlaceHolder() {
      if (this.placeholder) return this.placeholder;
      return this.create ? i18nlocales.t("searchOrCreateNew") : i18nlocales.t("search");
    }
    _getCreateFilterPatterns() {
      return {
        email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        token: /^\{[a-zA-Z0-9._%+-]+\.[a-zA-Z0-9._%+-]+\}$/,
        decimalAmerican: /^(?:\d*\.\d{1,2}|\d+)$/,
        decimal: /^(?:\d{1,3}(?:\.\d{3})*,\d{1,2}|\d+,\d{1,2}|\d+)$/,
        date: /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/([0-9]{4})$/,
        dateTime: /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/([0-9]{4})\s(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/,
        dateAmerican: /^(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/,
        dateTimeAmerican: /^(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])\s(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/,
        dateTimeUniversal: /^(?:\d{2}\/\d{2}\/\d{4}(?: \d{2}:\d{2})?|\d{2}\/\d{2}\/\d{4}(?: \d{2}:\d{2})?)$/,
        number: /^-?\d+$/
      };
    }
    _getCreateFilterHandler() {
      return input => {
        if (this.createPatterns.length === 0) return true;
        for (const patternUid of this.createPatterns) {
          const patterns = this._getCreateFilterPatterns();
          let regexCode = patterns[patternUid];
          if (!regexCode) regexCode = patternUid;
          const regex = new RegExp(regexCode);
          if (regex.test(input)) {
            return true;
          }
        }
        return false;
      };
    }
    _getSettingsRenders(settings) {
      settings.render = {
        no_results: (data, escape) => {
          return `<div class="no-results">${this._getNoResultsMessage(data, escape)}</div>`;
        },
        option_create: function (data, escape) {
          return `<div class="create">${i18nlocales.t("add")} <strong>${escape(data.input)}</strong>&hellip;</div>`;
        },
        loading: function () {
          return `<div class="padded text-danger"><wc-spinner></wc-spinner></div>`;
        }
      };
      const optionTemplate = this.getTemplateHtml("option");
      if (optionTemplate) {
        settings.render.option = item => {
          return this._processTemplate(optionTemplate, item);
        };
      }
      const itemTemplate = this.getTemplateHtml("item");
      if (itemTemplate) {
        settings.render.item = item => {
          return this._processTemplate(itemTemplate, item);
        };
      }
    }
    _getNoResultsMessage(data, escape) {
      if (this.noResultsMessage) return this.noResultsMessage;
      return `${i18nlocales.t("noItemFound")} "${escape(data.input)}"`;
    }
    _stylezide() {
      this.tomSelectElement.wrapper.classList.add(this.sizeClass);
    }
    _getPlugins() {
      const plugins = {};
      this._enableDropdownInputPlugin(plugins);
      this._enableCheckboxPlugin(plugins);
      this._enableRemoveButtonPlugin(plugins);
      this._enableClearButtonPlugin(plugins);
      this._enableDropdownHeaderPlugin(plugins);
      return plugins;
    }
    _enableDropdownInputPlugin(plugins) {
      if (!this.search) return;
      plugins.dropdown_input = {};
      TomSelect.define("dropdown_input", plugin$4);
    }
    _enableCheckboxPlugin(plugins) {
      const optionTemplate = this.getTemplateHtml("option");
      if (!optionTemplate && this.multiple) {
        plugins.checkbox_options = {};
        TomSelect.define("checkbox_options", plugin$3);
      }
    }
    _enableRemoveButtonPlugin(plugins) {
      if (!this.multiple) return;
      plugins.remove_button = {
        title: i18nlocales.t("remove")
      };
      TomSelect.define("remove_button", plugin$2);
    }
    _enableClearButtonPlugin(plugins) {
      if (this.multiple || !this.clearButton) return;
      plugins.clear_button = {
        title: i18nlocales.t("removeAllSelected")
      };
      TomSelect.define("clear_button", plugin$1);
    }
    _enableDropdownHeaderPlugin(plugins) {
      const headerTemplate = this.getTemplateHtml("header");
      if (!headerTemplate) return;
      plugins.dropdown_header = {
        html: () => {
          return headerTemplate;
        }
      };
      TomSelect.define("dropdown_header", plugin);
    }
    _addFooter() {
      if (!this.tomSelectElement) return;
      const footerTemplate = this.getTemplateHtml("footer");
      if (!footerTemplate) return;
      this.tomSelectElement.dropdown.insertAdjacentHTML("beforeend", footerTemplate);
    }
    _addSubSpinner() {
      const spinner = document.createElement("wc-spinner");
      spinner.className = "sub-spinner text-danger position-absolute top-50 end-0 translate-middle-y me-2";
      this.tomSelectElement.control.appendChild(spinner);
    }
    _processTemplate(template, data) {
      return template.replace(/\{\{\s*item\.(\w+)\s*\}\}/g, function (match, key) {
        // eslint-disable-next-line no-prototype-builtins
        if (data.hasOwnProperty(key)) {
          return data[key];
        }
        return match;
      });
    }
    _setLoading(value) {
      if (!this.tomSelectElement) return;
      if (value) this.tomSelectElement.wrapper.classList.add("sub-loading");else this.tomSelectElement.wrapper.classList.remove("sub-loading");
    }
    _updatePatterns() {
      if (!this.tomSelectElement) return;
      this.tomSelectElement.settings.createFilter = this._getCreateFilterHandler();
    }
    _updatePlaceholder() {
      if (!this.tomSelectElement) return;
      const placeholderText = this._getPlaceHolder();
      if (this.placeholderInnerElement) this.placeholderInnerElement.placeholder = placeholderText;
      if (this.placeholderOuterElement) this.placeholderOuterElement.placeholder = placeholderText;
      this.tomSelectElement.settings.placeholder = placeholderText;
    }
    asyncSearch(customSearchCallback) {
      const callbackWrapper = async (query, callback) => {
        try {
          const data = await customSearchCallback(query);
          callback(data);
        } catch (error) {
          callback();
        }
      };
      this.tomSelectElement.settings.load = callbackWrapper;
    }
    set valueField(newValue) {
      this.tomSelectElement.settings.valueField = newValue;
    }
    set labelField(newValue) {
      this.tomSelectElement.settings.labelField = newValue;
    }
    set searchField(newValue) {
      this.tomSelectElement.settings.searchField = newValue;
    }
    get options() {
      return this.tomSelectElement.options;
    }
    get query() {
      return this.tomSelectElement.query;
    }
    get tokens() {
      return this.tomSelectElement.tokens;
    }
    get total() {
      return this.tomSelectElement.total;
    }
    get items() {
      return this.tomSelectElement.items;
    }
    addToken(token) {
      const newItem = {
        value: token,
        text: token
      };
      this.addOption(newItem, true);
      this.addItem(newItem.value, true);
      this.refreshOptions(true);
    }
    add(data, user_created = false) {
      this.addOption(data, user_created);
    }
    addOption(data, user_created = false) {
      this.tomSelectElement.addOption(data, user_created);
    }
    addOptions(data, user_created = false) {
      this.tomSelectElement.addOptions(data, user_created);
    }
    updateOption(value, data) {
      this.tomSelectElement.updateOption(value, data);
    }
    removeOption(value) {
      this.tomSelectElement.removeOption(value);
    }
    getOption(value, create = false) {
      return this.tomSelectElement.getOption(value, create);
    }
    getAdjacent(HTMLElement, direction) {
      return this.tomSelectElement.getAdjacent(HTMLElement, direction);
    }
    refreshOptions(triggerDropdown) {
      this.tomSelectElement.refreshOptions(triggerDropdown);
    }
    clearOptions(clearFilter) {
      this.tomSelectElement.clearOptions(clearFilter);
    }
    clearFilter(option, value) {
      this.tomSelectElement.clearFilter(option, value);
    }
    clear(silent) {
      this.tomSelectElement.clear(silent);
    }
    getItem(value) {
      return this.tomSelectElement.getItem(value);
    }
    addItem(value, silent) {
      this.tomSelectElement.addItem(value, silent);
    }
    removeItem(value, silent) {
      this.tomSelectElement.removeItem(value, silent);
    }
    createItem(value, callback) {
      this.tomSelectElement.createItem(value, callback);
    }
    refreshItems() {
      this.tomSelectElement.refreshItems();
    }
    addOptionGroup(id, data) {
      this.tomSelectElement.addOptionGroup(id, data);
    }
    removeOptionGroup(id) {
      this.tomSelectElement.removeOptionGroup(id);
    }
    clearOptionGroups() {
      this.tomSelectElement.clearOptionGroups();
    }
    on(event, handler) {
      this.tomSelectElement.on(event, handler);
    }
    off(event, handler) {
      this.tomSelectElement.off(event, handler);
    }
    trigger(event) {
      this.tomSelectElement.trigger(event);
    }
    open() {
      this.tomSelectElement.open();
    }
    close() {
      this.tomSelectElement.close();
    }
    positionDropdown() {
      this.tomSelectElement.positionDropdown();
    }
    destroy() {
      this.tomSelectElement.destroy();
    }
    load(query) {
      this.tomSelectElement.load(query);
    }
    focus() {
      this.tomSelectElement.focus();
    }
    blur() {
      this.tomSelectElement.blur();
    }
    lock() {
      this.tomSelectElement.lock();
    }
    unlock() {
      this.tomSelectElement.unlock();
    }
    disable() {
      this.tomSelectElement?.disable();
    }
    enable() {
      this.tomSelectElement?.enable();
    }
    getValue() {
      this.tomSelectElement.getValue();
    }
    setValue(item, silent = false) {
      this.tomSelectElement.setValue(item, silent);
    }
    setCaret(index) {
      this.tomSelectElement.setCaret(index);
    }
    isFull() {
      this.tomSelectElement.isFull();
    }
    clearCache() {
      this.tomSelectElement.clearCache();
    }
    setTextboxValue(str) {
      this.tomSelectElement.setTextboxValue(str);
    }
    sync() {
      this.tomSelectElement.sync();
    }
    addOrUpdateOptions(options) {
      if (!options) return;
      if (Array.isArray(options)) {
        options.forEach(option => {
          this.addOrUpdateOption(option);
        });
        return;
      }
      this.addOrUpdateOption(options);
    }
    addOrUpdateOption(option) {
      if (!option) return;
      const existingOption = this.getOption(option.value);
      if (existingOption) {
        this.updateOption(option.value, option);
        return;
      }
      this.addOption(option);
    }
    setValues(value) {
      //is array and all values are objects
      if (Array.isArray(value) && value.every(obj => typeof obj === "object")) {
        this.addOptions(value);
        this.setValue(value.map(obj => obj.value), true);
        return;
      }

      //is array and all values are strings
      if (Array.isArray(value) && value.every(obj => typeof obj === "string")) {
        this.addOptions(value.map(obj => ({
          value: obj,
          text: obj
        })));
        this.setValue(value, true);
        return;
      }

      //is object
      if (typeof value === "object") {
        this.addOption(value);
        this.setValue(value.value, true);
        return;
      }

      //is string and is multiple, do not select anything
      if (this.multiple) return;

      //is string
      this.addOption({
        value,
        text: value
      });
      this.setValue(value, true);
    }
    getValues() {
      const values = this.tomSelectElement.getValue();
      if (!Array.isArray(values)) return values;
      if (!values || !values.length) return null;
      const options = this.tomSelectElement.options;
      if (!options) return null;
      const optionsArray = Object.entries(options).map(([key, value]) => ({
        key,
        value
      }));
      return values.map(value => {
        const option = optionsArray.find(option => option.key === value);
        return option ? option.value : value;
      });
    }
  }
  customElements.define("wc-advanced-select", advancedSelectElement);

  class divisorLineElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    _init() {
      this._render();
    }
    getStringAttribute(name, defaultValue, allowedValues = []) {
      const value = this.getAttribute(name) || defaultValue;
      return allowedValues.length > 0 && !allowedValues.includes(value) ? defaultValue : value;
    }
    get caption() {
      return this.getStringAttribute("caption", "");
    }
    set caption(value) {
      this.setAttribute("caption", value);
    }
    get icon() {
      return this.getStringAttribute("icon", "");
    }
    set icon(value) {
      this.setAttribute("icon", value);
    }
    get size() {
      return this.getStringAttribute("size", "md", ["sm", "md", "lg"]);
    }
    set size(value) {
      this.setAttribute("size", value);
    }
    get sizeClass() {
      if (this.size === "sm") return "small-divisor";
      if (this.size === "lg") return "large-divisor";
      return "headline-divisor";
    }
    get sizeTag() {
      if (this.size === "sm") return "h6";
      if (this.size === "lg") return "h3";
      return "h4";
    }
    _render() {
      this.innerHTML = `
        <div class="${this.sizeClass} d-flex align-items-center">
            <div class="flex-shrink-0">
                <${this.sizeTag} class="text-truncate mb-0 me-3" title="${this.caption}">
                  ${this.icon ? `<wc-icon icon="${this.icon}" class="me-1" size="${this.size}"></wc-icon>` : ""} 
                  ${this.caption}
                </${this.sizeTag}>
            </div>
            <div class="divisor-line flex-grow-1"></div>
        </div>`;
    }
  }
  if (!customElements.get("wc-divisor-line")) {
    customElements.define("wc-divisor-line", divisorLineElement);
  }

  class tokenSelectorElement extends HTMLElement {
    constructor() {
      super();
      this._dataset = null;
    }
    connectedCallback() {
      this.id = generateRandomId();
      this._init();
    }
    get id() {
      return this.getAttribute("id");
    }
    set id(value) {
      this.setAttribute("id", value);
    }
    get target() {
      return this.getAttribute("target");
    }
    set target(value) {
      this.setAttribute("target", value);
    }
    get targetElement() {
      return document.querySelector(this.target);
    }
    get dropdownButtonElement() {
      return document.getElementById(`${this.id}_button`);
    }
    get dropdownBodyElement() {
      return document.getElementById(`${this.id}_body`);
    }
    get searchElement() {
      return document.getElementById(`${this.id}_search`);
    }
    get exclude() {
      return this.getArrayAttribute("exclude", []);
    }
    set exclude(value) {
      this.setAttribute("exclude", value);
    }
    get include() {
      return this.getArrayAttribute("include", []);
    }
    set include(value) {
      this.setAttribute("include", value);
    }
    get includeFilter() {
      return this.getArrayAttribute("includeFilter", []);
    }
    set includeFilter(value) {
      this.setAttribute("includeFilter", value);
    }
    get marginTop() {
      return this.getStringAttribute("marginTop", "-10px");
    }
    set marginTop(value) {
      this.setAttribute("marginTop", value);
    }
    get marginRight() {
      return this.getStringAttribute("marginRight", "-10px");
    }
    set marginRight(value) {
      this.setAttribute("marginRight", value);
    }
    get dataset() {
      return this._dataset;
    }
    set dataset(value) {
      this._dataset = value;
    }
    get lang() {
      return i18nlocales.lng();
    }
    get endpoints() {
      return this.getArrayAttribute("endpoints", []);
    }
    set endpoints(value) {
      this.setAttribute("endpoints", value);
    }
    _init() {
      this._render();
      this._setPosition();
      this._bindEvents();
    }
    _render() {
      this.innerHTML = `
        <div class="dropdown token-dropdown" id="${this.id}_container">
          <a  
            id="${this.id}_button" 
            class="" 
            role="button" 
            data-bs-toggle="dropdown" 
            data-bs-reference="body"
            aria-expanded="false">
            <wc-pin class="ms-2" size="lg" border="false"  color="secondary"  >
                <wc-icon icon="token" size="sm" class="position-relative"></wc-icon>
            </wc-pin>            
          </a>
          <div class="dropdown-menu dropdown-menu-end token-dropdown-menu">
            <input id="${this.id}_search" class="form-control form-control-sm" type="text" placeholder="${i18nlocales.t("searchTokens")}" aria-label=""${i18nlocales.t("searchTokens")}">
            <div id="${this.id}_body" class="token-dropdown-menu-body">
                ${`<div class="mt-3 px-3 placeholder-glow">
                        <span class="placeholder placeholder-lg col-12"></span>
                    </div>`.repeat(5)}
            </div>
          </div>
        </div>
        `;
    }
    _setPosition() {
      this.className = "position-absolute fade-element end-0";
      this.style.marginTop = this.marginTop;
      this.style.marginRight = this.marginRight;
      this.style.zIndex = "10";
    }
    _bindEvents() {
      this.dropdownButtonElement.addEventListener("show.bs.dropdown", async () => {
        this._hideOtherTokenSelectors();
        await this._buildTokensList();
        this._focusOnSearch();
      });
      this.dropdownButtonElement.addEventListener("hide.bs.dropdown", () => {
        this._showOtherTokenSelectors();
      });
      this.searchElement.addEventListener("input", async () => {
        await this._buildTokensList(this.searchElement.value);
      });
      delegateEvent(this, "[data-token]", "click", (e, target) => {
        const token = target.dataset.token;
        if (!token) return;
        this._addToken(token);
      });
    }

    /*
     * Para não haver sobreposição, esconder os demais selectores da tela quando esse for aberto
     */
    _hideOtherTokenSelectors() {
      const tokenSelectors = document.querySelectorAll(`wc-token-selector:not(#${this.id})`);
      tokenSelectors.forEach(tokenSelector => {
        tokenSelector.style.zIndex--;
        //tokenSelector.classList.add("d-none");
      });
    }

    /*
     * Mostrar novamente os seletores escondidos
     */
    _showOtherTokenSelectors() {
      const tokenSelectors = document.querySelectorAll(`wc-token-selector:not(#${this.id})`);
      tokenSelectors.forEach(tokenSelector => {
        tokenSelector.style.zIndex++;
        //tokenSelector.classList.remove("d-none");
      });
    }
    async _buildTokensList(query = null) {
      await this._loadTokens();
      this._clear();
      this.dataset.forEach(category => {
        const filteredTokens = query && query.length > 2 ? category.tokens.filter(token => this._filter(token, query)) : category.tokens;
        if (filteredTokens && filteredTokens.length > 0) {
          this.dropdownBodyElement.insertAdjacentHTML("beforeend", `
          <wc-divisor-line caption="${sanitize(this._getObjectProperty(category, "name"))}" size="sm"></wc-divisor-line>
          <div class="mb-3"><div class="extra-small">${this._getObjectProperty(category, "desc")}</div></div>
                `);
          filteredTokens.forEach(token => {
            this.dropdownBodyElement.insertAdjacentHTML("beforeend", `
              <a class="dropdown-item" role="button" data-token="${token.uid}">
                  <div class="d-flex justify-content-between align-itens-center">
                      <div class="token text-truncate">${token.uid}</div>
                      <div class="token-version extra-small text-tertiary-color">v. ${token.v} / ${token.type}</div>
                  </div>
                  <div class="extra-small text-wrap text-break">${this._getObjectProperty(token, "desc")}</div>
              </a>
                    `);
          });
        }
      });
    }
    _filter(token, query) {
      return stringContains(token.uid, query) || stringContains(this._getObjectProperty(token, "desc"), query);
    }
    _clear() {
      this.dropdownBodyElement.innerHTML = "";
    }
    _focusOnSearch() {
      this.searchElement.focus();
    }
    async _loadTokens() {
      if (this.dataset) return this.dataset;
      const endpointPromises = this.endpoints.map(async endpoint => {
        const url = endpoint;
        const response = await fetch(url);
        return await response.json();
      });
      const tokensPromise = async () => {
        const url = `${getZeevBootstrapPath("ondemand/tokens/tokens.json")}`;
        const response = await fetch(url);
        return await response.json();
      };
      const results = await Promise.all([...endpointPromises, tokensPromise()]);
      this.dataset = this._mapDataset(results.flat());
      this._excludeCategories();
      this._includeCategories();
      this._filterTokens();
    }
    _excludeCategories() {
      if (this.exclude.length === 0) return this.dataset;
      this.dataset = this.dataset.filter(category => {
        return !this.exclude.includes(category.uid);
      });
    }
    _includeCategories() {
      if (this.include.length === 0) return this.dataset;
      this.dataset = this.dataset.filter(category => {
        return this.include.includes(category.uid);
      });
    }
    _filterTokens() {
      if (this.includeFilter.length === 0) return this.dataset;
      this.dataset.forEach(category => {
        category.tokens = category.tokens.filter(token => {
          return this.includeFilter.includes(token.type);
        });
      });
    }
    _mapDataset(dataset) {
      if (!dataset) return [];
      const resultTokens = dataset.reduce((acc, current) => {
        if (!acc[current.uid]) {
          acc[current.uid] = _objectSpread2(_objectSpread2({}, current), {}, {
            tokens: [...(current.tokens ?? [])]
          });
          return acc;
        }
        acc[current.uid] = {
          uid: current.uid,
          name_pt: this._getCategoryParam(acc[current.uid].name_pt, current.name_pt),
          name_en: this._getCategoryParam(acc[current.uid].name_en, current.name_en),
          name_es: this._getCategoryParam(acc[current.uid].name_es, current.name_es),
          desc_pt: this._getCategoryParam(acc[current.uid].desc_pt, current.desc_pt),
          desc_en: this._getCategoryParam(acc[current.uid].desc_en, current.desc_en),
          desc_es: this._getCategoryParam(acc[current.uid].desc_es, current.desc_es),
          tokens: [...acc[current.uid].tokens, ...current.tokens]
        };
        return acc;
      }, {});
      dataset = Object.values(resultTokens);
      return dataset.map(item => {
        return item.tokens === null ? [] : item;
      });
    }
    _getControlConfiguration() {
      return {
        input: token => insertTextAtCursor(this.targetElement, token),
        textarea: token => insertTextAtCursor(this.targetElement, token),
        "wc-visual-editor": token => this.targetElement.addToken(token),
        "wc-advanced-visual-editor": token => this.targetElement.addToken(token),
        "wc-code-editor": token => this.targetElement.addToken(token),
        "wc-advanced-select": token => this.targetElement.addToken(token),
        "wc-input-mask": token => this.targetElement.addToken(token)
      };
    }
    _addToken(token) {
      if (!this.targetElement) return;
      this.targetElement.focus();
      const controlConfiguration = this._getControlConfiguration();
      controlConfiguration[this.targetElement.tagName.toLowerCase()](token);
      this.targetElement.dispatchEvent(new Event("input"));
    }
    _getObjectProperty(object, prop) {
      if (!object || !prop) return "";
      return this._getCategoryParam(object[`${prop}_${this.lang}`], object[`${prop}_pt`]);
    }
    _getCategoryParam(param1, param2) {
      return !isNullOrEmpty(param1) ? param1 : param2;
    }
  }
  customElements.define("wc-token-selector", tokenSelectorElement);

  //import i18nlocales from "@/assets/js/core/i18n.js";
  class mentionElement extends HTMLElement {
    constructor() {
      super();
      this._id = generateRandomId();
      this._initiated = false;
      this._lastAtSymbolPosition = 0;
      this._lastCursorPosition = 0;
    }
    connectedCallback() {
      this._monitorTargetEvents();
    }
    get target() {
      return this.getAttribute("target");
    }
    set target(value) {
      this.setAttribute("target", value);
    }
    get targetElement() {
      return document.querySelector(this.target);
    }
    get dropdownButtonElement() {
      return document.getElementById(`${this._id}_button`);
    }
    get dropdownBodyElement() {
      return document.getElementById(`${this._id}_body`);
    }
    get searchElement() {
      return document.getElementById(`${this._id}_search`);
    }
    get dataset() {
      return this._dataset;
    }
    set dataset(value) {
      this._dataset = value;
    }
    get initiated() {
      return this._initiated;
    }
    set initiated(value) {
      this._initiated = value;
    }
    get bsElement() {
      return window.bootstrap.Dropdown.getOrCreateInstance(document.getElementById(`${this._id}_button`));
    }
    _monitorTargetEvents() {
      this.targetElement.setAttribute("autocomplete", "off");
      this._bindTargetOnInput();
    }
    _bindTargetOnInput() {
      const monitorSymbolAndOpenList = async e => {
        //if(!symbolOpen) return;
        if (e.keyCode < 48 || e.keyCode > 90) return;
        const text = this.targetElement.value;
        const cursorPosition = this.targetElement.selectionStart;
        const textBeforeCursor = text.slice(0, cursorPosition);
        const lastBlankSpace = textBeforeCursor.lastIndexOf(" ");
        const textAfterBlankSpace = textBeforeCursor.slice(lastBlankSpace + 1, cursorPosition);
        const lastAtSymbolPosition = textAfterBlankSpace.lastIndexOf("@");
        const textBeforeSymbol = textBeforeCursor.slice(0, lastAtSymbolPosition);
        const mentionQuery = textBeforeCursor.slice(lastAtSymbolPosition + 1, cursorPosition);
        console.log("text", text);
        console.log("cursorPosition", cursorPosition);
        console.log("textBeforeCursor", textBeforeCursor);
        console.log("lastBlankSpace", lastBlankSpace);
        console.log("textAfterBlankSpace", textAfterBlankSpace);
        console.log("lastAtSymbolPosition", lastAtSymbolPosition);
        console.log("textBeforeSymbol", textBeforeSymbol);
        console.log("mentionQuery", mentionQuery);
        console.log("--------------");
        if (lastAtSymbolPosition !== -1 && cursorPosition - lastAtSymbolPosition > 3 && (textBeforeSymbol === "" || textBeforeSymbol.endsWith(" "))) {
          this._lastAtSymbolPosition = lastAtSymbolPosition;
          this._lastCursorPosition = cursorPosition;
          this._init();
          this.bsElement.show();
          await this._buildUsersList(mentionQuery);
        }
      };
      const monitorArrowDown = e => {
        if (e.key === "ArrowDown" && this.initiated) {
          this.bsElement.show();
          const firstItem = this.dropdownBodyElement.querySelector("[data-token]");
          if (!firstItem) return;
          firstItem.focus();
        }
      };
      this.targetElement.addEventListener("keyup", async e => {
        await monitorSymbolAndOpenList(e);
      });
      this.targetElement.addEventListener("keyup", e => {
        monitorArrowDown(e);
      });
    }
    _init() {
      if (this.initiated) return;
      this._render();
      this._bindEvents();
      this.initiated = true;
    }
    _render() {
      this.innerHTML = `
        <div class="dropdown" id="${this._id}">
          <a  
            id="${this._id}_button" 
            class="" 
            role="button" 
            data-bs-toggle="dropdown" 
            data-bs-reference="#${this.target}"
            aria-expanded="false"></a>
          <div class="dropdown-menu dropdown-menu-end token-dropdown-menu">
            <div id="${this._id}_body" class="token-dropdown-menu-body">
                ${`<div class="mt-3 px-3 placeholder-glow">
                        <span class="placeholder placeholder-lg col-12"></span>
                    </div>`.repeat(5)}
            </div>
          </div>
        </div>
        `;
    }
    async _buildUsersList(query = null) {
      await this._loadUsers(query);
      this._clear();
      const filteredUsers = query && query.length > 2 ? this.dataset.filter(token => this._filter(token, query)) : this.dataset;
      const orderedUsers = filteredUsers.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      if (orderedUsers.length === 0) return this._empty();
      orderedUsers.forEach(user => {
        this.dropdownBodyElement.insertAdjacentHTML("beforeend", `<a class="dropdown-item" role="button" tabindex="0" data-token="${sanitize(user.username)}">
            <div class="d-flex justify-content-between align-itens-center">
                <wc-user name="${sanitize(user.name)}" size="sm"></wc-user>
            </div>
        </a>`);
      });
    }
    _empty() {
      this.dropdownBodyElement.innerHTML = `
        <div class="small">
            Não foi encontrado nenhum registro
        </div>
        `;
    }
    _clear() {
      this.dropdownBodyElement.innerHTML = "";
    }
    _filter(user, query) {
      return stringContains(user.name, query) || stringContains(user.username, query);
    }
    _bindEvents() {
      this._selectUserHandlers();
    }
    _selectUserHandlers() {
      delegateEvent(this, "[data-token]", "click", (e, target) => {
        const token = target.dataset.token;
        if (!token) return;
        this._addUser(token);
        this.bsElement.hide();
      });
      delegateEvent(this, "[data-token]", "keyup", (e, target) => {
        if (e.key !== "Enter") return;
        const token = target.dataset.token;
        if (!token) return;
        this._addUser(token);
        this.bsElement.hide();
      });
    }
    _addUser(token) {
      if (!this.targetElement) return;
      this.targetElement.focus();
      token = `@${token} `;
      if (this.targetElement.tagName.toLowerCase() === "input") {
        insertTextBetweenPosition(this.targetElement, token, this._lastAtSymbolPosition, this._lastCursorPosition);
        return;
      }
      if (this.targetElement.tagName.toLowerCase() === "textarea") {
        insertTextBetweenPosition(this.targetElement, token, this._lastAtSymbolPosition, this._lastCursorPosition);
        return;
      }
    }
    async _loadUsers(query) {
      if (this.dataset) return this.dataset;
      const url = `${getZeevBootstrapPath(`assets/js/components/mention/usersSample.json?query=${query}`)}`;
      const response = await fetch(url);
      this.dataset = await response.json();
    }
  }
  customElements.define("wc-mention", mentionElement);

  // From https://github.com/sindresorhus/random-int/blob/c37741b56f76b9160b0b63dae4e9c64875128146/index.js#L13-L15
  const createAbortError = () => {
    const error = new Error('Delay aborted');
    error.name = 'AbortError';
    return error;
  };
  const clearMethods = new WeakMap();
  function createDelay({
    clearTimeout: defaultClear,
    setTimeout: defaultSet
  } = {}) {
    // We cannot use `async` here as we need the promise identity.
    return (milliseconds, {
      value,
      signal
    } = {}) => {
      // TODO: Use `signal?.throwIfAborted()` when targeting Node.js 18.
      if (signal?.aborted) {
        return Promise.reject(createAbortError());
      }
      let timeoutId;
      let settle;
      let rejectFunction;
      const clear = defaultClear ?? clearTimeout;
      const signalListener = () => {
        clear(timeoutId);
        rejectFunction(createAbortError());
      };
      const cleanup = () => {
        if (signal) {
          signal.removeEventListener('abort', signalListener);
        }
      };
      const delayPromise = new Promise((resolve, reject) => {
        settle = () => {
          cleanup();
          resolve(value);
        };
        rejectFunction = reject;
        timeoutId = (defaultSet ?? setTimeout)(settle, milliseconds);
      });
      if (signal) {
        signal.addEventListener('abort', signalListener, {
          once: true
        });
      }
      clearMethods.set(delayPromise, () => {
        clear(timeoutId);
        timeoutId = null;
        settle();
      });
      return delayPromise;
    };
  }
  const delay = createDelay();

  /**
   * Return the di object for a given element.
   *
   * @param {Element} element
   *
   * @return {ModdleElement}
   */
  function getDi(element) {
    return element && element.di;
  }

  const LANE_HEIGHT = 200;
  const LANE_WIDTH = 220;
  class CliModeling {
    constructor(modeler) {
      this._modeler = modeler;
      this._autoPlace = modeler.get("autoPlace");
      this._bpmnFactory = modeler.get("bpmnFactory");
      this._canvas = modeler.get("canvas");
      this._elementFactory = modeler.get("elementFactory");
      this._elementRegistry = modeler.get("elementRegistry");
      this._modeling = modeler.get("modeling");
      this._overlays = modeler.get("overlays");
    }

    //#region Basics

    centerDiagram() {
      this._canvas.zoom("fit-viewport", "auto");
    }
    reset() {
      this._modeler.clear();
      return this._modeler.createDiagram();
    }

    //#endregion Basics

    //#region Lane

    getOrCreateParticipant(name) {
      let participant = this._elementRegistry.find(element => element.type === "bpmn:Participant");
      if (participant) return participant;
      participant = this._elementFactory.createParticipantShape({
        type: "bpmn:Participant"
      });
      this._modeling.createShape(participant, {
        x: 400,
        y: 100
      }, this._canvas.getRootElement());
      this.renameElement(participant, name);
      return participant;
    }
    createFirstLane(participant, name) {
      let lanes = this.getLanes();
      if (lanes.length > 0) return lanes[0];

      // Para criar a primeira lane eh preciso fazer um split e depois remover a lane 2
      this._modeling.splitLane(participant, 2);
      lanes = this.getLanes();
      this._modeling.removeElements([lanes[1]]);
      this.renameElement(lanes[0], name);
      return lanes[0];
    }
    getLanes() {
      return this._elementRegistry.filter(element => element.type === "bpmn:Lane");
    }
    getLaneByName(name) {
      return this.getLanes().find(lane => lane.businessObject.name === name);
    }
    getLaneElements(laneId, filter = () => true) {
      return this._elementRegistry.filter(e => e.businessObject.lanes?.at(0).id === laneId && filter(e));
    }
    createLane(participant, name) {
      const lane = this._modeling.addLane(participant, "bottom");
      if (name !== undefined) this.renameElement(lane, name);
      return lane;
    }
    resizeLane(lane, height, width, balanced = false) {
      const bounds = this.getElementBounds(lane);
      const newBounds = _objectSpread2(_objectSpread2({}, bounds), {}, {
        height,
        width
      });
      this._modeling.resizeLane(lane, newBounds, balanced);
    }
    incrementLaneHeight(laneId, thresholdY) {
      // Ordena as raias pela posicao Y, decrescente.
      const lanes = this.getLanes().sort((a, b) => b.y - a.y);
      for (let lane of lanes) {
        // Aumenta a altura da raia sem mover os elementos.
        this.resizeLane(lane, lane.height + LANE_HEIGHT, lane.width, true);
        if (lane.id === laneId) {
          if (thresholdY === undefined) thresholdY = lane.y;
          // Nao precisa aumentar o tamanho das raias superiores.
          break;
        }
      }

      // Move os elementos abaixo do corte Y.
      const prefixes = ["Act", "Eve", "Gat"];
      const elements = this._elementRegistry.filter(e => prefixes.some(p => e.id.startsWith(p)) && e.y > thresholdY);
      this._modeling.moveElements(elements, {
        x: 0,
        y: LANE_HEIGHT
      });
    }

    //#endregion Lane

    //#region Tasks and Events

    createElement(type, name, lane, position, sourceId) {
      const createElement = this._getCreateElementHandler(type);
      return createElement.call(this, lane, name, position, sourceId);
    }
    _getCreateElementHandler(type) {
      const handlers = [{
        ids: ["start", "inicio"],
        handler: this.createStartEvent
      }, {
        ids: ["task", "tarefa", "human", "humano", "approval", "aprovação"],
        handler: this.createUserTask
      }, {
        ids: ["service", "servico", "integração", "api"],
        handler: this.createServiceTask
      }, {
        ids: ["paralelo", "parallel", "parallel-convergent"],
        handler: this.createParallelGateway
      }, {
        ids: ["exclusiveGateway", "condicional", "exclusivo", "exclusive", "decision", "gateway", "xor", "or"],
        handler: this.createExclusiveGateway
      }, {
        ids: ["email", "mail", "mensagem", "message"],
        handler: this.createMessageEvent
      }, {
        ids: ["end", "fim"],
        handler: this.createTerminateEvent
      }, {
        ids: ["timer", "relogio"],
        handler: this.createTimerEvent
      }, {
        ids: ["output-link"],
        handler: this.createOutputLinkEvent
      }, {
        ids: ["reentrance-link"],
        handler: this.createReentranceLinkEvent
      }];
      let {
        handler
      } = handlers.find(h => h.ids.some(id => stringContains(id, type)));
      if (!handler) {
        console.info(`Handler ${type} não encontrado.`);
        handler = this.createUserTask;
      }
      return handler.bind(this);
    }
    createUserTask(lane, name, position, from) {
      return this._createElement(lane, {
        type: "bpmn:UserTask"
      }, name, from, position);
    }
    createServiceTask(lane, name, position, from) {
      return this._createElement(lane, {
        type: "bpmn:ServiceTask"
      }, name, from, position);
    }
    createParallelGateway(lane, name, position, from) {
      return this._createElement(lane, {
        type: "bpmn:ParallelGateway"
      }, name, from, position);
    }
    createExclusiveGateway(lane, name, position, from) {
      return this._createElement(lane, {
        type: "bpmn:ExclusiveGateway"
      }, name, from, position);
    }
    createMessageEvent(lane, name, position, from) {
      return this._createElement(lane, {
        type: "bpmn:IntermediateThrowEvent",
        eventDefinitionType: "bpmn:MessageEventDefinition"
      }, name, from, position);
    }
    createTerminateEvent(lane, name, position, from) {
      return this._createElement(lane, {
        type: "bpmn:EndEvent",
        eventDefinitionType: "bpmn:TerminateEventDefinition"
      }, name, from, position);
    }
    createTimerEvent(lane, name, position, from) {
      return this._createElement(lane, {
        type: "bpmn:IntermediateCatchEvent",
        eventDefinitionType: "bpmn:TimerEventDefinition"
      }, name, from, position);
    }
    createOutputLinkEvent(lane, name, position, from) {
      return this._createElement(lane, {
        type: "bpmn:IntermediateThrowEvent",
        eventDefinitionType: "bpmn:LinkEventDefinition"
      }, name, from, position);
    }
    createReentranceLinkEvent(lane, name, position, from) {
      return this._createElement(lane, {
        type: "bpmn:IntermediateCatchEvent",
        eventDefinitionType: "bpmn:LinkEventDefinition"
      }, name, from, position);
    }
    getStartEvent() {
      return this._elementRegistry.find(element => element.type === "bpmn:StartEvent");
    }
    createStartEvent(lane, name, position) {
      const startEvent = this.getStartEvent();
      if (startEvent) {
        this._modeling.removeElements([startEvent]);
      }
      return this._createElement(lane, {
        type: "bpmn:StartEvent"
      }, name, undefined, position);
    }

    //#endregion Tasks and Events

    //#region Elements Utils

    getElement(elementId) {
      return this._elementRegistry.get(elementId);
    }
    getMid(element) {
      return {
        x: element.x + element.width / 2,
        y: element.y + element.height / 2
      };
    }
    getElementBounds(element) {
      return getDi(element).bounds;
    }
    renameElement(element, name) {
      this._modeling.updateProperties(element, {
        name
      });
    }

    //#endregion Elements Utils

    //#region Create and Positioning

    appendShape(source, shape, position) {
      if (position) {
        this._modeling.appendShape(source, shape, position, source.parent);
      } else {
        this._autoPlace.append(source, shape);
      }
    }
    connectElements(sourceId, targetId, preferredLayouts = ["h:h"], layout = undefined) {
      const source = this.getElement(sourceId);
      const target = this.getElement(targetId);

      // se source e target ja estao conectados, entao retorna
      if (source.outgoing.some(c => c.target === target)) return;
      const hints = {
        preferredLayouts
      };
      if (layout) {
        hints.preferredLayouts = [layout];
        this._modeling.createConnection(source, target, undefined, {
          type: "bpmn:SequenceFlow"
        }, source.parent, hints);
      } else {
        this._modeling.connect(source, target, undefined, hints);
      }
    }
    _createElement(lane, {
      type,
      eventDefinitionType
    }, name, sourceId, position) {
      const shape = this._createShape(type, eventDefinitionType, name);
      const source = this.getElement(sourceId);
      if (position) {
        this.appendShape(lane, shape, position);
      } else if (source) {
        this.appendShape(source, shape);
        this._ajustHorizontalDistance(shape, source, position);
        this._moveElementToLane(shape, lane, source);
      } else {
        this.appendShape(lane, shape);
        this._moveElementToLane(shape, lane, source);
      }
      return shape;
    }
    _createShape(type, eventDefinitionType, name) {
      const businessObject = this._bpmnFactory.create(type, {
        name
      });
      return this._elementFactory.createShape({
        type,
        eventDefinitionType,
        businessObject
      });
    }

    /**
     * Move um elemento para uma raia.
     */
    _moveElementToLane(element, lane, source) {
      if (source && lane.id === source.businessObject.lanes[0]?.id && this.getMid(element).y === this.getMid(source).y) {
        // o elemento ja esta na posicao correta.
        return;
      }

      // pega atividades, eventos e gateways na mesma posicao X do elemento.
      const prefixes = ["Act", "Eve", "Gat"];
      const midX = this.getMid(element).x;
      const elementsAtSameXAxis = this.getLaneElements(lane.id, e => {
        return e.id !== element.id && prefixes.some(p => e.id.startsWith(p)) && e.type !== "label" && midX === this.getMid(e).x;
      }).sort((a, b) => a.y > b.y ? 1 : -1);
      const delta = this._getDelta(element, lane);
      if (elementsAtSameXAxis.length > 0) {
        const midY = this.getMid(element).y;
        let i = 0;
        if (delta.y < 0) i = elementsAtSameXAxis.length - 1;
        for (; i < elementsAtSameXAxis.length; i++) {
          const e = elementsAtSameXAxis[i];
          const newMidY = midY + delta.y;
          // se ja existe um elemento na posicao alvo, entao pega uma mais abaixo.
          if (newMidY === this.getMid(e).y) delta.y += LANE_HEIGHT;
        }
      }

      // se a nova posicao do elemento ultrapassa a raia,
      // entao aumenta a altura da raia
      const newY = element.y + delta.y;
      if (newY > lane.y + lane.height) {
        this.incrementLaneHeight(lane.id);
        // apos aumentar a altura da raia, o valor y do elemento aumentou junto.
        // assim, eh necessario ajustar o delta.y para a nova posicao do elemento.
        delta.y = newY - element.y;
      }
      this._modeling.moveElements([element], delta);
    }
    _getDelta(element, lane) {
      const lbh = LANE_HEIGHT;
      let y = 0;
      // se o elemento esta acima da raia, entao move para baixo.
      if (element.y < lane.y) {
        y = lane.y + lbh / 2 - element.y - element.height / 2;
      } else {
        // senao, move para cima.
        y = lane.y + lane.height - lbh / 2 - element.y - element.height / 2;
      }
      return {
        x: 0,
        y
      };
    }
    _ajustHorizontalDistance(element, source) {
      const elementX = this.getMid(element).x;
      const sourceX = this.getMid(source).x;
      const distance = elementX - sourceX;
      if (distance !== 160) {
        this._modeling.moveElements([element], {
          x: LANE_WIDTH - distance,
          y: 0
        });
      }
    }

    //#endregion Create and Positioning

    //#region Overlays

    _addOverlay(bpmnId, html, position = "bottomRight") {
      const positions = {
        topLeft: {
          top: 0,
          left: 0
        },
        topRight: {
          top: 0,
          right: 0
        },
        bottomLeft: {
          bottom: 0,
          left: 0
        },
        bottomRight: {
          bottom: 6,
          right: 6
        }
      };
      this._overlays.add(bpmnId, {
        position: positions[position],
        html: html
      });
    }
    _removeOverlay(bpmnId) {
      this._overlays.remove({
        element: bpmnId
      });
    }
    addErrorOverlay(bpmnId, message) {
      const html = `<div class="text-danger" title="${message}"><wc-icon size="xl" icon="x-circle"></wc-icon></div>`;
      this._addOverlay(bpmnId, html);
    }
    addWarningOverlay(bpmnId, message) {
      const html = `<div class="text-warning" title="${message}"><wc-icon size="xl" icon="alert-triangle"></wc-icon></div>`;
      this._addOverlay(bpmnId, html);
    }

    //#endregion Overlays
  }

  class Grid {
    constructor(rows, cols, lanes) {
      if (rows <= 0 || cols <= 0) {
        throw new Error(`Invalid grid dimensions: ${rows}x${cols}`);
      }
      if (!lanes || lanes.length === 0) {
        throw new Error("At least one lane is required");
      }
      this.rows = rows;
      this.cols = cols;
      this.data = Array.from({
        length: rows
      }, () => Array(cols).fill(null));
      this.values = new Map();
      this.lanes = lanes;
      this.laneRows = [];
    }
    addLaneRow(lane) {
      this.laneRows.push(lane);
    }
    getLaneFirstRow(lane) {
      return this.laneRows.indexOf(lane);
    }
    getLaneLastRow(lane) {
      return this.laneRows.lastIndexOf(lane);
    }
    removeLaneRow(row) {
      this.laneRows.splice(row, 1);
    }
    setCell(row, col, value) {
      if (this.isValidCell(row, col)) {
        this.data[row][col] = value;
        if (value) {
          this.values.set(value, {
            row,
            col
          });
          value.row = row;
        }
      } else {
        throw new Error(`Invalid cell position: ${row}x${col} of ${this.rows}x${this.cols} grid`);
      }
    }
    getCell(row, col) {
      if (this.isValidCell(row, col)) {
        return this.data[row][col];
      }
      throw new Error("Invalid cell position");
    }
    getPosition(value) {
      return this.values.get(value);
    }
    isValidCell(row, col) {
      return row >= 0 && row < this.rows && col >= 0 && col < this.cols;
    }
    hasValue(row, col) {
      return this.isValidCell(row, col) && this.data[row][col] !== null;
    }
    addRow() {
      this.data.push(Array(this.cols).fill(null));
      this.rows++;
    }
    removeRow(rowIndex) {
      if (rowIndex >= 0 && rowIndex < this.rows) {
        this.data.splice(rowIndex, 1);
        this.rows--;
        this.removeLaneRow(rowIndex);
        this.values.forEach(({
          row,
          col
        }, value) => {
          if (row >= rowIndex) {
            this.values.set(value, {
              row: row - 1,
              col
            });
            value.row = row - 1;
          }
        });
      } else {
        throw new Error("Invalid row index");
      }
    }
    tryRemoveRow(row) {
      if (row >= 0 && row < this.rows) {
        if (!this.data[row].some(cell => cell)) {
          this.removeRow(row);
          return true;
        }
        return false;
      } else {
        throw new Error("Invalid row index");
      }
    }
    swap(rowA, colA, rowB, colB) {
      const t = this.getCell(rowA, colA);
      this.setCell(rowA, colA, this.getCell(rowB, colB));
      this.setCell(rowB, colB, t);
    }
    print() {
      // eslint-disable-next-line no-undef
      if (null === "dev") console.table(this.data.map(row => [...row.map(cell => cell ? cell.name : "")]));
    }
  }

  /**
   * Verifica se existe um caminho livre entre dois elementos.
   * HB: Horizontal to Bottom.
   * HT: Horizontal to Top.
   */
  function hasClearPath_HB_HT(grid, source, target) {
    return isHorizontalPathClear(grid, source.row, source.col, target.col, source, target) && isVerticalPathClear(grid, target.col, source.row, target.row, source, target);
  }

  /**
   * Verifica se existe um caminho livre entre dois elementos.
   * TH: Top to Horizontal.
   * BH: Bottom to Horizontal.
   */
  function hasClearPath_TH_BH(grid, source, target) {
    return isHorizontalPathClear(grid, target.row, source.col, target.col, source, target) && isVerticalPathClear(grid, source.col, source.row, target.row, source, target);
  }

  /**
   * Verifica se existe um caminho livre entre dois elementos.
   * HH: Horizontal to Horizontal.
   */
  function hasClearPath_HH(grid, source, target) {
    return isHorizontalPathClear(grid, source.row, source.col, target.col, source, target);
  }
  function isHorizontalPathClear(grid, row, col1, col2, source, target) {
    let minCol = Math.min(col1, col2),
      maxCol = Math.max(col1, col2);
    for (let col = minCol; col <= maxCol; col++) {
      const v = grid.data[row][col];
      if (v != null && v != source && v != target) return false;
    }
    return true;
  }
  function isVerticalPathClear(grid, col, row1, row2, source, target) {
    let minRow = Math.min(row1, row2),
      maxRow = Math.max(row1, row2);
    for (let row = minRow; row <= maxRow; row++) {
      const v = grid.data[row][col];
      if (v != null && v != source && v != target) return false;
    }
    return true;
  }

  const COLUMN_START = 0;
  const ROW_START = 0;
  const NEW_ELEMENT_ID = 100;
  const ACTIVITY_HEIGHT = 80;
  function _debug(...args) {
    // eslint-disable-next-line no-undef
    if (null === "dev") console.debug(...args);
  }
  class JsonCliModeling extends CliModeling {
    constructor(modeler) {
      super(modeler);
      this.elMap = new Map();
      this.lastId = NEW_ELEMENT_ID;
      this.reentranceLinks = [];
    }

    /**
     * Monta o diagrama BPMN a partir de processo de negocio no formato JSON.
     * @param {String} jsonBusinessProcess
     */
    async build(jsonBusinessProcess) {
      const {
        elements
      } = JSON.parse(jsonBusinessProcess);
      this.elements = elements;
      for (const e of elements) this.elMap.set(e.id, e);
      this.firstElement = this.elements[0];
      try {
        this._defineElementPositions();
        this._drawLanes("Nome do processo");
        await this._drawElements();
      } catch (err) {
        console.error(err);
        throw err;
      } finally {
        this._reset();
      }
    }
    _reset() {
      this.elements = [];
      this.elMap.clear();
      this.firstElement = null;
      this.grid = null;
      this.lastId = NEW_ELEMENT_ID;
      this.reentranceLinks = [];
    }

    /**
     * Monta as raias (lanes) a partir das roles dos elementos.
     * @param {String} processName
     */
    _drawLanes(processName) {
      const participant = this.getOrCreateParticipant(processName);
      this.createFirstLane(participant, this.grid.lanes[0]);
      for (let i = 1; i < this.grid.lanes.length; i++) {
        this.createLane(participant, this.grid.lanes[i]);
      }
      this.getLanes().forEach(lane => {
        const rows = this.grid.laneRows.filter(x => x === lane.businessObject.name).length;
        this.resizeLane(lane, LANE_HEIGHT * rows, 20);
      });
      this.firstLane = this.getLanes()[0];
    }

    /**
     * Desenha o modelo de processo.
     */
    async _drawElements() {
      this.firstElement.bpmnId = this._addElement(this.firstElement);
      const visited = new Set();
      const queue = [this.firstElement];
      while (queue.length) {
        const curr = queue.shift();
        if (visited.has(curr)) continue;
        visited.add(curr);
        for (const {
          id
        } of curr.next) {
          const nextElement = this.elMap.get(id);
          if (!nextElement.bpmnId) {
            nextElement.bpmnId = this._addElement(nextElement, curr);
          }
          this._connect(curr, nextElement);
          queue.push(nextElement);
          await delay(100);
        }
      }
      for (let i = this.reentranceLinks.length - 1; i >= 0; i--) {
        this._addReentranceLink(this.reentranceLinks[i]);
      }
    }
    _addElement(element, source) {
      const x = this.firstLane.x + LANE_WIDTH * (element.col + 1) - LANE_WIDTH / 2;
      const y = this.firstLane.y + LANE_HEIGHT * (element.row + 1) - LANE_HEIGHT / 2;
      const position = {
        x,
        y
      };
      const lane = this.getLaneByName(element.role);
      return this.createElement(element.type, element.name, lane, position, source?.bpmnId).id;
    }
    _connect(source, target) {
      let connectionLayout = "h:h";
      if (source.row === target.row) {
        if (!hasClearPath_HH(this.grid, source, target)) {
          connectionLayout = "b:b";
        }
      } else if (source.row < target.row) {
        if (hasClearPath_HB_HT(this.grid, source, target)) {
          connectionLayout = "h:t";
        } else if (hasClearPath_TH_BH(this.grid, source, target)) {
          connectionLayout = "b:h";
        }
      } else if (source.row > target.row) {
        if (hasClearPath_HB_HT(this.grid, source, target)) {
          connectionLayout = "h:b";
        } else if (hasClearPath_TH_BH(this.grid, source, target)) {
          connectionLayout = "t:h";
        }
      }
      this.connectElements(source.bpmnId, target.bpmnId, null, connectionLayout);
    }
    _addReentranceLink(element) {
      const lane = this.getLaneByName(element.role);
      const targetBpmnId = this.elMap.get(element.next[0].id).bpmnId;
      const target = this.getElement(targetBpmnId);
      const {
        x,
        y
      } = this.getMid(target);
      this.incrementLaneHeight(lane.id, y + ACTIVITY_HEIGHT / 2);
      const position = {
        x: x - LANE_WIDTH / 2,
        y: y + LANE_HEIGHT
      };
      const linkBpmnId = this.createElement(element.type, element.name, lane, position, targetBpmnId).id;
      this.connectElements(linkBpmnId, targetBpmnId, ["h:b"]);
    }
    _defineElementPositions() {
      this._defineHorizontalPosition();
      this._setGrid();
      this._assignElementRows();
      this._changeElementRows();
      this._orderElements();
      this._mergeRows();
    }

    /**
     * Define a posicao horizontal dos elementos.
     * Para cada elemento eh definido um valor de 'coluna'.
     * Utiliza um algoritmo de ordenacao topologica.
     */
    _defineHorizontalPosition() {
      const visited = new Set();
      const queue = [this.firstElement];
      this.firstElement.col = COLUMN_START;
      this.firstElement.previous = [];
      while (queue.length) {
        const curr = queue.shift();
        if (visited.has(curr)) continue;
        visited.add(curr);
        for (const {
          id
        } of curr.next) {
          const nextElement = this.elMap.get(id);
          if (!nextElement.previous) nextElement.previous = [];
          nextElement.previous.push(curr);
          queue.push(nextElement);
          this._updateNextElementColumn(curr, nextElement, visited);
        }
      }
    }
    _updateNextElementColumn(currElement, nextElement, visited) {
      if (nextElement.col === undefined) {
        nextElement.col = currElement.col + 1;
        return;
      }
      if (nextElement.col === currElement.col) {
        nextElement.col = currElement.col + 1;
        nextElement.next.forEach(x => {
          if (visited.has(this.elMap.get(x.id)) && x.id != currElement.id) {
            this.elMap.get(x.id).col += 1;
          }
        });
        return;
      }
      if (nextElement.col < currElement.col) {
        this._createLinks(currElement, nextElement);
      }
    }
    _createLinks(currElement, nextElement) {
      const outputLinkId = this.lastId++;
      const reentranceLinkId = this.lastId++;
      const link = this._createOutputLink(outputLinkId, currElement);
      currElement.next.find(n => n.id === nextElement.id).id = link.id;
      this.elements.push(link);
      this.elMap.set(link.id, link);
      this.reentranceLinks.push(this._createReentranceLink(reentranceLinkId, nextElement));
      nextElement.previous.pop();
    }
    _createOutputLink(newId, sourceElement) {
      return {
        id: newId,
        name: `S${newId}:\nVai para C${newId + 1}`,
        type: "output-link",
        role: sourceElement.role,
        col: sourceElement.col + 1,
        next: [],
        previous: [sourceElement]
      };
    }
    _createReentranceLink(newId, targetElement) {
      return {
        id: newId,
        name: `C${newId}:\nVem de S${newId - 1}`,
        type: "reentrance-link",
        role: targetElement.role,
        col: targetElement.col - 1,
        next: [{
          id: targetElement.id
        }],
        previous: []
      };
    }

    /**
     * Cria um grid para posicionar os elementos.
     * Cada raia eh representada por uma ou mais linhas.
     */
    _setGrid() {
      const lanes = [...new Set(this.elements.map(e => e.role))];
      const lastColumn = this.elements.reduce((acc, x) => Math.max(acc, x.col), 0);
      this.grid = new Grid(this.elements.length, lastColumn + 1, lanes);
    }

    /**
     * Atribui uma linha para cada elemento.
     */
    _assignElementRows() {
      const lanes = this.grid.lanes;
      this.elements.sort((a, b) => lanes.indexOf(a.role) - lanes.indexOf(b.role)).forEach((e, row) => {
        this.grid.setCell(row, e.col, e);
        this.grid.addLaneRow(e.role);
      });
      this.grid.print();
    }

    /**
     * Altera a linha do elemento a fim de diminuir a distancia das suas conexoes.
     * Pode mover para cima e para baixo.
     */
    _changeElementRows() {
      for (let column = COLUMN_START; column < this.grid.cols; column++) {
        for (let row = ROW_START; row < this.grid.rows; row++) {
          if (this.grid.hasValue(row, column)) {
            const element = this.grid.getCell(row, column);
            const distA = this._calculateDistanceFromPreviousElements(element);
            const distB = this._calculateDistanceToNextElements(element);
            _debug(`${element.name}:${element.row}`, "=>", distA[1], distB[1]);
            // Se alguma distancia for zero, entao o elemento esta alinhado
            // com outro e entao nao precisa ser movido.
            if (distA[1].includes(0) || distB[1].includes(0)) continue;
            this._changeElementRow(element, distA[0], distB[0]);
            row = element.row;
          }
        }
      }
    }
    _calculateDistanceFromPreviousElements(el) {
      if (el.previous.length === 0) return [undefined, []];
      const distances = el.previous.map(p => p.row - el.row);
      const totalDistance = distances.reduce((a, c) => a + c);
      return [totalDistance, distances];
    }
    _calculateDistanceToNextElements(el) {
      if (el.next.length === 0) return [undefined, []];
      const distances = el.next.map(n => {
        n = this.elMap.get(n.id);
        return n.row - el.row;
      });
      const totalDistance = distances.reduce((a, c) => a + c);
      return [totalDistance, distances];
    }
    _changeElementRow(el, d1, d2) {
      if (d1 + d2 <= 0 || d1 < 0 && d2 === undefined || d2 < 0 && d1 === undefined || d1 === -1) {
        this._moveElementToRowAbove(el);
      } else if (d1 + d2 > 0 || d1 > 0 && d2 === undefined || d2 > 0 && d1 === undefined) {
        this._moveElementToRowBelow(el);
      }
    }
    _moveElementToRowAbove(el) {
      let t = this.grid.getLaneFirstRow(el.role);
      for (let newRow = el.row - 1; newRow >= t; newRow--) {
        if (this.grid.hasValue(newRow, el.col)) continue;
        const row = el.row;
        this.grid.swap(el.row, el.col, newRow, el.col);
        if (this.grid.tryRemoveRow(row)) {
          t = this.grid.getLaneFirstRow(el.role);
        }
        const d1 = this._calculateDistanceFromPreviousElements(el);
        const d2 = this._calculateDistanceToNextElements(el);
        _debug(`${el.name}:${el.row}`, "=>", d1[1], d2[1]);
        if (d1[1].includes(0) || d2[1].includes(0)) break;
      }
    }
    _moveElementToRowBelow(el) {
      let t = this.grid.getLaneLastRow(el.role);
      for (let newRow = el.row + 1; newRow <= t; newRow++) {
        if (this.grid.hasValue(newRow, el.col)) continue;
        const row = el.row;
        this.grid.swap(el.row, el.col, newRow, el.col);
        if (this.grid.tryRemoveRow(row)) {
          t = this.grid.getLaneLastRow(el.role);
          newRow--;
        }
        const d1 = this._calculateDistanceFromPreviousElements(el);
        const d2 = this._calculateDistanceToNextElements(el);
        _debug(`${el.name}:${el.row}`, "=>", d1[1], d2[1]);
        if (d1[1].includes(0) || d2[1].includes(0)) break;
      }
    }

    /**
     * Ordena os elementos em cada coluna.
     */
    _orderElements() {
      for (let column = COLUMN_START; column < this.grid.cols; column++) {
        const elementsInColumn = this.elements.filter(e => e.col === column);
        for (let i = 0; i < elementsInColumn.length; i++) {
          for (let j = i + 1; j < elementsInColumn.length; j++) {
            const elementA = elementsInColumn[i];
            const elementB = elementsInColumn[j];

            // Se os elementos nao estao na mesma raia, entao nao precisa ordenar
            // pois eles nao podem ser trocados.
            if (elementA.role !== elementB.role) continue;
            const rowA = elementA.row;
            const rowB = elementB.row;
            _debug(`compare:${elementA.name}:${rowA}x${elementB.name}:${rowB}`);

            // Calcula a distancia entre os elementos na posicao atual.
            const distanceCurrent = this._calculateDistance(elementA, rowA) + this._calculateDistance(elementB, rowB);

            // Calcula a distancia entre os elementos se eles trocassem de posicao.
            const distanceSwapped = this._calculateDistance(elementA, rowB) + this._calculateDistance(elementB, rowA);

            // Se a distancia entre os elementos trocados for menor, entao troca.
            if (distanceSwapped < distanceCurrent) {
              this.grid.swap(rowA, column, rowB, column);
              _debug(`swap:${elementA.name}:${rowA}x${elementB.name}:${rowB}`);
            }
          }
        }
      }
    }

    /**
     * Mescla linhas.
     */
    _mergeRows() {
      for (let row = ROW_START; row < this.grid.rows - 1; row++) {
        // Se a proxima linha esta em outra raia entao nao eh possivel mesclar.
        if (this.grid.laneRows[row] !== this.grid.laneRows[row + 1]) continue;
        let canMerge = true;
        for (let column = COLUMN_START; column < this.grid.cols; column++) {
          // Verifica se as linhas tem elementos nas mesmas colunas.
          if (this.grid.hasValue(row, column) && this.grid.hasValue(row + 1, column)) {
            canMerge = false;
            break;
          }
        }
        if (!canMerge) continue;
        for (let column = COLUMN_START; column < this.grid.cols; column++) {
          if (this.grid.hasValue(row, column)) {
            this.grid.swap(row, column, row + 1, column);
          }
        }
        this.grid.tryRemoveRow(row);
        // Depois que a linha 0 eh excluida, a linha 1 passa a ser a nova 0.
        // Por isso eh necessario reavaliar a linha 0 e assim por diante.
        row--;
      }
    }
    _calculateDistance(element, row) {
      const distancePrev = element.previous.reduce((acc, curr) => acc + Math.abs(row - curr.row), 0);
      const distanceNext = element.next.reduce((acc, curr) => {
        curr = this.elMap.get(curr.id);
        return acc + Math.abs(row - curr.row);
      }, 0);
      _debug(`${element.name}:${row}`, "=>", distancePrev, distanceNext);
      return distancePrev + distanceNext;
    }
  }

  const bpmnCustomEvents = Object.freeze({
    BPMN_ELEMENT_DELETE: "bpmnElementDelete",
    BPMN_SELECTION_REMOVE: "bpmnSelectionRemove",
    BPMN_ELEMENT_EDIT: "bpmnElementEdit",
    BPMN_ELEMENTS_CHANGED: "bpmnElementsChanged",
    BPMN_CLI_MODELING: "bpmnCliModeling",
    BPMN_PALETTE_ENTRY_HELP: "bpmnPaletteEntryHelp",
    BPMN_PALETTE_SEARCH: "bpmnPaletteSearch",
    BPMN_SKIN_CHANGE: "bpmnSkinChange",
    BPMN_OPEN_SUBPROCESS: "bpmnOpenSubprocess",
    BPMN_LOAD_MODELING_CALLBACK: "bpmnLoadModelingCallback"
  });

  var name = "Custom Tasks";
  var uri = "http://zeev.it/schema/bpmn/custom-tasks";
  var prefix = "zeev";
  var xml = {
  	tagAlias: "lowerCase"
  };
  var types$1 = [
  	{
  		name: "ServiceTask",
  		"extends": [
  			"bpmn:ServiceTask"
  		],
  		properties: [
  			{
  				name: "specialTaskType",
  				isAttr: true,
  				type: "String"
  			}
  		]
  	}
  ];
  var bpmnExtensionModel = {
  	name: name,
  	uri: uri,
  	prefix: prefix,
  	xml: xml,
  	types: types$1
  };

  var metaElements = [
  	{
  		comment: "É obrigatório que o grupo abaixo se chame `tools`, pois esta hardcoded no bpmn.js",
  		groupId: "tools",
  		title: "Tools",
  		className: "group-tools",
  		elements: [
  			{
  				zeevId: "hand-tool",
  				type: null,
  				classList: [
  					"zv-tool",
  					"zv-tool-hand",
  					"bpmn-icon-hand-tool"
  				],
  				title: "Move diagram",
  				action: "handTool",
  				palette: true,
  				help: null,
  				skins: {
  					classic: {
  						icon: "move-diagram.svg"
  					},
  					friendly: {
  						icon: "move-diagram.svg"
  					},
  					"default": {
  						icon: "move-diagram.svg"
  					}
  				}
  			},
  			{
  				zeevId: "lasso-tool",
  				type: null,
  				classList: [
  					"zv-tool",
  					"zv-tool-lasso",
  					"bpmn-icon-lasso-tool"
  				],
  				title: "Select elements",
  				action: "lassoTool",
  				palette: true,
  				help: null,
  				skins: {
  					classic: {
  						icon: "select-elements.svg"
  					},
  					friendly: {
  						icon: "select-elements.svg"
  					},
  					"default": {
  						icon: "select-elements.svg"
  					}
  				}
  			},
  			{
  				zeevId: "space-tool",
  				type: null,
  				classList: [
  					"zv-tool",
  					"zv-tool-space",
  					"bpmn-icon-space-tool"
  				],
  				title: "Create or remove space",
  				action: "spaceTool",
  				palette: true,
  				help: null,
  				skins: {
  					classic: {
  						icon: "create-space.svg"
  					},
  					friendly: {
  						icon: "create-space.svg"
  					},
  					"default": {
  						icon: "create-space.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.participant-expanded",
  				type: "bpmn:Participant",
  				classList: [
  					"zv-tool",
  					"zv-tool-participant",
  					"bpmn-icon-participant"
  				],
  				title: "Pool/participant",
  				bpmnTitle: "Swinlanes",
  				action: "createParticipant",
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-usar-swinlanes",
  						img: "pool.gif",
  						resume: "Ajudam a organizar o desenho separando responsáveis",
  						full: "São utilizadas para delimitar o escopo do processo e quem são os atores responsáveis por atividades. A piscina pode receber o nome do processo e as raias os nomes dos atores.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					}
  				},
  				skins: {
  					classic: {
  						icon: "swinlane.svg"
  					},
  					friendly: {
  						icon: "swinlane.svg"
  					},
  					"default": {
  						icon: "swinlane.svg"
  					}
  				}
  			}
  		]
  	},
  	{
  		groupId: "events",
  		title: "Events",
  		className: "group-events",
  		elements: [
  			{
  				zeevId: "create.start-event",
  				type: "bpmn:StartEvent",
  				title: "Request",
  				bpmnTitle: "Start event",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-usar-eventos/evento-de-inicio",
  						img: "start-event.gif",
  						resume: "O pedido da solicitação, como \"Solicitar férias\"",
  						full: "Representa a solicitação realizada por uma pessoa ao iniciar um aplicativo, por exemplo, solicitar pedido de férias. Cada processo deve ter apenas um evento de início.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-use-events/start-event",
  						img: "start-event.gif",
  						resume: "The request for the application, such as \"Request vacation\"",
  						full: "Represents the request made by a person when starting an application, for example, requesting a vacation. Each process should have only one start event.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-diseñar-procesos/como-usar-eventos/evento-de-inicio",
  						img: "start-event.gif",
  						resume: "La solicitud de la aplicación, como \"Solicitar vacaciones\"",
  						full: "Representa la solicitud realizada por una persona al iniciar una aplicación, por ejemplo, solicitar vacaciones. Cada proceso debe tener solo un evento de inicio.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-event",
  					"zv-start-event",
  					"bpmn-icon-start-event-none"
  				],
  				skins: {
  					classic: {
  						icon: "start-event.svg"
  					},
  					friendly: {
  						icon: "start-event.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.throw-message-event",
  				type: "bpmn:IntermediateThrowEvent",
  				title: "Message",
  				bpmnTitle: "Message event",
  				options: {
  					eventDefinitionType: "bpmn:MessageEventDefinition"
  				},
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-usar-eventos/como-configurar-eventos-de-mensagens",
  						img: "message-event.gif",
  						resume: "Um e-mail enviado para uma pessoa ou grupo",
  						full: "Representa o envio de um e-mail a um usuário do Zeev, um grupo de usuários ou usuários externos. Pode ser utilizado para enviar notificações diversas.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-use-events/how-to-configure-message-events",
  						img: "message-event.gif",
  						resume: "An email sent to a person or group",
  						full: "Represents sending an email to a Zeev user, a group of users, or external users. It can be used to send various notifications.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-diseñar-procesos/como-usar-eventos/como-configurar-eventos-de-mensajes",
  						img: "message-event.gif",
  						resume: "Un correo electrónico enviado a una persona o grupo",
  						full: "Representa el envío de un correo electrónico a un usuario de Zeev, un grupo de usuarios o usuarios externos. Se puede usar para enviar varias notificaciones.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-event",
  					"zv-intermediate-event",
  					"zv-message-event",
  					"bpmn-icon-intermediate-event-throw-message"
  				],
  				skins: {
  					classic: {
  						icon: "message-event.svg"
  					},
  					friendly: {
  						icon: "message-event.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.event-milestone",
  				type: "bpmn:IntermediateThrowEvent",
  				title: "Milestone",
  				bpmnTitle: "Milestone event",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-usar-eventos/evento-de-marco",
  						img: "milestone-event.gif",
  						resume: "Delimita grandes etapas do processo",
  						full: "Delimita marcações ou milestones de processo. Pode ser utilizado para o acompanhamento de mais alto nível das etapas de um processo, como aprovação de pagamento, separação em estoque e entrega.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-use-events/milestone-event",
  						img: "milestone-event.gif",
  						resume: "Delimits major stages of the process",
  						full: "Delimits process markers or milestones. It can be used for high-level tracking of process stages, such as payment approval, stock separation, and delivery.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-diseñar-procesos/como-usar-eventos/evento-de-hito",
  						img: "milestone-event.gif",
  						resume: "Delimita grandes etapas del proceso",
  						full: "Delimita marcadores o hitos del proceso. Se puede usar para el seguimiento de alto nivel de las etapas de un proceso, como la aprobación de pagos, la separación de existencias y la entrega.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-event",
  					"zv-intermediate-event",
  					"zv-milestone-event",
  					"bpmn-icon-intermediate-event-none"
  				],
  				skins: {
  					classic: {
  						icon: "milestone-event.svg"
  					},
  					friendly: {
  						icon: "milestone-event.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.event-timer",
  				type: "bpmn:IntermediateCatchEvent",
  				title: "Timer",
  				bpmnTitle: "Timer event",
  				options: {
  					eventDefinitionType: "bpmn:TimerEventDefinition"
  				},
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-usar-eventos/como-configurar-timers",
  						img: "timer-event.gif",
  						resume: "Aguarda um tempo pré-definido",
  						full: "Permite aguardar a execução do processo conforme uma regra de tempo. Pode ser utilizado no curso do processo para que o fluxo fique parado por um tempo determinado e só após, tenha continuidade.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-use-events/how-to-configure-timers",
  						img: "timer-event.gif",
  						resume: "Waits for a predefined time",
  						full: "Allows the process to wait according to a time rule. It can be used during the process to pause the flow for a determined time and only then continue.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-diseñar-procesos/como-usar-eventos/como-configurar-temporizadores",
  						img: "timer-event.gif",
  						resume: "Espera un tiempo predefinido",
  						full: "Permite que el proceso espere según una regla de tiempo. Se puede usar durante el proceso para pausar el flujo por un tiempo determinado y solo después continuar.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-event",
  					"zv-intermediate-event",
  					"zv-timer-event",
  					"bpmn-icon-intermediate-event-catch-timer"
  				],
  				skins: {
  					classic: {
  						icon: "timer-event.svg"
  					},
  					friendly: {
  						icon: "timer-event.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.event-boundarytimer",
  				type: "bpmn:BoundaryEvent",
  				title: "Timer",
  				bpmnTitle: "Timer event",
  				options: {
  					eventDefinitionType: "bpmn:TimerEventDefinition"
  				},
  				associations: null,
  				palette: false,
  				help: null,
  				classList: [
  					"zv-element",
  					"zv-event",
  					"zv-intermediate-event",
  					"zv-timer-event",
  					"bpmn-icon-intermediate-event-catch-timer"
  				],
  				skins: {
  					classic: {
  						icon: "timer-event.svg"
  					},
  					friendly: {
  						icon: "timer-event.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.throw-link",
  				type: "bpmn:IntermediateThrowEvent",
  				title: "Output link",
  				bpmnTitle: "Intermediate throw event",
  				options: {
  					eventDefinitionType: "bpmn:LinkEventDefinition"
  				},
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-usar-eventos/como-configurar-links",
  						img: "output-link.gif",
  						resume: "Conecta elementos distantes, sem linhas. É o início da conexão",
  						full: "Realiza a conexão entre dois elementos. Representa o início desta conexão, que deve ser continuada a partir de um link de chegada. Pode ser utilizado, por exemplo, para retornar o fluxo do processo para uma tarefa de aprovação após serem identificadas inconformidades.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-use-events/how-to-configure-links",
  						img: "output-link.gif",
  						resume: "Connects distant elements without lines. It's the start of the connection",
  						full: "Connects two elements. Represents the start of this connection, which should be continued from an incoming link. It can be used, for example, to return the process flow to an approval task after identifying non-conformities.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-diseñar-procesos/como-usar-eventos/como-configurar-enlaces",
  						img: "output-link.gif",
  						resume: "Conecta elementos distantes sin líneas. Es el inicio de la conexión",
  						full: "Conecta dos elementos. Representa el inicio de esta conexión, que debe continuar a partir de un enlace entrante. Se puede usar, por ejemplo, para devolver el flujo del proceso a una tarea de aprobación después de identificar no conformidades.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-event",
  					"zv-intermediate-event",
  					"zv-output-link-event",
  					"bpmn-icon-intermediate-event-throw-link"
  				],
  				skins: {
  					classic: {
  						icon: "output-link-event.svg"
  					},
  					friendly: {
  						icon: "output-link-event.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.catch-link",
  				type: "bpmn:IntermediateCatchEvent",
  				title: "Re-entrance link",
  				bpmnTitle: "Intermediate catch event",
  				options: {
  					eventDefinitionType: "bpmn:LinkEventDefinition"
  				},
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-usar-eventos/como-configurar-links",
  						img: "reentrance-link.gif",
  						resume: "Conecta elementos distantes, sem linhas. É a chegada da conexão ",
  						full: "Realiza a conexão entre dois elementos. Representa a continuidade desta conexão, que deve ser iniciada a a partir de um link de saída. Pode ser utilizado para retornar o fluxo do processo para uma tarefa de aprovação após serem identificadas inconformidades.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-use-events/how-to-configure-links",
  						img: "reentrance-link.gif",
  						resume: "Connects distant elements without lines. It's the arrival of the connection",
  						full: "Connects two elements. Represents the continuation of this connection, which should be started from an outgoing link. It can be used to return the process flow to an approval task after identifying non-conformities.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-diseñar-procesos/como-usar-eventos/como-configurar-enlaces",
  						img: "reentrance-link.gif",
  						resume: "Conecta elementos distantes sin líneas. Es la llegada de la conexión",
  						full: "Conecta dos elementos. Representa la continuación de esta conexión, que debe iniciarse desde un enlace de salida. Se puede usar para devolver el flujo del proceso a una tarea de aprobación después de identificar no conformidades.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-event",
  					"zv-intermediate-event",
  					"zv-re-entrance-link-event",
  					"bpmn-icon-intermediate-event-catch-link"
  				],
  				skins: {
  					classic: {
  						icon: "re-entrance-link-event.svg"
  					},
  					friendly: {
  						icon: "re-entrance-link-event.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.end-partial",
  				type: "bpmn:EndEvent",
  				title: "Partial end",
  				bpmnTitle: "Partial end event",
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-usar-eventos/evento-de-fim-parcial",
  						img: "partial-end-event.gif",
  						resume: "Encerra uma ramificação, sem finalizar o processo",
  						full: "Determina o fim de um \"braço\" do processo. Ao contrário do evento de término / fim total, não finaliza a solicitação como um todo. É comumente utilizado para determinar o fim de uma ramificação paralela à principal em um fluxo de processo.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-use-events/partial-end-event",
  						img: "partial-end-event.gif",
  						resume: "Ends a branch without finishing the process",
  						full: "Determines the end of a 'branch' of the process. Unlike the total end event, it does not finish the request as a whole. It is commonly used to determine the end of a parallel branch to the main one in a process flow.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-diseñar-procesos/como-usar-eventos/evento-de-fin-parcial",
  						img: "partial-end-event.gif",
  						resume: "Termina una rama sin finalizar el proceso",
  						full: "Determina el fin de una 'rama' del proceso. A diferencia del evento de fin total, no finaliza la solicitud en su totalidad. Se usa comúnmente para determinar el fin de una rama paralela a la principal en un flujo de proceso.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-event",
  					"zv-end-event",
  					"zv-partial-end-event",
  					"bpmn-icon-end-event-none"
  				],
  				skins: {
  					classic: {
  						icon: "partial-end-event.svg"
  					},
  					friendly: {
  						icon: "partial-end-event.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.end-terminate",
  				type: "bpmn:EndEvent",
  				title: "Terminate",
  				bpmnTitle: "Terminate end event",
  				options: {
  					eventDefinitionType: "bpmn:TerminateEventDefinition"
  				},
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-usar-eventos/evento-de-fim-total",
  						img: "terminate-end-event.gif",
  						resume: "Finaliza completamente a solicitação",
  						full: "Representa a finalização total da solicitação. Todo processo precisa ter ao menos um fim total. É comumente utilizado para definir que um processo chegou a um fim, como, compra concluída ou compra rejeitada.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-use-events/terminate-end-event",
  						img: "terminate-end-event.gif",
  						resume: "Completely finishes the request",
  						full: "Represents the complete end of the request. Every process needs to have at least one total end. It is commonly used to define that a process has reached an end, such as completed purchase or rejected purchase.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-diseñar-procesos/como-usar-eventos/evento-de-fin-total",
  						img: "terminate-end-event.gif",
  						resume: "Finaliza completamente la solicitud",
  						full: "Representa la finalización total de la solicitud. Todo proceso necesita tener al menos un fin total. Se usa comúnmente para definir que un proceso ha llegado a un fin, como compra completada o compra rechazada.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-event",
  					"zv-end-event",
  					"zv-terminate-end-event",
  					"bpmn-icon-end-event-terminate"
  				],
  				skins: {
  					classic: {
  						icon: "terminate-end-event.svg"
  					},
  					friendly: {
  						icon: "terminate-end-event.svg"
  					}
  				}
  			}
  		]
  	},
  	{
  		groupId: "activities",
  		title: "Activities",
  		className: "group-activities",
  		elements: [
  			{
  				zeevId: "create.user-task",
  				type: "bpmn:UserTask",
  				title: "Task human",
  				bpmnTitle: "User task",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/o-que-sao-atividades/como-configurar-atividades-humanas",
  						img: "human-task.gif",
  						resume: "Uma tarefa ou trabalho a ser realizado",
  						full: "Representa um trabalho que é executado por uma pessoa. Por exemplo: aprovar solicitação de compra ou efetuar a compra de materiais.",
  						keywords: "trabalho, procedimento, aprovação, instrução, tarefa, atividade, ação, operação, execução, missão, incumbência, encargo, obrigação, dever, responsabilidade"
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/what-are-activities/how-to-configure-human-tasks",
  						img: "human-task.gif",
  						resume: "A task or job to be performed",
  						full: "Represents a job that is performed by a person. For example: approving a purchase request or making a material purchase.",
  						keywords: "work, procedure, approval, instruction, task, activity, action, operation, execution, mission, duty, responsibility"
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-disenar-procesos/que-son-actividades/como-configurar-tareas-humanas",
  						img: "human-task.gif",
  						resume: "Una tarea o trabajo a realizar",
  						full: "Representa un trabajo que es realizado por una persona. Por ejemplo: aprobar una solicitud de compra o realizar la compra de materiales.",
  						keywords: "trabajo, procedimiento, aprobación, instrucción, tarea, actividad, acción, operación, ejecución, misión, incumbencia, encargo, obligación, deber, responsabilidad"
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-task",
  					"zv-human-task",
  					"bpmn-icon-user-task"
  				],
  				skins: {
  					classic: {
  						icon: "human-task.svg"
  					},
  					friendly: {
  						icon: "human-task.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.business-rule-task",
  				type: "bpmn:BusinessRuleTask",
  				title: "Business rule",
  				bpmnTitle: "Business rule task",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/o-que-sao-atividades/o-que-e-atividade-de-regra-de-negocio",
  						img: "business-rules-task.gif",
  						resume: "Executa lógicas e operações pré-definidas",
  						full: "Representa uma lógica ou operação que é executada pelo sistema. Permite realizar automações como definir o valor de campos de formulário, modificar status ou executar regras de negócio.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/what-are-activities/what-is-business-rules-task",
  						img: "business-rules-task.gif",
  						resume: "Executes predefined logic and operations",
  						full: "Represents a logic or operation executed by the system. It allows automations such as setting form field values, modifying statuses, or executing business rules.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-disenar-procesos/que-son-actividades/que-es-tarea-de-reglas-de-negocio",
  						img: "business-rules-task.gif",
  						resume: "Ejecuta lógicas y operaciones predefinidas",
  						full: "Representa una lógica u operación ejecutada por el sistema. Permite realizar automatizaciones como definir valores de campos de formulario, modificar estados o ejecutar reglas de negocio.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-task",
  					"zv-business-rules-task",
  					"bpmn-icon-business-rule-task"
  				],
  				skins: {
  					classic: {
  						icon: "business-rules-task.svg"
  					},
  					friendly: {
  						icon: "business-rules-task.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.script-task",
  				type: "bpmn:ScriptTask",
  				title: "Code",
  				bpmnTitle: "Script task",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/o-que-sao-atividades/tarefa-de-script",
  						img: "script-task.gif",
  						resume: "Executa código customizado",
  						full: "Permite a execução de códigos em Javascrit, que podem realizar lógicas avançadas para manipular campos de formulários.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/what-are-activities/script-task",
  						img: "script-task.gif",
  						resume: "Executes custom code",
  						full: "Allows the execution of JavaScript code, which can perform advanced logic to manipulate form fields.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-disenar-procesos/que-son-actividades/tarea-de-script",
  						img: "script-task.gif",
  						resume: "Ejecuta código personalizado",
  						full: "Permite la ejecución de códigos en JavaScript, que pueden realizar lógicas avanzadas para manipular campos de formularios.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-task",
  					"zv-script-task",
  					"bpmn-icon-script-task"
  				],
  				skins: {
  					classic: {
  						icon: "script-task.svg"
  					},
  					friendly: {
  						icon: "script-task.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.service-task",
  				type: "bpmn:ServiceTask",
  				title: "Integration",
  				bpmnTitle: "Service task",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/o-que-sao-atividades/tarefa-de-servico",
  						img: "service-task.gif",
  						resume: "Realiza uma integração com outro sistema",
  						full: "Representa uma integração que é executada pelo aplicativo, conectando o Zeev a outro sistema. Pode ser utilizada para consultar dados em um CRM, ERP, entre outras aplicações.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/what-are-activities/service-task",
  						img: "service-task.gif",
  						resume: "Performs an integration with another system",
  						full: "Represents an integration executed by the application, connecting Zeev to another system. It can be used to query data from a CRM, ERP, or other applications.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-disenar-procesos/que-son-actividades/tarea-de-servicio",
  						img: "service-task.gif",
  						resume: "Realiza una integración con otro sistema",
  						full: "Representa una integración que es ejecutada por la aplicación, conectando Zeev con otro sistema. Puede ser utilizada para consultar datos en un CRM, ERP u otras aplicaciones.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-task",
  					"zv-service-task",
  					"bpmn-icon-service-task"
  				],
  				skins: {
  					classic: {
  						icon: "service-task.svg"
  					},
  					friendly: {
  						icon: "service-task.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.subprocess-task",
  				type: "bpmn:SubProcess",
  				title: "Subprocess",
  				bpmnTitle: "Task subprocess",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/o-que-sao-atividades/o-que-sao-subprocessos-e-como-usa-los",
  						img: "subprocess-task.gif",
  						resume: "Abre um outro processo já automatizado",
  						full: "Representa, em um único elemento, outro aplicativo de processo já criado. Pode ser utilizado para executar outro aplicativo existente, como, um aplicativo de contas a pagar dentro do aplicativo de compras.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/what-are-activities/what-are-subprocesses-and-how-to-use-them",
  						img: "subprocess-task.gif",
  						resume: "Opens another already automated process",
  						full: "Represents, in a single element, another pre-existing process application. It can be used to execute an existing application, such as an accounts payable application within a purchasing application.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-disenar-procesos/que-son-actividades/que-son-subprocesos-y-como-usarlos",
  						img: "subprocess-task.gif",
  						resume: "Abre otro proceso ya automatizado",
  						full: "Representa, en un solo elemento, otra aplicación de proceso ya creada. Puede ser utilizada para ejecutar una aplicación existente, como, una aplicación de cuentas por pagar dentro de la aplicación de compras.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-task",
  					"zv-subprocess",
  					"bpmn-icon-subprocess-collapsed"
  				],
  				skins: {
  					classic: {
  						icon: "subprocess.svg"
  					},
  					friendly: {
  						icon: "subprocess.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.reference-task",
  				type: "bpmn:Task",
  				title: "Reference",
  				bpmnTitle: "Task reference",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/o-que-sao-atividades/tarefa-de-referencia",
  						img: "reference-task.gif",
  						resume: "Repete todas as configurações de outra tarefa já existente",
  						full: "Copia o funcionamento e configuração de uma tarefa humana já existente. Pode ser utilizada em um processo que possua atividades recorrentes  para não ser necessário repetir a mesma configuração diversas vezes",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/what-are-activities/reference-task",
  						img: "reference-task.gif",
  						resume: "Repeats all configurations of another existing task",
  						full: "Copies the functionality and configuration of an existing human task. It can be used in a process with recurring activities to avoid repeating the same configuration multiple times.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-disenar-procesos/que-son-actividades/tarea-de-referencia",
  						img: "reference-task.gif",
  						resume: "Repite todas las configuraciones de otra tarea ya existente",
  						full: "Copia la funcionalidad y configuración de una tarea humana existente. Puede ser utilizada en un proceso con actividades recurrentes para evitar repetir la misma configuración varias veces.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-task",
  					"zv-reference-task",
  					"bpmn-icon-task"
  				],
  				skins: {
  					classic: {
  						icon: "reference-task.svg"
  					},
  					friendly: {
  						icon: "reference-task.svg"
  					}
  				}
  			}
  		]
  	},
  	{
  		groupId: "specials",
  		title: "Specials",
  		className: "group-specials",
  		elements: [
  			{
  				zeevId: "create.agent-task",
  				type: "bpmn:ServiceTask",
  				options: {
  					specialTaskType: "aiAgentTask"
  				},
  				title: "AI Agent",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: false,
  				classList: [
  					"zv-element",
  					"zv-special",
  					"zv-agent-task",
  					"bpmn-icon-custom-agent-task"
  				],
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/o-que-sao-atividades/tarefa-de-agente-de-ia",
  						img: null,
  						resume: "Execute um agente inteligente capaz de colaborar e executar ações no processo",
  						full: "Permite executar um Agente de IA. Um Agente consulta modelos de Inteligência Artificial para executar tarefas, como analisar dados, processar documentos, preencher campos e tomar decisoes.",
  						keywords: "IA, Inteligência, Inteligencia, Artificial, Robo, RPA, LLM, ChatGPT, OpenAI, Gemini, Claude, DeepSeek"
  					},
  					en: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/o-que-sao-atividades/tarefa-de-agente-de-ia",
  						img: null,
  						resume: "Run an intelligent agent capable of collaborating in the process",
  						full: "Allows you to run an AI Agent. An Agent consults Artificial Intelligence models to perform tasks, such as analyzing data, processing documents, filling in fields and making decisions.",
  						keywords: "AI, Intelligence, Intelligence, Artificial, Robot, RPA, LLM, ChatGPT, OpenAI, Gemini, Claude, DeepSeek"
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/o-que-sao-atividades/tarefa-de-agente-de-ia",
  						img: null,
  						resume: "Ejecutar un agente inteligente capaz de colaborar en el proceso",
  						full: "Le permite ejecutar un agente de IA. Un Agente consulta modelos de Inteligencia Artificial para realizar tareas, como analizar datos, procesar documentos, completar campos y tomar decisiones.",
  						keywords: "IA, Inteligencia, Inteligencia Artificial, Robot, RPA, LLM, ChatGPT, OpenAI, Gemini, Claude, DeepSeek"
  					}
  				},
  				skins: {
  					classic: {
  						icon: "ai-task.svg"
  					},
  					friendly: {
  						icon: "ai-task.svg"
  					},
  					"default": {
  						icon: "ai-task.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.msteams-task",
  				type: "bpmn:ServiceTask",
  				title: "MS Teams",
  				options: {
  					specialTaskType: "msTeamsTask"
  				},
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: false,
  				classList: [
  					"zv-element",
  					"zv-special",
  					"zv-msteams-task",
  					"bpmn-icon-custom-msteams-task"
  				],
  				help: {
  					pt: {
  						url: null,
  						img: null,
  						resume: "Executa operações no Microsoft Teams",
  						full: null,
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: null,
  						img: null,
  						resume: "Executes operations in Microsoft Teams",
  						full: null,
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: null,
  						img: null,
  						resume: "Ejecuta operaciones en Microsoft Teams",
  						full: null,
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				skins: {
  					classic: {
  						icon: "ms-teams-task.svg"
  					},
  					friendly: {
  						icon: "ms-teams-task.svg"
  					},
  					"default": {
  						icon: "ms-teams-task.svg"
  					}
  				}
  			}
  		]
  	},
  	{
  		groupId: "gateways",
  		title: "Gateways",
  		className: "group-gateways",
  		elements: [
  			{
  				zeevId: "create.parallel-gateway",
  				type: "bpmn:ParallelGateway",
  				title: "Parallel",
  				bpmnTitle: "Parallel gateway",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-configurar-gateways/gateway-paralelo",
  						img: "paralel-gateway.gif",
  						resume: "Todos os caminhos são iniciados em paralelo ou sincronizados. ",
  						full: "Representa um ponto de decisão no qual todos os caminhos são válidos. Pode ser utilizado após uma tarefa de \"Efetuar Venda\", para direcionar o fluxo do processo para tarefas como \"Emitir Nota Fiscal\", \"Despachar\" e \"Pagar comissão\", simultâneamente.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-configure-gateways/parallel-gateway",
  						img: "paralel-gateway.gif",
  						resume: "All paths are initiated in parallel or synchronized.",
  						full: "Represents a decision point where all paths are valid. It can be used after a task like 'Complete Sale' to direct the process flow to tasks such as 'Issue Invoice,' 'Dispatch,' and 'Pay Commission' simultaneously.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-disenar-procesos/como-configurar-gateways/gateway-paralelo",
  						img: "paralel-gateway.gif",
  						resume: "Todos los caminos se inician en paralelo o se sincronizan.",
  						full: "Representa un punto de decisión en el que todos los caminos son válidos. Puede ser utilizado después de una tarea como 'Completar Venta' para dirigir el flujo del proceso a tareas como 'Emitir Factura,' 'Despachar,' y 'Pagar Comisión' simultáneamente.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-gateway",
  					"zv-parallel-gateway",
  					"bpmn-icon-gateway-parallel"
  				],
  				skins: {
  					classic: {
  						icon: "parallel-gateway.svg"
  					},
  					friendly: {
  						icon: "parallel-gateway.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.exclusive-gateway",
  				type: "bpmn:ExclusiveGateway",
  				title: "Decision",
  				bpmnTitle: "Exclusive gateway",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-configurar-gateways/gateway-condicional-exclusivo",
  						img: "exclusive-gateway.gif",
  						resume: "Somente um caminho pode ser seguido",
  						full: "Representa um ponto de decisão no qual apenas um dos caminhos pode ser válido. Pode ser utilizado para direcionar o fluxo do processo para uma ramificação de aprovação, de reprovação ou outras, personalizáveis.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-configure-gateways/exclusive-gateway",
  						img: "exclusive-gateway.gif",
  						resume: "Only one path can be followed",
  						full: "Represents a decision point where only one of the paths can be valid. It can be used to direct the process flow to a branch of approval, rejection, or other customizable paths.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-disenar-procesos/como-configurar-gateways/gateway-condicional-exclusivo",
  						img: "exclusive-gateway.gif",
  						resume: "Solo se puede seguir un camino",
  						full: "Representa un punto de decisión en el cual solo uno de los caminos puede ser válido. Puede ser utilizado para dirigir el flujo del proceso a una ramificación de aprobación, rechazo u otros caminos personalizables.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-gateway",
  					"zv-exclusive-gateway",
  					"bpmn-icon-gateway-xor"
  				],
  				skins: {
  					classic: {
  						icon: "exclusive-gateway.svg"
  					},
  					friendly: {
  						icon: "exclusive-gateway.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.inclusive-gateway",
  				type: "bpmn:InclusiveGateway",
  				title: "Conditions",
  				bpmnTitle: "Inclusive gateway",
  				associations: [
  					"create.user-task",
  					"create.throw-message-event",
  					"create.exclusive-gateway"
  				],
  				palette: true,
  				help: {
  					pt: {
  						url: "https://kb.zeev.it/como-construir-processos/como-desenhar-processos/como-configurar-gateways/gateway-condicional-inclusivo",
  						img: "inclusive-gateway.gif",
  						resume: "Um ou mais caminhos podem ser seguidos. ",
  						full: "Representa um ponto de decisão no qual um ou mais caminhos podem ser válidos. Pode ser utilizado para definir quais áreas da empresa devem aprovar um pedido de compra com base em condicionais.",
  						keywords: "Pedido, Requisição, Demanda, Solicitação, Início, Abertura, Acionamento, Ativação, Convocação, Chamada."
  					},
  					en: {
  						url: "https://kb.zeev.it/how-to-build-processes/how-to-design-processes/how-to-configure-gateways/inclusive-gateway",
  						img: "inclusive-gateway.gif",
  						resume: "One or more paths can be followed.",
  						full: "Represents a decision point where one or more paths can be valid. It can be used to define which areas of the company need to approve a purchase request based on conditions.",
  						keywords: "Request, Application, Demand, Submission, Start, Opening, Trigger, Activation, Summons, Call."
  					},
  					es: {
  						url: "https://kb.zeev.it/como-construir-procesos/como-disenar-procesos/como-configurar-gateways/gateway-condicional-inclusivo",
  						img: "inclusive-gateway.gif",
  						resume: "Uno o más caminos pueden ser seguidos.",
  						full: "Representa un punto de decisión en el cual uno o más caminos pueden ser válidos. Puede ser utilizado para definir qué áreas de la empresa deben aprobar una solicitud de compra en función de condiciones.",
  						keywords: "Solicitud, Petición, Demanda, Requerimiento, Inicio, Apertura, Disparador, Activación, Convocatoria, Llamada."
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-gateway",
  					"zv-inclusive-gateway",
  					"bpmn-icon-gateway-or"
  				],
  				skins: {
  					classic: {
  						icon: "inclusive-gateway.svg"
  					},
  					friendly: {
  						icon: "inclusive-gateway.svg"
  					}
  				}
  			}
  		]
  	},
  	{
  		groupId: "artifacts",
  		title: "Artifacts",
  		className: "group-artifacts",
  		elements: [
  			{
  				zeevId: "create.group",
  				type: "bpmn:Group",
  				title: "Group",
  				palette: true,
  				help: {
  					pt: {
  						resume: "Permitir agrupar elementos, para facilitar compreensão"
  					},
  					en: {
  						resume: "Allows grouping elements to facilitate understanding"
  					},
  					es: {
  						resume: "Permite agrupar elementos para facilitar la comprensión"
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-artifact",
  					"zv-group",
  					"bpmn-icon-group"
  				],
  				skins: {
  					classic: {
  						icon: "group.svg"
  					},
  					friendly: {
  						icon: "group.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.data-object",
  				type: "bpmn:DataObjectReference",
  				title: "File",
  				palette: true,
  				help: {
  					pt: {
  						resume: "Representa um documento, para ajudar na documentação"
  					},
  					en: {
  						resume: "Represents a document to aid in documentation"
  					},
  					es: {
  						resume: "Representa un documento para ayudar en la documentación"
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-artifact",
  					"zv-file",
  					"bpmn-icon-data-object"
  				],
  				skins: {
  					classic: {
  						icon: "file.svg"
  					},
  					friendly: {
  						icon: "file.svg"
  					}
  				}
  			},
  			{
  				zeevId: "create.data-store",
  				type: "bpmn:DataStoreReference",
  				className: "bpmn-icon-data-store",
  				title: "Database",
  				palette: true,
  				help: {
  					pt: {
  						resume: "Utilizado na documentação na processo"
  					},
  					en: {
  						resume: "Used in process documentation"
  					},
  					es: {
  						resume: "Utilizado en la documentación del proceso"
  					}
  				},
  				classList: [
  					"zv-element",
  					"zv-artifact",
  					"zv-database",
  					"bpmn-icon-data-store"
  				],
  				skins: {
  					classic: {
  						icon: "database.svg"
  					},
  					friendly: {
  						icon: "database.svg"
  					}
  				}
  			}
  		]
  	}
  ];

  function createModal({
    url = "",
    endpoint = "",
    caption = "",
    size = "",
    keyboard = "",
    backdrop = "",
    fullscreen = "",
    centered = "",
    body = "",
    footer = "",
    header = "",
    autofocus = true,
    ctrlclick = false
  } = {}) {
    if (_dialogsCtrlClick(ctrlclick, url, window.event)) return;
    const component = document.createElement("wc-modal");
    component.url = url;
    component.endpoint = endpoint;
    component.size = size;
    component.caption = caption;
    component.keyboard = keyboard;
    component.backdrop = backdrop;
    component.fullscreen = fullscreen;
    component.centered = centered;
    component.header = header;
    component.body = body;
    component.footer = footer;
    component.autofocus = autofocus;
    document.body.appendChild(component);
    component.show();
    return component;
  }
  function alert({
    message = "",
    okCallback = null
  }) {
    //for legacy support
    if (typeof arguments[0] === "string") {
      message = arguments[0];
      okCallback = arguments[1];
    }
    if (!message) return;
    message = message.replace(/\n/g, "<br>");
    const modal = createModal({
      caption: i18nlocales.t("attention"),
      centered: true,
      size: "md",
      fullscreen: false,
      keyboard: true,
      backdrop: "static",
      body: message,
      footer: `<button type="button" data-bs-dismiss="modal" class="btn btn-primary w-100 btn-ok">OK</button>`
    });
    _delegateEvent(modal, ".btn-ok", "click", okCallback);
    if (okCallback != null && okCallback != undefined) {
      modal.addEventListener("hidden.bs.modal", event => {
        if (!event.target.querySelector(".btn-ok:focus")) {
          okCallback(event);
        }
      });
    }
  }
  function confirm({
    message = "",
    okCallback = null,
    noCallback = null
  }) {
    //for legacy support
    if (typeof arguments[0] === "string") {
      message = arguments[0];
      okCallback = arguments[1];
      noCallback = arguments[2];
    }
    if (!message) message = i18nlocales.t("confirmDefault");
    message = message.replace(/\n/g, "<br>");
    const modal = createModal({
      caption: i18nlocales.t("confirm"),
      centered: true,
      size: "md",
      fullscreen: false,
      keyboard: true,
      backdrop: true,
      body: message,
      footer: `<div class="flex-column align-items-stretch w-100 gap-2">
                          <div class="col-12">
                            <button type="button" data-bs-dismiss="modal" class="btn btn-primary btn-ok w-100">OK</button>
                        </div>
                        <div class="col-12 mt-2">
                            <button type="button" data-bs-dismiss="modal" class="btn btn-outline-secondary btn-nok w-100">${i18nlocales.t("cancel")}</button>
                        </div>
                    </div>`
    });
    _delegateEvent(modal, ".btn-ok", "click", okCallback);
    _delegateEvent(modal, ".btn-nok", "click", noCallback);
    if (noCallback != null && noCallback != undefined) {
      modal.addEventListener("hidden.bs.modal", event => {
        if (!event.target.querySelector(".btn-ok:focus") && !event.target.querySelector(".btn-nok:focus")) {
          noCallback(event);
        }
      });
    }
  }
  function _dialogsCtrlClick(ctrlClick, url, ev) {
    if (!ctrlClick) return false;
    if ((ev.ctrlKey || ev.metaKey) && url) {
      window.open(url, "_blank");
      return true;
    }
    return false;
  }
  function _delegateEvent(container, selector, event, callback) {
    if (!container) return;
    if (!selector) return;
    if (!event) return;
    if (!callback) return;
    container.addEventListener(event, function (e) {
      const target = e.target.closest(selector);
      if (!target) return;
      callback(e, target);
    });
  }

  // eslint-disable-next-line no-unused-vars
  function toast({
    message,
    color,
    header,
    caption,
    subcaption,
    icon,
    placement,
    timeout
  } = {}) {
    //for legacy support
    if (typeof arguments[0] === "string") {
      message = arguments[0];
      color = arguments[1]?.replace("alert-notification-", "").replace("alert-", "");
    }
    const toastContainer = document.querySelector(`wc-toast-container[placement='${placement}']`) || _createToastContainer();
    const toast = document.createElement("wc-toast");
    toast.setAttribute("header", header);
    toast.setAttribute("caption", caption);
    toast.setAttribute("subcaption", subcaption);
    toast.setAttribute("icon", icon);
    toast.setAttribute("message", message);
    toast.setAttribute("color", color);
    toast.setAttribute("timeout", timeout);
    toastContainer.appendChild(toast);
    toast.show();
    return toast;
    function _createToastContainer() {
      const toastContainer = document.createElement("wc-toast-container");
      toastContainer.setAttribute("placement", placement);
      document.body.appendChild(toastContainer);
      return toastContainer;
    }
  }
  function success(message) {
    return toast({
      message: message,
      color: "success",
      placement: "bottom",
      header: false
    });
  }

  class bpmnJsModelerElement extends HTMLElement {
    constructor() {
      super();
      this._canvas = null;
      this._cli = null;
      this._editorActions = null;
      this._elementFactory = null;
      this._elementRegistry = null;
      this._eventBus = null;
      this._lastDiagramXML = null;
      this._minimap = null;
      this._modeler = null;
      this._modeling = null;
      this._overlays = null;
      this._palette = null;
      this._selection = null;
      this._translate = null;
      this._issues = [];
    }

    //#region getters & setters
    get id() {
      return this.getAttribute("id");
    }
    set id(value) {
      this.setAttribute("id", value);
    }
    get flowName() {
      return this.getStringAttribute("flowName");
    }
    set flowName(value) {
      this.setAttribute("flowName", value);
    }
    get mode() {
      return this.getStringAttribute("mode", "full", ["full", "cli", "original"]);
    }
    set mode(value) {
      this.setAttribute("mode", value);
    }
    get file() {
      return this.getStringAttribute("file");
    }
    set file(value) {
      this.setAttribute("file", value);
    }
    get endpoint() {
      return this.getStringAttribute("endpoint");
    }
    set endpoint(value) {
      this.setAttribute("endpoint", value);
    }
    get blocked() {
      return this.getArrayAttribute("blocked", []);
    }
    set blocked(value) {
      this.setAttribute("blocked", value);
    }
    get premium() {
      return this.getArrayAttribute("premium", []);
    }
    set premium(value) {
      this.setAttribute("premium", value);
    }
    get include() {
      return this.getArrayAttribute("include", []);
    }
    set include(value) {
      this.setAttribute("include", value);
    }
    get enableNextElementAi() {
      return this.getBooleanAttribute("enableNextElementAi", false);
    }
    set enableNextElementAi(value) {
      this.setAttribute("enableNextElementAi", value);
    }
    get existsExecution() {
      return this.getBooleanAttribute("existsExecution", false);
    }
    set existsExecution(value) {
      this.setAttribute("existsExecution", value);
    }
    get enableParentFullScreen() {
      return this.getBooleanAttribute("enableParentFullScreen", false);
    }
    set enableParentFullScreen(value) {
      this.setAttribute("enableParentFullScreen", value);
    }
    get bpmnJsContainerId() {
      return `${this.id}_container`;
    }
    get minimapElement() {
      return this.querySelector(".djs-minimap");
    }
    get modeler() {
      return this._modeler;
    }
    get editorActions() {
      return this._editorActions;
    }
    get elementFactory() {
      return this._elementFactory;
    }
    get elementRegistry() {
      return this._elementRegistry;
    }
    get selection() {
      return this._selection;
    }
    get cli() {
      return this._cli;
    }
    get overlays() {
      return this._overlays;
    }
    get translate() {
      return this._translate;
    }
    get palette() {
      return this._palette;
    }
    get skin() {
      return localStorage.getItem("modelerSkin") || "friendly";
    }
    set skin(value) {
      localStorage.setItem("modelerSkin", value);
    }
    get btnSearch() {
      return this.querySelector(".btn-diagram-search");
    }
    get btnMinimap() {
      return this.querySelector(".btn-diagram-minimap");
    }
    get btnZoomIn() {
      return this.querySelector(".btn-diagram-zoomin");
    }
    get btnZoomOut() {
      return this.querySelector(".btn-diagram-zoomout");
    }
    get btnResetZoom() {
      return this.querySelector(".btn-diagram-reset-zoom");
    }
    get btnFullscreen() {
      return this.querySelector(".btn-diagram-fullscreen");
    }
    get btnSpinnerDiagram() {
      return this.querySelector(".btn-diagram-spinner");
    }
    get rainbowLoader() {
      return this.querySelector("wc-rainbow");
    }
    get bpmnJsContainer() {
      return this.querySelector(`#${this.bpmnJsContainerId}`);
    }
    get canvas() {
      return this._canvas;
    }
    get eventBus() {
      return this._eventBus;
    }
    get modeling() {
      return this._modeling;
    }
    get minimap() {
      return this._minimap;
    }
    get lastDiagramXML() {
      return this._lastDiagramXML;
    }
    set lastDiagramXML(value) {
      this._lastDiagramXML = value;
    }
    get paletteElement() {
      return this.querySelector(".djs-palette");
    }
    //#endregion

    async connectedCallback() {
      // importante! este elemento deve possuir espaco no documento para funcionar corretamente.
      // para inicia-lo escondido, use a propriedade visibility:hidden ao inves de display:none.
      await this._init();
    }
    async _init() {
      await loadBpmnJs();
      if (!this.getAttribute("id")) this.id = generateRandomId();
      this._addContainer();
      await this._configure();
      await this._bindEvents();
    }
    _addContainer() {
      this.insertAdjacentHTML("beforeend", `
      <div class="w-100 h-100">
        <wc-rainbow></wc-rainbow>
        <div id="${this.bpmnJsContainerId}" class="zv-modeler">
            <div class="zv-modeler-controls">
              <button type="button" class="btn btn-sm btn-outline-secondary btn-diagram-spinner d-none"><wc-spinner color="danger"></wc-spinner></button>
              <button type="button" class="btn btn-sm btn-outline-secondary btn-diagram-search"><wc-icon icon="search"></wc-icon></button>
              <button type="button" class="btn btn-sm btn-outline-secondary btn-diagram-fullscreen"><wc-icon icon="maximize"></wc-icon></button>
              <button type="button" class="btn btn-sm btn-outline-secondary btn-diagram-minimap"><wc-icon icon="map"></wc-icon></button>
              <button type="button" class="btn btn-sm btn-outline-secondary btn-diagram-reset-zoom"><wc-icon icon="crosshair"></wc-icon></button>
              <button type="button" class="btn btn-sm btn-outline-secondary btn-diagram-zoomin"><wc-icon icon="zoom-in"></wc-icon></button>
              <button type="button" class="btn btn-sm btn-outline-secondary  btn-diagram-zoomout"><wc-icon icon="zoom-out"></wc-icon></button>
              <wc-tooltip target=".btn-diagram-search" placement="bottom" content="${i18nlocales.t("modeler.search")}"></wc-tooltip>
              <wc-tooltip target=".btn-diagram-fullscreen" placement="bottom" content="${i18nlocales.t("modeler.fullScreen")}"></wc-tooltip>
              <wc-tooltip target=".btn-diagram-minimap" placement="bottom" content="${i18nlocales.t("modeler.minimap")}"></wc-tooltip>
              <wc-tooltip target=".btn-diagram-reset-zoom" placement="bottom" content="${i18nlocales.t("modeler.center")}"></wc-tooltip>
              <wc-tooltip target=".btn-diagram-zoomin" placement="bottom" content="${i18nlocales.t("modeler.zoomIn")}"></wc-tooltip>
              <wc-tooltip target=".btn-diagram-zoomout" placement="bottom" content="${i18nlocales.t("modeler.zoomOut")}"></wc-tooltip>
            </div>
          </div>
      </div>`);
    }
    async _configure() {
      const options = {
        container: `#${this.bpmnJsContainerId}`,
        custom: {
          contextId: generateGUID(),
          flowName: this.flowName,
          enableNextElementAi: this.enableNextElementAi,
          blockedElements: this.blocked,
          premiumElements: this.premium,
          includeElements: this.include,
          lang: i18nlocales.lng(),
          skin: this.skin,
          skins: [{
            id: "classic",
            name: i18nlocales.t("modeler.notationBpmn")
          }, {
            id: "friendly",
            name: i18nlocales.t("modeler.notationFriendly")
          }]
        },
        moddleExtensions: {
          zeev: bpmnExtensionModel
        },
        keyboard: {
          bindTo: document
        }
      };
      this._modeler = this._getModeler(options);
      await this._loadInitialBpmn();
      this._canvas = this._getm("canvas");
      this._editorActions = this._getm("editorActions");
      this._elementRegistry = this._getm("elementRegistry");
      this._eventBus = this._getm("eventBus");
      this._minimap = this._getm("minimap");
      this._modeling = this._getm("modeling");
      this._palette = this._getm("palette");
      this._selection = this._getm("selection");
      this._translate = this._getm("translate");
      this._overlays = this._getm("overlays");
      this._cli = new JsonCliModeling(this._modeler);
      this.setInitialPosition();
      this.rainbowLoader.classList.add("d-none");
      this._setModelerClassList();
      this.bpmnJsContainer.classList.add("show");
    }
    _getModeler(options) {
      if (this.mode === "full") return new BpmnJs.FullModeler(options);
      if (this.mode === "cli") return new BpmnJs.CliModeler(options);
      return new BpmnJs.OriginalModeler(options);
    }
    _getm(moduleName) {
      return this._modeler.get(moduleName, false);
    }
    async _loadInitialBpmn() {
      const model = (await this._fetchBpmnModelTextFromFile()) ?? (await this._fetchBpmnModelJsonFromEndpoint());
      if (model) await this.modeler.importXML(model);else await this.modeler.createDiagram();
    }
    async _fetchBpmnModelTextFromFile() {
      if (!this.file) return null;
      const response = await fetch(this.file);
      return await response.text();
    }
    async _fetchBpmnModelJsonFromEndpoint() {
      if (!this.endpoint) return null;
      const response = await fetch(this.endpoint);
      const model = await response.json();
      document.dispatchEvent(new CustomEvent(bpmnCustomEvents.BPMN_LOAD_MODELING_CALLBACK, {
        detail: {
          newXml: model.modeling,
          newProcess: model.newProcess,
          lastUpdate: model.lastUpdate
        }
      }));
      return model.modeling;
    }

    //#region Eventos
    async _bindEvents() {
      this._bindDeleteElementEvent();
      this._bindRemoveSelectionEvent();
      this._bindSearchButton();
      this._bindZoomInButton();
      this._bindZoomOutButton();
      this._bindMinimapButton();
      this._bindResetZoomButton();
      this._bindFullscreenButton();
      this._bindPaletteHeightScroll();
      this._bindCliModelingBuilder();
      this._bindPaletteEntryHelp();
      await this._bindSkinChange();
      await this._bindDiagramElementsChange();
    }
    _bindPaletteEntryHelp() {
      document.addEventListener(bpmnCustomEvents.BPMN_PALETTE_ENTRY_HELP, e => {
        const zeevId = e?.detail;
        if (!zeevId) return;
        this.showEntryHelp(zeevId);
      });
    }
    _bindCliModelingBuilder() {
      document.addEventListener(bpmnCustomEvents.BPMN_CLI_MODELING, e => {
        this.modeler.clear();
        this.modeler.createDiagram().then(() => this.cli.build(e.detail.model, e.detail.v));
      });
    }
    _bindDeleteElementEvent() {
      document.addEventListener(bpmnCustomEvents.BPMN_ELEMENT_DELETE, e => {
        const element = e.detail;
        if (this._cannotDeleteHasExecutions([element])) return;
        if (confirm(i18nlocales.t("modeler.deleteConfirm"), () => {
          this.modeling.removeElements([element]);
        })) ;
      });
    }
    _bindRemoveSelectionEvent() {
      document.addEventListener(bpmnCustomEvents.BPMN_SELECTION_REMOVE, e => {
        if (this._cannotDeleteHasExecutions(e.detail)) return;
        if (confirm(i18nlocales.t("modeler.deleteConfirm"), () => {
          this.editorActions.trigger("removeSelection");
        })) ;
      });
    }
    _cannotDeleteHasExecutions(elements) {
      if (!this.existsExecution) return;
      const cannotDelete = elements.some(element => element.type !== "bpmn:Group" && element.type !== "label" && element.type !== "bpmn:DataInputAssociation" && element.type !== "bpmn:MessageFlow" && element.type !== "bpmn:SequenceFlow");

      // Se encontrar um type diferente exibe a mensagem de alerta avisando que a exclusão não é possível
      if (cannotDelete) alert(i18nlocales.t("modeler.cannotDeleteHasExecutions"));
      return cannotDelete;
    }
    _bindSearchButton() {
      this.btnSearch.addEventListener("click", e => {
        this.editorActions.trigger("find");
        e.cancelBubble = true;
        e.preventDefault();
      });
    }
    _bindZoomInButton() {
      this.btnZoomIn.addEventListener("click", () => {
        this.editorActions.trigger("stepZoom", {
          value: 0.5
        });
      });
    }
    _bindZoomOutButton() {
      this.btnZoomOut.addEventListener("click", () => {
        this.editorActions.trigger("stepZoom", {
          value: -0.5
        });
      });
    }
    _bindResetZoomButton() {
      this.btnResetZoom.addEventListener("click", () => {
        this.centerDiagram();
      });
    }
    _bindMinimapButton() {
      this.btnMinimap.addEventListener("click", () => {
        this.minimap.toggle();
        if (this.minimap.isOpen()) {
          this.btnMinimap.classList.add("active");
        } else {
          this.btnMinimap.classList.remove("active");
        }
      });
    }
    _bindFullscreenButton() {
      const doc = this.enableParentFullScreen ? window.parent.document : document;
      this.btnFullscreen.addEventListener("click", () => {
        if (!doc.fullscreenElement) {
          doc.documentElement.requestFullscreen();
          this.btnFullscreen.classList.add("active");
        } else {
          doc.exitFullscreen();
          this.btnFullscreen.classList.remove("active");
        }
      });
    }
    _bindPaletteHeightScroll() {
      this.paletteElement?.addEventListener("mousewheel", event => {
        event.stopPropagation();
      });
    }
    async _bindDiagramElementsChange() {
      this.eventBus.on("elements.changed", async () => {
        this.toggleSpinner(true);
        const newXml = await this._saveXML();
        document.dispatchEvent(new CustomEvent(bpmnCustomEvents.BPMN_ELEMENTS_CHANGED, {
          detail: {
            new: newXml,
            old: this.lastDiagramXML
          }
        }));
        this.lastDiagramXML = newXml;
        this.toggleSpinner(false);
      });
    }
    async _bindSkinChange() {
      document.addEventListener(bpmnCustomEvents.BPMN_SKIN_CHANGE, async e => {
        const skin = e.detail;
        this.skin = skin;
        await this._reload();
      });
    }
    //#endregion

    _setModelerClassList() {
      this.bpmnJsContainer.className = `zv-modeler zv-modeler-skin-${this.skin}`;
    }
    async _reload() {
      this.modeler.destroy();
      await this._configure();
      await this._bindDiagramElementsChange();
      this._bindPaletteHeightScroll();
    }
    async _saveXML() {
      const response = await this.modeler.saveXML({
        format: true
      });
      return response.xml;
    }
    async _saveSVG() {
      const response = await this.modeler.saveSVG({
        format: true
      });
      return response.svg;
    }
    addElementError(elementId, description) {
      if (this._issues.find(issue => issue.elementId === elementId)) return;
      this._issues.push({
        elementId,
        type: "error"
      });
      const html = `<wc-icon icon="x-circle" title="${description}" class="text-bg-danger border border-3 border-danger rounded-circle d-flex"></wc-icon>`;
      this._overlays.add(elementId, {
        position: {
          top: -15,
          right: 5
        },
        html: html
      });
    }
    addElementWarning(elementId, description) {
      if (this._issues.find(issue => issue.elementId === elementId)) return;
      this._issues.push({
        elementId,
        type: "warning"
      });
      const html = `<wc-icon icon="alert-circle" title="${description}" class="text-bg-info border border-3 border-info rounded-circle d-flex"></wc-icon>`;
      this._overlays.add(elementId, {
        position: {
          top: -15,
          right: -5
        },
        html: html
      });
    }
    removeElementIssues(elementId) {
      this._issues = this._issues.filter(issue => issue.elementId !== elementId);
      this._overlays.remove({
        elementId
      });
    }
    removeAllIssues() {
      this._issues.forEach(issue => {
        this._overlays.remove({
          element: issue.elementId
        });
      });
      this._issues = [];
    }
    async _loadBpmnJsBundleCSS() {
      const response = await fetch(getZeevBootstrapPath("ondemand/bpmn-js/css/zeev-bundle-bpmnjs.min.css"));
      return response.text();
    }
    async _getBlobFromModelerSVG() {
      const svg = await this._saveSVG();

      // Parse o SVG string em um documento XML
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svg, "image/svg+xml");
      const svgElement = svgDoc.documentElement;
      const compiledCSS = await this._loadBpmnJsBundleCSS();
      const styleElement = document.createElement("style");
      styleElement.textContent = compiledCSS;

      // Adicione o estilo como o primeiro filho da tag <svg>
      svgElement.insertBefore(styleElement, svgElement.firstChild);

      // Adicionando o estilo no elemento <svg>
      const modelerSkinCss = localStorage.getItem("modelerSkin") === "friendly" ? "zv-modeler-skin-friendly" : "zv-modeler-skin-classic";
      svgElement.classList.add("zv-modeler", modelerSkinCss, "show");
      svgElement.style.background = "#FFFFFF";

      // Retornar para uma string
      const serializer = new XMLSerializer();
      const modifiedSvgString = serializer.serializeToString(svgElement);
      const blob = new Blob([modifiedSvgString], {
        type: "image/svg+xml"
      });
      return blob;
    }
    async downloadSVG() {
      const blob = await this._getBlobFromModelerSVG();
      const fileName = this.flowName ?? "diagram";
      this._downloadFile(`${fileName}.svg`, blob);
    }
    async downloadXML() {
      const xml = await this._saveXML();
      const blob = new Blob([xml], {
        type: "application/xml"
      });
      const fileName = this.flowName ?? "diagram";
      this._downloadFile(`${fileName}.bpmn`, blob);
    }
    async downloadPNG() {
      const blob = await this._getBlobFromModelerSVG();
      const url = URL.createObjectURL(blob);
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const bbox = document.querySelector(".djs-container svg .viewport").getBBox();
      const scale = Math.min(3, 4096 / Math.max(bbox.width, bbox.height));
      canvas.setAttribute("width", bbox.width * scale);
      canvas.setAttribute("height", bbox.height * scale);
      img.src = url;
      img.onload = () => {
        ctx.drawImage(img, 0, 0, bbox.width * scale, bbox.height * scale);
        URL.revokeObjectURL(url);
        canvas.toBlob(content => {
          const fileName = this.flowName ?? "diagram";
          this._downloadFile(`${fileName}.png`, content);
        });
      };
    }
    _downloadFile(fileName, contents) {
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(contents);
      a.download = fileName;
      a.click();
      success(i18nlocales.t("modeler.downloadSuccess"));
    }
    toggleSpinner(show) {
      if (show) {
        this.btnSpinnerDiagram.classList.remove("d-none");
      } else {
        this.btnSpinnerDiagram.classList.add("d-none");
      }
    }
    paletteMaximize() {
      this.palette.maximize();
    }
    paletteMinimize() {
      this.palette.minimize();
    }
    showEntryHelp(zeevId) {
      const flatMetaElements = metaElements.flatMap(group => group.elements);
      const metaElement = flatMetaElements.find(availableElement => availableElement.zeevId === zeevId);
      if (!metaElement) return;
      const title = this.translate(metaElement.title);
      const language = i18nlocales.lng();
      const defaultLang = "pt";
      const help = metaElement.help?.[language] ?? metaElement.help?.[defaultLang];
      const desc = help?.full;
      const url = help?.url;
      const img = help?.img;
      const imgSrc = img ? getZeevBootstrapPath(`assets/js/components/bpmnJs/images/${metaElement.help.pt.img}`) : null;
      if (!desc && !imgSrc && url) {
        window.open(url, "_blank");
        return;
      }
      const helpText = `
      ${imgSrc ? `<img class="img-fluid" src="${imgSrc}">` : ""}
      ${desc ? `<p>${desc}</p>` : ""}
      ${url ? `<p class="small"><a href="${url}" target="_blank">${url}</a></p>` : ""}`;
      if (!helpText) return;
      createModal({
        caption: title,
        body: helpText,
        size: "lg"
      });
    }

    /*
     * Centralizar o diagrama
     */
    centerDiagram() {
      this.cli.centerDiagram();
    }

    /*
     * Definir a posição inicial do desenho de acordo com o tamanho da paleta
     */
    setInitialPosition() {
      const x = this.palette && typeof this.palette.isMaximized === "function" && this.palette.isMaximized() ? -130 : 30;
      const currentViewBox = this.canvas.viewbox();
      this.canvas.viewbox({
        x: x,
        y: 0,
        width: currentViewBox.width,
        height: currentViewBox.height
      });
    }

    /*
     * Focar o desenho no elemento e selecionar o elemento
     */
    focusElement(elementId) {
      const element = this.elementRegistry.get(elementId);
      const currentViewbox = this.canvas.viewbox();
      const elementMid = {
        x: element.x + element.width / 2,
        y: element.y + element.height / 2
      };
      this.canvas.viewbox({
        x: elementMid.x - currentViewbox.width / 2,
        y: elementMid.y - currentViewbox.height / 2,
        width: currentViewbox.width,
        height: currentViewbox.height
      });
      this.selection.select(element);
    }
  }
  if (!customElements.get("wc-bpmn-modeler")) {
    customElements.define("wc-bpmn-modeler", bpmnJsModelerElement);
  }

  class tipElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    _init() {
      this._render();
    }
    _render() {
      this.innerHTML = `
            <div class="alert alert-info d-flex flex-row mt-2 small">
                <div class="me-1"><wc-icon icon="lightbulb"></wc-icon></div>
                <div>
                    <strong>${i18nlocales.t("tip")}:</strong>
                    ${this.innerHTML}
                </div>
          </div>
        `;
    }
  }
  customElements.define("wc-tip", tipElement);

  class warningElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    _init() {
      this._render();
    }
    _render() {
      this.innerHTML = `
            <div class="alert alert-warning d-flex flex-row mt-2 small">
                <div class="me-2"><wc-icon icon="alert-triangle"></wc-icon></div>
                <div>
                    <strong>${i18nlocales.t("warning")}:</strong>
                    ${this.innerHTML}
                </div>
          </div>
        `;
    }
  }
  customElements.define("wc-warning", warningElement);

  class alertModernElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }
    get icon() {
      return this.getStringAttribute("icon", "user");
    }
    set icon(value) {
      this.setAttribute("icon", value);
    }
    get iconSize() {
      return this.getStringAttribute("icon-size", "lg", ["sm", "md", "lg", "xl"]);
    }
    set iconSize(value) {
      this.setAttribute("icon-size", value);
    }
    get color() {
      const validColors = ["primary", "secondary", "success", "warning", "danger", "info"];
      const currentColor = this.getStringAttribute("color", "secondary");
      return validColors.includes(currentColor) ? currentColor : "secondary";
    }
    set color(value) {
      this.setAttribute("color", value);
    }
    get caption() {
      return this.getStringAttribute("caption", "");
    }
    set caption(value) {
      this.setAttribute("caption", value);
    }
    get content() {
      return this.getStringAttribute("content", "");
    }
    set content(value) {
      this.setAttribute("content", value);
    }
    get url() {
      return this.getStringAttribute("url", "");
    }
    set url(value) {
      this.setAttribute("url", value);
    }
    get buttonTitle() {
      return this.getStringAttribute("button-title", "");
    }
    set buttonTitle(value) {
      this.setAttribute("button-title", value);
    }
    get buttonIcon() {
      return this.getStringAttribute("button-icon", "arrow-right");
    }
    set buttonIcon(value) {
      this.setAttribute("button-icon", value);
    }
    get buttonIconSize() {
      return this.getStringAttribute("button-icon-size", "lg", ["sm", "md", "lg", "xl"]);
    }
    set buttonIconSize(value) {
      this.setAttribute("button-icon-size", value);
    }
    _getButton() {
      if (!this.url) return "";
      return `
            <a
              href="${this.url}"
              target="_blank"
              class="btn btn-outline-${this.color} btn-sm py-1 px-3"
            >
              ${this.buttonTitle}
              <wc-icon
                icon="${this.buttonIcon}"
                size="${this.buttonIconSize}"
                class="ms-1 align-middle"
              ></wc-icon>
            </a>`;
    }
    _init() {
      this._render();
    }
    _render() {
      this.innerHTML = `
            <div class="alert-modern-wrapper mb-3">
              <div class="alert-modern alert-${this.color}">
                <div class="alert-icon-wrapper">
                  <wc-icon icon="${this.icon}" size="${this.iconSize}"></wc-icon>
                </div>
                <div>
                  <div class="alert-title">${this.caption}</div>
                  <p class="alert-text mb-2">
                    ${this.content}
                  </p>
                  ${this._getButton()}
                </div>
              </div>
            </div>
    `;
    }
  }
  customElements.define("wc-alert-modern", alertModernElement);

  class baseRole {
    constructor(uid, name, photo, icon, color) {
      this.uid = uid;
      this.name = name;
      this.photo = photo;
      this.icon = icon;
      this.color = color;
    }
  }

  function getOpenAiRoles({
    userName,
    userPhoto,
    userIcon,
    userColor,
    assistantName,
    assistantPhoto
  }) {
    return {
      user: new baseRole("user", userName, userPhoto, userIcon, userColor),
      assistant: new baseRole("assistant", assistantName, assistantPhoto, "ai", "primary"),
      system: new baseRole("system", assistantName, assistantPhoto, "ai", "primary")
    };
  }

  class baseChatConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      this._env = env;
      this._log = log;
      this._userName = userName;
      this._userPhoto = userPhoto;
      this._userIcon = userIcon;
      this._userColor = userColor;
    }

    // eslint-disable-next-line no-unused-vars
    async feed(data) {}

    /**
     * Controles na mensagem.
     * Valores permitidos: like, dislike, copy, edit.
     */
    get controls() {
      return "like,dislike,copy";
    }
    get enableFile() {
      return false;
    }
    get enableStream() {
      return false;
    }
    get enableInput() {
      return true;
    }
    get provider() {
      return {};
    }
    get roles() {
      return getOpenAiRoles({
        userName: this._userName,
        userPhoto: this._userPhoto,
        userIcon: this._userIcon,
        userColor: this._userColor
      });
    }
    get helpText() {
      return "";
    }
    get examples() {
      return [];
    }
    get contexts() {
      return [];
    }

    /**
     * Histórico de mensagens. Quantidade de mensagens anteriores ao contexto atual a serem consideradas no envio para o assistente.
     */
    get history() {
      return 1;
    }
    get body() {
      return null;
    }
    get feedData() {
      return null;
    }
    getStartupFiles() {
      return [];
    }
    getWelcomeMessage() {
      return "";
    }
    getAlertMessage() {
      return null;
    }
    getFixedMessages() {
      return [];
    }
    // eslint-disable-next-line no-unused-vars
    getFunctionCall(messages) {
      return {};
    }

    // eslint-disable-next-line no-unused-vars
    afterUserMessage(context) {}

    /**
     * Executado apos uma mensagem ser retornada pelo assistente.
     * @example
     * return [
     *   {
     *     match: (message) => message != "",
     *     handle: (message) => {
     *       // faz alguma coisa com a mensagem
     *       return {
     *         message, // obrigatorio
     *         previewMessage,
     *         info,
     *         urls,
     *         actions: [
     *           {
     *             label, // obrigatorio
     *             handle: () => {} // obrigatorio
     *           }
     *         ]
     *       }
     *     }
     *   }
     * ]
     * @returns {Array}
     */
    getGptMessageHandlers() {
      return [];
    }

    /**
     * Executado apos uma mensagem ser inserida pelo usuario e antes de ser enviada.
     * @returns {Array}
     */
    getUserMessageHandlers() {
      return [];
    }

    /**
     * Atualiza dinamicamente propriedades de um contexto registrado no chat.
     * @param {string} contextId - ID do contexto a atualizar.
     * @param {{ label?: string, description?: string, source?: Function, visibility?: "visible" | "hidden" }} updates
     *   `visibility: "hidden"` oculta e desabilita o contexto; `visibility: "visible"` o restaura.
     */
    updateContext(contextId, {
      label,
      description,
      source,
      visibility
    } = {}) {
      document.dispatchEvent(new CustomEvent("chatContextUpdate", {
        detail: {
          contextId,
          label,
          description,
          source,
          visibility
        }
      }));
    }
    loadNewConfig(newConfigId) {
      document.dispatchEvent(new CustomEvent("changeZeevConfig", {
        detail: newConfigId
      }));
    }
  }

  class AuthError extends Error {
    constructor() {
      super("Você precisa de permissão para utilizar o Chat de IA.");
      this.name = "AuthError";
    }
  }

  class RateLimitError extends Error {
    constructor(message) {
      super(message);
      this.name = "RateLimitError";
    }
  }

  class ValidationError extends Error {
    constructor(message) {
      super(message);
      this.name = "ValidationError";
    }
  }

  class zeevHubAiError extends Error {
    constructor(code, message) {
      super(message);
      this.code = code;
      this.name = this.constructor.name;
    }
  }

  var baseUrl = {
    dev: "http://localhost:3010/",
    test: "https://testing-ai.zeev.app/",
    hml: "https://candidate-ai.zeev.app/",
    prod: "https://aihub.zeev.it/"
  };

  /* eslint-disable no-unused-vars */
  function zeevHubAiHttpService(env) {
    const defaultHeader = {
      "Content-Type": "application/json"
    };
    async function sendMessageToChatCompletition(body, isStream, abortController, headers = {}) {
      return await _sendMessage(`api/v1/zeev/chat/completions`, body, isStream, abortController, headers);
    }
    async function sendMessageToAssistant(threadId, body, isStream, abortController, headers = {}) {
      const url = `api/v1/zeev/assistants/conversation${threadId ? `/${threadId}` : ""}`;
      return await _sendMessage(url, body, isStream, abortController, headers);
    }
    async function _sendMessage(url, body, isStream, abortController, headers = {}) {
      const abortSignal = abortController.signal;
      try {
        const parsedUrl = new URL(url, baseUrl[env]);
        const hasLangInUrl = !!parsedUrl.searchParams.get("lang");
        const cultureFromI18n = i18nlocales && typeof i18nlocales.lng === "function" ? i18nlocales.lng() : null;
        if (cultureFromI18n && !hasLangInUrl) {
          parsedUrl.searchParams.set("lang", String(cultureFromI18n));
          url = parsedUrl.pathname + parsedUrl.search;
        }
      } catch (e) {
        console.warn(`Failed to parse URL: ${url}. Proceeding with original URL. Error:`, e);
      }
      const options = {
        signal: abortSignal,
        method: "POST",
        headers: _objectSpread2(_objectSpread2({}, defaultHeader), headers),
        body: JSON.stringify(body)
      };
      const {
        response,
        json
      } = isStream ? await _fetchStream(url, options) : await _fetch(url, options);
      if (abortSignal.aborted) {
        return null;
      }
      return {
        response,
        json,
        abortController
      };
    }
    async function like(messageId, headers = {}) {
      await _fetch(`api/v1/feedback/like`, {
        method: "PUT",
        headers: _objectSpread2(_objectSpread2({}, defaultHeader), headers),
        body: JSON.stringify({
          id: messageId
        })
      });
    }
    async function dislike(messageId, headers = {}) {
      await _fetch(`api/v1/feedback/dislike`, {
        method: "PUT",
        headers: _objectSpread2(_objectSpread2({}, defaultHeader), headers),
        body: JSON.stringify({
          id: messageId
        })
      });
    }
    async function sendFeedback(messageId, message, headers = {}) {
      await _fetch(`api/v1/feedback/comment`, {
        method: "PUT",
        headers: _objectSpread2(_objectSpread2({}, defaultHeader), headers),
        body: JSON.stringify({
          id: messageId,
          message: message
        })
      });
    }
    async function uploadFile(body, headers = {}) {
      const {
        json
      } = await _fetch(`api/v1/zeev/files`, {
        method: "POST",
        headers: _objectSpread2(_objectSpread2({}, defaultHeader), headers),
        body: JSON.stringify(body)
      });
      return json?.data?.id;
    }
    async function deleteFile(fileId, body, headers = {}) {
      await _fetch(`api/v1/zeev/files/${fileId}`, {
        method: "DELETE",
        headers: _objectSpread2(_objectSpread2({}, defaultHeader), headers),
        body: JSON.stringify(body)
      });
    }
    async function searchEmbeddings(body, headers = {}) {
      const {
        json
      } = await _fetch(`api/v1/zeev/embeddings/search`, {
        method: "POST",
        headers: _objectSpread2(_objectSpread2({}, defaultHeader), headers),
        body: JSON.stringify(body)
      });
      return json;
    }
    async function _fetch(resource, options) {
      options.credentials = "include";
      const url = new URL(resource, baseUrl[env]);
      const response = await fetch(url, options);
      const json = await response.json();
      if (json?.error?.code) {
        throw new zeevHubAiError(json.error.code, json.error.message);
      }
      if (!response.ok) {
        throw new zeevHubAiError(response.status, json.error);
      }
      return {
        response,
        json
      };
    }
    async function _fetchStream(resource, options) {
      options.credentials = "include";
      const url = new URL(resource, baseUrl[env]);
      const response = await fetch(url, options);
      const json = !response.ok ? await response.json() : null;
      if (json?.error?.code) {
        throw new zeevHubAiError(json.error.code, json.error.message);
      }
      if (!response.ok) {
        throw new zeevHubAiError(response.status, json.error);
      }
      return {
        response,
        json
      };
    }
    return {
      like,
      dislike,
      sendFeedback,
      sendMessageToChatCompletition,
      sendMessageToAssistant,
      uploadFile,
      deleteFile,
      searchEmbeddings
    };
  }

  function zeevHubAiAuthHttpService(env) {
    async function exchangeToken(token) {
      const url = new URL("/api/v1/auth/token", baseUrl[env]);
      await fetch(url, {
        method: "post",
        body: JSON.stringify({
          token
        }),
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include"
      });
    }
    return {
      exchangeToken
    };
  }

  function zeevTokenHttpService() {
    async function getToken() {
      const response = await fetch("/api/internal/services/1.0/token/aihub", {
        credentials: "include"
      });
      return await response.json();
    }
    async function addBilling(brt) {
      const response = await fetch("/api/internal/services/1.0/billing", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          brt: brt
        }),
        credentials: "include"
      });
      return await response.json();
    }
    return {
      getToken,
      addBilling
    };
  }

  function zeevHubAiWithAuthService(env) {
    const aiHubServices = zeevHubAiHttpService(env);
    const {
      exchangeToken
    } = zeevHubAiAuthHttpService(env);
    const {
      getToken,
      addBilling
    } = zeevTokenHttpService();
    async function authIfNotThenRetry(service, ...args) {
      try {
        return await service.call(this, ...args);
      } catch (err) {
        if (err.code == 403) throw new AuthError();
        if (err.code == 429) throw new RateLimitError(err.message);
        if (err.code == 422) throw new ValidationError(err.message);
        if (err.code != 401) throw err;
        try {
          const token = await getToken();
          await exchangeToken(token);
          return await service.call(this, ...args);
        } catch (err) {
          if (err.code == 403) throw new AuthError();
          throw err;
        }
      }
    }
    async function deleteFile(fileId, body, headers = {}) {
      return authIfNotThenRetry(aiHubServices.deleteFile, fileId, body, headers);
    }
    async function dislike(messageId, headers = {}) {
      return authIfNotThenRetry(aiHubServices.dislike, messageId, headers);
    }
    async function like(messageId, headers = {}) {
      return authIfNotThenRetry(aiHubServices.like, messageId, headers);
    }
    async function searchEmbeddings(body, headers = {}) {
      return authIfNotThenRetry(aiHubServices.searchEmbeddings, body, headers);
    }
    async function sendMessageToAssistant(threadId, body, isStream, abortController, headers = {}) {
      return authIfNotThenRetry(aiHubServices.sendMessageToAssistant, threadId, body, isStream, abortController, headers);
    }
    async function sendMessageToChatCompletition(body, isStream, abortController, headers = {}) {
      if (body.requestData?.task) {
        await addBilling("AIForQuickActions");
      }
      return authIfNotThenRetry(aiHubServices.sendMessageToChatCompletition, body, isStream, abortController, headers);
    }
    async function sendFeedback(messageId, message, headers = {}) {
      return authIfNotThenRetry(aiHubServices.sendFeedback, messageId, message, headers);
    }
    async function uploadFile(body, headers = {}) {
      return authIfNotThenRetry(aiHubServices.uploadFile, body, headers);
    }
    return {
      deleteFile,
      dislike,
      like,
      searchEmbeddings,
      sendMessageToAssistant,
      sendMessageToChatCompletition,
      sendFeedback,
      uploadFile
    };
  }

  class baseProvider {
    constructor({
      env,
      log
    }) {
      this._abortController = null;
      this._contextId = generateGUID();
      this._env = env;
      this._log = log;
      this._zeevHubAiHttpService = new zeevHubAiWithAuthService(this._env);
    }
    async sendMessageToGpt(messages, streamCallback, batchCallback, errorCallback) {
      throw new Error("Not implemented");
    }
    abort() {
      this._abortController?.abort();
    }
    async searchEmbeddings({
      n,
      t,
      ms,
      s
    }) {
      const body = _objectSpread2({
        clientUid: window.location.origin,
        contextId: this._contextId,
        enableLog: this._log
      }, {
        n,
        t,
        ms,
        s
      });
      const json = await this._zeevHubAiHttpService.searchEmbeddings(body);
      if (!json?.success) return [];
      return json.data;
    }
    async like(messageId) {
      await this._zeevHubAiHttpService.like(messageId);
    }
    async dislike(messageId) {
      await this._zeevHubAiHttpService.dislike(messageId);
    }
    async sendFeedback(messageId, feedback) {
      await this._zeevHubAiHttpService.sendFeedback(messageId, feedback);
    }
    formatMessageBeforeSend(message) {
      const regex = / {2,}/g; // busca espaços repetidos
      if (Array.isArray(message)) {
        message.forEach(item => {
          item.content = item.content.replace(regex, " ");
        });
        return message;
      }
      return message.replace(regex, " ");
    }
  }

  class zeevOpenAiChatCompletionProvider extends baseProvider {
    constructor({
      env,
      log,
      body
    }) {
      super({
        env,
        log
      });
      this._body = body;
    }
    get useStream() {
      return this._body?.requestData?.stream ?? false;
    }
    get streamOptions() {
      if (this.useStream === true) return {
        stream_options: {
          include_usage: true
        }
      };
      return null;
    }
    async sendMessageToGpt(messages, streamCallback, batchCallback, errorCallback, skipToCallback) {
      if (skipToCallback) {
        await batchCallback("", messages);
      } else if (this.useStream) {
        await this._sendAndProcessStreamMessage(messages, streamCallback, batchCallback, errorCallback);
      } else {
        return await this._sendAndProcessBatchMessage(messages, batchCallback, errorCallback);
      }
    }
    async _sendAndProcessStreamMessage(messages, streamCallback, batchCallback, errorCallback) {
      try {
        let message = "";
        this._abortController = new AbortController();
        const {
          response
        } = await this._sendChatCompletitionMessageToHub(messages, this._abortController);
        const stream = response.body.getReader();
        if (!stream) await batchCallback();
        let done = false;
        let messageId = null;
        let urls;
        while (!done) {
          if (!stream) await batchCallback();
          const {
            value,
            done
          } = await stream.read();
          if (done) break;
          if (!value) continue;

          // Decodifique o valor binário para uma string UTF-8
          const text = new TextDecoder("utf-8").decode(value);
          const textParts = text.split("\n");
          for (const part of textParts) {
            if (!part) continue;
            let jsonPart;
            try {
              jsonPart = JSON.parse(part);
            } catch {
              // ignorar partes incompletas
              continue;
            }
            const isFunctionCall = this._isGptStreamMessageFunction(jsonPart);
            const partResponse = this._processGptStreamMessage(jsonPart);
            if (!partResponse) continue;
            message += partResponse.message;
            messageId = partResponse.messageId;
            if (partResponse.urls !== undefined) urls = partResponse.urls;
            if (!isFunctionCall) streamCallback(messageId, message);
          }
        }

        //ao final do stream, é realizado um batch só, de modo a por executar todos os handlers de uma só vez,
        //caso contrário seriam executados milhares de vezes, uma vez para cada part.
        //util principalmente em functions, onde não ocorre o streamCallback.
        await batchCallback(messageId, message, undefined, urls);
      } catch (e) {
        errorCallback(e);
      }
    }
    async _sendAndProcessBatchMessage(messages, batchCallback, errorCallback) {
      try {
        this._abortController = new AbortController();
        const {
          json
        } = await this._sendChatCompletitionMessageToHub(messages, this._abortController);
        const result = this._processGptBatchMessage(json);
        return await batchCallback(result.messageId, result.message, result.tokens, result.url);
      } catch (e) {
        return errorCallback(e);
      }
    }
    async _sendChatCompletitionMessageToHub(messages, abortController) {
      const requestData = _objectSpread2(_objectSpread2({
        messages: super.formatMessageBeforeSend(messages)
      }, this._body.requestData), this.streamOptions);
      const projectOpenAI = this._body.projectOpenAI ? this._body.projectOpenAI : null;
      const body = {
        clientUid: window.location.origin,
        contextId: this._contextId,
        enableLog: this._log,
        projectOpenAI,
        requestData
      };
      return await this._zeevHubAiHttpService.sendMessageToChatCompletition(body, this.useStream, abortController);
    }
    _processGptBatchMessage(response) {
      if (!response?.data?.choices) return null;
      const {
        content,
        function_call,
        tool_calls
      } = response.data.choices[0].message;
      let message = content;
      if (function_call?.arguments) message = function_call.arguments;
      if (tool_calls?.length) message = tool_calls[0].function.arguments;
      return {
        messageId: response.id,
        threadId: null,
        message: message || "",
        tokens: {
          completionTokens: response.data.usage?.completion_tokens,
          promptTokens: response.data.usage?.prompt_tokens,
          totalTokens: response.data.usage?.total_tokens
        }
      };
    }
    _processGptStreamMessage(responsePart) {
      if (!responsePart?.data?.choices) return null;
      return {
        messageId: responsePart.id,
        threadId: null,
        message: responsePart.data.choices[0]?.message?.content || responsePart.data.choices[0]?.delta?.content || responsePart.data.choices[0]?.delta?.function_call?.arguments || "",
        tokens: {
          completionTokens: responsePart.data.usage?.completion_tokens,
          promptTokens: responsePart.data.usage?.prompt_tokens,
          totalTokens: responsePart.data.usage?.total_tokens
        },
        urls: responsePart.data.urls
      };
    }
    _isGptStreamMessageFunction(responsePart) {
      if (!responsePart?.data?.choices) return;
      return responsePart.data.choices[0]?.message?.function_call || responsePart.data.choices[0]?.delta?.function_call || false;
    }
  }

  class baseZeevChatCompletionConfig extends baseChatConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this._env = env;
      this._log = log;
      this._userName = userName;
      this._userPhoto = userPhoto;
      this._userIcon = userIcon;
      this._userColor = userColor;
      this._provider = null;
    }
    get enableStream() {
      return false;
    }
    get enableInput() {
      return true;
    }
    get provider() {
      if (!this._provider) {
        this._provider = new zeevOpenAiChatCompletionProvider({
          env: this._env,
          log: this._log,
          body: this.body
        });
      }
      return this._provider;
    }
    get history() {
      return 5;
    }
    get helpText() {
      return "";
    }
    get examples() {
      return [];
    }
    get body() {
      throw new Error("Not implemented");
    }
    get inactivityMinutes() {
      return 20;
    }
    getStartupFiles() {
      return [];
    }
    getWelcomeMessage() {
      return null;
    }
    getFixedMessages() {
      return [];
    }
    getUserMessageHandlers() {
      return [];
    }
    getGptMessageHandlers() {
      return [];
    }
  }

  const systemMessageContent = `Você é a Zai, uma especialista em BPMN. Seu objetivo é ajudar a criar processos usando BPMN. Você é paciente e faz as coisas aos poucos para evitar erros. Você vai analisar o que eu pedir e então criar o processo usando BPMN. Crie os elementos na ordem em que são executados.

# Regras:
- Um participante por elemento.
- Elementos: INÍCIO, HUMANA, API, MENSAGEM, TIMER, FIM, EXCLUSIVO, PARALELO, PARALELO-CONVERGENTE.
- Notificar e enviar e-mail são MENSAGEM.
- Esperar e congelar são TIMER.
- Integrar e chamar API/sistema externo são API.
- Identifique todos os elementos com números únicos.
- "Paralelo", "simultâneo" e "ao mesmo tempo" indicam atividades paralelas.
- Use um gateway PARALELO antes de atividades paralelas.
- Cada PARALELO precisa de um PARALELO-CONVERGENTE.
- "Novamente", "de novo" e "voltar para" conectam a uma tarefa já existente.
- Formulários são feitos depois.
- Gateways EXCLUSIVOS controlam o fluxo e devem ser precedidos por uma atividade realizada pelo ator que tomou a decisão.

# Se eu tenho dúvidas de por onde começar, você sugere:
- que eu use um dos nossos exemplos.
- que comece com algo simples como "Crie um processo sobre ...".
- que descreva um processo detalhado.
- que veja alguns exemplos na seção "Exemplos".

# Responda usando o seguinte formato:
Descrição: O requisitante solicita a compra de um produto. O líder imediato do requisitante aprova ou rejeita o pedido de compra. Se rejeitar, o requisitante é notificado via e-mail e então a solicitação termina. Se ele aprovar, então o time de compras precisa efetuar cotação e realizar a compra. Depois disso, o requisitante aguarda e confirma o recebimento do produto. Ao mesmo tempo, o time financeiro aguarda e confirma o recimento da nota fiscal. Quando o requisitante e o financeiro derem OK, o processo enfim termina.

Elementos:
1. INÍCIO: O processo inicia com uma solicitação de compra.
2. HUMANA 1: O líder imediato do requsitante analisa a solicitação.
3. EXCLUSIVO: Aqui decidiremos o que acontece com base na análise do líder imediato.
4. MENSAGEM: Se a compra for rejeitada, o requisitante é notificado.
5. FIM 1: O processo termina após a notificação ao requisitante.
6. HUMANA 2: Se a compra for aprovada, o setor de compras vai realizar a compra do produto.
7. PARALELO: Aqui, criaremos um gateway para permitir as duas atividades de confirmar as entregas simultâneas.
8. HUMANA 3: O requisitante confirma o recebimento do produto.
9. HUMANA 4: O setor financeiro confirma o recebimento da nota fiscal.
10. PARALELO-CONVERGENTE: Esse gateway serve para convergir as atividades do requsitante e do financeiro.
11. FIM 2: O processo termina após as confirmações de entrega.

Fluxo do processo:
1. Solicitar compra
- Tipo: INÍCIO
- Ator: Requisitante
- Próxima: 2_Analisar pedido de compra

2. Analisar pedido de compra
- Tipo: HUMANA
- Ator: Líder imediato
- Próxima: 3_Compra aprovada ou rejeitada?

3. Compra aprovada ou rejeitada?
- Tipo: EXCLUSIVO
- Ator: Líder imediato
- Próxima: [SE aprovado] 6_Efetuar cotação e comprar
- Próxima: [SE rejeitado] 4_Solicitação rejeitada

4. Solicitação rejeitada
- Tipo: MENSAGEM
- Próxima: 5_Fim 1: Solicitação rejeitada

5. Fim 1: Solicitação rejeitada
- Tipo: FIM

6. Efetuar cotação e comprar
- Tipo: HUMANA
- Ator: Compras
- Próxima: 7_Confirmar recebimentos

7. Confirmar recebimentos
- Tipo: PARALELO
- Próxima: 8_Confirmar recebimento do produto
- Próxima: 9_Confirmar recebimento da nota fiscal

8. Confirmar recebimento do produto
- Tipo: HUMANA
- Ator: Requisitante
- Próxima: 10_Confirmar recebimentos - Convergente

9. Confirmar recebimento da nota fiscal
- Tipo: HUMANA
- Ator: Financeiro
- Próxima: 10_Confirmar recebimentos - Convergente

10. Confirmar recebimentos - Convergente
- Tipo: PARALELO-CONVERGENTE
- Próxima: 11_Fim 2: Compra efetuada

11. Fim 2: Compra efetuada
- Tipo: FIM

# Responda usando o seguinte formato:
Descrição: o meu processo inicia quando um cliente faz uma solicitação para registrar uma sugestão de melhoria ou relatar um erro no site. Se for uma melhoria, então a sugestão é salva/integrada na plataforma de gestão de feedbacks (Blip) e então o processo termina. Mas se for um problema, então duas atividades são iniciadas ao mesmo tempo: o time de produtos analisa o relato de problema e o time de sucesso de cliente entra em contato com o cliente para dar suporte. Depois dessas duas atividades, o processo termina.

Elementos:
1. INÍCIO: O processo inicia com um registro de sugestão de melhoria ou problema.
2. EXCLUSIVO: Um gateway EXCLUSIVO será usado para decidir se é uma melhoria ou um problema.
3. API: Se é uma melhoria, então vamos salvar a sugestão no Blip.
4. FIM: O processo termina após salvar a sugestão.
5. PARALELO: Usaremos um gateway PARALELO para permitir as atividade de analisar e fornecer suporte ao mesmo tempo.
6. HUMANA: O time de produtos analisa o problema registrado pelo cliente.
7. HUMANA: Ao mesmo tempo, o time de help desk dá suporte para cliente enquanto o problema não é corrigido.
8. PARALELO-CONVERGENTE: O gateway PARALELO-CONVERGENTE é usado para convergir as atividades simultêneas.
9. FIM: O problema foi tratado e agora o processo termina.

Fluxo do processo:
1. Registrar sugestão de melhoria ou problema
- Tipo: INÍCIO
- Ator: Cliente
- Próxima: 2_Sugestão de melhoria ou problema?

2. Sugestão de melhoria ou problema?
- Tipo: EXCLUSIVO
- Ator: Cliente
- Próxima: [SE é uma sugestão] 3_Salvar sugestão de melhoria no Blip
- Próxima: [SE é um problema] 5_Analisar e fornecer suporte

3. Salvar sugestão de melhoria no Blip
- Tipo: API
- Próxima: 4_Fim 1: Sugestão registrada

4. Fim 1: Sugestão registrada
- Tipo: FIM

5. Analisar e fornecer suporte
- Tipo: PARALELO
- Próxima: 6_Analisar problema
- Próxima: 7_Dar suporte para cliente

6. Analisar problema
- Tipo: HUMANA
- Ator: Produtos
- Próxima: 8_Analisar e fornecer suporte - Convergente

7. Dar suporte para cliente
- Tipo: HUMANA
- Ator: Help Desk
- Próxima: 8_Analisar e fornecer suporte - Convergente

8. Analisar e fornecer suporte - Convergente
- Tipo: PARALELO-CONVERGENTE
- Próxima: 9_Fim 2: Problema registrado

9. Fim 2: Problema registrado
- Tipo: FIM`.replaceAll("\n", " ");
  var settings1 = {
    messages: [{
      role: "system",
      content: systemMessageContent
    }],
    body: {
      projectOpenAI: null,
      requestData: {
        model: "84279b09-a6e2-4157-a610-9969a3464fdc",
        stream: false,
        temperature: 0.9 // abaixo de 0.7 fica ruim
      }
    }
  };

  var settings2 = {
    messages: [{
      role: "system",
      content: `Você é a Zai, uma especialista em BPMN. Seu objetivo é transformar a descrição de um processo para o formato JSON. Você precisa transformar todos os elementos em JSON.`
    }],
    body: {
      projectOpenAI: null,
      requestData: {
        model: "84279b09-a6e2-4157-a610-9969a3464fdc",
        stream: false,
        temperature: 0.2,
        response_format: {
          type: "json_schema",
          json_schema: {
            name: "map_process",
            strict: true,
            schema: {
              type: "object",
              required: ["elements"],
              additionalProperties: false,
              properties: {
                elements: {
                  type: "array",
                  description: "Elementos BPMN.",
                  items: {
                    type: "object",
                    required: ["id", "name", "type", "role", "next"],
                    additionalProperties: false,
                    properties: {
                      id: {
                        type: "number",
                        description: "Um número único."
                      },
                      name: {
                        type: "string",
                        description: "Nome do elemento no infinitivo."
                      },
                      type: {
                        type: "string",
                        description: "Tipo de elemento.",
                        enum: ["api", "end", "message", "human", "start", "timer", "parallel", "exclusive", "parallel-convergent"]
                      },
                      role: {
                        type: "string",
                        description: "Responsável por executar a atividade."
                      },
                      next: {
                        type: "array",
                        description: "Ids dos próximos elementos.",
                        items: {
                          type: "object",
                          required: ["id", "condition"],
                          additionalProperties: false,
                          properties: {
                            id: {
                              type: "number"
                            },
                            condition: {
                              type: "string"
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  };

  /**
   * Define o papel de cada elemento sem papel.
   * Elementos do tipo message, timer e parallel assumem o papel do proximo elemento conectado.
   * Elementos do tipo exclusive, end, parallel-convergent, api, human e start assumem o papel do
   * elemento anterior, ou do proximo elemento se nao houver um anterior.
   */
  function assignRoles(elementsMap, firstElement) {
    const visited = new Set();
    function dfs(curr, role) {
      if (visited.has(curr)) return;else visited.add(curr);
      if (!curr.role && curr.next.length === 0) curr.role = role;
      for (const {
        id
      } of curr.next) {
        const neighbor = elementsMap.get(id);
        dfs(neighbor, neighbor.role || role);
        if (curr.role) continue;
        if (["message", "timer", "parallel"].includes(curr.type)) {
          curr.role = neighbor.role;
        } else if (["exclusive", "end", "parallel-convergent", "api", "human", "start"].includes(curr.type)) {
          curr.role = role || neighbor.role;
        }
      }
    }

    // Define papel inicial para os elementos que não são iniciais ou humanos.
    elementsMap.forEach(e => {
      if (!["start", "human"].includes(e.type)) e.role = undefined;
    });
    dfs(firstElement, firstElement.role);
  }

  const types = {
    api: "API",
    end: "FIM",
    message: "MENSAGEM",
    timer: "TIMER",
    human: "HUMANA",
    start: "INÍCIO",
    parallel: "PARALELO",
    "parallel-convergent": "PARALELO-CONVERGENTE",
    exclusive: "EXCLUSIVO"
  };
  const elements = new Map();

  /**
   * Formata a descrição de processo.
   * @param {object} processDescription
   */
  function formatProcessDescription(processDescription) {
    try {
      processDescription.elements.forEach(e => elements.set(e.id, e));
      const firstElement = processDescription.elements[0];
      _basicFixes();
      _removeUnreachableElements(firstElement);
      _setFirstElementAsStart(firstElement);
      assignRoles(elements, firstElement);
      _addUserTaskBeforeGateway();
      _insertExclusiveGateways();
      _combineSequentialHumanTasks();
      _insertConvergentParallelGateways();
      processDescription.elements = _convertMapToElementsArray();
    } catch (err) {
      console.error(err);
    } finally {
      elements.clear();
    }
  }

  /**
   * Corrige problemas basicos.
   */
  function _basicFixes() {
    elements.forEach(e => {
      // Capitaliza a primeira palavra do nome.
      e.name = capitalizeFirstLetter(e.name);

      // Adiciona um array vazio em 'next' se nao existir.
      if (!e.next || e.type === "end") e.next = [];
      if (e.next.length === 0) return;

      // Substitui id de si mesmo para o proximo elemento.
      if (e.next.length === 1 && e.next[0].id === e.id) {
        e.next[0].id = e.id + 1;
      }

      // Remove ids que nao correspondem a nenhum elemento.
      const invalidIds = [];
      for (const {
        id
      } of e.next) if (!elements.get(id)) invalidIds.push(id);
      e.next = e.next.filter(({
        id
      }) => !invalidIds.includes(id));
    });
  }

  /**
   * Define o tipo do primeiro elemento como start.
   */
  function _setFirstElementAsStart(firstElement) {
    firstElement.type = "start";
  }

  /**
   * Adiciona uma tarefa humana antes de um gateway exclusivo.
   * Motivo: as vezes, a IA cria um gateway exclusivo mas nao cria a atividade tarefa que gera as decisoes.
   */
  function _addUserTaskBeforeGateway() {
    elements.forEach(e => {
      if (e.type !== "exclusive") return;
      const gateway = e;
      if (!gateway.role) {
        throw new Error(`Gateway "${gateway.name}" does not have a role`);
      }
      for (const el of elements.values()) {
        if (el.next.some(({
          id
        }) => id === gateway.id)) {
          if (el.role !== gateway.role && ["human", "start", "exclusive"].includes(el.type)) {
            const task = {
              id: el.id + 0.1,
              name: gateway.name.replace("?", ""),
              type: "human",
              role: gateway.role,
              next: [{
                id: gateway.id
              }]
            };
            el.next = el.next.filter(x => x.id !== gateway.id);
            el.next.push({
              id: task.id
            });
            elements.set(task.id, task);
          }
          break;
        }
      }
    });
  }

  /**
   * Remove elementos desconectados.
   */
  function _removeUnreachableElements(firstElement) {
    const visited = new Set();
    const queue = [firstElement];
    while (queue.length) {
      const curr = queue.shift();
      if (visited.has(curr)) continue;else visited.add(curr);
      for (const {
        id
      } of curr.next) {
        const nextElement = elements.get(id);
        queue.push(nextElement);
      }
    }
    const invalidIds = [];
    elements.forEach(e => {
      if (!visited.has(e)) invalidIds.push(e.id);
    });
    invalidIds.forEach(id => elements.delete(id));
  }

  /**
   * Insere gateways exclusivos.
   * O novo gateway eh inserido apos atividade humana que tem mais de um alvo.
   * Antes:
   * A──>B
   * └──>C
   * Depois:
   * A──>G──>B
   *     └──>C
   */
  function _insertExclusiveGateways() {
    elements.forEach(e => {
      if (e.next.length > 1 && e.type !== "exclusive" && !e.type.startsWith("parallel")) {
        const gateway = {
          id: e.id + 0.1,
          name: "Decisão",
          type: "exclusive",
          role: e.role,
          next: e.next
        };
        e.next = [{
          id: gateway.id
        }];
        elements.set(gateway.id, gateway);
      }
    });
  }

  /**
   * Combina atividades humanas sequencias que tenham o mesmo role.
   * Antes:
   * A, B e C sao atividades humanas com o mesmo role.
   * A──>B──>C──>D
   * Depois:
   * B e C sao removidas.
   * A──>D
   */
  function _combineSequentialHumanTasks() {
    const replacements = [];
    elements.forEach(e => {
      if (e.type !== "human" || e.next.length === 0) return;
      let nextElement = elements.get(e.next[0].id);
      while (nextElement.type === "human" && nextElement.role === e.role) {
        replacements.push({
          id: nextElement.id,
          replaceTo: e.id
        });
        if (nextElement.next.length === 0) break;
        nextElement = elements.get(nextElement.next[0].id);
      }
      e.next = [{
        id: nextElement.id
      }];
    });

    // Remover de 'next' os ids dos elementos que serao removidos.
    for (const r of replacements) for (const [_, e] of elements) {
      const idx = e.next.findIndex(({
        id
      }) => id === r.id);
      if (idx !== -1) e.next.splice(idx, 1, {
        id: r.replaceTo
      });
    }

    // Remover elementos.
    replacements.map(r => r.id).forEach(i => elements.delete(i));
  }

  /**
   * Insere gateways paralelos convergentes.
   */
  function _insertConvergentParallelGateways() {
    elements.forEach(e => {
      if (e.type !== "parallel" || e.next.length <= 1) return;
      const paths = [];
      for (const {
        id
      } of e.next) paths.push(..._listAllPaths(id));
      const common = _findFirstCommonElementId(paths);
      if (common === undefined || elements.get(common.id).type === "parallel-convergent") return;
      const gateway = {
        id: common.id - 0.1,
        name: e.name + " - Convergente",
        type: "parallel-convergent",
        next: [{
          id: common.id
        }],
        role: e.role
      };
      for (const prev of common.previous) {
        const element = elements.get(prev);
        element.next = element.next.filter(({
          id
        }) => id !== common.id);
        element.next.push({
          id: gateway.id
        });
      }
      elements.set(gateway.id, gateway);
    });

    /**
     * Lista todos os caminhos a partir de startElementId.
     */
    function _listAllPaths(startElementId) {
      const stack = [[startElementId]];
      const paths = [];
      while (stack.length) {
        const path = stack.pop();
        const elementId = path[path.length - 1];
        const element = elements.get(elementId);
        if (element.next.length === 0) {
          paths.push(path);
          continue;
        }
        for (const {
          id
        } of element.next) {
          if (path.includes(id)) {
            paths.push(path);
            continue;
          }
          const newPath = path.concat([id]);
          stack.push(newPath);
        }
      }
      return paths;
    }
    function _findFirstCommonElementId(paths) {
      const elements = {};
      const elementsInThePaths = [].concat(...paths);
      for (let i = 1; i < elementsInThePaths.length; i++) {
        const elementId = elementsInThePaths[i];
        if (elements[elementId]) elements[elementId]++;else elements[elementId] = 1;
      }
      for (const elementId in elements) {
        if (elements[elementId] !== paths.length) continue;
        const id = parseFloat(elementId);
        const previous = new Set();
        for (let path of paths) {
          const idx = path.indexOf(id);
          if (idx > 0) previous.add(path[idx - 1]);
        }
        return {
          id,
          previous: [...previous]
        };
      }
      return undefined;
    }
  }
  function _convertMapToElementsArray() {
    // Ordena.
    const sortedElements = Array.from(elements.values()).sort((a, b) => a.id - b.id);

    // Cria novos ids.
    const oldToNewId = new Map();
    sortedElements.forEach((e, i) => oldToNewId.set(e.id, i + 1));

    // Atribui os novos ids.
    sortedElements.forEach(e => {
      e.id = oldToNewId.get(e.id);
      e.next = e.next.map(({
        id,
        condition
      }) => ({
        id: oldToNewId.get(id),
        condition
      }));
    });
    return sortedElements;
  }

  /**
   * Transforma a descricao de processo de JSON para Markdown.
   * @param {string} jsonProcess
   * @returns
   */
  function transformProcessDescrFromJsonToMarkdown(jsonProcess) {
    function transform(o) {
      let r = `${o.id}. ${o.name}\n`;
      r += `- Tipo: ${types[o.type] || o.type}\n`;
      if (!isEventOrGateway(o.type) && o.role) r += `- Ator: ${o.role}\n`;
      (o.next || []).forEach(({
        id,
        condition
      }) => {
        condition = condition ? `[${condition}] ` : "";
        r += `- Próxima: ${condition}${id}\n`;
      });
      return r + "\n";
    }
    const {
      elements
    } = JSON.parse(jsonProcess);
    let result = "";
    for (const e of elements) result += transform(e);
    return result;
  }
  function isEventOrGateway(type) {
    return ["exclusive", "parallel", "parallel-convergent", "message", "timer", "end"].includes(type);
  }

  class zeevModelerConfig extends baseZeevChatCompletionConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this.lastJsonProcessDescr = "";
      this.lastMdProcessDescr = "";
      this._regexFormatDescription = /(\d+)[\\.\-\\#\\@\\:\\)\s]*\s*(.+?)\n*-?\s*Tipo:\s*([A-Z]+)\n*/g;
      this._skipFirstSendMessage = false;
      this._onlyUserMessage = "";
      this._checkUsage = undefined;
      this._doBilling = undefined;
      this._welcomeMessage = "Olá! Estou aqui para ajudar você a desenhar processos em BPMN." + "\n\nPara começar, você pode usar um dos nossos exemplos à esquerda." + "\n\nSe preferir, você pode descrever o seu processo. Nesse caso, a cada etapa, procure indicar os responsáveis e os procedimentos necessários.";
    }
    get body() {
      return settings1.body;
    }
    get helpText() {
      return "Descreva seu processo de negócio ou solicite uma alteração no processo que você já está modelando.";
    }
    get history() {
      return 1;
    }
    get inactivityMinutes() {
      return 20;
    }
    get skipSendToGpt() {
      return this._skipFirstSendMessage;
    }
    feed(_, {
      checkUsageAndLimit,
      doBilling
    } = {}) {
      if (checkUsageAndLimit) this._checkUsage = checkUsageAndLimit;
      if (doBilling) this._doBilling = doBilling;
    }
    getFixedMessages() {
      const fixed = [...settings1.messages];
      if (this.lastMdProcessDescr) fixed.push({
        role: "user",
        content: "Este é o meu processo atual. Faça ajustes no fluxo se for necessário. Após alterações, liste os elementos e fluxo do processo. " + this.lastMdProcessDescr
      });
      return fixed;
    }
    getWelcomeMessage() {
      return {
        message: this._welcomeMessage
      };
    }
    async getAlertMessage() {
      if (this._checkUsage) return await this._checkUsage();
      return null;
    }
    async afterUserMessage(context) {
      const {
        envelopSize
      } = context;
      if (envelopSize === 0 && this._doBilling) return await this._doBilling();
      return null;
    }
    getUserMessageHandlers() {
      return [{
        match: message => {
          this._skipFirstSendMessage = this._matchFormat(message);
          return this._skipFirstSendMessage;
        },
        handle: async message => {
          this._onlyUserMessage = message;
          return {
            message
          };
        }
      }];
    }
    getGptMessageHandlers() {
      return [{
        match: message => message !== this._welcomeMessage,
        handle: async message => {
          const isFirstDescription = this.lastJsonProcessDescr === "";
          const {
            messageId,
            created
          } = await this._tryCreateJsonProcessDescr(this._skipFirstSendMessage ? this._onlyUserMessage : message);
          if (!created) return {
            message
          };
          this._dispatchUpdateModelerEvent();
          message = this.lastMdProcessDescr = transformProcessDescrFromJsonToMarkdown(this.lastJsonProcessDescr);
          const previewMessage = this._addInstructions(message, isFirstDescription);
          return {
            message,
            previewMessage,
            copyToInput: true,
            messageId
          };
        }
      }];
    }

    /**
     * Tenta criar a descricao de processo no formato JSON a partir da descricao textual.
     * @param {string} processDescription
     * @returns true, se criou. false, caso contrario.
     */
    async _tryCreateJsonProcessDescr(processDescription) {
      if (!this._matchFormat(processDescription)) return {
        created: false
      };
      let result = false;
      let messageId = null;
      this.provider._body = settings2.body;
      const messages = [...settings2.messages];
      messages.push({
        role: "user",
        content: processDescription.replaceAll("\n", " ")
      });
      await this._sendMessages(messages, (id, json) => {
        messageId = id;
        result = this._tryFormatJsonProcessDescr(json);
      });
      this.provider._body = settings1.body;
      return {
        messageId,
        created: result
      };
    }
    _sendMessages(messages, callback) {
      return this.provider.sendMessageToGpt(messages, undefined, callback, e => console.error(e));
    }
    _tryFormatJsonProcessDescr(jsonProcessDescr) {
      try {
        const processDescr = JSON.parse(jsonProcessDescr);
        if (!processDescr.elements) return false;
        formatProcessDescription(processDescr);
        this.lastJsonProcessDescr = JSON.stringify(processDescr);
        return true;
      } catch (err) {
        console.info(err, jsonProcessDescr);
        return false;
      }
    }
    _dispatchUpdateModelerEvent() {
      document.dispatchEvent(new CustomEvent("update-modeler", {
        detail: {
          model: this.lastJsonProcessDescr,
          v: 2
        }
      }));
    }
    _addInstructions(processDescr, isFirstDescr) {
      if (isFirstDescr) return "Compreendi seu processo. Vou descrever ele abaixo e depois modelar ele para você. " + "Você pode solicitar alterações ou propor outra estrutura para ele. " + `Quando o processo estiver pronto, clique no botão **Continuar** no topo da página.\n\n${processDescr}`;
      return `Certo, aqui está.\n\n${processDescr}\n\nDeseja fazer mais alguma alteração? ` + "Quando o processo estiver pronto, clique em **Continuar**.";
    }
    _matchFormat(message) {
      return [...message.matchAll(this._regexFormatDescription)].length >= 2;
    }
  }

  var settings$2 = {
    messages: [],
    body: {
      project: "Default",
      requestData: {
        model: "300e7c19-df8d-47c1-ada2-eeee71ec6f2a",
        stream: true,
        tool_choice: "auto",
        tools: [{
          type: "function",
          function: {
            name: "search_kb",
            description: "Busca na documentação por conteúdo relevante, exemplos de código, referências de API e guias",
            parameters: {
              type: "object",
              properties: {
                query: {
                  type: "string"
                }
              },
              required: ["query"]
            }
          }
        }]
      }
    }
  };

  class zeevSupportCompletionConfig extends baseZeevChatCompletionConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      userName = "Eu";
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
    }
    get body() {
      return settings$2.body;
    }
    get enableStream() {
      return true;
    }
    get examples() {
      return ["Como criar uma instância por API?", "O que é uma tabela multivalorada?", "O que são os templates de aplicativo?"];
    }
    get history() {
      return 8;
    }
    getFixedMessages() {
      return settings$2.messages;
    }
    async getWelcomeMessage() {
      return `Olá! Eu sou a Zai, sua assistente virtual de IA no Zeev. Estou aqui para ajudar com dúvidas gerais sobre o funcionamento do produto. Caso você precise de suporte técnico adicional ou queira abrir um chamado, é só acessar a aba “Ticket” no menu ao lado.\n\nMas que tal me dar uma chance de ajudar você antes? O que você precisa?`;
    }
  }

  class zeevModelerEntry extends baseChatConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this._userName = userName;
      this._billingData = null;
      this._checkUsage = undefined;
    }
    get enableInput() {
      return false;
    }
    feed(data, {
      checkUsageAndLimit
    }) {
      this._billingData = data;
      if (checkUsageAndLimit) this._checkUsage = checkUsageAndLimit;
    }
    getWelcomeMessage() {
      return `Oi, ${this._userName}, tudo bem? Eu sou a Zai, como posso te ajudar hoje?`;
    }
    getGptMessageHandlers() {
      return [{
        match: () => true,
        handle: async message => {
          return {
            message,
            actions: await this._getActionsEntryByAllowance()
          };
        }
      }];
    }
    async _getActionsEntryByAllowance() {
      if (this._checkUsage) this._billingData = await this._checkUsage();
      const kbIsDisabled = this._getChatIsDisabledByKey("AIForKB");
      const configIsDisabled = this._getChatIsDisabledByKey("AIForConfig");
      const analyticsIsDisabled = this._getChatIsDisabledByKey("AIForAnalytics");
      return [{
        label: "Tenho dúvidas gerais sobre o funcionamento do Zeev.",
        attributes: kbIsDisabled ? {
          disabled: ""
        } : null,
        progress: this._getConsumptionByChat("AIForKB"),
        handle: () => {
          if (!kbIsDisabled) this._actionHandleVerifyUsageLimit("prod/modeler/kb", "AIForKB");
        }
      }, {
        label: "Gostaria de saber mais sobre as configurações desse aplicativo.",
        attributes: configIsDisabled ? {
          disabled: ""
        } : null,
        progress: this._getConsumptionByChat("AIForConfig"),
        handle: () => {
          if (!configIsDisabled) this._actionHandleVerifyUsageLimit("prod/modeler/configs", "AIForConfig");
        }
      }, {
        label: "Gostaria de analisar as estatísticas de uso desse aplicativo.",
        attributes: analyticsIsDisabled ? {
          disabled: ""
        } : null,
        progress: this._getConsumptionByChat("AIForAnalytics"),
        handle: () => {
          if (!analyticsIsDisabled) this._actionHandleVerifyUsageLimit("prod/modeler/analytics", "AIForAnalytics");
        }
      }];
    }
    _getChatIsDisabledByKey(chatKey) {
      const billingConsumption = this._getBillingConsumption(chatKey);
      // Esse if é para manter a compatibilidade com ambientes que ainda não possuiem os dados e tabela de billing
      if (billingConsumption === null) return false;
      return billingConsumption.usageLimit === 0;
    }
    _getConsumptionByChat(chatKey) {
      const billingConsumption = this._getBillingConsumption(chatKey);
      if (billingConsumption === null) return null;
      return {
        percentage: Math.floor(billingConsumption.usageCount / billingConsumption.usageLimit * 100),
        title: `Recurso com limite mensal de ${billingConsumption.usageLimit} usos. Este recurso já foi consumido ${billingConsumption.usageCount} vezes neste mês.`
      };
    }
    _actionHandleVerifyUsageLimit(config, chatKey) {
      if (this._checkUsage) {
        super.loadNewConfig(config);
        return;
      }

      // Codigo mantido para manter compatibilidade.
      // Deve ser removido em uma versao futura.
      const consumption = this._getBillingConsumption(chatKey);
      if (!consumption || consumption.usageCount < consumption.usageLimit) {
        super.loadNewConfig(config);
      } else {
        confirm({
          message: `Você está prestes a ultrapassar seu limite de ${consumption.usageLimit} usos mensais. Esta será sua ${consumption.usageCount + 1}ª utilização. Tem certeza de que deseja continuar?`,
          okCallback: () => super.loadNewConfig(config),
          noCallback: () => {
            this._resetInteractions();
            super.loadNewConfig("prod/modeler/entry");
          }
        });
      }
    }
    _getBillingConsumption(chatKey) {
      if (!this._billingData || !chatKey) return null;
      return this._billingData[chatKey] ?? null;
    }
    _resetInteractions() {
      document.querySelector(".chat-body").querySelectorAll("wc-chat-interaction").forEach(interaction => interaction.remove());
    }
  }

  class zeevHubOpenAiAssistantProvider extends baseProvider {
    constructor({
      env,
      log,
      config
    }) {
      super({
        env,
        log
      });
      this._config = config;
      this._clientUid = window.location.origin;
      this._files = [];
      this._threadId = null;
      this._feedDataSended = false;
    }
    get useStream() {
      return this._config?.body?.runOptions?.stream ?? false;
    }
    get threadId() {
      return this._threadId;
    }
    set threadId(value) {
      this._threadId = value;
    }
    get config() {
      return this._config;
    }
    set config(value) {
      this._config = value;
    }
    async sendMessageToGpt(messages, streamCallback, batchCallback, errorCallback) {
      if (this.useStream) {
        await this._sendAndProcessStreamMessageToHub(messages, batchCallback, errorCallback, streamCallback);
      } else {
        await this._sendAndProcessBatchMessageToHub(messages, batchCallback, errorCallback);
      }
    }
    async uploadFile(fileName, base64) {
      fileName = `${this._clientUid}-${generateRandomId()}.${fileName.split(".").pop()}`;
      const uploadBody = {
        clientUid: this._clientUid,
        contextId: this._contextId,
        enableLog: this._log,
        purpose: "assistants",
        fileName: fileName,
        f: base64
      };
      const fileId = await this._zeevHubAiHttpService.uploadFile(uploadBody);
      this._files.push(fileId);
      return {
        fileId
      };
    }
    async deleteAllFiles() {
      if (this._files.length === 0) return;
      this._files.forEach(async fileId => {
        await this.deleteFile(fileId);
      });
    }
    async deleteFile(fileId) {
      const deleteBody = {
        clientUid: this._clientUid,
        contextId: this._contextId,
        enableLog: this._log
      };
      await this._zeevHubAiHttpService.deleteFile(fileId, deleteBody);
      this._files = this._files.filter(id => id !== fileId);
    }
    async _sendAndProcessStreamMessageToHub(messages, batchCallback, errorCallback, streamCallback) {
      try {
        let message = "";
        this._abortController = new AbortController();
        const {
          response
        } = await this._sendMessageToHubAssistant(messages, this._abortController);
        const stream = response.body.getReader();
        if (!stream) await batchCallback();
        let done = false;
        let messageId = null;
        let url = null;
        let tokens = null;
        while (!done) {
          if (!stream) await batchCallback();
          const {
            value,
            done
          } = await stream.read();
          if (done) break;
          if (!value) continue;

          // Decodifique o valor binário para uma string UTF-8
          const text = new TextDecoder("utf-8").decode(value);
          const textParts = text.split("\n");
          for (const part of textParts) {
            if (!part) continue;
            const jsonPart = JSON.parse(part);
            const partResponse = this._handleGptMessage(jsonPart);
            message += partResponse.message;
            messageId = partResponse.messageId;
            tokens = partResponse.tokens;
            url = partResponse.url;
            streamCallback(messageId, message);
          }
        }
        await batchCallback(messageId, message, tokens, url);
      } catch (e) {
        errorCallback(e);
      }
    }
    async _sendAndProcessBatchMessageToHub(messages, batchCallback, errorCallback) {
      try {
        this._abortController = new AbortController();
        const {
          json
        } = await this._sendMessageToHubAssistant(messages, this._abortController);
        const result = this._handleGptMessage(json);
        batchCallback(result.messageId, result.message, result.tokens, result.url);
      } catch (e) {
        errorCallback(e);
      }
    }
    async _sendMessageToHubAssistant(messages, abortController) {
      const sendMessageBody = _objectSpread2({
        clientUid: this._clientUid,
        contextId: this._contextId,
        enableLog: this._log,
        userMessage: super.formatMessageBeforeSend(messages[0].content),
        feedData: this._getValidFeedData(this._feedDataSended, this.config.feedData)
      }, this.config.body);
      if (this._files.length > 0) {
        const attachments = this._files.map(id => ({
          file_id: id,
          tools: [{
            type: "file_search"
          }]
        }));
        sendMessageBody.messageOptions = {
          attachments
        };
        this._files = [];
      }
      return await this._zeevHubAiHttpService.sendMessageToAssistant(this.threadId, sendMessageBody, this.useStream, abortController);
    }
    _handleGptMessage(response) {
      if (!response?.data) return null;
      this._feedDataSended = true;
      this.threadId = response.data.threadId;
      const messageResponse = this._getMessageResponseAndSetFeedDataSended(response);
      return {
        messageId: response.id,
        threadId: response.data.threadId,
        message: messageResponse,
        url: response.data.url,
        tokens: {
          completionTokens: response.data.usage?.completion_tokens,
          promptTokens: response.data.usage?.prompt_tokens,
          totalTokens: response.data.usage?.total_tokens
        }
      };
    }
    _getMessageResponseAndSetFeedDataSended(response) {
      const messageValue = response.data.lastMessageForRun?.content[0]?.text?.value;
      if (messageValue === "empty_feed_data") {
        this._feedDataSended = false;
      }
      return messageValue;
    }
    _getValidFeedData(feedDataSended, feedData) {
      if (!feedDataSended && feedData) {
        return feedData;
      }
      return null;
    }
  }

  /* eslint-disable no-unused-vars */
  class baseZeevAssistantConfig extends baseChatConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this._env = env;
      this._log = log;
      this._userName = userName;
      this._userPhoto = userPhoto;
      this._userIcon = userIcon;
      this._userColor = userColor;
      this._provider = null;
    }
    get enableStream() {
      return false;
    }
    get assistantId() {
      throw new Error("Not implemented");
    }
    get provider() {
      if (!this._provider) {
        this._provider = new zeevHubOpenAiAssistantProvider({
          env: this._env,
          log: this._log,
          config: this
        });
      }
      return this._provider;
    }
    get history() {
      return 1;
    }
    get examples() {
      return [];
    }
    get body() {
      const additional_instructions = this.getAdditionalInstructions();
      if (additional_instructions === "") return {
        assistantId: this.assistantId
      };
      return {
        assistantId: this.assistantId,
        runOptions: {
          additional_instructions: additional_instructions
        }
      };
    }
    get feedData() {
      return null;
    }
    async addFile(name, content) {
      return await this.provider.uploadFile(name, content);
    }
    async afterAddFile(fileId) {
      return null;
    }
    async deleteFile(fileId) {
      return await this.provider.deleteFile(fileId);
    }
    async afterDeleteFile(fileId) {
      return null;
    }
    getAdditionalInstructions() {
      return "";
    }
    getStartupFiles() {
      return [];
    }
    getFixedMessages() {
      return [];
    }
    getUserMessageHandlers() {
      return [];
    }
    getGptMessageHandlers() {
      return [];
    }
  }

  /*
   * Chat responsável por responder dúvidas gerais do Zeev
   */
  class zeevModelerKbConfigV1 extends baseZeevAssistantConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this._checkUsage = undefined;
      this._doBilling = undefined;
      this._assistantId = env === "prod" ? "asst_pww4ke2vOwHpsA6ocx9ItBtk" : "asst_45drtnFJYcHhAR4RXGOjE3Un";
    }
    get body() {
      return {
        assistantId: this._assistantId,
        projectOpenAI: "AIForKB",
        runOptions: {
          stream: true,
          tool_choice: {
            type: "function",
            function: {
              name: "HubRetriever"
            }
          }
        }
      };
    }
    get inactivityMinutes() {
      return 20;
    }
    get examples() {
      return ["Quais tipos de autenticação o Zeev permite?", "O que é uma tabela multivalorada?"];
    }
    feed(_, {
      checkUsageAndLimit,
      doBilling
    } = {}) {
      if (checkUsageAndLimit) this._checkUsage = checkUsageAndLimit;
      if (doBilling) this._doBilling = doBilling;
    }
    getWelcomeMessage() {
      return "Certo! Como especialista no Zeev, estou aqui para esclarecer suas dúvidas gerais sobre o funcionamento do produto. Como posso te ajudar?";
    }
    async getAlertMessage() {
      if (!this._checkUsage) return null;
      const usage = await this._checkUsage();
      if (!usage) return null;
      return {
        main: i18nlocales.t("chat.consumeInfo2").replace("{n}", usage.usageLimit),
        secondary: i18nlocales.t("chat.consumeInfo3")
      };
    }
    async afterUserMessage(context) {
      const {
        envelopSize
      } = context;
      if (envelopSize === 0 && this._doBilling) {
        const {
          usageLimit,
          usageCount
        } = await this._doBilling();
        const chatInfo = i18nlocales.t("chat.consumeInfo1").replace("{0}", usageLimit).replace("{1}", usageCount);
        return {
          chatInfo
        };
      }
      return null;
    }
  }

  /*
   * Chat responsável por responder dúvidas analytics de aplicativos
   */
  class zeevModelerAnalyticsConfigV1 extends baseZeevAssistantConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this._contents = "";
      this._daysOfData = 60;
      this._sendContents = true;
      this._checkUsage = undefined;
      this._doBilling = undefined;
      this._assistantId = env === "prod" ? "asst_hnYbu8BHhxuS7taa3eAMiFw1" : "asst_svehn9pEQQQA4LXKWTMvURDG";
      this.setInitialContent();
    }
    get body() {
      return {
        assistantId: this._assistantId,
        runOptions: {
          stream: true
        }
      };
    }
    get inactivityMinutes() {
      return 20;
    }
    get examples() {
      return ["Qual o leadtime do aplicativo?", "Qual atividade tem a maior duração?", "Qual tarefa atrasa mais?", "Quantas atividades foram finalizadas no prazo?", "Sobre o que você pode falar?", "Explique como posso melhorar esse processo.", "Fale sobre a atividade ..."];
    }
    setInitialContent() {
      this._contents = `Hoje é dia ${new Date().toLocaleDateString("pt-BR")}.\n\n`;
    }
    feed(data, {
      checkUsageAndLimit,
      daysOfData,
      doBilling
    } = {}) {
      if (data) {
        this._contents += data;
      } else {
        this._contents = "";
      }
      if (checkUsageAndLimit) this._checkUsage = checkUsageAndLimit;
      if (daysOfData) this._daysOfData = daysOfData;
      if (doBilling) this._doBilling = doBilling;
    }
    getWelcomeMessage() {
      return "Certo! Como posso te ajudar em dúvidas sobre estatísticas de uso desse aplicativo?";
    }
    async getAlertMessage() {
      if (!this._checkUsage) return null;
      const usage = await this._checkUsage();
      if (!usage) return null;
      return {
        main: i18nlocales.t("chat.consumeInfo2").replace("{n}", usage.usageLimit),
        secondary: i18nlocales.t("chat.consumeInfo3")
      };
    }
    async afterUserMessage(context) {
      const {
        envelopSize
      } = context;
      if (envelopSize === 0 && this._doBilling) {
        const {
          usageLimit,
          usageCount
        } = await this._doBilling();
        const chatInfo = i18nlocales.t("chat.consumeInfo1").replace("{0}", usageLimit).replace("{1}", usageCount);
        return {
          chatInfo
        };
      }
      return null;
    }
    getGptMessageHandlers() {
      return [{
        match: (_, isReal) => isReal,
        handle: message => {
          // se ja enviou os dados sobre o aplicativo, entao nao envia mais.
          this._sendContents = false;
          if (message === "empty_feed_data") {
            message = `Eu só tenho as informações dos últimos ${this._daysOfData} e nesse período não encontrei dados sobre a execução desse aplicativo.`;
            this._sendContents = true;
          }
          return {
            message,
            info: `Respostas geradas com base nos dados dos últimos ${this._daysOfData} dias.`
          };
        }
      }];
    }
    getUserMessageHandlers() {
      return [{
        match: () => this._sendContents,
        handle: message => {
          message = `AIForAnalytics ''''${this._contents}''''\n\n${message}`;
          return {
            message
          };
        }
      }];
    }
  }

  /*
   * Chat responsável por responder dúvidas configurações de aplicativos
   */
  class zeevModelerConfigurationsConfigV1 extends baseZeevAssistantConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this._contents = "";
      this._sendContents = true;
      this._checkUsage = undefined;
      this._doBilling = undefined;
      this._assistantId = env === "prod" ? "asst_8yxKaB4oLOgbsH2CU3b1bgth" : "asst_mMuiMIgnBPwRTK20K5Js2OB0";
    }
    get body() {
      return {
        assistantId: this._assistantId,
        runOptions: {
          stream: true
        }
      };
    }
    get inactivityMinutes() {
      return 20;
    }
    get examples() {
      return ["Quais atividades enviam e-mails?", "Quais atividades possuem horas úteis?", "Qual o prazo total de execução deste aplicativo?", "Existe algum campo que nunca é utilizado?", "Quais atividades possuem arquivos obrigatórios?", "Monte uma tabela de atividades e prazo em horas."];
    }
    feed(data, {
      checkUsageAndLimit,
      doBilling
    } = {}) {
      this._contents = data;
      if (checkUsageAndLimit) this._checkUsage = checkUsageAndLimit;
      if (doBilling) this._doBilling = doBilling;
    }
    getWelcomeMessage() {
      return "Certo! Como te ajudar em dúvidas de configurações desse aplicativo?";
    }
    async getAlertMessage() {
      if (!this._checkUsage) return null;
      const usage = await this._checkUsage();
      if (!usage) return null;
      return {
        main: i18nlocales.t("chat.consumeInfo2").replace("{n}", usage.usageLimit),
        secondary: i18nlocales.t("chat.consumeInfo3")
      };
    }
    async afterUserMessage(context) {
      const {
        envelopSize
      } = context;
      if (envelopSize === 0 && this._doBilling) {
        const {
          usageLimit,
          usageCount
        } = await this._doBilling();
        const chatInfo = i18nlocales.t("chat.consumeInfo1").replace("{0}", usageLimit).replace("{1}", usageCount);
        return {
          chatInfo
        };
      }
      return null;
    }
    getGptMessageHandlers() {
      return [{
        match: (_, isReal) => isReal,
        handle: message => {
          // se ja enviou os dados sobre o aplicativo, entao nao envia mais.
          this._sendContents = false;
          if (message === "empty_feed_data") {
            message = `Não tenho informações suficientes para responder essa pergunta.`;
            this._sendContents = true;
          }
          return {
            message
          };
        }
      }];
    }
    getUserMessageHandlers() {
      return [{
        match: () => this._sendContents,
        handle: message => {
          message = `AIForConfig ''''${this._contents}''''\n${message}`;
          return {
            message
          };
        }
      }];
    }
  }

  let script = [];
  let step = 1;
  let currGroup = null;
  let currTableId = null;
  let currTableName = null;
  function generateScriptFromFields(fields, flowId, flowName) {
    script.push(`### Hoje é dia: "${new Date().toLocaleDateString("pt-BR")}"\n`);
    script.push(`### Formulário: "${flowName}"\n`);
    script.push(`### Identificador "flowId": ${flowId}\n\n`);
    script.push("### Roteiro:\n");
    fields.forEach((field, index) => {
      if (field.tableId) {
        handleMultivaluedField(field);
        checkRepetitionOrTermination(fields[index + 1]);
      } else {
        handleBasicField(field);
      }
    });
    addFinalStep();
    try {
      return script.join("");
    } finally {
      script = [];
      step = 1;
      currGroup = currTableId = currTableName = null;
    }
  }
  function handleBasicField(field) {
    if (currGroup !== "basic") startBasicGroup();
    addFieldScript(field);
  }
  function startBasicGroup() {
    currGroup = "basic";
    script.push(`\n${step}. **Perguntar sobre campos básicos**:\n`);
    step++;
  }
  function handleMultivaluedField(field) {
    if (currTableId !== field.tableId) startTable(field);
    addFieldScript(field);
  }
  function startTable(field) {
    currGroup = "table";
    currTableId = field.tableId;
    currTableName = field.groupName;
    script.push(`\n${step}. **Perguntar sobre a tabela "${field.groupName}"**:\n`);
    script.push("- Pergunte ao cliente sobre os seguintes campos:\n");
    step++;
  }
  function checkRepetitionOrTermination(nextField) {
    const nextTableId = nextField?.tableId || 0;
    if (currGroup === "table" && currTableId !== nextTableId) {
      script.push(`\n${step}. **Adicionar mais itens**:\n`);
      script.push(`- Pergunte se o cliente deseja adicionar mais um item à tabela ${currTableName}.\n`);
      script.push("- Se **sim**, volte para a etapa anterior.\n");
      script.push("- Se **não**, prossiga para a próxima etapa.\n");
      step++;
    }
  }
  function addFieldScript(field) {
    let fieldScript = `- |#${field.name}#|${field.label},`;
    if (field.required) fieldScript += "Obrigatório";else fieldScript += "Opcional";
    if (field.format) fieldScript += `,(Campo do tipo ${field.format})`;
    script.push(fieldScript + "\n");
  }
  function addFinalStep() {
    script.push(`\n${step}. **Resumo e confirmação**:\n`);
    script.push("- Informe que o formulário está preenchido.\n");
    script.push("- Exiba a lista com TODOS os nomes dos campos e valores preenchidos.\n");
    script.push("- Pergunte se o cliente deseja enviar a solicitação.\n");
  }

  class zeevRequestConfigV1 extends baseZeevAssistantConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor,
      dataset
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this._contents = null;
      this._flowName = dataset?.cFlowname ?? "";
      this._fields = [];
      this._files = [];
      this._checkUsage = undefined;
      this._doBilling = undefined;
      this._getDataSourceAttributes = undefined;
      this._sendRequestProcess = undefined;
      this._assistantId = env === "prod" ? "asst_qH0jvUyD9zqEaqpmD4YBUTaU" : "asst_GTzjBVBPFXFjjqKiHQMHfrWl";
      this._body = {
        assistantId: this._assistantId,
        projectOpenAI: env === "prod" ? "AIForRequest" : "Default",
        runOptions: {
          stream: true
        },
        allAssistants: {
          FLW: {
            name: env === "prod" ? "asst_hX6pRqN01U6C5guZfHymMF62" : "asst_XxrTLvO0oZKFj0pvTYUoPd9B"
          }
        }
      };
    }
    get body() {
      return this._body;
    }
    get controls() {
      return "like,dislike";
    }
    get inactivityMinutes() {
      return 20;
    }
    get feedData() {
      return this._contents;
    }
    addFile(name, content) {
      const fileId = Date.now() + "-" + name;
      this._files.push({
        fileId,
        name,
        content
      });
      return {
        fileId
      };
    }
    afterAddFile(fileId) {
      return {
        message: fileId
      };
    }
    deleteFile(fileId) {
      this._files = this._files.filter(f => f.fileId !== fileId);
    }
    afterDeleteFile() {
      return {
        message: ""
      };
    }
    feed({
      fields,
      flowId
    }, {
      getDataSourceAttributes,
      sendRequestProcess,
      checkUsageAndLimit,
      doBilling
    } = {}) {
      this._contents = {
        fixedMessage: generateScriptFromFields(fields, flowId, this._flowName)
      };
      this._fields = fields;
      this._getDataSourceAttributes = getDataSourceAttributes;
      this._sendRequestProcess = sendRequestProcess;
      if (checkUsageAndLimit) this._checkUsage = checkUsageAndLimit;
      if (doBilling) this._doBilling = doBilling;
    }
    async getWelcomeMessage() {
      return {
        message: `Olá, ${this._userName}! Irei te ajudar a abrir uma solicitação do aplicativo **${this._flowName}**.\nQuanto estiver pronta, clique em **okay** e então começaremos.`,
        chatInfo: "Essa é uma experiência inicial e limitada de abertura de solicitações com IA.\nSeu feedback é importante para definirmos os próximos passos."
      };
    }
    async getAlertMessage() {
      if (this._checkUsage) {
        const usage = await this._checkUsage();
        if (usage) {
          return {
            main: i18nlocales.t("chat.consumeInfo2").replace("{n}", usage.usageLimit),
            secondary: i18nlocales.t("chat.consumeInfo3")
          };
        }
      }
    }
    getGptMessageHandlers() {
      return [{
        match: message => message.startsWith("Olá,"),
        handle: message => {
          return {
            message,
            actions: [{
              label: "Okay!",
              handle: () => ({
                "set:message": "Vamos começar!"
              })
            }]
          };
        }
      }, {
        match: message => this.getIdentifierFieldName(message) !== null,
        handle: async message => {
          const actions = [];
          const name = this.getIdentifierFieldName(message);
          const field = this._fields.find(f => f.name === name);
          if (field?.dataSourceId) {
            const {
              successAttributes
            } = await this._getDataSourceAttributes(field.dataSourceId);
            successAttributes.forEach(a => actions.push({
              label: a.txt,
              handle: () => ({
                "set:message": a.cod
              })
            }));
          } else if (field?.format === "arquivo") {
            actions.push({
              label: "Adicionar arquivo",
              handle: () => ({
                "select:file": 0
              }),
              keepEnabled: true
            });
          }
          return {
            message: this.removeIdentifierFieldName(message),
            actions
          };
        }
      }, {
        match: message => /.*\benviar\b.*\bsolicitação\b.*/i.test(message),
        handle: async message => {
          return {
            message: this.removeIdentifierFieldName(message),
            actions: [{
              label: "Sim, quero enviar!",
              handle: () => this.confirmSendFormField()
            }, {
              label: "Ainda não…",
              handle: () => ({
                "set:message": "Não, quero mudar uma coisa."
              })
            }]
          };
        }
      }, {
        match: message => isJson(message),
        handle: async message => await this.createInstance(message)
      }];
    }
    getIdentifierFieldName(message) {
      const matches = message.match(/\|#([^#]*)#\|/);
      return matches ? matches[1] : null;
    }
    removeIdentifierFieldName(message) {
      return message.replace(/\|#.*?#\|/g, "").trim();
    }
    confirmSendFormField() {
      this._body.runOptions.stream = false;
      return {
        "set:message": "Sim, quero enviar!"
      };
    }
    async createInstance(message) {
      this._body.runOptions.stream = true;
      const payload = JSON.parse(message);
      try {
        this._files.forEach(file => {
          const field = payload.formFields.find(ff => ff.value === file.fileId);
          if (field) field.value = file.content;
        });
        const {
          error,
          success
        } = await this._sendRequest(payload);
        if (error) throw error;
        return {
          message: `Solicitação iniciada com sucesso! Para acompanhar, clique no código [#${success.id}](${success.url}).`
        };
      } catch (error) {
        console.error(error);
        throw error;
      }
    }

    // TODO: informar ao chat quais as acoes possiveis para a pessoa escolher
    async _sendRequest(payload) {
      const response = await this._sendRequestProcess(payload);
      if (response?.error) {
        const error = response.error;
        if (error.key === "execute.msgresultnotexpected") {
          payload.result = error.vars[1].replaceAll('"', "");
          return await this._sendRequestProcess(payload);
        }
      }
      return response || {
        error: {
          message: "Erro ao enviar a solicitação."
        }
      };
    }
    async afterUserMessage(context) {
      if (context.envelopSize === 0 && this._doBilling) {
        const {
          usageLimit,
          usageCount
        } = await this._doBilling();
        const chatInfo = i18nlocales.t("chat.consumeInfo1").replace("{0}", usageLimit).replace("{1}", usageCount);
        return {
          chatInfo
        };
      }
      return null;
    }
  }

  class baseAIToolConfig {
    constructor({
      env,
      log
    }) {
      this._env = env;
      this._log = log;
      this._provider = new zeevOpenAiChatCompletionProvider({
        env,
        log,
        body: this.body
      });
    }
    get body() {
      throw new Error("Not implemented.");
    }
    get feedData() {
      return null;
    }
    get fixedMessages() {
      return [];
    }
    get provider() {
      return this._provider;
    }
    get jsonResponse() {
      return this.body.requestData?.response_format?.type === "json_schema";
    }
  }

  class createAppTitleConfig extends baseAIToolConfig {
    constructor({
      env,
      log
    }) {
      super({
        env,
        log
      });
    }
    get body() {
      return {
        requestData: {
          model: "84279b09-a6e2-4157-a610-9969a3464fdc",
          temperature: 1,
          response_format: {
            type: "json_schema",
            json_schema: {
              name: "title_suggestions",
              strict: true,
              schema: {
                type: "object",
                required: ["titles", "explication"],
                additionalProperties: false,
                properties: {
                  titles: {
                    type: "array",
                    items: {
                      type: "string"
                    }
                  },
                  explication: {
                    type: "string"
                  }
                }
              }
            }
          }
        }
      };
    }
    get fixedMessages() {
      return [{
        role: "system",
        content: 'Crie sugestões de título para o processo. O importante é que o nome do processo reflita de forma objetiva, mas também abrangente, qual é o trabalho realizado nele. Sugira nomes iniciando tanto com substantivo quanto verbo no infinitivo. Explique o processo para si mesma para te ajudar. Retorne apenas os 3 melhores. Apenas uma das 3 sugestões deve começar com "Processo de". Responda no formato JSON.'
      }];
    }
  }

  class agentConfigurationGenerator extends baseAIToolConfig {
    constructor({
      env,
      log
    }) {
      super({
        env,
        log
      });
    }
    get body() {
      return {
        requestData: {
          model: "84279b09-a6e2-4157-a610-9969a3464fdc",
          temperature: 1,
          response_format: {
            type: "json_schema",
            json_schema: {
              name: "agent_configuration",
              strict: true,
              schema: {
                type: "object",
                required: ["prompt", "files", "outputs"],
                additionalProperties: false,
                properties: {
                  prompt: {
                    type: "string"
                  },
                  files: {
                    type: "array",
                    items: {
                      type: "object",
                      required: ["file", "description"],
                      additionalProperties: false,
                      properties: {
                        file: {
                          type: "string"
                        },
                        description: {
                          type: "string"
                        }
                      }
                    }
                  },
                  outputs: {
                    type: "array",
                    items: {
                      type: "object",
                      required: ["output", "description"],
                      additionalProperties: false,
                      properties: {
                        output: {
                          type: "string"
                        },
                        description: {
                          type: "string"
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      };
    }
    get fixedMessages() {
      return [];
    }
  }

  class agentOutputMapper extends baseAIToolConfig {
    constructor({
      env,
      log
    }) {
      super({
        env,
        log
      });
    }
    get body() {
      return {
        requestData: {
          model: "84279b09-a6e2-4157-a610-9969a3464fdc",
          temperature: 1,
          response_format: {
            type: "json_schema",
            json_schema: {
              name: "mappings",
              strict: true,
              schema: {
                type: "object",
                required: ["mappings"],
                additionalProperties: false,
                properties: {
                  mappings: {
                    type: "array",
                    items: {
                      type: "object",
                      required: ["output", "field"],
                      additionalProperties: false,
                      properties: {
                        output: {
                          type: "string"
                        },
                        field: {
                          type: "string"
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      };
    }
    get fixedMessages() {
      return [];
    }
  }

  class agentInputMapper extends baseAIToolConfig {
    constructor({
      env,
      log
    }) {
      super({
        env,
        log
      });
    }
    get body() {
      return {
        requestData: {
          model: "84279b09-a6e2-4157-a610-9969a3464fdc",
          temperature: 1,
          response_format: {
            type: "json_schema",
            json_schema: {
              name: "mappings",
              strict: true,
              schema: {
                type: "object",
                required: ["mappings"],
                additionalProperties: false,
                properties: {
                  mappings: {
                    type: "array",
                    items: {
                      type: "object",
                      required: ["input", "source"],
                      additionalProperties: false,
                      properties: {
                        input: {
                          type: "string"
                        },
                        source: {
                          type: "string"
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      };
    }
    get fixedMessages() {
      return [];
    }
  }

  var settings$1 = {
    messages: [{
      role: "system",
      content: `**User**: o nome do usuário é {{user_name}}.\n**User app/process**: o nome do aplicativo/processo é "{{app_name}}".`
    }, {
      role: "system",
      content: `<app_analytics_data_context>\n{{data_analitics}}\n</app_analytics_data_context>`
    }, {
      role: "system",
      content: `<app_configuration_data_context>\n{{data_config}}\n</app_configuration_data_context>`
    }],
    body: {
      project: "Default",
      requestData: {
        model: "b242a367-0379-4d13-8dd9-e8cbb9a934a8",
        stream: true,
        tool_choice: "auto",
        tools: [{
          type: "function",
          function: {
            name: "search_kb",
            description: "Busca na documentação por conteúdo relevante, exemplos de código, referências de API e guias",
            parameters: {
              type: "object",
              properties: {
                query: {
                  type: "string"
                }
              },
              required: ["query"]
            }
          }
        }]
      }
    }
  };

  const GUID_NAME_REGEX = /\|\s*([^|]+)\s*\|\s*# ([^#]+) #\s*\|/g;
  const GUID_REGEX = /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/g;
  class zeevChatCompletionUnifiedConfig extends baseZeevChatCompletionConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this._guidToNameMap = {};
      this._checkUsage = undefined;
      this._doBilling = undefined;
    }
    get body() {
      return settings$1.body;
    }
    get enableStream() {
      return true;
    }
    get examples() {
      return ["Existe algum campo que nunca é utilizado?", "Quais atividades enviam e-mails?", "Monte uma tabela de atividades e prazo em horas com base nas atividades.", "Qual atividade atrasa mais?", "Qual pessoa executa mais atividades fora do prazo?"];
    }
    get history() {
      return 8;
    }
    feed(feedData, {
      checkUsageAndLimit,
      doBilling
    } = {}) {
      if (checkUsageAndLimit) this._checkUsage = checkUsageAndLimit;
      if (doBilling) this._doBilling = doBilling;
      const {
        AIForAnalytics,
        AIForConfig
      } = feedData || {};
      this._createGuidToNameMap(AIForAnalytics);
      const appName = window.parent?.document?.querySelector("span[data-original-title]")?.textContent || "Aplicativo de " + this._userName;
      this._fixedMessages = settings$1.messages.map(message => {
        if (message.role === "system") {
          return _objectSpread2(_objectSpread2({}, message), {}, {
            content: message.content.replace(/{{data_analitics}}/g, this._anonymize(AIForAnalytics)).replace(/{{data_config}}/g, AIForConfig || "").replace(/{{app_name}}/g, appName).replace(/{{user_name}}/g, this._userName)
          });
        }
        return message;
      });
    }
    getFixedMessages() {
      return this._fixedMessages || [...settings$1.messages];
    }
    async getWelcomeMessage() {
      return `Olá, ${this._userName}! Como especialista no Zeev, posso esclarecer suas dúvidas gerais sobre o funcionamento do produto, te ajudar em dúvidas de configurações desse aplicativo e também posso te ajudar em dúvidas sobre estatísticas de uso desse aplicativo. Como posso te ajudar?`;
    }
    getGptMessageHandlers() {
      return [{
        match: (_, isReal) => isReal,
        handle: message => {
          return {
            message: this._replaceGuidWithName(message)
          };
        }
      }];
    }
    async getAlertMessage() {
      if (!this._checkUsage) return null;
      const usage = await this._checkUsage();
      if (!usage) return null;
      return {
        main: i18nlocales.t("chat.consumeInfo2").replace("{n}", usage.usageLimit),
        secondary: i18nlocales.t("chat.consumeInfo3")
      };
    }
    async afterUserMessage(context) {
      const {
        envelopSize
      } = context;
      if (envelopSize === 0 && this._doBilling) {
        const {
          usageLimit,
          usageCount
        } = await this._doBilling();
        const chatInfo = i18nlocales.t("chat.consumeInfo1").replace("{0}", usageLimit).replace("{1}", usageCount);
        return {
          chatInfo
        };
      }
      return null;
    }
    _replaceGuidWithName(message) {
      if (!message) return message;
      return message.replace(GUID_REGEX, (_, guid) => `**${this._guidToNameMap[guid] || guid}**`);
    }
    _anonymize(rawData) {
      if (!rawData) return "";
      return rawData.replace(GUID_NAME_REGEX, (_, guid) => `| ${guid.trim()} |`);
    }
    _createGuidToNameMap(dataRaw) {
      this._guidToNameMap = {};
      if (!dataRaw || typeof dataRaw !== "string") return;
      const matches = dataRaw.matchAll(GUID_NAME_REGEX);
      for (const match of matches) {
        const guid = match[1].trim(); // (GUID)
        const name = match[2].trim(); // (# Nome #)
        this._guidToNameMap[guid] = name;
      }
    }
  }

  var settings = {
    messages: [{
      role: "system",
      content: `**User**: o nome do usuário é {{user_name}}.`
    }],
    body: {
      project: "Default",
      requestData: {
        model: "e360c58c-5c33-4cf4-b703-96598d26506d",
        stream: true,
        tool_choice: "auto",
        tools: [{
          type: "function",
          function: {
            name: "search_kb",
            description: "Busca na documentação por conteúdo relevante, exemplos de código, referências de API e guias",
            parameters: {
              type: "object",
              properties: {
                query: {
                  type: "string"
                }
              },
              required: ["query"]
            }
          }
        }]
      }
    }
  };

  class zeevChatCompletionKbConfig extends baseZeevChatCompletionConfig {
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this._checkUsage = undefined;
      this._doBilling = undefined;
    }
    get body() {
      return settings.body;
    }
    get enableStream() {
      return true;
    }
    get examples() {
      return ["Como criar uma instância por API?", "Como funciona a configuração multi-instância de um subprocesso?", "O que são os templates de aplicativo?", "Como registrar uma ausência temporária usando API?"];
    }
    get history() {
      return 8;
    }
    feed(_, {
      checkUsageAndLimit,
      doBilling
    } = {}) {
      if (checkUsageAndLimit) this._checkUsage = checkUsageAndLimit;
      if (doBilling) this._doBilling = doBilling;
      this._fixedMessages = settings.messages.map(message => {
        if (message.role === "system") {
          return _objectSpread2(_objectSpread2({}, message), {}, {
            content: message.content.replace(/{{user_name}}/g, this._userName)
          });
        }
        return message;
      });
    }
    getFixedMessages() {
      return this._fixedMessages || [...settings.messages];
    }
    async getWelcomeMessage() {
      return `Olá, ${this._userName}! Como especialista no Zeev, estou aqui para esclarecer suas dúvidas gerais sobre o funcionamento do produto. Como posso te ajudar?`;
    }
    async getAlertMessage() {
      if (!this._checkUsage) return null;
      const usage = await this._checkUsage();
      if (!usage) return null;
      return {
        main: i18nlocales.t("chat.consumeInfo2").replace("{n}", usage.usageLimit),
        secondary: i18nlocales.t("chat.consumeInfo3")
      };
    }
    async afterUserMessage(context) {
      const {
        envelopSize
      } = context;
      if (envelopSize === 0 && this._doBilling) {
        const {
          usageLimit,
          usageCount
        } = await this._doBilling();
        const chatInfo = i18nlocales.t("chat.consumeInfo1").replace("{0}", usageLimit).replace("{1}", usageCount);
        return {
          chatInfo
        };
      }
      return null;
    }
  }

  class FormScriptsConfig extends baseZeevChatCompletionConfig {
    CONTEXT_ID = "ActiveTab";
    TAB_MAP = {
      javaScript: {
        label: i18nlocales.t("chat.formScripts.tabs.javaScript.label"),
        description: i18nlocales.t("chat.formScripts.tabs.javaScript.description"),
        editorId: "editor-js"
      },
      css: {
        label: i18nlocales.t("chat.formScripts.tabs.css.label"),
        description: i18nlocales.t("chat.formScripts.tabs.css.description"),
        editorId: "editor-css"
      },
      headers: {}
    };
    constructor({
      env,
      log,
      userName,
      userPhoto,
      userIcon,
      userColor
    }) {
      super({
        env,
        log,
        userName,
        userPhoto,
        userIcon,
        userColor
      });
      this._checkUsage = undefined;
      this._doBilling = undefined;
      this._resourceName = "AIForFormScripts";
      this._messages = [{
        role: "system",
        content: `<campos_formulario>
        |Identificador do Campo|Está em uma tabela?|Nome da Tabela|
        |----|----|----|
        {{form_fields}}
        </campos_formulario>`
      }, {
        role: "user",
        content: `Meu nome é {{user_name}}.`
      }];
    }
    get body() {
      return {
        project: "Default",
        requestData: {
          model: "a23c112e-e66c-441b-a579-e6f5792ac16f",
          stream: true,
          stop: ["Enquanto isso"]
        }
      };
    }
    get contexts() {
      const tab = this.TAB_MAP.javaScript;
      return [{
        contextId: this.CONTEXT_ID,
        label: tab.label,
        description: tab.description,
        checked: true,
        source: () => document.getElementById(tab.editorId)?.value || ""
      }];
    }
    get enableStream() {
      return true;
    }
    get examples() {
      return [{
        message: "Analise meu código e informe como posso melhorá-lo.",
        context: this.CONTEXT_ID
      }, {
        message: "Explique este código JavaScript e liste as principais funcionalidades.",
        context: this.CONTEXT_ID
      }, {
        message: "Crie uma função que valide os campos..."
      }];
    }
    get history() {
      return 999;
    }

    /**
     * Altera dinamicamente o contexto da aba ativa no formulário.
     * @param {string} contextId - ID do contexto a atualizar.
     * @param {{ activeTab: "javaScript" | "css", visibility?: "visible" | "hidden" }} options
     */
    updateContext(contextId, {
      activeTab,
      visibility
    } = {}) {
      const tab = this.TAB_MAP[activeTab];
      if (!tab) return;
      super.updateContext(contextId, {
        label: tab.label,
        description: tab.description,
        source: () => document.getElementById(tab.editorId)?.value || "",
        visibility
      });
    }
    feed({
      checkUsageAndLimit,
      doBilling,
      formFields
    } = {}) {
      if (checkUsageAndLimit) this._checkUsage = checkUsageAndLimit;
      if (doBilling) this._doBilling = doBilling;
      formFields = formFields || "Este formulário não possui campos ainda.";
      this._fixedMessages = this._messages.map(message => {
        return _objectSpread2(_objectSpread2({}, message), {}, {
          content: message.content.replace(/{{user_name}}/g, this._userName).replace(/{{form_fields}}/g, formFields)
        });
      });
    }
    getFixedMessages() {
      return this._fixedMessages || [];
    }
    async getWelcomeMessage() {
      return `Olá! Sou a Zai e estou aqui para te apoiar na análise e geração de customização de formulários. Sinta-se à vontade para me perguntar sobre melhorias, explicações ou pedir funções específicas.`;
    }
    async getAlertMessage() {
      if (this._checkUsage) return await this._checkUsage(this._resourceName);
      return null;
    }
    async afterUserMessage(context) {
      const {
        envelopSize
      } = context;
      if (envelopSize === 0 && this._doBilling) return await this._doBilling(this._resourceName);
      return null;
    }
  }

  const configMap = {
    "lab/ai-modeler": zeevModelerConfig,
    "lab/ai-modeler-2": zeevModelerConfig,
    "prod/kb": zeevChatCompletionKbConfig,
    "support/kb": zeevSupportCompletionConfig,
    "prod/kb/unified": zeevChatCompletionKbConfig,
    "prod/modeler/entry": zeevModelerEntry,
    "prod/modeler/kb": zeevModelerKbConfigV1,
    "prod/modeler/analytics": zeevModelerAnalyticsConfigV1,
    "prod/modeler/configs": zeevModelerConfigurationsConfigV1,
    "prod/modeler/unified": zeevChatCompletionUnifiedConfig,
    "prod/request": zeevRequestConfigV1,
    "prod/form-scripts": FormScriptsConfig,
    "tool/create-app-title": createAppTitleConfig,
    "tool/agent-configuration-generator": agentConfigurationGenerator,
    "tool/agent-output-mapper": agentOutputMapper,
    "tool/agent-input-mapper": agentInputMapper
  };

  class baseEnvelop {
    constructor(role, content) {
      this.role = role;
      this.content = content;
    }
    getMessage() {
      return {
        role: this.role,
        content: this.content
      };
    }
  }

  class chatElement extends HTMLElement {
    //#region constructor & getters & setters
    constructor() {
      super();
      this._envelops = [];
      this._config = {};
      this._animationInterval = null;
      this._currentControlsMessageId = null;
      this._inactivityTimer;
      this._chatInputInitialHeight = 0;
      this._registeredContexts = new Map();
      this._boundOnChangeZeevConfig = this._onChangeZeevConfig.bind(this);
      this._boundOnChatContextUpdate = this._onChatContextUpdate.bind(this);
    }

    //#region HTML attributes
    get maxlength() {
      return this.getIntegerAttribute("maxlength", 600);
    }
    set maxlength(value) {
      this.setAttribute("maxlength", value);
    }

    /**
     * Identificador da configuração. Atributo.
     */
    get config() {
      return this.getStringAttribute("config", "");
    }
    set config(value) {
      this.setAttribute("config", value);
    }

    /**
     * Ambiente: DEV, HML ou PROD. Atributo.
     */
    set env(value) {
      this.setAttribute("env", value);
    }
    get env() {
      return this.getStringAttribute("env", "hml", ["dev", "test", "hml", "prod"]);
    }

    /**
     * Indica se logs devem ser armazenados ou não
     */
    get log() {
      return this.getBooleanAttribute("log", true);
    }
    set log(value) {
      this.setAttribute("log", value);
    }

    /**
     * Se estiver ativo, o console.log será usado para mostrar informações de debug.
     */
    get debug() {
      return this.getBooleanAttribute("debug", false);
    }
    set debug(value) {
      this.setAttribute("debug", value);
    }

    /**
     * Nome do usuário logado
     */
    get userName() {
      return this.getStringAttribute("username");
    }
    set userName(value) {
      this.setAttribute("username", value);
    }

    /**
     * Foto do usuário logado
     */
    get userPhoto() {
      return this.getStringAttribute("userphoto");
    }
    set userPhoto(value) {
      this.setAttribute("userphoto", value);
    }

    /**
     * Icone do usuário logado, em substituição ao nome e foto
     */
    get userIcon() {
      return this.getStringAttribute("usericon");
    }
    set userIcon(value) {
      this.setAttribute("usericon", value);
    }

    /**
     * Cor do usuário logado, em substituição a foto
     */
    get userColor() {
      return this.getStringAttribute("usercolor", "primary", ["primary", "secondary", "success", "danger", "warning", "info", "light", "dark"]);
    }
    set userColor(value) {
      this.setAttribute("usercolor", value);
    }
    get disabled() {
      return this.getBooleanAttribute("disabled", false);
    }
    set disabled(value) {
      this.setAttribute("disabled", value);
    }

    //#endregion HTML attributes

    /**
     * Configurações do chat: modelo, body, stream, exemplos, etc..
     */
    get configuration() {
      return this._config;
    }
    set configuration(value) {
      this._config = value;
      document.dispatchEvent(new CustomEvent("zeevChatConfigChanged", {
        detail: this.config
      }));
    }

    /**
     * Array com todas as mensagens trocadas nessa interação
     */
    get envelops() {
      return this._envelops;
    }
    set envelops(value) {
      this._envelops = value;
    }
    get fileaccept() {
      return this.configuration.fileAccept || "*";
    }

    /**
     * Máximo de mensagens históricas que devem ser enviadas a cada requisição. Economizar tokens.
     */
    get history() {
      return this.configuration.history ?? 5;
    }

    /**
     * Lista de exemplos conforme padrão de configuração.
     */
    get examples() {
      return (this.configuration.examples ?? []).sort((a, b) => (a.message?.length || a.length) - (b.message?.length || b.length));
    }
    get currentControlsMessageId() {
      return this._currentControlsMessageId;
    }
    set currentControlsMessageId(value) {
      this._currentControlsMessageId = value;
    }

    //#region HTML elements
    get chatFeedbackElement() {
      return this.querySelector(".chat-feedback");
    }
    get chatBtnFeedbackElement() {
      return this.querySelector(".btn-feedback");
    }
    get chatBtnCancelFeedbackElement() {
      return this.querySelector(".btn-cancel-feedback");
    }
    get chatInputFeedbackElement() {
      return this.querySelector("#inputFeedback");
    }
    get chatMainElement() {
      return this.querySelector(".chat-main");
    }
    get chatBodyElement() {
      return this.querySelector(".chat-body");
    }
    get chatFooterElement() {
      return this.querySelector(".chat-footer");
    }
    get chatInputElement() {
      return document.getElementById("chat-input");
    }
    get chatBtnSendMessageElement() {
      return this.querySelector(".btn-input-chat");
    }
    get chatBtnStop() {
      return this.querySelector(".btn-stop-response");
    }
    get chatBtnReset() {
      return this.querySelector(".btn-chat-reset");
    }
    get chatExamplesColapseHandlerElement() {
      return this.querySelector(".chat-examples-colapse-handler");
    }
    get chatExamplesElement() {
      return this.querySelector(".chat-examples");
    }
    get chatOverlay() {
      return this.querySelector(".chat-overlay");
    }
    get chatBtnSelectFileElement() {
      return this.querySelector("#btn-select-file");
    }
    get chatSelectFileElement() {
      return this.querySelector("#select-file");
    }
    get chatFilesElement() {
      return this.querySelector(".chat-files");
    }
    get chatContextsElement() {
      return this.querySelector(".chat-contexts");
    }

    //#endregion HTML elements

    //#endregion constructor & getters & setters

    //#region public methods
    /**
     * Adiciona mensagem no input do chat mas não faz o envio.
     * @param {string} message
     * @param {boolean} focus
     */
    addUserMessage(message, focus) {
      this._setInputValue(message);
      if (focus) this._focusInput();
    }
    getModuleConfig(configUid) {
      return configMap[configUid];
    }

    /**
     * Reinicia o chat.
     */
    async reset() {
      this.chatBodyElement.innerHTML = "";
      this.envelops = [];
      this._setInputValue("");
      clearTimeout(this._inactivityTimer);
      await this._reconfigure(this._originalConfigUid);
      this.dispatchEvent(new Event("after-reset:chat"));
    }
    static get observedAttributes() {
      return ["disabled"];
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "disabled" && oldValue !== newValue) {
        this._setDisabledControllers();
      }
    }
    async connectedCallback() {
      await this._init();
      this.dispatchEvent(new Event("ready"));
    }
    disconnectedCallback() {
      document.removeEventListener("changeZeevConfig", this._boundOnChangeZeevConfig);
      document.removeEventListener("chatContextUpdate", this._boundOnChatContextUpdate);
    }
    //#endregion

    //#region private methods
    async _init() {
      this._originalConfigUid = this.config;
      this._originalDocumentTitle = document.title;
      this.configuration = await this._createConfig();
      this._render();
      this._configureUi();
      await this._bindEvents();
      await this._showWelcomeMessage();
      await this._showAlert();
    }
    async _createConfig() {
      const zeevChatConfig = this.getModuleConfig(this.config);
      if (!zeevChatConfig) throw new Error(`Configuração ${this.config} não encontrada.`);
      return new zeevChatConfig({
        env: this.env,
        log: this.log,
        userName: this.userName,
        userPhoto: this.userPhoto,
        userIcon: this.userIcon,
        userColor: this.userColor,
        dataset: this.dataset
      });
    }
    _render() {
      this.innerHTML = `
        <div class="d-none chat-feedback">
          <textarea class="form-control form-control-feedback" 
                    id="inputFeedback"
                    rows="4" 
                    placeholder="${i18nlocales.t("chat.feedbackPlaceholder")}" 
                    maxlength="${this.maxlength}"></textarea>
          <div class="chat-feedback-buttons">
            <button type="button" class="btn btn-link btn-cancel-feedback">${i18nlocales.t("chat.feedbackCancel")}</button>
            <button type="button" class="btn btn-sm btn-primary btn-feedback">${i18nlocales.t("chat.feedbackSend")}</button>
          </div>
        </div>
        <div class="chat-main">
          <div class="btn-group chat-options d-none" role="group" aria-label="${i18nlocales.t("chat.optionsAriaLabel")}">
            <button type="button" id="chats-history" class="btn btn-secondary"><wc-icon icon="align-justify" size="md"></wc-icon></button>
            <button type="button" id="chat-delete" class="btn btn-secondary"><wc-icon icon="trash" size="md"></wc-icon></button>        
          </div>
          <wc-tooltip target="#chats-history" content="${i18nlocales.t("chat.tooltipHistory")}"></wc-tooltip>            
          <wc-tooltip target="#chat-delete" content="${i18nlocales.t("chat.tooltipDeleteChat")}"></wc-tooltip>    
          <div class="chat-body">          
          </div>
          <div class="chat-footer">
            <div class="chat-examples-colapse-handler" title="${i18nlocales.t("chat.toggleExamplesTitle")}">
              <div class="chat-examples-colapse-handler-left"></div>
              <div class="chat-examples-colapse-handler-right"></div>
            </div>
            <div class="chat-examples"></div>
            <div class="d-flex gap-2 align-items-center my-2">
              <div class="chat-reset">
                <button id="btn-new-chat" type="button" class="btn btn-primary btn-lg btn-chat-reset rounded-circle p-2" title="${i18nlocales.t("chat.resetConversationTitle")}"><wc-icon icon="message-circle-plus" size="lg"></wc-icon></button>
                <wc-tooltip target="#btn-new-chat" content="${i18nlocales.t("chat.tooltipNewChat")}"></wc-tooltip>            
              </div>
              <div class="chat-input-container form-control">
                <div class="chat-contexts d-none"></div>
                <div class="chat-files">
                  <button id="btn-select-file" type="button" style="display: none"><wc-icon icon="paperclip" size="md"></wc-icon></button>
                  <input id="select-file" type="file" accept="${this.fileaccept}" style="display: none" />
                </div>
                <textarea id="chat-input"
                  rows="3"
                  maxlength="${this.maxlength}"></textarea>
                <div class="chat-extra">
                  <div class="text-nowrap"><wc-input-counter target="#chat-input"></wc-input-counter></div>
                  <button type="button" class="btn-input-chat"><wc-icon icon="send" size="md"></wc-icon></button>
                  <button type="button" class="d-none btn-stop-response" title="${i18nlocales.t("chat.stopTitle")}">
                    <wc-icon icon="stop" size="md"></wc-icon>
                  </button>
                </div>
              </div>
            </div>
            <div class="extra-small d-flex justify-content-center">
              <div class="text-muted">${i18nlocales.t("chat.aiWarning")}<a class="extra-small ms-2 btn-chat-privacy"
                  href="https://central.zeev.it/items/privacidade-para-recursos-de-inteligencia-artificial" target="_blank">${i18nlocales.t("chat.privacy")}</a>
              </div>
            </div>
          </div>
          <div class="chat-overlay d-none">
            <div class="container-alert"></div>
          </div>
        </div>
        `;
    }

    /*
     * Configura detalhes do UI conforme configuração
     */
    _configureUi() {
      this.configuration.enableInput ? this.chatFooterElement.classList.remove("d-none") : this.chatFooterElement.classList.add("d-none");
      this._setExamples();
      this._setContexts();
      const chatInput = this.chatInputElement;
      this._chatInputInitialHeight = chatInput.clientHeight;
      chatInput.setAttribute("placeholder", this.configuration.helpText || i18nlocales.t("chat.inputPlaceholder"));
      if (this.configuration.enableFile) {
        this.chatBtnSelectFileElement.style.display = "inline-block";
      }
      this._setDisabledControllers();
    }
    _setDisabledControllers() {
      const isDisabled = this.disabled;
      if (isDisabled) {
        this.chatInputElement?.setAttribute("disabled", "");
        this.chatBtnSendMessageElement?.setAttribute("disabled", "");
        this.chatBtnReset?.setAttribute("disabled", "");
      } else {
        this.chatInputElement?.removeAttribute("disabled");
        this.chatBtnSendMessageElement?.removeAttribute("disabled");
        this.chatBtnReset?.removeAttribute("disabled");
      }
    }

    /**
     * Registra eventos.
     */
    async _bindEvents() {
      // Selecionar exemplo
      delegateEvent(this.chatExamplesElement, "button", "click", event => {
        this._setInputValue(event.target.textContent);
        this._focusInput();
      });

      // Mostrar/esconder exemplos
      delegateEvent(this.chatExamplesColapseHandlerElement, "div", "click", () => {
        this.chatExamplesElement.classList.toggle("d-none");
        this.chatExamplesColapseHandlerElement.classList.toggle("collapsed");
      });

      // Quando usuário gosta de uma resposta
      delegateEvent(this.chatBodyElement, "wc-chat-interaction", "like", async event => this._like(event.detail.messageId));

      // Quando usuário não gosta de uma resposta
      delegateEvent(this.chatBodyElement, "wc-chat-interaction", "dislike", async event => this._dislike(event.detail.messageId));
      const chatInput = this.chatInputElement;

      // Enviar mensagem com Enter
      chatInput.addEventListener("keydown", async e => {
        if (e.key === "Enter" && !e.shiftKey && e.target.clientHeight <= this._chatInputInitialHeight) {
          e.preventDefault();
          await this._addUserMessage(chatInput.value);
        }
      });

      // Ajustar altura do input
      chatInput.addEventListener("input", ({
        target
      }) => {
        target.style.height = "auto";
        target.style.height = target.scrollHeight + "px";

        // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
        const diff = Math.abs(target.scrollHeight - target.clientHeight - target.scrollTop);
        if (diff <= 21) target.scrollTop = target.scrollHeight;
      });
      chatInput.addEventListener("keydown", e => {
        if (e.key === "Escape") {
          e.preventDefault();
          this._setInputValue("");
        }
      });

      // Enviar mensagem com clique
      this.chatBtnSendMessageElement.addEventListener("click", async () => {
        await this._addUserMessage(chatInput.value);
      });

      // Copiar resposta do assistente para o input
      delegateEvent(this.chatBodyElement, "wc-chat-interaction", "copy-to-input", async ({
        detail
      }) => this._setInputValue(detail));

      // Quando usuário para o processamneto
      this.chatBtnStop.addEventListener("click", () => this._abort());

      // Quando usuário envia um feedback
      this.chatBtnFeedbackElement.addEventListener("click", async () => this._sendFeedback());

      // Quando usuário cancela o envio de feedback
      this.chatBtnCancelFeedbackElement.addEventListener("click", () => this._hideFeedback());

      // Reiniciar chat
      delegateEvent(this, ".btn-chat-reset", "click", async () => await this.reset());

      // Quando a configuração do chat deve mudar
      document.addEventListener("changeZeevConfig", this._boundOnChangeZeevConfig);

      // Quando um contexto deve ser atualizado dinamicamente
      document.addEventListener("chatContextUpdate", this._boundOnChatContextUpdate);

      // Fechar mensagem de alerta
      this.addEventListener("close-chat-alert", () => this.chatOverlay.classList.add("d-none"));

      // Abrir caixa de seleção de arquivo
      this.chatBtnSelectFileElement?.addEventListener("click", () => this.chatSelectFileElement.click());

      // Adicionar arquivo
      this.chatSelectFileElement?.addEventListener("change", e => this._addFile(e.target.files[0]).then(() => e.target.value = null));
      this.addEventListener("delete-file", e => this._deleteFile(e.detail.fileId));
      delegateEvent(this.chatBodyElement, "wc-chat-interaction", "action:selected", e => this._handleSelectedAction(e.detail));
    }
    async _onChangeZeevConfig(e) {
      await this._reconfigure(e.detail);
    }
    _onChatContextUpdate(e) {
      const {
        contextId,
        label,
        description,
        source,
        visibility
      } = e.detail;
      const contextConfig = this._registeredContexts.get(contextId);
      if (!contextConfig) return;
      contextConfig.element.visibility = visibility;
      contextConfig.element.label = label || "";
      contextConfig.element.description = description || "";
      if (source !== undefined) contextConfig.contextSource = source;
    }
    async _addFile(file) {
      if (!file) return;
      const reader = new FileReader();
      reader.addEventListener("load", async () => {
        const content = reader.result.split(";base64,", 2)[1];
        const {
          fileId
        } = await this.configuration.addFile(file.name, content);
        const fileElement = document.createElement("wc-chat-file");
        fileElement.fileId = fileId;
        this.chatFilesElement.insertAdjacentElement("beforeend", fileElement);
        this._afterAddFile(fileId);
      }, false);
      reader.readAsDataURL(file);
    }
    _afterAddFile(fileId) {
      const result = this.configuration.afterAddFile(fileId);
      if ("message" in result) this._setInputValue(fileId);
    }
    async _deleteFile(fileId) {
      await this.configuration.deleteFile(fileId);
      this._afterDeleteFile(fileId);
    }
    _afterDeleteFile(fileId) {
      const result = this.configuration.afterDeleteFile(fileId);
      if ("message" in result) this._setInputValue(result.message);
    }
    _handleSelectedAction(action) {
      if ("set:message" in action) {
        this._setInputValue(action["set:message"]);
        this.chatBtnSendMessageElement.click();
      }
      if ("select:file" in action) {
        this.chatSelectFileElement.click();
      }
    }

    /*
     * Reconfigura o chat após mudança de configuração
     */
    async _reconfigure(newConfigUid) {
      this.config = newConfigUid;
      this.configuration = await this._createConfig();
      this._configureUi();
      await this._showWelcomeMessage();
      await this._showAlert();
    }
    _setExamples() {
      const element = this.chatExamplesElement;
      element.innerHTML = "";
      this.examples.forEach(example => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "btn btn-secondary btn-sm rounded-5";
        button.textContent = example.message || example;
        if (example.context) {
          button.addEventListener("click", () => {
            const context = this._registeredContexts.get(example.context);
            if (context) context.element.checked = true;
          });
        }
        element.insertAdjacentElement("afterbegin", button);
      });
      if (element.innerHTML) {
        element.classList.remove("d-none");
        this.chatExamplesColapseHandlerElement.classList.remove("d-none", "collapsed");
      } else {
        element.classList.add("d-none");
        this.chatExamplesColapseHandlerElement.classList.add("d-none");
      }
    }
    _setContexts() {
      if (!this.configuration.contexts?.length) return;
      this.chatContextsElement.classList.remove("d-none");
      this.configuration.contexts.forEach(context => {
        if (this._registeredContexts.has(context.contextId)) return;
        const contextElement = document.createElement("wc-chat-context");
        contextElement.contextId = context.contextId;
        contextElement.label = context.label;
        contextElement.description = context.description;
        contextElement.checked = context.checked;
        this._registeredContexts.set(context.contextId, {
          element: contextElement,
          contextSource: context.source
        });
        this.chatContextsElement.insertAdjacentElement("beforeend", contextElement);
      });
    }

    /**
     * Mostra a mensagem fixa de boas vindas.
     */
    async _showWelcomeMessage() {
      const welcomeMessage = await this.configuration.getWelcomeMessage();
      if (!welcomeMessage) return;
      const message = welcomeMessage.message ?? welcomeMessage;
      await this._addGptFakeResponse(message, welcomeMessage.info);
      this._addChatInfo(welcomeMessage.chatInfo);
    }
    async _showAlert() {
      const alertMessage = await this.configuration.getAlertMessage();
      if (!alertMessage || !alertMessage.main) return;
      const alert = document.createElement("wc-chat-alert");
      alert.mainText = alertMessage.main;
      alert.secondaryText = alertMessage.secondary;
      this._blurInput();
      this.chatOverlay.classList.remove("d-none");
      this.chatOverlay.firstElementChild.appendChild(alert);
    }

    /**
     * Adiciona uma interação no chat.
     * @returns {HTMLElement}
     */
    _addChatInteraction(role, showControls = true) {
      const interaction = document.createElement("wc-chat-interaction");
      interaction.role = role.uid;
      interaction.id = generateRandomId();
      if (role.photo) interaction.photo = role.photo;
      if (role.name) interaction.name = role.name;
      if (role.icon) interaction.icon = role.icon;
      if (role.color) interaction.color = role.color;
      if (role.animation) interaction.animation = role.animation;
      interaction.showControls = showControls;
      interaction.controls = this.configuration.controls;
      this.chatBodyElement.appendChild(interaction);
      this._scrollToBottom();
      return interaction;
    }

    /**
     * Adiciona um texto informativo no chat.
     * @param {String} text
     * @returns
     */
    _addChatInfo(text) {
      if (!text) return;
      const info = document.createElement("wc-chat-info");
      info.text = replaceNewLine(text);
      this.chatBodyElement.appendChild(info);
    }

    /*
     * Registra mensagem do usuário
     */
    async _addUserMessage(message) {
      if (!message) return;
      const userInteraction = this._addChatInteraction(this.configuration.roles.user, false);
      userInteraction.setMessage(message, {
        format: ["nl"]
      });
      this.chatFilesElement.querySelectorAll("wc-chat-file").forEach(el => el.remove());
      this._setInputValue("");
      this._focusInput();
      this._scrollToBottom();
      this._toggleStopButton(true);
      this._hideExamples();
      this._disablePreviousActions();
      const {
        chatInfo
      } = (await this.configuration.afterUserMessage({
        envelopSize: this._envelops.length
      })) || {};
      this._addChatInfo(chatInfo);
      const gptInteraction = this._addChatInteraction(this.configuration.roles.assistant);
      const handledMessage = await this._handleUserMessage(message);
      this._addEnvelopToSend(new baseEnvelop(this.configuration.roles.user.uid, handledMessage.message), true);
      await this._sendMessagesToGpt(gptInteraction);
    }

    /*
     * Envia as mensagens para o assistente e retorna a resposta
     * @param {boolean} persistMessage - se a mensagem deve persistir no histórico para ser reenviada nas próximas interações. Importante para economizar tokens.
     */
    async _sendMessagesToGpt(interaction, persistMessage = true) {
      this._startInactivityTimerToReset(this.configuration.inactivityMinutes);
      this._startTypingTitle();
      this._toggleStopButton(true);
      const messagesToSend = await this._getMessagesToSendToGpt();
      await this.configuration.provider.sendMessageToGpt(messagesToSend, (messageId, message) => this._gptStreamResponseCallback(interaction, messageId, message, persistMessage), async (messageId, message, tokens, urls) => this._gptBatchResponseCallback(interaction, messageId, message, tokens, urls, persistMessage), e => this._addGptErrorMessage(interaction, e), this.configuration.skipSendToGpt);
      this._toggleStopButton(false);
      this._scrollToBottom();
      this._focusInput();
      this._stopTypingTitle();
    }
    _addGptErrorMessage(interaction, e) {
      if (e.name === "AbortError") {
        interaction.hideTyping();
        return;
      }
      if (e.name === "AuthError") {
        interaction.hideTyping();
        interaction.setErrorMessage(e.message);
        return;
      }
      if (e.name === "ValidationError") {
        interaction.hideTyping();
        interaction.setErrorMessage(e.message);
        this.envelops.pop();
        return;
      }
      if (e.name === "RateLimitError") {
        interaction.hideTyping();
        interaction.setInfoMessage(e.message);
        return;
      }
      if (this.debug) {
        interaction.setErrorMessage(e.message);
        return;
      }
      console.error(e);
      interaction.setErrorMessage("Ops... Desculpe, ocorreu um erro nesse momento. Por favor, tente de novo mais tarde.");
    }
    _gptStreamResponseCallback(interaction, messageId, message) {
      interaction.addStream(message);
      this._scrollToBottom();
    }
    async _gptBatchResponseCallback(interaction, messageId, message, tokens, urls, persistMessage) {
      const handledMessage = await this._handleGptMessage(message);
      this._addEnvelopToSend(new baseEnvelop(this.configuration.roles.assistant.uid, handledMessage.message), persistMessage);

      //verifica se o GPT ja retornou as URLs, ou elas vieram de lógica de negócio do handler
      const urlsToShow = handledMessage.urls?.length ? handledMessage.urls : urls;
      interaction.setMessage(handledMessage.message, {
        messageId: handledMessage.messageId ? handledMessage.messageId : messageId,
        previewMessage: handledMessage.previewMessage
      });
      interaction.setActions(handledMessage.actions);
      interaction.setUrls(urlsToShow);
      interaction.setInfo(handledMessage.info);
      interaction.setEnableCopyToInput(handledMessage.copyToInput);
    }
    async _addGptFakeResponse(message, info) {
      this._toggleStopButton(true);
      const interaction = this._addChatInteraction(this.configuration.roles.assistant, false);
      await delay(1000);
      const handledMessage = await this._handleGptMessage(message, false);
      interaction.setMessage(handledMessage.message, {
        format: ["markdown", "nl"]
      });
      interaction.setActions(handledMessage.actions);
      interaction.setInfo(info);
      this._toggleStopButton(false);
      this._scrollToBottom();
      this._focusInput();
    }

    /*
     * Monta o array de mensagens que serão enviadas ao OpenAI
     */
    async _getMessagesToSendToGpt() {
      const messagesToSend = [];
      const fixedMessages = this.configuration.getFixedMessages();
      if (fixedMessages) messagesToSend.push(...fixedMessages.map(x => new baseEnvelop(x.role, x.content).getMessage()));
      const lastMessagesToShow = this.envelops.length > this.history ? -this.history : 0;
      messagesToSend.push(...this.envelops.slice(lastMessagesToShow).filter(x => x.persistant).map(x => x.message).map(message => message.getMessage()));
      const activeContexts = await this._getActiveContextValues();
      if (activeContexts) {
        messagesToSend.push(...activeContexts.map(contextValue => new baseEnvelop(this.configuration.roles.user.uid, contextValue).getMessage()));
      }
      return messagesToSend;
    }
    async _handleUserMessage(message) {
      let userProcessedResponse = {
        message
      };
      for (const rule of this.configuration.getUserMessageHandlers()) {
        if (rule.match(message) && rule.handle) userProcessedResponse = await rule.handle(message);
      }
      return userProcessedResponse;
    }
    async _getActiveContextValues() {
      const contexts = [];
      for (const [contextId, contextConfig] of this._registeredContexts) {
        const {
          checked,
          visibility
        } = contextConfig.element;
        if (!checked || visibility === "hidden") continue;
        try {
          const sourceValue = await contextConfig.contextSource();
          if (sourceValue) contexts.push(sourceValue);
        } catch (error) {
          console.error(`Erro ao coletar contexto ${contextId}:`, error);
        }
      }
      return contexts;
    }
    async _handleGptMessage(message, isReal = true) {
      let handledMessage = {
        message
      };
      for (const rule of this.configuration.getGptMessageHandlers()) if (rule.match(message, isReal) && rule.handle) handledMessage = rule.handle(message);
      return handledMessage;
    }
    _startInactivityTimerToReset(minutes) {
      if (!minutes) return;
      const milliseconds = minutes * 60 * 1000;
      clearTimeout(this._inactivityTimer);
      this._inactivityTimer = setTimeout(async () => {
        await this.reset();
        alert(i18nlocales.t("chat.inactivityInfo").replace("{0}", minutes));
      }, milliseconds);
    }
    async _like(messageId) {
      if (!messageId) return;
      this.currentControlsMessageId = messageId;
      this._showFeedback();
      await this.configuration.provider.like(messageId);
    }
    async _dislike(messageId) {
      this.currentControlsMessageId = messageId;
      this._showFeedback();
      await this.configuration.provider.dislike(messageId);
    }
    async _sendFeedback() {
      if (!this.currentControlsMessageId) return;
      if (!this.chatInputFeedbackElement.value) return;
      await this.configuration.provider.sendFeedback(this.currentControlsMessageId, this.chatInputFeedbackElement.value);
      this.chatInputFeedbackElement.value = "";
      this.currentControlsMessageId = "";
      this._hideFeedback();
    }
    _showFeedback() {
      this.chatFeedbackElement.classList.remove("d-none");
      this.chatInputFeedbackElement.focus();
      this.chatMainElement.classList.add("d-none");
    }
    _hideFeedback() {
      this.chatFeedbackElement.classList.add("d-none");
      this.chatMainElement.classList.remove("d-none");
    }
    _abort() {
      this.configuration.provider.abort();
      this._toggleStopButton();
    }
    _toggleStopButton(value) {
      if (value) {
        this._showStopButton();
        this._hideSendButton();
        return;
      }
      this._showSendButton();
      this._hideStopButton();
    }
    _showStopButton() {
      this.chatBtnStop.classList.remove("d-none");
    }
    _hideStopButton() {
      this.chatBtnStop.classList.add("d-none");
    }
    _showSendButton() {
      this.chatBtnSendMessageElement.classList.remove("d-none");
    }
    _hideSendButton() {
      this.chatBtnSendMessageElement.classList.add("d-none");
    }
    _hideExamples() {
      this.chatExamplesElement.classList.add("d-none");
      this.chatExamplesColapseHandlerElement.classList.add("collapsed");
    }
    _disablePreviousActions() {
      const interactions = this.chatBodyElement.querySelectorAll("wc-chat-interaction[role=assistant]");
      interactions[interactions.length - 1]?.disableActions();
    }
    _scrollToBottom() {
      this.chatBodyElement.scrollTop = this.chatBodyElement.scrollHeight;
    }
    _focusInput() {
      if (!this.chatInputElement) return;
      this.chatInputElement.focus();
    }
    _blurInput() {
      if (!this.chatInputElement) return;
      this.chatInputElement.blur();
    }
    _setInputValue(value) {
      const chatInput = this.chatInputElement;
      chatInput.value = value;
      chatInput.dispatchEvent(new Event("change"));
      chatInput.dispatchEvent(new Event("input"));
    }
    _addEnvelopToSend(message, persistant = false) {
      if (!message) return;
      this.envelops.push({
        message,
        persistant
      });
    }
    _startTypingTitle() {
      let dots = ".";
      function updateTitle() {
        document.title = dots;
        dots = dots.length === 4 ? "." : dots + ".";
      }
      this._animationInterval = setInterval(updateTitle, 500);
    }
    _stopTypingTitle() {
      clearInterval(this._animationInterval);
      document.title = this._originalDocumentTitle;
    }
    //#endregion
  }
  customElements.define("wc-chat", chatElement);

  /**
   * marked v9.1.6 - a markdown parser
   * Copyright (c) 2011-2023, Christopher Jeffrey. (MIT Licensed)
   * https://github.com/markedjs/marked
   */

  /**
   * DO NOT EDIT THIS FILE
   * The code in this file is generated from files in ./src/
   */

  /**
   * Gets the original marked default options.
   */
  function _getDefaults() {
    return {
      async: false,
      breaks: false,
      extensions: null,
      gfm: true,
      hooks: null,
      pedantic: false,
      renderer: null,
      silent: false,
      tokenizer: null,
      walkTokens: null
    };
  }
  let _defaults = _getDefaults();
  function changeDefaults(newDefaults) {
    _defaults = newDefaults;
  }

  /**
   * Helpers
   */
  const escapeTest = /[&<>"']/;
  const escapeReplace = new RegExp(escapeTest.source, 'g');
  const escapeTestNoEncode = /[<>"']|&(?!(#\d{1,7}|#[Xx][a-fA-F0-9]{1,6}|\w+);)/;
  const escapeReplaceNoEncode = new RegExp(escapeTestNoEncode.source, 'g');
  const escapeReplacements = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  };
  const getEscapeReplacement = ch => escapeReplacements[ch];
  function escape(html, encode) {
    if (encode) {
      if (escapeTest.test(html)) {
        return html.replace(escapeReplace, getEscapeReplacement);
      }
    } else {
      if (escapeTestNoEncode.test(html)) {
        return html.replace(escapeReplaceNoEncode, getEscapeReplacement);
      }
    }
    return html;
  }
  const unescapeTest = /&(#(?:\d+)|(?:#x[0-9A-Fa-f]+)|(?:\w+));?/ig;
  function unescape(html) {
    // explicitly match decimal, hex, and named HTML entities
    return html.replace(unescapeTest, (_, n) => {
      n = n.toLowerCase();
      if (n === 'colon') return ':';
      if (n.charAt(0) === '#') {
        return n.charAt(1) === 'x' ? String.fromCharCode(parseInt(n.substring(2), 16)) : String.fromCharCode(+n.substring(1));
      }
      return '';
    });
  }
  const caret = /(^|[^\[])\^/g;
  function edit(regex, opt) {
    regex = typeof regex === 'string' ? regex : regex.source;
    opt = opt || '';
    const obj = {
      replace: (name, val) => {
        val = typeof val === 'object' && 'source' in val ? val.source : val;
        val = val.replace(caret, '$1');
        regex = regex.replace(name, val);
        return obj;
      },
      getRegex: () => {
        return new RegExp(regex, opt);
      }
    };
    return obj;
  }
  function cleanUrl(href) {
    try {
      href = encodeURI(href).replace(/%25/g, '%');
    } catch (e) {
      return null;
    }
    return href;
  }
  const noopTest = {
    exec: () => null
  };
  function splitCells(tableRow, count) {
    // ensure that every cell-delimiting pipe has a space
    // before it to distinguish it from an escaped pipe
    const row = tableRow.replace(/\|/g, (match, offset, str) => {
        let escaped = false;
        let curr = offset;
        while (--curr >= 0 && str[curr] === '\\') escaped = !escaped;
        if (escaped) {
          // odd number of slashes means | is escaped
          // so we leave it alone
          return '|';
        } else {
          // add space before unescaped |
          return ' |';
        }
      }),
      cells = row.split(/ \|/);
    let i = 0;
    // First/last cell in a row cannot be empty if it has no leading/trailing pipe
    if (!cells[0].trim()) {
      cells.shift();
    }
    if (cells.length > 0 && !cells[cells.length - 1].trim()) {
      cells.pop();
    }
    if (count) {
      if (cells.length > count) {
        cells.splice(count);
      } else {
        while (cells.length < count) cells.push('');
      }
    }
    for (; i < cells.length; i++) {
      // leading or trailing whitespace is ignored per the gfm spec
      cells[i] = cells[i].trim().replace(/\\\|/g, '|');
    }
    return cells;
  }
  /**
   * Remove trailing 'c's. Equivalent to str.replace(/c*$/, '').
   * /c*$/ is vulnerable to REDOS.
   *
   * @param str
   * @param c
   * @param invert Remove suffix of non-c chars instead. Default falsey.
   */
  function rtrim(str, c, invert) {
    const l = str.length;
    if (l === 0) {
      return '';
    }
    // Length of suffix matching the invert condition.
    let suffLen = 0;
    // Step left until we fail to match the invert condition.
    while (suffLen < l) {
      const currChar = str.charAt(l - suffLen - 1);
      if (currChar === c && !invert) {
        suffLen++;
      } else if (currChar !== c && invert) {
        suffLen++;
      } else {
        break;
      }
    }
    return str.slice(0, l - suffLen);
  }
  function findClosingBracket(str, b) {
    if (str.indexOf(b[1]) === -1) {
      return -1;
    }
    let level = 0;
    for (let i = 0; i < str.length; i++) {
      if (str[i] === '\\') {
        i++;
      } else if (str[i] === b[0]) {
        level++;
      } else if (str[i] === b[1]) {
        level--;
        if (level < 0) {
          return i;
        }
      }
    }
    return -1;
  }
  function outputLink(cap, link, raw, lexer) {
    const href = link.href;
    const title = link.title ? escape(link.title) : null;
    const text = cap[1].replace(/\\([\[\]])/g, '$1');
    if (cap[0].charAt(0) !== '!') {
      lexer.state.inLink = true;
      const token = {
        type: 'link',
        raw,
        href,
        title,
        text,
        tokens: lexer.inlineTokens(text)
      };
      lexer.state.inLink = false;
      return token;
    }
    return {
      type: 'image',
      raw,
      href,
      title,
      text: escape(text)
    };
  }
  function indentCodeCompensation(raw, text) {
    const matchIndentToCode = raw.match(/^(\s+)(?:```)/);
    if (matchIndentToCode === null) {
      return text;
    }
    const indentToCode = matchIndentToCode[1];
    return text.split('\n').map(node => {
      const matchIndentInNode = node.match(/^\s+/);
      if (matchIndentInNode === null) {
        return node;
      }
      const [indentInNode] = matchIndentInNode;
      if (indentInNode.length >= indentToCode.length) {
        return node.slice(indentToCode.length);
      }
      return node;
    }).join('\n');
  }
  /**
   * Tokenizer
   */
  class _Tokenizer {
    options;
    // TODO: Fix this rules type
    rules;
    lexer;
    constructor(options) {
      this.options = options || _defaults;
    }
    space(src) {
      const cap = this.rules.block.newline.exec(src);
      if (cap && cap[0].length > 0) {
        return {
          type: 'space',
          raw: cap[0]
        };
      }
    }
    code(src) {
      const cap = this.rules.block.code.exec(src);
      if (cap) {
        const text = cap[0].replace(/^ {1,4}/gm, '');
        return {
          type: 'code',
          raw: cap[0],
          codeBlockStyle: 'indented',
          text: !this.options.pedantic ? rtrim(text, '\n') : text
        };
      }
    }
    fences(src) {
      const cap = this.rules.block.fences.exec(src);
      if (cap) {
        const raw = cap[0];
        const text = indentCodeCompensation(raw, cap[3] || '');
        return {
          type: 'code',
          raw,
          lang: cap[2] ? cap[2].trim().replace(this.rules.inline._escapes, '$1') : cap[2],
          text
        };
      }
    }
    heading(src) {
      const cap = this.rules.block.heading.exec(src);
      if (cap) {
        let text = cap[2].trim();
        // remove trailing #s
        if (/#$/.test(text)) {
          const trimmed = rtrim(text, '#');
          if (this.options.pedantic) {
            text = trimmed.trim();
          } else if (!trimmed || / $/.test(trimmed)) {
            // CommonMark requires space before trailing #s
            text = trimmed.trim();
          }
        }
        return {
          type: 'heading',
          raw: cap[0],
          depth: cap[1].length,
          text,
          tokens: this.lexer.inline(text)
        };
      }
    }
    hr(src) {
      const cap = this.rules.block.hr.exec(src);
      if (cap) {
        return {
          type: 'hr',
          raw: cap[0]
        };
      }
    }
    blockquote(src) {
      const cap = this.rules.block.blockquote.exec(src);
      if (cap) {
        const text = rtrim(cap[0].replace(/^ *>[ \t]?/gm, ''), '\n');
        const top = this.lexer.state.top;
        this.lexer.state.top = true;
        const tokens = this.lexer.blockTokens(text);
        this.lexer.state.top = top;
        return {
          type: 'blockquote',
          raw: cap[0],
          tokens,
          text
        };
      }
    }
    list(src) {
      let cap = this.rules.block.list.exec(src);
      if (cap) {
        let bull = cap[1].trim();
        const isordered = bull.length > 1;
        const list = {
          type: 'list',
          raw: '',
          ordered: isordered,
          start: isordered ? +bull.slice(0, -1) : '',
          loose: false,
          items: []
        };
        bull = isordered ? `\\d{1,9}\\${bull.slice(-1)}` : `\\${bull}`;
        if (this.options.pedantic) {
          bull = isordered ? bull : '[*+-]';
        }
        // Get next list item
        const itemRegex = new RegExp(`^( {0,3}${bull})((?:[\t ][^\\n]*)?(?:\\n|$))`);
        let raw = '';
        let itemContents = '';
        let endsWithBlankLine = false;
        // Check if current bullet point can start a new List Item
        while (src) {
          let endEarly = false;
          if (!(cap = itemRegex.exec(src))) {
            break;
          }
          if (this.rules.block.hr.test(src)) {
            // End list if bullet was actually HR (possibly move into itemRegex?)
            break;
          }
          raw = cap[0];
          src = src.substring(raw.length);
          let line = cap[2].split('\n', 1)[0].replace(/^\t+/, t => ' '.repeat(3 * t.length));
          let nextLine = src.split('\n', 1)[0];
          let indent = 0;
          if (this.options.pedantic) {
            indent = 2;
            itemContents = line.trimStart();
          } else {
            indent = cap[2].search(/[^ ]/); // Find first non-space char
            indent = indent > 4 ? 1 : indent; // Treat indented code blocks (> 4 spaces) as having only 1 indent
            itemContents = line.slice(indent);
            indent += cap[1].length;
          }
          let blankLine = false;
          if (!line && /^ *$/.test(nextLine)) {
            // Items begin with at most one blank line
            raw += nextLine + '\n';
            src = src.substring(nextLine.length + 1);
            endEarly = true;
          }
          if (!endEarly) {
            const nextBulletRegex = new RegExp(`^ {0,${Math.min(3, indent - 1)}}(?:[*+-]|\\d{1,9}[.)])((?:[ \t][^\\n]*)?(?:\\n|$))`);
            const hrRegex = new RegExp(`^ {0,${Math.min(3, indent - 1)}}((?:- *){3,}|(?:_ *){3,}|(?:\\* *){3,})(?:\\n+|$)`);
            const fencesBeginRegex = new RegExp(`^ {0,${Math.min(3, indent - 1)}}(?:\`\`\`|~~~)`);
            const headingBeginRegex = new RegExp(`^ {0,${Math.min(3, indent - 1)}}#`);
            // Check if following lines should be included in List Item
            while (src) {
              const rawLine = src.split('\n', 1)[0];
              nextLine = rawLine;
              // Re-align to follow commonmark nesting rules
              if (this.options.pedantic) {
                nextLine = nextLine.replace(/^ {1,4}(?=( {4})*[^ ])/g, '  ');
              }
              // End list item if found code fences
              if (fencesBeginRegex.test(nextLine)) {
                break;
              }
              // End list item if found start of new heading
              if (headingBeginRegex.test(nextLine)) {
                break;
              }
              // End list item if found start of new bullet
              if (nextBulletRegex.test(nextLine)) {
                break;
              }
              // Horizontal rule found
              if (hrRegex.test(src)) {
                break;
              }
              if (nextLine.search(/[^ ]/) >= indent || !nextLine.trim()) {
                // Dedent if possible
                itemContents += '\n' + nextLine.slice(indent);
              } else {
                // not enough indentation
                if (blankLine) {
                  break;
                }
                // paragraph continuation unless last line was a different block level element
                if (line.search(/[^ ]/) >= 4) {
                  // indented code block
                  break;
                }
                if (fencesBeginRegex.test(line)) {
                  break;
                }
                if (headingBeginRegex.test(line)) {
                  break;
                }
                if (hrRegex.test(line)) {
                  break;
                }
                itemContents += '\n' + nextLine;
              }
              if (!blankLine && !nextLine.trim()) {
                // Check if current line is blank
                blankLine = true;
              }
              raw += rawLine + '\n';
              src = src.substring(rawLine.length + 1);
              line = nextLine.slice(indent);
            }
          }
          if (!list.loose) {
            // If the previous item ended with a blank line, the list is loose
            if (endsWithBlankLine) {
              list.loose = true;
            } else if (/\n *\n *$/.test(raw)) {
              endsWithBlankLine = true;
            }
          }
          let istask = null;
          let ischecked;
          // Check for task list items
          if (this.options.gfm) {
            istask = /^\[[ xX]\] /.exec(itemContents);
            if (istask) {
              ischecked = istask[0] !== '[ ] ';
              itemContents = itemContents.replace(/^\[[ xX]\] +/, '');
            }
          }
          list.items.push({
            type: 'list_item',
            raw,
            task: !!istask,
            checked: ischecked,
            loose: false,
            text: itemContents,
            tokens: []
          });
          list.raw += raw;
        }
        // Do not consume newlines at end of final item. Alternatively, make itemRegex *start* with any newlines to simplify/speed up endsWithBlankLine logic
        list.items[list.items.length - 1].raw = raw.trimEnd();
        list.items[list.items.length - 1].text = itemContents.trimEnd();
        list.raw = list.raw.trimEnd();
        // Item child tokens handled here at end because we needed to have the final item to trim it first
        for (let i = 0; i < list.items.length; i++) {
          this.lexer.state.top = false;
          list.items[i].tokens = this.lexer.blockTokens(list.items[i].text, []);
          if (!list.loose) {
            // Check if list should be loose
            const spacers = list.items[i].tokens.filter(t => t.type === 'space');
            const hasMultipleLineBreaks = spacers.length > 0 && spacers.some(t => /\n.*\n/.test(t.raw));
            list.loose = hasMultipleLineBreaks;
          }
        }
        // Set all items to loose if list is loose
        if (list.loose) {
          for (let i = 0; i < list.items.length; i++) {
            list.items[i].loose = true;
          }
        }
        return list;
      }
    }
    html(src) {
      const cap = this.rules.block.html.exec(src);
      if (cap) {
        const token = {
          type: 'html',
          block: true,
          raw: cap[0],
          pre: cap[1] === 'pre' || cap[1] === 'script' || cap[1] === 'style',
          text: cap[0]
        };
        return token;
      }
    }
    def(src) {
      const cap = this.rules.block.def.exec(src);
      if (cap) {
        const tag = cap[1].toLowerCase().replace(/\s+/g, ' ');
        const href = cap[2] ? cap[2].replace(/^<(.*)>$/, '$1').replace(this.rules.inline._escapes, '$1') : '';
        const title = cap[3] ? cap[3].substring(1, cap[3].length - 1).replace(this.rules.inline._escapes, '$1') : cap[3];
        return {
          type: 'def',
          tag,
          raw: cap[0],
          href,
          title
        };
      }
    }
    table(src) {
      const cap = this.rules.block.table.exec(src);
      if (cap) {
        if (!/[:|]/.test(cap[2])) {
          // delimiter row must have a pipe (|) or colon (:) otherwise it is a setext heading
          return;
        }
        const item = {
          type: 'table',
          raw: cap[0],
          header: splitCells(cap[1]).map(c => {
            return {
              text: c,
              tokens: []
            };
          }),
          align: cap[2].replace(/^\||\| *$/g, '').split('|'),
          rows: cap[3] && cap[3].trim() ? cap[3].replace(/\n[ \t]*$/, '').split('\n') : []
        };
        if (item.header.length === item.align.length) {
          let l = item.align.length;
          let i, j, k, row;
          for (i = 0; i < l; i++) {
            const align = item.align[i];
            if (align) {
              if (/^ *-+: *$/.test(align)) {
                item.align[i] = 'right';
              } else if (/^ *:-+: *$/.test(align)) {
                item.align[i] = 'center';
              } else if (/^ *:-+ *$/.test(align)) {
                item.align[i] = 'left';
              } else {
                item.align[i] = null;
              }
            }
          }
          l = item.rows.length;
          for (i = 0; i < l; i++) {
            item.rows[i] = splitCells(item.rows[i], item.header.length).map(c => {
              return {
                text: c,
                tokens: []
              };
            });
          }
          // parse child tokens inside headers and cells
          // header child tokens
          l = item.header.length;
          for (j = 0; j < l; j++) {
            item.header[j].tokens = this.lexer.inline(item.header[j].text);
          }
          // cell child tokens
          l = item.rows.length;
          for (j = 0; j < l; j++) {
            row = item.rows[j];
            for (k = 0; k < row.length; k++) {
              row[k].tokens = this.lexer.inline(row[k].text);
            }
          }
          return item;
        }
      }
    }
    lheading(src) {
      const cap = this.rules.block.lheading.exec(src);
      if (cap) {
        return {
          type: 'heading',
          raw: cap[0],
          depth: cap[2].charAt(0) === '=' ? 1 : 2,
          text: cap[1],
          tokens: this.lexer.inline(cap[1])
        };
      }
    }
    paragraph(src) {
      const cap = this.rules.block.paragraph.exec(src);
      if (cap) {
        const text = cap[1].charAt(cap[1].length - 1) === '\n' ? cap[1].slice(0, -1) : cap[1];
        return {
          type: 'paragraph',
          raw: cap[0],
          text,
          tokens: this.lexer.inline(text)
        };
      }
    }
    text(src) {
      const cap = this.rules.block.text.exec(src);
      if (cap) {
        return {
          type: 'text',
          raw: cap[0],
          text: cap[0],
          tokens: this.lexer.inline(cap[0])
        };
      }
    }
    escape(src) {
      const cap = this.rules.inline.escape.exec(src);
      if (cap) {
        return {
          type: 'escape',
          raw: cap[0],
          text: escape(cap[1])
        };
      }
    }
    tag(src) {
      const cap = this.rules.inline.tag.exec(src);
      if (cap) {
        if (!this.lexer.state.inLink && /^<a /i.test(cap[0])) {
          this.lexer.state.inLink = true;
        } else if (this.lexer.state.inLink && /^<\/a>/i.test(cap[0])) {
          this.lexer.state.inLink = false;
        }
        if (!this.lexer.state.inRawBlock && /^<(pre|code|kbd|script)(\s|>)/i.test(cap[0])) {
          this.lexer.state.inRawBlock = true;
        } else if (this.lexer.state.inRawBlock && /^<\/(pre|code|kbd|script)(\s|>)/i.test(cap[0])) {
          this.lexer.state.inRawBlock = false;
        }
        return {
          type: 'html',
          raw: cap[0],
          inLink: this.lexer.state.inLink,
          inRawBlock: this.lexer.state.inRawBlock,
          block: false,
          text: cap[0]
        };
      }
    }
    link(src) {
      const cap = this.rules.inline.link.exec(src);
      if (cap) {
        const trimmedUrl = cap[2].trim();
        if (!this.options.pedantic && /^</.test(trimmedUrl)) {
          // commonmark requires matching angle brackets
          if (!/>$/.test(trimmedUrl)) {
            return;
          }
          // ending angle bracket cannot be escaped
          const rtrimSlash = rtrim(trimmedUrl.slice(0, -1), '\\');
          if ((trimmedUrl.length - rtrimSlash.length) % 2 === 0) {
            return;
          }
        } else {
          // find closing parenthesis
          const lastParenIndex = findClosingBracket(cap[2], '()');
          if (lastParenIndex > -1) {
            const start = cap[0].indexOf('!') === 0 ? 5 : 4;
            const linkLen = start + cap[1].length + lastParenIndex;
            cap[2] = cap[2].substring(0, lastParenIndex);
            cap[0] = cap[0].substring(0, linkLen).trim();
            cap[3] = '';
          }
        }
        let href = cap[2];
        let title = '';
        if (this.options.pedantic) {
          // split pedantic href and title
          const link = /^([^'"]*[^\s])\s+(['"])(.*)\2/.exec(href);
          if (link) {
            href = link[1];
            title = link[3];
          }
        } else {
          title = cap[3] ? cap[3].slice(1, -1) : '';
        }
        href = href.trim();
        if (/^</.test(href)) {
          if (this.options.pedantic && !/>$/.test(trimmedUrl)) {
            // pedantic allows starting angle bracket without ending angle bracket
            href = href.slice(1);
          } else {
            href = href.slice(1, -1);
          }
        }
        return outputLink(cap, {
          href: href ? href.replace(this.rules.inline._escapes, '$1') : href,
          title: title ? title.replace(this.rules.inline._escapes, '$1') : title
        }, cap[0], this.lexer);
      }
    }
    reflink(src, links) {
      let cap;
      if ((cap = this.rules.inline.reflink.exec(src)) || (cap = this.rules.inline.nolink.exec(src))) {
        let link = (cap[2] || cap[1]).replace(/\s+/g, ' ');
        link = links[link.toLowerCase()];
        if (!link) {
          const text = cap[0].charAt(0);
          return {
            type: 'text',
            raw: text,
            text
          };
        }
        return outputLink(cap, link, cap[0], this.lexer);
      }
    }
    emStrong(src, maskedSrc, prevChar = '') {
      let match = this.rules.inline.emStrong.lDelim.exec(src);
      if (!match) return;
      // _ can't be between two alphanumerics. \p{L}\p{N} includes non-english alphabet/numbers as well
      if (match[3] && prevChar.match(/[\p{L}\p{N}]/u)) return;
      const nextChar = match[1] || match[2] || '';
      if (!nextChar || !prevChar || this.rules.inline.punctuation.exec(prevChar)) {
        // unicode Regex counts emoji as 1 char; spread into array for proper count (used multiple times below)
        const lLength = [...match[0]].length - 1;
        let rDelim,
          rLength,
          delimTotal = lLength,
          midDelimTotal = 0;
        const endReg = match[0][0] === '*' ? this.rules.inline.emStrong.rDelimAst : this.rules.inline.emStrong.rDelimUnd;
        endReg.lastIndex = 0;
        // Clip maskedSrc to same section of string as src (move to lexer?)
        maskedSrc = maskedSrc.slice(-1 * src.length + lLength);
        while ((match = endReg.exec(maskedSrc)) != null) {
          rDelim = match[1] || match[2] || match[3] || match[4] || match[5] || match[6];
          if (!rDelim) continue; // skip single * in __abc*abc__
          rLength = [...rDelim].length;
          if (match[3] || match[4]) {
            // found another Left Delim
            delimTotal += rLength;
            continue;
          } else if (match[5] || match[6]) {
            // either Left or Right Delim
            if (lLength % 3 && !((lLength + rLength) % 3)) {
              midDelimTotal += rLength;
              continue; // CommonMark Emphasis Rules 9-10
            }
          }
          delimTotal -= rLength;
          if (delimTotal > 0) continue; // Haven't found enough closing delimiters
          // Remove extra characters. *a*** -> *a*
          rLength = Math.min(rLength, rLength + delimTotal + midDelimTotal);
          // char length can be >1 for unicode characters;
          const lastCharLength = [...match[0]][0].length;
          const raw = src.slice(0, lLength + match.index + lastCharLength + rLength);
          // Create `em` if smallest delimiter has odd char count. *a***
          if (Math.min(lLength, rLength) % 2) {
            const text = raw.slice(1, -1);
            return {
              type: 'em',
              raw,
              text,
              tokens: this.lexer.inlineTokens(text)
            };
          }
          // Create 'strong' if smallest delimiter has even char count. **a***
          const text = raw.slice(2, -2);
          return {
            type: 'strong',
            raw,
            text,
            tokens: this.lexer.inlineTokens(text)
          };
        }
      }
    }
    codespan(src) {
      const cap = this.rules.inline.code.exec(src);
      if (cap) {
        let text = cap[2].replace(/\n/g, ' ');
        const hasNonSpaceChars = /[^ ]/.test(text);
        const hasSpaceCharsOnBothEnds = /^ /.test(text) && / $/.test(text);
        if (hasNonSpaceChars && hasSpaceCharsOnBothEnds) {
          text = text.substring(1, text.length - 1);
        }
        text = escape(text, true);
        return {
          type: 'codespan',
          raw: cap[0],
          text
        };
      }
    }
    br(src) {
      const cap = this.rules.inline.br.exec(src);
      if (cap) {
        return {
          type: 'br',
          raw: cap[0]
        };
      }
    }
    del(src) {
      const cap = this.rules.inline.del.exec(src);
      if (cap) {
        return {
          type: 'del',
          raw: cap[0],
          text: cap[2],
          tokens: this.lexer.inlineTokens(cap[2])
        };
      }
    }
    autolink(src) {
      const cap = this.rules.inline.autolink.exec(src);
      if (cap) {
        let text, href;
        if (cap[2] === '@') {
          text = escape(cap[1]);
          href = 'mailto:' + text;
        } else {
          text = escape(cap[1]);
          href = text;
        }
        return {
          type: 'link',
          raw: cap[0],
          text,
          href,
          tokens: [{
            type: 'text',
            raw: text,
            text
          }]
        };
      }
    }
    url(src) {
      let cap;
      if (cap = this.rules.inline.url.exec(src)) {
        let text, href;
        if (cap[2] === '@') {
          text = escape(cap[0]);
          href = 'mailto:' + text;
        } else {
          // do extended autolink path validation
          let prevCapZero;
          do {
            prevCapZero = cap[0];
            cap[0] = this.rules.inline._backpedal.exec(cap[0])[0];
          } while (prevCapZero !== cap[0]);
          text = escape(cap[0]);
          if (cap[1] === 'www.') {
            href = 'http://' + cap[0];
          } else {
            href = cap[0];
          }
        }
        return {
          type: 'link',
          raw: cap[0],
          text,
          href,
          tokens: [{
            type: 'text',
            raw: text,
            text
          }]
        };
      }
    }
    inlineText(src) {
      const cap = this.rules.inline.text.exec(src);
      if (cap) {
        let text;
        if (this.lexer.state.inRawBlock) {
          text = cap[0];
        } else {
          text = escape(cap[0]);
        }
        return {
          type: 'text',
          raw: cap[0],
          text
        };
      }
    }
  }

  /**
   * Block-Level Grammar
   */
  // Not all rules are defined in the object literal
  // @ts-expect-error
  const block = {
    newline: /^(?: *(?:\n|$))+/,
    code: /^( {4}[^\n]+(?:\n(?: *(?:\n|$))*)?)+/,
    fences: /^ {0,3}(`{3,}(?=[^`\n]*(?:\n|$))|~{3,})([^\n]*)(?:\n|$)(?:|([\s\S]*?)(?:\n|$))(?: {0,3}\1[~`]* *(?=\n|$)|$)/,
    hr: /^ {0,3}((?:-[\t ]*){3,}|(?:_[ \t]*){3,}|(?:\*[ \t]*){3,})(?:\n+|$)/,
    heading: /^ {0,3}(#{1,6})(?=\s|$)(.*)(?:\n+|$)/,
    blockquote: /^( {0,3}> ?(paragraph|[^\n]*)(?:\n|$))+/,
    list: /^( {0,3}bull)([ \t][^\n]+?)?(?:\n|$)/,
    html: '^ {0,3}(?:' // optional indentation
    + '<(script|pre|style|textarea)[\\s>][\\s\\S]*?(?:</\\1>[^\\n]*\\n+|$)' // (1)
    + '|comment[^\\n]*(\\n+|$)' // (2)
    + '|<\\?[\\s\\S]*?(?:\\?>\\n*|$)' // (3)
    + '|<![A-Z][\\s\\S]*?(?:>\\n*|$)' // (4)
    + '|<!\\[CDATA\\[[\\s\\S]*?(?:\\]\\]>\\n*|$)' // (5)
    + '|</?(tag)(?: +|\\n|/?>)[\\s\\S]*?(?:(?:\\n *)+\\n|$)' // (6)
    + '|<(?!script|pre|style|textarea)([a-z][\\w-]*)(?:attribute)*? */?>(?=[ \\t]*(?:\\n|$))[\\s\\S]*?(?:(?:\\n *)+\\n|$)' // (7) open tag
    + '|</(?!script|pre|style|textarea)[a-z][\\w-]*\\s*>(?=[ \\t]*(?:\\n|$))[\\s\\S]*?(?:(?:\\n *)+\\n|$)' // (7) closing tag
    + ')',
    def: /^ {0,3}\[(label)\]: *(?:\n *)?([^<\s][^\s]*|<.*?>)(?:(?: +(?:\n *)?| *\n *)(title))? *(?:\n+|$)/,
    table: noopTest,
    lheading: /^(?!bull )((?:.|\n(?!\s*?\n|bull ))+?)\n {0,3}(=+|-+) *(?:\n+|$)/,
    // regex template, placeholders will be replaced according to different paragraph
    // interruption rules of commonmark and the original markdown spec:
    _paragraph: /^([^\n]+(?:\n(?!hr|heading|lheading|blockquote|fences|list|html|table| +\n)[^\n]+)*)/,
    text: /^[^\n]+/
  };
  block._label = /(?!\s*\])(?:\\.|[^\[\]\\])+/;
  block._title = /(?:"(?:\\"?|[^"\\])*"|'[^'\n]*(?:\n[^'\n]+)*\n?'|\([^()]*\))/;
  block.def = edit(block.def).replace('label', block._label).replace('title', block._title).getRegex();
  block.bullet = /(?:[*+-]|\d{1,9}[.)])/;
  block.listItemStart = edit(/^( *)(bull) */).replace('bull', block.bullet).getRegex();
  block.list = edit(block.list).replace(/bull/g, block.bullet).replace('hr', '\\n+(?=\\1?(?:(?:- *){3,}|(?:_ *){3,}|(?:\\* *){3,})(?:\\n+|$))').replace('def', '\\n+(?=' + block.def.source + ')').getRegex();
  block._tag = 'address|article|aside|base|basefont|blockquote|body|caption' + '|center|col|colgroup|dd|details|dialog|dir|div|dl|dt|fieldset|figcaption' + '|figure|footer|form|frame|frameset|h[1-6]|head|header|hr|html|iframe' + '|legend|li|link|main|menu|menuitem|meta|nav|noframes|ol|optgroup|option' + '|p|param|section|source|summary|table|tbody|td|tfoot|th|thead|title|tr' + '|track|ul';
  block._comment = /<!--(?!-?>)[\s\S]*?(?:-->|$)/;
  block.html = edit(block.html, 'i').replace('comment', block._comment).replace('tag', block._tag).replace('attribute', / +[a-zA-Z:_][\w.:-]*(?: *= *"[^"\n]*"| *= *'[^'\n]*'| *= *[^\s"'=<>`]+)?/).getRegex();
  block.lheading = edit(block.lheading).replace(/bull/g, block.bullet) // lists can interrupt
  .getRegex();
  block.paragraph = edit(block._paragraph).replace('hr', block.hr).replace('heading', ' {0,3}#{1,6}(?:\\s|$)').replace('|lheading', '') // setex headings don't interrupt commonmark paragraphs
  .replace('|table', '').replace('blockquote', ' {0,3}>').replace('fences', ' {0,3}(?:`{3,}(?=[^`\\n]*\\n)|~{3,})[^\\n]*\\n').replace('list', ' {0,3}(?:[*+-]|1[.)]) ') // only lists starting from 1 can interrupt
  .replace('html', '</?(?:tag)(?: +|\\n|/?>)|<(?:script|pre|style|textarea|!--)').replace('tag', block._tag) // pars can be interrupted by type (6) html blocks
  .getRegex();
  block.blockquote = edit(block.blockquote).replace('paragraph', block.paragraph).getRegex();
  /**
   * Normal Block Grammar
   */
  block.normal = _objectSpread2({}, block);
  /**
   * GFM Block Grammar
   */
  block.gfm = _objectSpread2(_objectSpread2({}, block.normal), {}, {
    table: '^ *([^\\n ].*)\\n' // Header
    + ' {0,3}((?:\\| *)?:?-+:? *(?:\\| *:?-+:? *)*(?:\\| *)?)' // Align
    + '(?:\\n((?:(?! *\\n|hr|heading|blockquote|code|fences|list|html).*(?:\\n|$))*)\\n*|$)' // Cells
  });
  block.gfm.table = edit(block.gfm.table).replace('hr', block.hr).replace('heading', ' {0,3}#{1,6}(?:\\s|$)').replace('blockquote', ' {0,3}>').replace('code', ' {4}[^\\n]').replace('fences', ' {0,3}(?:`{3,}(?=[^`\\n]*\\n)|~{3,})[^\\n]*\\n').replace('list', ' {0,3}(?:[*+-]|1[.)]) ') // only lists starting from 1 can interrupt
  .replace('html', '</?(?:tag)(?: +|\\n|/?>)|<(?:script|pre|style|textarea|!--)').replace('tag', block._tag) // tables can be interrupted by type (6) html blocks
  .getRegex();
  block.gfm.paragraph = edit(block._paragraph).replace('hr', block.hr).replace('heading', ' {0,3}#{1,6}(?:\\s|$)').replace('|lheading', '') // setex headings don't interrupt commonmark paragraphs
  .replace('table', block.gfm.table) // interrupt paragraphs with table
  .replace('blockquote', ' {0,3}>').replace('fences', ' {0,3}(?:`{3,}(?=[^`\\n]*\\n)|~{3,})[^\\n]*\\n').replace('list', ' {0,3}(?:[*+-]|1[.)]) ') // only lists starting from 1 can interrupt
  .replace('html', '</?(?:tag)(?: +|\\n|/?>)|<(?:script|pre|style|textarea|!--)').replace('tag', block._tag) // pars can be interrupted by type (6) html blocks
  .getRegex();
  /**
   * Pedantic grammar (original John Gruber's loose markdown specification)
   */
  block.pedantic = _objectSpread2(_objectSpread2({}, block.normal), {}, {
    html: edit('^ *(?:comment *(?:\\n|\\s*$)' + '|<(tag)[\\s\\S]+?</\\1> *(?:\\n{2,}|\\s*$)' // closed tag
    + '|<tag(?:"[^"]*"|\'[^\']*\'|\\s[^\'"/>\\s]*)*?/?> *(?:\\n{2,}|\\s*$))').replace('comment', block._comment).replace(/tag/g, '(?!(?:' + 'a|em|strong|small|s|cite|q|dfn|abbr|data|time|code|var|samp|kbd|sub' + '|sup|i|b|u|mark|ruby|rt|rp|bdi|bdo|span|br|wbr|ins|del|img)' + '\\b)\\w+(?!:|[^\\w\\s@]*@)\\b').getRegex(),
    def: /^ *\[([^\]]+)\]: *<?([^\s>]+)>?(?: +(["(][^\n]+[")]))? *(?:\n+|$)/,
    heading: /^(#{1,6})(.*)(?:\n+|$)/,
    fences: noopTest,
    lheading: /^(.+?)\n {0,3}(=+|-+) *(?:\n+|$)/,
    paragraph: edit(block.normal._paragraph).replace('hr', block.hr).replace('heading', ' *#{1,6} *[^\n]').replace('lheading', block.lheading).replace('blockquote', ' {0,3}>').replace('|fences', '').replace('|list', '').replace('|html', '').getRegex()
  });
  /**
   * Inline-Level Grammar
   */
  // Not all rules are defined in the object literal
  // @ts-expect-error
  const inline = {
    escape: /^\\([!"#$%&'()*+,\-./:;<=>?@\[\]\\^_`{|}~])/,
    autolink: /^<(scheme:[^\s\x00-\x1f<>]*|email)>/,
    url: noopTest,
    tag: '^comment' + '|^</[a-zA-Z][\\w:-]*\\s*>' // self-closing tag
    + '|^<[a-zA-Z][\\w-]*(?:attribute)*?\\s*/?>' // open tag
    + '|^<\\?[\\s\\S]*?\\?>' // processing instruction, e.g. <?php ?>
    + '|^<![a-zA-Z]+\\s[\\s\\S]*?>' // declaration, e.g. <!DOCTYPE html>
    + '|^<!\\[CDATA\\[[\\s\\S]*?\\]\\]>',
    link: /^!?\[(label)\]\(\s*(href)(?:\s+(title))?\s*\)/,
    reflink: /^!?\[(label)\]\[(ref)\]/,
    nolink: /^!?\[(ref)\](?:\[\])?/,
    reflinkSearch: 'reflink|nolink(?!\\()',
    emStrong: {
      lDelim: /^(?:\*+(?:((?!\*)[punct])|[^\s*]))|^_+(?:((?!_)[punct])|([^\s_]))/,
      //         (1) and (2) can only be a Right Delimiter. (3) and (4) can only be Left.  (5) and (6) can be either Left or Right.
      //         | Skip orphan inside strong      | Consume to delim | (1) #***              | (2) a***#, a***                    | (3) #***a, ***a                  | (4) ***#                 | (5) #***#                         | (6) a***a
      rDelimAst: /^[^_*]*?__[^_*]*?\*[^_*]*?(?=__)|[^*]+(?=[^*])|(?!\*)[punct](\*+)(?=[\s]|$)|[^punct\s](\*+)(?!\*)(?=[punct\s]|$)|(?!\*)[punct\s](\*+)(?=[^punct\s])|[\s](\*+)(?!\*)(?=[punct])|(?!\*)[punct](\*+)(?!\*)(?=[punct])|[^punct\s](\*+)(?=[^punct\s])/,
      rDelimUnd: /^[^_*]*?\*\*[^_*]*?_[^_*]*?(?=\*\*)|[^_]+(?=[^_])|(?!_)[punct](_+)(?=[\s]|$)|[^punct\s](_+)(?!_)(?=[punct\s]|$)|(?!_)[punct\s](_+)(?=[^punct\s])|[\s](_+)(?!_)(?=[punct])|(?!_)[punct](_+)(?!_)(?=[punct])/ // ^- Not allowed for _
    },
    code: /^(`+)([^`]|[^`][\s\S]*?[^`])\1(?!`)/,
    br: /^( {2,}|\\)\n(?!\s*$)/,
    del: noopTest,
    text: /^(`+|[^`])(?:(?= {2,}\n)|[\s\S]*?(?:(?=[\\<!\[`*_]|\b_|$)|[^ ](?= {2,}\n)))/,
    punctuation: /^((?![*_])[\spunctuation])/
  };
  // list of unicode punctuation marks, plus any missing characters from CommonMark spec
  inline._punctuation = '\\p{P}$+<=>`^|~';
  inline.punctuation = edit(inline.punctuation, 'u').replace(/punctuation/g, inline._punctuation).getRegex();
  // sequences em should skip over [title](link), `code`, <html>
  inline.blockSkip = /\[[^[\]]*?\]\([^\(\)]*?\)|`[^`]*?`|<[^<>]*?>/g;
  inline.anyPunctuation = /\\[punct]/g;
  inline._escapes = /\\([punct])/g;
  inline._comment = edit(block._comment).replace('(?:-->|$)', '-->').getRegex();
  inline.emStrong.lDelim = edit(inline.emStrong.lDelim, 'u').replace(/punct/g, inline._punctuation).getRegex();
  inline.emStrong.rDelimAst = edit(inline.emStrong.rDelimAst, 'gu').replace(/punct/g, inline._punctuation).getRegex();
  inline.emStrong.rDelimUnd = edit(inline.emStrong.rDelimUnd, 'gu').replace(/punct/g, inline._punctuation).getRegex();
  inline.anyPunctuation = edit(inline.anyPunctuation, 'gu').replace(/punct/g, inline._punctuation).getRegex();
  inline._escapes = edit(inline._escapes, 'gu').replace(/punct/g, inline._punctuation).getRegex();
  inline._scheme = /[a-zA-Z][a-zA-Z0-9+.-]{1,31}/;
  inline._email = /[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+(@)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+(?![-_])/;
  inline.autolink = edit(inline.autolink).replace('scheme', inline._scheme).replace('email', inline._email).getRegex();
  inline._attribute = /\s+[a-zA-Z:_][\w.:-]*(?:\s*=\s*"[^"]*"|\s*=\s*'[^']*'|\s*=\s*[^\s"'=<>`]+)?/;
  inline.tag = edit(inline.tag).replace('comment', inline._comment).replace('attribute', inline._attribute).getRegex();
  inline._label = /(?:\[(?:\\.|[^\[\]\\])*\]|\\.|`[^`]*`|[^\[\]\\`])*?/;
  inline._href = /<(?:\\.|[^\n<>\\])+>|[^\s\x00-\x1f]*/;
  inline._title = /"(?:\\"?|[^"\\])*"|'(?:\\'?|[^'\\])*'|\((?:\\\)?|[^)\\])*\)/;
  inline.link = edit(inline.link).replace('label', inline._label).replace('href', inline._href).replace('title', inline._title).getRegex();
  inline.reflink = edit(inline.reflink).replace('label', inline._label).replace('ref', block._label).getRegex();
  inline.nolink = edit(inline.nolink).replace('ref', block._label).getRegex();
  inline.reflinkSearch = edit(inline.reflinkSearch, 'g').replace('reflink', inline.reflink).replace('nolink', inline.nolink).getRegex();
  /**
   * Normal Inline Grammar
   */
  inline.normal = _objectSpread2({}, inline);
  /**
   * Pedantic Inline Grammar
   */
  inline.pedantic = _objectSpread2(_objectSpread2({}, inline.normal), {}, {
    strong: {
      start: /^__|\*\*/,
      middle: /^__(?=\S)([\s\S]*?\S)__(?!_)|^\*\*(?=\S)([\s\S]*?\S)\*\*(?!\*)/,
      endAst: /\*\*(?!\*)/g,
      endUnd: /__(?!_)/g
    },
    em: {
      start: /^_|\*/,
      middle: /^()\*(?=\S)([\s\S]*?\S)\*(?!\*)|^_(?=\S)([\s\S]*?\S)_(?!_)/,
      endAst: /\*(?!\*)/g,
      endUnd: /_(?!_)/g
    },
    link: edit(/^!?\[(label)\]\((.*?)\)/).replace('label', inline._label).getRegex(),
    reflink: edit(/^!?\[(label)\]\s*\[([^\]]*)\]/).replace('label', inline._label).getRegex()
  });
  /**
   * GFM Inline Grammar
   */
  inline.gfm = _objectSpread2(_objectSpread2({}, inline.normal), {}, {
    escape: edit(inline.escape).replace('])', '~|])').getRegex(),
    _extended_email: /[A-Za-z0-9._+-]+(@)[a-zA-Z0-9-_]+(?:\.[a-zA-Z0-9-_]*[a-zA-Z0-9])+(?![-_])/,
    url: /^((?:ftp|https?):\/\/|www\.)(?:[a-zA-Z0-9\-]+\.?)+[^\s<]*|^email/,
    _backpedal: /(?:[^?!.,:;*_'"~()&]+|\([^)]*\)|&(?![a-zA-Z0-9]+;$)|[?!.,:;*_'"~)]+(?!$))+/,
    del: /^(~~?)(?=[^\s~])([\s\S]*?[^\s~])\1(?=[^~]|$)/,
    text: /^([`~]+|[^`~])(?:(?= {2,}\n)|(?=[a-zA-Z0-9.!#$%&'*+\/=?_`{\|}~-]+@)|[\s\S]*?(?:(?=[\\<!\[`*~_]|\b_|https?:\/\/|ftp:\/\/|www\.|$)|[^ ](?= {2,}\n)|[^a-zA-Z0-9.!#$%&'*+\/=?_`{\|}~-](?=[a-zA-Z0-9.!#$%&'*+\/=?_`{\|}~-]+@)))/
  });
  inline.gfm.url = edit(inline.gfm.url, 'i').replace('email', inline.gfm._extended_email).getRegex();
  /**
   * GFM + Line Breaks Inline Grammar
   */
  inline.breaks = _objectSpread2(_objectSpread2({}, inline.gfm), {}, {
    br: edit(inline.br).replace('{2,}', '*').getRegex(),
    text: edit(inline.gfm.text).replace('\\b_', '\\b_| {2,}\\n').replace(/\{2,\}/g, '*').getRegex()
  });

  /**
   * Block Lexer
   */
  class _Lexer {
    tokens;
    options;
    state;
    tokenizer;
    inlineQueue;
    constructor(options) {
      // TokenList cannot be created in one go
      // @ts-expect-error
      this.tokens = [];
      this.tokens.links = Object.create(null);
      this.options = options || _defaults;
      this.options.tokenizer = this.options.tokenizer || new _Tokenizer();
      this.tokenizer = this.options.tokenizer;
      this.tokenizer.options = this.options;
      this.tokenizer.lexer = this;
      this.inlineQueue = [];
      this.state = {
        inLink: false,
        inRawBlock: false,
        top: true
      };
      const rules = {
        block: block.normal,
        inline: inline.normal
      };
      if (this.options.pedantic) {
        rules.block = block.pedantic;
        rules.inline = inline.pedantic;
      } else if (this.options.gfm) {
        rules.block = block.gfm;
        if (this.options.breaks) {
          rules.inline = inline.breaks;
        } else {
          rules.inline = inline.gfm;
        }
      }
      this.tokenizer.rules = rules;
    }
    /**
     * Expose Rules
     */
    static get rules() {
      return {
        block,
        inline
      };
    }
    /**
     * Static Lex Method
     */
    static lex(src, options) {
      const lexer = new _Lexer(options);
      return lexer.lex(src);
    }
    /**
     * Static Lex Inline Method
     */
    static lexInline(src, options) {
      const lexer = new _Lexer(options);
      return lexer.inlineTokens(src);
    }
    /**
     * Preprocessing
     */
    lex(src) {
      src = src.replace(/\r\n|\r/g, '\n');
      this.blockTokens(src, this.tokens);
      let next;
      while (next = this.inlineQueue.shift()) {
        this.inlineTokens(next.src, next.tokens);
      }
      return this.tokens;
    }
    blockTokens(src, tokens = []) {
      if (this.options.pedantic) {
        src = src.replace(/\t/g, '    ').replace(/^ +$/gm, '');
      } else {
        src = src.replace(/^( *)(\t+)/gm, (_, leading, tabs) => {
          return leading + '    '.repeat(tabs.length);
        });
      }
      let token;
      let lastToken;
      let cutSrc;
      let lastParagraphClipped;
      while (src) {
        if (this.options.extensions && this.options.extensions.block && this.options.extensions.block.some(extTokenizer => {
          if (token = extTokenizer.call({
            lexer: this
          }, src, tokens)) {
            src = src.substring(token.raw.length);
            tokens.push(token);
            return true;
          }
          return false;
        })) {
          continue;
        }
        // newline
        if (token = this.tokenizer.space(src)) {
          src = src.substring(token.raw.length);
          if (token.raw.length === 1 && tokens.length > 0) {
            // if there's a single \n as a spacer, it's terminating the last line,
            // so move it there so that we don't get unnecessary paragraph tags
            tokens[tokens.length - 1].raw += '\n';
          } else {
            tokens.push(token);
          }
          continue;
        }
        // code
        if (token = this.tokenizer.code(src)) {
          src = src.substring(token.raw.length);
          lastToken = tokens[tokens.length - 1];
          // An indented code block cannot interrupt a paragraph.
          if (lastToken && (lastToken.type === 'paragraph' || lastToken.type === 'text')) {
            lastToken.raw += '\n' + token.raw;
            lastToken.text += '\n' + token.text;
            this.inlineQueue[this.inlineQueue.length - 1].src = lastToken.text;
          } else {
            tokens.push(token);
          }
          continue;
        }
        // fences
        if (token = this.tokenizer.fences(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // heading
        if (token = this.tokenizer.heading(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // hr
        if (token = this.tokenizer.hr(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // blockquote
        if (token = this.tokenizer.blockquote(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // list
        if (token = this.tokenizer.list(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // html
        if (token = this.tokenizer.html(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // def
        if (token = this.tokenizer.def(src)) {
          src = src.substring(token.raw.length);
          lastToken = tokens[tokens.length - 1];
          if (lastToken && (lastToken.type === 'paragraph' || lastToken.type === 'text')) {
            lastToken.raw += '\n' + token.raw;
            lastToken.text += '\n' + token.raw;
            this.inlineQueue[this.inlineQueue.length - 1].src = lastToken.text;
          } else if (!this.tokens.links[token.tag]) {
            this.tokens.links[token.tag] = {
              href: token.href,
              title: token.title
            };
          }
          continue;
        }
        // table (gfm)
        if (token = this.tokenizer.table(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // lheading
        if (token = this.tokenizer.lheading(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // top-level paragraph
        // prevent paragraph consuming extensions by clipping 'src' to extension start
        cutSrc = src;
        if (this.options.extensions && this.options.extensions.startBlock) {
          let startIndex = Infinity;
          const tempSrc = src.slice(1);
          let tempStart;
          this.options.extensions.startBlock.forEach(getStartIndex => {
            tempStart = getStartIndex.call({
              lexer: this
            }, tempSrc);
            if (typeof tempStart === 'number' && tempStart >= 0) {
              startIndex = Math.min(startIndex, tempStart);
            }
          });
          if (startIndex < Infinity && startIndex >= 0) {
            cutSrc = src.substring(0, startIndex + 1);
          }
        }
        if (this.state.top && (token = this.tokenizer.paragraph(cutSrc))) {
          lastToken = tokens[tokens.length - 1];
          if (lastParagraphClipped && lastToken.type === 'paragraph') {
            lastToken.raw += '\n' + token.raw;
            lastToken.text += '\n' + token.text;
            this.inlineQueue.pop();
            this.inlineQueue[this.inlineQueue.length - 1].src = lastToken.text;
          } else {
            tokens.push(token);
          }
          lastParagraphClipped = cutSrc.length !== src.length;
          src = src.substring(token.raw.length);
          continue;
        }
        // text
        if (token = this.tokenizer.text(src)) {
          src = src.substring(token.raw.length);
          lastToken = tokens[tokens.length - 1];
          if (lastToken && lastToken.type === 'text') {
            lastToken.raw += '\n' + token.raw;
            lastToken.text += '\n' + token.text;
            this.inlineQueue.pop();
            this.inlineQueue[this.inlineQueue.length - 1].src = lastToken.text;
          } else {
            tokens.push(token);
          }
          continue;
        }
        if (src) {
          const errMsg = 'Infinite loop on byte: ' + src.charCodeAt(0);
          if (this.options.silent) {
            console.error(errMsg);
            break;
          } else {
            throw new Error(errMsg);
          }
        }
      }
      this.state.top = true;
      return tokens;
    }
    inline(src, tokens = []) {
      this.inlineQueue.push({
        src,
        tokens
      });
      return tokens;
    }
    /**
     * Lexing/Compiling
     */
    inlineTokens(src, tokens = []) {
      let token, lastToken, cutSrc;
      // String with links masked to avoid interference with em and strong
      let maskedSrc = src;
      let match;
      let keepPrevChar, prevChar;
      // Mask out reflinks
      if (this.tokens.links) {
        const links = Object.keys(this.tokens.links);
        if (links.length > 0) {
          while ((match = this.tokenizer.rules.inline.reflinkSearch.exec(maskedSrc)) != null) {
            if (links.includes(match[0].slice(match[0].lastIndexOf('[') + 1, -1))) {
              maskedSrc = maskedSrc.slice(0, match.index) + '[' + 'a'.repeat(match[0].length - 2) + ']' + maskedSrc.slice(this.tokenizer.rules.inline.reflinkSearch.lastIndex);
            }
          }
        }
      }
      // Mask out other blocks
      while ((match = this.tokenizer.rules.inline.blockSkip.exec(maskedSrc)) != null) {
        maskedSrc = maskedSrc.slice(0, match.index) + '[' + 'a'.repeat(match[0].length - 2) + ']' + maskedSrc.slice(this.tokenizer.rules.inline.blockSkip.lastIndex);
      }
      // Mask out escaped characters
      while ((match = this.tokenizer.rules.inline.anyPunctuation.exec(maskedSrc)) != null) {
        maskedSrc = maskedSrc.slice(0, match.index) + '++' + maskedSrc.slice(this.tokenizer.rules.inline.anyPunctuation.lastIndex);
      }
      while (src) {
        if (!keepPrevChar) {
          prevChar = '';
        }
        keepPrevChar = false;
        // extensions
        if (this.options.extensions && this.options.extensions.inline && this.options.extensions.inline.some(extTokenizer => {
          if (token = extTokenizer.call({
            lexer: this
          }, src, tokens)) {
            src = src.substring(token.raw.length);
            tokens.push(token);
            return true;
          }
          return false;
        })) {
          continue;
        }
        // escape
        if (token = this.tokenizer.escape(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // tag
        if (token = this.tokenizer.tag(src)) {
          src = src.substring(token.raw.length);
          lastToken = tokens[tokens.length - 1];
          if (lastToken && token.type === 'text' && lastToken.type === 'text') {
            lastToken.raw += token.raw;
            lastToken.text += token.text;
          } else {
            tokens.push(token);
          }
          continue;
        }
        // link
        if (token = this.tokenizer.link(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // reflink, nolink
        if (token = this.tokenizer.reflink(src, this.tokens.links)) {
          src = src.substring(token.raw.length);
          lastToken = tokens[tokens.length - 1];
          if (lastToken && token.type === 'text' && lastToken.type === 'text') {
            lastToken.raw += token.raw;
            lastToken.text += token.text;
          } else {
            tokens.push(token);
          }
          continue;
        }
        // em & strong
        if (token = this.tokenizer.emStrong(src, maskedSrc, prevChar)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // code
        if (token = this.tokenizer.codespan(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // br
        if (token = this.tokenizer.br(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // del (gfm)
        if (token = this.tokenizer.del(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // autolink
        if (token = this.tokenizer.autolink(src)) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // url (gfm)
        if (!this.state.inLink && (token = this.tokenizer.url(src))) {
          src = src.substring(token.raw.length);
          tokens.push(token);
          continue;
        }
        // text
        // prevent inlineText consuming extensions by clipping 'src' to extension start
        cutSrc = src;
        if (this.options.extensions && this.options.extensions.startInline) {
          let startIndex = Infinity;
          const tempSrc = src.slice(1);
          let tempStart;
          this.options.extensions.startInline.forEach(getStartIndex => {
            tempStart = getStartIndex.call({
              lexer: this
            }, tempSrc);
            if (typeof tempStart === 'number' && tempStart >= 0) {
              startIndex = Math.min(startIndex, tempStart);
            }
          });
          if (startIndex < Infinity && startIndex >= 0) {
            cutSrc = src.substring(0, startIndex + 1);
          }
        }
        if (token = this.tokenizer.inlineText(cutSrc)) {
          src = src.substring(token.raw.length);
          if (token.raw.slice(-1) !== '_') {
            // Track prevChar before string of ____ started
            prevChar = token.raw.slice(-1);
          }
          keepPrevChar = true;
          lastToken = tokens[tokens.length - 1];
          if (lastToken && lastToken.type === 'text') {
            lastToken.raw += token.raw;
            lastToken.text += token.text;
          } else {
            tokens.push(token);
          }
          continue;
        }
        if (src) {
          const errMsg = 'Infinite loop on byte: ' + src.charCodeAt(0);
          if (this.options.silent) {
            console.error(errMsg);
            break;
          } else {
            throw new Error(errMsg);
          }
        }
      }
      return tokens;
    }
  }

  /**
   * Renderer
   */
  class _Renderer {
    options;
    constructor(options) {
      this.options = options || _defaults;
    }
    code(code, infostring, escaped) {
      const lang = (infostring || '').match(/^\S*/)?.[0];
      code = code.replace(/\n$/, '') + '\n';
      if (!lang) {
        return '<pre><code>' + (escaped ? code : escape(code, true)) + '</code></pre>\n';
      }
      return '<pre><code class="language-' + escape(lang) + '">' + (escaped ? code : escape(code, true)) + '</code></pre>\n';
    }
    blockquote(quote) {
      return `<blockquote>\n${quote}</blockquote>\n`;
    }
    html(html, block) {
      return html;
    }
    heading(text, level, raw) {
      // ignore IDs
      return `<h${level}>${text}</h${level}>\n`;
    }
    hr() {
      return '<hr>\n';
    }
    list(body, ordered, start) {
      const type = ordered ? 'ol' : 'ul';
      const startatt = ordered && start !== 1 ? ' start="' + start + '"' : '';
      return '<' + type + startatt + '>\n' + body + '</' + type + '>\n';
    }
    listitem(text, task, checked) {
      return `<li>${text}</li>\n`;
    }
    checkbox(checked) {
      return '<input ' + (checked ? 'checked="" ' : '') + 'disabled="" type="checkbox">';
    }
    paragraph(text) {
      return `<p>${text}</p>\n`;
    }
    table(header, body) {
      if (body) body = `<tbody>${body}</tbody>`;
      return '<table>\n' + '<thead>\n' + header + '</thead>\n' + body + '</table>\n';
    }
    tablerow(content) {
      return `<tr>\n${content}</tr>\n`;
    }
    tablecell(content, flags) {
      const type = flags.header ? 'th' : 'td';
      const tag = flags.align ? `<${type} align="${flags.align}">` : `<${type}>`;
      return tag + content + `</${type}>\n`;
    }
    /**
     * span level renderer
     */
    strong(text) {
      return `<strong>${text}</strong>`;
    }
    em(text) {
      return `<em>${text}</em>`;
    }
    codespan(text) {
      return `<code>${text}</code>`;
    }
    br() {
      return '<br>';
    }
    del(text) {
      return `<del>${text}</del>`;
    }
    link(href, title, text) {
      const cleanHref = cleanUrl(href);
      if (cleanHref === null) {
        return text;
      }
      href = cleanHref;
      let out = '<a href="' + href + '"';
      if (title) {
        out += ' title="' + title + '"';
      }
      out += '>' + text + '</a>';
      return out;
    }
    image(href, title, text) {
      const cleanHref = cleanUrl(href);
      if (cleanHref === null) {
        return text;
      }
      href = cleanHref;
      let out = `<img src="${href}" alt="${text}"`;
      if (title) {
        out += ` title="${title}"`;
      }
      out += '>';
      return out;
    }
    text(text) {
      return text;
    }
  }

  /**
   * TextRenderer
   * returns only the textual part of the token
   */
  class _TextRenderer {
    // no need for block level renderers
    strong(text) {
      return text;
    }
    em(text) {
      return text;
    }
    codespan(text) {
      return text;
    }
    del(text) {
      return text;
    }
    html(text) {
      return text;
    }
    text(text) {
      return text;
    }
    link(href, title, text) {
      return '' + text;
    }
    image(href, title, text) {
      return '' + text;
    }
    br() {
      return '';
    }
  }

  /**
   * Parsing & Compiling
   */
  class _Parser {
    options;
    renderer;
    textRenderer;
    constructor(options) {
      this.options = options || _defaults;
      this.options.renderer = this.options.renderer || new _Renderer();
      this.renderer = this.options.renderer;
      this.renderer.options = this.options;
      this.textRenderer = new _TextRenderer();
    }
    /**
     * Static Parse Method
     */
    static parse(tokens, options) {
      const parser = new _Parser(options);
      return parser.parse(tokens);
    }
    /**
     * Static Parse Inline Method
     */
    static parseInline(tokens, options) {
      const parser = new _Parser(options);
      return parser.parseInline(tokens);
    }
    /**
     * Parse Loop
     */
    parse(tokens, top = true) {
      let out = '';
      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        // Run any renderer extensions
        if (this.options.extensions && this.options.extensions.renderers && this.options.extensions.renderers[token.type]) {
          const genericToken = token;
          const ret = this.options.extensions.renderers[genericToken.type].call({
            parser: this
          }, genericToken);
          if (ret !== false || !['space', 'hr', 'heading', 'code', 'table', 'blockquote', 'list', 'html', 'paragraph', 'text'].includes(genericToken.type)) {
            out += ret || '';
            continue;
          }
        }
        switch (token.type) {
          case 'space':
            {
              continue;
            }
          case 'hr':
            {
              out += this.renderer.hr();
              continue;
            }
          case 'heading':
            {
              const headingToken = token;
              out += this.renderer.heading(this.parseInline(headingToken.tokens), headingToken.depth, unescape(this.parseInline(headingToken.tokens, this.textRenderer)));
              continue;
            }
          case 'code':
            {
              const codeToken = token;
              out += this.renderer.code(codeToken.text, codeToken.lang, !!codeToken.escaped);
              continue;
            }
          case 'table':
            {
              const tableToken = token;
              let header = '';
              // header
              let cell = '';
              for (let j = 0; j < tableToken.header.length; j++) {
                cell += this.renderer.tablecell(this.parseInline(tableToken.header[j].tokens), {
                  header: true,
                  align: tableToken.align[j]
                });
              }
              header += this.renderer.tablerow(cell);
              let body = '';
              for (let j = 0; j < tableToken.rows.length; j++) {
                const row = tableToken.rows[j];
                cell = '';
                for (let k = 0; k < row.length; k++) {
                  cell += this.renderer.tablecell(this.parseInline(row[k].tokens), {
                    header: false,
                    align: tableToken.align[k]
                  });
                }
                body += this.renderer.tablerow(cell);
              }
              out += this.renderer.table(header, body);
              continue;
            }
          case 'blockquote':
            {
              const blockquoteToken = token;
              const body = this.parse(blockquoteToken.tokens);
              out += this.renderer.blockquote(body);
              continue;
            }
          case 'list':
            {
              const listToken = token;
              const ordered = listToken.ordered;
              const start = listToken.start;
              const loose = listToken.loose;
              let body = '';
              for (let j = 0; j < listToken.items.length; j++) {
                const item = listToken.items[j];
                const checked = item.checked;
                const task = item.task;
                let itemBody = '';
                if (item.task) {
                  const checkbox = this.renderer.checkbox(!!checked);
                  if (loose) {
                    if (item.tokens.length > 0 && item.tokens[0].type === 'paragraph') {
                      item.tokens[0].text = checkbox + ' ' + item.tokens[0].text;
                      if (item.tokens[0].tokens && item.tokens[0].tokens.length > 0 && item.tokens[0].tokens[0].type === 'text') {
                        item.tokens[0].tokens[0].text = checkbox + ' ' + item.tokens[0].tokens[0].text;
                      }
                    } else {
                      item.tokens.unshift({
                        type: 'text',
                        text: checkbox + ' '
                      });
                    }
                  } else {
                    itemBody += checkbox + ' ';
                  }
                }
                itemBody += this.parse(item.tokens, loose);
                body += this.renderer.listitem(itemBody, task, !!checked);
              }
              out += this.renderer.list(body, ordered, start);
              continue;
            }
          case 'html':
            {
              const htmlToken = token;
              out += this.renderer.html(htmlToken.text, htmlToken.block);
              continue;
            }
          case 'paragraph':
            {
              const paragraphToken = token;
              out += this.renderer.paragraph(this.parseInline(paragraphToken.tokens));
              continue;
            }
          case 'text':
            {
              let textToken = token;
              let body = textToken.tokens ? this.parseInline(textToken.tokens) : textToken.text;
              while (i + 1 < tokens.length && tokens[i + 1].type === 'text') {
                textToken = tokens[++i];
                body += '\n' + (textToken.tokens ? this.parseInline(textToken.tokens) : textToken.text);
              }
              out += top ? this.renderer.paragraph(body) : body;
              continue;
            }
          default:
            {
              const errMsg = 'Token with "' + token.type + '" type was not found.';
              if (this.options.silent) {
                console.error(errMsg);
                return '';
              } else {
                throw new Error(errMsg);
              }
            }
        }
      }
      return out;
    }
    /**
     * Parse Inline Tokens
     */
    parseInline(tokens, renderer) {
      renderer = renderer || this.renderer;
      let out = '';
      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        // Run any renderer extensions
        if (this.options.extensions && this.options.extensions.renderers && this.options.extensions.renderers[token.type]) {
          const ret = this.options.extensions.renderers[token.type].call({
            parser: this
          }, token);
          if (ret !== false || !['escape', 'html', 'link', 'image', 'strong', 'em', 'codespan', 'br', 'del', 'text'].includes(token.type)) {
            out += ret || '';
            continue;
          }
        }
        switch (token.type) {
          case 'escape':
            {
              const escapeToken = token;
              out += renderer.text(escapeToken.text);
              break;
            }
          case 'html':
            {
              const tagToken = token;
              out += renderer.html(tagToken.text);
              break;
            }
          case 'link':
            {
              const linkToken = token;
              out += renderer.link(linkToken.href, linkToken.title, this.parseInline(linkToken.tokens, renderer));
              break;
            }
          case 'image':
            {
              const imageToken = token;
              out += renderer.image(imageToken.href, imageToken.title, imageToken.text);
              break;
            }
          case 'strong':
            {
              const strongToken = token;
              out += renderer.strong(this.parseInline(strongToken.tokens, renderer));
              break;
            }
          case 'em':
            {
              const emToken = token;
              out += renderer.em(this.parseInline(emToken.tokens, renderer));
              break;
            }
          case 'codespan':
            {
              const codespanToken = token;
              out += renderer.codespan(codespanToken.text);
              break;
            }
          case 'br':
            {
              out += renderer.br();
              break;
            }
          case 'del':
            {
              const delToken = token;
              out += renderer.del(this.parseInline(delToken.tokens, renderer));
              break;
            }
          case 'text':
            {
              const textToken = token;
              out += renderer.text(textToken.text);
              break;
            }
          default:
            {
              const errMsg = 'Token with "' + token.type + '" type was not found.';
              if (this.options.silent) {
                console.error(errMsg);
                return '';
              } else {
                throw new Error(errMsg);
              }
            }
        }
      }
      return out;
    }
  }
  class _Hooks {
    options;
    constructor(options) {
      this.options = options || _defaults;
    }
    static passThroughHooks = new Set(['preprocess', 'postprocess']);
    /**
     * Process markdown before marked
     */
    preprocess(markdown) {
      return markdown;
    }
    /**
     * Process HTML after marked is finished
     */
    postprocess(html) {
      return html;
    }
  }
  class Marked {
    defaults = _getDefaults();
    options = this.setOptions;
    parse = this.#parseMarkdown(_Lexer.lex, _Parser.parse);
    parseInline = this.#parseMarkdown(_Lexer.lexInline, _Parser.parseInline);
    Parser = _Parser;
    Renderer = _Renderer;
    TextRenderer = _TextRenderer;
    Lexer = _Lexer;
    Tokenizer = _Tokenizer;
    Hooks = _Hooks;
    constructor(...args) {
      this.use(...args);
    }
    /**
     * Run callback for every token
     */
    walkTokens(tokens, callback) {
      let values = [];
      for (const token of tokens) {
        values = values.concat(callback.call(this, token));
        switch (token.type) {
          case 'table':
            {
              const tableToken = token;
              for (const cell of tableToken.header) {
                values = values.concat(this.walkTokens(cell.tokens, callback));
              }
              for (const row of tableToken.rows) {
                for (const cell of row) {
                  values = values.concat(this.walkTokens(cell.tokens, callback));
                }
              }
              break;
            }
          case 'list':
            {
              const listToken = token;
              values = values.concat(this.walkTokens(listToken.items, callback));
              break;
            }
          default:
            {
              const genericToken = token;
              if (this.defaults.extensions?.childTokens?.[genericToken.type]) {
                this.defaults.extensions.childTokens[genericToken.type].forEach(childTokens => {
                  values = values.concat(this.walkTokens(genericToken[childTokens], callback));
                });
              } else if (genericToken.tokens) {
                values = values.concat(this.walkTokens(genericToken.tokens, callback));
              }
            }
        }
      }
      return values;
    }
    use(...args) {
      const extensions = this.defaults.extensions || {
        renderers: {},
        childTokens: {}
      };
      args.forEach(pack => {
        // copy options to new object
        const opts = _objectSpread2({}, pack);
        // set async to true if it was set to true before
        opts.async = this.defaults.async || opts.async || false;
        // ==-- Parse "addon" extensions --== //
        if (pack.extensions) {
          pack.extensions.forEach(ext => {
            if (!ext.name) {
              throw new Error('extension name required');
            }
            if ('renderer' in ext) {
              // Renderer extensions
              const prevRenderer = extensions.renderers[ext.name];
              if (prevRenderer) {
                // Replace extension with func to run new extension but fall back if false
                extensions.renderers[ext.name] = function (...args) {
                  let ret = ext.renderer.apply(this, args);
                  if (ret === false) {
                    ret = prevRenderer.apply(this, args);
                  }
                  return ret;
                };
              } else {
                extensions.renderers[ext.name] = ext.renderer;
              }
            }
            if ('tokenizer' in ext) {
              // Tokenizer Extensions
              if (!ext.level || ext.level !== 'block' && ext.level !== 'inline') {
                throw new Error("extension level must be 'block' or 'inline'");
              }
              const extLevel = extensions[ext.level];
              if (extLevel) {
                extLevel.unshift(ext.tokenizer);
              } else {
                extensions[ext.level] = [ext.tokenizer];
              }
              if (ext.start) {
                // Function to check for start of token
                if (ext.level === 'block') {
                  if (extensions.startBlock) {
                    extensions.startBlock.push(ext.start);
                  } else {
                    extensions.startBlock = [ext.start];
                  }
                } else if (ext.level === 'inline') {
                  if (extensions.startInline) {
                    extensions.startInline.push(ext.start);
                  } else {
                    extensions.startInline = [ext.start];
                  }
                }
              }
            }
            if ('childTokens' in ext && ext.childTokens) {
              // Child tokens to be visited by walkTokens
              extensions.childTokens[ext.name] = ext.childTokens;
            }
          });
          opts.extensions = extensions;
        }
        // ==-- Parse "overwrite" extensions --== //
        if (pack.renderer) {
          const renderer = this.defaults.renderer || new _Renderer(this.defaults);
          for (const prop in pack.renderer) {
            const rendererFunc = pack.renderer[prop];
            const rendererKey = prop;
            const prevRenderer = renderer[rendererKey];
            // Replace renderer with func to run extension, but fall back if false
            renderer[rendererKey] = (...args) => {
              let ret = rendererFunc.apply(renderer, args);
              if (ret === false) {
                ret = prevRenderer.apply(renderer, args);
              }
              return ret || '';
            };
          }
          opts.renderer = renderer;
        }
        if (pack.tokenizer) {
          const tokenizer = this.defaults.tokenizer || new _Tokenizer(this.defaults);
          for (const prop in pack.tokenizer) {
            const tokenizerFunc = pack.tokenizer[prop];
            const tokenizerKey = prop;
            const prevTokenizer = tokenizer[tokenizerKey];
            // Replace tokenizer with func to run extension, but fall back if false
            tokenizer[tokenizerKey] = (...args) => {
              let ret = tokenizerFunc.apply(tokenizer, args);
              if (ret === false) {
                ret = prevTokenizer.apply(tokenizer, args);
              }
              return ret;
            };
          }
          opts.tokenizer = tokenizer;
        }
        // ==-- Parse Hooks extensions --== //
        if (pack.hooks) {
          const hooks = this.defaults.hooks || new _Hooks();
          for (const prop in pack.hooks) {
            const hooksFunc = pack.hooks[prop];
            const hooksKey = prop;
            const prevHook = hooks[hooksKey];
            if (_Hooks.passThroughHooks.has(prop)) {
              hooks[hooksKey] = arg => {
                if (this.defaults.async) {
                  return Promise.resolve(hooksFunc.call(hooks, arg)).then(ret => {
                    return prevHook.call(hooks, ret);
                  });
                }
                const ret = hooksFunc.call(hooks, arg);
                return prevHook.call(hooks, ret);
              };
            } else {
              hooks[hooksKey] = (...args) => {
                let ret = hooksFunc.apply(hooks, args);
                if (ret === false) {
                  ret = prevHook.apply(hooks, args);
                }
                return ret;
              };
            }
          }
          opts.hooks = hooks;
        }
        // ==-- Parse WalkTokens extensions --== //
        if (pack.walkTokens) {
          const walkTokens = this.defaults.walkTokens;
          const packWalktokens = pack.walkTokens;
          opts.walkTokens = function (token) {
            let values = [];
            values.push(packWalktokens.call(this, token));
            if (walkTokens) {
              values = values.concat(walkTokens.call(this, token));
            }
            return values;
          };
        }
        this.defaults = _objectSpread2(_objectSpread2({}, this.defaults), opts);
      });
      return this;
    }
    setOptions(opt) {
      this.defaults = _objectSpread2(_objectSpread2({}, this.defaults), opt);
      return this;
    }
    lexer(src, options) {
      return _Lexer.lex(src, options ?? this.defaults);
    }
    parser(tokens, options) {
      return _Parser.parse(tokens, options ?? this.defaults);
    }
    #parseMarkdown(lexer, parser) {
      return (src, options) => {
        const origOpt = _objectSpread2({}, options);
        const opt = _objectSpread2(_objectSpread2({}, this.defaults), origOpt);
        // Show warning if an extension set async to true but the parse was called with async: false
        if (this.defaults.async === true && origOpt.async === false) {
          if (!opt.silent) {
            console.warn('marked(): The async option was set to true by an extension. The async: false option sent to parse will be ignored.');
          }
          opt.async = true;
        }
        const throwError = this.#onError(!!opt.silent, !!opt.async);
        // throw error in case of non string input
        if (typeof src === 'undefined' || src === null) {
          return throwError(new Error('marked(): input parameter is undefined or null'));
        }
        if (typeof src !== 'string') {
          return throwError(new Error('marked(): input parameter is of type ' + Object.prototype.toString.call(src) + ', string expected'));
        }
        if (opt.hooks) {
          opt.hooks.options = opt;
        }
        if (opt.async) {
          return Promise.resolve(opt.hooks ? opt.hooks.preprocess(src) : src).then(src => lexer(src, opt)).then(tokens => opt.walkTokens ? Promise.all(this.walkTokens(tokens, opt.walkTokens)).then(() => tokens) : tokens).then(tokens => parser(tokens, opt)).then(html => opt.hooks ? opt.hooks.postprocess(html) : html).catch(throwError);
        }
        try {
          if (opt.hooks) {
            src = opt.hooks.preprocess(src);
          }
          const tokens = lexer(src, opt);
          if (opt.walkTokens) {
            this.walkTokens(tokens, opt.walkTokens);
          }
          let html = parser(tokens, opt);
          if (opt.hooks) {
            html = opt.hooks.postprocess(html);
          }
          return html;
        } catch (e) {
          return throwError(e);
        }
      };
    }
    #onError(silent, async) {
      return e => {
        e.message += '\nPlease report this to https://github.com/markedjs/marked.';
        if (silent) {
          const msg = '<p>An error occurred:</p><pre>' + escape(e.message + '', true) + '</pre>';
          if (async) {
            return Promise.resolve(msg);
          }
          return msg;
        }
        if (async) {
          return Promise.reject(e);
        }
        throw e;
      };
    }
  }
  const markedInstance = new Marked();
  function marked(src, opt) {
    return markedInstance.parse(src, opt);
  }
  /**
   * Sets the default options.
   *
   * @param options Hash of options
   */
  marked.options = marked.setOptions = function (options) {
    markedInstance.setOptions(options);
    marked.defaults = markedInstance.defaults;
    changeDefaults(marked.defaults);
    return marked;
  };
  /**
   * Gets the original marked default options.
   */
  marked.getDefaults = _getDefaults;
  marked.defaults = _defaults;
  /**
   * Use Extension
   */
  marked.use = function (...args) {
    markedInstance.use(...args);
    marked.defaults = markedInstance.defaults;
    changeDefaults(marked.defaults);
    return marked;
  };
  /**
   * Run callback for every token
   */
  marked.walkTokens = function (tokens, callback) {
    return markedInstance.walkTokens(tokens, callback);
  };
  /**
   * Compiles markdown to HTML without enclosing `p` tag.
   *
   * @param src String of markdown source to be compiled
   * @param options Hash of options
   * @return String of compiled HTML
   */
  marked.parseInline = markedInstance.parseInline;
  /**
   * Expose
   */
  marked.Parser = _Parser;
  marked.parser = _Parser.parse;
  marked.Renderer = _Renderer;
  marked.TextRenderer = _TextRenderer;
  marked.Lexer = _Lexer;
  marked.lexer = _Lexer.lex;
  marked.Tokenizer = _Tokenizer;
  marked.Hooks = _Hooks;
  marked.parse = marked;
  marked.options;
  marked.setOptions;
  marked.use;
  marked.walkTokens;
  marked.parseInline;
  _Parser.parse;
  _Lexer.lex;

  const STREAM_IDLE_DELAY_MS = 1000;
  class chatInteractionElement extends HTMLElement {
    constructor() {
      super();
      this.unformattedMessage = "";
    }

    //#region HTML attributes
    get showControls() {
      return this.getBooleanAttribute("showControls", false);
    }
    set showControls(value) {
      this.setAttribute("showControls", value);
    }
    get messageId() {
      return this.getStringAttribute("message-id");
    }
    set messageId(value) {
      this.setAttribute("message-id", value);
    }
    get controls() {
      return this.getStringAttribute("controls", "");
    }
    set controls(value) {
      this.setAttribute("controls", value);
    }
    get photo() {
      return this.getStringAttribute("photo");
    }
    set photo(value) {
      this.setAttribute("photo", value);
    }
    get name() {
      return this.getStringAttribute("name");
    }
    set name(value) {
      this.setAttribute("name", value);
    }
    get icon() {
      return this.getStringAttribute("icon");
    }
    set icon(value) {
      this.setAttribute("icon", value);
    }
    get color() {
      return this.getStringAttribute("color", "primary", ["primary", "secondary", "success", "danger", "warning", "info", "light", "dark"]);
    }
    set color(value) {
      this.setAttribute("color", value);
    }
    get role() {
      return this.getStringAttribute("role", "user", ["user", "assistant"]);
    }
    set role(value) {
      this.setAttribute("role", value);
    }
    //#endregion HTML attributes

    //#region HTML elements
    get chatMessageElement() {
      return this.querySelector(".chat-message");
    }
    get chatMessageContentElement() {
      return this.querySelector(".chat-message-content");
    }
    get chatTypingLoaderElement() {
      return this.querySelector(".chat-typing-loader");
    }
    get chatMessageInfoElement() {
      return this.querySelector(".chat-info");
    }
    get chatControlsElement() {
      return this.querySelector(".chat-controls");
    }
    get chatBtnControlsEditElement() {
      return this.querySelector(".btn-chat-ctrl-edit");
    }
    get chatBtnControlsCopyElement() {
      return this.querySelector(".btn-chat-ctrl-copy");
    }
    get chatBtnControlsLikeElement() {
      return this.querySelector(".btn-chat-ctrl-like");
    }
    get chatBtnControlsDislikeElement() {
      return this.querySelector(".btn-chat-ctrl-dislike");
    }
    get chatActionsElement() {
      return this.querySelector(".chat-actions");
    }
    //#endregion HTML elements

    get roleClass() {
      return `chat-${this.role}`;
    }
    get copyEnabled() {
      return this.controls.includes("copy");
    }
    get likeEnabled() {
      return this.controls.includes("like");
    }
    get dislikeEnabled() {
      return this.controls.includes("dislike");
    }
    connectedCallback() {
      this._render();
      this._bindEvents();
    }
    disableActions() {
      this.chatActionsElement.querySelectorAll("button").forEach(button => this._disableAction(button));
    }
    _render() {
      this.innerHTML = `
      <div class="chat-interaction ${this.roleClass}">
        <div class="chat-avatar">
          <wc-avatar photo="${this.photo}" name="${this.name}" icon="${this.icon}" size="sm" color="${this.color}"/>
        </div>
        <div class="chat-messages">
          <div class="chat-message">
            <div class="chat-message-content">
              <div class="chat-typing-loader">
                <div class="chat-typing-loader-bar"></div>
                <div class="chat-typing-loader-bar"></div>
                <div class="chat-typing-loader-bar"></div>
              </div>
            </div>
          </div>
          <div class="chat-actions d-none"></div>
          <div class="chat-info d-none">
            ${sIf(this.showControls, `<div class="chat-controls">
                ${sIf(true, '<wc-chat-ctrl-edit class="btn-chat-ctrl-edit"></wc-chat-ctrl-edit>')}
                ${sIf(this.copyEnabled, '<wc-chat-ctrl-copy class="btn-chat-ctrl-copy"></wc-chat-ctrl-copy>')}
                ${sIf(this.likeEnabled, '<wc-chat-ctrl-like class="btn-chat-ctrl-like"></wc-chat-ctrl-like>')}
                ${sIf(this.dislikeEnabled, '<wc-chat-ctrl-dislike class="btn-chat-ctrl-dislike"></wc-chat-ctrl-dislike>')}
              </div>`)}
          </div>
        </div>
      </div>`;
    }
    addStream(message) {
      clearTimeout(this._streamIdleTimer);
      this.hideTyping();
      this._setContent(message);
      this._streamIdleTimer = setTimeout(() => {
        this.chatMessageContentElement.insertAdjacentHTML("beforeend", `<div class="chat-typing-loader">
          <div class="chat-typing-loader-bar"></div>
          <div class="chat-typing-loader-bar"></div>
          <div class="chat-typing-loader-bar"></div>
        </div>`);
      }, STREAM_IDLE_DELAY_MS);
    }
    setMessage(message, {
      format,
      messageId,
      previewMessage
    }) {
      clearTimeout(this._streamIdleTimer);
      this.hideTyping();
      this.messageId = messageId || generateGUID();
      this.unformattedMessage = message;
      this._setContent(previewMessage || message, format);
      this._showMessageInfo();
    }
    _showMessageInfo() {
      const messageInfo = this.chatMessageInfoElement;
      messageInfo.classList.remove("d-none");
      if (messageInfo.clientHeight === 0) messageInfo.classList.add("d-none");
    }
    _addElementProgressBarRounded(button, progress) {
      if (!progress) return;
      const internal = document.createElement("div");
      internal.classList.add("internal-circle");
      const progressCircle = document.createElement("div");
      progressCircle.classList.add("progress-bar-rounded");
      progressCircle.setAttribute("title", progress.title);
      if (progress.percentage > 0) progressCircle.setAttribute("style", `background: conic-gradient(#bebebe 0% ${progress.percentage}%, #737b97 ${progress.percentage + 0}% 100%) !important`);
      progressCircle.appendChild(internal);
      button.appendChild(progressCircle);
    }
    setActions(actions) {
      if (!actions || actions.length === 0) return;
      this.chatActionsElement.classList.remove("d-none");
      this.chatMessageInfoElement.classList.add("d-none");
      actions.forEach(action => {
        const button = document.createElement("button");
        button.classList.add("btn", "btn-sm", "btn-outline-secondary");
        button.textContent = action.label;
        if (action.handle) {
          button.addEventListener("click", e => {
            const actionResult = action.handle(e, this);
            this.dispatchEvent(new CustomEvent("action:selected", {
              detail: actionResult,
              bubbles: true
            }));
          });
        }
        button.addEventListener("click", () => this._actionSelected(button, action));
        this._setAttributes(button, action.attributes);
        this._addElementProgressBarRounded(button, action.progress);
        this.chatActionsElement.appendChild(button);
      });
    }
    setUrls(urls) {
      if (!urls || !urls.length) return;
      const listId = `chat-urls-list-${generateGUID()}`;
      let html = `<div class="chat-urls">
      <button type="button" class="btn btn-link btn-sm p-0 text-decoration-none chat-urls-toggle" data-list="${listId}" aria-expanded="false">
        <wc-icon icon="chevron-right" size="sm" class="me-1 chat-urls-toggle-icon"></wc-icon>${i18nlocales.t("chat.referencesLabel")} (${urls.length})
      </button>
      <ul class="list-unstyled mt-2 ms-2" id="${listId}">`;
      urls.forEach(url => html += `<li><a href="${url}" target="_blank">${url}</a></li>`);
      html += `</ul></div>`;
      this.chatMessageContentElement.insertAdjacentHTML("beforeend", html);
      const toggleBtn = this.chatMessageContentElement.querySelector(`[data-list="${listId}"]`);
      const list = this.chatMessageContentElement.querySelector(`#${listId}`);
      toggleBtn?.addEventListener("click", () => {
        const expanded = toggleBtn.getAttribute("aria-expanded") === "true";
        toggleBtn.setAttribute("aria-expanded", !expanded);
        list?.classList.toggle("expanded");
      });
    }
    setInfo(info) {
      if (!info) return;
      this.chatMessageContentElement.insertAdjacentHTML("beforeend", `<div class="chat-info"><label>${info}</label></div>`);
    }
    setEnableCopyToInput(enable) {
      this.chatBtnControlsEditElement.style.display = enable ? "inline-block" : "none";
    }
    _actionSelected(actionBtnClicked, action) {
      this._removeOtherActions(actionBtnClicked);
      if (action.keepEnabled) actionBtnClicked.blur();else this._disableAction(actionBtnClicked);
    }
    _disableAction(button) {
      button.onclick = "";
      button.classList.add("disabled");
      button.setAttribute("disabled", true);
    }
    _removeOtherActions(actionBtnClicked) {
      const actionButtons = this.chatActionsElement.querySelectorAll("button");
      actionButtons.forEach(button => {
        if (button !== actionBtnClicked) button.remove();
      });
    }
    setErrorMessage(message) {
      clearTimeout(this._streamIdleTimer);
      this.hideTyping();
      this.chatMessageContentElement.classList.add("chat-message-error");
      this._setContent(message);
    }
    setInfoMessage(message) {
      clearTimeout(this._streamIdleTimer);
      this.hideTyping();
      this.chatMessageContentElement.classList.add("chat-message-info");
      this._setContent(message);
    }
    hideTyping() {
      this.chatTypingLoaderElement?.remove();
    }

    /*
     * Format: nl (newline), markdown e table
     */
    _setContent(message, format = ["markdown", "table"]) {
      if (!message) return;
      if (format?.length) message = this._format(message, format);else message = this._sanitize(message);
      this.chatMessageContentElement.innerHTML = message;
      this._addCopyCodeButtons();
    }
    _addCopyCodeButtons() {
      this.chatMessageContentElement.querySelectorAll("pre").forEach(pre => {
        if (pre.querySelector(".btn-copy-code")) return;
        const button = document.createElement("button");
        button.type = "button";
        button.classList.add("btn", "btn-sm", "btn-primary", "btn-copy-code", "mt-1");
        button.innerHTML = `<wc-icon icon="copy" size="sm" class="me-1"></wc-icon>${i18nlocales.t("chat.copyCode")}`;
        button.addEventListener("click", e => {
          e.preventDefault();
          e.stopPropagation();
          const codeElement = pre.querySelector("code");
          const textToCopy = codeElement ? codeElement.textContent : pre.textContent;
          navigator.clipboard.writeText(textToCopy).then(() => {
            const originalHTML = button.innerHTML;
            button.innerHTML = `<wc-icon icon="check" size="sm" class="me-1"></wc-icon>${i18nlocales.t("chat.copiedCode")}`;
            setTimeout(() => {
              button.innerHTML = originalHTML;
            }, 2000);
          });
        });
        const container = document.createElement("div");
        container.appendChild(button);
        pre.appendChild(container);
      });
    }
    _bindEvents() {
      this._bindControlsEvents();
    }
    _bindControlsEvents() {
      if (!this.chatControlsElement) return;

      //this.addEventListener("dblclick", this._copyMessageToChatInput.bind(this));

      this.chatBtnControlsCopyElement?.addEventListener("click", this._copyMessageToClipboard.bind(this));
      this.chatBtnControlsEditElement?.addEventListener("click", this._copyMessageToChatInput.bind(this));
      this.chatBtnControlsLikeElement?.addEventListener("click", this._like.bind(this));
      this.chatBtnControlsDislikeElement?.addEventListener("click", this._dislike.bind(this));
    }
    _copyMessageToClipboard() {
      navigator.clipboard.writeText(this.chatMessageContentElement.innerText).catch(err => {
        console.error("Failed to copy text: ", err);
      });
    }
    _copyMessageToChatInput() {
      this.dispatchEvent(new CustomEvent("copy-to-input", {
        detail: this.unformattedMessage,
        bubbles: true
      }));
    }
    _like() {
      this.dispatchEvent(new CustomEvent("like", {
        detail: {
          messageId: this.messageId
        },
        bubbles: true
      }));
      this.chatMessageElement.classList.remove("chat-message-disliked");
      this.chatMessageElement.classList.add("chat-message-liked");
    }
    _dislike() {
      this.dispatchEvent(new CustomEvent("dislike", {
        detail: {
          messageId: this.messageId
        },
        bubbles: true
      }));
      this.chatMessageElement.classList.remove("chat-message-liked");
      this.chatMessageElement.classList.add("chat-message-disliked");
    }
    _sanitize(message) {
      if (!message) return "";
      return htmlEncode(message);
    }
    _format(message, format) {
      if (!message) return "";
      if (format.includes("nl")) message = replaceNewLine(message);
      if (format.includes("markdown")) message = this._formatMarkdown(message);
      if (format.includes("table")) message = this._formatTable(message);
      return message;
    }
    _formatMarkdown(message) {
      if (!message) return "";

      //force target="_blank" on links
      const renderer = new marked.Renderer();
      renderer.link = (href, title, text) => {
        return `<a href="${href}" title="${title || ""}" target="_blank">${text}</a>`;
      };
      const options = {
        renderer
      };
      return marked(message, options);
    }
    _formatTable(message) {
      return message.replace(/(<table>)/gm, `<table class="table table-striped">`);
    }
    _setAttributes(element, attributes) {
      if (!attributes || !element) return;
      for (const attributeName in attributes) {
        element.setAttribute(attributeName, attributes[attributeName]);
      }
    }
  }
  customElements.define("wc-chat-interaction", chatInteractionElement);
  function sIf(condition, result) {
    return condition ? result : "";
  }

  class chatInteractionControlElement extends HTMLElement {
    constructor() {
      super();
    }
    get icon() {
      return this.getStringAttribute("icon");
    }
    set icon(value) {
      this.setAttribute("icon", value);
    }
    connectedCallback() {
      this.innerHTML = `<button type="button" title="${this.title}" class="text-tertiary-color btn-chat-control text-reset"><wc-icon icon="${this.icon}" size="sm"></wc-icon></button>`;
    }
  }
  customElements.define("wc-chat-interaction-control", chatInteractionControlElement);
  class chatControlLikeElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.innerHTML = `<wc-chat-interaction-control icon="thumbs-up" title="${i18nlocales.t("chat.controlLikeTitle")}" />`;
    }
  }
  customElements.define("wc-chat-ctrl-like", chatControlLikeElement);
  class chatControlDislikeElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.innerHTML = `<wc-chat-interaction-control icon="thumbs-down" title="${i18nlocales.t("chat.controlDislikeTitle")}" />`;
    }
  }
  customElements.define("wc-chat-ctrl-dislike", chatControlDislikeElement);
  class chatControlCopyElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.innerHTML = `<wc-chat-interaction-control icon="copy" title="${i18nlocales.t("chat.controlCopyTitle")}" />`;
    }
  }
  customElements.define("wc-chat-ctrl-copy", chatControlCopyElement);
  class chatControlEditElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.innerHTML = `<wc-chat-interaction-control icon="edit" title="${i18nlocales.t("chat.controlCopyEditTitle")}" />`;
    }
  }
  customElements.define("wc-chat-ctrl-edit", chatControlEditElement);

  class chatInfoElement extends HTMLElement {
    constructor() {
      super();
    }
    get text() {
      return this.innerHTML;
    }
    set text(value) {
      this.innerHTML = value;
    }
  }
  customElements.define("wc-chat-info", chatInfoElement);

  class chatAlertElement extends HTMLElement {
    constructor() {
      super();
      this.mainText = "";
      this.secondaryText = "";
    }
    connectedCallback() {
      this.innerHTML = `
      <div class="alert alert-dismissible alert-secondary d-flex flex-column shadow-lg">
        <button type="button" class="btn-close" aria-label="Close"></button>
        <span>${this.mainText}</span>
        <span class="small text-muted">${this.secondaryText}</span>
      </div>`;
      this.querySelector(".btn-close").addEventListener("click", () => {
        this.dispatchEvent(new Event("close-chat-alert", {
          bubbles: true
        }));
        this.remove();
      });
    }
  }
  customElements.define("wc-chat-alert", chatAlertElement);

  class chatFileElement extends HTMLElement {
    constructor() {
      super();
    }
    get fileId() {
      return this.getStringAttribute("file-id", "");
    }
    set fileId(value) {
      this.setAttribute("file-id", value);
    }
    connectedCallback() {
      this.innerHTML = `<span>${this.fileId}</span>
    <button type="button">
      <wc-icon icon="close" size="sm"></wc-icon>
    </button>`;
      this.querySelector("button").addEventListener("click", () => {
        this.dispatchEvent(new CustomEvent("delete-file", {
          detail: this.fileId,
          bubbles: true
        }));
        this.remove();
      });
    }
  }
  customElements.define("wc-chat-file", chatFileElement);

  class chatContextElement extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
    }
    static get observedAttributes() {
      return ["context-id", "label", "description", "checked", "visibility"];
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (!this.initialized || oldValue === newValue) return;
      if (name === "checked") {
        this._updateCheckedState();
      } else if (name === "visibility") {
        this._updateVisibilityState();
      } else {
        this._render();
      }
    }
    get contextId() {
      return this.getStringAttribute("context-id", "");
    }
    set contextId(value) {
      this.setAttribute("context-id", value);
    }
    get label() {
      return this.getStringAttribute("label", this.contextId);
    }
    set label(value) {
      this.setAttribute("label", value);
    }
    get description() {
      return this.getStringAttribute("description", "");
    }
    set description(value) {
      this.setAttribute("description", value);
    }
    get checked() {
      return this.hasAttribute("checked");
    }
    set checked(value) {
      if (value) {
        this.setAttribute("checked", "");
      } else {
        this.removeAttribute("checked");
      }
    }
    get visibility() {
      return this.getStringAttribute("visibility", "visible", ["visible", "hidden"]);
    }
    set visibility(value) {
      this.setAttribute("visibility", value);
    }
    async connectedCallback() {
      await this._init();
    }
    async _init() {
      if (this.initialized) return;
      this.classList.toggle("context-inactive", !this.checked);
      this._render();
      this._setupEventListeners();
      this.initialized = true;
    }
    _setupEventListeners() {
      this.addEventListener("click", e => {
        if (e.target.closest(".btn-toggle-context")) {
          e.stopPropagation();
          this.checked = false;
        } else if (!this.checked) {
          this.checked = true;
        }
      });
    }
    _updateCheckedState() {
      const isChecked = this.checked;
      this.classList.toggle("context-inactive", !isChecked);
      const tooltip = this.querySelector(`#label-tooltip-${this.contextId}`);
      if (tooltip) tooltip.disabled = isChecked;
    }
    _updateVisibilityState() {
      this.style.visibility = this.visibility;
    }
    _render() {
      const safeLabel = this._escapeHtml(this.label);
      const safeDescription = this._escapeHtml(this.description);
      this.innerHTML = `
      <span id="label-${this.contextId}">${safeLabel}</span>
      <button id="btn-${this.contextId}" type="button" class="btn-toggle-context" aria-label="${i18nlocales.t("chat.removeContextAria")}">
        <wc-icon icon="close" size="md" class="icon-close"></wc-icon>
      </button>
      <wc-icon icon="plus" size="md" class="icon-plus"></wc-icon>
      <wc-tooltip id="label-tooltip-${this.contextId}" target="#label-${this.contextId}" content="${safeDescription}" ${this.checked ? "disabled" : ""} />
      <wc-tooltip id="btn-tooltip-${this.contextId}" target="#btn-${this.contextId}" content="${i18nlocales.t("chat.removeContextTooltip")}" />
    `;
    }
    _escapeHtml(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    }
  }
  customElements.define("wc-chat-context", chatContextElement);

  class labelElement extends HTMLElement {
    constructor() {
      super();
      this._isInitialized = false;
    }
    connectedCallback() {
      this._init();
    }

    // Atributos para o label
    get for() {
      return this.getStringAttribute("for");
    }
    set for(value) {
      this.setAttribute("for", value);
    }
    get class() {
      return this.getStringAttribute("class", "form-label");
    }
    set class(value) {
      this.setAttribute("class", value);
    }
    get required() {
      return this.hasAttribute("required") && this.getAttribute("required") !== "false";
    }
    set required(value) {
      this.setAttribute("required", value);
    }
    get requiredHidden() {
      const val = this.getAttribute("required-hidden");
      return val === "true" || val === "";
    }

    // V2: novo padrão de selos (obrigatório visível, opcional oculto por padrão).
    // Opt-in via atributo `v2` para não impactar módulos legados que usam
    // `required` sem necessidade real de obrigatoriedade.
    get v2() {
      return this.getBooleanAttribute("v2", false);
    }
    get showOptional() {
      return this.getBooleanAttribute("show-optional", false);
    }
    get contents() {
      return this.getStringAttribute("label") || this.innerHTML;
    }
    get url() {
      return this.getStringAttribute("url");
    }
    set url(value) {
      this.setAttribute("url", value);
    }

    // Novos getters para as propriedades do wc-help
    get helpCaption() {
      return this.getStringAttribute("help-caption");
    }
    get helpContent() {
      return this.getStringAttribute("help-content");
    }
    _init() {
      if (this._isInitialized) return;
      this._isInitialized = true;
      this._render();
    }
    _render() {
      this.innerHTML = `
      <label class="${this.class}" for="${this.for}">
        ${this.contents}
        ${this._getHelpLabel()}
        <span class="ms-auto fade-element">
          ${this._getOptionLabel()}
          ${this._getUrlLabel()}
        </span>
      </label>
    `;
    }

    // Método que retorna a marcação do wc-help caso as props sejam informadas
    _getHelpLabel() {
      const caption = this.helpCaption;
      const content = this.helpContent;
      if (caption || content) {
        return `<wc-help caption="${caption || ""}" content="${content || ""}"></wc-help>`;
      }
      return "";
    }
    _getOptionLabel() {
      if (this.requiredHidden) return "";
      if (this.required) {
        return this.v2 ? `<span class="text-danger">${i18nlocales.t("required")}</span>` : `<span class="text-danger d-none">${i18nlocales.t("required")}</span>`;
      }
      if (this.v2) {
        return this.showOptional ? i18nlocales.t("optional") : "";
      }
      return i18nlocales.t("optional");
    }
    _getUrlLabel() {
      return this.url ? `<a href="${this.url}" target="_blank">${i18nlocales.t("portal")}</a>` : "";
    }
  }
  customElements.define("wc-label", labelElement);

  const EVENTS = Object.freeze({
    START: "start:ai-action"
  });
  const TYPE = Object.freeze({
    SINGLE: "SINGLE",
    SPLIT: "SPLIT"
  });
  const STATE = Object.freeze({
    INITIAL: "initial",
    THINKING: "thinking",
    RESPONSE: "response"
  });
  const TOAST_TIMEOUT = 9999;
  const DATETIME_FORMAT = Object.freeze({
    hour: "2-digit",
    minute: "2-digit"
  });
  const PULSE_CLASS_NAME = "ai-response-pulse";
  const MAIN_BUTTON_CLASSES = Object.freeze(["mainActionButton", "btn", "d-inline-flex", "align-items-center", "button-spacing"]);
  const DROPDOWN_CLASSES = Object.freeze(["dropdownToggle", "btn", "dropdown-toggle", "dropdown-toggle-split"]);
  class AIQuickActionButton extends HTMLElement {
    constructor() {
      super();
      this._innerId = generateRandomId();
      this._isInitialized = false;
      this._lottieInstance = null;
      this._state = this.getStringAttribute("state", STATE.INITIAL);
      this._history = [];
      if (this.dropdownPromptEnabled) {
        this._promptTabId = "prompt-tab-" + this._innerId;
        this._promptTabPaneId = "prompt-tab-pane-" + this._innerId;
      }
      if (this.historyTabEnabled) {
        this._historyTabId = "history-tab-" + this._innerId;
        this._historyTabPaneId = "history-tab-pane-" + this._innerId;
      }
    }
    static get observedAttributes() {
      return ["disabled", "button-class"];
    }
    async connectedCallback() {
      if (this._isInitialized) return;
      await loadLottie();
      this._renderHTML();
      this._setupListeners();
      this._isInitialized = true;
      this._updateButtonClasses();
      this._setState(this._state, true);
    }
    disconnectedCallback() {
      this._lottieInstance?.destroy();
      this._lottieInstance = null;
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (!this._isInitialized) return;
      if (name === "disabled" && oldValue !== newValue) {
        this._mainButton.disabled = this.disabled;
        if (this._dropdownToggle) this._dropdownToggle.disabled = this.disabled;
      } else if (name === "button-class") {
        this._updateButtonClasses();
      }
    }

    //#region Elements
    get _mainButton() {
      return this.querySelector(".mainActionButton");
    }
    get _dropdownToggle() {
      return this.querySelector(".dropdownToggle");
    }
    get _promptTextarea() {
      return this.querySelector(".promptTextarea");
    }
    get _sendPromptButton() {
      return this.querySelector(".sendPromptButton");
    }
    get _iconContainer() {
      return this._mainButton.querySelector(".wc-ai-icon-container");
    }
    get _labelText() {
      return this._mainButton.querySelector(".wc-ai-label-text");
    }
    //#endregion

    //#region Attributes
    get disabled() {
      return this.hasAttribute("disabled");
    }
    set disabled(value) {
      if (value) this.setAttribute("disabled", "");else this.removeAttribute("disabled");
    }
    get state() {
      return this._state;
    }
    set state(value) {
      this._state = value;
    }
    get label() {
      return this.getStringAttribute("label", "Ação");
    }
    set label(value) {
      this.setAttribute("label", value);
    }
    get responseLabel() {
      return this.getStringAttribute("response-label", "Sucesso!");
    }
    set responseLabel(value) {
      this.setAttribute("response-label", value);
    }
    get thinkingLabel() {
      return this.getStringAttribute("thinking-label", "Processando...");
    }
    set thinkingLabel(value) {
      this.setAttribute("thinking-label", value);
    }
    get lottiePath() {
      return this.getStringAttribute("lottie-path", `${getZeevBootstrapPath("assets/animations/sparkles-floating.json")}`);
    }
    set lottiePath(value) {
      this.setAttribute("lottie-path", value);
    }
    get buttonClass() {
      return this.getStringAttribute("button-class", "btn-secondary");
    }
    set buttonClass(value) {
      this.setAttribute("button-class", value);
    }
    get confirmStart() {
      return this.hasAttribute("confirm-start");
    }
    set confirmStart(value) {
      if (value) this.setAttribute("confirm-start", "");else this.removeAttribute("confirm-start");
    }
    get toastMessage() {
      return this.getStringAttribute("toast-message");
    }
    set toastMessage(value) {
      this.setAttribute("toast-message", value);
    }
    get toastFeedbackMessage() {
      return this.getStringAttribute("toast-feedback-message");
    }
    set toastFeedbackMessage(value) {
      this.setAttribute("toast-feedback-message", value);
    }
    get defaultPrompt() {
      return this.getStringAttribute("default-prompt", "");
    }
    set defaultPrompt(value) {
      this.setAttribute("default-prompt", value);
    }
    get maxPromptChars() {
      const v = Number.parseInt(this.getAttribute("max-chars"));
      return Number.isInteger(v) && v > 0 ? v : 20;
    }
    set maxPromptChars(value) {
      this.setAttribute("max-chars", String(value));
    }
    get shadowVariant() {
      return this.getAttribute("shadow-variant") || "default";
    }
    set shadowVariant(value) {
      this.setAttribute("shadow-variant", value);
    }
    get type() {
      return this.getStringAttribute("type", TYPE.SINGLE).toUpperCase();
    }
    set type(value) {
      this.setAttribute("type", value);
    }
    get dropdownPromptEnabled() {
      return this.hasAttribute("dropdown-prompt");
    }
    set dropdownPromptEnabled(value) {
      if (value) this.setAttribute("dropdown-prompt", "");else this.removeAttribute("dropdown-prompt");
    }
    get historyTabEnabled() {
      return this.hasAttribute("history-tab");
    }
    set historyTabEnabled(value) {
      if (value) this.setAttribute("history-tab", "");else this.removeAttribute("history-tab");
    }
    get hasDropdown() {
      return this.dropdownPromptEnabled || this.historyTabEnabled;
    }
    //#endregion

    //#region Render HTML
    _renderHTML() {
      const isSplit = this.type === TYPE.SPLIT;
      const shadowClass = this.shadowVariant === "soft" ? "shadow-soft" : "";
      let html = `
      <div class="ia-quick-action ${shadowClass}">
        <div class="${isSplit && this.hasDropdown ? "btn-group" : "d-inline-flex"}" role="group">
        `;
      const buttonClasses = [...MAIN_BUTTON_CLASSES, this.type === TYPE.SINGLE && this.hasDropdown ? "dropdown-toggle" : ""];
      html += `
      <button type="button" class="${buttonClasses.join(" ")}"
        ${this.disabled ? "disabled" : ""}
        role="button"
        ${this.type === TYPE.SINGLE && this.hasDropdown ? `data-bs-auto-close="outside" data-bs-toggle="dropdown" aria-expanded="false"` : ""}>
        <span class="wc-ai-icon-container">
          <wc-icon icon="sparkles-default" class="sparkles-default-position"></wc-icon>
        </span>
        <span class="wc-ai-label-text">${this.label}</span>
      </button>
      `;
      if (isSplit && this.hasDropdown) {
        html += `
        <button type="button" class="dropdownToggle ${DROPDOWN_CLASSES.join(" ")}"
          data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside">
          <span class="visually-hidden">${this.label}</span>
        </button>
        `;
      }
      if (this.hasDropdown) {
        html += this._getDropdownMenuHTML();
      }
      html += `
        </div>
      </div>
      `;
      this.innerHTML = html;
    }
    _getDropdownMenuHTML() {
      let tabsHtml = "";
      let contentHtml = "";
      const isHistoryActive = this.historyTabEnabled && !this.dropdownPromptEnabled;
      if (this.dropdownPromptEnabled) {
        tabsHtml += `
        <li class="nav-item" role="presentation">
          <a id="${this._promptTabId}"
            class="nav-link fs-sm p-1 ${this.dropdownPromptEnabled ? "active" : ""}"
            data-bs-toggle="tab"
            data-bs-target="#${this._promptTabPaneId}"
            type="button"
            role="tab"
            aria-controls="${this._promptTabPaneId}"
            aria-selected="${this.dropdownPromptEnabled}">
            <wc-icon icon="sparkles-default" class="me-1"></wc-icon>${i18nlocales.t("prompt")}
          </a>
        </li>
        `;
        contentHtml += this._getPromptTabContentHTML(this.dropdownPromptEnabled, this.maxPromptChars);
      }
      if (this.historyTabEnabled) {
        tabsHtml += `
        <li class="nav-item" role="presentation">
          <a id="${this._historyTabId}"
            class="nav-link fs-sm p-1 link-secondary ${isHistoryActive ? "active" : ""}"
            data-bs-toggle="tab"
            data-bs-target="#${this._historyTabPaneId}"
            type="button"
            role="tab"
            aria-controls="${this._historyTabPaneId}"
            aria-selected="${isHistoryActive}"
            data-tab-name="history">
            <wc-icon icon="history" class="me-1"></wc-icon>${i18nlocales.t("history")}
          </a>
        </li>
        `;
        contentHtml += this._getHistoryTabContentHTML(isHistoryActive);
      }
      return `
      <div class="dropdown-menu dropdown-menu-end shadow-lg p-3" style="min-width: 380px; max-height: 250px;">
        <ul class="nav nav-pills nav-fill mb-2" role="tablist">
          ${tabsHtml}
        </ul>
        <div>
          ${contentHtml}
        </div>
      </div>
    `;
    }
    _getPromptTabContentHTML(isActive, maxChars) {
      const currentLen = (this.defaultPrompt || "").length;
      return `
      <div class="tab-pane fade ${isActive ? "show active" : ""}"
        id="${this._promptTabPaneId}"
        role="tabpanel"
        aria-labelledby="${this._promptTabId}">
        <div class="mb-3">
          <textarea class="promptTextarea form-control"
            rows="4"
            maxlength="${maxChars}"
            placeholder="${i18nlocales.t("promptPlaceholder")}">${this.defaultPrompt}</textarea>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted fs-xs ms-2 promptCounter">${currentLen} de ${maxChars}</span>
          <button type="button" class="sendPromptButton btn btn-primary btn-sm rounded-pill" ${currentLen === 0 ? "disabled" : ""}>
            <wc-icon icon="send" class="me-2"></wc-icon>${i18nlocales.t("send")}
          </button>
        </div>
      </div>
      `;
    }
    _getHistoryTabContentHTML(isActive) {
      return `
      <div class="tab-pane fade ${isActive ? "show active" : ""}" id="${this._historyTabPaneId}" role="tabpanel" aria-labelledby="${this._historyTabId}">
        <div class="d-flex justify-content-between align-items-center mb-3">
          ${this.dropdownPromptEnabled ? `
              <button type="button" class="btn btn-sm btn-link p-0 backToPrompt">
                <wc-icon icon="arrow-left" class="me-1"></wc-icon>${i18nlocales.t("return")}
              </button>` : "<div></div>"}
          <span class="text-muted fs-sm historyCount">0 prompts</span>
        </div>
        <div style="max-height: 250px; overflow-y: scroll;" class="list-group historyListContainer">
        </div>
      </div>
      `;
    }
    _renderHistory() {
      if (!this.historyTabEnabled) return;
      const countSpan = this.querySelector(".historyCount");
      countSpan.textContent = `${this._history.length} prompt${this._history.length > 1 ? "s" : ""}`;
      const container = this.querySelector(".historyListContainer");
      if (this._history.length === 0) {
        container.innerHTML = `<div class="alert alert-info py-2 m-0 fs-sm text-center">${i18nlocales.t("emptyPromptHistory")}</div>`;
        return;
      }
      container.innerHTML = "";
      this._history.slice().reverse().forEach(item => {
        const listItem = document.createElement("div");
        listItem.className = "list-group-item list-group-item-action d-flex flex-column align-items-start py-2";
        listItem.innerHTML = `
          <div class="w-100 text-truncate fw-bold fs-sm">
            <wc-icon icon="terminal" class="me-1 text-muted"></wc-icon> ${item.prompt}
          </div>
          <small class="text-muted w-100 text-end">${item.timestamp}</small>
          `;
        container.appendChild(listItem);
      });
    }
    //#endregion

    _setupListeners() {
      this._mainButton.addEventListener("click", this._handleButtonClick.bind(this));
      this._sendPromptButton?.addEventListener("click", this._handlePromptSubmit.bind(this));
      this._promptTextarea?.addEventListener("input", this._checkTextareaContent.bind(this));
      this._checkTextareaContent();
      this.querySelector(".backToPrompt")?.addEventListener("click", () => this._showTab(this._promptTabId));
      this.querySelector(`#${this._historyTabId}`)?.addEventListener("shown.bs.tab", this._renderHistory.bind(this));
      const dropdownElement = this.querySelector(".dropdown-menu") ? this.querySelector(".btn-group") || this.querySelector(".ia-quick-action") : null;
      dropdownElement?.addEventListener("hide.bs.dropdown", () => {
        this.dispatchEvent(new CustomEvent("dropdown-toggled", {
          detail: {
            state: "closed"
          }
        }));
      });
      dropdownElement?.addEventListener("show.bs.dropdown", () => {
        this.dispatchEvent(new CustomEvent("dropdown-toggled", {
          detail: {
            state: "open"
          }
        }));
      });
    }
    _showTab(tabId) {
      this.querySelector(`#${tabId}`)?.click();
    }
    _setState(newState, force = false) {
      if (this._state === newState && !force) return;
      const oldState = this._state;
      this._state = newState;
      this._renderContent();
      if (oldState === STATE.THINKING && newState === STATE.RESPONSE) {
        this._mainButton.blur();
        this._showResponseToast();
      }
      if (newState === STATE.RESPONSE) {
        this._mainButton.classList.add(PULSE_CLASS_NAME);
      } else {
        this._mainButton.classList.remove(PULSE_CLASS_NAME);
      }
      const isDisabled = newState === STATE.THINKING;
      if (this._sendPromptButton) {
        this._sendPromptButton.disabled = isDisabled || this._promptTextarea?.value?.trim() === "";
      }
      if (this._promptTextarea) this._promptTextarea.disabled = isDisabled;
      if (this._dropdownToggle) this._dropdownToggle.disabled = isDisabled;
    }
    _showResponseToast() {
      if (!this.toastFeedbackMessage) return;
      let toastContent = this.toastFeedbackMessage;
      let toastColor = "success";

      // Verifica se a mensagem de feedback é o valor padrão.
      // Se for o texto padrão, ativamos o modo interativo.
      const isDefaultMessage = this.toastFeedbackMessage === this.responseLabel || this.toastMessage && this.toastFeedbackMessage === this.toastMessage;

      //esse toast nao esta pronto e nao deve ser utilizado ainda
      //TODO: incluir eventos, traducao e estilo
      if (isDefaultMessage) {
        toastContent = `
        <div class="d-flex flex-column w-100 p-0 text-white">
          <div class="mb-2">
            <strong class="d-block">${this.responseLabel}</strong>
            <span>Ação da Zai concluída. Confirme ou desfaça a alteração.</span>
          </div>
          <div class="d-flex gap-3 justify-content-start mt-2">
            <a href="#" class="btn btn-sm btn-link p-0 text-white fw-bold me-3" style="text-decoration: none;" data-action="undo">Desfazer</a>
            <a href="#" class="btn btn-success btn-sm rounded-pill fw-bold px-3 py-1" data-action="accept">Aceitar</a>
          </div>
        </div>
      `;
        toastColor = "dark";
      }
      toast({
        message: toastContent,
        color: toastColor,
        timeout: TOAST_TIMEOUT,
        placement: "bottom"
      });
    }
    _renderContent() {
      let label = "";
      let iconHTML = "";
      this._iconContainer.innerHTML = "";
      switch (this._state) {
        case STATE.THINKING:
          label = this.thinkingLabel;
          if (this.lottiePath && lottie.lottie !== undefined) {
            const lottieContainer = this._createLottieContainer();
            this._iconContainer.appendChild(lottieContainer);
            this._startAnimation(lottieContainer);
          } else {
            iconHTML = '<wc-icon icon="spinner"></wc-icon>';
          }
          break;
        case STATE.INITIAL:
        case STATE.RESPONSE:
        default:
          label = this._state === STATE.RESPONSE ? this.responseLabel : this.label;
          iconHTML = '<wc-icon icon="sparkles-default" class="sparkles-default-position"></wc-icon>';
          this._stopAnimation();
      }
      this._labelText.textContent = label;
      if (this._state !== STATE.THINKING || !this.lottiePath || lottie.lottie === undefined) {
        this._iconContainer.innerHTML = iconHTML;
      }
    }
    _updateButtonClasses() {
      const newClasses = this.buttonClass.split(/\s+/).filter(c => !!c);
      let buttonClasses = [...this._mainButton.classList];
      for (const c of buttonClasses) {
        if (MAIN_BUTTON_CLASSES.includes(c) || newClasses.includes(c)) continue;
        if ("dropdown-toggle" === c && this.type === TYPE.SINGLE && this.hasDropdown) {
          continue;
        }
        this._mainButton.classList.remove(c);
      }
      this._mainButton.classList.add(...newClasses);
      if (this._dropdownToggle) {
        buttonClasses = [...this._dropdownToggle.classList];
        for (const c of buttonClasses) {
          if (DROPDOWN_CLASSES.includes(c) || newClasses.includes(c)) {
            continue;
          }
          this._dropdownToggle.classList.remove(c);
        }
        this._dropdownToggle.classList.add(...newClasses);
      }
    }
    _createLottieContainer() {
      const div = document.createElement("div");
      div.classList.add("lottie-placeholder-container", "lottie-container");
      return div;
    }
    _startAnimation(container) {
      if (!this.lottiePath || lottie.lottie === undefined) return;
      if (!this._lottieInstance || this._lottieInstance.container !== container) {
        this._lottieInstance = lottie.lottie.loadAnimation({
          container: container,
          renderer: "svg",
          loop: true,
          autoplay: true,
          path: this.lottiePath
        });
      }
      this._lottieInstance.play();
    }
    _stopAnimation() {
      this._lottieInstance?.stop();
      this._lottieInstance?.destroy();
    }
    _checkTextareaContent() {
      if (this._promptTextarea && this._sendPromptButton) {
        let value = this._promptTextarea.value || "";
        const max = this.maxPromptChars;
        if (value.length > max) {
          value = value.substring(0, max);
          this._promptTextarea.value = value;
        }
        const isEmpty = value.trim() === "";
        this._sendPromptButton.disabled = isEmpty || this._state === STATE.THINKING;
        const counter = this.querySelector(".promptCounter");
        if (counter) counter.textContent = `${value.length} de ${max}`;
      }
    }
    _closeDropdown() {
      const dropdown = this._dropdownToggle || this._mainButton;
      const bsDropdown = window.bootstrap.Dropdown.getInstance(dropdown) || new window.bootstrap.Dropdown(dropdown);
      bsDropdown.hide();
    }
    async _handleButtonClick(event) {
      if (this._state !== STATE.INITIAL) return;
      const isToggleAction = this.type === TYPE.SINGLE && this.hasDropdown && event.currentTarget.classList.contains("dropdown-toggle");

      // Se o botao principal eh um toggle de dropdown no modo SINGLE, nao realiza a acao rapida.
      if (isToggleAction) return;
      await this._dispatchStartEvent("quick");
    }
    async _handlePromptSubmit() {
      if (this._sendPromptButton.disabled) return;
      const prompt = this._promptTextarea.value.trim();
      if (!prompt) return;
      if (this.historyTabEnabled) {
        const locale = document.documentElement.lang || "pt-BR";
        const now = new Date();
        const timestamp = `${now.toLocaleDateString(locale)} ${now.toLocaleTimeString(locale, DATETIME_FORMAT)}`;
        this._history.push({
          prompt,
          timestamp
        });
        this._renderHistory();
      }
      this._checkTextareaContent();
      this._closeDropdown();
      await this._dispatchStartEvent("manual", prompt);
    }
    async _dispatchStartEvent(actionType, prompt = null) {
      if (this.confirmStart) {
        const confirmed = await this._waitForConfirmation();
        if (!confirmed) return;
      }
      this._setState(STATE.THINKING);
      this.dispatchEvent(new CustomEvent(EVENTS.START, {
        bubbles: true,
        composed: true,
        detail: {
          prompt,
          actionType,
          complete: () => {
            this._setState(STATE.RESPONSE);
            if (this._promptTextarea) {
              this._promptTextarea.value = "";
              this._checkTextareaContent();
            }
            setTimeout(() => this._setState(STATE.INITIAL), 4000);
          }
        }
      }));
    }
    async _waitForConfirmation() {
      return new Promise(resolve => {
        confirm({
          okCallback: () => resolve(true),
          noCallback: () => resolve(false)
        });
      });
    }
  }
  customElements.define("wc-ai-quick-action", AIQuickActionButton);

  class IconWrapperElement extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this._init();
    }

    // --- Propriedades (Getters e Setters) ---

    get icon() {
      return this.getAttribute("icon") || "";
    }
    set icon(value) {
      this.setAttribute("icon", value);
    }
    get size() {
      const validSizes = ["sm", "md", "lg", "xl"];
      const size = this.getAttribute("size");
      return validSizes.includes(size) ? size : "md";
    }
    set size(value) {
      this.setAttribute("size", value);
    }
    get bgColor() {
      return this.getAttribute("bg-color") || "bg-warning";
    }
    set bgColor(value) {
      this.setAttribute("bg-color", value);
    }
    get iconColor() {
      return this.getAttribute("icon-color") || "text-warning";
    }
    set iconColor(value) {
      this.setAttribute("icon-color", value);
    }

    // --- Métodos Internos ---

    _init() {
      this._render();
    }
    _getDimensions() {
      const sizes = {
        sm: "32px",
        md: "44px",
        lg: "56px",
        xl: "68px"
      };
      return sizes[this.size] || sizes.md;
    }
    _render() {
      const dim = this._getDimensions();
      this.innerHTML = `
      <div 
        style="
          width: ${dim}; min-width: ${dim}; max-width: ${dim}; 
          height: ${dim}; min-height: ${dim}; max-height: ${dim}; 
          display: flex; justify-content: center; align-items: center;
        " 
        class="${this.bgColor} ${this.iconColor} p-2 rounded flex-shrink-0"
      >
        <wc-icon 
          icon="${this.icon}" 
          size="${this.size}" 
          style="position: relative; top: -1px;"
        ></wc-icon>
      </div>
    `;
    }
  }
  customElements.define("wc-icon-wrapper", IconWrapperElement);

})();
